import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	MdAccessTime,
	MdArrowBackIosNew,
	MdAutorenew,
	MdCalendarToday,
	MdKeyboardArrowDown,
	MdChevronLeft,
	MdChevronRight,
	MdDeleteOutline,
	MdInfoOutline,
	MdOutlineEdit,
	MdOutlineWarningAmber,
	MdSettings,
	MdVisibility,
} from 'react-icons/md';
import { IoIosSearch } from 'react-icons/io';
import { createPortal } from 'react-dom';
import Cookies from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../Footer';
import NavBar from '../NavBar';
import {
	canManageEscalaEnfermagem,
	isDeveloperUser,
} from '../../utils/escalaFeatures';
import {
	requestAbrirAmbientePreenchimentoEscala,
	requestAbrirAlocacaoOrigemAmbienteEscala,
	requestAdicionarVacanciaAmbienteEscala,
	requestAplicarParticionamentoAlocacaoOrigem,
	requestCadastrarVinculoProfissionalEstabelecimento,
	requestExecutarAcaoEdicaoAlocacaoOrigem,
	requestAplicarAcoesAmbienteEscala,
	requestAplicarPreenchimentoAmbienteEscala,
	requestBuscarProfissionalEscala,
	requestCatalogosEscalaSelects,
	requestComplementosTrocaEquivalente,
	requestConsultarTrocasEquivalentes,
	requestConsultarTodasTrocasEquivalentes,
	requestEfetivarTrocaEquivalente,
	requestSalvarProfissionalAmbienteEscala,
	requestSimularTrocaEquivalente,
	requestAtualizarVinculoProfissionalEstabelecimento,
	requestNewUnits,
	requestOpcoesAmbientePreenchimentoEscala,
	requestPreverAcoesAmbienteEscala,
	requestPreverParticionamentoAlocacaoOrigem,
	requestPreverPreenchimentoAmbienteEscala,
	requestVinculosEscala,
} from '../../services/escalaRequests';
import { sortSetoresPorAndar } from '../../utils/escalaSetorOrdering';
import SearchableSetorSelect from './SearchableSetorSelect';
import PlanejamentoProfissionalEnfermagem from '../Escala de enfermagem/PlanejamentoProfissionalEnfermagem';

const TABLE_BODY_HEIGHT = 510;
const TABLE_ROW_HEIGHT = 42.5;
const LINHAS_VAZIAS_MINIMO = Math.ceil(TABLE_BODY_HEIGHT / TABLE_ROW_HEIGHT);
const TABLE_SKELETON_MIN_LOADING_MS = 2000;
const TABLE_MONTH_NAVIGATION_MIN_LOADING_MS = 1000;

const PROF_COL_WIDTH = 240;
const EDIT_PANEL_WIDTH = 170;
const CALENDAR_DAY_MIN_WIDTH = 34;
const SETOR_TODOS_VALUE = '__todos__';
const DEFAULT_MEDICO_CARGO = 'Médico';
const DATA_ESPECIFICA_INFO =
	'Acrescente datas específicas para o profissional, caso ele tenha disponibilidade em dias diferentes do padrão da escala.';
const DATA_INICIAL_INFO =
	'Não permite datas anteriores a hoje. Define a partir de quando a escala do profissional será exibida.';
const DATA_INICIAL_EDICAO_INFO =
	'Caso este campo seja editado, a escala será alterada a partir da data escolhida. Escalas existentes em datas anteriores a essa serão mantidos.';
const ULTIMA_ATUALIZACAO_INFO =
	'Indica a data de início da versão mais recente da alocação deste setor.';
const DATA_INICIAL_ORIGINAL_INFO =
	'Data em que esta alocação foi criada neste setor. Este histórico não pode ser alterado.';

const FieldInfoTooltip = ({
	ariaLabel,
	text,
	triggerContent = null,
	triggerClassName = '',
	triggerContainerClassName = '',
	hoverDelayMs = 1000,
	openToLeft = false,
	widthClassName = 'w-[235px]',
}) => {
	const [showInfo, setShowInfo] = useState(false);
	const [infoPinned, setInfoPinned] = useState(false);
	const [tooltipPosition, setTooltipPosition] = useState({
		top: 0,
		left: 0,
	});
	const hoverTimerRef = useRef(null);
	const infoContainerRef = useRef(null);
	const tooltipRef = useRef(null);

	const clearHoverTimer = () => {
		if (!hoverTimerRef.current) return;
		clearTimeout(hoverTimerRef.current);
		hoverTimerRef.current = null;
	};

	useEffect(() => () => clearHoverTimer(), []);

	useEffect(() => {
		if (!showInfo) return undefined;
		const updateTooltipPosition = () => {
			const rect = infoContainerRef.current?.getBoundingClientRect();
			if (!rect) return;
			setTooltipPosition({
				top: rect.top + rect.height / 2,
				left: openToLeft ? rect.left - 6 : rect.right + 6,
			});
		};
		updateTooltipPosition();

		const handleOutsidePointerDown = (event) => {
			if (
				infoContainerRef.current?.contains(event.target) ||
				tooltipRef.current?.contains(event.target)
			) {
				return;
			}
			clearHoverTimer();
			setShowInfo(false);
			setInfoPinned(false);
		};

		document.addEventListener('pointerdown', handleOutsidePointerDown);
		window.addEventListener('resize', updateTooltipPosition);
		window.addEventListener('scroll', updateTooltipPosition, true);
		return () => {
			document.removeEventListener(
				'pointerdown',
				handleOutsidePointerDown,
			);
			window.removeEventListener('resize', updateTooltipPosition);
			window.removeEventListener('scroll', updateTooltipPosition, true);
		};
	}, [showInfo, openToLeft]);

	return (
		<div
			ref={infoContainerRef}
			className={
				triggerContainerClassName ||
				'relative flex h-3 w-3 items-center justify-center'
			}>
			<button
				type='button'
				aria-label={ariaLabel}
				aria-expanded={showInfo}
				onClick={() => {
					clearHoverTimer();
					setInfoPinned((current) => {
						const nextPinned = !current;
						setShowInfo(nextPinned);
						return nextPinned;
					});
				}}
				onMouseEnter={() => {
					clearHoverTimer();
					hoverTimerRef.current = setTimeout(
						() => setShowInfo(true),
						hoverDelayMs,
					);
				}}
				onMouseLeave={() => {
					clearHoverTimer();
					if (!infoPinned) setShowInfo(false);
				}}
				className={
					triggerClassName ||
					'flex h-4 w-4 items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:text-[#004A80] focus:text-[#004A80] focus:outline-none'
				}>
				{triggerContent || <MdInfoOutline className='h-4 w-4' />}
			</button>

			{showInfo &&
				typeof document !== 'undefined' &&
				createPortal(
					<div
						ref={tooltipRef}
						style={tooltipPosition}
						className={`fixed z-[900] ${widthClassName} -translate-y-1/2 rounded-md border border-[#D8DEE8] bg-white px-3 py-2 text-[11px] font-normal leading-relaxed text-[#4F5257] shadow-lg ${
							openToLeft ? '-translate-x-full' : ''
						}`}>
						{text}
					</div>,
					document.body,
				)}
		</div>
	);
};

const DataEspecificaInfo = () => (
	<FieldInfoTooltip
		ariaLabel='Informações sobre data específica'
		text={DATA_ESPECIFICA_INFO}
	/>
);

const DataInicialInfo = ({ isEditing = false }) => (
	<FieldInfoTooltip
		ariaLabel='Informações sobre data inicial'
		text={isEditing ? DATA_INICIAL_EDICAO_INFO : DATA_INICIAL_INFO}
	/>
);

const UltimaAtualizacaoInfo = () => (
	<FieldInfoTooltip
		ariaLabel='Informações sobre última atualização'
		text={ULTIMA_ATUALIZACAO_INFO}
	/>
);

const DataInicialOriginalInfo = () => (
	<FieldInfoTooltip
		ariaLabel='Informações sobre data inicial original'
		text={DATA_INICIAL_ORIGINAL_INFO}
	/>
);

const TrocaPerfilInfo = () => (
	<FieldInfoTooltip
		ariaLabel='Informações sobre os perfis de troca'
		hoverDelayMs={2000}
		openToLeft
		widthClassName='w-[320px]'
		text={
			<>
				<p>
					<strong>Trocar os dias:</strong> inverte completamente a
					carga horária dos profissionais fazendo com que um ocupe o
					lugar do outro nos dias que foram trocados.
				</p>
				<p className='mt-2'>
					<strong>Dividir plantão:</strong> escolhe apenas uma parcela
					do plantão do profissional de maior carga horária pra ser
					trocado, permitindo assim que o profissional de carga
					horária menor possa definir qual turno do plantão ele ira
					ser atribuido.
				</p>
			</>
		}
	/>
);

const DataEspecificaAction = ({ compact = false, onClick }) => (
	<div className={`flex items-center gap-2 ${compact ? '' : 'mt-4'}`}>
		<button
			type='button'
			onClick={onClick}
			className={
				compact
					? 'inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md border border-gray-300 text-[18px] font-medium leading-none text-[#13335A] transition hover:bg-[#F8FAFC]'
					: 'inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-[12px] font-medium leading-none text-[#13335A] transition hover:bg-[#F8FAFC]'
			}>
			{compact ? '+' : '+ Data específica'}
		</button>
		{!compact && <DataEspecificaInfo />}
	</div>
);

const DELETE_REASON_OPTIONS = [
	{ value: 'EXONERACAO', label: 'Exoneração' },
	{ value: 'APOSENTADORIA', label: 'Aposentadoria' },
	{ value: 'TRANSFERENCIA', label: 'Transferência' },
	{ value: 'ERRO_CADASTRO', label: 'Erro de Cadastro' },
	{ value: 'MUDANCA_SETOR', label: 'Mudança de Setor' },
	{ value: 'OBITO', label: 'Óbito' },
	{ value: 'OUTRO', label: 'Outro' },
];

const normalizeCode = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toUpperCase()
		.trim();

const getEscalaViewModeFromLocation = (location) => {
	const params = new URLSearchParams(location?.search || '');
	const rawMode =
		location?.state?.escalaOrigem ||
		location?.state?.escalaViewMode ||
		location?.state?.scope ||
		params.get('escala') ||
		params.get('origem') ||
		params.get('scope') ||
		'';
	const normalized = normalizeCode(rawMode);

	if (normalized.includes('ENFERMAG')) return 'enfermagem';
	if (normalized.includes('MEDIC')) return 'medica';
	return '';
};

export const resolveEscalaViewModeForUser = (requestedMode = '', user = {}) =>
	requestedMode === 'enfermagem' && canManageEscalaEnfermagem(user)
		? 'enfermagem'
		: 'medica';

const LEGENDA_FALLBACK = [
	{ codigo: 'ATRIBUIDO', nome: 'Dia atribuído' },
	{ codigo: 'NAO_ATRIBUIDO', nome: 'Não atribuído' },
	{ codigo: 'VACANCIA', nome: 'Vacância' },
	{ codigo: 'FERIAS', nome: 'Férias' },
	{ codigo: 'ATESTADO', nome: 'Atestado' },
	{ codigo: 'FOLGA', nome: 'Folga' },
	{ codigo: 'LICENCA', nome: 'Licença' },
	{ codigo: 'FALTA', nome: 'Falta' },
	{ codigo: 'TROCA', nome: 'Troca' },
];

const LEGENDA_TEXTO = [
	{ sigla: 'D', label: 'Diurno' },
	{ sigla: 'N', label: 'Noturno' },
	{ sigla: '24h', label: '24 Horas' },
];

const DIAS_SEMANA_OPTIONS = [
	{ value: 1, label: 'Segunda', shortLabel: 'Seg' },
	{ value: 2, label: 'Terça', shortLabel: 'Ter' },
	{ value: 3, label: 'Quarta', shortLabel: 'Qua' },
	{ value: 4, label: 'Quinta', shortLabel: 'Qui' },
	{ value: 5, label: 'Sexta', shortLabel: 'Sex' },
	{ value: 6, label: 'Sábado', shortLabel: 'Sáb' },
	{ value: 7, label: 'Domingo', shortLabel: 'Dom' },
];

const getDefaultMiniTabs = () => ({
	ferias: false,
	atestado: false,
	folga: false,
	licenca: false,
	falta: false,
	troca: false,
	excluir: false,
});

const getDefaultContexto = () => {
	const agora = new Date();
	const ano = agora.getFullYear();
	const mes = String(agora.getMonth() + 1).padStart(2, '0');
	return {
		estabelecimento: '',
		setor: '',
		mes_referencia: `${ano}-${mes}-01`,
	};
};

const PROFESSIONAL_MANAGEMENT_FILTERS_CACHE_KEY =
	'escala.profissionais.filters.cache';
const MOSTRAR_GERENCIAR_SOLICITACOES_UI = false;

const buildProfessionalManagementContextKey = ({ scope, contexto } = {}) =>
	[
		scope || '',
		getEstabelecimentoIdValue(contexto?.estabelecimento),
		String(contexto?.setor || '').trim(),
		String(contexto?.mes_referencia || '').trim(),
	].join('|');

function getEstabelecimentoIdValue(value = '') {
	if (value && typeof value === 'object') {
		return String(
			value?.id ||
				value?.cnes ||
				value?.cnes_estabelecimento ||
				value?.codigo ||
				value?.value ||
				'',
		).trim();
	}

	return String(value || '').trim();
}

const getCachedProfessionalManagementFilters = () => {
	try {
		const raw = localStorage.getItem(
			PROFESSIONAL_MANAGEMENT_FILTERS_CACHE_KEY,
		);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch (_error) {
		return null;
	}
};

const persistCachedProfessionalManagementFilters = (filters = {}) => {
	try {
		localStorage.setItem(
			PROFESSIONAL_MANAGEMENT_FILTERS_CACHE_KEY,
			JSON.stringify(filters),
		);
	} catch (_error) {
		// no-op
	}
};

const getIssueMessages = (result = {}) => {
	const issueLists = [
		result?.issues,
		result?.erros,
		result?.error?.issues,
		result?.error?.erros,
		result?.error?.data?.issues,
		result?.error?.data?.erros,
		result?.error?.detail?.issues,
		result?.response?.data?.issues,
		result?.response?.data?.erros,
	].filter(Array.isArray);

	return Array.from(
		new Map(
			issueLists
				.flat()
				.map((issue = {}) => String(issue?.mensagem || '').trim())
				.filter(Boolean)
				.map((message) => [normalizeCode(message), message]),
		).values(),
	);
};

export const getErrorMessage = (result, fallback) => {
	const issueMessages = getIssueMessages(result);
	if (issueMessages.length > 0) return issueMessages.join(' ');

	const detail =
		result?.mensagem ||
		result?.error?.detail ||
		result?.error?.mensagem ||
		result?.error?.data?.mensagem;
	const messages = [];
	const visit = (value) => {
		if (typeof value === 'string' || typeof value === 'number') {
			const message = String(value).trim();
			if (message) messages.push(message);
			return;
		}
		if (Array.isArray(value)) {
			value.forEach(visit);
			return;
		}
		if (value && typeof value === 'object') {
			Object.values(value).forEach(visit);
		}
	};

	visit(detail);
	const uniqueMessages = Array.from(
		new Map(
			messages.map((message) => [normalizeCode(message), message]),
		).values(),
	);
	return uniqueMessages.join(' ') || fallback;
};

const getApiErrorPayload = (result = {}) =>
	result?.error?.data ||
	result?.response?.data ||
	result?.error ||
	result ||
	{};

const getNonRetryableValidationError = (result = {}) => {
	const payload = getApiErrorPayload(result);
	const status = Number(payload?.status || payload?.status_code || 0);
	return status === 400 && payload?.retryable === false ? payload : null;
};

const isPreviewOrSnapshotOutdated = (result = {}) => {
	const payload = getApiErrorPayload(result);
	const status = Number(payload?.status || payload?.status_code || 0);
	const codigo = normalizeCode(
		payload?.codigo || payload?.code || result?.error?.code || '',
	);
	return (
		status === 409 ||
		codigo === 'PREVIEW_DESATUALIZADO' ||
		codigo.includes('SNAPSHOT_DESATUALIZADO')
	);
};

const getMotivoAcaoIndisponivel = (acao = {}) => {
	const motivo = String(acao?.motivo || '').trim();
	const codigo = String(acao?.motivo_codigo || '').trim();
	if (motivo && codigo) return `${motivo} (${codigo})`;
	return motivo || codigo;
};

const createApiResponseError = (result = {}, fallback = '') => {
	const error = new Error(getErrorMessage(result, fallback) || fallback);
	error.apiResult = result;
	return error;
};

// Mensagens de falha só podem ser apresentadas quando uma chamada realmente
// retornou erro. Erros locais de composição/preview não devem virar alertas na
// interface, pois não representam uma requisição mal-sucedida.
const isApiRequestFailure = (result = {}) => {
	const payload = result?.apiResult || result || {};
	return Boolean(
		payload?.error ||
		payload?.response?.error ||
		payload?.error?.response ||
		payload?.response?.status >= 400,
	);
};

const getProfissionalApiFallbackMessage = ({ isEditing = false } = {}) =>
	isEditing
		? 'Não foi possível aplicar essa mudança. Tente novamente mais tarde.'
		: 'Não foi possível concluir a criação do profissional. Tente novamente mais tarde.';

const getProfissionalHorarioSobrepostoPayload = (result = {}) => {
	const candidates = [
		result,
		result?.apiResult,
		result?.apiResult?.error,
		result?.apiResult?.error?.data,
		result?.error?.data,
		result?.error?.data?.erro,
		result?.error?.detail,
		result?.error?.detail?.erro,
		result?.error?.detail?.data,
		result?.error?.detail?.data?.erro,
		result?.error?.response?.data,
		result?.error?.response?.data?.erro,
		result?.error,
		result?.response?.data,
		result?.response?.data?.erro,
		result?.erro,
		result?.data,
		result?.data?.erro,
	];
	const hasConflictList = (item) =>
		Boolean(
			item &&
			typeof item === 'object' &&
			(Array.isArray(item?.conflitos) ||
				Array.isArray(item?.conflitos_agrupados) ||
				Array.isArray(item?.erros)),
		);
	const hasConflictCode = (item) => {
		if (!item || typeof item !== 'object') return false;
		const code = normalizeConflictText(item?.codigo || item?.code || '');
		return (
			code === 'profissional_horario_sobreposto' ||
			code.includes('horario_sobreposto') ||
			code.includes('jornada_sobreposta')
		);
	};
	const hasConflictMessage = (item) => {
		if (!item || typeof item !== 'object') return false;
		return [
			item?.mensagem,
			item?.message,
			item?.detalhe,
			item?.detail,
			item?.resumo?.mensagem,
		]
			.map(normalizeConflictText)
			.some(
				(message) =>
					(message.includes('jornada') &&
						message.includes('sobrepost')) ||
					(message.includes('horario') &&
						message.includes('sobrepost')) ||
					message.includes('padroes desta alocacao'),
			);
	};
	const hasConflictEntry = (item) =>
		[
			...(Array.isArray(item?.conflitos) ? item.conflitos : []),
			...(Array.isArray(item?.conflitos_agrupados)
				? item.conflitos_agrupados
				: []),
			...(Array.isArray(item?.erros) ? item.erros : []),
			...(Array.isArray(item?.issues) ? item.issues : []),
		].some((entry) => hasConflictCode(entry) || hasConflictMessage(entry));

	return (
		candidates.find(
			(item) =>
				(hasConflictCode(item) || hasConflictMessage(item) || hasConflictEntry(item)) &&
				hasConflictList(item),
		) ||
		candidates.find((item) => hasConflictCode(item) || hasConflictMessage(item)) ||
		null
	);
};

const normalizeConflictText = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const formatDiaSemanaConflito = (value = '') => {
	const diaNumerico = Number(value);
	if (Number.isInteger(diaNumerico) && diaNumerico >= 1 && diaNumerico <= 7) {
		return DIAS_SEMANA_OPTIONS.find(
			(option) => Number(option?.value) === diaNumerico,
		)?.label || `Dia ${diaNumerico}`;
	}
	const normalized = normalizeConflictText(value);
	if (normalized.includes('segunda')) return 'Segunda-feira';
	if (normalized.includes('terca')) return 'Terça-feira';
	if (normalized.includes('quarta')) return 'Quarta-feira';
	if (normalized.includes('quinta')) return 'Quinta-feira';
	if (normalized.includes('sexta')) return 'Sexta-feira';
	if (normalized.includes('sabado')) return 'Sábado';
	if (normalized.includes('domingo')) return 'Domingo';
	return String(value || 'Dia não informado').trim();
};

const getSetorConflitoLabel = (conflito = {}) => {
	const nestedConflito =
		conflito?.alocacao_conflitante ||
		conflito?.alocacao ||
		conflito?.item ||
		conflito?.conflito ||
		(Array.isArray(conflito?.alocacoes_conflitantes)
			? conflito.alocacoes_conflitantes[0]
			: null) ||
		(Array.isArray(conflito?.ocorrencias)
			? conflito.ocorrencias[0]
			: null) ||
		{};
	const setor =
		conflito?.setor ||
		conflito?.setor_nome ||
		conflito?.nome_setor ||
		conflito?.nome_setor_exibicao ||
		conflito?.setor_exibicao ||
		conflito?.setor_identificador ||
		conflito?.identificador_setor ||
		conflito?.setor?.nome_exibicao ||
		conflito?.setor?.nome ||
		conflito?.setor?.identificador ||
		nestedConflito?.setor ||
		nestedConflito?.setor_nome ||
		nestedConflito?.nome_setor ||
		nestedConflito?.nome_setor_exibicao ||
		nestedConflito?.setor_exibicao ||
		nestedConflito?.setor_identificador ||
		nestedConflito?.identificador_setor ||
		nestedConflito?.setor?.nome_exibicao ||
		nestedConflito?.setor?.nome ||
		nestedConflito?.setor?.identificador ||
		'';

	const setorLabel =
		setor && typeof setor === 'object'
			? setor?.nome_exibicao || setor?.nome || setor?.identificador || ''
			: setor;
	return String(setorLabel || 'Setor não informado').trim();
};

const buildProfissionalHorarioSobrepostoDisplay = (result = {}) => {
	const payload = getProfissionalHorarioSobrepostoPayload(result);
	if (!payload) return null;

	const conflitosAgrupados = Array.isArray(payload?.conflitos_agrupados)
		? payload.conflitos_agrupados
		: [];
	const conflitosDiretos = Array.isArray(payload?.conflitos)
		? payload.conflitos
		: [];
	const errosConflito = Array.isArray(payload?.erros)
		? payload.erros.filter((erro = {}) => {
				const code = String(erro?.codigo || erro?.code || '').trim();
				return code === 'PROFISSIONAL_HORARIO_SOBREPOSTO';
			})
		: [];
	const conflitos =
		conflitosAgrupados.length > 0
			? conflitosAgrupados
			: conflitosDiretos.length > 0
				? conflitosDiretos
				: errosConflito;
	const renderedMessage = String(payload?.mensagem_renderizada || '').trim();
	const renderedLines = renderedMessage
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean);
	const renderedConflictLines = renderedLines.filter(
		(line) =>
			line.startsWith('•') ||
			normalizeConflictText(line).includes('ocorrencia'),
	);
	const titulo = 'Criação impedida por conflito de escala';
	const mensagem =
		'O profissional já está alocado em outro setor, e a nova escala conflita com uma jornada já criada.';

	const conflictItems = Array.from(
		new Map(
			conflitos.map((conflito = {}) => {
				const dia = formatDiaSemanaConflito(
					conflito?.nome_dia_semana || conflito?.rotulo,
				);
				const inicio = String(conflito?.hora_inicio || '--:--').slice(
					0,
					5,
				);
				const fim = String(conflito?.hora_fim || '--:--').slice(0, 5);
				const setor = getSetorConflitoLabel(conflito);
				const key = [
					normalizeConflictText(dia),
					inicio,
					fim,
					normalizeConflictText(setor),
				].join('|');

				return [
					key,
					{
						dia,
						horario: `${inicio} às ${fim}`,
						setor,
						ocorrencias:
							conflito?.quantidade_ocorrencias ||
							conflito?.total_ocorrencias ||
							conflito?.total_conflitos ||
							conflito?.ocorrencias_count ||
							'',
					},
				];
			}),
		).values(),
	);

	return {
		titulo,
		mensagem,
		renderedMessage,
		renderedConflictLines,
		conflitos: conflictItems,
		acao: '',
	};
};

const formatProfissionalHorarioSobrepostoMessage = (result = {}) => {
	const display = buildProfissionalHorarioSobrepostoDisplay(result);
	if (!display) return '';

	if (display.renderedMessage) {
		return display.renderedMessage;
	}

	return [
		display.titulo,
		display.mensagem,
		...display.conflitos.map(
			(conflito) =>
				`• ${conflito.dia}, ${conflito.horario} - ${conflito.setor}`,
		),
	]
		.filter(Boolean)
		.join('\n\n');
};

const getServerErrorDisplayMessage = (result = {}) => {
	const candidates = [
		result?.error,
		result?.response?.error,
		result?.response?.data,
		result?.error?.data,
		result,
	];
	const serverError = candidates.find((item = {}) => {
		const status = Number(item?.status || item?.status_code || 0);
		return status >= 500 && status <= 599;
	});
	if (!serverError) return '';

	const status = Number(
		serverError?.status || serverError?.status_code || 500,
	);
	return `Não foi possível aplicar a alteração: ocorreu um erro no servidor (${status}). Acione o suporte para obter ajuda.`;
};

const getFriendlyProfissionalApiErrorMessage = ({
	result,
	isEditing = false,
} = {}) => {
	const fallback = getProfissionalApiFallbackMessage({ isEditing });
	const horarioSobrepostoMessage =
		formatProfissionalHorarioSobrepostoMessage(result);

	if (horarioSobrepostoMessage) return horarioSobrepostoMessage;
	const issueMessage = getIssueMessages(result).join(' ');
	if (issueMessage) return issueMessage;

	const serverErrorMessage = getServerErrorDisplayMessage(result);
	if (serverErrorMessage) return serverErrorMessage;

	const rawMessage = String(getErrorMessage(result, '') || '').trim();

	const erroValidacao = [
		result?.error,
		result?.response?.error,
		result?.response?.data,
		result?.error?.data,
		result,
	].find((item = {}) => {
		const status = Number(item?.status || item?.status_code || 0);
		return status === 400 && item?.retryable === false;
	});

	// Um 400 não-retryable é uma validação da intenção enviada. A mensagem
	// precisa orientar a correção no formulário, jamais sugerir indisponibilidade
	// temporária ou uma nova tentativa idêntica mais tarde.
	if (erroValidacao) {
		return rawMessage || 'Revise os dados informados antes de confirmar.';
	}

	if (!rawMessage) return fallback;

	const normalizedMessage = normalizeCode(rawMessage);
	const hasSnakeCaseToken = /[a-z]+_[a-z]+/i.test(rawMessage);
	const isTechnicalMessage =
		hasSnakeCaseToken ||
		normalizedMessage.includes('ALTERAR_REGIME') ||
		normalizedMessage.includes('VIGENCIA DA LINHA') ||
		normalizedMessage.includes('IDEMPOT') ||
		normalizedMessage.includes('CONSTRAINT') ||
		normalizedMessage.includes('UNIQUE') ||
		normalizedMessage.includes('FOREIGN KEY') ||
		normalizedMessage.includes('SQLSTATE') ||
		normalizedMessage.includes('TRACEBACK') ||
		normalizedMessage.includes('STACK TRACE') ||
		normalizedMessage.includes('INTERNAL SERVER ERROR');

	return isTechnicalMessage ? fallback : rawMessage;
};

const hasNamedPermission = (permissions = [], permissionName = '') => {
	const target = String(permissionName || '')
		.trim()
		.toLowerCase();
	if (!target) return false;

	const normalizedPermissions = Array.isArray(permissions)
		? permissions
		: [permissions];

	return normalizedPermissions.some(
		(permission) =>
			String(permission || '')
				.trim()
				.toLowerCase() === target,
	);
};

const normalizeCpf = (value = '') =>
	String(value).replace(/\D/g, '').slice(0, 11);

const normalizeDigits = (value = '', max = Infinity) =>
	String(value).replace(/\D/g, '').slice(0, max);

const formatCpfMask = (value = '') => {
	const digits = normalizeCpf(value);
	if (!digits) return '';
	if (digits.length <= 3) return digits;
	if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
	if (digits.length <= 9)
		return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
	return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
};

const formatPhoneMask = (value = '') => {
	const digits = normalizeDigits(value, 11);
	if (!digits) return '';
	if (digits.length <= 2) return `(${digits}`;
	if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
	if (digits.length <= 10) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
	}
	return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const formatConselhoNumber = (value = '') => normalizeDigits(value, 20);

const formatNumericId = (value = '', max = 10) => normalizeDigits(value, max);

const formatProfessionalNameInput = (value = '', { trimEnd = false } = {}) => {
	const sanitized = String(value || '')
		.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, '')
		.replace(/\s+/g, ' ')
		.trimStart();
	return trimEnd ? sanitized.trimEnd() : sanitized;
};

export const formatDateDisplayMask = (value = '') => {
	const raw = String(value || '').trim();
	const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (isoMatch) {
		return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
	}

	const digits = normalizeDigits(value, 8);
	if (digits.length <= 2) return digits;
	if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
	return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const normalizeSearchValue = (value = '') =>
	String(value)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const normalizeVinculoOptionName = (value = '') =>
	normalizeSearchValue(value).replace(/\s+/g, ' ');

const findVinculoOpcaoByNome = (opcoes = [], nome = '') => {
	const target = normalizeVinculoOptionName(nome);
	if (!target) return null;

	return (
		(opcoes || []).find((option = {}) => {
			const raw = option?.raw || {};
			const aliases = [
				option?.label,
				raw?.nome,
				raw?.label,
				raw?.descricao,
				raw?.titulo,
				raw?.nome_vinculo,
				raw?.vinculo_nome,
				raw?.descricao_vinculo,
				raw?.tipo?.nome,
			];

			return aliases.some(
				(alias) => normalizeVinculoOptionName(alias) === target,
			);
		}) || null
	);
};

const isPlantonistaRegime = (value = '') =>
	normalizeCode(value) === 'PLANTONISTA';

const isRotinaRegime = (value = '') => normalizeCode(value) === 'ROTINA';

const usesWeekdayScheduleRegime = (value = '') =>
	isPlantonistaRegime(value) || isRotinaRegime(value);

const isWeeklyRecorrencia = (value = '') => normalizeCode(value) === 'SEMANAL';

const isSpecificDatesRecorrencia = (value = '') =>
	normalizeCode(value) === 'DATAS_ESPECIFICAS';

const getLockedWeekdayFromStartDate = ({
	regime = '',
	startDate = '',
} = {}) => {
	if (!usesWeekdayScheduleRegime(regime)) return null;
	return getIsoWeekdayFromValue(startDate);
};

const getEffectiveDiasSemanaSelection = ({ dias_semana = [] } = {}) => {
	// A vigência define quando a regra passa a valer; ela não é uma seleção de
	// dia da semana. Acrescentar o dia da data inicial aqui fazia o payload de
	// uma regra, por exemplo [segunda, quarta], ganhar uma terça silenciosa.
	return normalizeDiasSemanaValues(dias_semana);
};

const syncDiasSemanaWithStartDateSeed = ({
	dias_semana = [],
	previousStartDate = '',
	nextStartDate = '',
} = {}) => {
	const diasAtuais = normalizeDiasSemanaValues(dias_semana);
	const previousWeekday = getIsoWeekdayFromValue(previousStartDate);
	const nextWeekday = getIsoWeekdayFromValue(nextStartDate);
	const diasManuais = diasAtuais.filter((item) => item !== previousWeekday);

	if (!nextWeekday) return diasManuais;

	return normalizeDiasSemanaValues([...diasManuais, nextWeekday]);
};

const getIsoWeekdayFromValue = (value = '') => {
	const date = parseISODateLocal(value);
	if (!date) return null;
	const jsDay = date.getDay();
	return jsDay === 0 ? 7 : jsDay;
};

const getRequiredWeekdayFromStartDate = (startDate = '') =>
	getIsoWeekdayFromValue(startDate);

const getDiasSemanaWithRequiredStartDate = ({
	dias_semana = [],
	startDate = '',
} = {}) => {
	const dias = normalizeDiasSemanaValues(dias_semana);
	const requiredWeekday = getRequiredWeekdayFromStartDate(startDate);

	if (!requiredWeekday) return dias;

	return normalizeDiasSemanaValues([...dias, requiredWeekday]);
};

const normalizePresetCode = (value = '') =>
	normalizeCode(value)
		.replace(/\s+/g, '')
		.replace(/^CICLO_/, '');

const REGIME_PRESET_DEFAULTS = {
	'12X36': {
		tipo_recorrencia: 'CICLO_CONTINUO',
		trabalho_horas: 12,
		descanso_horas: 36,
		hora_inicio: '07:00',
		hora_fim: '19:00',
	},
	'12X60': {
		tipo_recorrencia: 'CICLO_CONTINUO',
		trabalho_horas: 12,
		descanso_horas: 60,
		hora_inicio: '07:00',
		hora_fim: '19:00',
	},
	'24X72': {
		tipo_recorrencia: 'CICLO_CONTINUO',
		trabalho_horas: 24,
		descanso_horas: 72,
		hora_inicio: '07:00',
		hora_fim: '07:00',
	},
	'24X120': {
		tipo_recorrencia: 'CICLO_CONTINUO',
		trabalho_horas: 24,
		descanso_horas: 120,
		hora_inicio: '07:00',
		hora_fim: '07:00',
	},
	SEMANAL: {
		tipo_recorrencia: 'SEMANAL',
	},
	PERSONALIZADO: {
		tipo_recorrencia: 'CICLO_CONTINUO',
	},
};

const RECORRENCIA_NOME_OPTIONS = [
	{
		value: 'SEMANAL',
		label: 'Semanal',
		tipo_recorrencia: 'SEMANAL',
		preset_ciclo: 'SEMANAL',
	},
	{
		value: 'CICLO_12X36',
		label: 'Ciclo-12x36',
		tipo_recorrencia: 'CICLO_CONTINUO',
		preset_ciclo: '12x36',
	},
	{
		value: 'CICLO_12X60',
		label: 'Ciclo-12x60',
		tipo_recorrencia: 'CICLO_CONTINUO',
		preset_ciclo: '12x60',
	},
];

const TURNOS_DOZE_HORAS_OPTIONS = [
	{
		value: '06:00-18:00',
		label: '06h às 18h',
		inicio: '06:00',
		fim: '18:00',
	},
	{
		value: '07:00-19:00',
		label: '07h às 19h',
		inicio: '07:00',
		fim: '19:00',
	},
	{
		value: '08:00-20:00',
		label: '08h às 20h',
		inicio: '08:00',
		fim: '20:00',
	},
	{
		value: '18:00-06:00',
		label: '18h às 06h',
		inicio: '18:00',
		fim: '06:00',
	},
	{
		value: '19:00-07:00',
		label: '19h às 07h',
		inicio: '19:00',
		fim: '07:00',
	},
	{
		value: '20:00-08:00',
		label: '20h às 08h',
		inicio: '20:00',
		fim: '08:00',
	},
];

const buildRecorrenciaNomeOptionsFromPresets = (presets = []) => {
	const presetList = Array.isArray(presets) ? presets : [];
	const cycleOptions = presetList
		.filter((item) => Boolean(item?.codigo))
		.filter((item) => {
			const codigo = normalizeCode(item?.codigo || '');
			return ['12X36', '12X60', 'ROTACAO_PERIODICA'].includes(codigo);
		})
		.map((item) => {
			const codigo = String(item?.codigo || '').trim();
			const normalizedCodigo = normalizeCode(codigo);
			return {
				value:
					normalizedCodigo === 'ROTACAO_PERIODICA'
						? 'ROTACAO_PERIODICA'
						: `CICLO_${normalizedCodigo}`,
				label:
					normalizedCodigo === 'ROTACAO_PERIODICA'
						? String(item?.nome || 'Rotação periódica').trim()
						: `Ciclo-${String(item?.nome || codigo).trim()}`,
				tipo_recorrencia:
					normalizedCodigo === 'ROTACAO_PERIODICA'
						? 'ROTACAO_PERIODICA'
						: 'CICLO_CONTINUO',
				preset_ciclo: codigo,
			};
		});

	if (!cycleOptions.length) {
		return RECORRENCIA_NOME_OPTIONS;
	}

	return [RECORRENCIA_NOME_OPTIONS[0], ...cycleOptions];
};

const buildRecorrenciaNomeOptionsFromModalidades = (modalidades = []) => {
	const catalogo = Array.isArray(modalidades) ? modalidades : [];
	const planejaveis = catalogo.filter((item) => item?.planejamento_assistido);
	const options = (planejaveis.length ? planejaveis : catalogo)
		.filter((item) => Boolean(item?.codigo || item?.preset_ciclo))
		.map((item) => {
			const preset = String(
				item?.preset_ciclo || item?.codigo || '',
			).trim();
			const normalizedPreset = normalizeCode(preset);
			const tipoDeclarado = normalizeCode(item?.tipo_recorrencia || '');
			const tipo = [
				'ROTACAO_PERIODICA',
				'SEMANAL',
				'DATAS_ESPECIFICAS',
				'CICLO_CONTINUO',
			].includes(tipoDeclarado)
				? tipoDeclarado
				: normalizedPreset === 'ROTACAO_PERIODICA'
					? 'ROTACAO_PERIODICA'
					: normalizedPreset === 'SEMANAL'
						? 'SEMANAL'
						: 'CICLO_CONTINUO';
			return {
				value:
					normalizedPreset === 'ROTACAO_PERIODICA'
						? 'ROTACAO_PERIODICA'
						: normalizedPreset === 'SEMANAL'
							? 'SEMANAL'
							: tipo === 'DATAS_ESPECIFICAS'
								? 'DATAS_ESPECIFICAS'
								: `CICLO_${normalizedPreset}`,
				label: String(item?.nome || preset).trim(),
				tipo_recorrencia: tipo,
				preset_ciclo: preset,
			};
		})
		.filter((item) =>
			['12X36', '12X60', 'SEMANAL', 'ROTACAO_PERIODICA'].includes(
				normalizeCode(item?.preset_ciclo),
			),
		);

	return options.length ? options : null;
};

const ensureNursingRotationRecorrenciaOption = (options = []) => {
	const normalizedOptions = Array.isArray(options) ? options : [];
	if (
		normalizedOptions.some(
			(item) =>
				normalizeCode(item?.tipo_recorrencia || item?.value || '') ===
					'ROTACAO_PERIODICA' ||
				normalizeCode(item?.preset_ciclo || '') === 'ROTACAO_PERIODICA',
		)
	) {
		return normalizedOptions;
	}

	return [
		...normalizedOptions,
		{
			value: 'ROTACAO_PERIODICA',
			label: '12H (segunda à sexta)',
			tipo_recorrencia: 'ROTACAO_PERIODICA',
			preset_ciclo: 'ROTACAO_PERIODICA',
		},
	];
};

const ensureNursing24x120RecorrenciaOption = (options = []) => {
	const normalizedOptions = Array.isArray(options) ? options : [];
	if (
		normalizedOptions.some((item = {}) =>
			[item?.value, item?.preset_ciclo].some(
				(value) => normalizePresetCode(value) === '24X120',
			),
		)
	) {
		return normalizedOptions;
	}

	return [
		...normalizedOptions,
		{
			value: 'CICLO_24X120',
			label: 'Plantão 24x120',
			tipo_recorrencia: 'CICLO_CONTINUO',
			preset_ciclo: '24x120',
		},
	];
};

// Os códigos continuam sendo aqueles definidos pelo catálogo/API. A
// enfermagem só apresenta uma nomenclatura mais adequada para o fluxo da
// tela: o 12x36 isolado não é uma escolha e a rotação periódica representa
// a opção de 12x36 de segunda a sexta.
const getNursingRecorrenciaNomeOptions = (options = []) =>
	ensureNursingRotationRecorrenciaOption(
		ensureNursing24x120RecorrenciaOption(options),
	)
		.filter((item = {}) => {
			const isDozePorTrintaSeis = [item?.value, item?.preset_ciclo].some(
				(valor) =>
					normalizeCode(valor || '').replace(/^(CICLO_)+/, '') ===
					'12X36',
			);
			return !isDozePorTrintaSeis;
		})
		.map((item = {}) => {
			const isRotacaoPeriodica =
				normalizeCode(item?.tipo_recorrencia || '') ===
					'ROTACAO_PERIODICA' ||
				[item?.value, item?.preset_ciclo].some(
					(valor) =>
						normalizeCode(valor || '').replace(/^(CICLO_)+/, '') ===
						'ROTACAO_PERIODICA',
				);
			if (isRotacaoPeriodica) {
				return { ...item, label: '12H (segunda à sexta)' };
			}
			if (
				normalizePresetCode(item?.preset_ciclo || item?.value || '') ===
				'24X120'
			) {
				return { ...item, label: 'Plantão 24x120' };
			}

			return normalizeCode(item?.tipo_recorrencia || '') === 'SEMANAL'
				? { ...item, label: 'Semanal' }
				: item;
		})
		.sort((first = {}, second = {}) => {
			const isFirstWeekday12h =
				normalizeCode(first?.tipo_recorrencia || '') ===
					'ROTACAO_PERIODICA' ||
				normalizeCode(first?.preset_ciclo || '') ===
					'ROTACAO_PERIODICA';
			const isSecondWeekday12h =
				normalizeCode(second?.tipo_recorrencia || '') ===
					'ROTACAO_PERIODICA' ||
				normalizeCode(second?.preset_ciclo || '') ===
					'ROTACAO_PERIODICA';
			return Number(isSecondWeekday12h) - Number(isFirstWeekday12h);
		});

const getCanonicalPresetCiclo = (preset = '') => {
	const normalizedPreset = normalizePresetCode(preset);
	if (normalizedPreset === 'SEMANAL') return 'SEMANAL';
	if (normalizedPreset === 'DATAS_ESPECIFICAS') return 'DATAS_ESPECIFICAS';
	if (normalizedPreset === 'ROTACAO_PERIODICA') return 'ROTACAO_PERIODICA';
	if (normalizedPreset === 'PERSONALIZADO') return 'PERSONALIZADO';
	if (normalizedPreset)
		return `CICLO_${normalizedPreset.replace(/[^A-Z0-9]/g, '_')}`;
	return '';
};

const getPayloadPresetCiclo = (preset = '') => {
	const normalizedPreset = normalizePresetCode(preset);
	if (normalizedPreset === 'SEMANAL') return 'SEMANAL';
	if (normalizedPreset === 'ROTACAO_PERIODICA') return 'ROTACAO_PERIODICA';
	if (
		normalizedPreset === 'DATAS_ESPECIFICAS' ||
		normalizedPreset === 'PERSONALIZADO'
	) {
		return 'PERSONALIZADO';
	}
	if (normalizedPreset === '12X36') return '12x36';
	if (normalizedPreset === '12X60') return '12x60';
	if (normalizedPreset === '24X72') return '24x72';
	const numericCycleMatch = normalizedPreset.match(/^(\d+)X(\d+)$/);
	if (numericCycleMatch) {
		return `${numericCycleMatch[1]}x${numericCycleMatch[2]}`;
	}
	return preset
		? String(preset)
				.replace(/^CICLO_/i, '')
				.replace(/^(\d+)X(\d+)$/i, '$1x$2')
		: '';
};

const getPresetLookupKeys = (preset = '') => {
	const keys = [
		preset,
		normalizeCode(preset),
		normalizePresetCode(preset),
		getPayloadPresetCiclo(preset),
		getCanonicalPresetCiclo(preset),
	];

	const normalized = normalizeCode(preset);
	if (normalized) keys.push(`CICLO_${normalized}`);

	return Array.from(
		new Set(keys.map((key) => String(key || '').trim()).filter(Boolean)),
	);
};

const getRecorrenciaOptionByValue = (
	value = '',
	options = RECORRENCIA_NOME_OPTIONS,
) =>
	(options || []).find(
		(item) => normalizeCode(item?.value || '') === normalizeCode(value),
	) || null;

export const getRecorrenciaDisplayLabel = ({
	value = '',
	options = RECORRENCIA_NOME_OPTIONS,
	fallback = '',
} = {}) =>
	getRecorrenciaOptionByValue(value, options)?.label ||
	getRecorrenciaOptionByValue(value)?.label ||
	fallback ||
	String(value || '');

const getRecorrenciaNomeValue = ({
	tipo_recorrencia = '',
	preset_ciclo = '',
	padroes_alocacao = [],
} = {}) => {
	const padraoBase = Array.isArray(padroes_alocacao)
		? padroes_alocacao[0]
		: null;
	const tipo = normalizeCode(
		padraoBase?.tipo_recorrencia || tipo_recorrencia || '',
	);
	const presetDoPadrao = getCanonicalPresetCiclo(padraoBase?.preset_ciclo);
	const presetInformado = getCanonicalPresetCiclo(preset_ciclo);
	// Um padrão individual pode vir marcado genericamente como PERSONALIZADO,
	// enquanto a alocação já possui o ciclo canônico calculado pela API.
	// Preserve o ciclo específico nesse caso para o select exibir 12x36,
	// 12x60 etc., em vez do rótulo genérico.
	const preset =
		presetDoPadrao === 'PERSONALIZADO' &&
		presetInformado &&
		presetInformado !== 'PERSONALIZADO'
			? presetInformado
			: presetDoPadrao || presetInformado || '';

	if (tipo === 'SEMANAL') return 'SEMANAL';
	if (tipo === 'DATAS_ESPECIFICAS') return 'DATAS_ESPECIFICAS';
	if (tipo === 'ROTACAO_PERIODICA' || preset === 'ROTACAO_PERIODICA') {
		return 'ROTACAO_PERIODICA';
	}
	if (tipo === 'CICLO_CONTINUO') {
		return preset;
	}

	return '';
};

const isWeekdayDateValue = (value = '') => {
	const iso = formatDateInputToISO(value);
	if (!iso) return false;
	const weekday = getIsoWeekdayFromValue(iso);
	return weekday >= 1 && weekday <= 5;
};

const inferCycleDiasSemanaSelection = ({
	presetCiclo = '',
	startDate = '',
	mesReferencia = '',
} = {}) => {
	const canonicalPreset = getPayloadPresetCiclo(presetCiclo);
	const cycleMatch = String(canonicalPreset || '').match(/^(\d+)x(\d+)$/i);
	const stepDays = cycleMatch
		? Math.max(
				1,
				Math.round(
					(Number(cycleMatch[1]) + Number(cycleMatch[2])) / 24,
				),
			)
		: 0;
	const startIso =
		formatDateInputToISO(startDate) || formatDateInputToISO(mesReferencia);
	if (!stepDays || !startIso) return [];

	const endIso = addDaysToISODate(startIso, 6);
	let cursorIso = startIso;
	const dias = new Set();

	while (cursorIso && cursorIso <= endIso) {
		const weekday = getIsoWeekdayFromValue(cursorIso);
		if (weekday >= 1 && weekday <= 5) dias.add(weekday);
		cursorIso = addDaysToISODate(cursorIso, stepDays);
	}

	return normalizeDiasSemanaValues(Array.from(dias));
};

const inferDiasSemanaFromLinha = (linha = {}) => {
	const dias = new Set();
	const celulas = Array.isArray(linha?.celulas) ? linha.celulas : [];

	celulas.forEach((celula = {}) => {
		const cards = Array.isArray(celula?.cards) ? celula.cards : [];
		const status = normalizeCode(celula?.status || '');
		const hasPresenca =
			cards.length > 0 ||
			status === 'ATRIBUIDO' ||
			status === 'SUGERIDO' ||
			status === 'VACANCIA';

		if (!hasPresenca) return;

		const weekday = getIsoWeekdayFromValue(
			celula?.data || cards[0]?.data || cards[0]?.horario?.data || '',
		);
		if (weekday) dias.add(weekday);
	});

	return Array.from(dias).sort((a, b) => a - b);
};

const normalizeDiasSemanaValues = (value) =>
	(Array.isArray(value) ? value : [])
		.map((item) => Number(item))
		.filter(
			(item, index, array) =>
				Number.isInteger(item) &&
				item >= 1 &&
				item <= 7 &&
				array.indexOf(item) === index,
		)
		.sort((a, b) => a - b);

export const buildWeeklyOccurrencesFromEditableSchedule = ({
	diasSemana = [],
	horariosByDia = {},
} = {}) =>
	normalizeDiasSemanaValues(diasSemana)
		.map((diaSemana) => {
			const horario = horariosByDia?.[diaSemana] || {};
			return {
				dia_semana: diaSemana,
				hora_inicio: formatHourInputToTimeValue(
					horario?.hora_inicio || horario?.horaInicio,
				),
				hora_fim: formatHourInputToTimeValue(
					horario?.hora_fim || horario?.horaFim,
				),
			};
		})
		.filter((horario) => horario.hora_inicio && horario.hora_fim);

const getPresetDefaults = (preset = '') =>
	REGIME_PRESET_DEFAULTS[normalizePresetCode(preset)] || null;

const inferPresetCicloFromRegimeEscala = (regimeEscala = {}) => {
	const presetExplicito = getCanonicalPresetCiclo(regimeEscala?.preset_ciclo);
	// "PERSONALIZADO" é o valor genérico usado pela API para ciclos que não
	// carregam um preset explícito. Quando a própria resposta informa uma
	// combinação conhecida de trabalho/descanso, ela deve aparecer como o
	// ciclo real (por exemplo, 12x36), e não como "Personalizado".
	if (presetExplicito && presetExplicito !== 'PERSONALIZADO') {
		return presetExplicito;
	}

	const tipoRecorrencia = normalizeCode(regimeEscala?.tipo_recorrencia);
	const trabalhoHoras =
		Number(regimeEscala?.trabalho_horas || 0) ||
		(Number(regimeEscala?.trabalho_minutos || 0) > 0
			? Number(regimeEscala?.trabalho_minutos || 0) / 60
			: 0);
	const descansoHoras =
		Number(regimeEscala?.descanso_horas || 0) ||
		(Number(regimeEscala?.descanso_minutos || 0) > 0
			? Number(regimeEscala?.descanso_minutos || 0) / 60
			: 0);

	if (tipoRecorrencia === 'SEMANAL') return 'SEMANAL';
	if (trabalhoHoras === 12 && descansoHoras === 36) return '12x36';
	if (trabalhoHoras === 12 && descansoHoras === 60) return '12x60';
	if (trabalhoHoras === 24 && descansoHoras === 72) return '24x72';
	if (trabalhoHoras === 24 && descansoHoras === 120) return '24x120';
	return presetExplicito || 'PERSONALIZADO';
};

const getTipoRecorrenciaFallbackByRegime = (regime = '') =>
	usesWeekdayScheduleRegime(regime) ? 'SEMANAL' : 'CICLO_CONTINUO';

const inferContinuousPresetFromShift = ({
	hora_inicio = '',
	hora_fim = '',
	fallbackPreset = '',
} = {}) => {
	const fallbackPayloadPreset = getPayloadPresetCiclo(fallbackPreset);
	if (fallbackPayloadPreset && fallbackPayloadPreset !== 'PERSONALIZADO') {
		return fallbackPayloadPreset;
	}

	const shiftIndicator = getShiftIndicator({ hora_inicio, hora_fim });
	if (shiftIndicator === '24h') return '24x72';
	if (shiftIndicator === 'D' || shiftIndicator === 'N') return '12x36';
	return fallbackPayloadPreset || 'PERSONALIZADO';
};

const calculateShiftDurationHours = (horaInicio = '', horaFim = '') => {
	const inicio = String(horaInicio || '').trim();
	const fim = String(horaFim || '').trim();
	if (!inicio || !fim) return 0;

	const [inicioHora, inicioMinuto] = inicio.split(':').map(Number);
	const [fimHora, fimMinuto] = fim.split(':').map(Number);
	if (
		![inicioHora, inicioMinuto, fimHora, fimMinuto].every((item) =>
			Number.isFinite(item),
		)
	) {
		return 0;
	}

	const minutosInicio = inicioHora * 60 + inicioMinuto;
	const minutosFim = fimHora * 60 + fimMinuto;
	let diferenca = minutosFim - minutosInicio;
	if (diferenca < 0) diferenca += 24 * 60;
	if (diferenca === 0) diferenca = 24 * 60;

	return Math.round((diferenca / 60) * 100) / 100;
};

const getMinutesFromHour = (value = '') => {
	const normalized = formatHourInputToTimeValue(value);
	if (!normalized || !normalized.includes(':')) return null;
	const [hours, minutes] = normalized.split(':').map(Number);
	if (
		!Number.isFinite(hours) ||
		!Number.isFinite(minutes) ||
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59
	) {
		return null;
	}
	return hours * 60 + minutes;
};

const addMinutesToHour = (value = '', minutesToAdd = 0) => {
	const baseMinutes = getMinutesFromHour(value);
	const extraMinutes = Number(minutesToAdd || 0);
	if (baseMinutes == null || !Number.isFinite(extraMinutes)) return '';
	const total = (baseMinutes + extraMinutes) % (24 * 60);
	const normalizedTotal = total < 0 ? total + 24 * 60 : total;
	const hours = Math.floor(normalizedTotal / 60);
	const minutes = normalizedTotal % 60;
	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const calculateShiftDurationMinutes = (horaInicio = '', horaFim = '') =>
	Math.round(calculateShiftDurationHours(horaInicio, horaFim) * 60);

const getNumeroConselhoFromProfissional = (profissional = {}) =>
	String(
		profissional?.numero_registro ||
			profissional?.registro ||
			profissional?.crm ||
			profissional?.numeroConselho ||
			profissional?.conselho ||
			'',
	)
		.replace(/\D/g, '')
		.slice(0, 20);

const getUfConselhoFromProfissional = (profissional = {}) =>
	String(
		profissional?.uf_conselho ||
			profissional?.ufConselho ||
			profissional?.conselho_uf ||
			profissional?.uf ||
			'',
	)
		.trim()
		.toUpperCase()
		.slice(0, 2);

const getNomeVinculoFromProfissional = (profissional = {}) =>
	String(
		profissional?.nome_vinculo ||
			profissional?.nomeVinculo ||
			profissional?.vinculo_nome ||
			profissional?.vinculoNome ||
			profissional?.vinculo?.nome ||
			'',
	)
		.trim()
		.slice(0, 100);

const getVinculoTipoIdFromProfissional = (profissional = {}) => {
	const vinculo =
		profissional?.vinculo_profissional || profissional?.vinculo || {};
	const vinculoTipo =
		vinculo?.vinculo_tipo ||
		vinculo?.tipo_vinculo ||
		profissional?.vinculo_tipo ||
		profissional?.tipo_vinculo ||
		{};
	const vinculoId =
		typeof vinculo === 'object' && vinculo !== null
			? vinculo?.vinculo_tipo_id ||
				vinculo?.tipo_vinculo_id ||
				vinculoTipo?.id ||
				vinculoTipo?.vinculo_tipo_id ||
				vinculoTipo?.value ||
				vinculo?.id
			: 0;

	return (
		Number(
			profissional?.vinculo_tipo_id ||
				profissional?.vinculo_tipo ||
				vinculoId ||
				0,
		) || 0
	);
};

const formatUnidadeOptionLabel = (unidade = {}) => {
	const nome = String(unidade?.nome || unidade?.label || '').trim();
	const nomeNormalizado = nome
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();

	return nomeNormalizado === 'cer jacarepagua'
		? 'Hospital Cardoso Fontes'
		: nome;
};

const shouldHideMedicalScaleUnit = (unidade = {}) => {
	const nome = String(unidade?.nome || unidade?.label || '').trim();
	const nomeNormalizado = nome
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

	return nomeNormalizado === 'cer andarai';
};

const formatSetorOptionLabel = (setor = {}) =>
	String(
		setor?.nome_exibicao || setor?.nome || setor?.identificador || '',
	).trim();

const SETOR_TODOS_OPTION = {
	id: SETOR_TODOS_VALUE,
	nome: 'Todos',
	nome_exibicao: 'Todos',
	identificador: SETOR_TODOS_VALUE,
	isTodos: true,
};

const isSetorTodosValue = (value = '') =>
	String(value || '') === SETOR_TODOS_VALUE;

const formatDateInputToISO = (value = '') => {
	const raw = String(value || '').trim();
	if (!raw) return '';

	const exact = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (exact?.[0]) return exact[0];

	const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
	if (br) {
		const [, dia, mes, ano] = br;
		return `${ano}-${mes}-${dia}`;
	}

	const parsed = new Date(raw);
	if (Number.isNaN(parsed.getTime())) return '';

	const ano = parsed.getFullYear();
	const mes = String(parsed.getMonth() + 1).padStart(2, '0');
	const dia = String(parsed.getDate()).padStart(2, '0');
	return `${ano}-${mes}-${dia}`;
};

const getInicioSemanaIso = (value = '') => {
	const iso = formatDateInputToISO(value);
	if (!iso) return '';

	const date = new Date(`${iso}T12:00:00Z`);
	if (Number.isNaN(date.getTime())) return '';
	const weekday = date.getUTCDay() || 7;
	date.setUTCDate(date.getUTCDate() - weekday + 1);
	return date.toISOString().slice(0, 10);
};

const formatISOToDisplay = (value = '') => {
	const iso = formatDateInputToISO(value);
	if (!iso) return '';
	const [ano, mes, dia] = iso.split('-');
	return `${dia}/${mes}/${ano}`;
};

const DateMaskInputWithPicker = ({
	value = '',
	onChange,
	placeholder = '00/00/0000',
	className = '',
	ariaLabel = 'Selecionar data',
	disabled = false,
}) => {
	const pickerRef = useRef(null);
	const isoValue = formatDateInputToISO(value);

	return (
		<div className='relative'>
			<input
				type='text'
				placeholder={placeholder}
				value={formatDateDisplayMask(value)}
				onChange={(event) =>
					onChange?.(formatDateDisplayMask(event.target.value))
				}
				disabled={disabled}
				inputMode='numeric'
				maxLength={10}
				className={`${className} pr-8`}
			/>
			<input
				ref={pickerRef}
				type='date'
				value={isoValue}
				onChange={(event) =>
					onChange?.(formatISOToDisplay(event.target.value))
				}
				disabled={disabled}
				className='absolute opacity-0 pointer-events-none'
				tabIndex={-1}
			/>
			<button
				type='button'
				disabled={disabled}
				onClick={() =>
					pickerRef.current?.showPicker?.() ||
					pickerRef.current?.click?.()
				}
				className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-[#004A80] disabled:cursor-not-allowed disabled:opacity-50'
				aria-label={ariaLabel}>
				<MdCalendarToday className='h-4 w-4' />
			</button>
		</div>
	);
};

const getMesReferenciaFromDateInput = (value = '') => {
	const iso = formatDateInputToISO(value);
	if (!iso) return '';
	return `${iso.slice(0, 7)}-01`;
};

const normalizeTrocaDateValue = (value = '') => {
	const raw = String(value || '').trim();
	if (!raw) return '';
	return formatDateInputToISO(raw) || raw;
};

const parseISODateLocal = (value = '') => {
	const iso = formatDateInputToISO(value);
	if (!iso) return null;
	const [ano, mes, dia] = iso.split('-').map(Number);
	if (!ano || !mes || !dia) return null;
	return new Date(ano, mes - 1, dia);
};

const addDaysToISODate = (value = '', days = 0) => {
	const baseDate = parseISODateLocal(value);
	if (!baseDate) return '';

	const nextDate = new Date(baseDate);
	nextDate.setDate(nextDate.getDate() + days);

	const ano = nextDate.getFullYear();
	const mes = String(nextDate.getMonth() + 1).padStart(2, '0');
	const dia = String(nextDate.getDate()).padStart(2, '0');
	return `${ano}-${mes}-${dia}`;
};

const getTodayISODateLocal = () => {
	const now = new Date();
	const ano = now.getFullYear();
	const mes = String(now.getMonth() + 1).padStart(2, '0');
	const dia = String(now.getDate()).padStart(2, '0');
	return `${ano}-${mes}-${dia}`;
};

// Uma edição de regime não pode reescrever a escala que já foi publicada.
// A vigência da nova versão é sempre o dia em que a alteração é realizada;
// para uma alocação nova, a data escolhida no formulário continua válida.
export const getVigenciaInicioParaEdicao = ({
	isNovo = false,
	dataInformada = '',
	dataMinima = '',
	today = getTodayISODateLocal(),
} = {}) => {
	if (!isNovo) return formatDateInputToISO(today);

	const dataInicial = formatDateInputToISO(dataInformada);
	const minimo = formatDateInputToISO(dataMinima);
	if (!dataInicial || (minimo && dataInicial < minimo))
		return minimo || dataInicial;
	return dataInicial;
};

const getCalendarDayNumber = (diaCalendario = {}) => {
	const date = parseISODateLocal(diaCalendario?.data);
	if (date) return String(date.getDate()).padStart(2, '0');

	const diaNumerico = Number(diaCalendario?.dia);
	if (Number.isFinite(diaNumerico) && diaNumerico >= 1 && diaNumerico <= 31) {
		return String(diaNumerico).padStart(2, '0');
	}

	return '--';
};

const getCalendarWeekdayShortPtBr = (diaCalendario = {}) => {
	const date = parseISODateLocal(diaCalendario?.data);
	if (date) {
		const mapWeekday = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
		return mapWeekday[date.getDay()] || '';
	}

	const diaSemanaNumerico = Number(diaCalendario?.dia_semana);
	if (Number.isFinite(diaSemanaNumerico)) {
		const mapNumerico = {
			0: 'Dom',
			1: 'Seg',
			2: 'Ter',
			3: 'Qua',
			4: 'Qui',
			5: 'Sex',
			6: 'Sab',
			7: 'Dom',
		};
		return mapNumerico[diaSemanaNumerico] || '';
	}

	const diaSemanaTexto = normalizeSearchValue(
		diaCalendario?.dia_semana || '',
	);
	if (diaSemanaTexto.startsWith('seg')) return 'seg';
	if (diaSemanaTexto.startsWith('ter')) return 'ter';
	if (diaSemanaTexto.startsWith('qua')) return 'qua';
	if (diaSemanaTexto.startsWith('qui')) return 'qui';
	if (diaSemanaTexto.startsWith('sex')) return 'sex';
	if (diaSemanaTexto.startsWith('sab')) return 'sab';
	if (diaSemanaTexto.startsWith('dom')) return 'dom';

	return String(diaCalendario?.dia_semana || '')
		.slice(0, 3)
		.toLowerCase();
};

const getMonthLabelPtBr = (mesReferencia = '') => {
	const iso = formatDateInputToISO(mesReferencia);
	if (!iso) return 'Competência';
	const [ano, mes] = iso.split('-');
	const date = new Date(Number(ano), Number(mes) - 1, 1);
	return date.toLocaleDateString('pt-BR', {
		month: 'long',
		year: 'numeric',
	});
};

const getMonthNamePtBr = (mesReferencia = '') => {
	const iso = formatDateInputToISO(mesReferencia);
	if (!iso) return '';
	const [ano, mes] = iso.split('-');
	return new Date(Number(ano), Number(mes) - 1, 1)
		.toLocaleDateString('pt-BR', {
			month: 'long',
		})
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();
};

const createNovoProfissionalDataExtraItem = () => ({
	id: `data-extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	selected_date: '',
	hora_inicio: '',
	hora_fim: '',
});

const isDataExtraReadOnly = (item = {}) =>
	Boolean(item?.readOnly || item?.bloqueado || item?.origem === 'troca');

const getEditableDatasExtras = (datasExtras = []) =>
	(Array.isArray(datasExtras) ? datasExtras : []).filter(
		(item) => !isDataExtraReadOnly(item),
	);

const getFirstTwoNames = (nome = '') => {
	const parts = String(nome || '')
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	if (parts.length <= 2) return parts.join(' ');
	return `${parts[0]} ${parts[1]}`;
};

const normalizeShiftDescriptor = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toUpperCase()
		.trim();

const extractShiftHoursFromText = (value = '') => {
	const match = String(value || '').match(
		/(\d{1,2}:\d{2}).*?(\d{1,2}:\d{2})/,
	);
	if (!match) return null;

	const normalizeHour = (hour = '') => {
		const [h = '', m = ''] = String(hour).split(':');
		if (h === '' || m === '') return '';
		return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
	};

	const hora_inicio = normalizeHour(match[1]);
	const hora_fim = normalizeHour(match[2]);

	if (!hora_inicio || !hora_fim) return null;

	return { hora_inicio, hora_fim };
};

const getShiftIndicator = ({
	hora_inicio,
	hora_fim,
	turno,
	regime,
	faixa_horaria_texto,
	nome_faixa_horaria,
	tipo_item,
} = {}) => {
	const inicio = String(hora_inicio || '').slice(0, 5);
	const fim = String(hora_fim || '').slice(0, 5);
	if (inicio && fim) {
		if (inicio === fim) return '24h';
		const inicioHora = Number(inicio.split(':')[0] || 0);
		if (Number.isFinite(inicioHora)) {
			return inicioHora >= 18 ? 'N' : 'D';
		}
	}

	const faixaTexto = [faixa_horaria_texto, nome_faixa_horaria]
		.map((value) => String(value || '').trim())
		.find(Boolean);
	const faixaHoras = extractShiftHoursFromText(faixaTexto);
	if (faixaHoras) {
		return getShiftIndicator({
			hora_inicio: faixaHoras.hora_inicio,
			hora_fim: faixaHoras.hora_fim,
			turno,
			tipo_item,
		});
	}

	const turnoNormalizado = normalizeShiftDescriptor(
		[turno, faixa_horaria_texto, nome_faixa_horaria]
			.filter(Boolean)
			.join(' '),
	);
	if (turnoNormalizado.includes('DIUR')) return 'D';
	if (turnoNormalizado.includes('NOTUR')) return 'N';
	if (turnoNormalizado.includes('24')) return '24h';
	if (isRotinaRegime(regime)) return 'D';

	return 'P';
};

const getLinhaShiftIndicator = (linha = {}) =>
	getShiftIndicator({
		hora_inicio: linha?.regime_escala?.hora_inicio,
		hora_fim: linha?.regime_escala?.hora_fim,
		regime: linha?.regime_escala?.regime || linha?.regime,
		turno:
			linha?.regime_escala?.turno ||
			linha?.turno ||
			linha?.regime_escala?.nome,
		tipo_item: linha?.tipo_item,
	});

const getFieldErrorMessage = (error) =>
	error ? (
		<span
			className='mt-1 block text-[10px] text-[#dc2626]'
			role='alert'>
			{error}
		</span>
	) : null;

const getLockedFieldClassName = (isLocked) =>
	isLocked ? 'bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed' : 'bg-white';

const normalizeHourInputValue = (value = '') => {
	const digitsOnly = String(value || '')
		.replace(/\D/g, '')
		.slice(0, 2);

	if (!digitsOnly) return '';

	const numericValue = Number(digitsOnly);
	if (Number.isNaN(numericValue)) return '';

	return String(Math.min(numericValue, 23));
};

const getHourInputFromTimeValue = (value = '') => {
	const raw = String(value || '').trim();
	if (!raw) return '';

	const match = raw.match(/^(\d{1,2})(?::\d{2})?/);
	return normalizeHourInputValue(match?.[1] || raw);
};

const formatHourInputToTimeValue = (value = '') => {
	const raw = String(value || '').trim();
	const timeMatch = raw.match(/^(\d{1,2}):(\d{1,2})$/);
	if (timeMatch) {
		const hour = normalizeHourInputValue(timeMatch[1]);
		const minuteValue = Math.min(Number(timeMatch[2] || 0), 59);
		if (hour && Number.isFinite(minuteValue)) {
			return `${hour.padStart(2, '0')}:${String(minuteValue).padStart(2, '0')}`;
		}
	}
	const normalized = normalizeHourInputValue(value);
	return normalized ? `${normalized.padStart(2, '0')}:00` : '';
};

const formatHourInputToDisplayTime = (value = '') => {
	const raw = String(value || '').trim();
	if (/^\d{1}$/.test(raw)) return raw;
	const match = raw.match(/^(\d{1,2})(?::(\d{1,2}))?/);
	if (!match) return '';
	const hour = normalizeHourInputValue(match[1]);
	if (!hour) return '';
	if (!raw.includes(':') && raw.length < 2) return raw;
	const minute = String(match[2] || '00')
		.replace(/\D/g, '')
		.slice(0, 2)
		.padEnd(2, '0');
	return `${hour.padStart(2, '0')}:${minute}`;
};

const normalizeTimeInputDisplayValue = (value = '') => {
	const digitsOnly = String(value || '')
		.replace(/\D/g, '')
		.slice(0, 4);
	if (!digitsOnly) return '';
	const hourDigits = digitsOnly.slice(0, 2);
	const minuteDigits = digitsOnly.slice(2, 4);
	const hour = normalizeHourInputValue(hourDigits);
	if (!hour) return '';
	if (digitsOnly.length < 2) return hourDigits;
	if (digitsOnly.length === 2) return hour.padStart(2, '0');
	return `${hour.padStart(2, '0')}:${minuteDigits}`;
};

const normalizeDiasSemanaHorariosMap = (value = {}) =>
	Object.entries(
		value && typeof value === 'object' && !Array.isArray(value)
			? value
			: {},
	).reduce((acc, [key, item]) => {
		const diaSemana = Number(key);
		if (!Number.isInteger(diaSemana) || diaSemana < 1 || diaSemana > 7) {
			return acc;
		}

		const horaInicio = normalizeHourInputValue(
			item?.hora_inicio || item?.horaInicio || '',
		);
		const horaFim = normalizeHourInputValue(
			item?.hora_fim || item?.horaFim || '',
		);

		if (!horaInicio && !horaFim) {
			return acc;
		}

		acc[diaSemana] = {
			hora_inicio: horaInicio,
			hora_fim: horaFim,
		};
		return acc;
	}, {});

const buildDiasSemanaHorariosMapFromOcorrencias = (ocorrencias = []) =>
	(Array.isArray(ocorrencias) ? ocorrencias : []).reduce(
		(acc, ocorrencia) => {
			const diaSemana = Number(ocorrencia?.dia_semana || 0);
			if (
				!Number.isInteger(diaSemana) ||
				diaSemana < 1 ||
				diaSemana > 7
			) {
				return acc;
			}

			const horaInicio = getHourInputFromTimeValue(
				ocorrencia?.hora_inicio,
			);
			const horaFim = getHourInputFromTimeValue(ocorrencia?.hora_fim);

			if (!horaInicio && !horaFim) {
				return acc;
			}

			acc[diaSemana] = {
				hora_inicio: horaInicio,
				hora_fim: horaFim,
			};
			return acc;
		},
		{},
	);

const buildDiasSemanaHorariosMapFromDias = ({
	dias_semana = [],
	hora_inicio = '',
	hora_fim = '',
} = {}) => {
	const horaInicio = getHourInputFromTimeValue(hora_inicio);
	const horaFim = getHourInputFromTimeValue(hora_fim);
	if (!horaInicio && !horaFim) return {};

	return normalizeDiasSemanaValues(dias_semana).reduce((acc, diaSemana) => {
		acc[diaSemana] = {
			hora_inicio: horaInicio,
			hora_fim: horaFim,
		};
		return acc;
	}, {});
};

const getDiaSemanaHorarioConfig = ({
	diaSemana,
	diasSemanaHorarios = {},
	fallbackHoraInicio = '',
	fallbackHoraFim = '',
} = {}) => {
	const rawHorariosMap =
		diasSemanaHorarios &&
		typeof diasSemanaHorarios === 'object' &&
		!Array.isArray(diasSemanaHorarios)
			? diasSemanaHorarios
			: {};
	const rawDiaConfig = rawHorariosMap?.[Number(diaSemana)] || {};
	const hasHoraInicio = Object.prototype.hasOwnProperty.call(
		rawDiaConfig,
		'hora_inicio',
	);
	const hasHoraFim = Object.prototype.hasOwnProperty.call(
		rawDiaConfig,
		'hora_fim',
	);
	const horariosMap = normalizeDiasSemanaHorariosMap(diasSemanaHorarios);
	const diaConfig = horariosMap?.[Number(diaSemana)] || {};

	return {
		hora_inicio: hasHoraInicio
			? normalizeHourInputValue(rawDiaConfig?.hora_inicio)
			: diaConfig?.hora_inicio ||
				getHourInputFromTimeValue(fallbackHoraInicio),
		hora_fim: hasHoraFim
			? normalizeHourInputValue(rawDiaConfig?.hora_fim)
			: diaConfig?.hora_fim || getHourInputFromTimeValue(fallbackHoraFim),
	};
};

const syncDiasSemanaHorariosWithSelection = ({
	dias_semana = [],
	dias_semana_horarios = {},
	fallbackHoraInicio = '',
	fallbackHoraFim = '',
} = {}) => {
	const diasSelecionados = normalizeDiasSemanaValues(dias_semana);
	const horariosAtuais = normalizeDiasSemanaHorariosMap(dias_semana_horarios);

	return diasSelecionados.reduce((acc, diaSemana) => {
		const diaConfig = getDiaSemanaHorarioConfig({
			diaSemana,
			diasSemanaHorarios: horariosAtuais,
			fallbackHoraInicio,
			fallbackHoraFim,
		});

		if (!diaConfig?.hora_inicio && !diaConfig?.hora_fim) {
			return acc;
		}

		acc[diaSemana] = {
			hora_inicio: diaConfig?.hora_inicio || '',
			hora_fim: diaConfig?.hora_fim || '',
		};
		return acc;
	}, {});
};

export const buildWeeklyOccurrencesPayload = ({
	dias_semana = [],
	dias_semana_horarios = {},
	fallbackHoraInicio = '',
	fallbackHoraFim = '',
	regime = '',
	startDate = '',
} = {}) => {
	const diasEfetivos = getEffectiveDiasSemanaSelection({
		dias_semana,
		regime,
		startDate,
	});

	return diasEfetivos
		.map((diaSemana) => {
			const diaConfig = getDiaSemanaHorarioConfig({
				diaSemana,
				diasSemanaHorarios: dias_semana_horarios,
				// Na recorrência semanal da enfermagem, o horário geral vale para
				// todos os dias selecionados, salvo quando houver uma exceção por dia.
				fallbackHoraInicio,
				fallbackHoraFim,
			});
			const horaInicio = formatHourInputToTimeValue(
				diaConfig?.hora_inicio,
			);
			const horaFim = formatHourInputToTimeValue(diaConfig?.hora_fim);

			if (!horaInicio || !horaFim) {
				return null;
			}

			return {
				dia_semana: diaSemana,
				hora_inicio: horaInicio,
				hora_fim: horaFim,
			};
		})
		.filter(Boolean);
};

const getWeeklyOccurrenceReference = (
	ocorrencias = [],
	fallbackHoraInicio = '',
	fallbackHoraFim = '',
) => {
	const primeiraOcorrencia = [
		...(Array.isArray(ocorrencias) ? ocorrencias : []),
	].sort(
		(a, b) => Number(a?.dia_semana || 0) - Number(b?.dia_semana || 0),
	)[0];

	return {
		hora_inicio: String(
			primeiraOcorrencia?.hora_inicio || fallbackHoraInicio || '',
		).slice(0, 5),
		hora_fim: String(
			primeiraOcorrencia?.hora_fim || fallbackHoraFim || '',
		).slice(0, 5),
	};
};

const serializeWeeklyOccurrences = (ocorrencias = []) =>
	(Array.isArray(ocorrencias) ? ocorrencias : [])
		.map((ocorrencia) => ({
			dia_semana: Number(ocorrencia?.dia_semana || 0),
			hora_inicio: String(ocorrencia?.hora_inicio || '').slice(0, 5),
			hora_fim: String(ocorrencia?.hora_fim || '').slice(0, 5),
		}))
		.filter(
			(ocorrencia) =>
				ocorrencia.dia_semana >= 1 &&
				ocorrencia.dia_semana <= 7 &&
				ocorrencia.hora_inicio &&
				ocorrencia.hora_fim,
		)
		.sort((a, b) => a.dia_semana - b.dia_semana)
		.map(
			(ocorrencia) =>
				`${ocorrencia.dia_semana}:${ocorrencia.hora_inicio}-${ocorrencia.hora_fim}`,
		)
		.join('|');

export const normalizePadroesAlocacao = (padroes = []) =>
	(Array.isArray(padroes) ? padroes : [])
		.map((padrao = {}) => {
			const tipoRecorrencia = normalizeCode(
				padrao?.tipo_recorrencia || '',
			);
			const presetCiclo =
				tipoRecorrencia === 'SEMANAL'
					? 'SEMANAL'
					: getCanonicalPresetCiclo(padrao?.preset_ciclo) ||
						(tipoRecorrencia === 'DATAS_ESPECIFICAS'
							? 'PERSONALIZADO'
							: '');
			const horaInicio = String(padrao?.hora_inicio || '').slice(0, 5);
			const horaFim = String(padrao?.hora_fim || '').slice(0, 5);
			const diasSemana = normalizeDiasSemanaValues(padrao?.dias_semana);
			const trabalhoHoras =
				Number(padrao?.trabalho_horas || 0) ||
				(Number(padrao?.trabalho_minutos || 0) > 0
					? Number(padrao?.trabalho_minutos || 0) / 60
					: 0);
			const descansoHoras =
				Number(padrao?.descanso_horas || 0) ||
				(Number(padrao?.descanso_minutos || 0) > 0
					? Number(padrao?.descanso_minutos || 0) / 60
					: 0);
			const datasEspecificas = (
				Array.isArray(padrao?.datas_especificas)
					? padrao.datas_especificas
					: [padrao?.data]
			)
				.map((item) => formatDateInputToISO(item))
				.filter(Boolean);
			const dataAncora = formatDateInputToISO(padrao?.data_ancora);
			const semanasRotacao = (
				Array.isArray(padrao?.semanas_rotacao)
					? padrao.semanas_rotacao
					: []
			).map((semana = {}, index) => ({
				semana: Number(semana?.semana || index + 1),
				dias_semana: normalizeDiasSemanaValues(
					semana?.dias_semana || semana,
				),
			}));
			const periodoSemanas = Number(padrao?.periodo_semanas || 0);

			if (!tipoRecorrencia || !horaInicio || !horaFim) return null;

			return {
				tipo_recorrencia: tipoRecorrencia,
				preset_ciclo: presetCiclo,
				dias_semana: diasSemana,
				datas_especificas: datasEspecificas,
				hora_inicio: horaInicio,
				hora_fim: horaFim,
				trabalho_horas: trabalhoHoras || undefined,
				descanso_horas: descansoHoras || undefined,
				data_ancora: dataAncora || undefined,
				// A edição usa a sequência persistida para montar os botões de cada
				// semana. Sem preservá-la aqui, o normalizador descartava a
				// informação recebida da API antes de o modal ser hidratado.
				periodo_semanas:
					periodoSemanas || semanasRotacao.length || undefined,
				semanas_rotacao:
					semanasRotacao.length > 0 ? semanasRotacao : undefined,
			};
		})
		.filter(Boolean);

const getWeeklyScheduleDetails = (regimeEscala = {}) => {
	const padroesSemanais = normalizePadroesAlocacao(
		regimeEscala?.padroes_alocacao,
	).filter((padrao) => normalizeCode(padrao?.tipo_recorrencia) === 'SEMANAL');
	const ocorrenciasSemanais = Array.isArray(regimeEscala?.ocorrencias)
		? regimeEscala.ocorrencias
		: [];
	const diasSemana = normalizeDiasSemanaValues([
		...normalizeDiasSemanaValues(regimeEscala?.dias_semana),
		...padroesSemanais.flatMap((padrao) => padrao?.dias_semana || []),
		...ocorrenciasSemanais.map((ocorrencia) => ocorrencia?.dia_semana),
	]);
	const horariosOptionsByDia = {};
	const adicionarHorario = (diaSemana, horaInicio, horaFim) => {
		const inicio = formatHourInputToTimeValue(horaInicio);
		const fim = formatHourInputToTimeValue(horaFim);
		if (!diaSemana || !inicio || !fim) return;
		const atuais = horariosOptionsByDia[diaSemana] || [];
		if (
			atuais.some(
				(item) => item.hora_inicio === inicio && item.hora_fim === fim,
			)
		)
			return;
		horariosOptionsByDia[diaSemana] = [
			...atuais,
			{ hora_inicio: inicio, hora_fim: fim },
		].sort((a, b) =>
			`${a.hora_inicio}-${a.hora_fim}`.localeCompare(
				`${b.hora_inicio}-${b.hora_fim}`,
			),
		);
	};
	const horariosInformados =
		regimeEscala?.dias_semana_horarios &&
		typeof regimeEscala.dias_semana_horarios === 'object'
			? regimeEscala.dias_semana_horarios
			: {};

	Object.entries(horariosInformados).forEach(([dia, horario = {}]) => {
		const diaSemana = Number(dia);
		if (!diasSemana.includes(diaSemana)) return;
		adicionarHorario(
			diaSemana,
			horario?.hora_inicio || horario?.horaInicio,
			horario?.hora_fim || horario?.horaFim,
		);
	});

	padroesSemanais.forEach((padrao = {}) => {
		normalizeDiasSemanaValues(padrao?.dias_semana).forEach((diaSemana) => {
			adicionarHorario(diaSemana, padrao?.hora_inicio, padrao?.hora_fim);
		});
	});

	ocorrenciasSemanais.forEach((ocorrencia = {}) => {
		const diaSemana = Number(ocorrencia?.dia_semana || 0);
		if (!diasSemana.includes(diaSemana)) return;
		adicionarHorario(
			diaSemana,
			ocorrencia?.hora_inicio,
			ocorrencia?.hora_fim,
		);
	});
	const horariosByDia = Object.fromEntries(
		Object.entries(horariosOptionsByDia).map(([dia, horarios]) => [
			dia,
			horarios[0],
		]),
	);

	return {
		padraoSemanal: padroesSemanais[0] || {},
		diasSemana,
		horariosByDia,
		horariosOptionsByDia,
	};
};

const getDatasEspecificasComRecorrenciasSemanais = (datasEspecificas = []) => {
	const grupos = new Map();
	(datasEspecificas || []).forEach((item = {}) => {
		const data = formatDateInputToISO(item?.data);
		const diaSemana = getIsoWeekdayFromValue(data);
		const horaInicio = formatHourInputToTimeValue(item?.horaInicio);
		const horaFim = formatHourInputToTimeValue(item?.horaFim);
		if (!data || !diaSemana || !horaInicio || !horaFim) return;
		const chave = `${diaSemana}|${horaInicio}|${horaFim}`;
		grupos.set(chave, [...(grupos.get(chave) || []), item]);
	});
	const recorrentes = new Set(
		Array.from(grupos.entries())
			.filter(([, itens]) => itens.length > 1)
			.map(([chave]) => chave),
	);
	const horariosOptionsByDia = {};
	grupos.forEach((itens, chave) => {
		if (!recorrentes.has(chave)) return;
		const [diaSemana, horaInicio, horaFim] = chave.split('|');
		horariosOptionsByDia[diaSemana] = [
			...(horariosOptionsByDia[diaSemana] || []),
			{ hora_inicio: horaInicio, hora_fim: horaFim },
		];
	});

	return {
		datasEspecificas: (datasEspecificas || []).filter((item = {}) => {
			const chave = `${getIsoWeekdayFromValue(item?.data)}|${formatHourInputToTimeValue(item?.horaInicio)}|${formatHourInputToTimeValue(item?.horaFim)}`;
			return !recorrentes.has(chave);
		}),
		horariosOptionsByDia,
	};
};

const mergeHorariosOptionsByDia = (...mapas) => {
	const resultado = {};
	mapas.forEach((mapa = {}) => {
		Object.entries(mapa || {}).forEach(([dia, horarios]) => {
			(Array.isArray(horarios) ? horarios : [horarios]).forEach(
				(horario = {}) => {
					const inicio = formatHourInputToTimeValue(
						horario?.hora_inicio,
					);
					const fim = formatHourInputToTimeValue(horario?.hora_fim);
					if (!inicio || !fim) return;
					const atuais = resultado[dia] || [];
					if (
						!atuais.some(
							(item) =>
								item.hora_inicio === inicio &&
								item.hora_fim === fim,
						)
					)
						resultado[dia] = [
							...atuais,
							{ hora_inicio: inicio, hora_fim: fim },
						];
				},
			);
		});
	});
	return resultado;
};

const serializePadroesAlocacao = (padroes = []) =>
	normalizePadroesAlocacao(padroes)
		.map((padrao) => {
			const diasSemana = normalizeDiasSemanaValues(
				padrao?.dias_semana,
			).join(',');
			const datasEspecificas = (
				Array.isArray(padrao?.datas_especificas)
					? padrao.datas_especificas
					: []
			)
				.map((item) => formatDateInputToISO(item))
				.filter(Boolean)
				.join(',');
			const semanasRotacao = (
				Array.isArray(padrao?.semanas_rotacao)
					? padrao.semanas_rotacao
					: []
			)
				.map((semana = {}) =>
					normalizeDiasSemanaValues(
						semana?.dias_semana || semana,
					).join(','),
				)
				.join('|');

			return [
				padrao?.tipo_recorrencia || '',
				padrao?.preset_ciclo || '',
				diasSemana,
				datasEspecificas,
				semanasRotacao,
				String(Number(padrao?.periodo_semanas || 0) || ''),
				String(padrao?.hora_inicio || '').slice(0, 5),
				String(padrao?.hora_fim || '').slice(0, 5),
				String(Number(padrao?.trabalho_horas || 0) || ''),
				String(Number(padrao?.descanso_horas || 0) || ''),
				formatDateInputToISO(padrao?.data_ancora),
			].join(':');
		})
		.join('|');

export const buildPadroesAlocacaoPayload = ({
	source,
	fallbackHoraInicio = '',
	fallbackHoraFim = '',
	regime = '',
	vigenciaInicio = '',
	incluirDiaInicialSemanal = false,
} = {}) => {
	const tipoRecorrencia = normalizeCode(
		source?.tipo_recorrencia ||
			getPresetDefaults(source?.preset_ciclo)?.tipo_recorrencia ||
			'',
	);

	if (tipoRecorrencia === 'SEMANAL') {
		return buildWeeklyOccurrencesPayload({
			dias_semana: source?.dias_semana,
			dias_semana_horarios: source?.dias_semana_horarios,
			fallbackHoraInicio,
			fallbackHoraFim,
			regime: incluirDiaInicialSemanal ? regime : '',
			startDate: vigenciaInicio,
		})
			.map((ocorrencia) => ({
				tipo_recorrencia: 'SEMANAL',
				preset_ciclo: 'SEMANAL',
				dias_semana: [Number(ocorrencia?.dia_semana || 0)].filter(
					Boolean,
				),
				hora_inicio: ocorrencia?.hora_inicio,
				hora_fim: ocorrencia?.hora_fim,
			}))
			.filter(
				(item) =>
					item?.dias_semana?.length > 0 &&
					item?.hora_inicio &&
					item?.hora_fim,
			);
	}

	if (tipoRecorrencia === 'ROTACAO_PERIODICA') {
		const semanasRotacao = (
			Array.isArray(source?.semanas_rotacao) ? source.semanas_rotacao : []
		).map((semana, index) => ({
			// O contrato da escrita direta exige a semana explícita. O estado da
			// tela guarda apenas a sequência de dias, portanto o índice visual é
			// serializado como semana 1..N no momento de montar o payload.
			semana: index + 1,
			dias_semana: normalizeDiasSemanaValues(
				semana?.dias_semana || semana,
			),
		}));
		const dataAncoraInformada =
			formatDateInputToISO(source?.data_ancora) ||
			formatDateInputToISO(vigenciaInicio);
		// Para a escrita direta, a âncora da rotação identifica a segunda-feira
		// da semana 1; ela não expande nem escolhe ocorrências do ciclo.
		const dataAncora = getInicioSemanaIso(dataAncoraInformada);
		const horaInicio = String(fallbackHoraInicio || '').slice(0, 5);
		const horaFim = String(fallbackHoraFim || '').slice(0, 5);
		const trabalhoHoras =
			Number(source?.trabalho_horas || 0) ||
			calculateShiftDurationHours(horaInicio, horaFim) ||
			undefined;
		return semanasRotacao.length >= 2 &&
			semanasRotacao.some((semana) => semana.dias_semana.length > 0) &&
			dataAncora &&
			horaInicio &&
			horaFim
			? [
					{
						tipo_recorrencia: 'ROTACAO_PERIODICA',
						preset_ciclo: 'ROTACAO_PERIODICA',
						periodo_semanas: semanasRotacao.length,
						semanas_rotacao: semanasRotacao,
						trabalho_horas: trabalhoHoras,
						data_ancora: dataAncora,
						hora_inicio: horaInicio,
						hora_fim: horaFim,
					},
				]
			: [];
	}

	if (tipoRecorrencia === 'DATAS_ESPECIFICAS') {
		const datasExtrasPadroes = (
			Array.isArray(source?.datas_extras) ? source.datas_extras : []
		)
			.map((item = {}) => {
				const data = formatDateInputToISO(
					item?.selected_date || item?.data || '',
				);
				const horaInicio = formatHourInputToTimeValue(
					item?.hora_inicio || fallbackHoraInicio,
				);
				const horaFim = formatHourInputToTimeValue(
					item?.hora_fim || fallbackHoraFim,
				);
				if (!data || !horaInicio || !horaFim) return null;

				return {
					tipo_recorrencia: 'DATAS_ESPECIFICAS',
					preset_ciclo: 'PERSONALIZADO',
					datas_especificas: [data],
					hora_inicio: horaInicio,
					hora_fim: horaFim,
				};
			})
			.filter(Boolean);

		if (datasExtrasPadroes.length > 0) {
			return datasExtrasPadroes;
		}

		const diasSemana = getDiasSemanaWithRequiredStartDate({
			dias_semana: source?.dias_semana,
			startDate: vigenciaInicio,
		});

		if (diasSemana.length > 0) {
			return diasSemana
				.map((diaSemana) => {
					const diaConfig = getDiaSemanaHorarioConfig({
						diaSemana,
						diasSemanaHorarios: source?.dias_semana_horarios,
						fallbackHoraInicio,
						fallbackHoraFim,
					});
					const horaInicio = formatHourInputToTimeValue(
						diaConfig?.hora_inicio,
					);
					const horaFim = formatHourInputToTimeValue(
						diaConfig?.hora_fim,
					);
					if (!horaInicio || !horaFim) return null;

					return {
						tipo_recorrencia: 'DATAS_ESPECIFICAS',
						preset_ciclo: 'PERSONALIZADO',
						dias_semana: [diaSemana],
						hora_inicio: horaInicio,
						hora_fim: horaFim,
					};
				})
				.filter(Boolean);
		}

		return (Array.isArray(source?.datas_extras) ? source.datas_extras : [])
			.map((item = {}) => {
				const data = formatDateInputToISO(
					item?.selected_date || item?.data || '',
				);
				const horaInicio = formatHourInputToTimeValue(
					item?.hora_inicio,
				);
				const horaFim = formatHourInputToTimeValue(item?.hora_fim);
				if (!data || !horaInicio || !horaFim) return null;

				return {
					tipo_recorrencia: 'DATAS_ESPECIFICAS',
					preset_ciclo: 'PERSONALIZADO',
					datas_especificas: [data],
					hora_inicio: horaInicio,
					hora_fim: horaFim,
				};
			})
			.filter(Boolean);
	}

	const presetInformado = normalizePresetCode(source?.preset_ciclo);
	const isCiclo24x120 =
		presetInformado === '24X120' ||
		(Number(source?.trabalho_horas || 0) === 24 &&
			Number(source?.descanso_horas || 0) === 120);
	const presetCiclo =
		(isCiclo24x120 ? 'PERSONALIZADO' : '') ||
		getPayloadPresetCiclo(source?.preset_ciclo) ||
		inferContinuousPresetFromShift({
			hora_inicio: fallbackHoraInicio,
			hora_fim: fallbackHoraFim,
			fallbackPreset: '12x36',
		}) ||
		'12x36';
	const presetDefaults = getPresetDefaults(
		isCiclo24x120 ? '24X120' : presetCiclo,
	);
	const trabalhoHoras =
		Number(source?.trabalho_horas || 0) ||
		(Number(source?.trabalho_minutos || 0) > 0
			? Number(source?.trabalho_minutos || 0) / 60
			: 0) ||
		presetDefaults?.trabalho_horas ||
		undefined;
	const descansoHoras =
		Number(source?.descanso_horas || 0) ||
		(Number(source?.descanso_minutos || 0) > 0
			? Number(source?.descanso_minutos || 0) / 60
			: 0) ||
		presetDefaults?.descanso_horas ||
		undefined;
	const dataAncora = formatDateInputToISO(
		source?.data_ancora || vigenciaInicio,
	);
	return [
		{
			tipo_recorrencia: 'CICLO_CONTINUO',
			preset_ciclo: presetCiclo,
			trabalho_horas: trabalhoHoras,
			descanso_horas: descansoHoras,
			...(isCiclo24x120
				? {}
				: {
						trabalho_minutos: trabalhoHoras
							? Math.round(Number(trabalhoHoras) * 60)
							: undefined,
						descanso_minutos: descansoHoras
							? Math.round(Number(descansoHoras) * 60)
							: undefined,
					}),
			data_ancora: dataAncora || undefined,
			hora_inicio: String(fallbackHoraInicio || '').slice(0, 5),
			hora_fim: String(fallbackHoraFim || '').slice(0, 5),
		},
	].filter((item) => item?.hora_inicio && item?.hora_fim);
};

const getWeeklyHoursFromOccurrences = (ocorrencias = []) => {
	const total = (Array.isArray(ocorrencias) ? ocorrencias : []).reduce(
		(acc, ocorrencia) =>
			acc +
			(calculateShiftDurationHours(
				ocorrencia?.hora_inicio,
				ocorrencia?.hora_fim,
			) || 0),
		0,
	);

	return total > 0 ? Math.round(total * 100) / 100 : null;
};

const getIncompleteWeeklyScheduleDays = ({
	dias_semana = [],
	dias_semana_horarios = {},
	regime = '',
	startDate = '',
} = {}) => {
	const diasEfetivos = getEffectiveDiasSemanaSelection({
		dias_semana,
		regime,
		startDate,
	});
	const horarios = normalizeDiasSemanaHorariosMap(dias_semana_horarios);

	return diasEfetivos.filter((diaSemana) => {
		const diaHorario = horarios?.[diaSemana] || {};
		return !diaHorario?.hora_inicio || !diaHorario?.hora_fim;
	});
};

const DiaSemanaHorarioBloco = ({
	disabled = false,
	minWidthClassName = 'min-w-[97px]',
	marginTopClassName = 'mt-2',
	containerClassName = '',
	compact = false,
	forceFullTimeInputs = false,
	showClockIcon = true,
	horaInicio = '',
	horaFim = '',
	onChangeHoraInicio,
	onChangeHoraFim,
}) => {
	const inputTextColorClassName = disabled
		? compact
			? 'text-[#4F5257]'
			: 'text-gray-400'
		: 'text-[#4B5563]';
	const separatorColorClassName = disabled
		? compact
			? 'text-[#4F5257]'
			: 'text-gray-400'
		: 'text-[#6B7280]';
	const iconColorClassName = disabled ? 'text-gray-400' : 'text-[#9CA3AF]';
	const baseContainerClassName = disabled
		? 'bg-[#fcfcfc] border-[#F1F1F1] cursor-not-allowed'
		: 'bg-white border-gray-200';
	const inputClassName = disabled
		? `bg-transparent ${inputTextColorClassName} placeholder:text-[#E5E7EB] cursor-not-allowed`
		: `bg-transparent ${inputTextColorClassName} placeholder:text-[#9CA3AF]`;
	const showFullHoraInicio =
		forceFullTimeInputs || String(horaInicio || '').includes(':');
	const showFullHoraFim =
		forceFullTimeInputs || String(horaFim || '').includes(':');
	const inputInicioWidthClassName =
		compact ? 'w-[40px]' : showFullHoraInicio ? 'w-[37px]' : 'w-[19px]';
	const inputFimWidthClassName =
		compact ? 'w-[40px]' : showFullHoraFim ? 'w-[37px]' : 'w-[19px]';
	const inputTextClassName = compact ? 'text-[11px]' : 'text-[13px]';
	const containerHeightClassName = compact ? 'h-[26px]' : 'h-[34px]';
	const containerPaddingClassName = compact ? 'px-1' : 'px-1.5';
	const separatorTextClassName = compact ? 'text-[10px]' : 'text-[13px]';

	return (
		<div
			className={`${marginTopClassName} ${containerHeightClassName} ${minWidthClassName} rounded-md border ${containerPaddingClassName} flex items-center ${compact ? 'justify-start gap-0.5' : 'justify-center gap-1'} ${baseContainerClassName} ${containerClassName}`}>
			<input
				type='text'
				placeholder={compact ? '00:00' : '00'}
				value={
					compact || showFullHoraInicio
						? disabled && compact
							? formatHourInputToTimeValue(horaInicio)
							: formatHourInputToDisplayTime(horaInicio)
						: horaInicio
				}
				disabled={disabled}
				inputMode='numeric'
				maxLength={compact || showFullHoraInicio ? 5 : 2}
				onChange={(e) =>
					onChangeHoraInicio?.(
						compact || showFullHoraInicio
							? normalizeTimeInputDisplayValue(e.target.value)
							: normalizeHourInputValue(e.target.value),
					)
				}
				className={`${inputInicioWidthClassName} text-center ${inputTextClassName} font-normal outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${inputClassName}`}
			/>
			<span
				className={`${separatorColorClassName} ${separatorTextClassName}`}>
				às
			</span>
			<input
				type='text'
				placeholder={compact ? '00:00' : '00'}
				value={
					compact || showFullHoraFim
						? disabled && compact
							? formatHourInputToTimeValue(horaFim)
							: formatHourInputToDisplayTime(horaFim)
						: horaFim
				}
				disabled={disabled}
				inputMode='numeric'
				maxLength={compact || showFullHoraFim ? 5 : 2}
				onChange={(e) =>
					onChangeHoraFim?.(
						compact || showFullHoraFim
							? normalizeTimeInputDisplayValue(e.target.value)
							: normalizeHourInputValue(e.target.value),
					)
				}
				className={`${inputFimWidthClassName} text-center ${inputTextClassName} font-normal outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${inputClassName}`}
			/>
			{showClockIcon && (
				<div className='flex items-center justify-center'>
					<MdAccessTime
						className={`${compact ? 'h-3 w-3' : 'w-4.5 h-4.5'} ${iconColorClassName}`}
					/>
				</div>
			)}
		</div>
	);
};

const isValidDisplayDate = (value = '') => {
	if (!String(value || '').trim()) return true;
	return Boolean(formatDateInputToISO(value));
};

const validateContextoField = (field, value, { showUnidadeFilter } = {}) => {
	if (
		field === 'estabelecimento' &&
		showUnidadeFilter &&
		!String(value || '').trim()
	) {
		return 'Selecione uma unidade.';
	}
	if (field === 'setor' && !String(value || '').trim()) {
		return 'Selecione um setor.';
	}
	return '';
};

// No cadastro inline, o vínculo é necessário para montar a prévia da jornada.
// Esta regra precisa ser avaliada antes desse primeiro POST: caso contrário, o
// backend rejeita a escala e deixa um vínculo institucional sem alocação.
export const getErroAlocacaoAntesDaAdmissao = ({
	dataAdmissao = '',
	dataInicioAlocacao = '',
} = {}) => {
	const admissaoIso = formatDateInputToISO(dataAdmissao);
	const inicioAlocacaoIso = formatDateInputToISO(dataInicioAlocacao);

	if (
		!admissaoIso ||
		!inicioAlocacaoIso ||
		inicioAlocacaoIso >= admissaoIso
	) {
		return '';
	}

	return 'A alocação não pode começar antes da admissão institucional do vínculo. Ajuste a admissão ou a data inicial da escala.';
};

const validateNovoProfissionalField = (
	field,
	value,
	{ lockedFields = {}, form = {}, allowPastVigenciaInicio = false } = {},
) => {
	if (lockedFields[field]) return '';

	switch (field) {
		case 'nome': {
			const normalizedName = formatProfessionalNameInput(value, {
				trimEnd: true,
			});
			if (!normalizedName) return 'Informe o nome.';
			if (normalizedName !== String(value || '').trim()) {
				return 'Informe apenas letras e espaços.';
			}
			return '';
		}
		case 'cpf': {
			const digits = normalizeCpf(value);
			if (!digits) return 'Informe o CPF.';
			if (digits.length < 11) return 'CPF incompleto.';
			return '';
		}
		case 'admissao':
			if (!String(value || '').trim()) return '';
			return isValidDisplayDate(value) ? '' : 'Data inválida.';
		case 'vigencia_inicio':
			if (!String(value || '').trim()) return 'Informe a data inicial.';
			if (!isValidDisplayDate(value)) return 'Data inválida.';
			if (
				!allowPastVigenciaInicio &&
				formatDateInputToISO(value) < getTodayISODateLocal()
			) {
				return 'A data inicial deve ser hoje ou uma data futura.';
			}
			if (
				isWeeklyRecorrencia(form?.tipo_recorrencia) &&
				!form?.permitir_inicio_semanal_fim_semana &&
				!isWeekdayDateValue(value)
			) {
				return 'Para recorrência semanal, a data inicial deve cair entre segunda e sexta.';
			}
			return '';
		case 'regime':
			return String(value || '').trim() ? '' : 'Selecione um regime.';
		case 'nome_recorrencia':
			return '';
		case 'dias_semana':
			return Array.isArray(value) && value.length > 0
				? ''
				: 'Selecione ao menos um dia da semana.';
		case 'datas_extras':
			return Array.isArray(value) && value.length > 0
				? ''
				: 'Adicione ao menos uma data específica com horário.';
		case 'vinculo_tipo_id':
			if (!String(value || '').trim()) return 'Selecione um vínculo.';
			return !normalizeDigits(value, 10) ? 'Informe apenas números.' : '';
		case 'numero_registro':
			return value && !normalizeDigits(value, 20)
				? 'Informe apenas números.'
				: '';
		case 'telefone': {
			const digits = normalizeDigits(value, 11);
			if (value && digits.length < 10) return 'Telefone incompleto.';
			return '';
		}
		default:
			return '';
	}
};

const validateVacanciaField = (field, value) => {
	switch (field) {
		case 'data_inicio_vacancia':
			if (!String(value || '').trim()) return 'Informe a data inicial.';
			if (!isValidDisplayDate(value)) return 'Data inválida.';
			if (formatDateInputToISO(value) < getTodayISODateLocal()) {
				return 'A data inicial deve ser hoje ou uma data futura.';
			}
			return '';
		case 'especialidade_id':
			return String(value || '').trim() ? '' : 'Selecione uma opção.';
		case 'regime':
			return String(value || '').trim() ? '' : 'Selecione um regime.';
		case 'turno':
			return String(value || '').trim() &&
				!String(value || '').startsWith('-')
				? ''
				: 'Selecione um turno.';
		case 'dias_semana':
			return Array.isArray(value) && value.length > 0
				? ''
				: 'Selecione ao menos um dia da semana.';
		default:
			return '';
	}
};

const STATUS_CELL_CLASS = {
	SUGERIDO: 'bg-[#4D6C92] border border-[#4D6C92]',
	ATRIBUIDO: 'bg-[#4D6C92] border border-[#4D6C92]',
	VACANCIA: 'bg-[#BD504E] border border-[#BD504E]',
	FERIAS: 'bg-[#6ACEF8] border border-[#6ACEF8]',
	ATESTADO: 'bg-[#FB965C] border border-[#FB965C]',
	FOLGA: 'bg-[#8BBD8B] border border-[#8BBD8B]',
	LICENCA: 'bg-[#A476C7] border border-[#A476C7]',
	FALTA: 'bg-[#545454] border border-[#545454]',
	TROCA: 'bg-[#967771] border border-[#967771]',
	BLOQUEADO: 'bg-gray-300 border border-gray-300',
	NAO_ATRIBUIDO: 'border border-gray-300 bg-white',
};

const EMPTY_CELL_CLASS = 'border border-gray-300 bg-white';

const getLegendaColorClass = (codigo = '') => {
	const normalizedCodigo = normalizeCode(codigo);
	if (STATUS_CELL_CLASS[normalizedCodigo]) {
		return STATUS_CELL_CLASS[normalizedCodigo];
	}
	if (normalizedCodigo === 'NAO ATRIBUIDO') {
		return STATUS_CELL_CLASS.NAO_ATRIBUIDO;
	}
	return EMPTY_CELL_CLASS;
};

const isVacanciaLinha = (linha = {}) =>
	normalizeCode(linha?.tipo_item) === 'VACANCIA';

const isLinhaSugestaoOcultaPorLimpeza = (linha = {}) =>
	Boolean(linha?.sugestao?.oculta_na_grade_por_limpeza);

const getCardItemId = (card = {}) =>
	Number(card?.id || card?.item_id || card?.dados_edicao?.id || 0) || null;

const getPersistedCards = (celula = {}) =>
	(Array.isArray(celula?.cards) ? celula.cards : []).filter((card) =>
		Boolean(getCardItemId(card)),
	);

const AUSENCIA_STATUS_KEYS = new Set([
	'FERIAS',
	'ATESTADO',
	'FOLGA',
	'LICENCA',
	'FALTA',
	'TROCA',
]);

const getCardAusenciaStatus = (card = {}) => {
	const candidates = [
		card?.tipo_item,
		card?.tipo,
		card?.status,
		card?.codigo,
		card?.resumo?.tipo_item,
		card?.resumo?.tipo,
		card?.resumo?.status,
		card?.dados_edicao?.tipo_item,
		card?.dados_edicao?.tipo,
		card?.ausencia?.tipo_item,
		card?.ausencia?.tipo,
		card?.ausencia?.status,
	];

	return candidates
		.map((candidate) => normalizeCode(candidate))
		.find((status) => AUSENCIA_STATUS_KEYS.has(status));
};

const getCellCardAusenciaStatus = (celula = {}) =>
	getPersistedCards(celula)
		.map((card) => getCardAusenciaStatus(card))
		.find(Boolean) || '';

const getCardShiftSource = (card = {}, celula = {}, linha = {}) => {
	const horario =
		card?.horario ||
		card?.resumo?.horario ||
		card?.edicao?.horario ||
		card?.troca?.horario ||
		{};

	return {
		hora_inicio:
			card?.hora_inicio ||
			card?.dados_edicao?.hora_inicio ||
			horario?.hora_inicio ||
			card?.resumo?.hora_inicio ||
			celula?.hora_inicio ||
			linha?.regime_escala?.hora_inicio ||
			linha?.hora_inicio ||
			'',
		hora_fim:
			card?.hora_fim ||
			card?.dados_edicao?.hora_fim ||
			horario?.hora_fim ||
			card?.resumo?.hora_fim ||
			celula?.hora_fim ||
			linha?.regime_escala?.hora_fim ||
			linha?.hora_fim ||
			'',
		turno:
			card?.turno ||
			card?.dados_edicao?.turno ||
			horario?.turno ||
			card?.resumo?.turno ||
			celula?.turno ||
			linha?.regime_escala?.turno ||
			linha?.turno ||
			linha?.regime_escala?.nome ||
			'',
		regime:
			card?.regime ||
			card?.dados_edicao?.regime ||
			card?.resumo?.regime ||
			celula?.regime ||
			linha?.regime_escala?.regime ||
			linha?.regime ||
			'',
		faixa_horaria_texto:
			horario?.faixa_horaria_texto ||
			card?.faixa_horaria_texto ||
			card?.resumo?.faixa_horaria_texto ||
			card?.resumo?.nome_faixa_horaria ||
			'',
		nome_faixa_horaria:
			card?.faixa_horaria?.nome ||
			card?.nome_faixa_horaria ||
			card?.resumo?.nome_faixa_horaria ||
			'',
		tipo_item:
			card?.tipo_item || celula?.tipo_item || linha?.tipo_item || '',
	};
};

const hasCardShiftMetadata = (card = {}, celula = {}, linha = {}) => {
	const shiftSource = getCardShiftSource(card, celula, linha);
	return Boolean(
		getCardItemId(card) ||
		shiftSource.hora_inicio ||
		shiftSource.hora_fim ||
		shiftSource.turno ||
		shiftSource.faixa_horaria_texto ||
		shiftSource.nome_faixa_horaria,
	);
};

const hasPersistedCard = (celula = {}) => getPersistedCards(celula).length > 0;

const getLinhaVigenciaFimIso = (linha = {}) =>
	formatDateInputToISO(
		linha?.regime_escala?.vigencia_fim ||
			linha?.regime_escala?.data_fim ||
			linha?.vigencia_fim ||
			linha?.regime_vinculo?.vigencia_fim ||
			linha?.regime_vinculo?.data_fim ||
			'',
	);

export const isCelulaLegadaRenderizavel = ({
	celula = {},
	linha = {},
	modalidade = '',
	today = getTodayISODateLocal(),
} = {}) => {
	const data = formatDateInputToISO(celula?.data);
	const vigenciaFim = getLinhaVigenciaFimIso(linha);
	const vigenciaInicio = getDataInicialProfissionalEscalaIsoFromLinha(linha);
	const status = normalizeCode(celula?.status);

	return Boolean(
		// Na enfermagem, sugestões encerradas sem card persistido não são
		// alocações. Elas chegavam como quadrados azulados/opacos, sem turno,
		// e mantinham profissionais sem escala visíveis na grade.
		normalizeCode(modalidade) !== 'ENFERMAGEM' &&
		data &&
		vigenciaFim &&
		vigenciaFim < formatDateInputToISO(today) &&
		(!vigenciaInicio || data >= vigenciaInicio) &&
		data <= vigenciaFim &&
		!isLinhaSugestaoOcultaPorLimpeza(linha) &&
		!hasPersistedCard(celula) &&
		(celula?.sugerido === true || status === 'SUGERIDO'),
	);
};

const linhaHasCardRenderizavel = ({ linha = {}, modalidade = '' } = {}) =>
	(Array.isArray(linha?.celulas) ? linha.celulas : []).some(
		(celula) =>
			hasPersistedCard(celula) ||
			isCelulaLegadaRenderizavel({ celula, linha, modalidade }),
	);

// ENCERRAR_ALOCACAO_SETOR preserva o histórico. A linha deve continuar na
// competência em que ainda incidiu, mesmo quando o seu fim já passou; só pode
// desaparecer a partir da competência seguinte.
export const isLinhaEncerradaNoContextoOperacional = ({
	linha = {},
	mesReferencia = '',
} = {}) => {
	const vigenciaFim = getLinhaVigenciaFimIso(linha);
	const competencia = formatDateInputToISO(mesReferencia);

	return Boolean(vigenciaFim && competencia && vigenciaFim < competencia);
};

const hasCellShiftReference = (celula = {}, linha = {}) => {
	const firstCard = getPersistedCards(celula)[0] || null;
	const hiddenByCleanup =
		isLinhaSugestaoOcultaPorLimpeza(linha) &&
		(firstCard == null || !firstCard?.id) &&
		(celula?.cards || []).length === 0;
	if (hiddenByCleanup) return false;
	if (firstCard) return hasCardShiftMetadata(firstCard, celula, linha);
	return Boolean(celula?.hora_inicio || celula?.hora_fim || celula?.turno);
};

export const getCellVisualClass = (
	celula = {},
	linha = {},
	modalidade = '',
) => {
	const status = normalizeCode(celula?.status);
	const isVacanciaLine = normalizeCode(linha?.tipo_item) === 'VACANCIA';
	if (
		isLinhaSugestaoOcultaPorLimpeza(linha) &&
		(celula?.cards || []).length === 0
	) {
		return EMPTY_CELL_CLASS;
	}
	if (
		!hasPersistedCard(celula) &&
		!isCelulaLegadaRenderizavel({ celula, linha, modalidade })
	)
		return EMPTY_CELL_CLASS;
	if (isVacanciaLine && hasCellShiftReference(celula, linha)) {
		return STATUS_CELL_CLASS.VACANCIA;
	}
	// Em "Todos os setores", a célula agregadora pode manter o status da
	// alocação, embora o card daquele dia seja uma ausência. O card é o dado
	// específico do dia e deve determinar sua cor para não esconder férias,
	// folgas, atestados e demais ausências.
	const cardAusenciaStatus = getCellCardAusenciaStatus(celula);
	if (cardAusenciaStatus) return STATUS_CELL_CLASS[cardAusenciaStatus];
	// A API pode manter NAO_ATRIBUIDO na célula agregadora mesmo quando
	// existe um card persistido de outro setor. Nesse caso, o card é a fonte
	// de verdade para a grade consolidada e deve aparecer como atribuído.
	if (status === 'NAO_ATRIBUIDO' || status === 'NAO ATRIBUIDO') {
		return STATUS_CELL_CLASS.ATRIBUIDO;
	}
	if (STATUS_CELL_CLASS[status]) return STATUS_CELL_CLASS[status];
	if (hasPersistedCard(celula)) return STATUS_CELL_CLASS.ATRIBUIDO;
	return EMPTY_CELL_CLASS;
};

const getCellPrimaryCard = (celula = {}) => {
	const cards = getPersistedCards(celula);
	return (
		cards.find((card) => hasCardShiftMetadata(card, celula)) ||
		cards[0] ||
		null
	);
};

const getCellShiftText = (celula = {}, linha = {}) => {
	const firstCard = getCellPrimaryCard(celula);
	if (!hasCellShiftReference(celula, linha)) return '';

	return getShiftIndicator(
		getCardShiftSource(firstCard || {}, celula, linha),
	);
};

const getLinhaShiftIndicatorMajority = (linha = {}) => {
	const celulas = Array.isArray(linha?.celulas) ? linha.celulas : [];
	const contagem = { D: 0, N: 0, '24h': 0 };

	celulas.forEach((celula) => {
		const firstCard = getCellPrimaryCard(celula || {});
		if (!firstCard) return;
		const indicador = getShiftIndicator(
			getCardShiftSource(firstCard, celula, linha),
		);
		if (contagem[indicador] !== undefined) {
			contagem[indicador] += 1;
		}
	});

	const entries = Object.entries(contagem).sort(([, a], [, b]) => b - a);
	const [topSigla, topCount] = entries[0] || [];
	if (topCount > 0) return topSigla;
	return getLinhaShiftIndicator(linha);
};

const AUSENCIA_TIPO_TO_TAB = {
	FERIAS: 'ferias',
	ATESTADO: 'atestado',
	FOLGA: 'folga',
	LICENCA: 'licenca',
	FALTA: 'falta',
	TROCA: 'troca',
};

const normalizeTipoItem = (value = '') => normalizeCode(value);

const getAvailableActionsSet = (linha = {}, celula = null) => {
	const lineActions = Array.isArray(linha?.acoes_disponiveis)
		? linha.acoes_disponiveis
		: [];
	const cellActions = Array.isArray(celula?.acoes_disponiveis)
		? celula.acoes_disponiveis
		: [];
	const actions = (lineActions.length > 0 ? lineActions : cellActions)
		.map((item) => normalizeCode(item))
		.filter(Boolean);

	return new Set(actions);
};

const isActionAvailable = ({ linha = null, celula = null, acao = '' } = {}) => {
	const availableActions = getAvailableActionsSet(linha, celula);
	if (availableActions.size === 0) return true;
	const normalizedAction = normalizeCode(acao);
	if (normalizedAction === 'ENCERRAR_ALOCACAO_SETOR') {
		return (
			availableActions.has('ENCERRAR_ALOCACAO_SETOR') ||
			availableActions.has('REMOVER_FUTURO')
		);
	}
	if (normalizedAction === 'ENCERRAR_ALOCACOES_MODALIDADE') {
		return (
			availableActions.has('ENCERRAR_ALOCACOES_MODALIDADE') ||
			availableActions.has('REMOVER_FUTURO')
		);
	}
	return availableActions.has(normalizedAction);
};

const getActionUnavailableMessage = (acao = '') => {
	const action = normalizeCode(acao);
	if (action === 'REMOVER_FUTURO' || action === 'ENCERRAR_ALOCACAO_SETOR') {
		return 'A linha selecionada não permite remover o profissional da escala a partir de hoje.';
	}
	if (action === 'ENCERRAR_ALOCACOES_MODALIDADE') {
		return 'A linha selecionada não permite encerrar as alocações do profissional nesta modalidade.';
	}
	if (action === 'REMOVER_COMPETENCIA') {
		return 'A linha selecionada não permite limpar a competência.';
	}
	if (action === 'REMOVER_DIA') {
		return 'A linha selecionada não permite remover somente este dia.';
	}
	if (action === 'REGISTRAR_AUSENCIA') {
		return 'A linha selecionada não permite registrar ausência.';
	}
	if (action === 'REGISTRAR_TROCA') {
		return 'A linha selecionada não permite registrar troca.';
	}
	if (action === 'ALTERAR_TURNO') {
		return 'A linha selecionada não permite alterar turno.';
	}
	if (action === 'ATRIBUIR') {
		return 'A linha selecionada não permite atribuir este dia.';
	}
	return 'A ação selecionada não está disponível para esta linha.';
};

const getCellTipoItem = (celula = {}) => {
	const firstCard = getCellPrimaryCard(celula);
	const candidates = [
		celula?.tipo_item,
		firstCard?.tipo_item,
		firstCard?.tipo,
		firstCard?.status,
		celula?.status,
	];

	for (const candidate of candidates) {
		const tipo = normalizeTipoItem(candidate);
		if (AUSENCIA_TIPO_TO_TAB[tipo]) return tipo;
	}

	return '';
};

const getCellDateISO = (celula = {}, fallbackDate = '') => {
	const firstCard = getCellPrimaryCard(celula);
	return formatDateInputToISO(
		celula?.data ||
			firstCard?.data ||
			firstCard?.horario?.data ||
			firstCard?.dados_edicao?.data ||
			fallbackDate ||
			'',
	);
};

const isTrocaCard = (card = {}, celula = {}) => {
	const candidates = [
		card?.tipo_item,
		card?.tipo,
		card?.status,
		card?.codigo,
		card?.resumo?.tipo_item,
		card?.resumo?.tipo,
		card?.dados_edicao?.tipo_item,
		card?.troca?.tipo_item,
		celula?.tipo_item,
		celula?.status,
	];

	return candidates.some((value) => normalizeTipoItem(value) === 'TROCA');
};

// Uma troca confirmada é um item mensal autoritativo. Mesmo que a projeção
// temporária ainda não exponha uma alocação operacional para a nova pessoa da
// linha, o card não pode desaparecer da competência em que foi efetivado.
const linhaTemCardTrocaPersistido = (linha = {}) =>
	(Array.isArray(linha?.celulas) ? linha.celulas : []).some((celula = {}) =>
		getPersistedCards(celula).some((card = {}) => isTrocaCard(card, celula)),
	);

const buildTrocaDataExtraItem = ({ card = {}, celula = {}, linha = {} }) => {
	const data = formatDateInputToISO(
		card?.data ||
			card?.data_referencia ||
			card?.horario?.data ||
			card?.troca?.data ||
			card?.troca?.horario?.data ||
			card?.resumo?.data ||
			card?.dados_edicao?.data ||
			celula?.data ||
			'',
	);
	const shiftSource = getCardShiftSource(card, celula, linha);
	const horaInicio = getHourInputFromTimeValue(shiftSource?.hora_inicio);
	const horaFim = getHourInputFromTimeValue(shiftSource?.hora_fim);

	if (!data || !horaInicio || !horaFim) return null;

	return {
		id: `troca-extra-${getCardItemId(card) || data}-${horaInicio}-${horaFim}`,
		selected_date: data,
		hora_inicio: horaInicio,
		hora_fim: horaFim,
		readOnly: true,
		origem: 'troca',
		label: 'Troca',
	};
};

const extrairDatasExtrasTrocaLinha = (linha = {}) => {
	const seen = new Set();
	const extras = [];

	(Array.isArray(linha?.celulas) ? linha.celulas : []).forEach((celula) => {
		getPersistedCards(celula).forEach((card) => {
			if (!isTrocaCard(card, celula)) return;

			const item = buildTrocaDataExtraItem({ card, celula, linha });
			if (!item) return;

			const key = `${item.selected_date}-${item.hora_inicio}-${item.hora_fim}`;
			if (seen.has(key)) return;
			seen.add(key);
			extras.push(item);
		});
	});

	return extras.sort((a, b) => {
		const dataCompare = String(a.selected_date).localeCompare(
			String(b.selected_date),
		);
		if (dataCompare !== 0) return dataCompare;
		return String(a.hora_inicio).localeCompare(String(b.hora_inicio));
	});
};

const addTrocaItemId = (acc, value) => {
	const numeric = Number(value);
	if (Number.isFinite(numeric) && numeric > 0) {
		acc.add(numeric);
	}
};

const collectTrocaItemIds = (value, acc = new Set(), visited = new Set()) => {
	if (value == null) return acc;
	if (typeof value !== 'object') {
		addTrocaItemId(acc, value);
		return acc;
	}
	if (visited.has(value)) return acc;
	visited.add(value);

	if (Array.isArray(value)) {
		value.forEach((item) => collectTrocaItemIds(item, acc, visited));
		return acc;
	}

	[
		value?.item_id,
		value?.escala_item_id,
		value?.card_id,
		value?.id,
		value?.item?.id,
		value?.item?.item_id,
		value?.card?.id,
		value?.card?.item_id,
		value?.escala_item?.id,
		value?.escala_item?.item_id,
	].forEach((candidate) => addTrocaItemId(acc, candidate));

	return acc;
};

const collectTrocaItemIdsFromSources = (sources = []) => {
	const acc = new Set();
	sources.forEach((source) => collectTrocaItemIds(source, acc));
	return [...acc];
};

const getTrocaVisualGroupsFromCard = (card = {}) => {
	const troca =
		card?.troca ||
		card?.resumo?.troca ||
		card?.dados_edicao?.troca ||
		card?.operacao_troca ||
		{};
	const pacote =
		card?.pacote ||
		card?.troca_pacote ||
		troca?.pacote ||
		troca?.resumo ||
		{};

	const itensA = collectTrocaItemIdsFromSources([
		card?.itens_a,
		card?.itens_origem,
		card?.origens,
		card?.origem,
		card?.item_origem,
		card?.item_origem_id,
		card?.troca_item_origem_id,
		card?.origem_item_id,
		troca?.itens_a,
		troca?.itens_origem,
		troca?.origens,
		troca?.origem,
		troca?.item_origem,
		troca?.item_origem_id,
		troca?.troca_item_origem_id,
		troca?.origem_item_id,
		pacote?.itens_a,
		pacote?.itens_origem,
		pacote?.origens,
	]);

	const itensB = collectTrocaItemIdsFromSources([
		card?.itens_b,
		card?.itens_destino,
		card?.destinos,
		card?.destino,
		card?.item_destino,
		card?.item_destino_id,
		card?.troca_item_destino_id,
		card?.destino_item_id,
		troca?.itens_b,
		troca?.itens_destino,
		troca?.destinos,
		troca?.destino,
		troca?.item_destino,
		troca?.item_destino_id,
		troca?.troca_item_destino_id,
		troca?.destino_item_id,
		pacote?.itens_b,
		pacote?.itens_destino,
		pacote?.destinos,
	]);

	if (!itensA.length || !itensB.length) return null;

	return {
		itensA,
		itensB,
		signature: `${[...itensA].sort((a, b) => a - b).join(',')}|${[...itensB]
			.sort((a, b) => a - b)
			.join(',')}`,
	};
};

const cloneLinhaForTrocaVisual = (linha = {}) => ({
	...linha,
	celulas: (Array.isArray(linha?.celulas) ? linha.celulas : []).map(
		(celula = {}) => ({
			...celula,
			cards: Array.isArray(celula?.cards) ? [...celula.cards] : [],
		}),
	),
});

const getOrCreateTrocaVisualCell = ({
	linhas,
	linhaIndex,
	data,
	templateCell = {},
}) => {
	const linha = linhas[linhaIndex];
	if (!linha || !data) return null;
	if (!Array.isArray(linha.celulas)) linha.celulas = [];

	let celula = linha.celulas.find(
		(item) => String(item?.data) === String(data),
	);
	if (celula) {
		if (!Array.isArray(celula.cards)) celula.cards = [];
		return celula;
	}

	celula = {
		...templateCell,
		data,
		cards: [],
		status: templateCell?.status || 'ATRIBUIDO',
		tipo_item: templateCell?.tipo_item || '',
	};
	linha.celulas.push(celula);
	linha.celulas.sort((a, b) =>
		String(a?.data || '').localeCompare(b?.data || ''),
	);
	return celula;
};

const applyTrocaVisualRendering = (linhas = []) => {
	if (!Array.isArray(linhas) || linhas.length === 0) return linhas;

	const locations = new Map();
	const trocaGroups = [];
	const seenGroupSignatures = new Set();

	linhas.forEach((linha, linhaIndex) => {
		(Array.isArray(linha?.celulas) ? linha.celulas : []).forEach(
			(celula, celulaIndex) => {
				getPersistedCards(celula).forEach((card) => {
					const cardId = getCardItemId(card);
					const data = getCellDateISO(celula);
					if (cardId && data) {
						locations.set(cardId, {
							linhaIndex,
							celulaIndex,
							data,
							card,
							celula,
						});
					}

					if (!isTrocaCard(card, celula)) return;
					const group = getTrocaVisualGroupsFromCard(card);
					if (!group || seenGroupSignatures.has(group.signature))
						return;
					seenGroupSignatures.add(group.signature);
					trocaGroups.push(group);
				});
			},
		);
	});

	if (!trocaGroups.length) return linhas;

	// A troca altera o responsável do card, não o dono da linha. Manter o
	// card no slot recebido evita criar uma segunda linha visual para cada
	// profissional envolvido na troca.
	const manterCardsNoSlotOriginal = !linhas.some(
		(linha = {}) => linha?._mover_cards_troca_equivalente === true,
	);
	if (manterCardsNoSlotOriginal) return linhas;

	const linhasClone = linhas.map(cloneLinhaForTrocaVisual);
	let movedAny = false;

	const removeCardFromClone = (cardId) => {
		linhasClone.forEach((linha) => {
			(Array.isArray(linha?.celulas) ? linha.celulas : []).forEach(
				(celula) => {
					if (!Array.isArray(celula?.cards)) return;
					celula.cards = celula.cards.filter(
						(card) => getCardItemId(card) !== cardId,
					);
				},
			);
		});
	};

	const addCardToClone = ({ location, targetLinhaIndex }) => {
		const celula = getOrCreateTrocaVisualCell({
			linhas: linhasClone,
			linhaIndex: targetLinhaIndex,
			data: location.data,
			templateCell: location.celula,
		});
		if (!celula) return;

		const cardId = getCardItemId(location.card);
		if (celula.cards.some((card) => getCardItemId(card) === cardId)) return;

		celula.cards.unshift({
			...location.card,
			_renderizado_por_troca_equivalente: true,
		});
		movedAny = true;
	};

	trocaGroups.forEach(({ itensA, itensB }) => {
		const locationsA = itensA
			.map((id) => locations.get(id))
			.filter(Boolean);
		const locationsB = itensB
			.map((id) => locations.get(id))
			.filter(Boolean);
		if (!locationsA.length || !locationsB.length) return;

		const targetLinhaForA = locationsB[0]?.linhaIndex;
		const targetLinhaForB = locationsA[0]?.linhaIndex;
		if (
			!Number.isInteger(targetLinhaForA) ||
			!Number.isInteger(targetLinhaForB)
		) {
			return;
		}

		[...itensA, ...itensB].forEach(removeCardFromClone);
		locationsA.forEach((location) =>
			addCardToClone({ location, targetLinhaIndex: targetLinhaForA }),
		);
		locationsB.forEach((location) =>
			addCardToClone({ location, targetLinhaIndex: targetLinhaForB }),
		);
	});

	return movedAny ? linhasClone : linhas;
};

const getCellObservation = (celula = {}) => {
	const firstCard = getCellPrimaryCard(celula);
	return String(
		firstCard?.dados_edicao?.observacao ||
			firstCard?.edicao?.observacao ||
			firstCard?.observacao ||
			firstCard?.resumo?.observacao ||
			firstCard?.motivo ||
			firstCard?.descricao ||
			celula?.observacao ||
			'',
	).trim();
};

const getCellContinuousPeriodo = (
	linha = {},
	celula = {},
	fallbackDate = '',
) => {
	const tipoItem = getCellTipoItem(celula);
	if (!AUSENCIA_TIPO_TO_TAB[tipoItem] || tipoItem === 'TROCA') {
		return null;
	}

	const targetDate = getCellDateISO(celula, fallbackDate);
	if (!targetDate) return null;

	const relevantDates = [
		...new Set(
			(linha?.celulas || [])
				.filter(
					(linhaCelula) => getCellTipoItem(linhaCelula) === tipoItem,
				)
				.map((linhaCelula) => getCellDateISO(linhaCelula))
				.filter(Boolean),
		),
	].sort();

	if (!relevantDates.length) return null;

	let dataInicio = targetDate;
	let dataFim = targetDate;

	while (relevantDates.includes(addDaysToISODate(dataInicio, -1))) {
		dataInicio = addDaysToISODate(dataInicio, -1);
	}

	while (relevantDates.includes(addDaysToISODate(dataFim, 1))) {
		dataFim = addDaysToISODate(dataFim, 1);
	}

	return {
		periodo_inicio: formatISOToDisplay(dataInicio),
		periodo_fim: formatISOToDisplay(dataFim),
	};
};

const getCellPeriodo = (linha = {}, celula = {}, fallbackDate = '') => {
	const periodoContinuo = getCellContinuousPeriodo(
		linha,
		celula,
		fallbackDate,
	);
	if (periodoContinuo) return periodoContinuo;

	const firstCard = getCellPrimaryCard(celula);
	const dataInicio =
		celula?.data_inicio ||
		celula?.periodo_inicio ||
		celula?.periodo?.data_inicio ||
		firstCard?.data_inicio ||
		firstCard?.periodo_inicio ||
		firstCard?.periodo?.data_inicio ||
		celula?.data ||
		fallbackDate ||
		'';
	const dataFim =
		celula?.data_fim ||
		celula?.periodo_fim ||
		celula?.periodo?.data_fim ||
		firstCard?.data_fim ||
		firstCard?.periodo_fim ||
		firstCard?.periodo?.data_fim ||
		celula?.data ||
		fallbackDate ||
		'';

	return {
		periodo_inicio:
			formatISOToDisplay(dataInicio) || formatISOToDisplay(fallbackDate),
		periodo_fim:
			formatISOToDisplay(dataFim) || formatISOToDisplay(fallbackDate),
	};
};

const getCellActionPreset = (linha = {}, celula = {}, fallbackDate = '') => {
	const tipoItem = getCellTipoItem(celula);
	if (!tipoItem) return null;

	if (tipoItem === 'TROCA') {
		return {
			// Uma troca já efetivada não pode iniciar outra troca. Mantemos o
			// indicador ativo na interface, mas sem abrir a aba ou tentar obter
			// detalhes que o card não garante neste escopo.
			openTab: null,
			formPatch: {},
		};
	}

	return {
		openTab: AUSENCIA_TIPO_TO_TAB[tipoItem],
		formPatch: {
			acao: 'REGISTRAR_AUSENCIA',
			tipo_item: tipoItem,
			...getCellPeriodo(linha, celula, fallbackDate),
			observacao: getCellObservation(celula),
		},
	};
};

const IDEMPOTENCY_MONTH_LABELS = [
	'jan',
	'fev',
	'mar',
	'abr',
	'mai',
	'jun',
	'jul',
	'ago',
	'set',
	'out',
	'nov',
	'dez',
];

const toIdempotencySlug = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

const getCompetenciaIdempotencyPrefix = (mesReferencia = '') => {
	const iso = formatDateInputToISO(mesReferencia);
	if (!iso) return 'competencia';
	const [ano, mes] = iso.split('-');
	const monthLabel = IDEMPOTENCY_MONTH_LABELS[Number(mes) - 1] || 'mes';
	return `${monthLabel}-${ano}`;
};

const getIdempotencyDayToken = (value = '') => {
	const iso = formatDateInputToISO(value);
	if (!iso) return '';
	const [, , dia] = iso.split('-');
	return String(Number(dia) || '').trim();
};

const buildActionIdempotencyKey = ({
	contexto,
	acao,
	regime_vinculo_id,
	datas,
	periodo,
	tipo_item,
	item_origem_id,
	item_destino_id,
	data_corte,
} = {}) => {
	const competenciaPrefix = getCompetenciaIdempotencyPrefix(
		contexto?.mes_referencia,
	);
	const action = normalizeCode(acao);
	const regimeId = Number(regime_vinculo_id || 0) || null;

	if (action === 'REGISTRAR_TROCA') {
		const origem = Number(item_origem_id || 0) || 'origem';
		const destino = Number(item_destino_id || 0) || 'destino';
		return `${competenciaPrefix}-troca-${origem}-${destino}`;
	}

	const baseParts = [competenciaPrefix];
	if (regimeId) {
		baseParts.push('regime', String(regimeId));
	}

	if (action === 'ATRIBUIR') {
		const dias = (Array.isArray(datas) ? datas : [])
			.map((data) => getIdempotencyDayToken(data))
			.filter(Boolean);
		return [...baseParts, 'atribuir', ...dias].join('-');
	}

	if (action === 'REMOVER_DIA') {
		const dias = (Array.isArray(datas) ? datas : [])
			.map((data) => getIdempotencyDayToken(data))
			.filter(Boolean);
		return [...baseParts, 'remover-dia', ...dias].join('-');
	}

	if (action === 'REMOVER_COMPETENCIA') {
		return [
			competenciaPrefix,
			'limpar',
			...(regimeId ? ['regime', String(regimeId)] : []),
		].join('-');
	}

	if (action === 'REMOVER_FUTURO') {
		return regimeId
			? `regime-${regimeId}-remover-futuro`
			: 'remover-futuro';
	}

	if (action === 'ENCERRAR_ALOCACAO_SETOR') {
		return [
			competenciaPrefix,
			'encerrar-setor',
			...(regimeId ? [String(regimeId)] : []),
			formatDateInputToISO(data_corte) || 'sem-corte',
		].join('-');
	}

	if (action === 'ENCERRAR_ALOCACOES_MODALIDADE') {
		return [
			competenciaPrefix,
			'encerrar-modalidade',
			...(regimeId ? [String(regimeId)] : []),
			formatDateInputToISO(data_corte) || 'sem-corte',
		].join('-');
	}

	if (action === 'REGISTRAR_AUSENCIA') {
		const absenceType = toIdempotencySlug(tipo_item) || 'ausencia';
		const dataInicio =
			formatDateInputToISO(periodo?.data_inicio) || 'sem-data-inicio';
		const dataFim =
			formatDateInputToISO(periodo?.data_fim) || 'sem-data-fim';
		return [...baseParts, absenceType, dataInicio, dataFim].join('-');
	}

	if (action === 'ALTERAR_TURNO') {
		return [...baseParts, 'alterar-turno'].join('-');
	}

	return [...baseParts, toIdempotencySlug(action) || 'acao'].join('-');
};

const buildApplyPreenchimentoIdempotencyKey = ({
	contexto,
	prefix = 'preenchimento',
	regimeVinculoId,
	primeiroDiaTrabalhoMes,
	operacaoId,
	fallbackToken,
} = {}) => {
	const competenciaPrefix = getCompetenciaIdempotencyPrefix(
		contexto?.mes_referencia,
	);
	const regimeId = Number(regimeVinculoId || 0) || null;
	const dayToken = getIdempotencyDayToken(primeiroDiaTrabalhoMes);
	const operationToken =
		String(operacaoId || '').trim() ||
		String(fallbackToken || '').trim() ||
		`${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

	return [
		competenciaPrefix,
		toIdempotencySlug(prefix) || 'preenchimento',
		regimeId ? `regime-${regimeId}` : '',
		dayToken ? `dia-${dayToken}` : '',
		`op-${toIdempotencySlug(operationToken)}`,
	]
		.filter(Boolean)
		.join('-');
};

const getPreenchimentoPreviewEtag = (response = {}) =>
	response?.preview_etag ||
	response?.preview?.preview_etag ||
	response?.etag ||
	response?.headers?.etag ||
	response?.headers?.ETag ||
	'';

// O preview é a autoridade da operação: em jornadas repartidas, a entrada de
// confirmação já vem promovida para unidade JORNADA e pode conter os IDs de
// jornada inferidos pelo backend. Não reconstruir esse comando no cliente.
export const buildApplyActionBodyFromPreview = ({
	preview = null,
	fallbackBody = null,
	idempotencyKey = '',
} = {}) => {
	const entradaAplicar = preview?.acoes?.aplicar?.entrada;
	const comandoNormalizado = preview?.comando_normalizado;
	const base =
		entradaAplicar && typeof entradaAplicar === 'object'
			? entradaAplicar
			: comandoNormalizado && typeof comandoNormalizado === 'object'
				? comandoNormalizado
				: fallbackBody || {};
	const previewEtag = getPreenchimentoPreviewEtag(preview || {});

	return {
		...base,
		...(idempotencyKey ? { chave_idempotencia: idempotencyKey } : {}),
		...(previewEtag ? { preview_etag: previewEtag } : {}),
	};
};

const normalizeUnidades = (payload) => {
	const list = Array.isArray(payload)
		? payload
		: Array.isArray(payload?.results)
			? payload.results
			: [];
	const map = new Map();
	list.forEach((item) => {
		const id = String(
			item?.cnes ||
				item?.cnes_estabelecimento ||
				item?.id ||
				item?.codigo ||
				'',
		).trim();
		const nome = String(
			item?.nome ||
				item?.nome_fantasia ||
				item?.unidade ||
				item?.label ||
				'',
		).trim();
		if (!id || !nome || shouldHideMedicalScaleUnit({ nome }) || map.has(id))
			return;
		map.set(id, { id, nome });
	});
	return Array.from(map.values());
};

const normalizeCatalogSetores = (catalogos = {}) => {
	const tiposSecao = Array.isArray(catalogos?.tiposSecao)
		? catalogos.tiposSecao
		: Array.isArray(catalogos?.servicos)
			? catalogos.servicos
			: [];

	const diretos = Array.isArray(catalogos?.setores) ? catalogos.setores : [];
	const aninhados = tiposSecao.flatMap((tipo) =>
		Array.isArray(tipo?.setores) ? tipo.setores : [],
	);
	const todos = diretos.length > 0 ? diretos : aninhados;
	const dedup = new Map();
	todos.forEach((setor) => {
		const id = setor?.id;
		if (!id || dedup.has(String(id))) return;
		const nomeExibicao =
			setor?.nome_exibicao || setor?.nome || `Setor ${id}`;
		dedup.set(String(id), {
			id,
			nome: setor?.nome || nomeExibicao,
			nome_exibicao: nomeExibicao,
			identificador:
				setor?.identificador ||
				setor?.codigo ||
				setor?.codigo_identificador ||
				'',
		});
	});
	return Array.from(dedup.values());
};

const getDimensionLabel = (linha = {}, modalidade = 'MEDICA') => {
	if (modalidade === 'ENFERMAGEM') {
		return (
			linha?.categoria?.nome ||
			linha?.vinculo_profissional?.categoria?.nome ||
			'Categoria'
		);
	}
	return (
		linha?.especialidade?.nome ||
		linha?.vinculo_profissional?.especialidade?.nome ||
		'Área de atuação'
	);
};

const getLinhaDisplayName = (linha = {}) => {
	if (isVacanciaLinha(linha)) return 'VACÂNCIA';
	return (
		linha?.profissional?.nome || linha?.vinculo_profissional?.nome || '-'
	);
};

const getFirstCardFromLinha = (linha = {}) =>
	Array.isArray(linha?.celulas)
		? linha.celulas.flatMap((celula) => celula?.cards || [])[0] || null
		: null;

const getCpfFromLinha = (linha = {}) =>
	formatCpfMask(
		linha?.profissional?.cpf ||
			linha?.vinculo_profissional?.cpf ||
			linha?.cpf ||
			linha?.profissional?.documento ||
			linha?.vinculo_profissional?.documento ||
			'',
	);

const getAdmissaoFromLinha = (linha = {}) =>
	formatISOToDisplay(
		linha?.vinculo_profissional?.data_admissao ||
			linha?.vinculo_profissional?.admissao ||
			linha?.profissional?.admissao ||
			'',
	);

const getDataInicialMinimaNovoSetor = (linha = {}) => {
	const datasMinimas = [
		getTodayISODateLocal(),
		formatDateInputToISO(getAdmissaoFromLinha(linha)),
	].filter(Boolean);

	return datasMinimas.sort().at(-1) || getTodayISODateLocal();
};

const getCargoFromLinha = (linha = {}) =>
	String(
		linha?.vinculo_profissional?.cargo ||
			linha?.profissional?.cargo ||
			getFirstCardFromLinha(linha)?.cargo ||
			linha?.cargo ||
			'',
	).trim();

const getNumeroConselhoFromLinha = (linha = {}) =>
	getNumeroConselhoFromProfissional(
		linha?.profissional ||
			linha?.vinculo_profissional ||
			getFirstCardFromLinha(linha) ||
			linha,
	);

const getUfConselhoFromLinha = (linha = {}) =>
	getUfConselhoFromProfissional(
		linha?.profissional ||
			linha?.vinculo_profissional ||
			getFirstCardFromLinha(linha) ||
			linha,
	);

const getNomeVinculoFromLinha = (linha = {}) =>
	getNomeVinculoFromProfissional(
		linha?.vinculo_profissional ||
			linha?.profissional ||
			getFirstCardFromLinha(linha) ||
			linha,
	) || String(getFirstCardFromLinha(linha)?.descricao_vinculo || '').trim();

const formatDiasSemanaResumo = (diasSemana = []) => {
	const dias = normalizeDiasSemanaValues(diasSemana);
	if (dias.length === 0) return 'Sem dias recorrentes';

	return dias
		.map(
			(dia) =>
				DIAS_SEMANA_OPTIONS.find(
					(option) => Number(option.value) === Number(dia),
				)?.label || dia,
		)
		.join(', ');
};

const getRegimeDisplayName = (regime = '', regimesOptions = []) => {
	const normalized = normalizeCode(regime);
	const match = regimesOptions.find(
		(item = {}) =>
			normalizeCode(item?.codigo || item?.value || '') === normalized,
	);

	return match?.nome || match?.label || String(regime || 'Regime').trim();
};

const getLinhaDatasEspecificasResumo = (linha = {}) => {
	const items = [];
	const seen = new Set();
	const regimeEscala = linha?.regime_escala || {};
	const tipoRecorrencia = normalizeCode(regimeEscala?.tipo_recorrencia);
	const padroes = normalizePadroesAlocacao(regimeEscala?.padroes_alocacao);
	const padroesDatasEspecificas =
		tipoRecorrencia === 'DATAS_ESPECIFICAS'
			? padroes
			: padroes.filter(
					(padrao = {}) =>
						normalizeCode(padrao?.tipo_recorrencia) ===
						'DATAS_ESPECIFICAS',
				);

	const adicionarData = (
		dataValue,
		horaInicioValue = regimeEscala?.hora_inicio,
		horaFimValue = regimeEscala?.hora_fim,
	) => {
		const data = formatDateInputToISO(dataValue);
		const horaInicio = formatHourInputToTimeValue(horaInicioValue);
		const horaFim = formatHourInputToTimeValue(horaFimValue);
		if (!data || !horaInicio || !horaFim) return;

		const key = `${data}|${horaInicio}|${horaFim}`;
		if (seen.has(key)) return;
		seen.add(key);
		items.push({
			data,
			dataLabel: formatISOToDisplay(data),
			horaInicio,
			horaFim,
			label: `${formatISOToDisplay(data)} - ${horaInicio} às ${horaFim}`,
		});
	};

	padroesDatasEspecificas.forEach((padrao = {}) => {
		const datas = Array.isArray(padrao?.datas_especificas)
			? padrao.datas_especificas
			: [padrao?.data || padrao?.selected_date].filter(Boolean);
		datas.forEach((dataValue) =>
			adicionarData(dataValue, padrao?.hora_inicio, padrao?.hora_fim),
		);
	});

	const datasDiretas = [
		...(Array.isArray(regimeEscala?.datas_especificas)
			? regimeEscala.datas_especificas
			: []),
		...(Array.isArray(regimeEscala?.datas_extras)
			? regimeEscala.datas_extras
			: []),
	];
	datasDiretas.forEach((dataItem) => {
		const item = dataItem && typeof dataItem === 'object' ? dataItem : {};
		adicionarData(
			item?.selected_date || item?.data || dataItem,
			item?.hora_inicio || item?.horaInicio,
			item?.hora_fim || item?.horaFim,
		);
	});

	const projecaoSplit = getProjecaoSplitLinha(linha);
	const ehProjecaoRecorrenteDeSplit =
		projecaoSplit?.derivada_de_split === true &&
		normalizeCode(projecaoSplit?.tipo) !== 'PONTUAL';
	const possuiRecorteSemClassificacao =
		getParticoesPersistidas([linha]).length > 0 && !projecaoSplit?.tipo;
	// Datas específicas que o ambiente devolve para representar segmentos de
	// um recorte são detalhes técnicos da jornada, não exceções criadas pelo
	// usuário. As faixas são exibidas no próprio dia recorrente; mantê-las
	// também neste bloco duplica informações e induz edição pelo fluxo errado.
	return ehProjecaoRecorrenteDeSplit || possuiRecorteSemClassificacao
		? []
		: items;
};

const getLinhaSetorId = (linha = {}) =>
	linha?.setor?.id ||
	linha?.setor_id ||
	linha?.regime_escala?.setor ||
	linha?.regime_escala?.setor_id ||
	getFirstCardFromLinha(linha)?.setor?.id ||
	getFirstCardFromLinha(linha)?.setor_id ||
	null;

const getLinhaSetorNome = (linha = {}, setorOptions = []) => {
	const setorLinha = linha?.setor || getFirstCardFromLinha(linha)?.setor;
	const setorId = getLinhaSetorId(linha);
	const setorCatalogo = setorOptions.find(
		(setor) => String(setor.id) === String(setorId),
	);

	return (
		formatSetorOptionLabel(setorLinha) ||
		formatSetorOptionLabel(setorCatalogo) ||
		String(linha?.nome_setor || linha?.setor_nome || '').trim() ||
		'Setor'
	);
};

const getProjecaoSplitLinha = (linha = {}) =>
	linha?.regime_escala?.projecao_split || linha?.projecao_split || {};

const getPapelSetorProjecaoSplit = (linha = {}) =>
	normalizeCode(getProjecaoSplitLinha(linha)?.papel_setor);

const isLinhaDestinoDeSplit = (linha = {}) =>
	getPapelSetorProjecaoSplit(linha) === 'DESTINO';

const isLinhaOrigemDeSplit = (linha = {}) => {
	if (getPapelSetorProjecaoSplit(linha) === 'ORIGEM') return true;

	// Algumas leituras filtradas do ambiente não repetem `papel_setor`, mas
	// mantêm o detalhe da alocação. Nesse caso, o setor de origem informado
	// pelo próprio agregado é a referência segura para não tratá-lo como
	// destino apenas porque há partições associadas.
	const setorDaLinha = String(getLinhaSetorId(linha) || '').trim();
	const setorDaOrigem = String(
		linha?.alocacao_origem?.setor_origem?.id ||
			linha?.alocacao_origem?.setor_origem_id ||
			linha?.regime_escala?.alocacao_origem?.setor_origem?.id ||
			linha?.regime_escala?.alocacao_origem?.setor_origem_id ||
			'',
	).trim();
	return Boolean(
		setorDaLinha && setorDaOrigem && setorDaLinha === setorDaOrigem,
	);
};

const getAlocacaoOrigemId = (linha = {}) => {
	const card = getFirstCardFromLinha(linha);
	const projecaoSplit = getProjecaoSplitLinha(linha);
	return (
		linha?.alocacao_origem_id ||
		linha?.alocacao_origem?.id ||
		linha?.regime_escala?.alocacao_origem_id ||
		linha?.regime_escala?.alocacao_origem?.id ||
		projecaoSplit?.alocacao_origem_id ||
		projecaoSplit?.alocacao_origem?.id ||
		linha?.vinculo_profissional?.alocacao_origem_id ||
		card?.alocacao_origem_id ||
		card?.origem?.alocacao_origem_chave ||
		null
	);
};

const getVersaoEsperadaAlocacaoOrigem = (linha = {}) => {
	const projecaoSplit = getProjecaoSplitLinha(linha);
	const candidatos = [
		linha?.versao_esperada,
		linha?.alocacao_origem?.versao,
		linha?.regime_escala?.alocacao_origem?.versao,
		linha?.regime_escala?.versao_alocacao_origem,
		projecaoSplit?.versao_alocacao_origem,
		projecaoSplit?.versao,
	];
	return Number(candidatos.find((versao) => Number(versao) > 0) || 0);
};

const getAlocacoesOrigemFromPayload = (payload = {}) => {
	const candidates = [
		payload?.alocacoes,
		payload?.alocacoes_origem,
		payload?.ambiente?.alocacoes_origem,
		payload?.ambiente?.alocacoes,
		payload?.resultado?.alocacoes_origem,
		payload?.resultado?.alocacoes,
		payload?.alocacao_origem ? [payload.alocacao_origem] : [],
		payload?.estado_aplicado ? [payload.estado_aplicado] : [],
		payload?.resultado?.estado_aplicado
			? [payload.resultado.estado_aplicado]
			: [],
	];
	const seen = new Set();
	return candidates
		.flatMap((candidate) => (Array.isArray(candidate) ? candidate : []))
		.filter((alocacao = {}) => {
			const id = String(
				alocacao?.id ||
					alocacao?.alocacao_origem_id ||
					alocacao?.chave ||
					'',
			);
			if (!id || seen.has(id)) return false;
			seen.add(id);
			return true;
		});
};

const getAlocacaoOrigemDaLinha = (linha = {}, alocacoesOrigem = []) => {
	const alocacaoOrigemId = String(getAlocacaoOrigemId(linha) || '');
	if (!alocacaoOrigemId) return linha?.alocacao_origem || null;
	return (
		alocacoesOrigem.find(
			(alocacao = {}) =>
				String(
					alocacao?.id ||
						alocacao?.alocacao_origem_id ||
						alocacao?.chave ||
						'',
				) === alocacaoOrigemId,
		) ||
		linha?.alocacao_origem ||
		null
	);
};

// A projeção mensal pode demorar a deixar de listar uma linha destino após a
// remoção de um recorte. O agregado de alocação de origem é a fonte de
// verdade: se ele não possui mais a partição daquele setor, a linha não pode
// voltar ao modal como um setor vazio sem vínculo.
export const filtrarLinhasDestinosInativos = ({
	linhas = [],
	alocacoesOrigem = [],
} = {}) => {
	const alocacoesPorId = new Map(
		(Array.isArray(alocacoesOrigem) ? alocacoesOrigem : [])
			.map((alocacao = {}) => [
				String(
					alocacao?.alocacao_origem_id ||
						alocacao?.id ||
						alocacao?.chave ||
						'',
				).trim(),
				alocacao,
			])
			.filter(([id]) => Boolean(id)),
	);

	return (Array.isArray(linhas) ? linhas : []).filter((linha = {}) => {
		const alocacaoOrigemId = String(
			getAlocacaoOrigemId(linha) || '',
		).trim();
		const alocacaoOrigem = alocacoesPorId.get(alocacaoOrigemId);
		if (!alocacaoOrigem) return true;

		const setorLinha = String(getLinhaSetorId(linha) || '').trim();
		const setorOrigem = String(
			alocacaoOrigem?.setor_origem?.id ||
				alocacaoOrigem?.setor_origem_id ||
				alocacaoOrigem?.setor?.id ||
				'',
		).trim();
		if (!setorLinha || !setorOrigem || setorLinha === setorOrigem) {
			return true;
		}

		return (
			Array.isArray(alocacaoOrigem?.particoes)
				? alocacaoOrigem.particoes
				: []
		).some(
			(particao = {}) =>
				String(particao?.setor_destino_id || '').trim() === setorLinha,
		);
	});
};

const enriquecerLinhaComAlocacoesOrigem = (
	linha = {},
	alocacoesOrigem = [],
) => {
	const alocacaoOrigem = getAlocacaoOrigemDaLinha(linha, alocacoesOrigem);
	const alocacaoOrigemId =
		getAlocacaoOrigemId(linha) ||
		alocacaoOrigem?.id ||
		alocacaoOrigem?.alocacao_origem_id ||
		alocacaoOrigem?.chave ||
		'';
	const setoresLinhas = Array.isArray(linha?.setores_linhas)
		? linha.setores_linhas.map((linhaSetor = {}) =>
				enriquecerLinhaComAlocacoesOrigem(linhaSetor, alocacoesOrigem),
			)
		: linha?.setores_linhas;
	if (!alocacaoOrigemId && !setoresLinhas) return linha;

	const regimeEscala = linha?.regime_escala || {};
	return {
		...linha,
		...(alocacaoOrigemId ? { alocacao_origem_id: alocacaoOrigemId } : {}),
		...(alocacaoOrigem ? { alocacao_origem: alocacaoOrigem } : {}),
		regime_escala: {
			...regimeEscala,
			...(alocacaoOrigemId
				? { alocacao_origem_id: alocacaoOrigemId }
				: {}),
			...(alocacaoOrigem ? { alocacao_origem: alocacaoOrigem } : {}),
		},
		...(setoresLinhas ? { setores_linhas: setoresLinhas } : {}),
	};
};

const enriquecerLinhasComAlocacoesOrigem = (
	linhas = [],
	alocacoesOrigem = [],
) =>
	(Array.isArray(linhas) ? linhas : []).map((linha = {}) =>
		enriquecerLinhaComAlocacoesOrigem(linha, alocacoesOrigem),
	);

const buildLinhaEstruturalDaAlocacaoOrigem = (alocacao = {}) => {
	const configuracao = alocacao?.configuracao || {};
	const alocacaoOrigemId =
		alocacao?.alocacao_origem_id || alocacao?.id || alocacao?.chave || '';
	const setorOrigem = alocacao?.setor_origem ||
		alocacao?.setor || {
			id: configuracao?.setor_origem_id,
		};
	const padroesOrigem = Array.isArray(alocacao?.padroes_origem)
		? alocacao.padroes_origem
		: Array.isArray(configuracao?.padroes_origem)
			? configuracao.padroes_origem
			: [];
	const alocacaoNormalizada = {
		...alocacao,
		id: alocacaoOrigemId,
		particoes: Array.isArray(alocacao?.particoes) ? alocacao.particoes : [],
		padroes_origem: padroesOrigem,
	};
	return {
		__estado_estrutural_alocacao_origem: true,
		linha_id: `alocacao-origem-${alocacaoOrigemId}-origem`,
		alocacao_origem_id: alocacaoOrigemId,
		versao_esperada: alocacao?.versao,
		alocacao_origem: alocacaoNormalizada,
		setor: setorOrigem,
		setor_id:
			setorOrigem?.id ||
			alocacao?.setor_origem_id ||
			configuracao?.setor_origem_id,
		vinculo_profissional: {
			id:
				alocacao?.vinculo_profissional_id ||
				configuracao?.vinculo_profissional_id,
		},
		profissional: { id: alocacao?.profissional_id },
		regime_escala: {
			regime: alocacao?.regime || configuracao?.regime,
			alocacao_origem_id: alocacaoOrigemId,
			versao_alocacao_origem: alocacao?.versao,
			alocacao_origem: alocacaoNormalizada,
			padroes_alocacao: padroesOrigem,
			projecao_split: {
				derivada_de_split: false,
				tipo: 'RECORRENTE',
				papel_setor: 'ORIGEM',
			},
		},
	};
};

// A leitura estrutural descreve a origem e as suas partições, mas a projeção
// mensal pode devolver somente a última linha operacional do setor de destino.
// Criamos uma representação visual dos destinos a partir desse estado oficial
// para que uma linha independente adicionada ao mesmo setor não esconda as
// faixas que continuam pertencendo à alocação de origem.
const buildLinhasDestinoEstruturaisDaAlocacaoOrigem = (alocacao = {}) => {
	const configuracao = alocacao?.configuracao || {};
	const alocacaoOrigemId = String(
		alocacao?.alocacao_origem_id || alocacao?.id || alocacao?.chave || '',
	);
	if (!alocacaoOrigemId) return [];

	const padroesOrigem = Array.isArray(alocacao?.padroes_origem)
		? alocacao.padroes_origem
		: Array.isArray(configuracao?.padroes_origem)
			? configuracao.padroes_origem
			: [];
	const particoes = Array.isArray(alocacao?.particoes)
		? alocacao.particoes
		: [];
	const alocacaoNormalizada = {
		...alocacao,
		id: alocacaoOrigemId,
		padroes_origem: padroesOrigem,
		particoes,
	};
	const destinos = new Map();

	particoes.forEach((particao = {}, index) => {
		const setorDestinoId = Number(particao?.setor_destino_id || 0);
		const padraoOrigem = padroesOrigem.find(
			(padrao = {}) =>
				String(getPadraoOrigemId(padrao) || '') ===
				String(particao?.padrao_origem_id || ''),
		);
		if (!setorDestinoId || !padraoOrigem) return;

		const diasSemana = normalizeDiasSemanaValues(
			padraoOrigem?.dias_semana,
		).filter((diaSemana) =>
			particaoCobreDiaRecorrente(particao, diaSemana),
		);
		const datasEspecificas = Array.from(
			new Set(
				(Array.isArray(particao?.aplicacao?.datas_especificas)
					? particao.aplicacao.datas_especificas
					: Array.isArray(particao?.datas_especificas)
						? particao.datas_especificas
						: []
				)
					.map((data) => formatDateInputToISO(data))
					.filter(Boolean),
			),
		);
		if (diasSemana.length === 0 && datasEspecificas.length === 0) return;

		const padraoDestino = {
			padrao_id:
				particao?.particao_id ||
				`${alocacaoOrigemId}-${setorDestinoId}-${index}`,
			tipo_recorrencia:
				diasSemana.length > 0
					? padraoOrigem?.tipo_recorrencia || 'SEMANAL'
					: 'DATAS_ESPECIFICAS',
			preset_ciclo:
				padraoOrigem?.preset_ciclo ||
				(diasSemana.length > 0 ? 'SEMANAL' : 'PERSONALIZADO'),
			dias_semana: diasSemana,
			datas_especificas: datasEspecificas,
			hora_inicio: formatHourInputToTimeValue(particao?.hora_inicio),
			hora_fim: formatHourInputToTimeValue(particao?.hora_fim),
			data_ancora: padraoOrigem?.data_ancora || null,
		};
		if (!padraoDestino.hora_inicio || !padraoDestino.hora_fim) return;

		const destinoAtual = destinos.get(setorDestinoId) || [];
		destinos.set(setorDestinoId, [...destinoAtual, padraoDestino]);
	});

	return Array.from(destinos.entries()).map(
		([setorDestinoId, padroesDestino]) => {
			const vigenciaInicioDestino = particoes
				.filter(
					(particao = {}) =>
						Number(particao?.setor_destino_id || 0) ===
						Number(setorDestinoId),
				)
				.flatMap((particao = {}) => [
					particao?.vigencia_inicio,
					particao?.data_inicio,
					particao?.data_inicial,
					particao?.aplicacao?.vigencia_inicio,
					particao?.aplicacao?.data_inicio,
					particao?.criado_em,
					particao?.created_at,
				])
				.map((data) => formatDateInputToISO(data))
				.filter(Boolean)
				.sort()[0];
			return {
				__estado_estrutural_alocacao_origem: true,
				__destino_estrutural_alocacao_origem: true,
				linha_id: `alocacao-origem-${alocacaoOrigemId}-destino-${setorDestinoId}`,
				alocacao_origem_id: alocacaoOrigemId,
				versao_esperada: alocacao?.versao,
				alocacao_origem: alocacaoNormalizada,
				setor: { id: setorDestinoId },
				setor_id: setorDestinoId,
				...(vigenciaInicioDestino
					? {
							vigencia_inicio: vigenciaInicioDestino,
							data_inicial: vigenciaInicioDestino,
						}
					: {}),
				vinculo_profissional: {
					id:
						alocacao?.vinculo_profissional_id ||
						configuracao?.vinculo_profissional_id,
				},
				profissional: { id: alocacao?.profissional_id },
				regime_escala: {
					regime: alocacao?.regime || configuracao?.regime,
					alocacao_origem_id: alocacaoOrigemId,
					versao_alocacao_origem: alocacao?.versao,
					alocacao_origem: alocacaoNormalizada,
					padroes_alocacao: padroesDestino,
					...(vigenciaInicioDestino
						? {
								vigencia_inicio: vigenciaInicioDestino,
								data_ancora: vigenciaInicioDestino,
							}
						: {}),
					dias_semana: normalizeDiasSemanaValues(
						padroesDestino.flatMap(
							(padrao = {}) => padrao?.dias_semana || [],
						),
					),
					projecao_split: {
						derivada_de_split: true,
						tipo: 'RECORRENTE',
						papel_setor: 'DESTINO',
						alocacao_origem_id: alocacaoOrigemId,
						versao_origem: alocacao?.versao,
					},
				},
			};
		},
	);
};

const buildLinhasEstruturaisDaAlocacaoOrigem = (alocacao = {}) => [
	buildLinhaEstruturalDaAlocacaoOrigem(alocacao),
	...buildLinhasDestinoEstruturaisDaAlocacaoOrigem(alocacao),
];

const isLinhaEstruturalAlocacaoOrigem = (linha = {}) =>
	linha?.__estado_estrutural_alocacao_origem === true;

const isLinhaDestinoEstruturalAlocacaoOrigem = (linha = {}) =>
	linha?.__destino_estrutural_alocacao_origem === true;

const removerLinhasEstruturaisDuplicadas = (linhas = []) =>
	(Array.isArray(linhas) ? linhas : []).filter((linha = {}, index, items) => {
		if (!isLinhaEstruturalAlocacaoOrigem(linha)) return true;
		// A linha operacional mensal pode estar resumida ou refletir apenas a
		// última alteração. A estrutural complementa a origem e os destinos; a
		// consolidação posterior as une na mesma aba. Só descartamos uma cópia
		// estrutural idêntica, nunca a contraparte mensal.
		const chaveEstrutural = String(
			linha?.linha_id ||
				`${getAlocacaoOrigemId(linha) || ''}-${getLinhaSetorId(linha) || ''}`,
		);
		return !items
			.slice(0, index)
			.some(
				(linhaAnterior = {}) =>
					isLinhaEstruturalAlocacaoOrigem(linhaAnterior) &&
					String(
						linhaAnterior?.linha_id ||
							`${getAlocacaoOrigemId(linhaAnterior) || ''}-${
								getLinhaSetorId(linhaAnterior) || ''
							}`,
					) === chaveEstrutural,
			);
	});

const getAlocacaoOrigemIdFromPayload = (payload = {}) => {
	const alocacoesOrigem = getAlocacoesOrigemFromPayload(payload);
	const candidatos = [
		payload?.id,
		payload?.chave,
		payload?.alocacao_origem_id,
		payload?.alocacao_origem?.id,
		payload?.linha?.alocacao_origem_id,
		payload?.linha?.alocacao_origem?.id,
		payload?.resultado?.alocacao_origem_id,
		payload?.resultado?.alocacao_origem?.id,
		payload?.estado_aplicado?.alocacao_origem_id,
		payload?.resultado?.estado_aplicado?.alocacao_origem_id,
		payload?.error?.data?.alocacao_origem_id,
		payload?.error?.data?.alocacao_origem?.id,
		payload?.error?.detail?.alocacao_origem_id,
		payload?.error?.detail?.alocacao_origem?.id,
		payload?.data?.alocacao_origem_id,
		payload?.data?.alocacao_origem?.id,
		...alocacoesOrigem.map(
			(alocacao = {}) =>
				alocacao?.id || alocacao?.alocacao_origem_id || alocacao?.chave,
		),
	];
	const linhas = [
		...(Array.isArray(payload?.linhas) ? payload.linhas : []),
		...(Array.isArray(payload?.ambiente?.linhas)
			? payload.ambiente.linhas
			: []),
	];
	return (
		candidatos.find(Boolean) ||
		getAlocacaoOrigemId(
			linhas.find((linha = {}) => Boolean(getAlocacaoOrigemId(linha))),
		)
	);
};

const getParticoesFromPayload = (payload = {}) => {
	const candidatos = [
		payload?.particoes,
		payload?.comando_normalizado?.particoes,
		payload?.acoes?.aplicar?.entrada?.particoes,
		payload?.entrada?.particoes,
		payload?.preview?.particoes,
		payload?.preview?.comando_normalizado?.particoes,
		payload?.preview?.acoes?.aplicar?.entrada?.particoes,
		payload?.resultado?.particoes,
		payload?.estado_aplicado?.particoes,
		payload?.resultado?.estado_aplicado?.particoes,
		...getAlocacoesOrigemFromPayload(payload).map(
			(alocacao = {}) => alocacao?.particoes,
		),
	];
	return (
		candidatos.find(
			(particoes) => Array.isArray(particoes) && particoes.length > 0,
		) ||
		candidatos.find(Array.isArray) ||
		[]
	);
};

const getApiErrorCode = (payload = {}) => {
	const candidatos = [
		payload?.codigo,
		payload?.code,
		payload?.resumo?.codigo,
		payload?.resumo?.code,
		payload?.error?.code,
		payload?.error?.codigo,
		payload?.error?.data?.codigo,
		payload?.error?.data?.code,
		payload?.error?.detail?.codigo,
		payload?.data?.codigo,
		payload?.data?.code,
		payload?.resultado?.codigo,
		payload?.resultado?.code,
		payload?.resultado?.resumo?.codigo,
		payload?.resultado?.resumo?.code,
		...(Array.isArray(payload?.issues) ? payload.issues : []),
		...(Array.isArray(payload?.erros) ? payload.erros : []),
		...(Array.isArray(payload?.error?.issues) ? payload.error.issues : []),
	];
	const candidato = candidatos.find((item) => {
		if (typeof item === 'string') return Boolean(item.trim());
		return Boolean(item?.codigo || item?.code);
	});
	return normalizeCode(
		typeof candidato === 'string'
			? candidato
			: candidato?.codigo || candidato?.code,
	);
};

const getAcoesAlocacaoOrigemFromPayload = (payload = {}) => {
	const candidatos = [
		payload?.acoes,
		payload?.alocacao_origem?.acoes,
		payload?.resultado?.acoes,
		payload?.resultado?.alocacao_origem?.acoes,
		payload?.data?.acoes,
		payload?.data?.alocacao_origem?.acoes,
		payload?.error?.acoes,
		payload?.error?.data?.acoes,
		payload?.error?.data?.alocacao_origem?.acoes,
		payload?.error?.detail?.acoes,
	];
	return (
		candidatos.find(
			(acoes) =>
				acoes &&
				typeof acoes === 'object' &&
				Object.keys(acoes).length > 0,
		) || {}
	);
};

// A decisão de reaproveitar uma origem é do backend. Basta que a linha já
// pertença a uma alocação de origem; dia, horário e setor não podem fazer o
// cliente desviar para a criação legada de outra carga.
export const deveAdicionarSetorPelaAlocacaoOrigem = (linha = {}) =>
	Boolean(String(getAlocacaoOrigemId(linha) || '').trim());

export const isAlocacaoOrigemAtivaExigeEdicao = (payload = {}) =>
	getApiErrorCode(payload) === 'ALOCACAO_ORIGEM_ATIVA_EXIGE_EDICAO';

export const getAcaoParaAdicionarSetorNaAlocacaoOrigem = (
	payload = {},
	{ preferirSugestoes = false } = {},
) => {
	const acoes = getAcoesAlocacaoOrigemFromPayload(payload);
	const acaoDireta =
		acoes?.editar || payload?.alocacao_origem?.acoes?.editar || null;
	const acaoAssistida =
		acoes?.preparar_edicao_como_particao ||
		payload?.alocacao_origem?.acoes?.preparar_edicao_como_particao ||
		null;
	// `preparar_edicao_como_particao` é o comando canônico para acrescentar
	// uma variação à origem. Em alguns ambientes ele abre o planejador de
	// alternativas; nesses casos a inclusão direta usa a ação editar. Nunca
	// escolhemos pela comparação de horário/setor feita no navegador.
	const prepararUsaPlanejador = Boolean(
		acaoAssistida?.href &&
		/(planej|sugest|alternativ)/i.test(String(acaoAssistida.href)),
	);
	if (preferirSugestoes) return acaoAssistida || acaoDireta || null;
	return (
		(prepararUsaPlanejador ? acaoDireta : acaoAssistida) ||
		acaoDireta ||
		acaoAssistida ||
		null
	);
};

const getTrocaPlantaoJaIniciadoMensagem = (payload = {}) => {
	const erro = payload?.error || payload;
	const data = erro?.data || erro?.detail || erro || {};
	const candidatos = [
		data,
		...(Array.isArray(data?.pacote) ? data.pacote : []),
		...(Array.isArray(data?.issues) ? data.issues : []),
		...(Array.isArray(data?.erros) ? data.erros : []),
	];
	const bloqueio = candidatos.find(
		(item = {}) =>
			normalizeCode(item?.codigo || item?.code) === 'PLANTAO_JA_INICIADO',
	);

	return bloqueio
		? String(
				bloqueio?.mensagem ||
					bloqueio?.message ||
					'Todos os cards selecionados precisam estar futuros e ainda não iniciados.',
			).trim()
		: '';
};

const exigeFluxoJornada = (payload = {}) =>
	getApiErrorCode(payload) === 'ALTERACAO_ESTRUTURAL_EXIGE_FLUXO_JORNADA';

const isPreviewDesatualizado = (payload = {}) =>
	getApiErrorCode(payload) === 'PREVIEW_DESATUALIZADO';

const isNenhumaJornadaElegivel = (payload = {}) =>
	getApiErrorCode(payload) === 'NENHUMA_JORNADA_ELEGIVEL';

const isPreviewAcaoConfirmavel = (preview = {}) => {
	const issues = Array.isArray(preview?.issues) ? preview.issues : [];
	return (
		// O contrato atual pode omitir `valido` e informar somente
		// `bloqueado: false`. Nesse caso o preview continua confirmável.
		preview?.valido !== false &&
		preview?.bloqueado !== true &&
		!issues.some(
			(issue = {}) =>
				issue?.bloqueante === true || issue?.blocking === true,
		)
	);
};

const getPreviewRawIssues = (preview = {}) =>
	[
		preview?.issues,
		preview?.preview?.issues,
		preview?.conflitos,
		preview?.preview?.conflitos,
		preview?.erros,
		preview?.preview?.erros,
	].flatMap((items) => (Array.isArray(items) ? items : []));

const getPreviewIssues = (preview = {}) => {
	const issues = getPreviewRawIssues(preview);

	// O preview pode repetir o mesmo diagnóstico para cada card da jornada.
	// Todos continuam bloqueando a ação no servidor, mas exibir centenas de
	// cópias não ajuda a pessoa responsável e torna o modal inutilizável.
	return Array.from(
		new Map(
			issues.map((issue = {}, index) => {
				const mensagem = String(
					issue?.mensagem || issue?.message || issue?.titulo || '',
				).trim();
				const chave = [
					normalizeCode(issue?.codigo || issue?.code),
					String(issue?.campo || issue?.campo_foco || '').trim(),
					normalizeCode(mensagem),
				].join('|');
				return [chave === '||' ? `indice-${index}` : chave, issue];
			}),
		).values(),
	);
};

const getJornadaDivergentePreviewDetail = (preview = {}) => {
	const issues = getPreviewRawIssues(preview).filter(
		(issue = {}) =>
			normalizeCode(issue?.codigo || issue?.code) ===
			'JORNADA_DIVERGENTE',
	);
	const uniqueIds = (field) =>
		Array.from(
			new Set(
				issues
					.flatMap((issue = {}) =>
						Array.isArray(issue?.[field]) ? issue[field] : [],
					)
					.map((id) => String(id || '').trim())
					.filter(Boolean),
			),
		);

	return {
		hasDivergence: issues.length > 0,
		setoresCardsIds: uniqueIds('setores_cards_ids'),
		setoresSemLinhaAtivaIds: uniqueIds('setores_sem_linha_ativa_ids'),
		setoresLinhasAtivasIds: uniqueIds('setores_linhas_ativas_ids'),
	};
};

const getQuantidadeJornadasSemCardsSaneadas = (preview = {}) => {
	const quantidade =
		preview?.preview?.quantidade_jornadas_sem_cards_saneadas ??
		preview?.quantidade_jornadas_sem_cards_saneadas ??
		0;
	return Math.max(0, Number(quantidade) || 0);
};

export const getPreviewBlockMessage = (preview = {}, fallback = '') => {
	const issues = getPreviewIssues(preview);
	const primeiraMensagemIssue = String(
		issues[0]?.mensagem || issues[0]?.message || '',
	).trim();
	// A API ordena os issues pela correção que deve ser feita primeiro. Não
	// substitua esse texto por um resumo agregado nem concatene outros erros:
	// em cada nova prévia a primeira mensagem devolvida é a que deve aparecer.
	if (primeiraMensagemIssue) return primeiraMensagemIssue;
	const resumoMensagem = String(
		preview?.resumo?.mensagem || preview?.preview?.resumo?.mensagem || '',
	).trim();
	if (resumoMensagem) return resumoMensagem;
	return getErrorMessage(preview, fallback) || fallback;
};

const persistirEstadoParticionadoNaLinha = (
	linha = {},
	{ alocacaoOrigemId = '', particoes = [] } = {},
) => {
	if (!linha || !alocacaoOrigemId) return linha;
	const regimeEscala = linha?.regime_escala || {};
	const particoesNormalizadas = Array.isArray(particoes) ? particoes : [];
	return {
		...linha,
		alocacao_origem_id: alocacaoOrigemId,
		particoes_alocacao_origem: particoesNormalizadas,
		alocacao_origem: {
			...(linha?.alocacao_origem || {}),
			id: alocacaoOrigemId,
			particoes: particoesNormalizadas,
		},
		regime_escala: {
			...regimeEscala,
			alocacao_origem_id: alocacaoOrigemId,
			particoes_alocacao_origem: particoesNormalizadas,
			alocacao_origem: {
				...(regimeEscala?.alocacao_origem || {}),
				id: alocacaoOrigemId,
				particoes: particoesNormalizadas,
			},
		},
		setores_linhas: Array.isArray(linha?.setores_linhas)
			? linha.setores_linhas.map((linhaSetor = {}) =>
					persistirEstadoParticionadoNaLinha(linhaSetor, {
						alocacaoOrigemId,
						particoes: particoesNormalizadas,
					}),
				)
			: linha?.setores_linhas,
	};
};

const getPadroesAlocacaoOrigem = (linha = {}) => {
	const candidates = [
		// O padrão da linha/regime selecionado é a referência que a API valida
		// contra o regime_vinculo_id de origem. Metadados agregados de uma
		// alocação podem conter padrões de outros setores e não devem prevalecer.
		linha?.regime_escala?.padroes_alocacao,
		linha?.padroes_alocacao,
		linha?.regime_escala?.padroes_origem,
		linha?.alocacao_origem?.padroes_origem,
		linha?.alocacao_origem?.padroes,
		linha?.padroes_origem,
	];

	return candidates.find(Array.isArray) || [];
};

const getPadraoOrigemId = (padrao = {}) =>
	padrao?.padrao_origem_id ||
	padrao?.origem_padrao_id ||
	padrao?.padrao_id ||
	padrao?.id ||
	null;

const getPadraoOrigemReferenciaCliente = (padrao = {}) =>
	String(padrao?.referencia_cliente || '').trim();

const getPadraoOrigemCorrelationKey = (padrao = {}) => {
	const padraoId = String(getPadraoOrigemId(padrao) || '').trim();
	if (padraoId) return `id:${padraoId}`;
	const referencia = getPadraoOrigemReferenciaCliente(padrao);
	return referencia ? `ref:${referencia}` : '';
};

const getParticaoPadraoOrigemCorrelationKey = (particao = {}) => {
	const padraoId = String(particao?.padrao_origem_id || '').trim();
	if (padraoId) return `id:${padraoId}`;
	const referencia = String(
		particao?.padrao_origem_referencia_cliente || '',
	).trim();
	return referencia ? `ref:${referencia}` : '';
};

const gerarReferenciaClientePadraoOrigem = ({
	padrao = {},
	setorDestinoId = 0,
	indice = 0,
	referenciasExistentes = new Set(),
} = {}) => {
	const escopo =
		getPadraoDiasSemana(padrao).join('-') ||
		getPadraoDatasEspecificas(padrao)
			.join('-')
			.replace(/[^0-9A-Za-z_-]/g, '-') ||
		String(indice + 1);
	const horario = [padrao?.hora_inicio, padrao?.hora_fim]
		.map((valor) =>
			String(formatHourInputToTimeValue(valor) || '').replace(':', ''),
		)
		.filter(Boolean)
		.join('-');
	const base = `novo-${Number(setorDestinoId) || 'setor'}-${escopo}${
		horario ? `-${horario}` : ''
	}`
		.replace(/[^0-9A-Za-z._:-]/g, '-')
		.slice(0, 58);
	let referencia = base || `novo-padrao-${indice + 1}`;
	let sufixo = 2;
	while (referenciasExistentes.has(referencia)) {
		const complemento = `-${sufixo}`;
		referencia = `${base.slice(0, 64 - complemento.length)}${complemento}`;
		sufixo += 1;
	}
	return referencia;
};

// `acoes.editar.entrada.configuracao.padroes_origem` é a lista canônica que
// pertence à alocação que será versionada. A grade mensal pode carregar uma
// projeção de outra versão ou de outro setor; usá-la para resolver IDs fazia o
// backend receber uma partição apontando para um padrão de outra origem.
// Mantemos todos os padrões já referenciados pelas partições e acusamos um
// estado realmente inconsistente antes de disparar uma requisição inválida.
export const reconciliarPadroesOrigemComParticoes = ({
	padroesConfiguracao = [],
	padroesCanonicos = [],
	particoes = [],
} = {}) => {
	const configuracao = Array.isArray(padroesConfiguracao)
		? padroesConfiguracao
		: [];
	const canonicos = Array.isArray(padroesCanonicos) ? padroesCanonicos : [];
	const idsConfiguracao = new Set(
		configuracao
			.map((padrao = {}) => String(getPadraoOrigemId(padrao) || ''))
			.filter(Boolean),
	);
	const referenciasConfiguracao = new Set(
		configuracao.map(getPadraoOrigemReferenciaCliente).filter(Boolean),
	);
	const canonicoPorId = new Map(
		canonicos
			.map((padrao = {}) => [
				String(getPadraoOrigemId(padrao) || ''),
				padrao,
			])
			.filter(([padraoId]) => Boolean(padraoId)),
	);
	const particoesNormalizadas = Array.isArray(particoes) ? particoes : [];
	const idsParticoes = Array.from(
		new Set(
			particoesNormalizadas
				.map((particao = {}) =>
					String(particao?.padrao_origem_id || ''),
				)
				.filter(Boolean),
		),
	);
	const referenciasParticoes = Array.from(
		new Set(
			particoesNormalizadas
				.map((particao = {}) =>
					String(
						particao?.padrao_origem_referencia_cliente || '',
					).trim(),
				)
				.filter(Boolean),
		),
	);
	const padroesParticoesInvalidos = idsParticoes.filter(
		(padraoId) =>
			!idsConfiguracao.has(padraoId) && !canonicoPorId.has(padraoId),
	);
	padroesParticoesInvalidos.push(
		...referenciasParticoes
			.filter((referencia) => !referenciasConfiguracao.has(referencia))
			.map((referencia) => `referencia_cliente:${referencia}`),
		...particoesNormalizadas
			.filter(
				(particao = {}) =>
					Boolean(String(particao?.padrao_origem_id || '').trim()) &&
					Boolean(
						String(
							particao?.padrao_origem_referencia_cliente || '',
						).trim(),
					),
			)
			.map(
				(particao = {}) =>
					`correlacao_ambigua:${particao.padrao_origem_id}`,
			),
	);
	const padroesFinais = [...configuracao];
	idsParticoes.forEach((padraoId) => {
		if (idsConfiguracao.has(padraoId)) return;
		const padraoCanonico = canonicoPorId.get(padraoId);
		if (padraoCanonico) {
			padroesFinais.push(padraoCanonico);
			idsConfiguracao.add(padraoId);
		}
	});

	return { padroesFinais, padroesParticoesInvalidos };
};

const getPadraoDatasEspecificas = (padrao = {}) =>
	(Array.isArray(padrao?.datas_especificas) ? padrao.datas_especificas : [])
		.map((data) => formatDateInputToISO(data))
		.filter(Boolean);

const getPadraoDiasSemana = (padrao = {}) =>
	normalizeDiasSemanaValues([
		...normalizeDiasSemanaValues(padrao?.dias_semana),
		...getPadraoDatasEspecificas(padrao).map((data) =>
			getIsoWeekdayFromValue(data),
		),
	]);

const padraoOrigemCorrespondeAoDestino = ({
	padraoOrigem = {},
	padraoDestino = {},
} = {}) => {
	const datasDestino = getPadraoDatasEspecificas(padraoDestino);
	const datasOrigem = getPadraoDatasEspecificas(padraoOrigem);
	const diasDestino = getPadraoDiasSemana(padraoDestino);
	const diasOrigem = getPadraoDiasSemana(padraoOrigem);

	// Para uma data avulsa, prefira a mesma data. Um padrão semanal de origem
	// também é compatível quando cobre o dia da semana da data informada.
	if (datasDestino.length > 0) {
		if (datasOrigem.length > 0) {
			return datasDestino.every((data) => datasOrigem.includes(data));
		}
		return (
			diasDestino.length > 0 &&
			diasDestino.every((dia) => diasOrigem.includes(dia))
		);
	}

	// Em recorrências semanais não é suficiente a faixa de horário caber: o
	// padrão de origem precisa pertencer ao mesmo dia selecionado no destino.
	return (
		diasDestino.length > 0 &&
		diasDestino.every((dia) => diasOrigem.includes(dia))
	);
};

const horarioEmMinutos = (horario = '') => {
	const [horas, minutos] = String(horario || '')
		.slice(0, 5)
		.split(':')
		.map(Number);
	return Number.isInteger(horas) && Number.isInteger(minutos)
		? horas * 60 + minutos
		: null;
};

const horarioEstaContido = ({
	horaInicioOrigem,
	horaFimOrigem,
	horaInicioDestino,
	horaFimDestino,
} = {}) => {
	const inicioOrigem = horarioEmMinutos(horaInicioOrigem);
	const fimOrigemBruto = horarioEmMinutos(horaFimOrigem);
	const inicioDestino = horarioEmMinutos(horaInicioDestino);
	const fimDestinoBruto = horarioEmMinutos(horaFimDestino);
	if (
		inicioOrigem === null ||
		fimOrigemBruto === null ||
		inicioDestino === null ||
		fimDestinoBruto === null
	) {
		return false;
	}

	const fimOrigem =
		fimOrigemBruto <= inicioOrigem
			? fimOrigemBruto + 24 * 60
			: fimOrigemBruto;
	let fimDestino =
		fimDestinoBruto <= inicioDestino
			? fimDestinoBruto + 24 * 60
			: fimDestinoBruto;
	let inicioDestinoAjustado = inicioDestino;
	if (inicioDestinoAjustado < inicioOrigem) {
		inicioDestinoAjustado += 24 * 60;
		fimDestino += 24 * 60;
	}

	return inicioDestinoAjustado >= inicioOrigem && fimDestino <= fimOrigem;
};

const getEscopoParticaoNoPadrao = ({ particao = {}, padrao = {} } = {}) => {
	const tipoAplicacao = normalizeCode(particao?.aplicacao?.tipo);
	const diasPadrao = getPadraoDiasSemana(padrao);
	const datasPadrao = getPadraoDatasEspecificas(padrao);
	if (tipoAplicacao === 'TODAS_OCORRENCIAS') {
		return { diasSemana: diasPadrao, datasEspecificas: datasPadrao };
	}
	if (tipoAplicacao === 'DIAS_SEMANA') {
		const diasAplicacao = normalizeDiasSemanaValues(
			particao?.aplicacao?.dias_semana,
		);
		return {
			diasSemana: diasAplicacao.filter((dia) => diasPadrao.includes(dia)),
			datasEspecificas: [],
		};
	}
	if (tipoAplicacao === 'DATAS_ESPECIFICAS') {
		const datasAplicacao = (
			Array.isArray(particao?.aplicacao?.datas_especificas)
				? particao.aplicacao.datas_especificas
				: []
		)
			.map(formatDateInputToISO)
			.filter(Boolean);
		return {
			diasSemana: [],
			datasEspecificas: datasAplicacao.filter((data) =>
				datasPadrao.includes(data),
			),
		};
	}
	return { diasSemana: [], datasEspecificas: [] };
};

const intervalosCobremFaixaIntegral = ({
	intervalos = [],
	horaInicio,
	horaFim,
} = {}) => {
	const inicioOrigem = horarioEmMinutos(horaInicio);
	const fimOrigemBruto = horarioEmMinutos(horaFim);
	if (inicioOrigem === null || fimOrigemBruto === null) return false;
	const fimOrigem =
		fimOrigemBruto <= inicioOrigem
			? fimOrigemBruto + 24 * 60
			: fimOrigemBruto;
	const normalizados = (Array.isArray(intervalos) ? intervalos : [])
		.map((intervalo = {}) => {
			let inicio = horarioEmMinutos(intervalo?.hora_inicio);
			let fim = horarioEmMinutos(intervalo?.hora_fim);
			if (inicio === null || fim === null) return null;
			if (fim <= inicio) fim += 24 * 60;
			if (inicio < inicioOrigem) {
				inicio += 24 * 60;
				fim += 24 * 60;
			}
			return {
				inicio: Math.max(inicioOrigem, inicio),
				fim: Math.min(fimOrigem, fim),
			};
		})
		.filter((intervalo) => intervalo && intervalo.fim > intervalo.inicio)
		.sort((a, b) => a.inicio - b.inicio);
	let cursor = inicioOrigem;
	for (const intervalo of normalizados) {
		if (intervalo.inicio > cursor) return false;
		cursor = Math.max(cursor, intervalo.fim);
		if (cursor >= fimOrigem) return true;
	}
	return false;
};

export const getDiasSemanaIntegralmenteParticionados = ({
	padroesOrigem = [],
	particoes = [],
} = {}) => {
	const diasCobertos = new Set();
	(Array.isArray(padroesOrigem) ? padroesOrigem : []).forEach(
		(padrao = {}) => {
			const correlacao = getPadraoOrigemCorrelationKey(padrao);
			if (!correlacao) return;
			const particoesDoPadrao = (
				Array.isArray(particoes) ? particoes : []
			).filter(
				(particao = {}) =>
					getParticaoPadraoOrigemCorrelationKey(particao) ===
					correlacao,
			);
			getPadraoDiasSemana(padrao).forEach((diaSemana) => {
				const intervalos = particoesDoPadrao
					.filter((particao = {}) =>
						getEscopoParticaoNoPadrao({
							particao,
							padrao,
						}).diasSemana.includes(diaSemana),
					)
					.map((particao = {}) => ({
						hora_inicio: particao?.hora_inicio,
						hora_fim: particao?.hora_fim,
					}));
				if (
					intervalosCobremFaixaIntegral({
						intervalos,
						horaInicio: padrao?.hora_inicio,
						horaFim: padrao?.hora_fim,
					})
				) {
					diasCobertos.add(diaSemana);
				}
			});
		},
	);
	return [...diasCobertos].sort((a, b) => a - b);
};

// Uma partição só pode recortar uma ocorrência que já exista na origem. A
// existência de outra faixa com o mesmo horário não é suficiente: criar uma
// quinta em cima do padrão de terça muda indevidamente a jornada-base.
export const setorDestinoInterfereComPadroesDaOrigem = ({
	padroesOrigem = [],
	padroesDestino = [],
} = {}) =>
	(Array.isArray(padroesDestino) ? padroesDestino : []).some(
		(padraoDestino = {}) =>
			(Array.isArray(padroesOrigem) ? padroesOrigem : []).some(
				(padraoOrigem = {}) =>
					padraoOrigemCorrespondeAoDestino({
						padraoOrigem,
						padraoDestino,
					}) &&
					horarioEstaContido({
						horaInicioOrigem: padraoOrigem?.hora_inicio,
						horaFimOrigem: padraoOrigem?.hora_fim,
						horaInicioDestino: padraoDestino?.hora_inicio,
						horaFimDestino: padraoDestino?.hora_fim,
					}),
			),
	);

// A sugestão assistida é reservada para quando a nova escala não pode ser
// formada por um recorte de nenhuma ocorrência já existente na jornada. A
// decisão final continua sendo do servidor: este predicado só escolhe a ação
// HATEOAS de sugestões quando ela foi publicada pela própria origem.
export const deveSolicitarAlternativasParaNovoSetor = ({
	padroesOrigem = [],
	padroesDestino = [],
} = {}) =>
	!setorDestinoInterfereComPadroesDaOrigem({
		padroesOrigem,
		padroesDestino,
	});

// O recorte prioriza o padrão canônico que já contém o dia/data escolhido. Se
// ele ainda não existe, o contrato permite declarar o padrão novo e a partição
// no mesmo preview: o padrão recebe `referencia_cliente` e a partição aponta
// para `padrao_origem_referencia_cliente`. Nunca reutilizamos o UUID de outro
// dia apenas porque os horários coincidem.
export const montarEstadoFinalOrigemComNovoDestino = ({
	padroesOrigem = [],
	padroesDestino = [],
	setorDestinoId = 0,
} = {}) => {
	const padroesFinais = (
		Array.isArray(padroesOrigem) ? padroesOrigem : []
	).map((padrao = {}) => ({ ...padrao }));
	const particoesNovas = [];
	const padroesNaoMapeados = [];
	const padroesNovos = [];
	const referenciasExistentes = new Set(
		padroesFinais.map(getPadraoOrigemReferenciaCliente).filter(Boolean),
	);

	(Array.isArray(padroesDestino) ? padroesDestino : []).forEach(
		(padraoDestino = {}, indiceDestino) => {
			const diasDestino = getPadraoDiasSemana(padraoDestino);
			const datasDestino = getPadraoDatasEspecificas(padraoDestino);
			const cicloContinuo =
				normalizeCode(padraoDestino?.tipo_recorrencia) ===
				'CICLO_CONTINUO';
			const horaInicio = formatHourInputToTimeValue(
				padraoDestino?.hora_inicio,
			);
			const horaFim = formatHourInputToTimeValue(padraoDestino?.hora_fim);
			if (
				(!diasDestino.length && !datasDestino.length && !cicloContinuo) ||
				!horaInicio ||
				!horaFim ||
				!Number(setorDestinoId)
			) {
				padroesNaoMapeados.push(padraoDestino);
				return;
			}

			const padraoEhCompativel = (padraoOrigem = {}) =>
				normalizeCode(padraoOrigem?.tipo_recorrencia) ===
					normalizeCode(padraoDestino?.tipo_recorrencia) &&
				getPayloadPresetCiclo(padraoOrigem?.preset_ciclo) ===
					getPayloadPresetCiclo(padraoDestino?.preset_ciclo) &&
				padraoOrigemCorrespondeAoDestino({
					padraoOrigem,
					padraoDestino,
				}) &&
				horarioEstaContido({
					horaInicioOrigem: padraoOrigem?.hora_inicio,
					horaFimOrigem: padraoOrigem?.hora_fim,
					horaInicioDestino: padraoDestino?.hora_inicio,
					horaFimDestino: padraoDestino?.hora_fim,
				});
			let padraoOrigem = padroesFinais.find(padraoEhCompativel);
			let correlacaoParticao;
			let aplicacao;

			if (padraoOrigem) {
				const padraoOrigemId = getPadraoOrigemId(padraoOrigem);
				const referenciaCliente =
					getPadraoOrigemReferenciaCliente(padraoOrigem);
				if (padraoOrigemId) {
					correlacaoParticao = { padrao_origem_id: padraoOrigemId };
				} else if (referenciaCliente) {
					correlacaoParticao = {
						padrao_origem_referencia_cliente: referenciaCliente,
					};
				}
				aplicacao = cicloContinuo
					? { tipo: 'TODAS_OCORRENCIAS' }
					: diasDestino.length > 0
						? { tipo: 'DIAS_SEMANA', dias_semana: diasDestino }
						: {
								tipo: 'DATAS_ESPECIFICAS',
								datas_especificas: datasDestino,
							};
			} else {
				const referenciaCliente = gerarReferenciaClientePadraoOrigem({
					padrao: padraoDestino,
					setorDestinoId,
					indice: indiceDestino,
					referenciasExistentes,
				});
				referenciasExistentes.add(referenciaCliente);
				const camposPadraoNovo = { ...padraoDestino };
				delete camposPadraoNovo.padrao_id;
				delete camposPadraoNovo.padrao_origem_id;
				delete camposPadraoNovo.origem_padrao_id;
				delete camposPadraoNovo.id;
				padraoOrigem = {
					...camposPadraoNovo,
					referencia_cliente: referenciaCliente,
					dias_semana: diasDestino,
					datas_especificas: datasDestino,
					hora_inicio: horaInicio,
					hora_fim: horaFim,
				};
				padroesFinais.push(padraoOrigem);
				padroesNovos.push(padraoOrigem);
				correlacaoParticao = {
					padrao_origem_referencia_cliente: referenciaCliente,
				};
				aplicacao = { tipo: 'TODAS_OCORRENCIAS' };
			}

			if (!correlacaoParticao) {
				padroesNaoMapeados.push(padraoDestino);
				return;
			}
			particoesNovas.push({
				...correlacaoParticao,
				setor_destino_id: Number(setorDestinoId),
				aplicacao,
				hora_inicio: horaInicio,
				hora_fim: horaFim,
			});
		},
	);

	return {
		padroesFinais,
		particoesNovas,
		padroesNaoMapeados,
		padroesNovos,
	};
};

// A edição de um setor variante é uma substituição do seu recorte completo.
// Manter uma partição antiga do mesmo destino faria o payload misturar os dias
// anteriores com os dias recém-selecionados no modal.
export const substituirParticoesDosSetoresEditados = ({
	particoesAtuais = [],
	setoresDestinoEditados = [],
	particoesEditadas = [],
} = {}) => {
	const destinosEditados = new Set(
		(Array.isArray(setoresDestinoEditados) ? setoresDestinoEditados : [])
			.map((setorId) => Number(setorId || 0))
			.filter(Boolean),
	);
	const resultado = [
		...(Array.isArray(particoesAtuais) ? particoesAtuais : []).filter(
			(particao = {}) =>
				!destinosEditados.has(Number(particao?.setor_destino_id || 0)),
		),
		...(Array.isArray(particoesEditadas) ? particoesEditadas : []),
	];
	return Array.from(
		new Map(
			resultado.map((particao = {}) => [
				JSON.stringify(particao),
				particao,
			]),
		).values(),
	);
};

// Ao remover um destino, recortes parciais voltam naturalmente à origem. Uma
// ocorrência integralmente destinada ao setor removido, porém, representa um
// dia próprio daquele setor na intenção do editor: remova também esse escopo da
// configuração para que ele não reapareça artificialmente no setor-base.
export const montarEstadoFinalOrigemSemDestino = ({
	padroesOrigem = [],
	particoes = [],
	setorDestinoId = 0,
} = {}) => {
	const destinoId = Number(setorDestinoId || 0);
	const todasParticoes = Array.isArray(particoes) ? particoes : [];
	const particoesRemovidas = todasParticoes.filter(
		(particao = {}) =>
			Number(particao?.setor_destino_id || 0) === destinoId,
	);
	const particoesFinais = todasParticoes.filter(
		(particao = {}) =>
			Number(particao?.setor_destino_id || 0) !== destinoId,
	);
	const correlacoesAindaReferenciadas = new Set(
		particoesFinais
			.map(getParticaoPadraoOrigemCorrelationKey)
			.filter(Boolean),
	);
	const padroesFinais = (Array.isArray(padroesOrigem) ? padroesOrigem : [])
		.map((padrao = {}) => {
			const correlacao = getPadraoOrigemCorrelationKey(padrao);
			const particoesRemovidasDoPadrao = particoesRemovidas.filter(
				(particao = {}) =>
					getParticaoPadraoOrigemCorrelationKey(particao) ===
					correlacao,
			);
			if (!correlacao || particoesRemovidasDoPadrao.length === 0) {
				return { ...padrao };
			}

			const tipoRecorrencia = normalizeCode(padrao?.tipo_recorrencia);
			const diasRemovidos =
				tipoRecorrencia === 'SEMANAL'
					? new Set(
							getDiasSemanaIntegralmenteParticionados({
								padroesOrigem: [padrao],
								particoes: particoesRemovidasDoPadrao,
							}),
						)
					: new Set();
			const datasRemovidas = new Set(
				getPadraoDatasEspecificas(padrao).filter((data) => {
					const intervalos = particoesRemovidasDoPadrao
						.filter((particao = {}) =>
							getEscopoParticaoNoPadrao({
								particao,
								padrao,
							}).datasEspecificas.includes(data),
						)
						.map((particao = {}) => ({
							hora_inicio: particao?.hora_inicio,
							hora_fim: particao?.hora_fim,
						}));
					return intervalosCobremFaixaIntegral({
						intervalos,
						horaInicio: padrao?.hora_inicio,
						horaFim: padrao?.hora_fim,
					});
				}),
			);
			const padraoAtualizado = {
				...padrao,
				...(tipoRecorrencia === 'SEMANAL'
					? {
							dias_semana: getPadraoDiasSemana(padrao).filter(
								(dia) => !diasRemovidos.has(dia),
							),
						}
					: {}),
				...(Array.isArray(padrao?.datas_especificas)
					? {
							datas_especificas: getPadraoDatasEspecificas(
								padrao,
							).filter((data) => !datasRemovidas.has(data)),
						}
					: {}),
			};
			const aindaPossuiOcorrencias =
				getPadraoDiasSemana(padraoAtualizado).length > 0 ||
				getPadraoDatasEspecificas(padraoAtualizado).length > 0 ||
				!['SEMANAL', 'DATAS_ESPECIFICAS'].includes(tipoRecorrencia);
			if (
				!aindaPossuiOcorrencias &&
				!correlacoesAindaReferenciadas.has(correlacao)
			) {
				return null;
			}
			return padraoAtualizado;
		})
		.filter(Boolean);

	return { particoesFinais, padroesFinais, particoesRemovidas };
};

const getParticoesPersistidas = (linhas = []) => {
	const candidates = linhas.flatMap((linha = {}) => [
		linha?.particoes,
		linha?.alocacao_origem?.particoes,
		linha?.alocacao_origem?.particoes_ativas,
		linha?.alocacao_origem?.estado_final?.particoes,
		linha?.particoes_alocacao_origem,
		linha?.regime_escala?.particoes,
		linha?.regime_escala?.particoes_alocacao_origem,
		linha?.regime_escala?.alocacao_origem?.particoes,
		linha?.regime_escala?.alocacao_origem?.particoes_ativas,
		linha?.regime_escala?.alocacao_origem?.estado_final?.particoes,
	]);
	const particoes = candidates.flatMap((particoesLinha) =>
		Array.isArray(particoesLinha) ? particoesLinha : [],
	);
	const seen = new Set();
	return particoes.filter((particao = {}) => {
		const key = JSON.stringify(particao);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

const removerParticoesDosSetoresDestinos = (
	linha = {},
	setoresDestinoRemovidos = new Set(),
) => {
	const filtrarParticoes = (particoes = []) =>
		(Array.isArray(particoes) ? particoes : []).filter(
			(particao = {}) =>
				!setoresDestinoRemovidos.has(
					String(particao?.setor_destino_id || ''),
				),
		);
	const alocacaoOrigem = linha?.alocacao_origem;
	const regimeEscala = linha?.regime_escala || {};
	const alocacaoOrigemRegime = regimeEscala?.alocacao_origem;

	return {
		...linha,
		...(alocacaoOrigem && typeof alocacaoOrigem === 'object'
			? {
					alocacao_origem: {
						...alocacaoOrigem,
						particoes: filtrarParticoes(alocacaoOrigem.particoes),
					},
				}
			: {}),
		particoes_alocacao_origem: filtrarParticoes(
			linha?.particoes_alocacao_origem,
		),
		regime_escala: {
			...regimeEscala,
			particoes_alocacao_origem: filtrarParticoes(
				regimeEscala?.particoes_alocacao_origem,
			),
			...(alocacaoOrigemRegime && typeof alocacaoOrigemRegime === 'object'
				? {
						alocacao_origem: {
							...alocacaoOrigemRegime,
							particoes: filtrarParticoes(
								alocacaoOrigemRegime.particoes,
							),
						},
					}
				: {}),
		},
		setores_linhas: Array.isArray(linha?.setores_linhas)
			? linha.setores_linhas.map((linhaSetor = {}) =>
					removerParticoesDosSetoresDestinos(
						linhaSetor,
						setoresDestinoRemovidos,
					),
				)
			: linha?.setores_linhas,
	};
};

const buildParticoesNovoSetor = ({
	linhaOrigem,
	linhasProfissional = [],
	setorDestinoId,
	regimePayload = {},
	incluirParticoesAtuais = true,
	padroesOrigemOverride = null,
} = {}) => {
	const padroesOrigem =
		Array.isArray(padroesOrigemOverride) && padroesOrigemOverride.length > 0
			? padroesOrigemOverride
			: getPadroesAlocacaoOrigem(linhaOrigem);
	const padroesDestino = Array.isArray(regimePayload?.padroes_alocacao)
		? regimePayload.padroes_alocacao
		: [];
	const destinos =
		padroesDestino.length > 0 ? padroesDestino : [regimePayload];
	const novasParticoes = destinos.map((padraoDestino = {}) => {
		const horaInicio = String(padraoDestino?.hora_inicio || '').slice(0, 5);
		const horaFim = String(padraoDestino?.hora_fim || '').slice(0, 5);
		const padraoOrigem = padroesOrigem.find(
			(padrao = {}) =>
				padraoOrigemCorrespondeAoDestino({
					padraoOrigem: padrao,
					padraoDestino,
				}) &&
				horarioEstaContido({
					horaInicioOrigem: padrao?.hora_inicio,
					horaFimOrigem: padrao?.hora_fim,
					horaInicioDestino: horaInicio,
					horaFimDestino: horaFim,
				}),
		);
		const padraoOrigemId = getPadraoOrigemId(padraoOrigem);
		if (!padraoOrigemId || !horaInicio || !horaFim) return null;
		const diasOrigem = getPadraoDiasSemana(padraoOrigem);
		const diasDestino = getPadraoDiasSemana(padraoDestino);
		const destinoCobreTodosDiasDaOrigem =
			diasOrigem.length > 0 &&
			diasOrigem.every((dia) => diasDestino.includes(dia));
		return {
			padrao_origem_id: padraoOrigemId,
			setor_destino_id: Number(setorDestinoId),
			// Um padrão semanal pode abranger mais de um dia. O recorte deve
			// afetar somente os dias escolhidos para o destino; usar TODAS nesses
			// casos deslocaria horários que o usuário não selecionou.
			aplicacao: destinoCobreTodosDiasDaOrigem
				? { tipo: 'TODAS_OCORRENCIAS' }
				: {
						tipo: 'DIAS_SEMANA',
						dias_semana: diasDestino,
					},
			hora_inicio: horaInicio,
			hora_fim: horaFim,
		};
	});

	if (novasParticoes.some((particao) => !particao)) return null;
	const padroesOrigemIds = new Set(
		padroesOrigem
			.map((padrao = {}) => String(getPadraoOrigemId(padrao) || ''))
			.filter(Boolean),
	);
	// Cada preview é aplicado a uma única origem. Não reenviar partições de
	// outras linhas: a API corretamente as rejeita como "padrão não pertence à
	// origem".
	const particoesAtuais = incluirParticoesAtuais
		? getParticoesPersistidas(linhasProfissional).filter((particao = {}) =>
				padroesOrigemIds.has(String(particao?.padrao_origem_id || '')),
			)
		: [];
	const seen = new Set();
	return [...particoesAtuais, ...novasParticoes].filter((particao = {}) => {
		const key = JSON.stringify(particao);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

const getLinhasParticionamento = (linhas = []) =>
	linhas.flatMap((linha = {}) => {
		const setoresLinhas = Array.isArray(linha?.setores_linhas)
			? linha.setores_linhas
			: [];
		return setoresLinhas.length > 0
			? getLinhasParticionamento(setoresLinhas)
			: [linha];
	});

export const isItemSetorDestinoParticionado = ({
	item = {},
	linhasProfissional = [],
} = {}) => {
	const setorId = Number(
		item?.setorId || getLinhaSetorId(item?.sourceLinha) || 0,
	);
	if (!setorId) return false;
	const linhaSetor = item?.sourceLinha || {};
	const setorOrigem = Number(
		linhaSetor?.alocacao_origem?.setor_origem?.id ||
			linhaSetor?.alocacao_origem?.setor_origem_id ||
			linhaSetor?.regime_escala?.alocacao_origem?.setor_origem?.id ||
			linhaSetor?.regime_escala?.alocacao_origem?.setor_origem_id ||
			0,
	);
	if (
		isLinhaOrigemDeSplit(linhaSetor) ||
		(setorOrigem && setorOrigem === setorId)
	) {
		return false;
	}

	return getParticoesPersistidas(linhasProfissional).some(
		(particao = {}) => Number(particao?.setor_destino_id || 0) === setorId,
	);
};

// A origem também faz parte de uma jornada versionada. Ela não é um destino
// de recorte, mas alterações na sua grade (dias, horários ou vigência) devem
// passar pelo mesmo fluxo estrutural para que as partições já existentes sejam
// preservadas pelo backend.
export const isItemSetorOrigemParticionada = ({
	item = {},
	linhasProfissional = [],
} = {}) => {
	const setorId = Number(
		item?.setorId || getLinhaSetorId(item?.sourceLinha) || 0,
	);
	if (!setorId) return false;
	const linhaSetor = item?.sourceLinha || {};
	if (isLinhaOrigemDeSplit(linhaSetor)) return true;
	const setorOrigemInformado = Number(
		linhaSetor?.alocacao_origem?.setor_origem?.id ||
			linhaSetor?.alocacao_origem?.setor_origem_id ||
			linhaSetor?.regime_escala?.alocacao_origem?.setor_origem?.id ||
			linhaSetor?.regime_escala?.alocacao_origem?.setor_origem_id ||
			0,
	);
	if (setorOrigemInformado === setorId) return true;

	const alocacaoOrigemId = String(getAlocacaoOrigemId(linhaSetor) || '');
	return getLinhasParticionamento(linhasProfissional).some((linha = {}) => {
		if (!isLinhaOrigemDeSplit(linha)) return false;
		if (Number(getLinhaSetorId(linha) || 0) !== setorId) return false;
		return (
			!alocacaoOrigemId ||
			String(getAlocacaoOrigemId(linha) || '') === alocacaoOrigemId
		);
	});
};

const formatMinutosComoHorario = (minutos = 0) => {
	const valorNormalizado = ((Number(minutos) % 1440) + 1440) % 1440;
	return `${String(Math.floor(valorNormalizado / 60)).padStart(2, '0')}:${String(
		valorNormalizado % 60,
	).padStart(2, '0')}`;
};

const getFaixasRemanescentes = ({
	horaInicio,
	horaFim,
	particoes = [],
} = {}) => {
	const inicio = horarioEmMinutos(horaInicio);
	let fim = horarioEmMinutos(horaFim);
	if (inicio === null || fim === null) return [];
	if (fim <= inicio) fim += 24 * 60;

	const recortes = particoes
		.map((particao = {}) => {
			let inicioParticao = horarioEmMinutos(particao?.hora_inicio);
			let fimParticao = horarioEmMinutos(particao?.hora_fim);
			if (inicioParticao === null || fimParticao === null) return null;
			if (fimParticao <= inicioParticao) fimParticao += 24 * 60;
			if (inicioParticao < inicio) {
				inicioParticao += 24 * 60;
				fimParticao += 24 * 60;
			}
			return {
				inicio: Math.max(inicio, inicioParticao),
				fim: Math.min(fim, fimParticao),
			};
		})
		.filter((particao) => particao && particao.inicio < particao.fim)
		.sort((a, b) => a.inicio - b.inicio);

	return recortes.reduce(
		(faixas, recorte) =>
			faixas.flatMap((faixa) => {
				if (
					recorte.fim <= faixa.inicio ||
					recorte.inicio >= faixa.fim
				) {
					return [faixa];
				}
				return [
					...(recorte.inicio > faixa.inicio
						? [{ inicio: faixa.inicio, fim: recorte.inicio }]
						: []),
					...(recorte.fim < faixa.fim
						? [{ inicio: recorte.fim, fim: faixa.fim }]
						: []),
				];
			}),
		[{ inicio, fim }],
	);
};

const particaoCobreDiaRecorrente = (particao = {}, diaSemana) => {
	const aplicacao = particao?.aplicacao || {};
	const tipo = normalizeCode(aplicacao?.tipo || particao?.tipo_aplicacao);
	if (!tipo || tipo === 'TODAS_OCORRENCIAS') return true;
	if (tipo === 'DIAS_SEMANA') {
		return normalizeDiasSemanaValues(
			aplicacao?.dias_semana || particao?.dias_semana,
		).includes(Number(diaSemana));
	}
	// Uma partição pontual não altera a visualização da regra semanal.
	return false;
};

const particaoCobreDataEspecifica = (particao = {}, data) => {
	const aplicacao = particao?.aplicacao || {};
	const tipo = normalizeCode(aplicacao?.tipo || particao?.tipo_aplicacao);
	if (!tipo || tipo === 'TODAS_OCORRENCIAS') return true;
	if (tipo !== 'DATAS_ESPECIFICAS') return false;
	return (aplicacao?.datas_especificas || particao?.datas_especificas || [])
		.map((valor) => formatDateInputToISO(valor))
		.includes(formatDateInputToISO(data));
};

// Ao desmarcar, dentro do editor, uma ocorrência que foi criada somente para
// uma variante, não basta retirar sua partição: `padroes_origem` ainda declara
// que ela existe e a API a projeta novamente no setor-base. Removemos apenas
// ocorrências integralmente cobertas por uma partição do destino editado que
// desapareceu do estado final. Partições parciais nunca removem a origem.
export const removerOcorrenciasIntegrasDesmarcadasDaOrigem = ({
	padroesOrigem = [],
	particoesAntes = [],
	particoesDepois = [],
	setoresDestinoEditados = [],
} = {}) => {
	const destinosEditados = new Set(
		(Array.isArray(setoresDestinoEditados) ? setoresDestinoEditados : [])
			.map((setorId) => Number(setorId || 0))
			.filter(Boolean),
	);
	const chavesFinais = new Set(
		(Array.isArray(particoesDepois) ? particoesDepois : []).map(
			getParticaoEstruturalKey,
		),
	);
	const particoesRemovidas = (
		Array.isArray(particoesAntes) ? particoesAntes : []
	).filter(
		(particao = {}) =>
			destinosEditados.has(Number(particao?.setor_destino_id || 0)) &&
			!chavesFinais.has(getParticaoEstruturalKey(particao)),
	);
	if (!particoesRemovidas.length) {
		return Array.isArray(padroesOrigem)
			? padroesOrigem.map((padrao = {}) => ({ ...padrao }))
			: [];
	}

	const particoesFinais = Array.isArray(particoesDepois)
		? particoesDepois
		: [];
	return (Array.isArray(padroesOrigem) ? padroesOrigem : [])
		.map((padrao = {}) => {
			const correlacaoPadrao = getPadraoOrigemCorrelationKey(padrao);
			const removidasDoPadrao = particoesRemovidas.filter(
				(particao = {}) =>
					getParticaoPadraoOrigemCorrelationKey(particao) ===
					correlacaoPadrao,
			);
			if (!correlacaoPadrao || !removidasDoPadrao.length)
				return { ...padrao };

			const diaPermaneceNaOrigem = (diaSemana) => {
				const recortesRemovidos = removidasDoPadrao.filter(
					(particao = {}) =>
						particaoCobreDiaRecorrente(particao, diaSemana),
				);
				if (!recortesRemovidos.length) return true;
				const aindaParticionado = particoesFinais.some(
					(particao = {}) =>
						getParticaoPadraoOrigemCorrelationKey(particao) ===
							correlacaoPadrao &&
						particaoCobreDiaRecorrente(particao, diaSemana),
				);
				if (aindaParticionado) return true;
				return (
					getFaixasRemanescentes({
						horaInicio: padrao?.hora_inicio,
						horaFim: padrao?.hora_fim,
						particoes: recortesRemovidos,
					}).length > 0
				);
			};
			const dataPermaneceNaOrigem = (data) => {
				const recortesRemovidos = removidasDoPadrao.filter(
					(particao = {}) =>
						particaoCobreDataEspecifica(particao, data),
				);
				if (!recortesRemovidos.length) return true;
				const aindaParticionada = particoesFinais.some(
					(particao = {}) =>
						getParticaoPadraoOrigemCorrelationKey(particao) ===
							correlacaoPadrao &&
						particaoCobreDataEspecifica(particao, data),
				);
				if (aindaParticionada) return true;
				return (
					getFaixasRemanescentes({
						horaInicio: padrao?.hora_inicio,
						horaFim: padrao?.hora_fim,
						particoes: recortesRemovidos,
					}).length > 0
				);
			};
			return {
				...padrao,
				dias_semana:
					getPadraoDiasSemana(padrao).filter(diaPermaneceNaOrigem),
				datas_especificas: getPadraoDatasEspecificas(padrao).filter(
					dataPermaneceNaOrigem,
				),
			};
		})
		.filter(
			(padrao = {}) =>
				getPadraoDiasSemana(padrao).length > 0 ||
				getPadraoDatasEspecificas(padrao).length > 0,
		);
};

const getParticaoEstruturalKey = (particao = {}) =>
	JSON.stringify({
		padrao_origem_id: String(particao?.padrao_origem_id || ''),
		padrao_origem_referencia_cliente: String(
			particao?.padrao_origem_referencia_cliente || '',
		),
		setor_destino_id: Number(particao?.setor_destino_id || 0),
		aplicacao: {
			tipo: normalizeCode(particao?.aplicacao?.tipo),
			dias_semana: normalizeDiasSemanaValues(
				particao?.aplicacao?.dias_semana || particao?.dias_semana,
			),
			datas_especificas: Array.isArray(
				particao?.aplicacao?.datas_especificas,
			)
				? [...particao.aplicacao.datas_especificas].sort()
				: [],
		},
		hora_inicio: formatHourInputToTimeValue(particao?.hora_inicio),
		hora_fim: formatHourInputToTimeValue(particao?.hora_fim),
	});

const particoesEstruturalmenteIguais = (particoesA = [], particoesB = []) => {
	const normalizar = (particoes) =>
		(Array.isArray(particoes) ? particoes : [])
			.map(getParticaoEstruturalKey)
			.sort();
	return (
		JSON.stringify(normalizar(particoesA)) ===
		JSON.stringify(normalizar(particoesB))
	);
};

const aplicarParticoesNosHorariosSemanais = ({
	linha,
	linhasParticionamento = [],
	horariosOptionsByDia = {},
} = {}) => {
	// A regra completa continua pertencendo à origem. Nos destinos a grade já
	// representa somente a faixa da própria partição; subtrair os recortes uma
	// segunda vez apagava o dia do setor variante (ou deixava-o espelhado no
	// base na reconciliação seguinte).
	if (isLinhaDestinoDeSplit(linha)) return horariosOptionsByDia;
	const linhas = getLinhasParticionamento(linhasParticionamento);
	const particoes = getParticoesPersistidas(linhas);
	if (particoes.length === 0) return horariosOptionsByDia;

	const setorAtual = String(getLinhaSetorId(linha) || '');
	const padroesDaOrigem = linhas.flatMap((linhaOrigem = {}) =>
		getPadroesAlocacaoOrigem(linhaOrigem).map((padrao = {}) => ({
			padrao,
			setorId: String(getLinhaSetorId(linhaOrigem) || ''),
		})),
	);
	const particoesPorPadrao = particoes.reduce((acc, particao = {}) => {
		const padraoId = String(particao?.padrao_origem_id || '');
		if (!padraoId) return acc;
		acc[padraoId] = [...(acc[padraoId] || []), particao];
		return acc;
	}, {});
	const resultado = Object.fromEntries(
		Object.entries(horariosOptionsByDia).map(([dia, horarios]) => [
			dia,
			Array.isArray(horarios) ? [...horarios] : [],
		]),
	);

	padroesDaOrigem
		.filter(({ setorId }) => setorId === setorAtual)
		.forEach(({ padrao }) => {
			const padraoId = String(getPadraoOrigemId(padrao) || '');
			const particoesDoPadrao = particoesPorPadrao[padraoId] || [];
			if (particoesDoPadrao.length === 0) return;

			const horaInicio = formatHourInputToTimeValue(padrao?.hora_inicio);
			const horaFim = formatHourInputToTimeValue(padrao?.hora_fim);
			if (!horaInicio || !horaFim) return;
			normalizeDiasSemanaValues(padrao?.dias_semana).forEach((dia) => {
				const particoesDoDia = particoesDoPadrao.filter(
					(particao = {}) =>
						particaoCobreDiaRecorrente(particao, dia),
				);
				if (particoesDoDia.length === 0) return;
				const faixasRemanescentes = getFaixasRemanescentes({
					horaInicio,
					horaFim,
					particoes: particoesDoDia,
				});
				const horariosAtuais = resultado[dia] || [];
				const semFaixaOriginal = horariosAtuais.filter(
					(horario = {}) =>
						formatHourInputToTimeValue(horario?.hora_inicio) !==
							horaInicio ||
						formatHourInputToTimeValue(horario?.hora_fim) !==
							horaFim,
				);
				// A mesma origem pode aparecer na projeção mensal e na leitura
				// estrutural. Mesclar por faixa evita repetir o primeiro turno e
				// preserva os dois intervalos distintos que restam na origem.
				resultado[dia] =
					mergeHorariosOptionsByDia(
						{ [dia]: semFaixaOriginal },
						{
							[dia]: faixasRemanescentes.map((faixa) => ({
								hora_inicio: formatMinutosComoHorario(
									faixa.inicio,
								),
								hora_fim: formatMinutosComoHorario(faixa.fim),
							})),
						},
					)[dia] || [];
			});
		});

	return resultado;
};

const getParticionamentoPreviewMessage = (response = {}) => {
	const primeiraMensagemIssue = String(
		getPreviewIssues(response)[0]?.mensagem ||
			getPreviewIssues(response)[0]?.message ||
			'',
	).trim();
	return (
		primeiraMensagemIssue ||
		getErrorMessage(response, '') ||
		'Não foi possível validar a divisão de jornada.'
	);
};

const isParticionamentoPreviewValido = (preview = {}) => {
	const issues = Array.isArray(preview?.issues) ? preview.issues : [];
	return (
		preview?.valido === true &&
		preview?.bloqueado !== true &&
		preview?.acoes?.aplicar?.disponivel === true &&
		!issues.some(
			(issue = {}) =>
				issue?.bloqueante === true || issue?.blocking === true,
		)
	);
};

const consolidarTurnosDoMesmoSetor = (turnos = []) => {
	const grupos = new Map();
	const getInicioTurno = (turno = {}) =>
		formatDateInputToISO(
			turno?.sourceLinha?.regime_escala?.vigencia_inicio ||
				turno?.sourceLinha?.regime_escala?.data_ancora ||
				turno?.dataInicial ||
				'',
		);
	const isTurnoVigente = (turno = {}) =>
		!getLinhaVigenciaFimIso(turno?.sourceLinha || {});
	const getTurnoMaisRecente = (primeiro = {}, segundo = {}) => {
		const primeiroVigente = isTurnoVigente(primeiro);
		const segundoVigente = isTurnoVigente(segundo);
		if (primeiroVigente !== segundoVigente) {
			return segundoVigente ? segundo : primeiro;
		}
		return getInicioTurno(segundo) >= getInicioTurno(primeiro)
			? segundo
			: primeiro;
	};
	const mergeDatasEspecificas = (...listas) => {
		const items = new Map();
		listas.flat().forEach((data = {}) => {
			const chave = `${formatDateInputToISO(data?.data)}|${
				data?.horaInicio || ''
			}|${data?.horaFim || ''}`;
			if (!chave.startsWith('|')) items.set(chave, data);
		});
		return Array.from(items.values()).sort((a, b) =>
			`${formatDateInputToISO(a?.data)}|${a?.horaInicio || ''}`.localeCompare(
				`${formatDateInputToISO(b?.data)}|${b?.horaInicio || ''}`,
			),
		);
	};

	turnos.forEach((turno = {}) => {
		const setorId = String(turno?.setorId || '');
		if (!setorId) return;
		if (!grupos.has(setorId)) {
			grupos.set(setorId, {
				...turno,
				sourceLinhas: [turno?.sourceLinha],
			});
			return;
		}
		const anterior = grupos.get(setorId);
		// Uma edição cria uma nova versão do regime. No bloco de dias
		// recorrentes deve prevalecer sempre a versão ainda vigente (ou, sem
		// vigência em aberto, a de início mais recente); os horários antigos não
		// podem continuar como opção editável. As datas específicas, por outro
		// lado, são ocorrências históricas e permanecem visíveis.
		const atual = getTurnoMaisRecente(anterior, turno);
		grupos.set(setorId, {
			...atual,
			isSetorBase: Boolean(anterior?.isSetorBase || turno?.isSetorBase),
			diasSemanaBloqueadosPorParticaoIntegral: normalizeDiasSemanaValues([
				...(anterior?.diasSemanaBloqueadosPorParticaoIntegral || []),
				...(turno?.diasSemanaBloqueadosPorParticaoIntegral || []),
			]),
			datasEspecificas: mergeDatasEspecificas(
				anterior?.datasEspecificas || [],
				turno?.datasEspecificas || [],
			),
			sourceLinhas: [
				...(anterior?.sourceLinhas || []),
				turno?.sourceLinha,
			],
		});
	});
	return Array.from(grupos.values());
};

export const buildTurnosSetores = ({
	linha,
	setorOptions = [],
	contextoAplicado = {},
	regimesOptions = [],
	linhasParticionamento = [],
} = {}) => {
	if (!linha) return [];
	if (Array.isArray(linha?.setores_linhas) && linha.setores_linhas.length) {
		const linhasCompartilhadas =
			linhasParticionamento.length > 0
				? linhasParticionamento
				: linha.setores_linhas;
		const turnosPorLinha = linha.setores_linhas.flatMap((setorLinha) =>
			buildTurnosSetores({
				linha: setorLinha,
				setorOptions,
				contextoAplicado,
				regimesOptions,
				linhasParticionamento: linhasCompartilhadas,
			}),
		);

		// Cards de troca chegam como linhas MANUAL. Eles são usados abaixo para
		// compor a data específica do setor de origem do profissional, jamais
		// como uma nova aba de setor no modal.
		return consolidarTurnosDoMesmoSetor(
			turnosPorLinha.filter(
				(item = {}) =>
					normalizeCode(item?.sourceLinha?.origem_linha) !== 'MANUAL',
			),
		);
	}

	const profissional =
		linha?.profissional || linha?.vinculo_profissional || {};
	const setoresProfissional = Array.isArray(profissional?.setores)
		? profissional.setores
		: [];
	const escalasProfissional = Array.isArray(profissional?.escalas)
		? profissional.escalas
		: [];
	const setoresBase =
		setoresProfissional.length > 0 && !getLinhaSetorId(linha)
			? setoresProfissional
			: [
					linha?.setor || {
						id: getLinhaSetorId(linha) || contextoAplicado?.setor,
						nome: getLinhaSetorNome(linha, setorOptions),
					},
				];
	const regimeEscala = linha?.regime_escala || {};
	const {
		padraoSemanal,
		diasSemana,
		horariosByDia: horariosSemana,
		horariosOptionsByDia: horariosSemanaOptions,
	} = getWeeklyScheduleDetails(regimeEscala);
	const datasClassificadas = getDatasEspecificasComRecorrenciasSemanais(
		getLinhaDatasEspecificasResumo(linha),
	);
	const isLinhaLegada = Boolean(getLinhaVigenciaFimIso(linha));
	const competencia = String(
		formatDateInputToISO(contextoAplicado?.mes_referencia) || '',
	).slice(0, 7);
	// A API pode devolver uma data específica de uma versão histórica fora da
	// competência aberta, mesmo sem devolver a linha dela no mês de origem.
	// Não a exibimos nem a reutilizamos no modal: ela seria uma ocorrência
	// órfã para o usuário. Ao consultar a competência da própria data, ela
	// continua sendo apresentada normalmente.
	const datasEspecificas = datasClassificadas.datasEspecificas
		.filter((data = {}) => {
			const dataIso = formatDateInputToISO(data?.data);
			return !competencia || dataIso.slice(0, 7) === competencia;
		})
		.map((data) => ({ ...data, legado: isLinhaLegada }));
	const horaInicio = String(
		padraoSemanal?.hora_inicio || regimeEscala?.hora_inicio || '07:00',
	).slice(0, 5);
	const horaFim = String(
		padraoSemanal?.hora_fim || regimeEscala?.hora_fim || '19:00',
	).slice(0, 5);

	return setoresBase.map((setor, index) => {
		const escala =
			escalasProfissional.find(
				(item) =>
					String(item?.setor?.id || item?.setor_id || item?.setor) ===
					String(setor?.id || setor?.setor_id || setor),
			) || {};
		const escalaRegime = escala?.regime_escala || escala;
		const itemPadroesAlocacao = normalizePadroesAlocacao(
			escalaRegime?.padroes_alocacao || regimeEscala?.padroes_alocacao,
		);
		const itemPadroesBase = itemPadroesAlocacao.filter(
			(padrao = {}) =>
				normalizeCode(padrao?.tipo_recorrencia) !== 'DATAS_ESPECIFICAS',
		);
		const itemPadraoPrimario =
			itemPadroesBase[0] || itemPadroesAlocacao[0] || {};
		const itemTipoRecorrencia =
			normalizeCode(
				itemPadraoPrimario?.tipo_recorrencia ||
					escalaRegime?.tipo_recorrencia ||
					regimeEscala?.tipo_recorrencia,
			) || getTipoRecorrenciaFallbackByRegime(escalaRegime?.regime);
		const itemPresetCiclo = inferPresetCicloFromRegimeEscala({
			...escalaRegime,
			...itemPadraoPrimario,
			tipo_recorrencia: itemTipoRecorrencia,
		});
		const itemNomeRecorrencia = getRecorrenciaNomeValue({
			tipo_recorrencia: itemTipoRecorrencia,
			preset_ciclo: itemPresetCiclo,
			padroes_alocacao: itemPadroesAlocacao,
		});
		const {
			padraoSemanal: escalaPadraoSemanal,
			diasSemana: escalaDiasSemana,
			horariosByDia: escalaHorariosSemana,
			horariosOptionsByDia: escalaHorariosSemanaOptions,
		} = getWeeklyScheduleDetails(escalaRegime);
		const setorNome =
			formatSetorOptionLabel(setor) ||
			formatSetorOptionLabel(escala?.setor) ||
			getLinhaSetorNome(linha, setorOptions);
		const itemRegime = escalaRegime?.regime || regimeEscala?.regime;
		// Em uma linha setorial, o regime/padrão dessa própria linha é a fonte
		// de verdade. `profissional.escalas` pode trazer um resumo agregado e
		// não deve sobrescrever a recorrência específica do setor.
		const itemHorariosOptionsSemanaBase = mergeHorariosOptionsByDia(
			diasSemana.length > 0
				? horariosSemanaOptions
				: escalaHorariosSemanaOptions,
			datasClassificadas.horariosOptionsByDia,
		);
		const itemHorariosOptionsSemana = aplicarParticoesNosHorariosSemanais({
			linha,
			linhasParticionamento:
				linhasParticionamento.length > 0
					? linhasParticionamento
					: [linha],
			horariosOptionsByDia: itemHorariosOptionsSemanaBase,
		});
		const horariosParticionadosByDia = Object.fromEntries(
			Object.entries(itemHorariosOptionsSemana).filter(
				([, horarios]) =>
					Array.isArray(horarios) && horarios.length > 1,
			),
		);
		const itemDiasSemana = normalizeDiasSemanaValues([
			...(diasSemana.length > 0 ? diasSemana : escalaDiasSemana),
			...Object.keys(itemHorariosOptionsSemana),
		]).filter(
			(dia) =>
				!Object.prototype.hasOwnProperty.call(
					itemHorariosOptionsSemana,
					dia,
				) || itemHorariosOptionsSemana[dia].length > 0,
		);
		const itemHorariosSemana = Object.fromEntries(
			Object.entries(itemHorariosOptionsSemana)
				.filter(
					([, horarios]) =>
						Array.isArray(horarios) && horarios.length > 0,
				)
				.map(([dia, horarios]) => [dia, horarios[0]]),
		);
		const itemHoraInicio = String(
			itemPadraoPrimario?.hora_inicio ||
				padraoSemanal?.hora_inicio ||
				regimeEscala?.hora_inicio ||
				escalaPadraoSemanal?.hora_inicio ||
				escalaRegime?.hora_inicio ||
				horaInicio ||
				'07:00',
		).slice(0, 5);
		const itemHoraFim = String(
			itemPadraoPrimario?.hora_fim ||
				padraoSemanal?.hora_fim ||
				regimeEscala?.hora_fim ||
				escalaPadraoSemanal?.hora_fim ||
				escalaRegime?.hora_fim ||
				horaFim ||
				'19:00',
		).slice(0, 5);
		const itemPadraoRotacao = itemPadroesBase.find(
			(padrao = {}) =>
				normalizeCode(padrao?.tipo_recorrencia) ===
					'ROTACAO_PERIODICA' ||
				normalizeCode(padrao?.preset_ciclo) === 'ROTACAO_PERIODICA',
		);
		const itemSemanasRotacao = (
			itemPadraoRotacao?.semanas_rotacao ||
			escalaRegime?.semanas_rotacao ||
			regimeEscala?.semanas_rotacao ||
			[]
		).map((semana = {}) =>
			normalizeDiasSemanaValues(semana?.dias_semana || semana),
		);
		const itemPeriodoRotacao =
			Number(itemPadraoRotacao?.periodo_semanas || 0) ||
			Number(escalaRegime?.periodo_semanas || 0) ||
			itemSemanasRotacao.length ||
			undefined;
		const setorIdDaAba = String(
			setor?.id || setor?.setor_id || escala?.setor?.id || '',
		);
		const linhasDoMesmoSetor = [linha, ...linhasParticionamento].filter(
			(linhaSetor = {}) =>
				String(getLinhaSetorId(linhaSetor) || '') === setorIdDaAba,
		);
		// A troca é pontual e não muda o setor de vínculo do profissional. O card
		// recebido fica fisicamente no setor do colega, mas a API fornece o item
		// relacionado: ele aponta para o card que saiu do setor deste profissional.
		// Usamos esse vínculo para exibir a data recebida na aba correta, sem criar
		// um setor fantasma para o setor físico do card trocado.
		const linhasCompartilhadas = getLinhasParticionamento([
			linha,
			...linhasParticionamento,
		]);
		const linhasComReferenciasTroca = [
			...linhasCompartilhadas,
			...linhasCompartilhadas.flatMap((linhaCompartilhada = {}) =>
				Array.isArray(linhaCompartilhada?._referencias_troca)
					? linhaCompartilhada._referencias_troca
					: [],
			),
		];
		const cardsCompartilhados = linhasComReferenciasTroca.flatMap(
			(linhaCompartilhada = {}) =>
				(Array.isArray(linhaCompartilhada?.celulas)
					? linhaCompartilhada.celulas
					: []
				).flatMap((celula = {}) =>
					getPersistedCards(celula).map((card = {}) => ({
						linha: linhaCompartilhada,
						celula,
						card,
					})),
				),
		);
		const vinculoDaAba = String(getVinculoIdFromLinha(linha) || '');
		const setoresRegularesDoVinculo = new Set(
			linhasCompartilhadas
				.filter(
					(linhaCompartilhada = {}) =>
						normalizeCode(linhaCompartilhada?.origem_linha) !== 'MANUAL' &&
						String(getVinculoIdFromLinha(linhaCompartilhada) || '') ===
							vinculoDaAba,
				)
				.map((linhaCompartilhada = {}) =>
					String(getLinhaSetorId(linhaCompartilhada) || ''),
				)
				.filter(Boolean),
		);
		const datasEspecificasTroca = cardsCompartilhados
			.filter(({ card, celula, linha: linhaCard }) => {
				if (!isTrocaCard(card, celula)) return false;
				const vinculoAtual = String(
					card?.vinculo_profissional?.id ||
						card?.vinculo_profissional_id ||
						getVinculoIdFromLinha(linhaCard) ||
						'',
				);
				if (!vinculoDaAba || vinculoAtual !== vinculoDaAba) return false;
				const troca =
					card?.troca || card?.resumo?.troca || card?.dados_edicao?.troca || {};
				const itemRelacionadoId = String(
					troca?.item_troca_relacionado_id ||
						troca?.item_relacionado_id ||
						card?.item_troca_relacionado_id ||
						'',
				);
				const cardRelacionado = cardsCompartilhados.find(
					(item = {}) =>
						String(getCardItemId(item?.card) || '') === itemRelacionadoId,
				);
				const setorRelacionado = String(
					getLinhaSetorId(cardRelacionado?.linha) || '',
				);
				if (itemRelacionadoId && setorRelacionado) {
					return setorRelacionado === setorIdDaAba;
				}
				// Mesmo dado que já aparece no modal de edição: se a resposta não
				// trouxe o par relacionado, associe a exceção ao único setor regular
				// daquele profissional, nunca à linha manual do setor externo.
				return (
					setoresRegularesDoVinculo.size === 1 &&
					setoresRegularesDoVinculo.has(setorIdDaAba)
				);
			})
			.map(({ card, celula, linha: linhaCard }) =>
				buildTrocaDataExtraItem({ card, celula, linha: linhaCard }),
			)
			.filter(Boolean)
			.filter((data = {}) => {
				const dataIso = formatDateInputToISO(data?.selected_date);
				return !competencia || dataIso.slice(0, 7) === competencia;
			})
			.map((data = {}) => ({
				data: formatISOToDisplay(data.selected_date),
				horaInicio: String(data.hora_inicio || '').slice(0, 5),
				horaFim: String(data.hora_fim || '').slice(0, 5),
				readOnly: true,
				origem: 'troca',
				label: 'Troca',
			}));
		const datasEspecificasDoSetor = Array.from(
			new Map(
				[...datasEspecificas, ...datasEspecificasTroca].map(
					(data = {}) => [
						`${formatDateInputToISO(data?.data)}|${String(
							data?.horaInicio || '',
						).slice(0, 5)}|${String(data?.horaFim || '').slice(0, 5)}`,
						data,
					],
				),
			).values(),
		);
		const particoesDoSetor = getParticoesPersistidas([
			linha,
			...linhasParticionamento,
		]).filter(
			(particao = {}) =>
				String(particao?.setor_destino_id || '') === setorIdDaAba,
		);
		const alocacaoOrigemIdDaLinha = String(
			getAlocacaoOrigemId(linha) || '',
		);
		const linhaEstruturalDaOrigem = [linha, ...linhasParticionamento].find(
			(linhaCandidata = {}) =>
				isLinhaEstruturalAlocacaoOrigem(linhaCandidata) &&
				!isLinhaDestinoEstruturalAlocacaoOrigem(linhaCandidata) &&
				(!alocacaoOrigemIdDaLinha ||
					String(getAlocacaoOrigemId(linhaCandidata) || '') ===
						alocacaoOrigemIdDaLinha),
		);
		const particoesEstruturaisDaOrigem = getParticoesPersistidas([
			linhaEstruturalDaOrigem || linha,
			...linhasParticionamento,
		]);
		// Nem todo resumo mensal inclui a linha estrutural completa. Nesse caso, o
		// padrão totalmente destinado ao variante existe apenas na projeção desse
		// destino. Reúna os padrões irmãos pelo UUID para que o bloqueio da origem
		// não desapareça durante a consolidação visual.
		const padroesEstruturaisDaOrigem = Array.from(
			new Map(
				[linhaEstruturalDaOrigem, linha, ...linhasParticionamento]
					.filter(Boolean)
					.flatMap((linhaCandidata = {}) =>
						getPadroesAlocacaoOrigem(linhaCandidata),
					)
					.map((padrao = {}, indice) => [
						`${getPadraoOrigemCorrelationKey(padrao) || `sem-id:${indice}`}|${getPadraoDiasSemana(padrao).join(',')}|${getPadraoDatasEspecificas(padrao).join(',')}|${padrao?.hora_inicio || ''}|${padrao?.hora_fim || ''}`,
						padrao,
					]),
			).values(),
		);
		const isAbaDoSetorOrigemParticionado =
			isLinhaOrigemDeSplit(linha) ||
			(isLinhaEstruturalAlocacaoOrigem(linha) &&
				!isLinhaDestinoEstruturalAlocacaoOrigem(linha)) ||
			(particoesEstruturaisDaOrigem.length > 0 &&
				!particoesEstruturaisDaOrigem.some(
					(particao = {}) =>
						String(particao?.setor_destino_id || '') ===
						setorIdDaAba,
				));
		const diasSemanaPresentesSomenteNosDestinos = normalizeDiasSemanaValues(
			[linha, ...linhasParticionamento]
				.filter((linhaCandidata = {}) => {
					const setorCandidato = String(
						getLinhaSetorId(linhaCandidata) || '',
					);
					if (!setorCandidato || setorCandidato === setorIdDaAba) {
						return false;
					}
					const alocacaoCandidata = String(
						getAlocacaoOrigemId(linhaCandidata) || '',
					);
					const pertenceMesmaOrigem =
						!alocacaoOrigemIdDaLinha ||
						!alocacaoCandidata ||
						alocacaoCandidata === alocacaoOrigemIdDaLinha;
					return (
						pertenceMesmaOrigem &&
						(isLinhaDestinoDeSplit(linhaCandidata) ||
							isLinhaDerivadaDeAlocacaoOrigem(linhaCandidata) ||
							particoesEstruturaisDaOrigem.some(
								(particao = {}) =>
									String(particao?.setor_destino_id || '') ===
									setorCandidato,
							))
					);
				})
				.flatMap((linhaDestino = {}) => {
					const detalhesSemana = getWeeklyScheduleDetails(
						linhaDestino?.regime_escala || {},
					);
					return [
						...detalhesSemana.diasSemana,
						...getPadroesAlocacaoOrigem(linhaDestino).flatMap(
							getPadraoDiasSemana,
						),
					];
				})
				.filter((dia) => !itemDiasSemana.includes(dia)),
		);
		const diasSemanaBloqueadosPorParticaoIntegral =
			isAbaDoSetorOrigemParticionado
				? normalizeDiasSemanaValues([
						...getDiasSemanaIntegralmenteParticionados({
							padroesOrigem: padroesEstruturaisDaOrigem,
							particoes: particoesEstruturaisDaOrigem,
						}),
						...diasSemanaPresentesSomenteNosDestinos,
					])
				: [];
		const normalizarDatas = (valores = []) =>
			valores
				.map((valor) => formatDateInputToISO(valor))
				.filter(Boolean)
				.sort();
		const datasCriacaoParticao = normalizarDatas(
			particoesDoSetor.flatMap((particao = {}) => [
				particao?.vigencia_inicio,
				particao?.data_inicio,
				particao?.data_inicial,
				particao?.aplicacao?.vigencia_inicio,
				particao?.aplicacao?.data_inicio,
				particao?.aplicacao?.data_inicial,
				particao?.criado_em,
				particao?.created_at,
				particao?.data_criacao,
			]),
		);
		const datasAtualizacaoParticao = normalizarDatas(
			particoesDoSetor.flatMap((particao = {}) => [
				particao?.atualizado_em,
				particao?.updated_at,
				particao?.ultima_atualizacao,
				particao?.data_atualizacao,
				particao?.modificado_em,
				particao?.modified_at,
				particao?.aplicacao?.atualizado_em,
				particao?.aplicacao?.updated_at,
			]),
		);
		const getDataAncoraDoSetor = (linhaSetor = {}) => {
			const regimeDaLinha = linhaSetor?.regime_escala || {};
			const escalaDaLinha =
				linhaSetor?.escala_regime ||
				linhaSetor?.escalaMensalRegime ||
				{};
			const padraoDaLinha = [
				...(Array.isArray(regimeDaLinha?.padroes_alocacao)
					? regimeDaLinha.padroes_alocacao
					: []),
				...(Array.isArray(escalaDaLinha?.padroes_alocacao)
					? escalaDaLinha.padroes_alocacao
					: []),
			].find((padrao = {}) => formatDateInputToISO(padrao?.data_ancora));
			return formatDateInputToISO(
				regimeDaLinha?.data_ancora ||
					padraoDaLinha?.data_ancora ||
					linhaSetor?.data_ancora ||
					escalaDaLinha?.data_ancora ||
					'',
			);
		};
		const dataAncoraDoSetor =
			getDataAncoraDoSetor(linha) ||
			linhasDoMesmoSetor
				.map(getDataAncoraDoSetor)
				.filter(Boolean)
				.sort()[0] ||
			'';
		const datasVigenciaDoMesmoSetor = linhasDoMesmoSetor
			.map((linhaSetor = {}) =>
				getDataInicialProfissionalEscalaIsoFromLinha(linhaSetor),
			)
			.filter(Boolean)
			.sort();
		const dataVigenciaDaVersaoDoSetor =
			getDataInicialProfissionalEscalaIsoFromLinha(linha) ||
			datasVigenciaDoMesmoSetor[datasVigenciaDoMesmoSetor.length - 1] ||
			'';
		// A vigência exibida pertence ao setor desta aba. Não use o resumo
		// agregado (`escala_regime`) nem a competência visual, pois ambos podem
		// carregar a data de outro setor ou de uma versão histórica.
		const dataInicialDoSetor =
			datasCriacaoParticao[0] ||
			dataAncoraDoSetor ||
			dataVigenciaDaVersaoDoSetor ||
			linhasDoMesmoSetor
				.map((linhaSetor = {}) =>
					getDataInicialProfissionalEscalaIsoFromLinha(linhaSetor),
				)
				.filter(Boolean)
				.sort()[0] ||
			'';
		// A data histórica também é setorial. Incluir linhas de outros setores
		// fazia uma partição recém-criada exibir a data do setor-base.
		const dataInicialOriginal =
			datasCriacaoParticao[0] ||
			dataAncoraDoSetor ||
			linhasDoMesmoSetor
				.map((linhaHistorica = {}) =>
					getDataInicialProfissionalEscalaIsoFromLinha(
						linhaHistorica,
					),
				)
				.filter(Boolean)
				.sort()[0] ||
			'';
		const datasAtualizacaoDaLinha = normalizarDatas(
			linhasDoMesmoSetor.flatMap((linhaSetor = {}) => [
				linhaSetor?.atualizado_em,
				linhaSetor?.updated_at,
				linhaSetor?.ultima_atualizacao,
				linhaSetor?.data_atualizacao,
				linhaSetor?.regime_escala?.atualizado_em,
				linhaSetor?.regime_escala?.updated_at,
				linhaSetor?.regime_escala?.ultima_atualizacao,
				linhaSetor?.regime_escala?.data_atualizacao,
			]),
		);
		const dataUltimaAtualizacaoDoSetor =
			datasAtualizacaoParticao[datasAtualizacaoParticao.length - 1] ||
			datasAtualizacaoDaLinha[datasAtualizacaoDaLinha.length - 1] ||
			// Uma nova versão para um setor destino pode iniciar em outra data,
			// enquanto a simples alteração de partição não muda a data exibida do
			// setor-base. O backend não fornece timestamp próprio nesse resumo,
			// portanto a origem usa sua âncora estável.
			(isLinhaOrigemDeSplit(linha)
				? dataAncoraDoSetor
				: dataVigenciaDaVersaoDoSetor) ||
			dataInicialDoSetor;

		return {
			id: `${setor?.id || setor?.setor_id || index}-${linha?.linha_id || 'linha'}`,
			setorId: String(setor?.id || setor?.setor_id || ''),
			setorNome: setorNome || `Setor ${index + 1}`,
			// A origem é a referência da jornada quando seus horários são
			// repartidos entre setores. Mantemos essa informação no item visual
			// para que ela continue identificável no modal, mesmo recolhida.
			isSetorBase: isAbaDoSetorOrigemParticionado,
			diasSemanaBloqueadosPorParticaoIntegral,
			edicaoVia: normalizeCode(getProjecaoSplitLinha(linha)?.edicao_via),
			exigeEdicaoViaAlocacaoOrigem: exigeEdicaoViaAlocacaoOrigem(linha),
			isSetorDerivado: isLinhaDerivadaDeAlocacaoOrigem(linha),
			sourceLinha: linha,
			regimeVinculoId:
				linha?.regime_vinculo_id || escalaRegime?.id || null,
			regime: getRegimeDisplayName(itemRegime, regimesOptions),
			tipoRecorrencia: itemTipoRecorrencia,
			presetCiclo: itemPresetCiclo,
			nomeRecorrencia: itemNomeRecorrencia,
			recorrenciaValue: itemNomeRecorrencia,
			dataInicial: formatISOToDisplay(dataInicialDoSetor) || '--/--/----',
			dataInicialOriginal: formatISOToDisplay(dataInicialOriginal),
			dataUltimaAtualizacao: formatISOToDisplay(
				dataUltimaAtualizacaoDoSetor,
			),
			diasSemanaValores: itemDiasSemana,
			diasSemana: formatDiasSemanaResumo(itemDiasSemana),
			horariosByDia: itemHorariosSemana,
			horariosOptionsByDia: itemHorariosOptionsSemana,
			horariosParticionadosByDia,
			horaInicio: itemHoraInicio || '07',
			horaFim: itemHoraFim || '19',
			horario: `${itemHoraInicio || '07:00'} às ${itemHoraFim || '19:00'}`,
			periodoRotacao: itemPeriodoRotacao,
			semanasRotacao: itemSemanasRotacao,
			datasEspecificas: datasEspecificasDoSetor,
		};
	});
};

const getContextoOperacional = (contextoAplicado = {}, linha = {}) => ({
	...contextoAplicado,
	setor: Number(getLinhaSetorId(linha) || contextoAplicado?.setor || 0),
});

const getLinhaRegimeVinculoId = (linha = {}) =>
	Number(
		linha?.regime_vinculo_id ||
			linha?.regime_escala?.id ||
			linha?.escala_regime?.id ||
			0,
	);

export const getLinhaSetorAcionavel = (itemOuLinha = {}) => {
	const linhaPrincipal = itemOuLinha?.sourceLinha || itemOuLinha;
	const setorId = String(
		itemOuLinha?.setorId || getLinhaSetorId(linhaPrincipal) || '',
	).trim();
	const candidatas = [
		linhaPrincipal,
		...(Array.isArray(itemOuLinha?.sourceLinhas)
			? itemOuLinha.sourceLinhas
			: []),
	].filter(Boolean);
	const doMesmoSetor = (linha = {}) =>
		!setorId || String(getLinhaSetorId(linha) || '').trim() === setorId;

	// As abas consolidadas também carregam uma linha estrutural, usada apenas
	// para desenhar as partições. Ela não possui regime_vinculo_id e nunca pode
	// ser usada para enviar uma mutação ao backend.
	return (
		candidatas.find(
			(linha = {}) =>
				doMesmoSetor(linha) &&
				!isLinhaEstruturalAlocacaoOrigem(linha) &&
				Boolean(getLinhaRegimeVinculoId(linha)),
		) ||
		candidatas.find(
			(linha = {}) =>
				doMesmoSetor(linha) && Boolean(getLinhaRegimeVinculoId(linha)),
		) ||
		linhaPrincipal
	);
};

const getContextoVisualizacaoRequest = (contextoAplicado = {}) => ({
	...contextoAplicado,
	setor: isSetorTodosValue(contextoAplicado?.setor)
		? ''
		: contextoAplicado?.setor,
});

export const resolveContextoVisualSetor = ({ targetSetor } = {}) => {
	const targetRepresentaTodos =
		targetSetor === '' ||
		targetSetor === null ||
		targetSetor === undefined ||
		isSetorTodosValue(targetSetor);
	if (targetRepresentaTodos) return SETOR_TODOS_VALUE;

	// O filtro escolhido pelo usuário é a fonte de verdade do refresh.
	// A API pode omitir ou devolver um contexto mais amplo após uma mutação.
	return targetSetor;
};

export const buildTrocaEquivalentePayload = ({
	itemOrigemId,
	itemDestinoId,
	complementos = [],
	recortes = {},
	previewEtag = '',
	comandoNormalizado = null,
} = {}) => {
	const origemId = Number(itemOrigemId || 0);
	const destinoId = Number(itemDestinoId || 0);
	if (!origemId || !destinoId) return null;
	const buildItem = (itemId) => {
		const id = Number(itemId || 0);
		const recorte = recortes?.[id] || recortes?.[String(id)] || null;
		return {
			item_id: id,
			...(recorte?.inicio && recorte?.fim
				? {
						recorte: {
							inicio: recorte.inicio,
							fim: recorte.fim,
						},
					}
				: {}),
		};
	};
	const itensA = [buildItem(origemId)];
	const itensB = [buildItem(destinoId)];

	complementos.forEach((item = {}) => {
		const itemId = Number(item?.item_id || 0);
		if (!itemId) return;
		if (normalizeCode(item?.lado) === 'A') itensA.push(buildItem(itemId));
		if (normalizeCode(item?.lado) === 'B') itensB.push(buildItem(itemId));
	});

	const dedupeItems = (items = []) => {
		const seen = new Set();
		return items.filter((item = {}) => {
			const key = `${item.item_id}-${item.recorte?.inicio || ''}-${item.recorte?.fim || ''}`;
			if (!item.item_id || seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	};
	// A confirmação deve reutilizar a decisão congelada pela simulação. Não
	// reconstrua os itens a partir dos campos visuais depois que o servidor
	// devolveu o comando normalizado e o seu etag.
	if (comandoNormalizado && typeof comandoNormalizado === 'object') {
		return {
			...comandoNormalizado,
			...(previewEtag ? { preview_etag: previewEtag } : {}),
		};
	}

	return {
		unidade: 'CARD',
		itens_a: dedupeItems(itensA),
		itens_b: dedupeItems(itensB),
		...(previewEtag ? { preview_etag: previewEtag } : {}),
	};
};

const getTrocaEquivalentePayloadSignature = (payload = {}) => {
	const itensA = (Array.isArray(payload?.itens_a) ? payload.itens_a : [])
		.map(
			(item) =>
				`${Number(item?.item_id || 0)}:${item?.recorte?.inicio || ''}-${item?.recorte?.fim || ''}`,
		)
		.filter(Boolean)
		.sort()
		.join('-');
	const itensB = (Array.isArray(payload?.itens_b) ? payload.itens_b : [])
		.map(
			(item) =>
				`${Number(item?.item_id || 0)}:${item?.recorte?.inicio || ''}-${item?.recorte?.fim || ''}`,
		)
		.filter(Boolean)
		.sort()
		.join('-');

	return itensA && itensB ? `a:${itensA}|b:${itensB}` : '';
};

const createTrocaEquivalenteIdempotencyKey = () => {
	if (
		typeof window !== 'undefined' &&
		window.crypto &&
		typeof window.crypto.randomUUID === 'function'
	) {
		return window.crypto.randomUUID();
	}

	return `troca-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const isTrocaEquivalenteValida = (avaliacao = {}) => {
	const acaoEfetivar = avaliacao?.acoes?.efetivar;
	const efetivacaoDisponivel = acaoEfetivar
		? acaoEfetivar.disponivel === true
		: true;

	return Boolean(
		avaliacao?.valido === true &&
		avaliacao?.bloqueado === false &&
		avaliacao?.saldo?.equivalente === true &&
		efetivacaoDisponivel &&
		(Array.isArray(avaliacao?.erros_pacote)
			? avaliacao.erros_pacote.length === 0
			: true) &&
		(Array.isArray(avaliacao?.itens)
			? avaliacao.itens.every((item) => item?.apto !== false)
			: true),
	);
};

export const getLinhasExclusaoPorContexto = ({
	linha,
	isSetorTodos = false,
	setorAtual = '',
} = {}) => {
	if (!linha) return [];
	const linhasConsolidadas =
		linha?.is_consolidada_todos && Array.isArray(linha?.setores_linhas)
			? linha.setores_linhas
			: [];
	const setorNormalizado = String(setorAtual || '').trim();
	// A grade consolida todas as linhas do profissional inclusive fora do filtro
	// "Todos". Ao excluir dentro de um setor, todas as linhas operacionais
	// daquele setor precisam receber o encerramento; excluir apenas a linha-base
	// deixava cards de outros regimes do mesmo profissional na escala.
	const linhasDoSetor =
		setorNormalizado && linhasConsolidadas.length > 0
			? linhasConsolidadas.filter(
					(linhaSetor = {}) =>
						String(getLinhaSetorId(linhaSetor) || '') ===
						setorNormalizado,
				)
			: [];
	const linhasBase = isSetorTodos
		? linhasConsolidadas.length > 0
			? linhasConsolidadas
			: [linha]
		: linhasDoSetor.length > 0
			? linhasDoSetor
			: [linha];
	const seen = new Set();

	return linhasBase.filter((linhaSetor = {}) => {
		const regimeVinculoId = getLinhaRegimeVinculoId(linhaSetor);
		const linhaId = String(linhaSetor?.linha_id || '').trim();
		if (!regimeVinculoId && !linhaId) return false;
		const key = `${regimeVinculoId || linhaId}:${getLinhaSetorId(linhaSetor) || ''}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

const getLinhaProfissionalGroupKey = (linha = {}) => {
	const profissionalId = getProfissionalIdFromLinha(linha);
	if (profissionalId) return `id:${profissionalId}`;

	const cpf = normalizeCpf(getCpfFromLinha(linha));
	if (cpf) return `cpf:${cpf}`;

	return `nome:${normalizeSearchValue(getLinhaDisplayName(linha))}`;
};

const getLinhaExclusaoLocalKey = (linha = {}) =>
	`${getLinhaProfissionalGroupKey(linha)}|${String(
		getLinhaSetorId(linha) || '',
	)}|${String(linha?.regime_vinculo_id || linha?.regime_escala?.id || '')}`;

const getLinhaSetorOcultacaoLocalKey = (linha = {}) =>
	`setor:${getLinhaProfissionalGroupKey(linha)}|${String(
		getLinhaSetorId(linha) || '',
	)}`;

const getLinhaProfissionalOcultacaoLocalKey = (linha = {}) =>
	`profissional:${getLinhaProfissionalGroupKey(linha)}`;

const getMarcadoresOcultacaoAposEncerramento = ({
	linhas = [],
	escopo = 'SETOR',
} = {}) => {
	const linhasNormalizadas = Array.isArray(linhas) ? linhas : [];
	return Array.from(
		new Set(
			linhasNormalizadas.flatMap((linha = {}) => [
				getLinhaExclusaoLocalKey(linha),
				getLinhaSetorOcultacaoLocalKey(linha),
				...(escopo === 'ESTABELECIMENTO'
					? [getLinhaProfissionalOcultacaoLocalKey(linha)]
					: []),
			]),
		),
	);
};

const formatMinutesAsHours = (minutos) => {
	const total = Number(minutos);
	if (!Number.isFinite(total)) return 'Não informada';
	return `${Math.floor(total / 60)}h${String(total % 60).padStart(2, '0')}`;
};

const formatResumoEdicaoOrigemValue = (campo, valor) => {
	const raw = String(valor || '').trim();
	if (!raw) return 'Não informado';
	if (campo === 'vigencia_inicio') return formatISOToDisplay(raw) || raw;
	if (campo === 'hora_inicio' || campo === 'hora_fim') {
		return raw.slice(0, 5);
	}
	if (campo === 'preset_ciclo') {
		return raw.replace(/^CICLO_/i, '').replace(/_/g, ' ');
	}
	return raw;
};

const buildResumoEdicaoOrigem = ({ entrada = {}, proposta = {} } = {}) => {
	const getPadrao = (configuracao = {}) =>
		configuracao?.padrao_desejado ||
		configuracao?.padroes_origem?.[0] ||
		configuracao?.padroes_alocacao?.[0] ||
		{};
	const padraoAtual = getPadrao(entrada);
	const padraoProposto = getPadrao(proposta);
	const campos = [
		{
			chave: 'regime',
			label: 'Regime',
			atual: entrada?.regime,
			proposto: proposta?.regime,
		},
		{
			chave: 'vigencia_inicio',
			label: 'Data inicial',
			atual: entrada?.vigencia_inicio,
			proposto: proposta?.vigencia_inicio,
		},
		{
			chave: 'preset_ciclo',
			label: 'Modalidade da escala',
			atual: padraoAtual?.preset_ciclo,
			proposto: padraoProposto?.preset_ciclo,
		},
		{
			chave: 'hora_inicio',
			label: 'Horário inicial',
			atual: padraoAtual?.hora_inicio,
			proposto: padraoProposto?.hora_inicio,
		},
		{
			chave: 'hora_fim',
			label: 'Horário final',
			atual: padraoAtual?.hora_fim,
			proposto: padraoProposto?.hora_fim,
		},
	];
	return campos.reduce(
		(resumo, campo) => {
			const atual = formatResumoEdicaoOrigemValue(
				campo.chave,
				campo.atual,
			);
			const proposto = formatResumoEdicaoOrigemValue(
				campo.chave,
				campo.proposto,
			);
			if (atual === proposto)
				resumo.mantidos.push({ ...campo, valor: atual });
			else
				resumo.editados.push({
					...campo,
					anterior: atual,
					valor: proposto,
				});
			return resumo;
		},
		{ mantidos: [], editados: [] },
	);
};

const getResumoHorarioAtualizacaoJornada = (resumoEdicao = {}) => {
	const editados = Array.isArray(resumoEdicao?.editados)
		? resumoEdicao.editados
		: [];
	const mantidos = Array.isArray(resumoEdicao?.mantidos)
		? resumoEdicao.mantidos
		: [];
	const getCampo = (chave) =>
		editados.find((campo = {}) => campo?.chave === chave) ||
		mantidos.find((campo = {}) => campo?.chave === chave) ||
		{};
	const hora = (value) => formatHourInputToTimeValue(value) || '00:00';
	const horaInicio = getCampo('hora_inicio');
	const horaFim = getCampo('hora_fim');

	return {
		anterior:
			hora(horaInicio.anterior || horaInicio.valor) +
			' às ' +
			hora(horaFim.anterior || horaFim.valor),
		atual: hora(horaInicio.valor) + ' às ' + hora(horaFim.valor),
	};
};

const getResumoFaixasAtualizadas = (itens = []) =>
	(Array.isArray(itens) ? itens : []).flatMap((item = {}) => {
		const dias = normalizeDiasSemanaValues(item?.diasSemanaValores);
		const snapshot = item?.originalSnapshot || {};
		const diasOriginais = normalizeDiasSemanaValues(
			String(snapshot?.diasSemana || '')
				.split(',')
				.filter(Boolean),
		);
		let horariosOriginais = {};
		try {
			horariosOriginais = JSON.parse(snapshot?.horariosByDia || '{}');
		} catch {
			horariosOriginais = {};
		}
		// Prioriza dias criados; sem isso uma inclusão é descrita como uma
		// alteração de horário que, na prática, nem ocorreu.
		const diasNovos = dias.filter((dia) => !diasOriginais.includes(dia));
		const diasAlterados = dias.filter((dia) => {
			const atual = item?.horariosByDia?.[dia] || {};
			const anterior = horariosOriginais?.[dia] || {};
			return (
				diasOriginais.includes(dia) &&
				(String(atual?.hora_inicio || '').slice(0, 5) !==
					String(anterior?.hora_inicio || '').slice(0, 5) ||
					String(atual?.hora_fim || '').slice(0, 5) !==
						String(anterior?.hora_fim || '').slice(0, 5))
			);
		});
		const diasParaResumo =
			diasNovos.length > 0 ? diasNovos : diasAlterados;
		return diasParaResumo.flatMap((dia) => {
			const horario = item?.horariosByDia?.[dia] || {};
			const inicio = formatHourInputToTimeValue(horario?.hora_inicio);
			const fim = formatHourInputToTimeValue(horario?.hora_fim);
			if (!inicio || !fim) return [];
			const nomeDia =
				DIAS_SEMANA_OPTIONS.find(
					(option) => Number(option?.value) === Number(dia),
				)?.label || `Dia ${dia}`;
			return [{ dia: nomeDia, inicio, fim }];
		});
	});

// Alternativas assistidas podem ser devolvidas mais de uma vez com IDs,
// posição no ranking e snapshots de assinatura diferentes, mesmo quando a
// decisão proposta é exatamente a mesma. Esses metadados não criam uma nova
// escolha para a pessoa responsável; todo o restante permanece na comparação
// para não ocultar uma diferença real de configuração, conflito ou impacto.
const ALTERNATIVA_JORNADA_CHAVES_TRANSITORIAS = new Set([
	'sugestao_id',
	'alternativa_id',
	'ordem',
	'ranking',
	'rank',
	'score',
	'pontuacao',
	'snapshot_planejamento_etag',
	'snapshot_etag',
	'preview_etag',
	'chave_idempotencia',
	'idempotency_key',
	'padrao_id',
	'particao_id',
	'jornada_id',
	'card_id',
	'hash_comando',
	'versao_algoritmo',
	'desempate',
	'ordem_final',
]);

const normalizarAlternativaJornadaParaComparacao = (valor) => {
	if (Array.isArray(valor)) {
		return valor.map(normalizarAlternativaJornadaParaComparacao);
	}
	if (!valor || typeof valor !== 'object') return valor;

	return Object.keys(valor)
		.filter((chave) => !ALTERNATIVA_JORNADA_CHAVES_TRANSITORIAS.has(chave))
		.sort()
		.reduce((resultado, chave) => {
			resultado[chave] = normalizarAlternativaJornadaParaComparacao(
				valor[chave],
			);
			return resultado;
		}, {});
};

export const alternativasJornadaSaoEquivalentes = (alternativas = []) => {
	const lista = Array.isArray(alternativas) ? alternativas : [];
	if (lista.length <= 1) return true;
	const camposDaDecisao = [
		'bloqueado',
		'preset_solicitado',
		'preset_proposto',
		'padrao_proposto',
		'padrao_desejado',
		'rigidez_ciclo',
		'tipo_recorrencia',
		'fase',
		'ancora',
		'padroes_origem',
		'particoes',
		'setores_elegiveis_ids',
		'distribuicao_setorial_desejada',
		'ocorrencias',
		'carga',
		'carga_media_semanal_minutos',
		'media_semanal_minutos',
		'issues',
		'avisos',
		'warnings',
		'conflitos',
		'impactos',
		'resumo',
		'explicacao',
		'motivo',
	];
	const assinaturas = new Set(
		lista.map((alternativa = {}) => {
			const decisao = camposDaDecisao.reduce((resultado, campo) => {
				if (Object.prototype.hasOwnProperty.call(alternativa, campo)) {
					resultado[campo] = alternativa[campo];
				}
				return resultado;
			}, {});
			// A entrada e a rota HATEOAS são assinadas por sugestão e podem variar
			// sem mudar a proposta. Mantemos apenas se a prévia está disponível;
			// o comando completo da primeira alternativa continua sendo usado
			// exatamente como foi devolvido pelo servidor.
			if (alternativa?.acoes?.prever) {
				decisao.prever_disponivel =
					alternativa.acoes.prever.disponivel !== false;
			}
			return JSON.stringify(
				normalizarAlternativaJornadaParaComparacao(decisao),
			);
		}),
	);
	return assinaturas.size === 1;
};

// A API identifica as jornadas que precisam ser versionadas tanto pelo
// vínculo explícito com a alocação de origem quanto por `edicao_via`. O
// segundo caso é importante: ele pode chegar antes de todos os detalhes da
// origem estarem materializados no ambiente mensal.
export const exigeEdicaoViaAlocacaoOrigem = (linha = {}) =>
	normalizeCode(getProjecaoSplitLinha(linha)?.edicao_via) ===
		'ALOCACAO_ORIGEM' || Boolean(getAlocacaoOrigemId(linha));

export const isLinhaDerivadaDeAlocacaoOrigem = (linha = {}) =>
	exigeEdicaoViaAlocacaoOrigem(linha) &&
	!isLinhaOrigemDeSplit(linha) &&
	(isLinhaDestinoDeSplit(linha) ||
		getProjecaoSplitLinha(linha)?.derivada_de_split === true);

const isAcaoEdicaoOrigemAssistida = (acao = {}) =>
	String(acao?.href || '')
		.toLowerCase()
		.includes('planejamento-profissional/sugerir');

const isAcaoSomenteLeitura = (acao = {}) =>
	String(acao?.method || '')
		.trim()
		.toUpperCase() === 'GET';

// Uma ação `editar` GET não altera a origem: ela apenas abre/prepara o
// comando HATEOAS que deverá ser previsto. Mesclar a entrada devolvida pela
// preparação com o estado final montado no modal preserva campos assinados
// (versão, contexto etc.) sem perder as alterações reais de configuração e
// partições.
export const montarEntradaPreviaEdicaoOrigem = ({
	entradaAcao = {},
	corpoEdicao = {},
} = {}) => ({
	...(entradaAcao || {}),
	...(corpoEdicao || {}),
	...(entradaAcao?.contexto || corpoEdicao?.contexto
		? {
				contexto: {
					...(entradaAcao?.contexto || {}),
					...(corpoEdicao?.contexto || {}),
				},
			}
		: {}),
	...(entradaAcao?.configuracao || corpoEdicao?.configuracao
		? {
				configuracao: {
					...(entradaAcao?.configuracao || {}),
					...(corpoEdicao?.configuracao || {}),
				},
			}
		: {}),
	...(Array.isArray(corpoEdicao?.particoes)
		? { particoes: corpoEdicao.particoes }
		: {}),
});

const mergeCelulasConsolidadas = (linhasGrupo = []) => {
	const celulasPorData = new Map();

	linhasGrupo.forEach((linha = {}) => {
		(Array.isArray(linha?.celulas) ? linha.celulas : []).forEach(
			(celula = {}) => {
				const data = String(celula?.data || '').trim();
				if (!data) return;

				const atual = celulasPorData.get(data) || {
					...celula,
					cards: [],
				};
				const currentCards = Array.isArray(atual?.cards)
					? atual.cards
					: [];
				const nextCards = Array.isArray(celula?.cards)
					? celula.cards.map((card = {}) => ({
							...card,
							setor: card?.setor || linha?.setor,
							setor_id: card?.setor_id || getLinhaSetorId(linha),
							escala_mensal:
								card?.escala_mensal || linha?.escala_mensal,
						}))
					: [];

				celulasPorData.set(data, {
					...atual,
					...celula,
					cards: [...currentCards, ...nextCards],
					setores_linhas: [
						...(Array.isArray(atual?.setores_linhas)
							? atual.setores_linhas
							: []),
						linha,
					],
				});
			},
		);
	});

	return Array.from(celulasPorData.values()).sort((a, b) =>
		String(a?.data || '').localeCompare(String(b?.data || '')),
	);
};

const mergeRegimeEscalaConsolidado = (linhasGrupo = []) => {
	const base = linhasGrupo[0]?.regime_escala || {};
	const padroes = [];
	const ocorrencias = [];
	const diasSemana = new Set();

	linhasGrupo.forEach((linha = {}) => {
		const regime = linha?.regime_escala || {};
		normalizeDiasSemanaValues(regime?.dias_semana).forEach((dia) =>
			diasSemana.add(dia),
		);
		(Array.isArray(regime?.ocorrencias) ? regime.ocorrencias : []).forEach(
			(ocorrencia) => {
				ocorrencias.push({
					...ocorrencia,
					setor_id: ocorrencia?.setor_id || getLinhaSetorId(linha),
				});
				normalizeDiasSemanaValues([ocorrencia?.dia_semana]).forEach(
					(dia) => diasSemana.add(dia),
				);
			},
		);
		normalizePadroesAlocacao(regime?.padroes_alocacao).forEach((padrao) => {
			padroes.push({
				...padrao,
				setor_id: padrao?.setor_id || getLinhaSetorId(linha),
			});
			normalizeDiasSemanaValues(padrao?.dias_semana).forEach((dia) =>
				diasSemana.add(dia),
			);
		});
	});

	return {
		...base,
		dias_semana: Array.from(diasSemana).sort((a, b) => a - b),
		ocorrencias:
			ocorrencias.length > 0 ? ocorrencias : base?.ocorrencias || [],
		padroes_alocacao:
			padroes.length > 0 ? padroes : base?.padroes_alocacao || [],
	};
};

export const consolidateLinhasPorProfissional = (linhas = []) => {
	const grupos = new Map();
	const output = [];
	const expandirLinhasSetores = (linha = {}) => {
		const setoresLinhas = Array.isArray(linha?.setores_linhas)
			? linha.setores_linhas
			: [];
		if (setoresLinhas.length === 0) return [linha];

		return setoresLinhas.flatMap((setorLinha = {}) =>
			expandirLinhasSetores({
				...linha,
				...setorLinha,
				setores_linhas: setorLinha?.setores_linhas,
				profissional: setorLinha?.profissional || linha?.profissional,
				vinculo_profissional:
					setorLinha?.vinculo_profissional ||
					linha?.vinculo_profissional,
			}),
		);
	};

	linhas.flatMap(expandirLinhasSetores).forEach((linha = {}) => {
		if (isVacanciaLinha(linha)) {
			output.push(linha);
			return;
		}

		const key = getLinhaProfissionalGroupKey(linha);
		if (!grupos.has(key)) grupos.set(key, []);
		grupos.get(key).push(linha);
	});

	grupos.forEach((grupo) => {
		const linhasUnicas = grupo.filter((linha = {}, index) => {
			const setorId = String(getLinhaSetorId(linha) || '');
			const regimeVinculoId = String(
				linha?.regime_vinculo_id || linha?.regime_escala?.id || '',
			);
			const linhaId = String(linha?.linha_id || '');
			const chave = `${setorId}|${regimeVinculoId || linhaId}`;
			return !grupo.slice(0, index).some((linhaAnterior = {}) => {
				const setorAnterior = String(
					getLinhaSetorId(linhaAnterior) || '',
				);
				const regimeAnterior = String(
					linhaAnterior?.regime_vinculo_id ||
						linhaAnterior?.regime_escala?.id ||
						'',
				);
				const linhaAnteriorId = String(linhaAnterior?.linha_id || '');
				return (
					`${setorAnterior}|${regimeAnterior || linhaAnteriorId}` ===
					chave
				);
			});
		});
		const base = linhasUnicas[0] || {};
		output.push({
			...base,
			linha_id: `todos-${getLinhaProfissionalGroupKey(base)}`,
			is_consolidada_todos: true,
			setores_linhas: linhasUnicas,
			regime_escala: mergeRegimeEscalaConsolidado(linhasUnicas),
			celulas: mergeCelulasConsolidadas(linhasUnicas),
			profissional: {
				...(base?.profissional || {}),
				setores:
					base?.profissional?.setores ||
					linhasUnicas.map((linha) => linha?.setor).filter(Boolean),
				escalas:
					base?.profissional?.escalas ||
					linhasUnicas
						.map(
							(linha) =>
								linha?.escala_mensal || linha?.regime_escala,
						)
						.filter(Boolean),
			},
		});
	});

	return output;
};

const getLinhaEscopoSetorAtual = (linha = {}, contextoAplicado = {}) => {
	if (!linha || !Array.isArray(linha?.setores_linhas)) return linha;
	if (isSetorTodosValue(contextoAplicado?.setor)) return linha;

	const setorAtual = String(contextoAplicado?.setor || '').trim();
	if (!setorAtual) return linha;

	return (
		linha.setores_linhas.find(
			(setorLinha) =>
				String(getLinhaSetorId(setorLinha) || '') === setorAtual,
		) || linha
	);
};

const createGerenciamentoTurnosDataExtra = () => ({
	id: `turnos-data-${Date.now()}-${Math.random().toString(36).slice(2)}`,
	data: '',
	horaInicio: '',
	horaFim: '',
});

const getGerenciamentoTurnosItemSnapshot = (item = {}) => {
	const diasSemana = normalizeDiasSemanaValues(item.diasSemanaValores);
	const horariosByDia = diasSemana.reduce((acc, dia) => {
		const horario = item.horariosByDia?.[dia] || {};
		acc[dia] = {
			hora_inicio: String(horario.hora_inicio || '').slice(0, 5),
			hora_fim: String(horario.hora_fim || '').slice(0, 5),
		};
		return acc;
	}, {});
	const datasEspecificas = (
		Array.isArray(item.datasEspecificasEditaveis)
			? item.datasEspecificasEditaveis
			: []
	)
		.map((dataItem = {}) => ({
			data: formatDateInputToISO(dataItem.data),
			hora_inicio: String(dataItem.horaInicio || '').slice(0, 5),
			hora_fim: String(dataItem.horaFim || '').slice(0, 5),
		}))
		.filter((dataItem) => dataItem.data)
		.sort((a, b) =>
			`${a.data}|${a.hora_inicio}|${a.hora_fim}`.localeCompare(
				`${b.data}|${b.hora_inicio}|${b.hora_fim}`,
			),
		);

	return {
		setorId: String(item.setorId || ''),
		regime: normalizeCode(item.regimeValue || item.regime),
		tipoRecorrencia: normalizeCode(item.tipoRecorrencia),
		presetCiclo: getPayloadPresetCiclo(item.presetCiclo),
		periodoRotacao: String(Number(item.periodoRotacao || 0) || ''),
		semanasRotacao: (Array.isArray(item.semanasRotacao)
			? item.semanasRotacao
			: []
		)
			.map((diasSemana) =>
				normalizeDiasSemanaValues(diasSemana).join(','),
			)
			.join('|'),
		horaInicio: String(item.horaInicio || '').slice(0, 5),
		horaFim: String(item.horaFim || '').slice(0, 5),
		dataInicial: formatDateInputToISO(
			item.dataInicialValue || item.dataInicial,
		),
		diasSemana: diasSemana.join(','),
		horariosByDia: JSON.stringify(horariosByDia),
		horariosOptionsByDia: JSON.stringify(
			diasSemana.reduce((acc, dia) => {
				acc[dia] = item?.horariosOptionsByDia?.[dia] || [];
				return acc;
			}, {}),
		),
		datasEspecificas: JSON.stringify(datasEspecificas),
	};
};

const hasGerenciamentoTurnosItemChanges = (item = {}) => {
	if (!item.originalSnapshot) return true;
	const currentSnapshot = getGerenciamentoTurnosItemSnapshot(item);
	return Object.keys(currentSnapshot).some(
		(key) =>
			String(currentSnapshot[key] || '') !==
			String(item.originalSnapshot[key] || ''),
	);
};

const buildGerenciamentoTurnosEditableItems = (setores = []) =>
	setores.map((item = {}) => {
		const diasSelecionados = normalizeDiasSemanaValues(
			item.diasSemanaValores,
		);
		const horaInicio = String(item.horaInicio || '').slice(0, 5);
		const horaFim = String(item.horaFim || '').slice(0, 5);
		const horariosOriginais =
			item.horariosByDia && typeof item.horariosByDia === 'object'
				? item.horariosByDia
				: {};
		const editableItem = {
			...item,
			regimeValue: item.regime || '',
			dataInicialValue: formatDateDisplayMask(item.dataInicial),
			diasSemanaValores: diasSelecionados,
			periodoRotacao:
				Number(item.periodoRotacao || 0) ||
				(Array.isArray(item.semanasRotacao)
					? item.semanasRotacao.length
					: 0) ||
				undefined,
			semanasRotacao: (Array.isArray(item.semanasRotacao)
				? item.semanasRotacao
				: []
			).map((diasSemana) => normalizeDiasSemanaValues(diasSemana)),
			horariosByDia: diasSelecionados.reduce((acc, dia) => {
				const horarioOriginal = horariosOriginais?.[dia] || {};
				acc[dia] = {
					hora_inicio:
						formatHourInputToTimeValue(
							horarioOriginal?.hora_inicio ||
								horarioOriginal?.horaInicio,
						) || horaInicio,
					hora_fim:
						formatHourInputToTimeValue(
							horarioOriginal?.hora_fim ||
								horarioOriginal?.horaFim,
						) || horaFim,
				};
				return acc;
			}, {}),
			horariosOptionsByDia: diasSelecionados.reduce((acc, dia) => {
				const opcoes = Array.isArray(item?.horariosOptionsByDia?.[dia])
					? item.horariosOptionsByDia[dia]
					: [];
				acc[dia] =
					opcoes.length > 0
						? opcoes
						: [horariosOriginais?.[dia] || {}];
				return acc;
			}, {}),
			horariosParticionadosByDia: diasSelecionados.reduce((acc, dia) => {
				const faixas = Array.isArray(
					item?.horariosParticionadosByDia?.[dia],
				)
					? item.horariosParticionadosByDia[dia]
					: [];
				if (faixas.length > 1) acc[dia] = faixas;
				return acc;
			}, {}),
			datasEspecificasEditaveis: (Array.isArray(item.datasEspecificas)
				? item.datasEspecificas
				: []
			).map((dataItem = {}, dataIndex) => ({
				// A mesma data pode conter mais de uma faixa após um recorte.
				// A data sozinha não é uma chave de edição: isso fazia os dois
				// campos compartilharem estado e alterarem seus horários juntos.
				id: `${item.id || 'setor'}-${
					dataItem.id || 'data'
				}-${formatDateInputToISO(dataItem.data || dataItem.dataLabel) || 'sem-data'}-${String(
					dataItem.horaInicio || '',
				).slice(
					0,
					5,
				)}-${String(dataItem.horaFim || '').slice(0, 5)}-${dataIndex}`,
				data: formatDateDisplayMask(
					dataItem.data || formatDateInputToISO(dataItem.dataLabel),
				),
				horaInicio: String(dataItem.horaInicio || '').slice(0, 5),
				horaFim: String(dataItem.horaFim || '').slice(0, 5),
				legado: dataItem.legado === true,
				readOnly: dataItem.readOnly === true,
				origem: dataItem.origem || '',
				label: dataItem.label || '',
			})),
		};

		return {
			...editableItem,
			originalSnapshot: getGerenciamentoTurnosItemSnapshot(editableItem),
		};
	});

const getGerenciamentoTurnosRegimeOptions = (
	currentRegime = '',
	regimesOptions = [],
) => {
	const seen = new Set();
	const options = [];
	const rawOptions = [
		currentRegime,
		...(Array.isArray(regimesOptions) ? regimesOptions : []).map(
			(regime = {}) =>
				regime?.nome ||
				regime?.label ||
				regime?.codigo ||
				regime?.value ||
				'',
		),
		'Plantonista',
		'Rotina',
	];

	rawOptions.forEach((label) => {
		const value = String(label || '').trim();
		const key = normalizeCode(value);
		if (!value || seen.has(key)) return;
		seen.add(key);
		options.push(value);
	});

	return options;
};

const getEspecialidadeIdFromLinha = (linha = {}) =>
	String(
		linha?.vinculo_profissional?.especialidade?.id ||
			linha?.vinculo_profissional?.especialidade_id ||
			linha?.vinculo_profissional?.categoria?.id ||
			linha?.vinculo_profissional?.categoria_id ||
			linha?.especialidade?.id ||
			linha?.especialidade_id ||
			linha?.categoria?.id ||
			linha?.categoria_id ||
			'',
	);

const getVacanciaObservacaoFromLinha = (linha = {}) =>
	String(
		linha?.vacancia?.observacao ||
			linha?.observacao ||
			getFirstCardFromLinha(linha)?.observacao ||
			getFirstCardFromLinha(linha)?.motivo ||
			getFirstCardFromLinha(linha)?.descricao ||
			'',
	).trim();

const getVacanciaInicioFromLinha = (linha = {}) =>
	formatISOToDisplay(
		linha?.vacancia?.data_inicio_vacancia ||
			linha?.data_inicio_vacancia ||
			linha?.regime_escala?.vigencia_inicio ||
			linha?.regime_escala?.data_ancora ||
			linha?.sugestao?.primeiro_dia ||
			'',
	);

const getInicioEscalaIsoFromLinha = (linha = {}) => {
	const candidatos = [
		linha?.regime_escala?.vigencia_inicio,
		linha?.regime_escala?.data_ancora,
		linha?.vacancia?.data_inicio_vacancia,
		linha?.data_inicio_vacancia,
		linha?.sugestao?.primeiro_dia,
		...(Array.isArray(linha?.celulas)
			? linha.celulas.map((celula = {}) => celula?.data)
			: []),
	]
		.map((valor) => formatDateInputToISO(valor))
		.filter(Boolean)
		.sort();

	return candidatos[0] || '';
};

export const getDataInicialProfissionalEscalaIsoFromLinha = (linha = {}) => {
	const regimeEscala = linha?.regime_escala || {};
	const escalaRegime =
		linha?.escala_regime || linha?.escalaMensalRegime || {};
	const regimeVinculo =
		linha?.regime_vinculo ||
		linha?.regimeVinculo ||
		linha?.regime_vinculo_escala ||
		{};
	const candidatos = [
		// A regra específica do setor é a fonte de verdade. O primeiro dia da
		// projeção mensal pode ser diferente da vigência cadastrada.
		regimeEscala?.vigencia_inicio,
		regimeEscala?.data_ancora,
		linha?.vigencia_inicio,
		linha?.data_ancora,
		regimeEscala?.primeiro_dia_trabalho_mes,
		escalaRegime?.vigencia_inicio,
		escalaRegime?.data_ancora,
		escalaRegime?.primeiro_dia_trabalho_mes,
		regimeVinculo?.vigencia_inicio,
		regimeVinculo?.data_ancora,
		regimeVinculo?.primeiro_dia_trabalho_mes,
		linha?.data_inicial,
		linha?.data_inicio,
		linha?.primeiro_dia_trabalho_mes,
		linha?.sugestao?.primeiro_dia,
	]
		.map((valor) => formatDateInputToISO(valor))
		.filter(Boolean);

	return candidatos[0] || '';
};

const getMesReferenciaInicioEscalaFromLinha = (linha = {}) => {
	const inicioIso = getInicioEscalaIsoFromLinha(linha);
	return inicioIso ? `${inicioIso.slice(0, 7)}-01` : '';
};

const getPhoneFromLinha = (linha = {}) =>
	formatPhoneMask(
		linha?.profissional?.telefone ||
			linha?.vinculo_profissional?.telefone ||
			linha?.telefone ||
			'',
	);

const getConselhoTipoFromLinha = (linha = {}) =>
	String(
		linha?.profissional?.tipo_conselho ||
			linha?.profissional?.conselho_tipo ||
			linha?.vinculo_profissional?.tipo_conselho ||
			linha?.vinculo_profissional?.conselho_tipo ||
			linha?.profissional?.conselho ||
			linha?.vinculo_profissional?.conselho ||
			'CRM',
	)
		.trim()
		.toUpperCase();

const getProfissionalIdFromLinha = (linha = {}) =>
	Number(
		linha?.profissional?.id ||
			linha?.vinculo_profissional?.profissional_id ||
			linha?.vinculo_profissional?.profissional?.id ||
			getFirstCardFromLinha(linha)?.profissional?.id ||
			getFirstCardFromLinha(linha)?.profissional_id ||
			0,
	) || null;

const getVinculoIdFromLinha = (linha = {}) =>
	Number(
		linha?.vinculo_profissional?.id ||
			linha?.vinculo_profissional_id ||
			linha?.profissional?.vinculo_id ||
			getFirstCardFromLinha(linha)?.vinculo_profissional_id ||
			getFirstCardFromLinha(linha)?.vinculo_id ||
			getFirstCardFromLinha(linha)?.vinculo?.id ||
			0,
	) || null;

// O agregado `alocacao-origem` descreve jornadas e versões estruturais, mas
// pode manter origens históricas. Para decidir quais abas existem no modal de
// setores, a fonte de verdade é a leitura detalhada do ambiente, que devolve
// somente as linhas operacionais da competência.
export const getLinhasAtivasDoAmbienteParaProfissional = ({
	ambiente = {},
	linhaReferencia = {},
	mesReferencia = '',
	ocultarEncerradasAteHoje = false,
	hoje = getTodayISODateLocal(),
} = {}) => {
	const referencias = getLinhasParticionamento([linhaReferencia]);
	const vinculos = new Set(
		referencias
			.map((linha = {}) => getVinculoIdFromLinha(linha))
			.filter(Boolean)
			.map(String),
	);
	const profissionais = new Set(
		referencias
			.map((linha = {}) => getProfissionalIdFromLinha(linha))
			.filter(Boolean)
			.map(String),
	);
	const cpfs = new Set(
		referencias
			.map((linha = {}) => normalizeCpf(getCpfFromLinha(linha)))
			.filter(Boolean),
	);
	const linhasResposta = [
		...(Array.isArray(ambiente?.linhas) ? ambiente.linhas : []),
		...(Array.isArray(ambiente?.ambiente?.linhas)
			? ambiente.ambiente.linhas
			: []),
	];

	const linhasPlanasResposta = getLinhasParticionamento(linhasResposta);
	const cardsDaResposta = linhasPlanasResposta.flatMap(
		(linha = {}) =>
			(Array.isArray(linha?.celulas) ? linha.celulas : []).flatMap(
				(celula = {}) =>
					getPersistedCards(celula).map((card = {}) => ({
						linha,
						celula,
						card,
					})),
			),
	);

	return linhasPlanasResposta.filter((linha = {}) => {
		if (isVacanciaLinha(linha)) return false;
		// O card de troca é devolvido em uma linha MANUAL, sem
		// `regime_vinculo_id`. Ele não cria uma aba própria, mas precisa integrar
		// a linha recorrente do mesmo profissional para aparecer como data
		// específica bloqueada no modal de setores e turnos.
		const linhaTrocaManual = linhaTemCardTrocaPersistido(linha);
		if (!getLinhaRegimeVinculoId(linha) && !linhaTrocaManual) return false;
		// A grade mantém o histórico dentro da competência, mas o modal de
		// gerenciamento representa somente setores que ainda podem receber
		// alterações. Sem esta distinção, um setor encerrado hoje reaparece ao
		// fechar e abrir o modal, embora o endpoint tenha respondido 200.
		const vigenciaFim = getLinhaVigenciaFimIso(linha);
		if (
			ocultarEncerradasAteHoje &&
			vigenciaFim &&
			vigenciaFim <= formatDateInputToISO(hoje)
		) {
			return false;
		}
		if (
			isLinhaEncerradaNoContextoOperacional({
				linha,
				mesReferencia,
			})
		) {
			return false;
		}

		const vinculoId = String(getVinculoIdFromLinha(linha) || '');
		const profissionalId = String(getProfissionalIdFromLinha(linha) || '');
		const cpf = normalizeCpf(getCpfFromLinha(linha));
		return Boolean(
			(vinculoId && vinculos.has(vinculoId)) ||
			(profissionalId && profissionais.has(profissionalId)) ||
			(cpf && cpfs.has(cpf)),
		);
	}).map((linha = {}) => {
		// O item relacionado pertence ao outro profissional e, portanto, não
		// passa pelo filtro acima. Guardamos sua linha apenas como referência para
		// saber em qual setor regular deste profissional a data de troca deve ser
		// exibida; ela nunca compõe uma aba própria.
		const referenciasTroca = (Array.isArray(linha?.celulas)
			? linha.celulas
			: []
		).flatMap((celula = {}) =>
			getPersistedCards(celula).flatMap((card = {}) => {
				if (!isTrocaCard(card, celula)) return [];
				const troca =
					card?.troca ||
					card?.resumo?.troca ||
					card?.dados_edicao?.troca ||
					{};
				const itemRelacionadoId = String(
					troca?.item_troca_relacionado_id ||
						troca?.item_relacionado_id ||
						card?.item_troca_relacionado_id ||
						'',
				);
				if (!itemRelacionadoId) return [];
				const referencia = cardsDaResposta.find(
					(item = {}) =>
						String(getCardItemId(item?.card) || '') === itemRelacionadoId,
				);
				return referencia?.linha ? [referencia.linha] : [];
			}),
		);
		const referenciasUnicas = Array.from(
			new Map(
				referenciasTroca.map((referencia = {}) => [
					String(referencia?.linha_id || getLinhaSetorId(referencia) || ''),
					referencia,
				]),
			).values(),
		);
		return referenciasUnicas.length > 0
			? { ...linha, _referencias_troca: referenciasUnicas }
			: linha;
	});
};

// A linha só pode permanecer na grade quando o mesmo ambiente que alimenta o
// modal ainda possui ao menos um setor operacional para aquele profissional.
// Isso evita exibir cartões residuais que o usuário não consegue abrir nem
// administrar no modal de setores.
export const isLinhaProfissionalGerenciavelNoAmbiente = ({
	ambiente = {},
	linha = {},
	mesReferencia = '',
	hoje = getTodayISODateLocal(),
} = {}) => {
	if (isVacanciaLinha(linha)) return true;

	return (
		getLinhasAtivasDoAmbienteParaProfissional({
			ambiente,
			linhaReferencia: linha,
			mesReferencia,
			ocultarEncerradasAteHoje: true,
			hoje,
		}).length > 0
	);
};

export const getCandidatasAlocacaoOrigemAtiva = (payload = {}) => {
	const candidatas = [
		...(Array.isArray(payload?.alocacoes_origem_candidatas)
			? payload.alocacoes_origem_candidatas
			: []),
		...(Array.isArray(payload?.resultado?.alocacoes_origem_candidatas)
			? payload.resultado.alocacoes_origem_candidatas
			: []),
		...(Array.isArray(payload?.error?.data?.alocacoes_origem_candidatas)
			? payload.error.data.alocacoes_origem_candidatas
			: []),
		...getAlocacoesOrigemFromPayload(payload),
	];
	const ids = new Set();
	return candidatas.filter((candidata = {}) => {
		const id = String(
			getAlocacaoOrigemIdFromPayload(candidata) || '',
		).trim();
		if (!id || ids.has(id)) return false;
		ids.add(id);
		return true;
	});
};

const getAcaoRefazerComoOrigemIndependente = (payload = {}) => {
	const acoes = getAcoesAlocacaoOrigemFromPayload(payload);
	return (
		acoes?.refazer_como_origem_independente ||
		payload?.alocacao_origem?.acoes?.refazer_como_origem_independente ||
		null
	);
};

const getVinculoTipoIdFromLinha = (linha = {}) =>
	Number(
		linha?.vinculo_profissional?.vinculo_tipo_id ||
			linha?.vinculo_profissional?.tipo_vinculo_id ||
			linha?.vinculo_profissional?.vinculo_tipo?.id ||
			linha?.vinculo_profissional?.vinculo?.id ||
			linha?.profissional?.vinculo_tipo_id ||
			getFirstCardFromLinha(linha)?.vinculo_tipo_id ||
			getFirstCardFromLinha(linha)?.vinculo_tipo?.id ||
			linha?.vinculo_tipo_id ||
			0,
	) || null;

const getCargaHorariaSemanalFromLinha = (
	linha = {},
	{
		regime = '',
		hora_inicio = '',
		hora_fim = '',
		dias_semana = [],
		ocorrencias = [],
	} = {},
) => {
	const cargaExistente = Number(
		linha?.profissional?.carga_horaria_semanal ||
			linha?.vinculo_profissional?.carga_horaria_semanal ||
			getFirstCardFromLinha(linha)?.carga_horaria_semanal ||
			0,
	);
	if (Number.isFinite(cargaExistente) && cargaExistente > 0) {
		return cargaExistente;
	}

	const cargaPorOcorrencias = getWeeklyHoursFromOccurrences(ocorrencias);
	if (cargaPorOcorrencias) {
		return cargaPorOcorrencias;
	}

	const duracaoTurno = calculateShiftDurationHours(hora_inicio, hora_fim);
	if (!duracaoTurno) return null;

	if (Array.isArray(dias_semana) && dias_semana.length > 0) {
		const quantidadeDias =
			Array.isArray(dias_semana) && dias_semana.length > 0
				? dias_semana.length
				: 1;
		return Math.round(duracaoTurno * quantidadeDias);
	}

	if (normalizeCode(regime) === 'ROTINA') {
		return Math.round(duracaoTurno * 5);
	}

	return null;
};

const buildCadastroProfissionalSnapshot = ({
	numeroRegistro = '',
	ufConselho = '',
	telefone = '',
	vinculo = '',
	especialidade = '',
	cargo = '',
	cargaHorariaSemanal = '',
	ativo = true,
}) => ({
	numeroRegistro: normalizeDigits(numeroRegistro, 20),
	ufConselho: String(ufConselho || '')
		.trim()
		.toUpperCase(),
	telefone: normalizeDigits(telefone, 11),
	vinculo: String(Number(vinculo || 0) || ''),
	especialidade: String(Number(especialidade || 0) || ''),
	cargo: String(cargo || '').trim(),
	cargaHorariaSemanal: String(Number(cargaHorariaSemanal || 0) || ''),
	ativo: Boolean(ativo),
});

const buildLinhaCadastroSnapshot = ({
	vinculoTipoId = '',
	dataAdmissao = '',
	cargo = '',
	dimensionValue = '',
}) => ({
	vinculoTipoId: String(Number(vinculoTipoId || 0) || ''),
	dataAdmissao: formatDateInputToISO(dataAdmissao),
	cargo: String(cargo || '').trim(),
	dimensionValue: String(Number(dimensionValue || 0) || ''),
});

const hasSnapshotChanges = (currentSnapshot = {}, nextSnapshot = {}) =>
	Object.keys(nextSnapshot).some(
		(key) =>
			String(currentSnapshot?.[key] || '') !==
			String(nextSnapshot?.[key] || ''),
	);

const serializeDiasSemanaHorariosSnapshot = (value = {}) =>
	Object.entries(normalizeDiasSemanaHorariosMap(value))
		.sort(([a], [b]) => Number(a) - Number(b))
		.map(
			([diaSemana, horario = {}]) =>
				`${diaSemana}:${normalizeHourInputValue(horario?.hora_inicio)}-${normalizeHourInputValue(horario?.hora_fim)}`,
		)
		.join('|');

const serializeDatasExtrasSnapshot = (items = []) =>
	(Array.isArray(items) ? items : [])
		.map((item = {}) => {
			const data = formatDateInputToISO(
				item?.selected_date || item?.data || '',
			);
			const horaInicio = normalizeHourInputValue(
				item?.hora_inicio || item?.horaInicio || '',
			);
			const horaFim = normalizeHourInputValue(
				item?.hora_fim || item?.horaFim || '',
			);
			if (!data && !horaInicio && !horaFim) return '';
			return `${data}:${horaInicio}-${horaFim}`;
		})
		.filter(Boolean)
		.sort()
		.join('|');

const buildNovoProfissionalEditSnapshot = (form = {}) => ({
	nome: formatProfessionalNameInput(form?.nome, { trimEnd: true }),
	admissao: formatDateInputToISO(form?.admissao),
	vinculo_tipo_id: String(Number(form?.vinculo_tipo_id || 0) || ''),
	cargo: String(form?.cargo || '').trim(),
	numero_registro: normalizeDigits(form?.numero_registro || '', 20),
	uf_conselho: String(form?.uf_conselho || '')
		.trim()
		.toUpperCase(),
	especialidade_id: String(Number(form?.especialidade_id || 0) || ''),
	telefone: normalizeDigits(form?.telefone || '', 11),
	conselho: String(form?.conselho || '')
		.trim()
		.toUpperCase(),
	vigencia_inicio: formatDateInputToISO(form?.vigencia_inicio),
	data_ancora: formatDateInputToISO(form?.data_ancora),
	regime: normalizeCode(form?.regime || ''),
	nome_recorrencia: normalizeCode(form?.nome_recorrencia || ''),
	tipo_recorrencia: normalizeCode(form?.tipo_recorrencia || ''),
	preset_ciclo: getCanonicalPresetCiclo(form?.preset_ciclo),
	hora_inicio: String(form?.hora_inicio || '').slice(0, 5),
	hora_fim: String(form?.hora_fim || '').slice(0, 5),
	trabalho_horas: String(Number(form?.trabalho_horas || 0) || ''),
	descanso_horas: String(Number(form?.descanso_horas || 0) || ''),
	dias_semana: normalizeDiasSemanaValues(form?.dias_semana).join(','),
	dias_semana_horarios: serializeDiasSemanaHorariosSnapshot(
		form?.dias_semana_horarios,
	),
	semanas_rotacao: (Array.isArray(form?.semanas_rotacao)
		? form.semanas_rotacao
		: []
	)
		.map((diasSemana) => normalizeDiasSemanaValues(diasSemana).join(','))
		.join('|'),
	datas_extras: serializeDatasExtrasSnapshot(form?.datas_extras),
});

const buildVacanciaEditSnapshot = (form = {}) => ({
	data_inicio_vacancia: formatDateInputToISO(form?.data_inicio_vacancia),
	especialidade_id: String(Number(form?.especialidade_id || 0) || ''),
	vinculo_tipo_id: String(Number(form?.vinculo_tipo_id || 0) || ''),
	observacao: String(form?.observacao || '').trim(),
	regime: normalizeCode(form?.regime || ''),
	nome_recorrencia: normalizeCode(form?.nome_recorrencia || ''),
	tipo_recorrencia: normalizeCode(form?.tipo_recorrencia || ''),
	preset_ciclo: getCanonicalPresetCiclo(form?.preset_ciclo),
	hora_inicio: String(form?.hora_inicio || '').slice(0, 5),
	hora_fim: String(form?.hora_fim || '').slice(0, 5),
	data_ancora: formatDateInputToISO(form?.data_ancora),
	trabalho_horas: String(Number(form?.trabalho_horas || 0) || ''),
	descanso_horas: String(Number(form?.descanso_horas || 0) || ''),
	dias_semana: normalizeDiasSemanaValues(form?.dias_semana).join(','),
	dias_semana_horarios: serializeDiasSemanaHorariosSnapshot(
		form?.dias_semana_horarios,
	),
	datas_extras: serializeDatasExtrasSnapshot(form?.datas_extras),
});

const getDefaultNovoProfissionalForm = () => ({
	setor_id: '',
	cpf: '',
	nome: '',
	admissao: '',
	nome_vinculo: '',
	vinculo_tipo_id: '',
	cargo: DEFAULT_MEDICO_CARGO,
	numero_registro: '',
	uf_conselho: 'RJ',
	conselho: 'CRM',
	especialidade_id: '',
	telefone: '',
	regime: '',
	nome_recorrencia: '',
	tipo_recorrencia: '',
	preset_ciclo: '',
	trabalho_horas: '',
	descanso_horas: '',
	dias_semana: [],
	dias_semana_horarios: {},
	semanas_rotacao: [[], []],
	datas_extras: [],
	hora_inicio: '',
	hora_fim: '',
	turno_personalizado: false,
	vigencia_inicio: '',
	data_ancora: '',
});

// A consulta por CPF devolve somente o cadastro institucional. Para reaproveitar
// a escala de quem já está alocado, a origem é identificada na leitura detalhada
// do ambiente: primeiro a origem estrutural de uma jornada repartida e, nos
// demais casos, a alocação mais antiga conhecida do profissional.
const getEscalaOrigemPreenchimentoPorCpf = ({
	linhas = [],
	cpf = '',
	ambiente = {},
	mesReferencia = '',
	modalidade = '',
} = {}) => {
	const cpfNormalizado = normalizeCpf(cpf);
	if (!cpfNormalizado) return null;

	const linhasDetalhadas = getLinhasParticionamento(linhas);
	const ambienteDaConsulta = {
		...ambiente,
		linhas: linhasDetalhadas,
	};
	const candidatas = linhasDetalhadas
		.filter(
			(linha = {}) =>
				!isVacanciaLinha(linha) &&
				linhaHasCardRenderizavel({ linha, modalidade }) &&
				isLinhaProfissionalGerenciavelNoAmbiente({
					ambiente: ambienteDaConsulta,
					linha,
					mesReferencia,
				}) &&
				normalizeCpf(getCpfFromLinha(linha)) === cpfNormalizado &&
				Boolean(linha?.regime_escala?.regime),
		)
		.sort((linhaA = {}, linhaB = {}) => {
			const prioridadeOrigem =
				Number(isLinhaOrigemDeSplit(linhaB)) -
				Number(isLinhaOrigemDeSplit(linhaA));
			if (prioridadeOrigem) return prioridadeOrigem;

			const inicioA =
				getDataInicialProfissionalEscalaIsoFromLinha(linhaA) ||
				formatDateInputToISO(linhaA?.regime_escala?.data_ancora) ||
				'9999-12-31';
			const inicioB =
				getDataInicialProfissionalEscalaIsoFromLinha(linhaB) ||
				formatDateInputToISO(linhaB?.regime_escala?.data_ancora) ||
				'9999-12-31';
			if (inicioA !== inicioB) return inicioA.localeCompare(inicioB);

			return String(getLinhaSetorId(linhaA) || '').localeCompare(
				String(getLinhaSetorId(linhaB) || ''),
			);
		});

	const linhaOrigem = candidatas[0];
	if (!linhaOrigem) return null;

	const regimeEscala = linhaOrigem.regime_escala || {};
	const padroes = normalizePadroesAlocacao(regimeEscala.padroes_alocacao);
	const padraoBase =
		padroes.find(
			(item = {}) =>
				normalizeCode(item?.tipo_recorrencia) !== 'DATAS_ESPECIFICAS',
		) || padroes[0] || {};
	const tipoRecorrencia =
		normalizeCode(
			padraoBase?.tipo_recorrencia || regimeEscala.tipo_recorrencia,
		) || getTipoRecorrenciaFallbackByRegime(regimeEscala.regime);
	const presetCiclo =
		getCanonicalPresetCiclo(padraoBase?.preset_ciclo) ||
		inferPresetCicloFromRegimeEscala(regimeEscala);

	return {
		regime: String(regimeEscala.regime || '').trim(),
		nome_recorrencia: getRecorrenciaNomeValue({
			tipo_recorrencia: tipoRecorrencia,
			preset_ciclo: presetCiclo,
			padroes_alocacao: padroes,
		}),
		tipo_recorrencia: tipoRecorrencia,
		preset_ciclo: presetCiclo,
		setor_origem_id: String(getLinhaSetorId(linhaOrigem) || ''),
	};
};

const withWeeklyRecurrence = (form = {}) => ({
	...form,
	nome_recorrencia: 'SEMANAL',
	tipo_recorrencia: 'SEMANAL',
	preset_ciclo: 'SEMANAL',
});

const getDefaultVacanciaForm = () => ({
	setor_id: '',
	data_inicio_vacancia: '',
	especialidade_id: '',
	nome_vinculo: '',
	vinculo_tipo_id: '',
	observacao: '',
	regime: '',
	nome_recorrencia: '',
	tipo_recorrencia: '',
	preset_ciclo: '',
	trabalho_horas: '',
	descanso_horas: '',
	hora_inicio: '',
	hora_fim: '',
	turno_personalizado: false,
	data_ancora: '',
	dias_semana: [],
	dias_semana_horarios: {},
	datas_extras: [],
});

const getDefaultNovoProfissionalLockedFields = () => ({
	cargo: true,
});

const GerenciarProfissionais = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const escalaViewModeOrigemSolicitada =
		getEscalaViewModeFromLocation(location);
	const [token, setToken] = useState('');
	const [escalaViewMode, setEscalaViewMode] = useState('medica');
	const [showUnidadeFilter, setShowUnidadeFilter] = useState(false);
	const [userCnes, setUserCnes] = useState('');
	const scope = escalaViewMode === 'enfermagem' ? 'enfermagem' : 'medica';
	const [contexto, setContexto] = useState(getDefaultContexto);
	const [contextoAplicado, setContextoAplicado] = useState(null);
	const [unidadeOptions, setUnidadeOptions] = useState([]);
	const [setorOptions, setSetorOptions] = useState([]);
	const [unidadeSearchTerm, setUnidadeSearchTerm] = useState('');
	const [showUnidadeSuggestions, setShowUnidadeSuggestions] = useState(false);
	const [setorSearchTerm, setSetorSearchTerm] = useState('');
	const [showSetorSuggestions, setShowSetorSuggestions] = useState(false);
	const [trocaProfissionalSearchTerm, setTrocaProfissionalSearchTerm] =
		useState('');
	const [
		showTrocaProfissionalSuggestions,
		setShowTrocaProfissionalSuggestions,
	] = useState(false);
	const trocaProfissionalInputRef = useRef(null);
	const [
		trocaProfissionalSuggestionsPosition,
		setTrocaProfissionalSuggestionsPosition,
	] = useState(null);
	const [ambienteOpcoes, setAmbienteOpcoes] = useState(null);
	const [vinculosEscala, setVinculosEscala] = useState([]);
	const [ambiente, setAmbiente] = useState(null);
	const [loadingInicial, setLoadingInicial] = useState(true);
	const [hasManagementAccess, setHasManagementAccess] = useState(false);
	const [loadingAmbiente, setLoadingAmbiente] = useState(false);
	const [gradeProntaParaExibir, setGradeProntaParaExibir] = useState(false);
	const [submitError, setSubmitError] = useState('');
	const [submitErrorScope, setSubmitErrorScope] = useState('');
	const [novoProfissionalApplyError, setNovoProfissionalApplyError] =
		useState('');
	const [isAbrindoGerenciamentoJornada, setIsAbrindoGerenciamentoJornada] =
		useState(false);
	const [
		profissionalHorarioSobrepostoPopup,
		setProfissionalHorarioSobrepostoPopup,
	] = useState(null);
	const [vacanciaApplyError, setVacanciaApplyError] = useState('');
	const [, setFeedbackMessage] = useState('');
	const [contextoErrors, setContextoErrors] = useState({});
	const [novoProfissionalErrors, setNovoProfissionalErrors] = useState({});
	const [
		novoProfissionalErrorDisplayMode,
		setNovoProfissionalErrorDisplayMode,
	] = useState('');
	const [vacanciaErrors, setVacanciaErrors] = useState({});
	const [panelErrors, setPanelErrors] = useState({});

	const [searchTerm, setSearchTerm] = useState('');
	const [isMobileProfessionalColumnOpen, setIsMobileProfessionalColumnOpen] =
		useState(false);
	const [selectedDiaIso, setSelectedDiaIso] = useState('');
	const [selectedLinhaId, setSelectedLinhaId] = useState('');

	const [isNovoProfissionalModalOpen, setIsNovoProfissionalModalOpen] =
		useState(false);
	const [isEditingProfissional, setIsEditingProfissional] = useState(false);
	const [editingProfissionalLinha, setEditingProfissionalLinha] =
		useState(null);
	const [isEditingVacancia, setIsEditingVacancia] = useState(false);
	const [isViewingVacancia, setIsViewingVacancia] = useState(false);
	const [editingVacanciaLinha, setEditingVacanciaLinha] = useState(null);
	const [isVacanciaModalOpen, setIsVacanciaModalOpen] = useState(false);
	const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
		useState(false);
	const [deleteTargetLinha, setDeleteTargetLinha] = useState(null);
	const [deletePreview, setDeletePreview] = useState(null);
	const [linhasOcultadasAposExclusao, setLinhasOcultadasAposExclusao] =
		useState([]);
	const [
		isDeleteFromGerenciamentoTurnos,
		setIsDeleteFromGerenciamentoTurnos,
	] = useState(false);
	const [isGerenciamentoTurnosModalOpen, setIsGerenciamentoTurnosModalOpen] =
		useState(false);
	const [isGerenciamentoTurnosLoading, setIsGerenciamentoTurnosLoading] =
		useState(false);
	const [isGerenciamentoTurnosReady, setIsGerenciamentoTurnosReady] =
		useState(false);
	const [gerenciamentoTurnosLoadError, setGerenciamentoTurnosLoadError] =
		useState('');
	const [gerenciamentoTurnosLinha, setGerenciamentoTurnosLinha] =
		useState(null);
	const [
		gerenciamentoTurnosProfissionalCadastro,
		setGerenciamentoTurnosProfissionalCadastro,
	] = useState(null);
	const [gerenciamentoTurnosNovoDias, setGerenciamentoTurnosNovoDias] =
		useState([]);
	const [
		gerenciamentoTurnosNovoHorarios,
		setGerenciamentoTurnosNovoHorarios,
	] = useState({});
	const [
		gerenciamentoTurnosSetoresEditaveis,
		setGerenciamentoTurnosSetoresEditaveis,
	] = useState([]);
	const [
		gerenciamentoTurnosSetoresRemovidos,
		setGerenciamentoTurnosSetoresRemovidos,
	] = useState([]);
	const [
		gerenciamentoTurnosSetoresComParticoesLiberadas,
		setGerenciamentoTurnosSetoresComParticoesLiberadas,
	] = useState([]);
	const [
		gerenciamentoTurnosSetoresAbertos,
		setGerenciamentoTurnosSetoresAbertos,
	] = useState({});
	const [
		isGerenciamentoTurnosNovoSetorAberto,
		setIsGerenciamentoTurnosNovoSetorAberto,
	] = useState(false);
	const [
		planejamentoConfirmacaoDisponivel,
		setPlanejamentoConfirmacaoDisponivel,
	] = useState(false);
	const [
		planejamentoConfirmacaoSolicitacao,
		setPlanejamentoConfirmacaoSolicitacao,
	] = useState(0);
	const [planejamentoNovoSetorForm, setPlanejamentoNovoSetorForm] =
		useState(null);
	const [usarSugestoesNovoSetor, setUsarSugestoesNovoSetor] = useState(false);
	const [sugestaoJornadaDisponivel, setSugestaoJornadaDisponivel] =
		useState(false);
	const [gerenciamentoTurnosNovoDatas, setGerenciamentoTurnosNovoDatas] =
		useState([]);
	const [gerenciamentoTurnosNovoSetor, setGerenciamentoTurnosNovoSetor] =
		useState('');
	const [gerenciamentoTurnosNovoRegime, setGerenciamentoTurnosNovoRegime] =
		useState('');
	const [
		gerenciamentoTurnosNovoRecorrencia,
		setGerenciamentoTurnosNovoRecorrencia,
	] = useState('SEMANAL');
	const [
		gerenciamentoTurnosNovoHoraInicio,
		setGerenciamentoTurnosNovoHoraInicio,
	] = useState('07:00');
	const [gerenciamentoTurnosNovoHoraFim, setGerenciamentoTurnosNovoHoraFim] =
		useState('19:00');
	const [
		gerenciamentoTurnosNovoDataInicial,
		setGerenciamentoTurnosNovoDataInicial,
	] = useState('');
	const [
		gerenciamentoTurnosNovoPeriodoRotacao,
		setGerenciamentoTurnosNovoPeriodoRotacao,
	] = useState(2);
	const [
		gerenciamentoTurnosNovoSemanasRotacao,
		setGerenciamentoTurnosNovoSemanasRotacao,
	] = useState([[], []]);
	const [gerenciamentoTurnosErrors, setGerenciamentoTurnosErrors] = useState(
		{},
	);
	const [particionamentoPendente, setParticionamentoPendente] =
		useState(null);
	// Uma prévia bloqueada por origem ativa não é erro genérico. Ela preserva o
	// rascunho até o usuário decidir explicitamente entre particionar a jornada
	// existente ou criar outra carga independente.
	const [origemAtivaPendente, setOrigemAtivaPendente] = useState(null);
	const [
		isSubmittingGerenciamentoTurnos,
		setIsSubmittingGerenciamentoTurnos,
	] = useState(false);
	const [deleteReason, setDeleteReason] = useState('');
	const [deleteReasonOther, setDeleteReasonOther] = useState('');
	const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
	const [editingProfissionalIndex, setEditingProfissionalIndex] =
		useState(null);
	const [openMiniTabs, setOpenMiniTabs] = useState(getDefaultMiniTabs);
	const [novoProfissionalLockedFields, setNovoProfissionalLockedFields] =
		useState({});
	const [novoProfissionalTemEscalaOrigem, setNovoProfissionalTemEscalaOrigem] =
		useState(false);
	const escalaReloadScopeRef = useRef(null);
	const autoLoadedContextKeyRef = useRef('');
	const gerenciamentoTurnosLoadRequestRef = useRef(0);
	const novoProfissionalConsultadoRef = useRef(null);
	// Os inputs de horário podem disparar o clique em Salvar antes do próximo
	// render em cenários de digitação rápida. O rascunho mantém o último valor
	// aceito pelo onChange para que o payload nunca volte ao valor anterior.
	const gerenciamentoTurnosSetoresEditaveisRef = useRef([]);
	// Durante a aplicação, a API pode atualizar a projeção mensal antes de a
	// consulta estrutural devolver a nova versão. Não reidrate os inputs com
	// esse estado transitório, pois ele faz o dia/horário recém-editado piscar
	// de volta ao valor anterior sob a caixa de confirmação.
	const gerenciamentoTurnosRascunhoProtegidoRef = useRef(false);
	const gerenciamentoTurnosCicloInputsRef = useRef({});
	const initialNovoProfissionalEditSnapshotRef = useRef(null);
	// Quando o cadastro institucional é aceito e a prévia da jornada falha,
	// mantemos esta identidade para retomar somente a alocação. Reenviar o
	// formulário não deve criar um segundo vínculo para a mesma pessoa.
	const cadastroVinculoPendenteRef = useRef(null);
	const alocacaoIntentIdempotencyKeysRef = useRef(new Map());
	const initialVacanciaEditSnapshotRef = useRef(null);
	const admissionDatePickerRef = useRef(null);
	const vigenciaInicioDatePickerRef = useRef(null);
	const vacanciaDatePickerRef = useRef(null);
	const trocaIdempotencyKeysRef = useRef(new Map());
	const getChaveIdempotenciaAlocacao = (assinatura = '') => {
		const chave = String(assinatura || 'alocacao').trim();
		if (!alocacaoIntentIdempotencyKeysRef.current.has(chave)) {
			alocacaoIntentIdempotencyKeysRef.current.set(
				chave,
				`alocacao-${createTrocaEquivalenteIdempotencyKey()}`,
			);
		}
		return alocacaoIntentIdempotencyKeysRef.current.get(chave);
	};

	const handlePlanejamentoConfirmacaoDisponibilidade = useCallback(
		(disponivel) => {
			const next = Boolean(disponivel);
			setPlanejamentoConfirmacaoDisponivel((current) =>
				current === next ? current : next,
			);
		},
		[],
	);
	const handlePlanejamentoNovoSetorFormChange = useCallback((form = {}) => {
		setPlanejamentoNovoSetorForm(form);
		if (form?.setorId !== undefined) {
			setGerenciamentoTurnosNovoSetor(String(form.setorId || ''));
		}
	}, []);

	const [isSubmittingAdicionar, setIsSubmittingAdicionar] = useState(false);
	const [isSubmittingVacancia, setIsSubmittingVacancia] = useState(false);
	const [isSubmittingPanel, setIsSubmittingPanel] = useState(false);
	const [trocaConsulta, setTrocaConsulta] = useState(null);
	const [trocaConsultaErro, setTrocaConsultaErro] = useState(null);
	const [isLoadingTrocaConsulta, setIsLoadingTrocaConsulta] = useState(false);
	const [trocaAvaliacao, setTrocaAvaliacao] = useState(null);
	const [trocaComplementos, setTrocaComplementos] = useState(null);
	const [trocaConsultaComplementar, setTrocaConsultaComplementar] =
		useState(null);
	const [trocaItensComplementares, setTrocaItensComplementares] = useState(
		[],
	);
	const [isLoadingTrocaAvaliacao, setIsLoadingTrocaAvaliacao] =
		useState(false);

	const [novoProfissionalForm, setNovoProfissionalForm] = useState(
		getDefaultNovoProfissionalForm,
	);

	const [vacanciaForm, setVacanciaForm] = useState(getDefaultVacanciaForm);

	const [panelForm, setPanelForm] = useState({
		acao: '',
		regime: '',
		regime_vinculo_id: null,
		hora_inicio: '',
		hora_fim: '',
		tipo_item: 'FERIAS',
		periodo_inicio: '',
		periodo_fim: '',
		observacao: '',
		item_origem_id: '',
		item_destino_id: '',
		troca_profissional_id: '',
		troca_data: '',
		troca_turno_id: '',
		troca_perfil: 'COMPENSAR',
		troca_recorte_item_id: '',
		troca_recorte_inicio: '',
		troca_recorte_fim: '',
		delete_mode: 'dia',
		primeiro_dia_trabalho_mes: '',
	});

	const modalidade = useMemo(
		() => (scope === 'enfermagem' ? 'ENFERMAGEM' : 'MEDICA'),
		[scope],
	);

	const diaCalendario = useMemo(
		() => (Array.isArray(ambiente?.calendario) ? ambiente.calendario : []),
		[ambiente],
	);

	const diasGridStyle = useMemo(
		() => ({
			gridTemplateColumns: `repeat(${diaCalendario.length || 1}, minmax(${CALENDAR_DAY_MIN_WIDTH}px, 1fr))`,
		}),
		[diaCalendario.length],
	);

	const calendarTableMinWidthStyle = useMemo(
		() => ({
			minWidth: `${Math.max(
				760,
				PROF_COL_WIDTH +
					(diaCalendario.length || 1) * CALENDAR_DAY_MIN_WIDTH +
					14 +
					(isEditPanelOpen ? EDIT_PANEL_WIDTH : 0),
			)}px`,
		}),
		[diaCalendario.length, isEditPanelOpen],
	);

	const professionalColumnWidthStyle = useMemo(
		() => ({
			'--professional-column-width': `${PROF_COL_WIDTH}px`,
			'--mobile-professional-column-width': isMobileProfessionalColumnOpen
				? `${PROF_COL_WIDTH}px`
				: '52px',
		}),
		[isMobileProfessionalColumnOpen],
	);

	const professionalColumnWidthClass =
		'w-[var(--professional-column-width)] min-w-[var(--professional-column-width)] max-w-[var(--professional-column-width)] max-sm:w-[var(--mobile-professional-column-width)] max-sm:min-w-[var(--mobile-professional-column-width)] max-sm:max-w-[var(--mobile-professional-column-width)] transition-[width,min-width,max-width] duration-200';

	const isSetorTodosAplicado = isSetorTodosValue(contextoAplicado?.setor);
	const isEdicaoProfissionalConsolidadaTodos = Boolean(
		isSetorTodosAplicado &&
		isEditingProfissional &&
		editingProfissionalLinha?.is_consolidada_todos,
	);

	const linhas = useMemo(() => {
		const linhasOcultadas = new Set(linhasOcultadasAposExclusao);
		const removerLinhasOcultadas = (linha = {}) => {
			const setoresLinhas = Array.isArray(linha?.setores_linhas)
				? linha.setores_linhas
				: [];
			if (setoresLinhas.length > 0) {
				const setoresVisiveis = setoresLinhas
					.map(removerLinhasOcultadas)
					.filter(Boolean);
				return setoresVisiveis.length > 0
					? { ...linha, setores_linhas: setoresVisiveis }
					: null;
			}
			// Uma linha de profissional sem regime_vinculo_id não é uma alocação
			// operacional. Ela pode permanecer em respostas legadas após um
			// encerramento, mas uma troca entre setores é devolvida justamente como
			// linha MANUAL sem regime: nesse caso o card TROCA é autoritativo e deve
			// continuar visível na grade.
			const profissionalSemRegimeVinculo =
				!isVacanciaLinha(linha) && linha?.regime_vinculo_id == null;
			if (
				profissionalSemRegimeVinculo &&
				!linhaTemCardTrocaPersistido(linha)
			)
				return null;
			if (
				isLinhaEncerradaNoContextoOperacional({
					linha,
					mesReferencia: contextoAplicado?.mes_referencia,
				})
			) {
				return null;
			}
			const estaOculta =
				linhasOcultadas.has(getLinhaExclusaoLocalKey(linha)) ||
				linhasOcultadas.has(getLinhaSetorOcultacaoLocalKey(linha)) ||
				linhasOcultadas.has(
					getLinhaProfissionalOcultacaoLocalKey(linha),
				);
			return estaOculta ? null : linha;
		};
		const linhasVisiveis = (
			Array.isArray(ambiente?.linhas) ? ambiente.linhas : []
		)
			.map(removerLinhasOcultadas)
			.filter(Boolean);
		const ambienteComLinhasVisiveis = {
			...ambiente,
			linhas: linhasVisiveis,
		};
		const linhasBase = linhasVisiveis
			.filter((linha) => linhaHasCardRenderizavel({ linha, modalidade }))
			.filter((linha) =>
				linhaTemCardTrocaPersistido(linha) ||
				isLinhaProfissionalGerenciavelNoAmbiente({
					ambiente: ambienteComLinhasVisiveis,
					linha,
					mesReferencia: contextoAplicado?.mes_referencia,
				}),
			);

		// O backend pode devolver várias linhas operacionais (regimes e cards
		// manuais) para o mesmo profissional no mesmo setor. A grade, porém,
		// representa o profissional; por isso a consolidação não pode depender
		// do filtro "Todos os setores".
		return consolidateLinhasPorProfissional(linhasBase);
	}, [
		ambiente,
		contextoAplicado?.mes_referencia,
		linhasOcultadasAposExclusao,
		modalidade,
	]);

	const linhasFiltradas = useMemo(() => {
		const termo = String(searchTerm || '')
			.toLowerCase()
			.trim();
		if (!termo) return linhas;
		return linhas.filter((linha) => {
			const nome = getLinhaDisplayName(linha).toLowerCase();
			const dim = getDimensionLabel(linha, modalidade).toLowerCase();
			return nome.includes(termo) || dim.includes(termo);
		});
	}, [linhas, searchTerm, modalidade]);

	const linhasComTrocasRenderizadas = useMemo(
		() => applyTrocaVisualRendering(linhasFiltradas),
		[linhasFiltradas],
	);

	const linhasRender = useMemo(() => {
		if (linhasComTrocasRenderizadas.length >= LINHAS_VAZIAS_MINIMO) {
			return linhasComTrocasRenderizadas;
		}

		const linhasVazias = Array.from({
			length: LINHAS_VAZIAS_MINIMO - linhasComTrocasRenderizadas.length,
		}).map((_, index) => ({
			linha_id: `placeholder-${index}`,
			placeholder: true,
			celulas: [],
		}));

		return [...linhasComTrocasRenderizadas, ...linhasVazias];
	}, [linhasComTrocasRenderizadas]);

	// Mantém o esqueleto visível até a grade estar montada e, no frame seguinte,
	// revela as linhas. Isso evita a troca visual brusca entre o loader e uma
	// tabela extensa, sem adiar a renderização dos dados recebidos.
	useEffect(() => {
		if (loadingInicial || loadingAmbiente || !ambiente) {
			setGradeProntaParaExibir(false);
			return undefined;
		}

		const frameId = window.requestAnimationFrame(() => {
			setGradeProntaParaExibir(true);
		});
		return () => window.cancelAnimationFrame(frameId);
	}, [ambiente, loadingInicial, loadingAmbiente]);

	const linhaSelecionada = useMemo(
		() =>
			linhas.find(
				(linha) => String(linha?.linha_id) === String(selectedLinhaId),
			) || null,
		[selectedLinhaId, linhas],
	);

	const selectedCell = useMemo(() => {
		if (!linhaSelecionada || !selectedDiaIso) return null;
		return (
			(linhaSelecionada?.celulas || []).find(
				(celula) => String(celula?.data) === String(selectedDiaIso),
			) || null
		);
	}, [linhaSelecionada, selectedDiaIso]);

	const monthLabel = useMemo(
		() =>
			getMonthLabelPtBr(
				contextoAplicado?.mes_referencia || contexto.mes_referencia,
			),
		[contextoAplicado?.mes_referencia, contexto.mes_referencia],
	);

	const setorLabel = useMemo(() => {
		if (!contextoAplicado?.setor) return 'Setor';
		if (isSetorTodosValue(contextoAplicado?.setor))
			return 'Todos os setores';
		const setor = setorOptions.find(
			(item) => String(item.id) === String(contextoAplicado.setor),
		);
		return (
			setor?.nome_exibicao ||
			setor?.nome ||
			setor?.identificador ||
			'Setor'
		);
	}, [contextoAplicado?.setor, setorOptions]);

	const currentEditName = useMemo(() => {
		if (!linhaSelecionada) return 'Selecione na grade';
		return getFirstTwoNames(getLinhaDisplayName(linhaSelecionada));
	}, [linhaSelecionada]);
	const gerenciamentoTurnosSetores = useMemo(() => {
		const setores = buildTurnosSetores({
			linha: gerenciamentoTurnosLinha,
			setorOptions,
			contextoAplicado,
			regimesOptions: Array.isArray(ambienteOpcoes?.regimes)
				? ambienteOpcoes.regimes
				: [],
		});
		const removidos = new Set(
			gerenciamentoTurnosSetoresRemovidos.map((id) => String(id)),
		);
		const setoresComParticoesLiberadas = new Set(
			gerenciamentoTurnosSetoresComParticoesLiberadas.map((id) =>
				String(id),
			),
		);
		return setores
			.filter((item) => !removidos.has(String(item?.setorId || '')))
			.map((item) =>
				setoresComParticoesLiberadas.has(String(item?.setorId || ''))
					? { ...item, horariosParticionadosByDia: {} }
					: item,
			);
	}, [
		gerenciamentoTurnosLinha,
		setorOptions,
		contextoAplicado,
		ambienteOpcoes,
		gerenciamentoTurnosSetoresRemovidos,
		gerenciamentoTurnosSetoresComParticoesLiberadas,
	]);
	const gerenciamentoTurnosSetoresDisponiveis = useMemo(() => {
		const setoresOcupados =
			gerenciamentoTurnosSetoresEditaveis.length > 0
				? gerenciamentoTurnosSetoresEditaveis
				: gerenciamentoTurnosSetores;

		return setorOptions.filter(
			(setor) =>
				!setoresOcupados.some(
					(item) => String(item.setorId) === String(setor.id),
				),
		);
	}, [
		gerenciamentoTurnosSetores,
		gerenciamentoTurnosSetoresEditaveis,
		setorOptions,
	]);

	useEffect(() => {
		if (!isGerenciamentoTurnosModalOpen || !isGerenciamentoTurnosReady)
			return;
		if (
			gerenciamentoTurnosRascunhoProtegidoRef.current &&
			gerenciamentoTurnosSetoresEditaveisRef.current.length > 0
		) {
			return;
		}
		const editableItems = buildGerenciamentoTurnosEditableItems(
			gerenciamentoTurnosSetores,
		);
		setGerenciamentoTurnosSetoresEditaveis(editableItems);
		setGerenciamentoTurnosSetoresAbertos((prev) =>
			editableItems.reduce((acc, item) => {
				const shouldOpenByDefault = editableItems.length === 1;
				acc[item.id] = Object.prototype.hasOwnProperty.call(
					prev,
					item.id,
				)
					? prev[item.id]
					: shouldOpenByDefault;
				return acc;
			}, {}),
		);
	}, [
		isGerenciamentoTurnosModalOpen,
		isGerenciamentoTurnosReady,
		gerenciamentoTurnosSetores,
	]);

	useEffect(() => {
		gerenciamentoTurnosSetoresEditaveisRef.current =
			gerenciamentoTurnosSetoresEditaveis;
	}, [gerenciamentoTurnosSetoresEditaveis]);

	const hasContextoAplicado = Boolean(
		contextoAplicado?.estabelecimento && contextoAplicado?.setor,
	);
	const unidadeUnicaSemSetores =
		!loadingInicial &&
		!showUnidadeFilter &&
		Boolean(String(contexto.estabelecimento || userCnes || '').trim()) &&
		setorOptions.length === 0;
	const contextoAreaMessage =
		submitErrorScope === 'contexto' && submitError
			? submitError
			: unidadeUnicaSemSetores
				? 'Não existem setores cadastrados para esta unidade e, portanto, também não existem escalas criadas.'
				: '';

	const vinculoOpcoes = useMemo(() => {
		const source =
			Array.isArray(vinculosEscala) && vinculosEscala.length > 0
				? vinculosEscala
				: Array.isArray(ambienteOpcoes?.vinculos)
					? ambienteOpcoes.vinculos
					: [];
		const dedup = new Map();

		source.forEach((item = {}) => {
			const value =
				item?.id ??
				item?.value ??
				item?.codigo ??
				item?.vinculo_tipo_id ??
				item?.tipo_id ??
				null;
			const label =
				item?.nome ??
				item?.label ??
				item?.descricao ??
				item?.titulo ??
				item?.nome_vinculo ??
				item?.descricao_vinculo ??
				item?.vinculo ??
				item?.tipo?.nome ??
				item?.codigo ??
				(value != null ? String(value) : '');

			if (value == null || !String(label || '').trim()) {
				return;
			}

			const optionValue = String(value);
			if (dedup.has(optionValue)) return;

			dedup.set(optionValue, {
				value: optionValue,
				label: String(label).trim(),
				raw: item,
			});
		});

		return Array.from(dedup.values())
			.map((item) => item)
			.sort((a, b) =>
				String(a.label || '').localeCompare(
					String(b.label || ''),
					'pt-BR',
				),
			);
	}, [vinculosEscala, ambienteOpcoes?.vinculos]);

	const shouldPreviewBeforeApply = useMemo(
		() =>
			Boolean(
				ambiente?.permissoes?.requer_preview_para_aplicar ??
				ambienteOpcoes?.limites?.exige_preview,
			),
		[
			ambiente?.permissoes?.requer_preview_para_aplicar,
			ambienteOpcoes?.limites?.exige_preview,
		],
	);

	const legendaItems = useMemo(() => {
		const legendaApi = Array.isArray(ambiente?.legenda)
			? ambiente.legenda
			: [];
		const source = legendaApi.length > 0 ? legendaApi : LEGENDA_FALLBACK;
		const dedup = new Map();

		source.forEach((item = {}) => {
			const codigoOriginal = normalizeCode(
				item?.codigo || item?.status || item?.nome || item?.label,
			);
			if (!codigoOriginal || codigoOriginal === 'BLOQUEADO') return;

			const codigo =
				codigoOriginal === 'SUGERIDO' ? 'ATRIBUIDO' : codigoOriginal;
			const label =
				codigo === 'ATRIBUIDO'
					? 'Dia atribuído'
					: String(
							item?.nome || item?.label || item?.codigo || '',
						).trim();

			if (!label || dedup.has(codigo)) return;

			dedup.set(codigo, {
				cor: getLegendaColorClass(codigo),
				label,
			});
		});

		return Array.from(dedup.values());
	}, [ambiente?.legenda]);

	const disableActions = useMemo(() => {
		const podeEditar = ambiente?.permissoes?.pode_editar;
		if (podeEditar === undefined) return false;
		return !podeEditar;
	}, [ambiente?.permissoes?.pode_editar]);

	const unidadeOptionsFiltradas = useMemo(() => {
		const termo = normalizeSearchValue(unidadeSearchTerm);
		if (!termo) return unidadeOptions;

		return unidadeOptions.filter((unidade) => {
			const nome = normalizeSearchValue(
				formatUnidadeOptionLabel(unidade),
			);
			const cnes = normalizeSearchValue(
				unidade?.id || unidade?.cnes || '',
			);
			return nome.includes(termo) || cnes.includes(termo);
		});
	}, [unidadeOptions, unidadeSearchTerm]);

	const novoProfissionalDataExtraOptions = useMemo(() => {
		const uniqueDates = new Map();

		diaCalendario.forEach((dia = {}) => {
			const data = formatDateInputToISO(dia?.data);
			if (!data || uniqueDates.has(data)) return;

			uniqueDates.set(data, {
				value: data,
				label: formatISOToDisplay(data) || data,
			});
		});

		return Array.from(uniqueDates.values()).sort((a, b) =>
			String(a.value || '').localeCompare(String(b.value || ''), 'pt-BR'),
		);
	}, [diaCalendario]);

	const getAvailableDataExtraOptions = useCallback(
		({
			diasSemana = [],
			dataInicial = '',
			datasExtras = [],
			currentItemId = '',
		} = {}) => {
			const diasRecorrentes = new Set(
				normalizeDiasSemanaValues(diasSemana),
			);
			const dataInicialIso = formatDateInputToISO(dataInicial);
			const datasExtrasUsadas = new Set(
				(Array.isArray(datasExtras) ? datasExtras : [])
					.filter((item) => item?.id !== currentItemId)
					.map((item) => formatDateInputToISO(item?.selected_date))
					.filter(Boolean),
			);

			return novoProfissionalDataExtraOptions.filter((option) => {
				const data = formatDateInputToISO(option?.value);
				if (!data || datasExtrasUsadas.has(data)) return false;

				const diaSemana = getIsoWeekdayFromValue(data);
				const pertenceRecorrencia =
					(!dataInicialIso || data >= dataInicialIso) &&
					diasRecorrentes.has(diaSemana);

				return !pertenceRecorrencia;
			});
		},
		[novoProfissionalDataExtraOptions],
	);

	const getNovoProfissionalDataExtraOptions = useCallback(
		(itemId) =>
			modalidade === 'MEDICA'
				? getAvailableDataExtraOptions({
						diasSemana: novoProfissionalForm.dias_semana,
						dataInicial: novoProfissionalForm.vigencia_inicio,
						datasExtras: novoProfissionalForm.datas_extras,
						currentItemId: itemId,
					})
				: novoProfissionalDataExtraOptions,
		[
			getAvailableDataExtraOptions,
			modalidade,
			novoProfissionalDataExtraOptions,
			novoProfissionalForm.datas_extras,
			novoProfissionalForm.dias_semana,
			novoProfissionalForm.vigencia_inicio,
		],
	);

	const getVacanciaDataExtraOptions = useCallback(
		(itemId) =>
			modalidade === 'MEDICA'
				? getAvailableDataExtraOptions({
						diasSemana: vacanciaForm.dias_semana,
						dataInicial: vacanciaForm.data_inicio_vacancia,
						datasExtras: vacanciaForm.datas_extras,
						currentItemId: itemId,
					})
				: novoProfissionalDataExtraOptions,
		[
			getAvailableDataExtraOptions,
			modalidade,
			novoProfissionalDataExtraOptions,
			vacanciaForm.data_inicio_vacancia,
			vacanciaForm.datas_extras,
			vacanciaForm.dias_semana,
		],
	);

	const unidadeInputInvalida =
		showUnidadeFilter &&
		String(unidadeSearchTerm || '').trim() !== '' &&
		!contexto.estabelecimento;

	const setorOptionsComTodos = useMemo(
		() =>
			setorOptions.length > 0
				? [SETOR_TODOS_OPTION, ...setorOptions]
				: setorOptions,
		[setorOptions],
	);

	const setorOptionsFiltrados = useMemo(() => {
		const termo = normalizeSearchValue(setorSearchTerm);
		if (!termo) return setorOptionsComTodos;

		return setorOptionsComTodos.filter((setor) => {
			const label = normalizeSearchValue(formatSetorOptionLabel(setor));
			const identificador = normalizeSearchValue(
				setor?.identificador || '',
			);
			const id = normalizeSearchValue(setor?.id || '');
			return (
				label.includes(termo) ||
				identificador.includes(termo) ||
				id.includes(termo)
			);
		});
	}, [setorOptionsComTodos, setorSearchTerm]);

	const setorInputInvalido =
		String(setorSearchTerm || '').trim() !== '' && !contexto.setor;

	const setorInputDisabled = showUnidadeFilter && !contexto.estabelecimento;

	const currentTrocaOrigemItemId = useMemo(
		() =>
			Number(
				panelForm.item_origem_id ||
					getCardItemId(getCellPrimaryCard(selectedCell)) ||
					0,
			) || null,
		[panelForm.item_origem_id, selectedCell],
	);

	const currentTrocaOrigemItem = useMemo(() => {
		const card = getCellPrimaryCard(selectedCell) || {};
		const shiftSource = getCardShiftSource(
			card,
			selectedCell,
			linhaSelecionada,
		);
		return {
			...card,
			item_id: currentTrocaOrigemItemId,
			data: selectedCell?.data || card?.data,
			hora_inicio: shiftSource?.hora_inicio || card?.hora_inicio,
			hora_fim: shiftSource?.hora_fim || card?.hora_fim,
			duracao_minutos:
				card?.duracao_minutos ||
				card?.resumo?.duracao_minutos ||
				calculateShiftDurationMinutes(
					shiftSource?.hora_inicio,
					shiftSource?.hora_fim,
				),
		};
	}, [currentTrocaOrigemItemId, linhaSelecionada, selectedCell]);

	const getTrocaConsultaItemId = useCallback((item = {}) => {
		const source = item?.item || item;
		return (
			Number(
				source?.id ||
					source?.item_id ||
					source?.dados_edicao?.id ||
					source?.dados_edicao?.item_id ||
					source?.id_item ||
					source?.dados_edicao?.id_item ||
					source?.escala_item_id ||
					source?.dados_edicao?.escala_item_id ||
					0,
			) || null
		);
	}, []);

	const getTrocaConsultaItemHorario = useCallback((item = {}) => {
		const source = item?.item || item;
		return {
			hora_inicio: String(
				source?.hora_inicio ||
					source?.dados_edicao?.hora_inicio ||
					source?.horario?.hora_inicio ||
					item?.hora_inicio ||
					'',
			).slice(0, 5),
			hora_fim: String(
				source?.hora_fim ||
					source?.dados_edicao?.hora_fim ||
					source?.horario?.hora_fim ||
					item?.hora_fim ||
					'',
			).slice(0, 5),
		};
	}, []);

	const getTrocaConsultaItemDurationMinutes = useCallback(
		(item = {}) => {
			const source = item?.item || item;
			const duracaoMinutos = Number(
				source?.duracao_minutos ||
					source?.resumo?.duracao_minutos ||
					item?.duracao_minutos ||
					0,
			);
			if (Number.isFinite(duracaoMinutos) && duracaoMinutos > 0) {
				return duracaoMinutos;
			}
			const horario = getTrocaConsultaItemHorario(item);
			return calculateShiftDurationMinutes(
				horario.hora_inicio,
				horario.hora_fim,
			);
		},
		[getTrocaConsultaItemHorario],
	);

	const getTrocaConsultaItemDate = useCallback((item = {}) => {
		const source = item?.item || item;
		return normalizeTrocaDateValue(
			source?.data || source?.dados_edicao?.data || '',
		);
	}, []);

	const getTrocaConsultaItemSetor = useCallback((item = {}) => {
		const source = item?.item || item;
		const setor =
			source?.setor || source?.dados_edicao?.setor || item?.setor || {};
		return {
			id: String(setor?.id || setor?.setor_id || source?.setor_id || ''),
			nome: formatSetorOptionLabel(setor) ||
				String(source?.setor_nome || item?.setor_nome || '').trim(),
		};
	}, []);

	const getTrocaConsultaProfessionalKey = useCallback((item = {}) => {
		const source = item?.item || item;
		const profissional = source?.profissional || item?.profissional || {};
		return String(
			profissional?.id ||
				profissional?.cpf ||
				source?.dados_edicao?.profissional ||
				source?.nome_profissional ||
				source?.resumo?.titulo ||
				'',
		).trim();
	}, []);

	const getTrocaConsultaProfessionalName = useCallback((item = {}) => {
		const source = item?.item || item;
		const profissional = source?.profissional || item?.profissional || {};
		return String(
			profissional?.nome ||
				source?.nome_profissional ||
				source?.dados_edicao?.nome ||
				source?.dados_edicao?.nome_profissional ||
				source?.resumo?.titulo ||
				'',
		).trim();
	}, []);

	const formatTrocaConsultaPlantaoOptionLabel = useCallback(
		(item = {}) => {
			const source = item?.item || item;
			const data = getTrocaConsultaItemDate(item);
			const horario = getTrocaConsultaItemHorario(item);
			const duracaoMinutos = Number(
				source?.duracao_minutos ||
					source?.resumo?.duracao_minutos ||
					item?.duracao_minutos ||
					0,
			);
			const duracaoHoras =
				Number.isFinite(duracaoMinutos) && duracaoMinutos > 0
					? Math.round((duracaoMinutos / 60) * 100) / 100
					: calculateShiftDurationHours(
							horario.hora_inicio,
							horario.hora_fim,
						);
			const cargaLabel =
				duracaoHoras > 0
					? `${String(duracaoHoras).replace('.', ',')}h`
					: 'Carga horária';

			const setor = getTrocaConsultaItemSetor(item);
			const setorOrigem = getTrocaConsultaItemSetor(
				trocaConsulta?.pacote?.itens_a?.[0] ||
					trocaConsulta?.origem ||
					currentTrocaOrigemItem,
			);
			const setorLabel = setor.nome
				? `${setorOrigem.id && setor.id && setorOrigem.id !== setor.id ? 'Outro setor' : 'Setor'}: ${setor.nome}`
				: '';
			const regimeLabel = String(
				source?.regime?.nome ||
				source?.regime?.descricao ||
				source?.regime ||
				source?.regime_escala?.regime ||
				'',
			).trim();
			const competencia = String(
				source?.mes_referencia ||
				source?.competencia ||
				source?.escala_mensal?.mes_referencia ||
				'',
			).slice(0, 7);

			return [
				formatISOToDisplay(data) || data || 'Data',
				horario.hora_inicio && horario.hora_fim
					? `${horario.hora_inicio} às ${horario.hora_fim}`
					: '',
				cargaLabel,
				setorLabel,
				regimeLabel ? `Regime: ${regimeLabel}` : '',
				competencia ? `Competência: ${competencia}` : '',
			]
				.filter(Boolean)
				.join(' • ');
		},
		[
			currentTrocaOrigemItem,
			getTrocaConsultaItemDate,
			getTrocaConsultaItemHorario,
			getTrocaConsultaItemSetor,
			trocaConsulta?.origem,
			trocaConsulta?.pacote?.itens_a,
		],
	);

	const isTrocaCandidateDisabled = useCallback((item = {}) => {
		const erros = Array.isArray(item?.erros) ? item.erros : [];
		const hasBlockingError = erros.some(
			(error) => normalizeCode(error?.codigo) !== 'SALDO_NAO_EQUIVALENTE',
		);

		return item?.selecionavel === false && hasBlockingError;
	}, []);

	const currentTrocaOrigemDate = useMemo(
		() =>
			getTrocaConsultaItemDate(trocaConsulta?.origem || {}) ||
			getTrocaConsultaItemDate(trocaConsulta?.item_origem || {}) ||
			normalizeTrocaDateValue(selectedCell?.data || '') ||
			'',
		[
			trocaConsulta?.origem,
			trocaConsulta?.item_origem,
			selectedCell?.data,
			getTrocaConsultaItemDate,
		],
	);

	const isTrocaExistenteSelecionada = useMemo(
		() => getCellTipoItem(selectedCell || {}) === 'TROCA',
		[selectedCell],
	);
	const trocaConsultaMensagemErro = useMemo(() => {
		if (modalidade !== 'ENFERMAGEM' || !trocaConsultaErro) return '';

		return (
			getTrocaPlantaoJaIniciadoMensagem(trocaConsultaErro) ||
			getErrorMessage(
				trocaConsultaErro,
				'Não foi possível carregar os candidatos para troca.',
			)
		);
	}, [modalidade, trocaConsultaErro]);
	const trocaBloqueadaPorConsulta = Boolean(trocaConsultaMensagemErro);

	const trocaExistenteResumo = useMemo(() => {
		if (!isTrocaExistenteSelecionada) return null;
		const card = getCellPrimaryCard(selectedCell || {}) || {};
		const troca =
			card?.troca ||
			card?.resumo?.troca ||
			card?.dados_edicao?.troca ||
			{};
		const origem =
			troca?.origem ||
			card?.origem ||
			card?.item_origem ||
			card?.troca_origem ||
			{};
		const destino =
			troca?.destino ||
			card?.destino ||
			card?.item_destino ||
			card?.troca_destino ||
			{};
		const getNome = (source = {}, fallback = '') =>
			String(
				source?.profissional?.nome ||
					source?.nome_profissional ||
					source?.nome ||
					source?.resumo?.titulo ||
					fallback ||
					'',
			).trim();
		const origemId =
			card?.item_origem_id ||
			troca?.item_origem_id ||
			getCardItemId(origem);
		const destinoId =
			card?.item_destino_id ||
			card?.troca_item_destino_id ||
			troca?.item_destino_id ||
			getCardItemId(destino);
		const data =
			normalizeTrocaDateValue(
				troca?.data ||
					card?.data ||
					card?.dados_edicao?.data ||
					selectedCell?.data ||
					'',
			) || '';
		const shiftSource = getCardShiftSource(
			card,
			selectedCell,
			linhaSelecionada,
		);
		const duracaoMinutos = Number(
			card?.duracao_minutos ||
				card?.resumo?.duracao_minutos ||
				troca?.duracao_minutos ||
				0,
		);
		const duracaoHoras =
			Number.isFinite(duracaoMinutos) && duracaoMinutos > 0
				? Math.round((duracaoMinutos / 60) * 100) / 100
				: calculateShiftDurationHours(
						shiftSource?.hora_inicio,
						shiftSource?.hora_fim,
					);

		return {
			origem:
				getNome(origem, origemId ? `Card ${origemId}` : '') ||
				(origemId ? `Card ${origemId}` : 'Origem não informada'),
			destino:
				getNome(destino, destinoId ? `Card ${destinoId}` : '') ||
				(destinoId ? `Card ${destinoId}` : 'Destino não informado'),
			data: formatISOToDisplay(data) || data || 'Data não informada',
			carga:
				duracaoHoras > 0
					? `${String(duracaoHoras).replace('.', ',')}h`
					: 'Carga não informada',
			observacao:
				getCellObservation(selectedCell || {}) ||
				card?.observacao ||
				troca?.observacao ||
				'',
		};
	}, [isTrocaExistenteSelecionada, linhaSelecionada, selectedCell]);

	useEffect(() => {
		let isCancelled = false;

		if (!openMiniTabs.troca || isTrocaExistenteSelecionada) {
			setTrocaConsulta(null);
			setTrocaConsultaErro(null);
			setIsLoadingTrocaConsulta(false);
			setTrocaAvaliacao(null);
			setTrocaComplementos(null);
			setTrocaConsultaComplementar(null);
			setTrocaItensComplementares([]);
			setIsLoadingTrocaAvaliacao(false);
			return undefined;
		}

		if (!token || !currentTrocaOrigemItemId) {
			setTrocaConsulta(null);
			setTrocaConsultaErro(null);
			setIsLoadingTrocaConsulta(false);
			return undefined;
		}

		const loadTrocaConsulta = async () => {
			setIsLoadingTrocaConsulta(true);
			setTrocaConsulta(null);
			setTrocaConsultaErro(null);

			const response = await requestConsultarTodasTrocasEquivalentes({
				token,
				itensA: [currentTrocaOrigemItemId],
				lado: 'B',
				scope,
			});

			if (isCancelled) return;

			setIsLoadingTrocaConsulta(false);

			if (response?.error) {
				setTrocaConsulta(null);
				setTrocaConsultaErro(response);
				if (modalidade === 'ENFERMAGEM') {
					return;
				}
				setPanelErrors((prev) => ({
					...prev,
					form: getErrorMessage(
						response,
						'Não foi possível carregar os candidatos para troca.',
					),
				}));
				return;
			}

			setTrocaConsultaErro(null);
			setTrocaConsulta(response);
		};

		loadTrocaConsulta();

		return () => {
			isCancelled = true;
		};
	}, [
		openMiniTabs.troca,
		isTrocaExistenteSelecionada,
		token,
		currentTrocaOrigemItemId,
		scope,
		modalidade,
	]);

	const trocaCandidatos = useMemo(() => {
		const source = Array.isArray(trocaConsulta?.profissionais)
			? trocaConsulta.profissionais.flatMap((profissional = {}) =>
					(Array.isArray(profissional?.candidatos)
						? profissional.candidatos
						: []
					).map((candidato = {}) => ({
						...candidato,
						profissional:
							candidato?.item?.profissional || profissional,
					})),
				)
			: Array.isArray(trocaConsulta?.candidatos)
				? trocaConsulta.candidatos
				: [];

		return source
			.filter((item = {}) => {
				const itemSource = item?.item || item;
				const itemId = getTrocaConsultaItemId(item);
				const tipoItem = normalizeTipoItem(
					itemSource?.tipo_item ||
						itemSource?.dados_edicao?.tipo_item ||
						'',
				);
				const nomeProfissional = getTrocaConsultaProfessionalName(item);

				return (
					Boolean(itemId) &&
					String(itemId) !== String(currentTrocaOrigemItemId || '') &&
					tipoItem !== 'VACANCIA' &&
					Boolean(nomeProfissional)
				);
			})
			.sort((a, b) => {
				const nomeA = getTrocaConsultaProfessionalName(a);
				const nomeB = getTrocaConsultaProfessionalName(b);
				if (nomeA !== nomeB) {
					return nomeA.localeCompare(nomeB, 'pt-BR');
				}

				const dataA = getTrocaConsultaItemDate(a);
				const dataB = getTrocaConsultaItemDate(b);
				if (dataA !== dataB) {
					return String(dataA || '').localeCompare(
						String(dataB || ''),
						'pt-BR',
					);
				}

				const sourceA = a?.item || a;
				const sourceB = b?.item || b;
				const inicioA = String(
					sourceA?.hora_inicio ||
						sourceA?.dados_edicao?.hora_inicio ||
						'',
				).slice(0, 5);
				const inicioB = String(
					sourceB?.hora_inicio ||
						sourceB?.dados_edicao?.hora_inicio ||
						'',
				).slice(0, 5);

				return inicioA.localeCompare(inicioB, 'pt-BR');
			});
	}, [
		trocaConsulta?.candidatos,
		trocaConsulta?.profissionais,
		currentTrocaOrigemItemId,
		getTrocaConsultaItemId,
		getTrocaConsultaItemDate,
		getTrocaConsultaProfessionalName,
	]);

	const trocaProfissionalOptions = useMemo(() => {
		const uniqueProfessionals = new Map();
		const groupedProfessionals = new Map();

		trocaCandidatos.forEach((item) => {
			const key = getTrocaConsultaProfessionalKey(item);
			const nome = getTrocaConsultaProfessionalName(item);
			if (!key || !nome) return;

			const currentItems = groupedProfessionals.get(key) || [];
			groupedProfessionals.set(key, [...currentItems, item]);
		});

		groupedProfessionals.forEach((items, key) => {
			const nome = getTrocaConsultaProfessionalName(items?.[0] || {});

			if (!nome || uniqueProfessionals.has(key) || items.length === 0)
				return;

			uniqueProfessionals.set(key, {
				value: key,
				label: `${nome} (${items.length} ${items.length === 1 ? 'plantão' : 'plantões'})`,
			});
		});

		return Array.from(uniqueProfessionals.values()).sort((a, b) =>
			String(a.label || '').localeCompare(String(b.label || ''), 'pt-BR'),
		);
	}, [
		trocaCandidatos,
		getTrocaConsultaProfessionalKey,
		getTrocaConsultaProfessionalName,
	]);

	const trocaProfissionalOptionsFiltradas = useMemo(() => {
		const termo = normalizeSearchValue(trocaProfissionalSearchTerm);
		if (!termo) return trocaProfissionalOptions;

		return trocaProfissionalOptions.filter((item) =>
			normalizeSearchValue(item?.label).includes(termo),
		);
	}, [trocaProfissionalOptions, trocaProfissionalSearchTerm]);

	useEffect(() => {
		if (panelForm.troca_profissional_id || showTrocaProfissionalSuggestions)
			return;
		setTrocaProfissionalSearchTerm('');
	}, [panelForm.troca_profissional_id, showTrocaProfissionalSuggestions]);

	const updateTrocaProfissionalSuggestionsPosition = useCallback(() => {
		const rect = trocaProfissionalInputRef.current?.getBoundingClientRect();
		if (!rect || typeof window === 'undefined') return;

		const viewportPadding = 8;
		const width = Math.min(260, window.innerWidth - viewportPadding * 2);
		setTrocaProfissionalSuggestionsPosition({
			top: rect.bottom + 4,
			left: Math.max(
				viewportPadding,
				Math.min(
					rect.left,
					window.innerWidth - width - viewportPadding,
				),
			),
			width,
		});
	}, []);

	useEffect(() => {
		if (!showTrocaProfissionalSuggestions) return undefined;

		updateTrocaProfissionalSuggestionsPosition();
		window.addEventListener(
			'resize',
			updateTrocaProfissionalSuggestionsPosition,
		);
		window.addEventListener(
			'scroll',
			updateTrocaProfissionalSuggestionsPosition,
			true,
		);
		return () => {
			window.removeEventListener(
				'resize',
				updateTrocaProfissionalSuggestionsPosition,
			);
			window.removeEventListener(
				'scroll',
				updateTrocaProfissionalSuggestionsPosition,
				true,
			);
		};
	}, [
		showTrocaProfissionalSuggestions,
		updateTrocaProfissionalSuggestionsPosition,
	]);

	const selectedTrocaProfissionalItems = useMemo(() => {
		if (!panelForm.troca_profissional_id) return [];

		const filteredItems = trocaCandidatos.filter(
			(item) =>
				getTrocaConsultaProfessionalKey(item) ===
				String(panelForm.troca_profissional_id),
		);

		return filteredItems;
	}, [
		trocaCandidatos,
		panelForm.troca_profissional_id,
		getTrocaConsultaProfessionalKey,
	]);

	useEffect(() => {
		if (!panelForm.troca_profissional_id) return;

		const selectedProfessionalStillValid = trocaProfissionalOptions.some(
			(item) =>
				String(item?.value || '') ===
				String(panelForm.troca_profissional_id || ''),
		);

		if (selectedProfessionalStillValid) return;

		setPanelForm((prev) => ({
			...prev,
			troca_profissional_id: '',
			troca_data: '',
			troca_turno_id: '',
			item_destino_id: '',
			troca_perfil: 'COMPENSAR',
			troca_recorte_item_id: '',
			troca_recorte_inicio: '',
			troca_recorte_fim: '',
		}));
	}, [panelForm.troca_profissional_id, trocaProfissionalOptions]);

	const trocaDataOptions = useMemo(() => {
		const uniqueDates = new Map();

		selectedTrocaProfissionalItems.forEach((item) => {
			const data = getTrocaConsultaItemDate(item);
			if (!data || uniqueDates.has(data)) return;

			uniqueDates.set(data, {
				value: data,
				label: formatISOToDisplay(data) || data,
			});
		});

		return Array.from(uniqueDates.values()).sort((a, b) =>
			String(a.value || '').localeCompare(String(b.value || ''), 'pt-BR'),
		);
	}, [selectedTrocaProfissionalItems, getTrocaConsultaItemDate]);

	useEffect(() => {
		if (!panelForm.troca_data) return;

		const selectedDateStillValid = trocaDataOptions.some(
			(item) =>
				String(item?.value || '') ===
				String(panelForm.troca_data || ''),
		);

		if (selectedDateStillValid) return;

		setPanelForm((prev) => ({
			...prev,
			troca_data: '',
			troca_turno_id: '',
			item_destino_id: '',
			troca_perfil: 'COMPENSAR',
			troca_recorte_item_id: '',
			troca_recorte_inicio: '',
			troca_recorte_fim: '',
		}));
	}, [panelForm.troca_data, trocaDataOptions]);

	const trocaTurnoOptions = useMemo(() => {
		return selectedTrocaProfissionalItems
			.filter((item) => {
				const selectedData = normalizeTrocaDateValue(
					panelForm.troca_data,
				);
				if (!selectedData) return true;
				return getTrocaConsultaItemDate(item) === selectedData;
			})
			.map((item) => {
				const itemId = getTrocaConsultaItemId(item);

				return {
					value: String(itemId || ''),
					label: formatTrocaConsultaPlantaoOptionLabel(item),
					disabled: false,
					error: item?.erros?.[0]?.mensagem || '',
					data: getTrocaConsultaItemDate(item),
				};
			})
			.filter((item) => Boolean(item.value));
	}, [
		selectedTrocaProfissionalItems,
		panelForm.troca_data,
		getTrocaConsultaItemId,
		getTrocaConsultaItemDate,
		formatTrocaConsultaPlantaoOptionLabel,
	]);

	const trocaDestinoResolvidoId = useMemo(() => {
		if (panelForm.troca_turno_id) {
			return String(panelForm.troca_turno_id);
		}

		const selectableOptions = trocaTurnoOptions.filter(
			(item) => item?.disabled !== true,
		);
		if (selectableOptions.length === 1) {
			return String(selectableOptions[0]?.value || '');
		}

		return '';
	}, [panelForm.troca_turno_id, trocaTurnoOptions]);

	useEffect(() => {
		if (trocaTurnoOptions.length !== 1) return;
		const unicoPlantao = trocaTurnoOptions[0] || {};
		const turnoId = String(unicoPlantao.value || '');
		if (!turnoId || String(panelForm.troca_turno_id || '') === turnoId) {
			return;
		}

		setPanelForm((prev) => ({
			...prev,
			troca_turno_id: turnoId,
			item_destino_id: turnoId,
			troca_data: unicoPlantao.data || prev.troca_data,
			troca_perfil: '',
			troca_recorte_item_id: '',
			troca_recorte_inicio: '',
			troca_recorte_fim: '',
		}));
	}, [panelForm.troca_turno_id, trocaTurnoOptions]);

	const trocaDestinoSelecionadoItem = useMemo(
		() =>
			selectedTrocaProfissionalItems.find(
				(item) =>
					String(getTrocaConsultaItemId(item) || '') ===
					String(trocaDestinoResolvidoId || ''),
			) || null,
		[
			selectedTrocaProfissionalItems,
			trocaDestinoResolvidoId,
			getTrocaConsultaItemId,
		],
	);

	const trocaRecorteCapacidadeHabilitada = useMemo(() => {
		const sources = [
			trocaAvaliacao?.capacidades,
			trocaConsulta?.capacidades,
			trocaDestinoSelecionadoItem?.capacidades,
			trocaDestinoSelecionadoItem?.item?.capacidades,
		];
		return sources.some((source = {}) => {
			if (source?.formas?.RECORTE?.habilitada === true) return true;
			if (source?.formas?.recorte?.habilitada === true) return true;
			if (source?.RECORTE?.habilitada === true) return true;
			return source?.recorte?.habilitada === true;
		});
	}, [trocaAvaliacao, trocaConsulta, trocaDestinoSelecionadoItem]);

	const trocaRecorteSugerido = useMemo(() => {
		if (!trocaDestinoSelecionadoItem) return null;
		const origemDuracao = getTrocaConsultaItemDurationMinutes(
			currentTrocaOrigemItem,
		);
		const destinoDuracao = getTrocaConsultaItemDurationMinutes(
			trocaDestinoSelecionadoItem,
		);
		if (
			!origemDuracao ||
			!destinoDuracao ||
			origemDuracao === destinoDuracao
		) {
			return null;
		}

		const recorteNoDestino = destinoDuracao > origemDuracao;
		const targetItem = recorteNoDestino
			? trocaDestinoSelecionadoItem
			: currentTrocaOrigemItem;
		const targetId = recorteNoDestino
			? Number(trocaDestinoResolvidoId || 0)
			: Number(currentTrocaOrigemItemId || 0);
		const menorDuracao = Math.min(origemDuracao, destinoDuracao);
		const targetHorario = getTrocaConsultaItemHorario(targetItem);
		const inicio = targetHorario.hora_inicio || '07:00';
		const fim = addMinutesToHour(inicio, menorDuracao);

		return targetId && inicio && fim
			? {
					itemId: String(targetId),
					lado: recorteNoDestino ? 'B' : 'A',
					inicio,
					fim,
					fimTurno: targetHorario.hora_fim || '',
					maiorDuracao: Math.max(origemDuracao, destinoDuracao),
					menorDuracao,
				}
			: null;
	}, [
		currentTrocaOrigemItem,
		currentTrocaOrigemItemId,
		trocaDestinoResolvidoId,
		trocaDestinoSelecionadoItem,
		getTrocaConsultaItemDurationMinutes,
		getTrocaConsultaItemHorario,
	]);

	const trocaRecorteOptions = useMemo(() => {
		if (!trocaRecorteSugerido) return [];
		const targetId = Number(trocaRecorteSugerido.itemId || 0);
		const targetDuration = Number(trocaRecorteSugerido.menorDuracao || 0);
		const itensAvaliacao = Array.isArray(trocaAvaliacao?.itens)
			? trocaAvaliacao.itens
			: [];
		const options = [];
		const seen = new Set();
		const appendOption = ({ itemId, intervalo, source }) => {
			const inicio = formatHourInputToTimeValue(intervalo?.inicio);
			const fim = formatHourInputToTimeValue(intervalo?.fim);
			const duracao = Number(
				intervalo?.duracao_minutos ||
					calculateShiftDurationMinutes(inicio, fim) ||
					0,
			);
			if (!inicio || !fim || !duracao) return;
			if (targetDuration && duracao !== targetDuration) return;

			const key = `${itemId}-${inicio}-${fim}`;
			if (seen.has(key)) return;
			seen.add(key);

			options.push({
				value: key,
				itemId: String(itemId),
				inicio,
				fim,
				duracao,
				source,
				label: `${inicio} às ${fim} (${String(duracao / 60).replace('.', ',')}h)`,
			});
		};

		itensAvaliacao.forEach((avaliacaoItem = {}) => {
			const item = avaliacaoItem?.item || avaliacaoItem;
			const itemId = Number(
				item?.item_id ||
					item?.id ||
					avaliacaoItem?.item_id ||
					avaliacaoItem?.id ||
					0,
			);
			if (!itemId || itemId !== targetId) return;
			if (item?.recorte_elegibilidade?.disponivel === false) return;

			if (Array.isArray(item?.intervalos_recorte_permitidos)) {
				item.intervalos_recorte_permitidos.forEach((intervalo = {}) =>
					appendOption({ itemId, intervalo, source: 'intervalo' }),
				);
			}
			if (Array.isArray(item?.politica_recorte?.sugestoes)) {
				item.politica_recorte.sugestoes.forEach((intervalo = {}) =>
					appendOption({ itemId, intervalo, source: 'sugestao' }),
				);
			}
		});

		const fontesRecorte = [
			trocaDestinoSelecionadoItem,
			trocaDestinoSelecionadoItem?.item,
			trocaAvaliacao?.capacidades?.recorte,
			trocaAvaliacao?.capacidades?.RECORTE,
			trocaConsulta?.capacidades?.recorte,
			trocaConsulta?.capacidades?.RECORTE,
		];
		fontesRecorte.forEach((fonte = {}) => {
			const intervalos = Array.isArray(
				fonte?.intervalos_recorte_permitidos,
			)
				? fonte.intervalos_recorte_permitidos
				: [];
			const sugestoes = [
				...(Array.isArray(fonte?.politica_recorte?.sugestoes)
					? fonte.politica_recorte.sugestoes
					: []),
				...(Array.isArray(fonte?.sugestoes) ? fonte.sugestoes : []),
			];
			intervalos.forEach((intervalo) =>
				appendOption({
					itemId: targetId,
					intervalo,
					source: 'intervalo',
				}),
			);
			sugestoes.forEach((intervalo) =>
				appendOption({
					itemId: targetId,
					intervalo,
					source: 'sugestao',
				}),
			);
		});

		if (trocaRecorteCapacidadeHabilitada && options.length === 0) {
			appendOption({
				itemId: targetId,
				intervalo: {
					inicio: trocaRecorteSugerido.inicio,
					fim: trocaRecorteSugerido.fim,
				},
				source: 'borda',
			});
			const inicioSufixo = addMinutesToHour(
				trocaRecorteSugerido.fimTurno,
				-targetDuration,
			);
			appendOption({
				itemId: targetId,
				intervalo: {
					inicio: inicioSufixo,
					fim: trocaRecorteSugerido.fimTurno,
				},
				source: 'borda',
			});
		}

		if (
			panelForm.troca_perfil === 'RECORTE' &&
			Number(panelForm.troca_recorte_item_id || 0) === targetId &&
			panelForm.troca_recorte_inicio &&
			panelForm.troca_recorte_fim
		) {
			appendOption({
				itemId: targetId,
				intervalo: {
					inicio: panelForm.troca_recorte_inicio,
					fim: panelForm.troca_recorte_fim,
				},
				source: 'selecionado',
			});
		}

		return options;
	}, [
		trocaAvaliacao?.itens,
		trocaAvaliacao?.capacidades,
		trocaConsulta?.capacidades,
		trocaDestinoSelecionadoItem,
		trocaRecorteSugerido,
		trocaRecorteCapacidadeHabilitada,
		panelForm.troca_perfil,
		panelForm.troca_recorte_item_id,
		panelForm.troca_recorte_inicio,
		panelForm.troca_recorte_fim,
	]);

	const trocaPodeEscolherPerfil = Boolean(
		trocaRecorteCapacidadeHabilitada &&
		trocaRecorteSugerido &&
		trocaRecorteOptions.length > 0,
	);

	useEffect(() => {
		if (!trocaPodeEscolherPerfil) {
			if (
				panelForm.troca_perfil === 'RECORTE' ||
				panelForm.troca_recorte_item_id ||
				panelForm.troca_recorte_inicio ||
				panelForm.troca_recorte_fim
			) {
				setPanelForm((prev) => ({
					...prev,
					troca_perfil: '',
					troca_recorte_item_id: '',
					troca_recorte_inicio: '',
					troca_recorte_fim: '',
				}));
			}
			return;
		}

		if (panelForm.troca_perfil !== 'RECORTE') {
			if (
				panelForm.troca_recorte_item_id ||
				panelForm.troca_recorte_inicio ||
				panelForm.troca_recorte_fim
			) {
				setPanelForm((prev) => ({
					...prev,
					troca_recorte_item_id: '',
					troca_recorte_inicio: '',
					troca_recorte_fim: '',
				}));
			}
			return;
		}

		const currentOptionStillValid = trocaRecorteOptions.some(
			(option) =>
				String(option.itemId) ===
					String(panelForm.troca_recorte_item_id || '') &&
				String(option.inicio) ===
					String(panelForm.troca_recorte_inicio || '') &&
				String(option.fim) ===
					String(panelForm.troca_recorte_fim || ''),
		);
		if (currentOptionStillValid) {
			return;
		}
		const defaultOption = trocaRecorteOptions[0] || null;
		if (!defaultOption) return;

		setPanelForm((prev) => ({
			...prev,
			troca_recorte_item_id: defaultOption.itemId,
			troca_recorte_inicio: defaultOption.inicio,
			troca_recorte_fim: defaultOption.fim,
		}));
	}, [
		trocaPodeEscolherPerfil,
		trocaRecorteOptions,
		panelForm.troca_perfil,
		panelForm.troca_recorte_item_id,
		panelForm.troca_recorte_inicio,
		panelForm.troca_recorte_fim,
	]);

	useEffect(() => {
		const nextDestino = String(trocaDestinoResolvidoId || '');
		const currentTurno = String(panelForm.troca_turno_id || '');
		const currentDestino = String(panelForm.item_destino_id || '');
		const expectedDestino = currentTurno || nextDestino;

		if (currentDestino === expectedDestino) return;

		setPanelForm((prev) => {
			const prevTurno = String(prev.troca_turno_id || '');
			const prevDestino = String(prev.item_destino_id || '');
			const desiredDestino =
				prevTurno || String(trocaDestinoResolvidoId || '');

			if (prevDestino === desiredDestino) return prev;

			return {
				...prev,
				item_destino_id: desiredDestino,
			};
		});
	}, [
		panelForm.item_destino_id,
		panelForm.troca_turno_id,
		trocaDestinoResolvidoId,
	]);

	useEffect(() => {
		setTrocaItensComplementares([]);
		setTrocaAvaliacao(null);
		setTrocaComplementos(null);
		setTrocaConsultaComplementar(null);
	}, [trocaDestinoResolvidoId]);

	const trocaRecortesPayload = useMemo(() => {
		if (panelForm.troca_perfil !== 'RECORTE') return {};
		const itemId = Number(panelForm.troca_recorte_item_id || 0);
		const inicio = formatHourInputToTimeValue(
			panelForm.troca_recorte_inicio,
		);
		const fim = formatHourInputToTimeValue(panelForm.troca_recorte_fim);
		if (!itemId || !inicio || !fim) return {};
		return {
			[itemId]: {
				inicio,
				fim,
			},
		};
	}, [
		panelForm.troca_perfil,
		panelForm.troca_recorte_item_id,
		panelForm.troca_recorte_inicio,
		panelForm.troca_recorte_fim,
	]);

	const trocaComandoNormalizado = useMemo(() => {
		const comando =
			trocaAvaliacao?.comando_normalizado ||
			trocaAvaliacao?.acoes?.efetivar?.entrada;
		return comando && typeof comando === 'object' && !Array.isArray(comando)
			? comando
			: null;
	}, [trocaAvaliacao]);

	const trocaPacotePayload = useMemo(() => {
		return buildTrocaEquivalentePayload({
			itemOrigemId: currentTrocaOrigemItemId,
			itemDestinoId: trocaDestinoResolvidoId,
			complementos: trocaItensComplementares,
			recortes: trocaRecortesPayload,
		});
	}, [
		currentTrocaOrigemItemId,
		trocaDestinoResolvidoId,
		trocaItensComplementares,
		trocaRecortesPayload,
	]);

	const trocaEfetivacaoBasePayload = useMemo(() => {
		return buildTrocaEquivalentePayload({
			itemOrigemId: currentTrocaOrigemItemId,
			itemDestinoId: trocaDestinoResolvidoId,
			complementos: trocaItensComplementares,
			recortes: trocaRecortesPayload,
			previewEtag: trocaAvaliacao?.preview_etag || '',
			comandoNormalizado: trocaComandoNormalizado,
		});
	}, [
		currentTrocaOrigemItemId,
		trocaDestinoResolvidoId,
		trocaItensComplementares,
		trocaRecortesPayload,
		trocaAvaliacao?.preview_etag,
		trocaComandoNormalizado,
	]);

	const trocaEfetivacaoPayload = useMemo(() => {
		return trocaEfetivacaoBasePayload;
	}, [trocaEfetivacaoBasePayload]);

	const trocaEfetivacaoIdempotencyKey = useMemo(() => {
		if (!trocaEfetivacaoBasePayload) return '';
		const signature = getTrocaEquivalentePayloadSignature(
			trocaEfetivacaoBasePayload,
		);
		if (!signature) return '';

		if (!trocaIdempotencyKeysRef.current.has(signature)) {
			trocaIdempotencyKeysRef.current.set(
				signature,
				createTrocaEquivalenteIdempotencyKey(),
			);
		}

		return trocaIdempotencyKeysRef.current.get(signature);
	}, [trocaEfetivacaoBasePayload]);

	useEffect(() => {
		let cancelled = false;
		if (
			!openMiniTabs.troca ||
			isTrocaExistenteSelecionada ||
			!trocaPacotePayload ||
			!token
		) {
			setTrocaAvaliacao(null);
			setTrocaComplementos(null);
			setTrocaConsultaComplementar(null);
			return undefined;
		}

		const avaliarPacote = async () => {
			setIsLoadingTrocaAvaliacao(true);
			try {
				const avaliacao = await requestSimularTrocaEquivalente({
					token,
					body: trocaPacotePayload,
					scope,
				});
				if (cancelled) return;
				setTrocaAvaliacao(avaliacao);

				if (
					!avaliacao?.error &&
					avaliacao?.saldo?.equivalente === false
				) {
					const itensA = trocaPacotePayload.itens_a.map(
						(item) => item.item_id,
					);
					const itensB = trocaPacotePayload.itens_b.map(
						(item) => item.item_id,
					);
					const hasRecorte = [
						...(trocaPacotePayload.itens_a || []),
						...(trocaPacotePayload.itens_b || []),
					].some((item) => item?.recorte);
					const ladoComplemento =
						avaliacao?.saldo?.diferenca_minutos > 0 ? 'B' : 'A';
					const [complementos, consultaComplementar] =
						await Promise.all([
							requestComplementosTrocaEquivalente({
								token,
								itensA,
								itensB,
								body: hasRecorte ? trocaPacotePayload : null,
								scope,
							}),
							requestConsultarTrocasEquivalentes({
								token,
								itensA,
								itensB,
								lado: ladoComplemento,
								body: hasRecorte
									? {
											...trocaPacotePayload,
											lado: ladoComplemento,
										}
									: null,
								scope,
							}),
						]);
					if (cancelled) return;
					setTrocaComplementos(complementos);
					setTrocaConsultaComplementar(consultaComplementar);
				} else {
					setTrocaComplementos(null);
					setTrocaConsultaComplementar(null);
				}
			} finally {
				if (!cancelled) setIsLoadingTrocaAvaliacao(false);
			}
		};

		avaliarPacote();
		return () => {
			cancelled = true;
		};
	}, [
		openMiniTabs.troca,
		isTrocaExistenteSelecionada,
		trocaPacotePayload,
		token,
		scope,
	]);

	const trocaComplementoOptions = useMemo(() => {
		const selecionados = new Set(
			[
				...(trocaPacotePayload?.itens_a || []),
				...(trocaPacotePayload?.itens_b || []),
			].map((item) => String(item?.item_id || '')),
		);
		const sugestoes = (
			Array.isArray(trocaComplementos?.complementos)
				? trocaComplementos.complementos
				: []
		).map((complemento = {}) => {
			const item = complemento?.item || {};
			const itemId = Number(item?.item_id || 0);
			return {
				value: String(itemId || ''),
				label: formatTrocaConsultaPlantaoOptionLabel(item),
				disabled: complemento?.apto === false,
				error: complemento?.erros?.[0]?.mensagem || '',
				lado: trocaComplementos?.lado_complemento,
			};
		});
		const consulta = Array.isArray(trocaConsultaComplementar?.profissionais)
			? trocaConsultaComplementar.profissionais.flatMap(
					(profissional = {}) =>
						(Array.isArray(profissional?.candidatos)
							? profissional.candidatos
							: []
						).map((candidato = {}) => ({
							...candidato,
							profissional,
						})),
				)
			: Array.isArray(trocaConsultaComplementar?.candidatos)
				? trocaConsultaComplementar.candidatos
				: [];
		const candidatos = consulta.map((candidato = {}) => {
			const itemId = getTrocaConsultaItemId(candidato);
			return {
				value: String(itemId || ''),
				label: formatTrocaConsultaPlantaoOptionLabel(candidato),
				disabled: isTrocaCandidateDisabled(candidato),
				error: candidato?.erros?.[0]?.mensagem || '',
				lado:
					candidato?.lado_adicao ||
					trocaConsultaComplementar?.lado_consultado,
			};
		});
		const unique = new Map();
		[...sugestoes, ...candidatos].forEach((item) => {
			if (!item?.value || selecionados.has(String(item.value))) return;
			if (!unique.has(String(item.value)))
				unique.set(String(item.value), item);
		});
		return Array.from(unique.values());
	}, [
		trocaComplementos,
		trocaConsultaComplementar,
		trocaPacotePayload,
		formatTrocaConsultaPlantaoOptionLabel,
		getTrocaConsultaItemId,
		isTrocaCandidateDisabled,
	]);
	const possuiTrocaComplementoDisponivel = trocaComplementoOptions.some(
		(item = {}) => item?.disabled !== true,
	);

	const handleTrocaPerfilChange = (perfil) => {
		if (perfil === 'COMPENSAR') {
			setPanelForm((prev) => ({
				...prev,
				troca_perfil: 'COMPENSAR',
				troca_recorte_item_id: '',
				troca_recorte_inicio: '',
				troca_recorte_fim: '',
			}));
			return;
		}

		setTrocaItensComplementares([]);
		setTrocaComplementos(null);
		setTrocaConsultaComplementar(null);
		setPanelForm((prev) => ({
			...prev,
			troca_perfil: 'RECORTE',
		}));
	};

	const handleAdicionarTrocaComplemento = (itemId) => {
		const option = trocaComplementoOptions.find(
			(item) => String(item.value) === String(itemId),
		);
		if (!option || option.disabled) return;
		if (
			trocaItensComplementares.some(
				(item) => String(item?.item_id) === String(option.value),
			)
		) {
			setPanelErrors((prev) => ({
				...prev,
				form: 'Este card já foi adicionado ao pacote de troca.',
			}));
			return;
		}
		const ladoKey =
			normalizeCode(option.lado) === 'A' ? 'itens_a' : 'itens_b';
		if ((trocaPacotePayload?.[ladoKey] || []).length >= 3) {
			setPanelErrors((prev) => ({
				...prev,
				form: 'O limite é de 3 cards por lado da troca.',
			}));
			return;
		}
		setTrocaItensComplementares((prev) => [
			...prev,
			{
				item_id: Number(option.value),
				lado: option.lado,
				label: option.label,
			},
		]);
		setPanelForm((prev) => ({
			...prev,
			troca_perfil: 'COMPENSAR',
			troca_recorte_item_id: '',
			troca_recorte_inicio: '',
			troca_recorte_fim: '',
		}));
	};

	const trocaAvaliacaoValida = isTrocaEquivalenteValida(trocaAvaliacao);
	const trocaAvaliacaoMensagemErro =
		trocaAvaliacao?.itens?.flatMap((item) => item?.erros || [])?.[0]
			?.mensagem ||
		trocaAvaliacao?.erros_pacote?.find(
			(error) => normalizeCode(error?.codigo) !== 'SALDO_NAO_EQUIVALENTE',
		)?.mensagem ||
		'';

	const resetPanelFormByLinha = useCallback(
		(linha = null) => {
			const celulaSelecionada = (linha?.celulas || []).find(
				(item) => String(item?.data) === String(selectedDiaIso),
			);
			const presetAcao = getCellActionPreset(
				linha,
				celulaSelecionada,
				selectedDiaIso,
			);
			const firstCard = getCellPrimaryCard(celulaSelecionada);
			const selectedShiftSource = getCardShiftSource(
				firstCard || {},
				celulaSelecionada,
				linha,
			);

			setPanelErrors({});
			setPanelForm((prev) => ({
				...prev,
				acao: '',
				regime: String(
					linha?.regime_escala?.regime || linha?.regime || '',
				).trim(),
				regime_vinculo_id: linha?.regime_vinculo_id || null,
				hora_inicio: selectedShiftSource?.hora_inicio
					? String(selectedShiftSource.hora_inicio).slice(0, 5)
					: '',
				hora_fim: selectedShiftSource?.hora_fim
					? String(selectedShiftSource.hora_fim).slice(0, 5)
					: '',
				tipo_item: 'FERIAS',
				periodo_inicio: selectedDiaIso
					? formatISOToDisplay(selectedDiaIso)
					: '',
				periodo_fim: selectedDiaIso
					? formatISOToDisplay(selectedDiaIso)
					: '',
				observacao: '',
				item_origem_id: String(getCardItemId(firstCard) || ''),
				item_destino_id: '',
				troca_profissional_id: '',
				troca_data: '',
				troca_turno_id: '',
				troca_perfil: 'COMPENSAR',
				troca_recorte_item_id: '',
				troca_recorte_inicio: '',
				troca_recorte_fim: '',
				delete_mode: 'dia',
				primeiro_dia_trabalho_mes: selectedDiaIso
					? formatISOToDisplay(selectedDiaIso)
					: '',
				...(presetAcao?.formPatch || {}),
			}));
			setOpenMiniTabs(
				presetAcao?.openTab
					? {
							...getDefaultMiniTabs(),
							[presetAcao.openTab]: true,
						}
					: getDefaultMiniTabs(),
			);
		},
		[selectedDiaIso],
	);

	const normalizeLinhaAfterCompetenciaCleanup = useCallback((linha = {}) => {
		if (!isLinhaSugestaoOcultaPorLimpeza(linha)) return linha;

		return {
			...linha,
			celulas: Array.isArray(linha?.celulas)
				? linha.celulas.map((celula = {}) => {
						const cards = Array.isArray(celula?.cards)
							? celula.cards
							: [];

						if (cards.length > 0) return celula;

						return {
							...celula,
							status: '',
							sugerido: false,
							acoes_disponiveis: Array.isArray(
								celula?.acoes_disponiveis,
							)
								? celula.acoes_disponiveis.filter(
										(acao) =>
											normalizeCode(acao) !==
											'REMOVER_DIA',
									)
								: celula?.acoes_disponiveis,
						};
					})
				: linha?.celulas,
		};
	}, []);

	const responseHasCompetenciaCleanupState = useCallback(
		(payload, regimeVinculoId) => {
			const resumoAcao = normalizeCode(payload?.resumo?.acao || '');
			if (resumoAcao && resumoAcao !== 'REMOVER_COMPETENCIA')
				return false;

			const ambientePayload = payload?.ambiente || payload;
			const linhasPayload = Array.isArray(ambientePayload?.linhas)
				? ambientePayload.linhas
				: [];
			const linha = linhasPayload.find(
				(item) =>
					String(item?.regime_vinculo_id) === String(regimeVinculoId),
			);

			if (!linha) return false;
			if (!isLinhaSugestaoOcultaPorLimpeza(linha)) return false;

			return (linha?.celulas || []).every((celula = {}) => {
				const cards = Array.isArray(celula?.cards) ? celula.cards : [];
				return (
					cards.length === 0 && !hasCellShiftReference(celula, linha)
				);
			});
		},
		[],
	);

	const getCompetenciaCleanupFeedbackMessage = useCallback((payload) => {
		const removidos = Number(payload?.resumo?.quantidade_removidos || 0);
		if (removidos > 0) {
			return `Competência limpa com sucesso. ${removidos} card${removidos > 1 ? 's foram removidos' : ' foi removido'}.`;
		}
		return 'Competência limpa com sucesso. Não havia cards persistidos para remover nesta linha.';
	}, []);

	const applyAmbienteResponse = (payload) => {
		if (!payload) return;
		const novoAmbiente = payload?.ambiente || payload;
		if (novoAmbiente?.linhas && novoAmbiente?.calendario) {
			const alocacoesOrigem = getAlocacoesOrigemFromPayload(payload);
			setAmbiente({
				...novoAmbiente,
				linhas: Array.isArray(novoAmbiente?.linhas)
					? enriquecerLinhasComAlocacoesOrigem(
							novoAmbiente.linhas,
							alocacoesOrigem,
						).map(normalizeLinhaAfterCompetenciaCleanup)
					: novoAmbiente?.linhas,
			});
		}
	};

	const loadCatalogosBase = useCallback(
		async ({
			authToken,
			estabelecimento,
			filtrarUnidadesComEscala = false,
		}) => {
			const [
				unitsResponse,
				catalogosResponse,
				catalogosUnidadesResponse,
			] = await Promise.all([
				requestNewUnits(),
				requestCatalogosEscalaSelects({
					token: authToken,
					estabelecimento,
					scope,
				}),
				filtrarUnidadesComEscala
					? requestCatalogosEscalaSelects({
							token: authToken,
							somenteComItens: true,
							scope,
						})
					: Promise.resolve(null),
			]);

			const unidadesComEscala = normalizeUnidades(
				catalogosUnidadesResponse?.estabelecimentos || [],
			);

			if (filtrarUnidadesComEscala && !catalogosUnidadesResponse?.error) {
				setUnidadeOptions(unidadesComEscala);
			} else if (!unitsResponse?.error) {
				setUnidadeOptions(normalizeUnidades(unitsResponse));
			}

			if (!catalogosResponse?.error) {
				setSetorOptions(
					sortSetoresPorAndar(
						normalizeCatalogSetores(catalogosResponse),
					),
				);
			}
		},
		[scope],
	);

	const loadVinculosEscala = useCallback(
		async (authToken) => {
			if (!authToken) return;

			const response = await requestVinculosEscala({
				token: authToken,
				scope,
			});

			if (response?.error) return;

			setVinculosEscala(Array.isArray(response) ? response : []);
		},
		[scope],
	);

	const loadOpcoesAmbiente = async ({ authToken, targetContexto }) => {
		const setorId = Number(targetContexto?.setor || 0);
		// O contrato de /opcoes/ exige um setor. No contexto "todos os setores",
		// aguarde uma operação que tenha setor-base em vez de disparar um 400.
		if (!setorId) {
			setAmbienteOpcoes(null);
			return null;
		}
		const opcoes = await requestOpcoesAmbientePreenchimentoEscala({
			token: authToken,
			contexto: targetContexto,
			scope,
		});
		if (opcoes?.error) {
			setSubmitErrorScope('contexto');
			setSubmitError(
				getErrorMessage(
					opcoes,
					'Erro ao carregar opções do ambiente assistido.',
				),
			);
			return null;
		}
		setAmbienteOpcoes(opcoes);
		return opcoes;
	};

	const loadAmbiente = async ({
		authToken,
		targetContexto,
		showLoading = true,
		forceRefresh = false,
		loadingMode = 'skeleton',
	}) => {
		const minimumLoadingMs =
			loadingMode === 'month-navigation'
				? TABLE_MONTH_NAVIGATION_MIN_LOADING_MS
				: TABLE_SKELETON_MIN_LOADING_MS;
		const loadingStartedAt = showLoading ? Date.now() : null;
		const finishLoading = async () => {
			if (!showLoading || !loadingStartedAt) return;
			const remainingMs = Math.max(
				0,
				minimumLoadingMs - (Date.now() - loadingStartedAt),
			);
			if (remainingMs > 0) {
				await new Promise((resolve) => setTimeout(resolve, remainingMs));
			}
			setLoadingAmbiente(false);
		};

		if (showLoading) {
			setLoadingAmbiente(true);
		}
		setSubmitError('');
		setSubmitErrorScope('');
		setFeedbackMessage('');

		const response = await requestAbrirAmbientePreenchimentoEscala({
			token: authToken,
			contexto: targetContexto,
			modo: 'detalhado',
			scope,
			forceRefresh,
		});

		if (response?.error) {
			setSubmitErrorScope('contexto');
			setSubmitError(
				getErrorMessage(
					response,
					'Erro ao abrir ambiente de preenchimento.',
				),
			);
			setAmbiente(null);
			setContextoAplicado(null);
			await finishLoading();
			return null;
		}

		const responseContexto =
			response?.contexto && typeof response.contexto === 'object'
				? response.contexto
				: {};
		const contextoVisual = {
			...targetContexto,
			...responseContexto,
			setor: resolveContextoVisualSetor({
				targetSetor: targetContexto?.setor,
			}),
		};

		setAmbiente(response);
		escalaReloadScopeRef.current = scope;
		setContextoAplicado(contextoVisual);
		setSelectedLinhaId('');
		setSelectedDiaIso('');
		setIsEditPanelOpen(false);
		await finishLoading();
		return response;
	};

	// A linha consolidada da grade pode ser uma projeção visual e, em casos
	// legados, chegar com uma identidade desatualizada. Antes de qualquer
	// escrita, releia as duas fontes canônicas que conhecem essa identidade.
	const reconciliarLinhasParaExclusao = async (linhas = []) => {
		const referencias = Array.isArray(linhas) ? linhas.filter(Boolean) : [];
		if (referencias.length === 0 || !token || !contextoAplicado)
			return linhas;

		const contextoConsulta = {
			...contextoAplicado,
			// `TODOS` é permitido somente na abertura do ambiente e devolve a
			// projeção agregada com os contextos_horario canônicos.
			setor: isSetorTodosValue(contextoAplicado?.setor)
				? 'TODOS'
				: contextoAplicado?.setor,
		};
		const extrairLinhasCanonicas = (payload = {}) => {
			const linhasResposta = [
				...(Array.isArray(payload?.linhas) ? payload.linhas : []),
				...(Array.isArray(payload?.ambiente?.linhas)
					? payload.ambiente.linhas
					: []),
			];
			return linhasResposta.flatMap((linha = {}) => {
				const contextosHorario = [
					...(Array.isArray(linha?.profissional?.contextos_horario)
						? linha.profissional.contextos_horario
						: []),
					...(Array.isArray(
						linha?.regime_escala?.profissional?.contextos_horario,
					)
						? linha.regime_escala.profissional.contextos_horario
						: []),
				];
				return [
					linha,
					...(Array.isArray(linha?.setores_linhas)
						? linha.setores_linhas
						: []),
					...(linha?.sourceLinha ? [linha.sourceLinha] : []),
					...contextosHorario.map((contextoHorario = {}) => ({
						...linha,
						setor:
							contextoHorario?.setor ||
							linha?.setor ||
							linha?.setor_origem,
						setor_id:
							contextoHorario?.setor?.id ||
							contextoHorario?.setor_id ||
							getLinhaSetorId(linha),
						regime_vinculo_id:
							contextoHorario?.regime_vinculo_id ||
							linha?.regime_vinculo_id ||
							null,
					})),
				];
			});
		};
		const correspondeLinha = (referencia = {}, candidata = {}) => {
			const setorReferencia = String(
				getLinhaSetorId(referencia) || '',
			).trim();
			const setorCandidato = String(
				getLinhaSetorId(candidata) || '',
			).trim();
			if (!setorReferencia || setorReferencia !== setorCandidato)
				return false;

			const vinculoReferencia = Number(
				getVinculoIdFromLinha(referencia) || 0,
			);
			const vinculoCandidato = Number(
				getVinculoIdFromLinha(candidata) || 0,
			);
			if (vinculoReferencia && vinculoCandidato) {
				return vinculoReferencia === vinculoCandidato;
			}

			const profissionalReferencia = Number(
				getProfissionalIdFromLinha(referencia) || 0,
			);
			const profissionalCandidato = Number(
				getProfissionalIdFromLinha(candidata) || 0,
			);
			if (profissionalReferencia && profissionalCandidato) {
				return profissionalReferencia === profissionalCandidato;
			}

			const cpfReferencia = normalizeCpf(getCpfFromLinha(referencia));
			const cpfCandidato = normalizeCpf(getCpfFromLinha(candidata));
			return Boolean(
				cpfReferencia && cpfCandidato && cpfReferencia === cpfCandidato,
			);
		};
		const resolverNaFonte = (referencia = {}, candidatas = []) => {
			const ids = Array.from(
				new Set(
					candidatas
						.filter((candidata = {}) =>
							correspondeLinha(referencia, candidata),
						)
						.map((candidata = {}) =>
							getLinhaRegimeVinculoId(candidata),
						)
						.filter(Boolean),
				),
			);
			return ids.length === 1 ? ids[0] : null;
		};

		let linhasAmbiente = [];
		let linhasEstruturais = [];
		try {
			const ambienteCanonico =
				await requestAbrirAmbientePreenchimentoEscala({
					token,
					contexto: contextoConsulta,
					modo: 'detalhado',
					scope,
					forceRefresh: true,
				});
			if (!ambienteCanonico?.error) {
				linhasAmbiente = extrairLinhasCanonicas(ambienteCanonico);
			}
		} catch {
			// A segunda fonte ainda pode fornecer a alocação de uma jornada antiga.
		}

		if (
			referencias.some((linha) => !resolverNaFonte(linha, linhasAmbiente))
		) {
			try {
				const estadoOrigem =
					await requestAbrirAlocacaoOrigemAmbienteEscala({
						token,
						contexto: contextoConsulta,
						scope,
					});
				if (!estadoOrigem?.error) {
					linhasEstruturais = [
						...extrairLinhasCanonicas(estadoOrigem),
						...getAlocacoesOrigemFromPayload(estadoOrigem).flatMap(
							buildLinhasEstruturaisDaAlocacaoOrigem,
						),
					];
				}
			} catch {
				// A mensagem final continua sendo apresentada apenas depois das duas
				// leituras terem sido tentadas.
			}
		}

		return linhas.map((linha = {}) => {
			const regimeVinculoId =
				resolverNaFonte(linha, linhasAmbiente) ||
				resolverNaFonte(linha, linhasEstruturais) ||
				getLinhaRegimeVinculoId(linha);
			return regimeVinculoId
				? { ...linha, regime_vinculo_id: regimeVinculoId }
				: linha;
		});
	};

	useEffect(() => {
		const localData = Cookies.get('user');
		if (!localData) {
			setHasManagementAccess(false);
			setLoadingInicial(false);
			setSubmitErrorScope('contexto');
			setSubmitError('Usuário não autenticado.');
			return;
		}

		const parsed = JSON.parse(localData);
		const authToken = parsed?.token || '';
		setToken(authToken);

		const cargoNormalizado = String(parsed?.cargo || '').toLowerCase();
		const permissaoUsuario = Array.isArray(parsed?.permissao)
			? parsed.permissao
			: [parsed?.permissao].filter(Boolean);
		const isDeveloper = isDeveloperUser(parsed);
		const canEditMedica = hasNamedPermission(
			permissaoUsuario,
			'EscalaMedicaEdicao',
		);
		const canEditEnfermagem = canManageEscalaEnfermagem(parsed);
		const nextEscalaViewMode = resolveEscalaViewModeForUser(
			escalaViewModeOrigemSolicitada,
			parsed,
		);
		const canAccessSelectedScale =
			nextEscalaViewMode === 'enfermagem'
				? canEditEnfermagem
				: isDeveloper || canEditMedica;
		const canManageScreen = canAccessSelectedScale;
		setHasManagementAccess(canManageScreen);
		setEscalaViewMode(nextEscalaViewMode);

		if (!canManageScreen) {
			setLoadingInicial(false);
			setSubmitErrorScope('contexto');
			setSubmitError('Você não possui permissão para acessar esta tela.');
			return;
		}

		const usuarioNormalizado = String(
			parsed?.username || parsed?.nome || '',
		).toLowerCase();
		const unidadeNormalizada = String(parsed?.unidade || '')
			.trim()
			.toLowerCase();
		const buscaGeralCatalogos =
			isDeveloper ||
			cargoNormalizado === 'subhue' ||
			usuarioNormalizado.includes('subhue') ||
			unidadeNormalizada === 'subhue' ||
			unidadeNormalizada.length === 0;
		setShowUnidadeFilter(buscaGeralCatalogos);

		const unidadePreferida = String(
			parsed?.cnes ||
				parsed?.cnes_estabelecimento ||
				parsed?.cnesEstabelecimento ||
				'',
		).trim();
		setUserCnes(unidadePreferida);

		const cachedFilters = getCachedProfessionalManagementFilters();
		const cachedContexto =
			cachedFilters?.scope === nextEscalaViewMode
				? cachedFilters?.contexto || {}
				: {};
		const cachedEstabelecimento = String(
			cachedContexto?.estabelecimento || '',
		).trim();
		const canUseCachedEstabelecimento =
			buscaGeralCatalogos ||
			!unidadePreferida ||
			cachedEstabelecimento === unidadePreferida;
		const nextEstabelecimento =
			(canUseCachedEstabelecimento && cachedEstabelecimento) ||
			unidadePreferida ||
			'';
		const nextSetor = String(cachedContexto?.setor || '').trim();
		// A competência é sempre operacional: filtros em cache podem restaurar
		// unidade, setor e busca, mas nunca uma competência anterior.
		const nextMesReferencia = getDefaultContexto().mes_referencia;

		setContexto((prev) => ({
			...prev,
			estabelecimento: nextEstabelecimento || prev.estabelecimento,
			setor: nextSetor || prev.setor,
			mes_referencia: nextMesReferencia || prev.mes_referencia,
		}));
		setSearchTerm(String(cachedFilters?.searchTerm || ''));

		(async () => {
			if (!authToken) {
				setSubmitErrorScope('contexto');
				setSubmitError('Token não encontrado.');
				setLoadingInicial(false);
				return;
			}
			await loadCatalogosBase({
				authToken,
				estabelecimento: nextEstabelecimento || undefined,
				filtrarUnidadesComEscala: buscaGeralCatalogos,
			});
			setLoadingInicial(false);
		})();
	}, [escalaViewModeOrigemSolicitada, loadCatalogosBase]);

	useEffect(() => {
		if (!token || !hasManagementAccess) return;

		persistCachedProfessionalManagementFilters({
			scope,
			contexto: {
				estabelecimento: String(
					contexto.estabelecimento || userCnes || '',
				).trim(),
				setor: String(contexto.setor || '').trim(),
				mes_referencia:
					formatDateInputToISO(contexto.mes_referencia || '') ||
					getDefaultContexto().mes_referencia,
			},
			searchTerm: String(searchTerm || ''),
		});
	}, [
		contexto.estabelecimento,
		contexto.mes_referencia,
		contexto.setor,
		hasManagementAccess,
		scope,
		searchTerm,
		token,
		userCnes,
	]);

	useEffect(() => {
		if (!token) return;
		loadVinculosEscala(token);
	}, [token, loadVinculosEscala]);

	useEffect(() => {
		if (!token) return;
		if (!contexto?.estabelecimento) return;
		loadCatalogosBase({
			authToken: token,
			estabelecimento: contexto.estabelecimento,
			filtrarUnidadesComEscala: showUnidadeFilter,
		});
	}, [
		token,
		contexto.estabelecimento,
		scope,
		showUnidadeFilter,
		loadCatalogosBase,
	]);

	useEffect(() => {
		if (!token || !contextoAplicado) return;
		if (escalaReloadScopeRef.current === scope) return;
		escalaReloadScopeRef.current = scope;
		const targetContexto = {
			estabelecimento: getEstabelecimentoIdValue(
				contextoAplicado.estabelecimento,
			),
			setor: isSetorTodosValue(contextoAplicado.setor)
				? undefined
				: Number(contextoAplicado.setor),
			mes_referencia: contextoAplicado.mes_referencia,
		};
		loadOpcoesAmbiente({ authToken: token, targetContexto });
		loadAmbiente({ authToken: token, targetContexto, showLoading: true });
	}, [escalaViewMode, token, contextoAplicado, scope]);

	useEffect(() => {
		if (!token || !hasManagementAccess || loadingInicial) return;

		const estabelecimentoSelecionado = String(
			contexto.estabelecimento || userCnes || '',
		).trim();
		const mesReferencia =
			formatDateInputToISO(contexto.mes_referencia || '') ||
			getDefaultContexto().mes_referencia;

		if (
			validateContextoField(
				'estabelecimento',
				estabelecimentoSelecionado,
				{
					showUnidadeFilter,
				},
			) ||
			validateContextoField('setor', contexto.setor) ||
			!mesReferencia
		) {
			return;
		}

		const targetContexto = {
			estabelecimento: estabelecimentoSelecionado,
			setor: isSetorTodosValue(contexto.setor)
				? ''
				: Number(contexto.setor),
			mes_referencia: mesReferencia,
		};
		const contextKey = buildProfessionalManagementContextKey({
			scope,
			contexto: {
				...targetContexto,
				setor: isSetorTodosValue(contexto.setor)
					? SETOR_TODOS_VALUE
					: targetContexto.setor,
			},
		});

		if (autoLoadedContextKeyRef.current === contextKey) return;

		const timeoutId = setTimeout(() => {
			autoLoadedContextKeyRef.current = contextKey;
			loadOpcoesAmbiente({
				authToken: token,
				targetContexto: {
					...targetContexto,
					setor:
						targetContexto.setor ||
						Number(setorOptions[0]?.id || 0) ||
						undefined,
				},
			});
			loadAmbiente({
				authToken: token,
				targetContexto,
				showLoading: true,
			});
		}, 250);

		return () => clearTimeout(timeoutId);
	}, [
		contexto.estabelecimento,
		contexto.mes_referencia,
		contexto.setor,
		hasManagementAccess,
		loadingInicial,
		scope,
		showUnidadeFilter,
		setorOptions,
		token,
		userCnes,
	]);

	useEffect(() => {
		if (!showUnidadeFilter) {
			setUnidadeSearchTerm('');
			return;
		}

		if (!contexto.estabelecimento) return;

		const unidadeAtual = unidadeOptions.find(
			(unit) => String(unit.id) === String(contexto.estabelecimento),
		);
		if (!unidadeAtual) return;

		const displayValue = formatUnidadeOptionLabel(unidadeAtual);
		if (
			normalizeSearchValue(unidadeSearchTerm) !==
			normalizeSearchValue(displayValue)
		) {
			setUnidadeSearchTerm(displayValue);
		}
	}, [
		showUnidadeFilter,
		contexto.estabelecimento,
		unidadeOptions,
		unidadeSearchTerm,
	]);

	useEffect(() => {
		if (setorInputDisabled) {
			if (setorSearchTerm) {
				setSetorSearchTerm('');
			}
			if (showSetorSuggestions) {
				setShowSetorSuggestions(false);
			}
			return;
		}

		if (!contexto.setor) return;

		const setorAtual = setorOptionsComTodos.find(
			(item) => String(item.id) === String(contexto.setor),
		);
		if (!setorAtual) return;

		const displayValue = formatSetorOptionLabel(setorAtual);
		if (
			normalizeSearchValue(setorSearchTerm) !==
			normalizeSearchValue(displayValue)
		) {
			setSetorSearchTerm(displayValue);
		}
	}, [
		contexto.setor,
		setorInputDisabled,
		setorOptionsComTodos,
		setorSearchTerm,
		showSetorSuggestions,
	]);

	useEffect(() => {
		if (!isEditPanelOpen && editingProfissionalIndex !== null) {
			const timeoutId = setTimeout(() => {
				setEditingProfissionalIndex(null);
			}, 280);
			return () => clearTimeout(timeoutId);
		}
	}, [isEditPanelOpen, editingProfissionalIndex]);

	useEffect(() => {
		if (!selectedLinhaId) {
			setIsEditPanelOpen(false);
			return;
		}
		const linha = linhas.find(
			(item) => String(item?.linha_id) === String(selectedLinhaId),
		);
		if (linha) {
			resetPanelFormByLinha(linha);
		}
	}, [selectedLinhaId, selectedDiaIso, linhas, resetPanelFormByLinha]);

	const clearAppliedContextState = useCallback(() => {
		autoLoadedContextKeyRef.current = '';
		escalaReloadScopeRef.current = null;
		setContextoAplicado(null);
		setAmbiente(null);
		setAmbienteOpcoes(null);
		setSelectedLinhaId('');
		setSelectedDiaIso('');
		setIsEditPanelOpen(false);
		setEditingProfissionalIndex(null);
		gerenciamentoTurnosLoadRequestRef.current += 1;
		setIsGerenciamentoTurnosModalOpen(false);
		setIsGerenciamentoTurnosLoading(false);
		setIsGerenciamentoTurnosReady(false);
		setGerenciamentoTurnosLoadError('');
		setGerenciamentoTurnosLinha(null);
		setGerenciamentoTurnosProfissionalCadastro(null);
		setGerenciamentoTurnosSetoresAbertos({});
		setGerenciamentoTurnosSetoresRemovidos([]);
		setGerenciamentoTurnosSetoresComParticoesLiberadas([]);
		setParticionamentoPendente(null);
		setOpenMiniTabs(getDefaultMiniTabs());
	}, []);

	const handleOpenGerenciamentoTurnosModal = async (linha) => {
		if (!linha || linha?.placeholder || isVacanciaLinha(linha)) return;
		const loadRequestId = ++gerenciamentoTurnosLoadRequestRef.current;
		setSubmitError('');
		setSubmitErrorScope('');
		setProfissionalHorarioSobrepostoPopup(null);
		setIsGerenciamentoTurnosLoading(true);
		setIsGerenciamentoTurnosReady(false);
		setGerenciamentoTurnosLoadError('');
		setGerenciamentoTurnosLinha(linha);
		setGerenciamentoTurnosProfissionalCadastro(null);
		setGerenciamentoTurnosNovoDias([]);
		setGerenciamentoTurnosNovoHorarios({});
		setGerenciamentoTurnosNovoDatas([]);
		setGerenciamentoTurnosSetoresRemovidos([]);
		setGerenciamentoTurnosSetoresComParticoesLiberadas([]);
		setGerenciamentoTurnosNovoSetor('');
		setGerenciamentoTurnosNovoRegime('');
		setGerenciamentoTurnosNovoRecorrencia('SEMANAL');
		setGerenciamentoTurnosNovoHoraInicio('07:00');
		setGerenciamentoTurnosNovoHoraFim('19:00');
		setGerenciamentoTurnosNovoPeriodoRotacao(2);
		setGerenciamentoTurnosNovoSemanasRotacao([[], []]);
		setGerenciamentoTurnosNovoDataInicial(
			formatISOToDisplay(getTodayISODateLocal()),
		);
		setIsGerenciamentoTurnosNovoSetorAberto(false);
		setPlanejamentoConfirmacaoDisponivel(false);
		setPlanejamentoConfirmacaoSolicitacao(0);
		setPlanejamentoNovoSetorForm(null);
		setUsarSugestoesNovoSetor(false);
		setGerenciamentoTurnosErrors({});
		setParticionamentoPendente(null);
		setIsGerenciamentoTurnosModalOpen(true);

		const linhaBase =
			Array.isArray(linha?.setores_linhas) &&
			linha.setores_linhas.length > 0
				? linha.setores_linhas[0]
				: linha;
		const cpf = normalizeCpf(getCpfFromLinha(linhaBase));
		const vinculoProfissionalId = getVinculoIdFromLinha(linhaBase);
		const estabelecimentoBusca = getEstabelecimentoIdValue(
			contexto.estabelecimento ||
				contextoAplicado?.estabelecimento ||
				userCnes,
		);
		try {
			if (!token || !cpf) {
				setGerenciamentoTurnosLoadError(
					'Não foi possível validar os setores e turnos deste profissional.',
				);
				return;
			}

			try {
				const result = await requestBuscarProfissionalEscala({
					token,
					cpf,
					estabelecimento: estabelecimentoBusca || undefined,
					vinculo: vinculoProfissionalId || undefined,
					scope,
				});
				if (
					gerenciamentoTurnosLoadRequestRef.current ===
						loadRequestId &&
					!result?.error &&
					Array.isArray(result) &&
					result.length > 0
				) {
					setGerenciamentoTurnosProfissionalCadastro(
						result[0] || null,
					);
				}
			} catch {
				// O cadastro é complementar; a estrutura da jornada continua sendo
				// carregada abaixo mesmo se essa consulta falhar.
			}

			// As abas do modal representam apenas alocações operacionais ativas.
			// O endpoint de origem é estrutural e pode conter jornadas históricas;
			// por isso não pode compor a listagem visual de setores.
			try {
				const contextoLeitura = {
					...getContextoVisualizacaoRequest(contextoAplicado),
				};
				delete contextoLeitura.setor;
				const ambienteAtual =
					await requestAbrirAmbientePreenchimentoEscala({
						token,
						contexto: contextoLeitura,
						modo: 'detalhado',
						scope,
						forceRefresh: true,
					});
				if (ambienteAtual?.error) {
					throw new Error(
						getErrorMessage(
							ambienteAtual,
							'Não foi possível carregar os setores ativos deste profissional.',
						),
					);
				}
				const linhasAtivas = getLinhasAtivasDoAmbienteParaProfissional({
					ambiente: ambienteAtual,
					linhaReferencia: linha,
					mesReferencia: contextoLeitura?.mes_referencia,
					ocultarEncerradasAteHoje: true,
				});
				const linhaAtualizada =
					consolidateLinhasPorProfissional(
						removerLinhasEstruturaisDuplicadas(linhasAtivas),
					)[0] || null;
				if (
					gerenciamentoTurnosLoadRequestRef.current === loadRequestId
				) {
					setGerenciamentoTurnosLinha(linhaAtualizada);
					if (!linhaAtualizada) {
						// A leitura canônica não encontrou nenhum setor ativo para este
						// profissional. Não mantenha na grade uma linha que não pode ser
						// administrada; isso também protege respostas locais defasadas.
						setLinhasOcultadasAposExclusao((prev) =>
							Array.from(
								new Set([
									...prev,
									getLinhaProfissionalOcultacaoLocalKey(
										linha,
									),
								]),
							),
						);
						setGerenciamentoTurnosLoadError(
							'Não há setores ativos para este profissional nesta competência.',
						);
					}
					setIsGerenciamentoTurnosReady(true);
				}
			} catch {
				if (
					gerenciamentoTurnosLoadRequestRef.current === loadRequestId
				) {
					setGerenciamentoTurnosLoadError(
						'Não foi possível validar os setores e turnos deste profissional. Feche e tente novamente.',
					);
				}
			}
		} finally {
			if (gerenciamentoTurnosLoadRequestRef.current === loadRequestId) {
				setIsGerenciamentoTurnosLoading(false);
			}
		}
	};

	const handlePlanejamentoEnfermagemApplied = async () => {
		const linhaAntesDaAplicacao = gerenciamentoTurnosLinha;
		const ambienteAtualizado = await loadAmbiente({
			authToken: token,
			// `__todos__` é um marcador exclusivo da interface. A rota de
			// enfermagem aceita setor vazio para todos os setores, nunca esse valor.
			targetContexto: getContextoVisualizacaoRequest(contextoAplicado),
			showLoading: false,
			forceRefresh: true,
		});
		if (!ambienteAtualizado || ambienteAtualizado?.error) return;

		const profissionalId = getProfissionalIdFromLinha(
			linhaAntesDaAplicacao,
		);
		const cpf = normalizeCpf(getCpfFromLinha(linhaAntesDaAplicacao));
		const linhasDoProfissional = (ambienteAtualizado?.linhas || []).filter(
			(linha = {}) => {
				const linhaProfissionalId = getProfissionalIdFromLinha(linha);
				const linhaCpf = normalizeCpf(getCpfFromLinha(linha));
				return Boolean(
					(profissionalId &&
						linhaProfissionalId === profissionalId) ||
					(cpf && linhaCpf === cpf),
				);
			},
		);
		const linhaAtualizada =
			consolidateLinhasPorProfissional(linhasDoProfissional)[0] || null;

		if (!linhaAtualizada) {
			setGerenciamentoTurnosLoadError(
				'A escala foi aplicada, mas não foi possível recarregar os setores do profissional.',
			);
			return;
		}

		// Reabre a estrutura canônica retornada pela API. Isso substitui o estado
		// local do formulário e mostra imediatamente o setor recém-criado.
		await handleOpenGerenciamentoTurnosModal(linhaAtualizada);
	};

	const handleCloseGerenciamentoTurnosModal = () => {
		gerenciamentoTurnosLoadRequestRef.current += 1;
		setSubmitError('');
		setSubmitErrorScope('');
		setProfissionalHorarioSobrepostoPopup(null);
		setIsGerenciamentoTurnosModalOpen(false);
		setIsGerenciamentoTurnosLoading(false);
		setIsGerenciamentoTurnosReady(false);
		setGerenciamentoTurnosLoadError('');
		setGerenciamentoTurnosLinha(null);
		setGerenciamentoTurnosProfissionalCadastro(null);
		setGerenciamentoTurnosNovoDias([]);
		setGerenciamentoTurnosNovoHorarios({});
		setGerenciamentoTurnosSetoresEditaveis([]);
		setGerenciamentoTurnosSetoresAbertos({});
		setGerenciamentoTurnosSetoresRemovidos([]);
		setGerenciamentoTurnosSetoresComParticoesLiberadas([]);
		setGerenciamentoTurnosNovoDatas([]);
		setGerenciamentoTurnosNovoSetor('');
		setGerenciamentoTurnosNovoRegime('');
		setGerenciamentoTurnosNovoPeriodoRotacao(2);
		setGerenciamentoTurnosNovoSemanasRotacao([[], []]);
		setGerenciamentoTurnosNovoRecorrencia('SEMANAL');
		setGerenciamentoTurnosNovoHoraInicio('07:00');
		setGerenciamentoTurnosNovoHoraFim('19:00');
		setGerenciamentoTurnosNovoDataInicial('');
		setIsGerenciamentoTurnosNovoSetorAberto(false);
		setPlanejamentoConfirmacaoDisponivel(false);
		setPlanejamentoConfirmacaoSolicitacao(0);
		setPlanejamentoNovoSetorForm(null);
		setUsarSugestoesNovoSetor(false);
		setGerenciamentoTurnosErrors({});
		setParticionamentoPendente(null);
	};

	const handleAbrirGerenciamentoJornadaInterligada = async () => {
		const linhaOriginal = editingProfissionalLinha;
		if (!linhaOriginal || !token) return;
		setIsAbrindoGerenciamentoJornada(true);

		try {
			const estabelecimento = getEstabelecimentoIdValue(
				contextoAplicado?.estabelecimento ||
					contexto.estabelecimento ||
					userCnes,
			);
			const mesReferencia = formatDateInputToISO(
				contextoAplicado?.mes_referencia ||
					contexto.mes_referencia ||
					getDefaultContexto().mes_referencia,
			);
			if (!estabelecimento || !mesReferencia) return;

			const contextoTodos = {
				estabelecimento,
				setor: '',
				mes_referencia: mesReferencia,
			};
			setContexto((prev) => ({
				...prev,
				estabelecimento,
				setor: SETOR_TODOS_VALUE,
				mes_referencia: mesReferencia,
			}));
			setSetorSearchTerm('Todos');
			setShowSetorSuggestions(false);
			setContextoErrors((prev) => ({ ...prev, setor: '' }));
			autoLoadedContextKeyRef.current =
				buildProfessionalManagementContextKey({
					scope,
					contexto: { ...contextoTodos, setor: SETOR_TODOS_VALUE },
				});

			const ambienteTodos = await loadAmbiente({
				authToken: token,
				targetContexto: contextoTodos,
			});
			if (!ambienteTodos || ambienteTodos?.error) return;

			const profissionalId = getProfissionalIdFromLinha(linhaOriginal);
			const cpf = normalizeCpf(getCpfFromLinha(linhaOriginal));
			const linhasProfissional = (ambienteTodos?.linhas || []).filter(
				(linha = {}) => {
					const linhaProfissionalId =
						getProfissionalIdFromLinha(linha);
					const linhaCpf = normalizeCpf(getCpfFromLinha(linha));
					return Boolean(
						(profissionalId &&
							linhaProfissionalId === profissionalId) ||
						(cpf && linhaCpf === cpf),
					);
				},
			);
			const linhaParaGerenciar =
				consolidateLinhasPorProfissional(linhasProfissional)[0] ||
				linhaOriginal;

			handleCloseNovoProfissionalModal();
			await handleOpenGerenciamentoTurnosModal(linhaParaGerenciar);
		} finally {
			setIsAbrindoGerenciamentoJornada(false);
		}
	};

	const toggleGerenciamentoTurnosSetorAberto = (setorId) => {
		setGerenciamentoTurnosSetoresAbertos((prev) => ({
			...prev,
			[setorId]: !prev[setorId],
		}));
	};

	const handleOpenDeleteModalFromGerenciamentoTurnos = (itemOuLinha) => {
		const linhaSetor = getLinhaSetorAcionavel(itemOuLinha);
		if (!linhaSetor) return;
		handleOpenDeleteModal(linhaSetor);
		setIsDeleteFromGerenciamentoTurnos(true);
	};

	const registrarOperacaoGerenciamentoTurnos = () => undefined;

	const updateGerenciamentoTurnosSetor = (
		setorId,
		updater,
		{ operacao, campos } = {},
	) => {
		const itensAtuais =
			gerenciamentoTurnosSetoresEditaveisRef.current.length > 0
				? gerenciamentoTurnosSetoresEditaveisRef.current
				: gerenciamentoTurnosSetoresEditaveis;
		const itemAnterior = itensAtuais.find(
			(item) => String(item.id) === String(setorId),
		);
		const itemAtualizado = itemAnterior ? updater(itemAnterior) : null;
		const proximosItens = itensAtuais.map((item) =>
			String(item.id) === String(setorId) && itemAtualizado
				? itemAtualizado
				: item,
		);
		gerenciamentoTurnosSetoresEditaveisRef.current = proximosItens;
		setGerenciamentoTurnosSetoresEditaveis(proximosItens);
		registrarOperacaoGerenciamentoTurnos({
			operacao,
			setorId,
			itemAnterior,
			itemAtualizado,
			campos,
		});
	};

	const bindGerenciamentoTurnosCicloInput = (setorId, campo) => (node) => {
		const chaveSetor = String(setorId || '');
		if (!chaveSetor) return;
		const inputsDoSetor =
			gerenciamentoTurnosCicloInputsRef.current[chaveSetor] || {};
		if (node) {
			gerenciamentoTurnosCicloInputsRef.current[chaveSetor] = {
				...inputsDoSetor,
				[campo]: node,
			};
			return;
		}
		delete inputsDoSetor[campo];
		if (Object.keys(inputsDoSetor).length === 0) {
			delete gerenciamentoTurnosCicloInputsRef.current[chaveSetor];
		} else {
			gerenciamentoTurnosCicloInputsRef.current[chaveSetor] =
				inputsDoSetor;
		}
	};

	const sincronizarHorariosCicloVisiveis = (item = {}) => {
		const inputsDoSetor =
			gerenciamentoTurnosCicloInputsRef.current[String(item.id || '')] ||
			{};
		return {
			...item,
			horaInicio:
				inputsDoSetor?.horaInicio?.value ?? item.horaInicio ?? '',
			horaFim: inputsDoSetor?.horaFim?.value ?? item.horaFim ?? '',
		};
	};

	const handleGerenciamentoTurnosSetorRegimeChange = (setorId, value) => {
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				regimeValue: value,
			}),
			{
				operacao: 'EDITAR_REGIME_SETOR',
				campos: { regime: value },
			},
		);
	};

	const getGerenciamentoTurnosRecorrenciaOption = (value) =>
		recorrenciaNomeOptions.find(
			(item) => normalizeCode(item?.value || '') === normalizeCode(value),
		);

	const getPresetPlanejamentoDoNovoSetor = () => {
		const option = getGerenciamentoTurnosRecorrenciaOption(
			gerenciamentoTurnosNovoRecorrencia,
		);
		const descricao = normalizeCode(
			[
				gerenciamentoTurnosNovoRecorrencia,
				option?.label,
				option?.nome,
			].filter(Boolean).join(' '),
		);
		if (descricao.includes('24X120') || descricao.includes('24X72')) {
			return 'PERSONALIZADO';
		}
		return option?.preset_ciclo;
	};

	const getConfiguracaoOrigemParaNovoSetor = () => {
		const itensAtuais =
			gerenciamentoTurnosSetoresEditaveisRef.current.length > 0
				? gerenciamentoTurnosSetoresEditaveisRef.current
				: gerenciamentoTurnosSetoresEditaveis;
		const origem =
			itensAtuais.find((item = {}) => item.isSetorBase) ||
			itensAtuais[0] ||
			{};
		return {
			regime: origem.regimeValue ||
				getGerenciamentoTurnosBaseLinha()?.regime_escala?.regime ||
				'',
			recorrencia:
				origem.recorrenciaValue || origem.nomeRecorrencia || 'SEMANAL',
			dataInicial: origem.dataInicialValue ||
				getDataInicialProfissionalEscalaIsoFromLinha(
					getGerenciamentoTurnosBaseLinha(),
				) ||
				formatISOToDisplay(getTodayISODateLocal()),
			horaInicio: origem.horaInicio || '07:00',
			horaFim: origem.horaFim || '19:00',
			periodoRotacao: Math.max(2, Math.min(6, Number(origem.periodoRotacao || 2))),
		};
	};

	const handleAbrirNovoSetorComConfiguracaoDaOrigem = () => {
		const origem = getConfiguracaoOrigemParaNovoSetor();
		setGerenciamentoTurnosNovoRegime(origem.regime);
		setGerenciamentoTurnosNovoRecorrencia(origem.recorrencia);
		setGerenciamentoTurnosNovoDataInicial(origem.dataInicial);
		setGerenciamentoTurnosNovoHoraInicio(origem.horaInicio);
		setGerenciamentoTurnosNovoHoraFim(origem.horaFim);
		setGerenciamentoTurnosNovoPeriodoRotacao(origem.periodoRotacao);
		setGerenciamentoTurnosNovoSemanasRotacao(
			Array.from({ length: origem.periodoRotacao }, () => []),
		);
		setGerenciamentoTurnosNovoDias([]);
		setGerenciamentoTurnosNovoHorarios({});
		setGerenciamentoTurnosNovoDatas([]);
		setIsGerenciamentoTurnosNovoSetorAberto(true);
	};

	const handleGerenciamentoTurnosSetorRecorrenciaChange = (
		setorId,
		value,
	) => {
		const option = getGerenciamentoTurnosRecorrenciaOption(value);
		if (!option) return;
		const isSemanal = option.tipo_recorrencia === 'SEMANAL';
		const isRotacaoPeriodica =
			option.tipo_recorrencia === 'ROTACAO_PERIODICA';
		const defaults = isSemanal
			? null
			: getRecurrencePresetDefaults(option.preset_ciclo);

		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				regimeValue: isSemanal ? 'ROTINA' : item.regimeValue,
				recorrenciaValue: option.value,
				nomeRecorrencia: option.value,
				tipoRecorrencia: option.tipo_recorrencia,
				presetCiclo: option.preset_ciclo,
				diasSemanaValores: isSemanal
					? item.diasSemanaValores || []
					: [],
				horariosByDia: isSemanal ? item.horariosByDia || {} : {},
				periodoRotacao: isRotacaoPeriodica
					? Math.max(2, Math.min(6, Number(item.periodoRotacao || 2)))
					: undefined,
				semanasRotacao: isRotacaoPeriodica
					? Array.from(
							{
								length: Math.max(
									2,
									Math.min(
										6,
										Number(item.periodoRotacao || 2),
									),
								),
							},
							(_, index) => item.semanasRotacao?.[index] || [],
						)
					: [],
				horaInicio: isSemanal
					? item.horaInicio
					: defaults?.hora_inicio || item.horaInicio || '07:00',
				horaFim: isSemanal
					? item.horaFim
					: defaults?.hora_fim || item.horaFim || '19:00',
			}),
			{
				operacao: 'EDITAR_RECORRENCIA_SETOR',
				campos: { recorrencia: option.value },
			},
		);
	};

	const setGerenciamentoTurnosSetorRotacaoSemanas = (setorId, quantidade) => {
		const total = Math.max(2, Math.min(6, Number(quantidade) || 2));
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				periodoRotacao: total,
				semanasRotacao: Array.from(
					{ length: total },
					(_, index) => item.semanasRotacao?.[index] || [],
				),
			}),
			{
				operacao: 'EDITAR_ROTACAO_SEMANAS',
				campos: { periodo_rotacao: total },
			},
		);
	};

	const toggleGerenciamentoTurnosSetorRotacaoDia = (
		setorId,
		semanaIndex,
		diaSemana,
	) => {
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				semanasRotacao: (item.semanasRotacao || []).map(
					(diasSemana, index) =>
						index === semanaIndex
							? normalizeDiasSemanaValues(
									diasSemana.includes(diaSemana)
										? diasSemana.filter(
												(dia) => dia !== diaSemana,
											)
										: [...diasSemana, diaSemana],
								)
							: diasSemana,
				),
			}),
			{
				operacao: 'EDITAR_DIA_ROTACAO',
				campos: { semana_indice: semanaIndex, dia_semana: diaSemana },
			},
		);
		setGerenciamentoTurnosErrors((prev) => ({
			...prev,
			[`${setorId}.rotacao`]: '',
		}));
	};

	const getRascunhoNovoSetorParaLog = (overrides = {}) => ({
		id: 'novo',
		setorId: gerenciamentoTurnosNovoSetor,
		regimeValue: gerenciamentoTurnosNovoRegime,
		recorrenciaValue: gerenciamentoTurnosNovoRecorrencia,
		diasSemanaValores: gerenciamentoTurnosNovoDias,
		horariosByDia: gerenciamentoTurnosNovoHorarios,
		periodoRotacao: gerenciamentoTurnosNovoPeriodoRotacao,
		semanasRotacao: gerenciamentoTurnosNovoSemanasRotacao,
		datasEspecificasEditaveis: gerenciamentoTurnosNovoDatas,
		...overrides,
	});

	const handleGerenciamentoTurnosNovoSetorChange = (setorId) => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const itemAtualizado = getRascunhoNovoSetorParaLog({ setorId });
		setGerenciamentoTurnosNovoSetor(setorId);
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'SELECIONAR_SETOR_NOVO',
			setorId,
			itemAnterior,
			itemAtualizado,
			campos: { setor_id: setorId },
		});
	};

	const handleGerenciamentoTurnosNovoRegimeChange = (regimeValue) => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const itemAtualizado = getRascunhoNovoSetorParaLog({ regimeValue });
		setGerenciamentoTurnosNovoRegime(regimeValue);
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'EDITAR_REGIME_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado,
			campos: { regime: regimeValue },
		});
	};

	const handleGerenciamentoTurnosNovoRecorrenciaChange = (value) => {
		const option = getGerenciamentoTurnosRecorrenciaOption(value);
		if (!option) return;
		const isRotacaoPeriodica =
			option.tipo_recorrencia === 'ROTACAO_PERIODICA';
		const periodoRotacao = isRotacaoPeriodica
			? Math.max(
					2,
					Math.min(6, gerenciamentoTurnosNovoPeriodoRotacao || 2),
				)
			: undefined;
		const semanasRotacao = isRotacaoPeriodica
			? Array.from(
					{ length: periodoRotacao },
					(_, index) =>
						gerenciamentoTurnosNovoSemanasRotacao[index] || [],
				)
			: [];
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const itemAtualizado = getRascunhoNovoSetorParaLog({
			recorrenciaValue: option.value,
			regimeValue:
				option.tipo_recorrencia === 'SEMANAL'
					? 'ROTINA'
					: gerenciamentoTurnosNovoRegime,
			diasSemanaValores:
				option.tipo_recorrencia === 'SEMANAL'
					? gerenciamentoTurnosNovoDias
					: [],
			horariosByDia:
				option.tipo_recorrencia === 'SEMANAL'
					? gerenciamentoTurnosNovoHorarios
					: {},
			periodoRotacao,
			semanasRotacao,
		});
		setGerenciamentoTurnosNovoRecorrencia(option.value);
		if (option.tipo_recorrencia === 'SEMANAL') {
			setGerenciamentoTurnosNovoRegime('ROTINA');
		}
		if (option.tipo_recorrencia !== 'SEMANAL') {
			const defaults = getRecurrencePresetDefaults(option.preset_ciclo);
			setGerenciamentoTurnosNovoDias([]);
			setGerenciamentoTurnosNovoHorarios({});
			setGerenciamentoTurnosNovoHoraInicio(
				defaults?.hora_inicio || '07:00',
			);
			setGerenciamentoTurnosNovoHoraFim(defaults?.hora_fim || '19:00');
		}
		setGerenciamentoTurnosNovoPeriodoRotacao(periodoRotacao || 2);
		setGerenciamentoTurnosNovoSemanasRotacao(
			semanasRotacao.length > 0 ? semanasRotacao : [[], []],
		);
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'EDITAR_RECORRENCIA_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado,
			campos: { recorrencia: option.value },
		});
	};

	const setGerenciamentoTurnosNovoRotacaoSemanas = (quantidade) => {
		const total = Math.max(2, Math.min(6, Number(quantidade) || 2));
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const semanasRotacao = Array.from(
			{ length: total },
			(_, index) => gerenciamentoTurnosNovoSemanasRotacao[index] || [],
		);
		setGerenciamentoTurnosNovoPeriodoRotacao(total);
		setGerenciamentoTurnosNovoSemanasRotacao(semanasRotacao);
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'EDITAR_ROTACAO_SEMANAS_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado: getRascunhoNovoSetorParaLog({
				periodoRotacao: total,
				semanasRotacao,
			}),
			campos: { periodo_rotacao: total },
		});
	};

	const toggleGerenciamentoTurnosNovoRotacaoDia = (
		semanaIndex,
		diaSemana,
	) => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const semanasRotacao = gerenciamentoTurnosNovoSemanasRotacao.map(
			(diasSemana, index) =>
				index === semanaIndex
					? normalizeDiasSemanaValues(
							diasSemana.includes(diaSemana)
								? diasSemana.filter((dia) => dia !== diaSemana)
								: [...diasSemana, diaSemana],
						)
					: diasSemana,
		);
		setGerenciamentoTurnosNovoSemanasRotacao(semanasRotacao);
		setGerenciamentoTurnosErrors((prev) => ({
			...prev,
			'novo.rotacao': '',
		}));
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'EDITAR_DIA_ROTACAO_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado: getRascunhoNovoSetorParaLog({ semanasRotacao }),
			campos: { semana_indice: semanaIndex, dia_semana: diaSemana },
		});
	};

	const handleGerenciamentoTurnosSetorCicloHorarioChange = (
		setorId,
		field,
		value,
	) => {
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				[field]: value,
			}),
			{
				operacao: 'EDITAR_HORARIO_CICLO',
				campos: { campo: field, valor: value },
			},
		);
	};

	const handleGerenciamentoTurnosNovoCicloHorarioChange = (field, value) => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const itemAtualizado = getRascunhoNovoSetorParaLog({
			[field]: value,
		});
		if (field === 'horaInicio') {
			setGerenciamentoTurnosNovoHoraInicio(value);
		} else {
			setGerenciamentoTurnosNovoHoraFim(value);
		}
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'EDITAR_HORARIO_CICLO_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado,
			campos: { campo: field, valor: value },
		});
	};

	const toggleGerenciamentoTurnosSetorDia = (setorId, diaSemana) => {
		const itemAtual = (
			gerenciamentoTurnosSetoresEditaveisRef.current.length > 0
				? gerenciamentoTurnosSetoresEditaveisRef.current
				: gerenciamentoTurnosSetoresEditaveis
		).find((item) => String(item.id) === String(setorId));
		if (
			itemAtual?.isSetorBase &&
			(itemAtual?.diasSemanaBloqueadosPorParticaoIntegral || []).includes(
				diaSemana,
			)
		) {
			return;
		}
		const diaJaSelecionado = normalizeDiasSemanaValues(
			itemAtual?.diasSemanaValores,
		).includes(diaSemana);
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => {
				const dias = normalizeDiasSemanaValues(item.diasSemanaValores);
				const isSelected = dias.includes(diaSemana);
				const nextDias = isSelected
					? dias.filter((dia) => dia !== diaSemana)
					: [...dias, diaSemana].sort((a, b) => a - b);
				const nextHorarios = { ...(item.horariosByDia || {}) };
				const nextHorariosOptions = {
					...(item.horariosOptionsByDia || {}),
				};
				const nextHorariosParticionados = {
					...(item.horariosParticionadosByDia || {}),
				};

				if (isSelected) {
					delete nextHorarios[diaSemana];
					delete nextHorariosOptions[diaSemana];
					delete nextHorariosParticionados[diaSemana];
				} else {
					// Um dia recém-adicionado precisa nascer com uma faixa real. Deixá-lo
					// vazio (ou somente com o valor visual 07–19) fazia o preview perder
					// o dia ou tentar uma partição fora da jornada de origem. Usamos a
					// primeira faixa já configurada neste setor; assim a nova ocorrência
					// continua coberta pelo padrão-base e pode ser validada pela API.
					const horarioReferencia = Object.values(
						item.horariosByDia || {},
					).find((horario = {}) => {
						return Boolean(
							formatHourInputToTimeValue(horario?.hora_inicio) &&
							formatHourInputToTimeValue(horario?.hora_fim),
						);
					});
					const horaInicioReferencia =
						formatHourInputToTimeValue(
							horarioReferencia?.hora_inicio || item.horaInicio,
						) || '07:00';
					const horaFimReferencia =
						formatHourInputToTimeValue(
							horarioReferencia?.hora_fim || item.horaFim,
						) || '19:00';
					nextHorarios[diaSemana] = {
						hora_inicio: horaInicioReferencia,
						hora_fim: horaFimReferencia,
					};
					nextHorariosOptions[diaSemana] = [nextHorarios[diaSemana]];
					delete nextHorariosParticionados[diaSemana];
				}

				return {
					...item,
					diasSemanaValores: nextDias,
					horariosByDia: nextHorarios,
					horariosOptionsByDia: nextHorariosOptions,
					horariosParticionadosByDia: nextHorariosParticionados,
				};
			},
			{
				operacao: diaJaSelecionado
					? 'REMOVER_DIA_SEMANAL_SETOR'
					: 'ADICIONAR_DIA_SEMANAL_SETOR',
				campos: { dia_semana: diaSemana },
			},
		);
	};

	const handleGerenciamentoTurnosSetorHorarioChange = (
		setorId,
		diaSemana,
		field,
		value,
	) => {
		const itemAtual = (
			gerenciamentoTurnosSetoresEditaveisRef.current.length > 0
				? gerenciamentoTurnosSetoresEditaveisRef.current
				: gerenciamentoTurnosSetoresEditaveis
		).find((item) => String(item.id) === String(setorId));
		if (
			itemAtual?.isSetorBase &&
			(itemAtual?.diasSemanaBloqueadosPorParticaoIntegral || []).includes(
				diaSemana,
			)
		) {
			return;
		}
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				horariosByDia: {
					...(item.horariosByDia || {}),
					[diaSemana]: {
						...(item.horariosByDia?.[diaSemana] || {}),
						[field]: value,
					},
				},
			}),
			{
				operacao: 'EDITAR_HORARIO_DIA_SETOR',
				campos: { dia_semana: diaSemana, campo: field, valor: value },
			},
		);
	};

	const handleAdicionarGerenciamentoTurnosSetorData = (setorId) => {
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				datasEspecificasEditaveis: [
					...(Array.isArray(item.datasEspecificasEditaveis)
						? item.datasEspecificasEditaveis
						: []),
					createGerenciamentoTurnosDataExtra(),
				],
			}),
			{
				operacao: 'ADICIONAR_DATA_ESPECIFICA_SETOR',
			},
		);
	};

	const handleGerenciamentoTurnosSetorDataChange = (
		setorId,
		dataId,
		field,
		value,
	) => {
		const itemAtual = gerenciamentoTurnosSetoresEditaveisRef.current.find(
			(item) => String(item.id) === String(setorId),
		);
		const dataAtual = itemAtual?.datasEspecificasEditaveis?.find(
			(item) => String(item.id) === String(dataId),
		);
		if (dataAtual?.readOnly === true || dataAtual?.legado === true) return;
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				datasEspecificasEditaveis: (Array.isArray(
					item.datasEspecificasEditaveis,
				)
					? item.datasEspecificasEditaveis
					: []
				).map((dataItem) =>
					String(dataItem.id) === String(dataId)
						? { ...dataItem, [field]: value }
						: dataItem,
				),
			}),
			{
				operacao: 'EDITAR_DATA_ESPECIFICA_SETOR',
				campos: { data_id: dataId, campo: field, valor: value },
			},
		);
	};

	const handleRemoverGerenciamentoTurnosSetorData = (setorId, dataId) => {
		const itemAtual = gerenciamentoTurnosSetoresEditaveisRef.current.find(
			(item) => String(item.id) === String(setorId),
		);
		const dataAtual = itemAtual?.datasEspecificasEditaveis?.find(
			(item) => String(item.id) === String(dataId),
		);
		if (dataAtual?.readOnly === true || dataAtual?.legado === true) return;
		updateGerenciamentoTurnosSetor(
			setorId,
			(item) => ({
				...item,
				datasEspecificasEditaveis: (Array.isArray(
					item.datasEspecificasEditaveis,
				)
					? item.datasEspecificasEditaveis
					: []
				).filter((dataItem) => String(dataItem.id) !== String(dataId)),
			}),
			{
				operacao: 'REMOVER_DATA_ESPECIFICA_SETOR',
				campos: { data_id: dataId },
			},
		);
	};

	const toggleGerenciamentoTurnosNovoDia = (diaSemana) => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const dias = normalizeDiasSemanaValues(gerenciamentoTurnosNovoDias);
		const diaJaSelecionado = dias.includes(diaSemana);
		const proximosDias = diaJaSelecionado
			? dias.filter((dia) => dia !== diaSemana)
			: [...dias, diaSemana].sort((a, b) => a - b);
		const itemAtualizado = getRascunhoNovoSetorParaLog({
			diasSemanaValores: proximosDias,
		});
		setGerenciamentoTurnosNovoDias(proximosDias);
		registrarOperacaoGerenciamentoTurnos({
			operacao: diaJaSelecionado
				? 'REMOVER_DIA_SEMANAL_NOVO_SETOR'
				: 'ADICIONAR_DIA_SEMANAL_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado,
			campos: { dia_semana: diaSemana },
		});
	};

	const handleGerenciamentoTurnosNovoHorarioChange = (
		diaSemana,
		field,
		value,
	) => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const proximosHorarios = {
			...(gerenciamentoTurnosNovoHorarios || {}),
			[diaSemana]: {
				...(gerenciamentoTurnosNovoHorarios?.[diaSemana] || {}),
				[field]: value,
			},
		};
		setGerenciamentoTurnosNovoHorarios(proximosHorarios);
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'EDITAR_HORARIO_DIA_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado: getRascunhoNovoSetorParaLog({
				horariosByDia: proximosHorarios,
			}),
			campos: { dia_semana: diaSemana, campo: field, valor: value },
		});
	};

	const handleAdicionarGerenciamentoTurnosNovoData = () => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const proximasDatas = [
			...gerenciamentoTurnosNovoDatas,
			createGerenciamentoTurnosDataExtra(),
		];
		setGerenciamentoTurnosNovoDatas(proximasDatas);
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'ADICIONAR_DATA_ESPECIFICA_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado: getRascunhoNovoSetorParaLog({
				datasEspecificasEditaveis: proximasDatas,
			}),
		});
	};

	const handleGerenciamentoTurnosNovoDataChange = (dataId, field, value) => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const proximasDatas = gerenciamentoTurnosNovoDatas.map((dataItem) =>
			String(dataItem.id) === String(dataId)
				? { ...dataItem, [field]: value }
				: dataItem,
		);
		setGerenciamentoTurnosNovoDatas(proximasDatas);
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'EDITAR_DATA_ESPECIFICA_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado: getRascunhoNovoSetorParaLog({
				datasEspecificasEditaveis: proximasDatas,
			}),
			campos: { data_id: dataId, campo: field, valor: value },
		});
	};

	const handleRemoverGerenciamentoTurnosNovoData = (dataId) => {
		const itemAnterior = getRascunhoNovoSetorParaLog();
		const proximasDatas = gerenciamentoTurnosNovoDatas.filter(
			(dataItem) => String(dataItem.id) !== String(dataId),
		);
		setGerenciamentoTurnosNovoDatas(proximasDatas);
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'REMOVER_DATA_ESPECIFICA_NOVO_SETOR',
			setorId: gerenciamentoTurnosNovoSetor,
			itemAnterior,
			itemAtualizado: getRascunhoNovoSetorParaLog({
				datasEspecificasEditaveis: proximasDatas,
			}),
			campos: { data_id: dataId },
		});
	};

	const resetGerenciamentoTurnosNovoSetor = () => {
		setGerenciamentoTurnosNovoDias([]);
		setGerenciamentoTurnosNovoHorarios({});
		setGerenciamentoTurnosNovoDatas([]);
		setGerenciamentoTurnosNovoSetor('');
		setGerenciamentoTurnosNovoRegime('');
		setGerenciamentoTurnosNovoRecorrencia('SEMANAL');
		setGerenciamentoTurnosNovoHoraInicio('07:00');
		setGerenciamentoTurnosNovoHoraFim('19:00');
		setGerenciamentoTurnosNovoPeriodoRotacao(2);
		setGerenciamentoTurnosNovoSemanasRotacao([[], []]);
		setGerenciamentoTurnosNovoDataInicial(
			formatISOToDisplay(getTodayISODateLocal()),
		);
		setIsGerenciamentoTurnosNovoSetorAberto(false);
		setPlanejamentoNovoSetorForm(null);
		setUsarSugestoesNovoSetor(false);
	};

	const resolveGerenciamentoTurnosVinculoTipoId = (
		linha = {},
		profissionalCadastro = {},
	) => {
		const vinculoOpcao = findVinculoOpcaoByNome(
			vinculoOpcoes,
			getNomeVinculoFromProfissional(profissionalCadastro) ||
				getNomeVinculoFromLinha(linha),
		);
		return (
			Number(
				vinculoOpcao?.value ||
					vinculoOpcao?.id ||
					getVinculoTipoIdFromProfissional(profissionalCadastro) ||
					getVinculoTipoIdFromLinha(linha) ||
					0,
			) || undefined
		);
	};

	const getGerenciamentoTurnosBaseLinha = () => {
		// A ordem das abas é a ordem de criação da jornada. A primeira marcada
		// como base é sempre a referência dos próximos recortes; um destino ou
		// uma aba criada depois nunca deve assumir esse papel automaticamente.
		const itemBase = gerenciamentoTurnosSetoresEditaveis.find(
			(item = {}) => item?.isSetorBase,
		);
		if (itemBase?.sourceLinha) return itemBase.sourceLinha;
		const linhas = Array.isArray(gerenciamentoTurnosLinha?.setores_linhas)
			? gerenciamentoTurnosLinha.setores_linhas
			: [gerenciamentoTurnosLinha].filter(Boolean);
		return linhas.find(isLinhaOrigemDeSplit) || linhas[0] || {};
	};

	const carregarEstadoAlocacaoOrigemDoProfissional = async (
		linhaReferencia = getGerenciamentoTurnosBaseLinha(),
		{ alocacaoOrigemId: alocacaoOrigemIdInformada = '' } = {},
	) => {
		const setorReferencia = Number(getLinhaSetorId(linhaReferencia) || 0);
		const alocacaoOrigemId =
			String(alocacaoOrigemIdInformada || '').trim() ||
			getAlocacaoOrigemId(linhaReferencia);
		const response = await requestAbrirAlocacaoOrigemAmbienteEscala({
			token,
			contexto: {
				...getContextoVisualizacaoRequest(contextoAplicado),
				setor:
					setorReferencia ||
					getContextoVisualizacaoRequest(contextoAplicado).setor,
			},
			alocacaoOrigemId,
			scope,
			// Não podemos usar uma representação revalidada (304) para calcular
			// partições: o corpo contém os UUIDs e a versão exigidos pelo preview.
			forceRefresh: true,
		});
		if (response?.error) {
			throw new Error(
				getErrorMessage(
					response,
					'Não foi possível carregar o estado estrutural da jornada.',
				),
			);
		}

		const profissionalId = getProfissionalIdFromLinha(linhaReferencia);
		const cpfProfissional = normalizeCpf(getCpfFromLinha(linhaReferencia));
		const alocacoesOrigem = getAlocacoesOrigemFromPayload(response);
		const vinculoProfissionalId = Number(
			getVinculoIdFromLinha(linhaReferencia) || 0,
		);
		const alocacoesDoProfissional = alocacoesOrigem.filter(
			(alocacao = {}) =>
				Boolean(
					(vinculoProfissionalId &&
						Number(alocacao?.vinculo_profissional_id || 0) ===
							vinculoProfissionalId) ||
					(profissionalId &&
						Number(alocacao?.profissional_id || 0) ===
							profissionalId),
				),
		);
		// Em linhas antigas o resumo pode não trazer profissional/vínculo, mas a
		// própria ação de validação informa a chave da origem. Nesse caso, a
		// chave é suficiente para recuperar somente o agregado correspondente.
		const alocacoesEstruturais =
			alocacoesDoProfissional.length > 0
				? alocacoesDoProfissional
				: alocacaoOrigemId
					? alocacoesOrigem.filter(
							(alocacao = {}) =>
								String(
									alocacao?.alocacao_origem_id ||
										alocacao?.id ||
										alocacao?.chave ||
										'',
								).trim() === String(alocacaoOrigemId).trim(),
						)
					: [];
		const linhasRetornadas = [
			...(Array.isArray(response?.linhas) ? response.linhas : []),
			...(Array.isArray(response?.ambiente?.linhas)
				? response.ambiente.linhas
				: []),
		];
		const linhasEnriquecidas = enriquecerLinhasComAlocacoesOrigem(
			linhasRetornadas,
			alocacoesOrigem,
		);
		const linhasEstruturais = alocacoesEstruturais.flatMap(
			buildLinhasEstruturaisDaAlocacaoOrigem,
		);
		const linhasProfissional = getLinhasParticionamento([
			...linhasEnriquecidas,
			...linhasEstruturais,
		]).filter((linha = {}) => {
			const cpfLinha = normalizeCpf(getCpfFromLinha(linha));
			const profissionalLinhaId = getProfissionalIdFromLinha(linha);
			const vinculoLinhaId = Number(getVinculoIdFromLinha(linha) || 0);
			return Boolean(
				(alocacaoOrigemId &&
					String(getAlocacaoOrigemId(linha) || '').trim() ===
						String(alocacaoOrigemId).trim()) ||
				(vinculoProfissionalId &&
					vinculoLinhaId === vinculoProfissionalId) ||
				(profissionalId && profissionalLinhaId === profissionalId) ||
				(cpfProfissional && cpfLinha === cpfProfissional),
			);
		});

		return {
			response,
			alocacoesOrigem,
			alocacoesDoProfissional: alocacoesEstruturais,
			linhasProfissional,
		};
	};

	// O resumo mensal pode omitir `papel_setor`, principalmente após uma
	// atualização de competência. Antes de encerrar uma linha ligada a uma
	// jornada, consultamos o agregado estrutural para usar a linha canônica
	// (e o respectivo regime_vinculo_id) no writer correto. Isto não promove o
	// setor a "base" na interface; serve somente para decidir a operação.
	const resolverLinhaEstruturalParaExclusao = async ({
		linha = {},
		alocacaoOrigemId: alocacaoOrigemIdInformada = '',
	} = {}) => {
		const alocacaoOrigemId = String(
			alocacaoOrigemIdInformada || getAlocacaoOrigemId(linha) || '',
		).trim();
		if (!alocacaoOrigemId && !exigeEdicaoViaAlocacaoOrigem(linha)) {
			return { linha, papelSetor: '' };
		}

		const estadoEstrutural =
			await carregarEstadoAlocacaoOrigemDoProfissional(linha, {
				alocacaoOrigemId,
			});
		const setorId = String(getLinhaSetorId(linha) || '').trim();
		const regimeVinculoId = String(
			getLinhaRegimeVinculoId(linha) || '',
		).trim();
		const linhasDaOrigem = (
			estadoEstrutural?.linhasProfissional || []
		).filter(
			(candidata = {}) =>
				!alocacaoOrigemId ||
				String(getAlocacaoOrigemId(candidata) || '').trim() ===
					alocacaoOrigemId,
		);
		const linhasDoSetor = linhasDaOrigem.filter(
			(candidata = {}) =>
				String(getLinhaSetorId(candidata) || '').trim() === setorId,
		);
		const linhaCanonica =
			linhasDoSetor.find(
				(candidata = {}) =>
					regimeVinculoId &&
					String(getLinhaRegimeVinculoId(candidata) || '').trim() ===
						regimeVinculoId,
			) ||
			linhasDoSetor.find(
				(candidata = {}) =>
					!isLinhaEstruturalAlocacaoOrigem(candidata) &&
					Boolean(getLinhaRegimeVinculoId(candidata)),
			) ||
			linhasDoSetor.find((candidata = {}) =>
				Boolean(getLinhaRegimeVinculoId(candidata)),
			) ||
			linhasDoSetor[0] ||
			linha;

		return {
			linha: linhaCanonica,
			papelSetor: isLinhaDestinoDeSplit(linhaCanonica)
				? 'DESTINO'
				: isLinhaOrigemDeSplit(linhaCanonica)
					? 'ORIGEM'
					: '',
			estadoEstrutural,
		};
	};

	const reconciliarLinhaEstruturalAusente = ({
		linha = {},
		estadoEstrutural = {},
	} = {}) => {
		const alocacaoOrigemId = String(
			getAlocacaoOrigemId(linha) || '',
		).trim();
		const setorId = String(getLinhaSetorId(linha) || '').trim();
		const vinculoId = Number(getVinculoIdFromLinha(linha) || 0);
		const profissionalId = getProfissionalIdFromLinha(linha);
		if (!alocacaoOrigemId && !setorId) return false;

		const aindaExiste = (estadoEstrutural?.alocacoesOrigem || []).some(
			(alocacao = {}) => {
				const idAtual = String(
					alocacao?.alocacao_origem_id ||
						alocacao?.id ||
						alocacao?.chave ||
						'',
				).trim();
				if (alocacaoOrigemId) return idAtual === alocacaoOrigemId;

				const setorOrigemId = String(
					alocacao?.setor_origem?.id ||
						alocacao?.setor_origem_id ||
						'',
				).trim();
				const mesmoVinculo =
					!vinculoId ||
					Number(alocacao?.vinculo_profissional_id || 0) ===
						vinculoId;
				const mesmoProfissional =
					!profissionalId ||
					Number(alocacao?.profissional_id || 0) ===
						Number(profissionalId);
				return (
					setorOrigemId === setorId &&
					mesmoVinculo &&
					mesmoProfissional
				);
			},
		);
		if (aindaExiste) return false;

		const correspondeLinhaAusente = (linhaAtual = {}) => {
			const idAtual = String(
				getAlocacaoOrigemId(linhaAtual) || '',
			).trim();
			if (alocacaoOrigemId) return idAtual === alocacaoOrigemId;
			return (
				String(getLinhaSetorId(linhaAtual) || '').trim() === setorId &&
				(!vinculoId ||
					Number(getVinculoIdFromLinha(linhaAtual) || 0) ===
						vinculoId)
			);
		};

		setGerenciamentoTurnosSetoresEditaveis((atual) =>
			atual.filter(
				(item = {}) => !correspondeLinhaAusente(item?.sourceLinha),
			),
		);
		setGerenciamentoTurnosLinha((atual) => {
			if (!atual) return atual;
			if (Array.isArray(atual?.setores_linhas)) {
				return {
					...atual,
					setores_linhas: atual.setores_linhas.filter(
						(linhaAtual = {}) =>
							!correspondeLinhaAusente(linhaAtual),
					),
				};
			}
			return correspondeLinhaAusente(atual) ? null : atual;
		});
		setFeedbackMessage(
			'Este setor já não existe na estrutura atual da escala e foi removido da visualização.',
		);
		return true;
	};

	const buildGerenciamentoTurnosPayloadData = (item = {}) => {
		const dimensionField =
			modalidade === 'ENFERMAGEM' ? 'categoria_id' : 'especialidade_id';
		// A dimensão é um atributo do vínculo. Em uma linha consolidada pode haver
		// especialidade/categoria de outro card; priorizamos o valor explícito do
		// rascunho e, depois, o vínculo da linha selecionada.
		const dimensionValue = String(
			item?.dimensionValue ||
				item?.[dimensionField] ||
				getEspecialidadeIdFromLinha(
					item.sourceLinha || getGerenciamentoTurnosBaseLinha(),
				) ||
				'',
		);
		const dataInicialInformada = formatDateInputToISO(
			item.dataInicialValue || item.dataInicial,
		);
		// A data herdada deve seguir para a API como foi informada. A validação de
		// vigência pertence ao backend; não antecipamos nem substituímos a data
		// localmente pelo dia atual.
		const dataInicialIso = getVigenciaInicioParaEdicao({
			isNovo: item?.id === 'novo',
			dataInformada: dataInicialInformada,
			dataMinima: '',
		});
		const diasSemana = normalizeDiasSemanaValues(item.diasSemanaValores);
		const horariosByDia = diasSemana.reduce((acc, dia) => {
			const horario = item.horariosByDia?.[dia] || {};
			acc[dia] = {
				hora_inicio: horario.hora_inicio || '',
				hora_fim: horario.hora_fim || '',
			};
			return acc;
		}, {});
		// Opções comuns são apenas o histórico visual do campo. Já uma jornada
		// repartida representa faixas estruturais reais e todas elas precisam ser
		// preservadas ao salvar o restante do setor.
		const weeklyOccurrences = diasSemana.flatMap((diaSemana) => {
			const faixasParticionadas = Array.isArray(
				item?.horariosParticionadosByDia?.[diaSemana],
			)
				? item.horariosParticionadosByDia[diaSemana]
				: [];
			const horarios =
				faixasParticionadas.length > 1
					? faixasParticionadas
					: [horariosByDia[diaSemana] || {}];
			return horarios
				.map((horario = {}) => ({
					dia_semana: diaSemana,
					hora_inicio: formatHourInputToTimeValue(
						horario?.hora_inicio || horario?.horaInicio,
					),
					hora_fim: formatHourInputToTimeValue(
						horario?.hora_fim || horario?.horaFim,
					),
				}))
				.filter((horario) => horario.hora_inicio && horario.hora_fim);
		});
		const datasExtras = (
			Array.isArray(item.datasEspecificasEditaveis)
				? item.datasEspecificasEditaveis
				: []
		)
			// Datas de versões encerradas são somente leitura: reenviá-las numa
			// edição do regime vigente faria a nova versão alterar o passado.
			.filter(
				(dataItem = {}) =>
					dataItem?.legado !== true &&
					dataItem?.readOnly !== true &&
					dataItem?.origem !== 'troca',
			)
			.map((dataItem = {}) => ({
				selected_date: formatDateInputToISO(dataItem.data),
				hora_inicio: dataItem.horaInicio,
				hora_fim: dataItem.horaFim,
			}))
			.filter((dataItem) => dataItem.selected_date);
		const hasDiasRecorrentes = diasSemana.length > 0;
		const hasSomenteDatasEspecificas =
			!hasDiasRecorrentes && datasExtras.length > 0;
		const recorrenciaOption = getGerenciamentoTurnosRecorrenciaOption(
			item.recorrenciaValue || item.nomeRecorrencia,
		);
		const tipoRecorrencia =
			normalizeCode(
				item.tipoRecorrencia || recorrenciaOption?.tipo_recorrencia,
			) || (hasSomenteDatasEspecificas ? 'DATAS_ESPECIFICAS' : 'SEMANAL');
		const isRecorrenciaSemanal = tipoRecorrencia === 'SEMANAL';
		const isRotacaoPeriodica = tipoRecorrencia === 'ROTACAO_PERIODICA';
		const periodoRotacao = isRotacaoPeriodica
			? Math.max(
					2,
					Math.min(
						6,
						Number(
							item.periodoRotacao ||
								item.semanasRotacao?.length ||
								2,
						),
					),
				)
			: undefined;
		const presetCiclo =
			getPayloadPresetCiclo(
				item.presetCiclo || recorrenciaOption?.preset_ciclo,
			) || (isRecorrenciaSemanal ? 'SEMANAL' : 'PERSONALIZADO');
		const source = {
			regime: normalizeCode(item.regimeValue || item.regime),
			vigencia_inicio: dataInicialIso,
			data_ancora: dataInicialIso,
			tipo_recorrencia: tipoRecorrencia,
			preset_ciclo: presetCiclo,
			dias_semana:
				isRecorrenciaSemanal && hasDiasRecorrentes ? diasSemana : [],
			semanas_rotacao: isRotacaoPeriodica
				? Array.isArray(item.semanasRotacao)
					? item.semanasRotacao
					: []
				: [],
			periodo_semanas: periodoRotacao,
			dias_semana_horarios: horariosByDia,
			datas_extras: datasExtras,
			datas_especificas: datasExtras.map(
				(dataItem) => dataItem.selected_date,
			),
			hora_inicio:
				(isRecorrenciaSemanal
					? horariosByDia[diasSemana[0]]?.hora_inicio
					: item.horaInicio) ||
				datasExtras[0]?.hora_inicio ||
				item.horaInicio ||
				'07:00',
			hora_fim:
				(isRecorrenciaSemanal
					? horariosByDia[diasSemana[0]]?.hora_fim
					: item.horaFim) ||
				datasExtras[0]?.hora_fim ||
				item.horaFim ||
				'19:00',
		};
		const regimePayload = buildRegimePayload({
			source: {
				...source,
				ocorrencias: weeklyOccurrences,
			},
			dimensionField,
			dimensionValue,
			fallbackVigenciaInicio: dataInicialIso,
			fallbackDataAncora: dataInicialIso,
			forceDefaultRegime: false,
			useFallbackVigenciaInicio: true,
			useFallbackDataAncora: true,
			incluirDiaInicialSemanal: false,
		});
		const extraDatePadroes = datasExtras
			.map((dataItem = {}) => ({
				tipo_recorrencia: 'DATAS_ESPECIFICAS',
				preset_ciclo: 'PERSONALIZADO',
				datas_especificas: [dataItem.selected_date],
				hora_inicio:
					formatHourInputToTimeValue(dataItem.hora_inicio) ||
					undefined,
				hora_fim:
					formatHourInputToTimeValue(dataItem.hora_fim) || undefined,
			}))
			.filter(
				(padrao) =>
					padrao.datas_especificas?.[0] &&
					padrao.hora_inicio &&
					padrao.hora_fim,
			);

		if (extraDatePadroes.length > 0) {
			regimePayload.padroes_alocacao = [
				...(Array.isArray(regimePayload.padroes_alocacao)
					? regimePayload.padroes_alocacao
					: []),
				...extraDatePadroes,
			];
		}

		return {
			dimensionField,
			dimensionValue,
			dataInicialIso,
			regimePayload,
			weeklyOccurrences,
			diasSemana,
			datasExtras,
		};
	};

	const buildGerenciamentoTurnosActionCommand = ({
		item = {},
		linhaSetor = {},
		regimePayload = {},
		dataInicialIso = '',
		regimeVinculoId = 0,
	} = {}) => {
		const originalSnapshot = item?.originalSnapshot || {};
		const currentSnapshot = getGerenciamentoTurnosItemSnapshot(item);
		const changedFields = Object.keys(currentSnapshot).filter(
			(key) =>
				String(currentSnapshot[key] || '') !==
				String(originalSnapshot[key] || ''),
		);
		// O catálogo de opções é somente visual. Ele não representa uma mudança
		// estrutural que deva gerar uma ação no backend.
		const meaningfulFields = changedFields.filter(
			(key) => key !== 'horariosOptionsByDia',
		);
		if (meaningfulFields.length === 0) return null;

		// ALTERAR_TURNO aceita apenas um par início/fim. Qualquer ajuste de
		// regime, recorrência, vigência ou horários por dia precisa seguir
		// ALTERAR_REGIME para que a API preserve os padrões canônicos.
		const requiresRegimeAction = meaningfulFields.some((key) =>
			[
				'regime',
				'tipoRecorrencia',
				'presetCiclo',
				'periodoRotacao',
				'semanasRotacao',
				'dataInicial',
				'diasSemana',
				'horariosByDia',
				'datasEspecificas',
			].includes(key),
		);
		const contexto = {
			estabelecimento: getEstabelecimentoIdValue(
				contextoAplicado?.estabelecimento,
			),
			setor:
				Number(item?.setorId || getLinhaSetorId(linhaSetor) || 0) ||
				undefined,
			mes_referencia:
				formatDateInputToISO(contextoAplicado?.mes_referencia) ||
				undefined,
		};
		const base = {
			contexto,
			acao: requiresRegimeAction ? 'ALTERAR_REGIME' : 'ALTERAR_TURNO',
			regime_vinculo_id: Number(regimeVinculoId || 0) || undefined,
		};

		if (!requiresRegimeAction) {
			return {
				...base,
				hora_inicio:
					formatHourInputToTimeValue(regimePayload?.hora_inicio) ||
					undefined,
				hora_fim:
					formatHourInputToTimeValue(regimePayload?.hora_fim) ||
					undefined,
			};
		}

		const {
			categoria_id: _categoriaId,
			especialidade_id: _especialidadeId,
			regime_vinculo_id: _regimeVinculoId,
			ocorrencias: _ocorrencias,
			...configuracaoRegime
		} = regimePayload || {};

		return Object.fromEntries(
			Object.entries({
				...base,
				...configuracaoRegime,
				vigencia_inicio:
					formatDateInputToISO(configuracaoRegime?.vigencia_inicio) ||
					dataInicialIso ||
					undefined,
			}).filter(([, value]) => value !== undefined),
		);
	};

	const validateGerenciamentoTurnosItem = (item = {}, prefix = '') => {
		const errors = {};
		const dataInicialIso = formatDateInputToISO(
			item.dataInicialValue || item.dataInicial,
		);
		const diasSemana = normalizeDiasSemanaValues(item.diasSemanaValores);
		const datasExtras = Array.isArray(item.datasEspecificasEditaveis)
			? item.datasEspecificasEditaveis
			: [];
		const recorrenciaOption = getGerenciamentoTurnosRecorrenciaOption(
			item.recorrenciaValue || item.nomeRecorrencia,
		);
		const tipoRecorrencia =
			normalizeCode(
				item.tipoRecorrencia || recorrenciaOption?.tipo_recorrencia,
			) || 'SEMANAL';
		const isRecorrenciaSemanal = tipoRecorrencia === 'SEMANAL';
		const isRotacaoPeriodica = tipoRecorrencia === 'ROTACAO_PERIODICA';
		const semanasRotacao = Array.isArray(item.semanasRotacao)
			? item.semanasRotacao
			: [];

		if (!normalizeCode(item.regimeValue || item.regime)) {
			errors[`${prefix}regime`] = 'Selecione o regime.';
		}
		if (!dataInicialIso) {
			errors[`${prefix}dataInicial`] = 'Informe a data inicial.';
		}
		const hasDiasInvalidos =
			isRecorrenciaSemanal &&
			diasSemana.some((dia) => {
				const horario = item.horariosByDia?.[dia] || {};
				return !horario.hora_inicio || !horario.hora_fim;
			});
		if (hasDiasInvalidos) {
			errors[`${prefix}dias`] =
				'Preencha hora inicial e final nos dias selecionados.';
		}
		const hasDataExtraInvalida = datasExtras.some(
			(dataItem) =>
				dataItem?.readOnly !== true &&
				dataItem?.legado !== true &&
				(!formatDateInputToISO(dataItem.data) ||
					!dataItem.horaInicio ||
					!dataItem.horaFim),
		);
		if (hasDataExtraInvalida) {
			errors[`${prefix}datasExtras`] =
				'Preencha data, hora inicial e hora final nas datas específicas.';
		}
		if (
			isRecorrenciaSemanal &&
			diasSemana.length === 0 &&
			datasExtras.length === 0
		) {
			errors[`${prefix}dias`] =
				'Selecione ao menos um dia recorrente ou uma data específica.';
		}
		if (
			!isRecorrenciaSemanal &&
			(!formatHourInputToTimeValue(item.horaInicio) ||
				!formatHourInputToTimeValue(item.horaFim))
		) {
			errors[`${prefix}ciclo`] =
				'Informe hora inicial e final para o ciclo.';
		}
		if (
			isRotacaoPeriodica &&
			(semanasRotacao.length < 2 ||
				!semanasRotacao.some(
					(diasSemana) =>
						normalizeDiasSemanaValues(diasSemana).length > 0,
				))
		) {
			errors[`${prefix}rotacao`] =
				'Selecione ao menos um dia na rotação de duas ou mais semanas.';
		}

		return errors;
	};

	const prepararParticionamentoNovoSetor = async (
		novoItem = {},
		linhaOrigemForcada = null,
		{
			alocacaoOrigemId: alocacaoOrigemIdInformada = '',
			acaoPreferida = null,
			originatedFromProfessionalModal = false,
			idempotencyKey = '',
			usarSugestoes = false,
		} = {},
	) => {
		let linhaOrigem =
			linhaOrigemForcada || getGerenciamentoTurnosBaseLinha();
		let linhasProfissional = Array.isArray(
			gerenciamentoTurnosLinha?.setores_linhas,
		)
			? getLinhasParticionamento(gerenciamentoTurnosLinha.setores_linhas)
			: [linhaOrigem];

		// A regra de origem é versionada e não pode ser inferida a partir da
		// projeção mensal que estava aberta no modal. Antes de acrescentar uma
		// partição, buscamos a configuração completa e preservamos todas as
		// partições já existentes daquela mesma origem.
		const estadoEstrutural =
			await carregarEstadoAlocacaoOrigemDoProfissional(linhaOrigem, {
				alocacaoOrigemId: alocacaoOrigemIdInformada,
			});
		if (estadoEstrutural.linhasProfissional.length > 0) {
			const setorOrigemAtual = String(getLinhaSetorId(linhaOrigem) || '');
			const alocacaoOrigemAtual = String(
				alocacaoOrigemIdInformada ||
					getAlocacaoOrigemId(linhaOrigem) ||
					'',
			);
			const correspondeAoSetorBase = (linha = {}) =>
				Boolean(
					String(getLinhaSetorId(linha) || '') === setorOrigemAtual &&
					(!alocacaoOrigemAtual ||
						String(getAlocacaoOrigemId(linha) || '') ===
							alocacaoOrigemAtual),
				);
			linhaOrigem =
				(alocacaoOrigemAtual &&
					estadoEstrutural.linhasProfissional.find(
						(linha = {}) =>
							isLinhaEstruturalAlocacaoOrigem(linha) &&
							String(getAlocacaoOrigemId(linha) || '') ===
								alocacaoOrigemAtual,
					)) ||
				estadoEstrutural.linhasProfissional.find(
					(linha = {}) =>
						correspondeAoSetorBase(linha) &&
						isLinhaEstruturalAlocacaoOrigem(linha),
				) ||
				estadoEstrutural.linhasProfissional.find(
					correspondeAoSetorBase,
				) ||
				linhaOrigem;
			linhasProfissional = estadoEstrutural.linhasProfissional;
		}
		const { regimePayload } = buildGerenciamentoTurnosPayloadData({
			...novoItem,
			sourceLinha: linhaOrigem,
		});
		const setorOrigem = Number(getLinhaSetorId(linhaOrigem) || 0);
		const setorDestino = Number(novoItem?.setorId || 0);
		const regimeVinculoId = Number(linhaOrigem?.regime_vinculo_id || 0);
		const alocacaoOrigemId =
			String(alocacaoOrigemIdInformada || '').trim() ||
			getAlocacaoOrigemId(linhaOrigem);
		const alocacaoOrigemAtiva = estadoEstrutural.alocacoesOrigem.find(
			(alocacao = {}) =>
				String(
					alocacao?.id ||
						alocacao?.alocacao_origem_id ||
						alocacao?.chave ||
						'',
				) === String(alocacaoOrigemId || ''),
		);
		const acaoEdicaoDireta =
			acaoPreferida ||
			getAcaoParaAdicionarSetorNaAlocacaoOrigem(alocacaoOrigemAtiva);
		// A entrada da ação é versionada pelo backend. A projeção mensal pode
		// conter IDs de outra versão e nunca deve ser usada para preencher
		// `particoes[].padrao_origem_id`.
		const padroesOrigemDaAcao = Array.isArray(
			acaoEdicaoDireta?.entrada?.configuracao?.padroes_origem,
		)
			? acaoEdicaoDireta.entrada.configuracao.padroes_origem
			: Array.isArray(acaoEdicaoDireta?.entrada?.padroes_origem)
				? acaoEdicaoDireta.entrada.padroes_origem
				: [];
		const padroesOrigem =
			padroesOrigemDaAcao.length > 0
				? padroesOrigemDaAcao
				: getPadroesAlocacaoOrigem(linhaOrigem);
		const padroesDestino = normalizePadroesAlocacao(
			regimePayload?.padroes_alocacao,
		);
		// A compatibilidade é decidida pelo preview do backend. O cliente só
		// transforma o formulário no estado final necessário para simulá-lo.
		const acaoEditarOrigem = acaoEdicaoDireta;
		const estadoFinalDoNovoDestino = montarEstadoFinalOrigemComNovoDestino({
			padroesOrigem,
			padroesDestino,
			setorDestinoId: setorDestino,
		});
		if (estadoFinalDoNovoDestino.padroesNaoMapeados.length > 0) {
			throw new Error(
				'Não foi possível montar o comando de prévia para os dias e horários informados.',
			);
		}
		// Para um dia novo, a ocorrência estrutural da origem e sua partição são
		// enviadas juntas. Se o recorte for parcial, a API mantém na origem apenas
		// a faixa remanescente; se for integral, o dia fica todo no destino enquanto
		// a partição estiver ativa.
		const padroesOrigemComNovosDiasIsolados =
			estadoFinalDoNovoDestino.padroesFinais;
		const padroesOrigemFinaisSemDuplicidade = Array.from(
			new Map(
				padroesOrigemComNovosDiasIsolados.map((padrao = {}) => [
					JSON.stringify({
						tipo_recorrencia: normalizeCode(
							padrao?.tipo_recorrencia,
						),
						preset_ciclo: getPayloadPresetCiclo(
							padrao?.preset_ciclo,
						),
						dias_semana: getPadraoDiasSemana(padrao),
						datas_especificas: getPadraoDatasEspecificas(padrao),
						hora_inicio: formatHourInputToTimeValue(
							padrao?.hora_inicio,
						),
						hora_fim: formatHourInputToTimeValue(padrao?.hora_fim),
					}),
					padrao,
				]),
			).values(),
		);

		const versaoEsperada =
			getVersaoEsperadaAlocacaoOrigem(linhaOrigem) ||
			Number(alocacaoOrigemAtiva?.versao || 0) ||
			Number(acaoEditarOrigem?.entrada?.versao_esperada || 0);
		const padroesOrigemIds = new Set(
			padroesOrigem
				.map((padrao = {}) => String(getPadraoOrigemId(padrao) || ''))
				.filter(Boolean),
		);
		const particoesAtuais =
			getParticoesFromPayload(alocacaoOrigemAtiva).length > 0
				? getParticoesFromPayload(alocacaoOrigemAtiva)
				: getParticoesPersistidas(linhasProfissional);
		const particoesAtuaisDaOrigem = particoesAtuais.filter(
			(particao = {}) =>
				padroesOrigemIds.has(String(particao?.padrao_origem_id || '')),
		);
		const particoes = Array.from(
			new Map(
				[
					...particoesAtuaisDaOrigem,
					...estadoFinalDoNovoDestino.particoesNovas,
				].map((particao = {}) => [JSON.stringify(particao), particao]),
			).values(),
		);
		if (
			!setorOrigem ||
			!setorDestino ||
			(!regimeVinculoId && !alocacaoOrigemId) ||
			(alocacaoOrigemId && !versaoEsperada) ||
			!particoes
		) {
			throw new Error(
				'Não foi possível preparar o estado atual da jornada de origem. Atualize a escala e tente novamente.',
			);
		}

		const body = {
			contexto: {
				estabelecimento: getEstabelecimentoIdValue(
					contextoAplicado.estabelecimento,
				),
				setor: setorOrigem,
				mes_referencia: contextoAplicado.mes_referencia,
			},
			...(alocacaoOrigemId
				? {
						alocacao_origem_id: alocacaoOrigemId,
						versao_esperada: versaoEsperada,
					}
				: { regime_vinculo_id: regimeVinculoId }),
			particoes,
			justificativa: 'Divisão de jornada entre setores',
		};
		let preview;
		if (alocacaoOrigemId) {
			if (
				!acaoEditarOrigem?.entrada ||
				!acaoEditarOrigem?.href ||
				acaoEditarOrigem?.disponivel === false
			) {
				throw new Error(
					getMotivoAcaoIndisponivel(acaoEditarOrigem) ||
						'Não foi possível abrir a edição da jornada atual. Atualize a escala e tente novamente.',
				);
			}

			const entradaEditar = JSON.parse(
				JSON.stringify(acaoEditarOrigem.entrada),
			);
			const configuracaoEntrada = entradaEditar?.configuracao || {};
			const configuracaoPersistida =
				alocacaoOrigemAtiva?.configuracao ||
				linhaOrigem?.alocacao_origem?.configuracao ||
				linhaOrigem?.regime_escala?.alocacao_origem?.configuracao ||
				{};
			const campoDimensao =
				modalidade === 'ENFERMAGEM'
					? 'categoria_id'
					: 'especialidade_id';
			// A configuração final e a lista de partições descrevem a mesma
			// jornada. Incluir um dia no destino sem criar esta partição era o que
			// deixava uma cópia espelho no setor-base.
			const padroesOrigemFinais = padroesOrigemFinaisSemDuplicidade;
			const configuracaoFinal = Object.fromEntries(
				Object.entries({
					...configuracaoPersistida,
					...configuracaoEntrada,
					vinculo_profissional_id:
						configuracaoEntrada?.vinculo_profissional_id ||
						configuracaoPersistida?.vinculo_profissional_id ||
						getVinculoIdFromLinha(linhaOrigem),
					setor_origem_id:
						configuracaoEntrada?.setor_origem_id ||
						configuracaoPersistida?.setor_origem_id ||
						setorOrigem,
					[campoDimensao]:
						configuracaoEntrada?.[campoDimensao] ||
						configuracaoPersistida?.[campoDimensao] ||
						Number(getEspecialidadeIdFromLinha(linhaOrigem)) ||
						undefined,
					regime:
						configuracaoEntrada?.regime ||
						configuracaoPersistida?.regime ||
						linhaOrigem?.regime_escala?.regime ||
						undefined,
					vigencia_inicio:
						configuracaoEntrada?.vigencia_inicio ||
						configuracaoPersistida?.vigencia_inicio ||
						getDataInicialProfissionalEscalaIsoFromLinha(
							linhaOrigem,
						) ||
						undefined,
					vigencia_fim:
						configuracaoEntrada?.vigencia_fim ??
						configuracaoPersistida?.vigencia_fim ??
						null,
					padroes_origem: padroesOrigemFinais,
				}).filter(([, value]) => value !== undefined),
			);
			const destinoSolicitado = {
				setor_destino_id: setorDestino,
				padroes_alocacao: padroesDestino,
			};
			const comandoEdicao = {
				...entradaEditar,
				particoes,
				// A entrada de preparar_edicao_como_particao pode trazer apenas a
				// versão e as partições. O contrato exige a configuração completa
				// na prévia, portanto ela é sempre anexada a partir da leitura
				// estrutural da mesma origem.
				configuracao: configuracaoFinal,
				// Só preenche campos que a própria ação declarou. Isto mantém o
				// contrato do backend e evita que o botão assuma que a carga deve
				// nascer independente quando a nova faixa não coincide com a origem.
				...(Object.prototype.hasOwnProperty.call(
					entradaEditar,
					'setor_destino_id',
				)
					? { setor_destino_id: setorDestino }
					: {}),
				...(Object.prototype.hasOwnProperty.call(
					entradaEditar,
					'particao_desejada',
				)
					? {
							particao_desejada: {
								...entradaEditar.particao_desejada,
								...destinoSolicitado,
							},
						}
					: {}),
				...(Object.prototype.hasOwnProperty.call(
					entradaEditar,
					'novo_setor',
				)
					? {
							novo_setor: {
								...entradaEditar.novo_setor,
								...destinoSolicitado,
							},
						}
					: {}),
			};
			const edicaoAssistida =
				isAcaoEdicaoOrigemAssistida(acaoEditarOrigem);
			if (isAcaoSomenteLeitura(acaoEditarOrigem)) {
				// Algumas origens publicam `editar`/`preparar_edicao_como_particao`
				// como uma consulta HATEOAS. O GET apenas devolve a ação canônica de
				// preview; jamais é, por si só, o resultado da criação da variante.
				const preparacao =
					await requestExecutarAcaoEdicaoAlocacaoOrigem({
						token,
						action: acaoEditarOrigem,
						body: acaoEditarOrigem.entrada,
						scope,
					});
				if (preparacao?.error) {
					throw createApiResponseError(
						preparacao,
						'Não foi possível preparar a criação deste setor na jornada atual.',
					);
				}
				const acaoPrever =
					getAcoesAlocacaoOrigemFromPayload(preparacao)?.prever ||
					preparacao?.acoes?.prever ||
					null;
				// O comando local contém o estado final completo, inclusive padrões
				// novos correlacionados por `referencia_cliente`. A preparação fornece
				// apenas o envelope/ação; substituir sua configuração aqui faria o dia
				// recém-selecionado desaparecer antes do POST de preview.
				const corpoPrevia = comandoEdicao;
				preview =
					acaoPrever?.href && acaoPrever?.disponivel !== false
						? await requestExecutarAcaoEdicaoAlocacaoOrigem({
								token,
								action: acaoPrever,
								body: montarEntradaPreviaEdicaoOrigem({
									entradaAcao: acaoPrever.entrada,
									corpoEdicao: corpoPrevia,
								}),
								scope,
							})
						: await requestPreverParticionamentoAlocacaoOrigem({
								token,
								body: comandoEdicao,
								scope,
							});
			} else {
				preview = await requestExecutarAcaoEdicaoAlocacaoOrigem({
					token,
					action: acaoEditarOrigem,
					body: comandoEdicao,
					scope,
				});
			}

			if (edicaoAssistida) {
				if (preview?.error) {
					throw createApiResponseError(
						preview,
						'Não foi possível gerar alternativas para esta jornada.',
					);
				}
				const alternativas = Array.isArray(preview?.alternativas)
					? preview.alternativas.filter(
							(alternativa = {}) =>
								alternativa?.acoes?.prever?.entrada &&
								alternativa?.acoes?.prever?.disponivel !==
									false,
						)
					: [];
				if (!alternativas.length) {
					throw new Error(
						getPreviewBlockMessage(
							preview,
							'Não há alternativa disponível para adicionar este setor à jornada.',
						),
					);
				}

				const pendenciaAlternativas = {
					tipo: 'ESCOLHER_ALTERNATIVA_JORNADA_ASSISTIDA',
					alternativas,
					setorDestino,
					origem: {
						setor: setorOrigem,
						nome: getLinhaSetorNome(linhaOrigem, setorOptions),
					},
					particoes,
					alocacoesIndependentes: [],
					issues: Array.isArray(preview?.issues)
						? preview.issues
						: [],
				};
				if (alternativasJornadaSaoEquivalentes(alternativas)) {
					await prepararPreviewAlternativaJornadaAssistida(
						alternativas[0],
						pendenciaAlternativas,
					);
				} else {
					setParticionamentoPendente(pendenciaAlternativas);
				}
				return;
			}
		} else {
			preview = await requestPreverParticionamentoAlocacaoOrigem({
				token,
				body,
				scope,
			});
		}
		if (preview?.error) {
			if (modalidade === 'ENFERMAGEM') {
				setSugestaoJornadaDisponivel(true);
				setSubmitErrorScope('gerenciamentoTurnosNovoSetor');
				setSubmitError(getParticionamentoPreviewMessage(preview));
				return;
			}
			throw createApiResponseError(
				preview,
				getParticionamentoPreviewMessage(preview),
			);
		}
		if (!isParticionamentoPreviewValido(preview)) {
			if (showProfissionalHorarioSobrepostoPopup(preview)) return;
			if (modalidade === 'ENFERMAGEM') {
				setSugestaoJornadaDisponivel(true);
			}
			setSubmitErrorScope(
				originatedFromProfessionalModal
					? 'novoProfissional'
					: 'gerenciamentoTurnosNovoSetor',
			);
			setSubmitError(
				getPreviewBlockMessage(
					preview,
					'Não foi possível criar este setor na jornada atual. Revise os dados e tente novamente.',
				),
			);
			return;
		}

		const entradaAplicar = preview?.acoes?.aplicar?.entrada;
		const previewEtag = getPreenchimentoPreviewEtag(preview);
		if (!entradaAplicar || !previewEtag) {
			throw new Error(
				'Não foi possível preparar a aplicação da divisão de jornada. Atualize a escala e tente novamente.',
			);
		}

		setParticionamentoPendente({
			tipo: 'CRIAR_DESTINO_PARTICIONADO',
			entrada: entradaAplicar,
			action: preview?.acoes?.aplicar || null,
			previewEtag,
			idempotencyKey:
				idempotencyKey ||
				`particionamento-${Date.now()}-${Math.random()
					.toString(36)
					.slice(2, 10)}`,
			setorDestino,
			origem: {
				setor: setorOrigem,
				regimeVinculoId,
				nome: getLinhaSetorNome(linhaOrigem, setorOptions),
			},
			particoes,
			alocacoesIndependentes: [],
			originatedFromProfessionalModal,
			novoItem: {
				...novoItem,
				diasSemanaValores: [...(novoItem?.diasSemanaValores || [])],
				horariosByDia: { ...(novoItem?.horariosByDia || {}) },
				datasEspecificasEditaveis: [
					...(novoItem?.datasEspecificasEditaveis || []),
				],
			},
			issues: Array.isArray(preview?.issues) ? preview.issues : [],
		});
	};

	const prepararCriacaoOrigemInicial = async (
		novoItem = {},
		linhaReferencia = getGerenciamentoTurnosBaseLinha(),
	) => {
		const setorOrigemId = Number(novoItem?.setorId || 0);
		const vinculoProfissionalId = Number(
			getVinculoIdFromLinha(linhaReferencia) || 0,
		);
		const { regimePayload, dataInicialIso, dimensionValue } =
			buildGerenciamentoTurnosPayloadData({
				...novoItem,
				sourceLinha: linhaReferencia,
			});

		if (
			!setorOrigemId ||
			!vinculoProfissionalId ||
			!Array.isArray(regimePayload?.padroes_alocacao) ||
			regimePayload.padroes_alocacao.length === 0
		) {
			throw new Error(
				'Não foi possível preparar a primeira jornada do profissional.',
			);
		}

		const dimensionField =
			modalidade === 'ENFERMAGEM' ? 'categoria_id' : 'especialidade_id';
		const padroesOrigem = normalizePadroesAlocacao(
			regimePayload.padroes_alocacao,
		).map((padrao = {}) =>
			Object.fromEntries(
				Object.entries({
					tipo_recorrencia: padrao.tipo_recorrencia,
					preset_ciclo: getPayloadPresetCiclo(padrao.preset_ciclo),
					dias_semana: padrao.dias_semana,
					datas_especificas: padrao.datas_especificas,
					hora_inicio: formatHourInputToTimeValue(padrao.hora_inicio),
					hora_fim: formatHourInputToTimeValue(padrao.hora_fim),
					trabalho_horas: padrao.trabalho_horas,
					descanso_horas: padrao.descanso_horas,
					data_ancora: formatDateInputToISO(padrao.data_ancora),
					periodo_semanas: padrao.periodo_semanas,
					semanas_rotacao: padrao.semanas_rotacao,
				}).filter(([, value]) => value !== undefined && value !== ''),
			),
		);
		const preview = await requestPreverParticionamentoAlocacaoOrigem({
			token,
			scope,
			body: {
				contexto: {
					estabelecimento: getEstabelecimentoIdValue(
						contextoAplicado.estabelecimento,
					),
					setor: setorOrigemId,
					mes_referencia:
						getMesReferenciaFromDateInput(dataInicialIso) ||
						contextoAplicado.mes_referencia,
				},
				configuracao: {
					vinculo_profissional_id: vinculoProfissionalId,
					setor_origem_id: setorOrigemId,
					[dimensionField]: Number(dimensionValue) || undefined,
					regime: regimePayload.regime,
					vigencia_inicio: dataInicialIso,
					vigencia_fim: null,
					padroes_origem: padroesOrigem,
				},
				particoes: [],
			},
		});

		// Mesmo quando a projeção aberta não carregou a chave da origem, o
		// backend pode encontrá-la. O 409 é uma continuação orientada: nunca
		// tentamos criar uma segunda origem para este vínculo.
		if (isAlocacaoOrigemAtivaExigeEdicao(preview)) {
			const alocacaoOrigemId = getAlocacaoOrigemIdFromPayload(preview);
			const acaoPreferida =
				getAcaoParaAdicionarSetorNaAlocacaoOrigem(preview);
			if (alocacaoOrigemId && acaoPreferida) {
				await prepararParticionamentoNovoSetor(
					novoItem,
					linhaReferencia,
					{
						alocacaoOrigemId,
						acaoPreferida,
					},
				);
				return;
			}
		}
		if (preview?.error) {
			throw createApiResponseError(
				preview,
				getParticionamentoPreviewMessage(preview),
			);
		}
		const entradaAplicar = preview?.acoes?.aplicar?.entrada;
		const previewEtag = getPreenchimentoPreviewEtag(preview);
		if (
			!isParticionamentoPreviewValido(preview) ||
			!entradaAplicar ||
			!previewEtag
		) {
			if (!isParticionamentoPreviewValido(preview)) {
				showProfissionalHorarioSobrepostoPopup(preview);
			}
			return;
		}
		setParticionamentoPendente({
			tipo: 'CRIAR_ORIGEM_INICIAL',
			entrada: entradaAplicar,
			action: preview?.acoes?.aplicar || null,
			previewEtag,
			idempotencyKey: `criar-origem-${vinculoProfissionalId}-${Date.now()}-${Math.random()
				.toString(36)
				.slice(2, 10)}`,
			setorDestino: setorOrigemId,
			origem: { setor: setorOrigemId, regimeVinculoId: 0 },
			linhaOrigemProfissional: linhaReferencia,
			particoes: [],
			issues: Array.isArray(preview?.issues) ? preview.issues : [],
		});
	};

	const prepararNovoSetorNaJornadaDoProfissional = async (
		novoItem = {},
		linhaReferencia = getGerenciamentoTurnosBaseLinha(),
		{ usarSugestoes = false } = {},
	) => {
		const estadoEstrutural =
			await carregarEstadoAlocacaoOrigemDoProfissional(linhaReferencia);
		const alocacaoDaLinha = String(
			getAlocacaoOrigemId(linhaReferencia) || '',
		).trim();
		const candidatas = Array.isArray(
			estadoEstrutural?.alocacoesDoProfissional,
		)
			? estadoEstrutural.alocacoesDoProfissional
			: [];
		const alocacaoAtiva =
			candidatas.find(
				(alocacao = {}) =>
					alocacaoDaLinha &&
					String(
						alocacao?.id ||
							alocacao?.alocacao_origem_id ||
							alocacao?.chave ||
							'',
					).trim() === alocacaoDaLinha,
			) || (candidatas.length === 1 ? candidatas[0] : null);

		if (alocacaoAtiva) {
			const alocacaoOrigemId = String(
				alocacaoAtiva?.id ||
					alocacaoAtiva?.alocacao_origem_id ||
					alocacaoAtiva?.chave ||
					'',
			).trim();
			const acaoPreferida =
				getAcaoParaAdicionarSetorNaAlocacaoOrigem(alocacaoAtiva) ||
				getAcaoParaAdicionarSetorNaAlocacaoOrigem(
					estadoEstrutural?.response,
				);
			if (!alocacaoOrigemId || !acaoPreferida) {
				throw new Error(
					'Não foi possível abrir a jornada de origem deste profissional. Atualize a escala e tente novamente.',
				);
			}
			await prepararParticionamentoNovoSetor(novoItem, linhaReferencia, {
				alocacaoOrigemId,
				acaoPreferida,
				usarSugestoes,
			});
			return;
		}

		if (candidatas.length > 1) {
			throw new Error(
				'O profissional possui mais de uma jornada de origem ativa. Atualize a escala para que o servidor indique qual delas deve receber este setor.',
			);
		}
		await prepararCriacaoOrigemInicial(novoItem, linhaReferencia);
	};

	const prepararAtualizacaoSetoresParticionados = async (
		itensEditados = [],
		{
			itemOrigem = null,
			novosItens = [],
			regimePayloadOverride = null,
			dataInicialIsoOverride = '',
			originatedFromProfessionalModal = false,
		} = {},
	) => {
		const itemReferencia = itemOrigem || itensEditados[0] || {};
		const estadoEstrutural =
			await carregarEstadoAlocacaoOrigemDoProfissional(
				itemReferencia?.sourceLinha ||
					getGerenciamentoTurnosBaseLinha(),
			);
		const novosDestinos = (
			Array.isArray(novosItens) ? novosItens : []
		).filter((item = {}) => Number(item?.setorId || 0) > 0);
		const setorDosItensEditados = new Set(
			[...itensEditados, ...novosDestinos]
				.map((item = {}) => Number(item?.setorId || 0))
				.filter(Boolean),
		);
		const alocacaoOrigemIdInformada = String(
			getAlocacaoOrigemId(itemReferencia?.sourceLinha || {}) || '',
		);
		const linhasOrigem = estadoEstrutural.linhasProfissional.filter(
			(linha = {}) =>
				isLinhaOrigemDeSplit(linha) &&
				(!alocacaoOrigemIdInformada ||
					String(getAlocacaoOrigemId(linha) || '') ===
						alocacaoOrigemIdInformada),
		);
		const setorOrigemEditado = Number(
			itemOrigem?.setorId ||
				getLinhaSetorId(itemOrigem?.sourceLinha) ||
				0,
		);
		const setorBase = Number(
			getLinhaSetorId(getGerenciamentoTurnosBaseLinha()) || 0,
		);
		const linhaOrigem =
			linhasOrigem.find(
				(linha = {}) =>
					Boolean(setorBase) &&
					Number(getLinhaSetorId(linha) || 0) === setorBase,
			) ||
			linhasOrigem.find(
				(linha = {}) =>
					Boolean(setorOrigemEditado) &&
					Number(getLinhaSetorId(linha) || 0) === setorOrigemEditado,
			) ||
			linhasOrigem.find((linha = {}) =>
				getParticoesPersistidas([linha]).some((particao = {}) =>
					setorDosItensEditados.has(
						Number(particao?.setor_destino_id || 0),
					),
				),
			);
		if (!linhaOrigem) {
			throw new Error(
				'Não foi possível identificar a alocação de origem deste setor. Atualize a escala e tente novamente.',
			);
		}
		// A linha mensal da origem pode exibir apenas a faixa que restou depois
		// dos recortes. Para atualizar a jornada, a fonte correta é a linha
		// estrutural, que mantém o horário integral de cada padrão e os seus IDs.
		const linhaOrigemEstrutural =
			linhasOrigem.find(isLinhaEstruturalAlocacaoOrigem) || linhaOrigem;

		const alocacaoOrigemId = getAlocacaoOrigemId(linhaOrigemEstrutural);
		const versaoEsperadaDaLinha = getVersaoEsperadaAlocacaoOrigem(
			linhaOrigemEstrutural,
		);
		const alocacaoOrigemAtiva = estadoEstrutural.alocacoesOrigem.find(
			(alocacao = {}) =>
				String(
					alocacao?.id ||
						alocacao?.alocacao_origem_id ||
						alocacao?.chave ||
						'',
				) === String(alocacaoOrigemId || ''),
		);
		const acaoEditarOrigem = alocacaoOrigemAtiva?.acoes?.editar || null;
		const versaoEsperada =
			versaoEsperadaDaLinha ||
			Number(alocacaoOrigemAtiva?.versao || 0) ||
			Number(acaoEditarOrigem?.entrada?.versao_esperada || 0);
		const setorOrigem = Number(getLinhaSetorId(linhaOrigemEstrutural) || 0);
		const particoesAtuais =
			getParticoesFromPayload(alocacaoOrigemAtiva).length > 0 ||
			Array.isArray(alocacaoOrigemAtiva?.particoes)
				? getParticoesFromPayload(alocacaoOrigemAtiva)
				: getParticoesPersistidas([linhaOrigemEstrutural]);
		// Todos os setores adicionais pertencem à mesma origem. Uma alteração não
		// pode aproveitar uma faixa fora dela para criar, silenciosamente, outra
		// carga independente.
		const itensDestino = itensEditados;
		if (
			!alocacaoOrigemId ||
			!versaoEsperada ||
			!setorOrigem ||
			(!itemOrigem && itensDestino.length === 0)
		) {
			throw new Error(
				'Não foi possível preparar o estado final desta jornada particionada. Atualize a escala e tente novamente.',
			);
		}
		if (
			!acaoEditarOrigem?.entrada ||
			!acaoEditarOrigem?.href ||
			acaoEditarOrigem?.disponivel === false
		) {
			throw new Error(
				getMotivoAcaoIndisponivel(acaoEditarOrigem) ||
					'A edição desta jornada ainda não está disponível nesta versão da escala. Atualize a página após a atualização do sistema.',
			);
		}

		// A ação HATEOAS é a única fonte que garante a associação entre o UUID do
		// padrão e esta alocação de origem. A linha mensal é apenas uma projeção e
		// pode conter padrões de uma versão anterior.
		const entradaEditar = JSON.parse(
			JSON.stringify(acaoEditarOrigem.entrada),
		);
		const padroesOrigemDaEntrada = Array.isArray(
			entradaEditar?.configuracao?.padroes_origem,
		)
			? entradaEditar.configuracao.padroes_origem
			: Array.isArray(entradaEditar?.padroes_origem)
				? entradaEditar.padroes_origem
				: [];
		const padroesOrigem =
			padroesOrigemDaEntrada.length > 0
				? padroesOrigemDaEntrada
				: getPadroesAlocacaoOrigem(linhaOrigemEstrutural);
		// Cada aba de setor destino representa o estado final daquele destino.
		// Ao editar terça para quinta, por exemplo, a partição anterior de terça
		// não pode continuar sendo usada como base do comando. Os destinos são
		// montados em sequência a partir dos dias selecionados no formulário e
		// preservam somente os UUIDs canônicos dos padrões da origem.
		let padroesOrigemComDestinosEditados = padroesOrigem;
		const atualizacoesDestino = itensDestino.map((item = {}) => {
			const { regimePayload } = buildGerenciamentoTurnosPayloadData({
				...item,
				sourceLinha: linhaOrigem,
			});
			const padroesDestino = Array.isArray(
				regimePayload?.padroes_alocacao,
			)
				? regimePayload.padroes_alocacao
				: [];
			const estadoFinalDestinoEditado =
				montarEstadoFinalOrigemComNovoDestino({
					padroesOrigem: padroesOrigemComDestinosEditados,
					padroesDestino,
					setorDestinoId: Number(item?.setorId || 0),
				});
			// Um dia novo usa a faixa canônica que o contém e recebe uma partição
			// própria. Se o recorte for parcial, a origem mantém somente a faixa
			// remanescente, conforme o estado calculado pelo backend.
			padroesOrigemComDestinosEditados =
				estadoFinalDestinoEditado.padroesFinais;
			return {
				particoes: estadoFinalDestinoEditado.particoesNovas,
				padroesNovosDaJornada: estadoFinalDestinoEditado.padroesNovos,
				padroesNaoMapeados:
					estadoFinalDestinoEditado.padroesNaoMapeados,
			};
		});
		if (
			atualizacoesDestino.some(
				(atualizacao = {}) =>
					(atualizacao.padroesNaoMapeados || []).length > 0,
			)
		) {
			throw new Error(
				'Não foi possível correlacionar todos os dias e horários editados com a jornada. Revise os campos e tente novamente.',
			);
		}
		const particoesEditadas = atualizacoesDestino.flatMap(
			(atualizacao = {}) => atualizacao.particoes || [],
		);
		const padroesNovosDaJornada = atualizacoesDestino.flatMap(
			(atualizacao = {}) => atualizacao.padroesNovosDaJornada || [],
		);
		const setoresDestinoEditados = new Set(
			itensDestino.map((item = {}) => Number(item?.setorId || 0)),
		);
		let particoes = substituirParticoesDosSetoresEditados({
			particoesAtuais,
			setoresDestinoEditados: [...setoresDestinoEditados],
			particoesEditadas,
		});
		// O editor devolve o estado completo do destino. Se um dia integral que
		// havia sido criado para esse destino foi desmarcado, retirar só a
		// partição faria a API devolvê-lo ao setor-base. Reconcilie também os
		// padrões da origem antes de montar o comando final.
		padroesOrigemComDestinosEditados =
			removerOcorrenciasIntegrasDesmarcadasDaOrigem({
				padroesOrigem: padroesOrigemComDestinosEditados,
				particoesAntes: particoesAtuais,
				particoesDepois: particoes,
				setoresDestinoEditados: [...setoresDestinoEditados],
			});
		let configuracao = itemOrigem
			? (() => {
					const {
						regimePayload: regimePayloadMontado,
						dataInicialIso: dataInicialIsoMontada,
					} = buildGerenciamentoTurnosPayloadData({
						...itemOrigem,
						sourceLinha: linhaOrigem,
					});
					const regimePayload =
						regimePayloadOverride || regimePayloadMontado;
					const dataInicialIso =
						dataInicialIsoOverride || dataInicialIsoMontada;
					const padroesAtuais = padroesOrigemComDestinosEditados;
					const padroesEditados = normalizePadroesAlocacao(
						regimePayload?.padroes_alocacao,
					);
					const snapshotOriginal = itemOrigem?.originalSnapshot || {};
					const snapshotAtual =
						getGerenciamentoTurnosItemSnapshot(itemOrigem);
					const camposAlterados = Object.keys(snapshotAtual).filter(
						(campo) =>
							String(snapshotAtual[campo] || '') !==
							String(snapshotOriginal[campo] || ''),
					);
					const alterouEstruturaDaRecorrencia = camposAlterados.some(
						(campo) =>
							[
								'tipoRecorrencia',
								'presetCiclo',
								'periodoRotacao',
								'semanasRotacao',
								'diasSemana',
								'datasEspecificas',
							].includes(campo),
					);
					const alterouHorarios = camposAlterados.some((campo) =>
						['horariosByDia', 'horaInicio', 'horaFim'].includes(
							campo,
						),
					);
					const horarioDoCicloEditado = {
						hora_inicio: formatHourInputToTimeValue(
							itemOrigem?.horaInicio,
						),
						hora_fim: formatHourInputToTimeValue(
							itemOrigem?.horaFim,
						),
					};
					// O backend pode devolver o campo visual como PERSONALIZADO após
					// uma edição, mesmo quando a recorrência continua sendo 12x60.
					// Para localizar o padrão estrutural que receberá o novo horário,
					// a recorrência escolhida no select tem precedência sobre esse rótulo
					// normalizado.
					const presetDaRecorrenciaEditada = getPayloadPresetCiclo(
						itemOrigem?.recorrenciaValue ||
							itemOrigem?.nomeRecorrencia,
					);
					const presetDoCicloEditado =
						presetDaRecorrenciaEditada &&
						presetDaRecorrenciaEditada !== 'PERSONALIZADO'
							? presetDaRecorrenciaEditada
							: getPayloadPresetCiclo(
									snapshotOriginal?.presetCiclo ||
										itemOrigem?.presetCiclo ||
										regimePayload?.preset_ciclo,
								);
					const horariosOriginais = (() => {
						try {
							return (
								JSON.parse(
									snapshotOriginal.horariosByDia || '{}',
								) || {}
							);
						} catch {
							return {};
						}
					})();
					const horariosAtuais = (() => {
						try {
							return (
								JSON.parse(
									snapshotAtual.horariosByDia || '{}',
								) || {}
							);
						} catch {
							return {};
						}
					})();
					const horariosAlteradosPorDia = Object.entries(
						horariosAtuais,
					).reduce((resultado, [dia, horario = {}]) => {
						const horarioOriginal = horariosOriginais?.[dia] || {};
						const inicio = formatHourInputToTimeValue(
							horario?.hora_inicio,
						);
						const fim = formatHourInputToTimeValue(
							horario?.hora_fim,
						);
						if (
							inicio !==
								formatHourInputToTimeValue(
									horarioOriginal?.hora_inicio,
								) ||
							fim !==
								formatHourInputToTimeValue(
									horarioOriginal?.hora_fim,
								)
						) {
							resultado[Number(dia)] = {
								hora_inicio: inicio,
								hora_fim: fim,
							};
						}
						return resultado;
					}, {});
					// No editor estrutural, a origem é carregada com seus padrões
					// integrais. Portanto, uma edição explícita substitui a lista de
					// padrões da nova versão; as partições existentes continuam no
					// mesmo comando e o backend valida se elas ainda cabem na origem.
					// O frontend não recalcula duração ou carga da jornada.
					// Uma simples troca de horário não pode recriar a recorrência a
					// partir da grade visual. Essa reconstrução trocava o dia do padrão
					// em alguns retornos assistidos. Partimos sempre dos padrões
					// canônicos e alteramos somente o horário do dia efetivamente
					// editado; dias, IDs e demais padrões continuam intactos.
					const diasVisiveisNoSnapshotOriginal =
						normalizeDiasSemanaValues(
							String(snapshotOriginal?.diasSemana || '')
								.split(',')
								.filter(Boolean),
						);
					const recorrenciaSemanalEditada =
						normalizeCode(regimePayload?.tipo_recorrencia) ===
						'SEMANAL';
					// Após uma aplicação, o modal pode ter sido reidratado com um
					// snapshot visual anterior, embora os campos de horário já tenham o
					// novo valor digitado. Comparar também contra o padrão estrutural
					// evita que uma segunda edição reenvie o horário da primeira.
					const horarioDoCicloDivergeDaOrigem =
						!recorrenciaSemanalEditada &&
						Boolean(
							horarioDoCicloEditado.hora_inicio &&
							horarioDoCicloEditado.hora_fim &&
							padroesAtuais.some((padrao = {}) => {
								const ehMesmoCiclo =
									getPadraoDiasSemana(padrao).length === 0 &&
									getPadraoDatasEspecificas(padrao).length ===
										0 &&
									normalizeCode(padrao?.tipo_recorrencia) ===
										normalizeCode(
											regimePayload?.tipo_recorrencia,
										) &&
									getPayloadPresetCiclo(
										padrao?.preset_ciclo,
									) === presetDoCicloEditado;
								return (
									ehMesmoCiclo &&
									(formatHourInputToTimeValue(
										padrao?.hora_inicio,
									) !== horarioDoCicloEditado.hora_inicio ||
										formatHourInputToTimeValue(
											padrao?.hora_fim,
										) !== horarioDoCicloEditado.hora_fim)
								);
							}),
						);
					const deveAtualizarHorarios =
						alterouHorarios || horarioDoCicloDivergeDaOrigem;
					const padroesOcultosNaGrade = padroesAtuais.filter(
						(padrao = {}) => {
							const diasDoPadrao = getPadraoDiasSemana(padrao);
							return (
								diasDoPadrao.length > 0 &&
								diasDoPadrao.every(
									(dia) =>
										!diasVisiveisNoSnapshotOriginal.includes(
											dia,
										),
								)
							);
						},
					);
					const padroesParaConfiguracao =
						alterouEstruturaDaRecorrencia
							? padroesEditados.length
								? recorrenciaSemanalEditada
									? [
											// Dias integralmente cobertos por partições podem não
											// aparecer no card. Como não foram editados, seguem
											// preservados na nova versão da jornada.
											...padroesOcultosNaGrade,
											...padroesEditados,
										]
									: padroesEditados
								: padroesAtuais
							: deveAtualizarHorarios
								? padroesAtuais.map((padrao = {}) => {
										const diasDoPadrao =
											getPadraoDiasSemana(padrao);
										const isPadraoDoCicloEditado =
											deveAtualizarHorarios &&
											diasDoPadrao.length === 0 &&
											getPadraoDatasEspecificas(padrao)
												.length === 0 &&
											normalizeCode(
												padrao?.tipo_recorrencia,
											) ===
												normalizeCode(
													regimePayload?.tipo_recorrencia,
												) &&
											getPayloadPresetCiclo(
												padrao?.preset_ciclo,
											) === presetDoCicloEditado;
										// Jornadas por ciclo não têm dia da semana. Antes, a
										// alteração dos campos "Hora inicial/final" ficava só
										// no estado do modal e o padrão canônico era reenviado
										// com os horários antigos.
										if (
											isPadraoDoCicloEditado &&
											horarioDoCicloEditado.hora_inicio &&
											horarioDoCicloEditado.hora_fim
										) {
											return {
												...padrao,
												hora_inicio:
													horarioDoCicloEditado.hora_inicio,
												hora_fim:
													horarioDoCicloEditado.hora_fim,
											};
										}
										const diaComHorarioAlterado =
											diasDoPadrao.find((dia) =>
												Boolean(
													horariosAlteradosPorDia[
														dia
													],
												),
											);
										const horarioAlterado =
											horariosAlteradosPorDia[
												diaComHorarioAlterado
											];
										// Um padrão com mais de um dia não pode receber o horário
										// de apenas um deles sem uma edição explícita da recorrência.
										// Mantê-lo evita deslocar ou criar dias não solicitados.
										if (
											!horarioAlterado ||
											diasDoPadrao.length !== 1 ||
											!horarioAlterado.hora_inicio ||
											!horarioAlterado.hora_fim
										) {
											return padrao;
										}
										return {
											...padrao,
											hora_inicio:
												horarioAlterado.hora_inicio,
											hora_fim: horarioAlterado.hora_fim,
										};
									})
								: padroesAtuais;
					const padroesOrigemSemDuplicidade =
						padroesParaConfiguracao.map((padrao = {}) => {
							const diasPadrao = getPadraoDiasSemana(padrao);
							const datasPadrao =
								getPadraoDatasEspecificas(padrao);
							const padraoExistente = padroesAtuais.find(
								(atual = {}) => {
									const mesmosDias =
										JSON.stringify(
											getPadraoDiasSemana(atual),
										) === JSON.stringify(diasPadrao);
									const mesmasDatas =
										JSON.stringify(
											getPadraoDatasEspecificas(atual),
										) === JSON.stringify(datasPadrao);
									return (
										normalizeCode(
											atual?.tipo_recorrencia,
										) ===
											normalizeCode(
												padrao?.tipo_recorrencia,
											) &&
										mesmosDias &&
										mesmasDatas
									);
								},
							);
							const padraoId = getPadraoOrigemId(padraoExistente);
							const padraoReferencia =
								padraoExistente ||
								padroesAtuais.find(
									(atual = {}) =>
										normalizeCode(
											atual?.tipo_recorrencia,
										) ===
											normalizeCode(
												padrao?.tipo_recorrencia,
											) &&
										getPayloadPresetCiclo(
											atual?.preset_ciclo,
										) ===
											getPayloadPresetCiclo(
												padrao?.preset_ciclo,
											),
								);
							const tipoRecorrenciaPadrao = normalizeCode(
								padrao?.tipo_recorrencia ||
									padraoExistente?.tipo_recorrencia,
							);
							const semanasRotacaoPadrao = (
								Array.isArray(padrao?.semanas_rotacao)
									? padrao.semanas_rotacao
									: Array.isArray(
												padraoExistente?.semanas_rotacao,
										  )
										? padraoExistente.semanas_rotacao
										: []
							).map((semana = {}, index) => ({
								semana: Number(semana?.semana || index + 1),
								dias_semana: normalizeDiasSemanaValues(
									semana?.dias_semana || semana,
								),
							}));
							const periodoSemanasPadrao =
								Number(padrao?.periodo_semanas || 0) ||
								Number(padraoExistente?.periodo_semanas || 0) ||
								semanasRotacaoPadrao.length;
							return {
								...(padraoId ? { padrao_id: padraoId } : {}),
								tipo_recorrencia:
									padrao?.tipo_recorrencia ||
									padraoExistente?.tipo_recorrencia ||
									'SEMANAL',
								preset_ciclo:
									getPayloadPresetCiclo(
										padrao?.preset_ciclo ||
											padraoExistente?.preset_ciclo,
									) || 'SEMANAL',
								dias_semana: diasPadrao,
								datas_especificas: datasPadrao,
								hora_inicio: formatHourInputToTimeValue(
									padrao?.hora_inicio,
								),
								hora_fim: formatHourInputToTimeValue(
									padrao?.hora_fim,
								),
								data_ancora:
									formatDateInputToISO(padrao?.data_ancora) ||
									formatDateInputToISO(
										padraoExistente?.data_ancora,
									) ||
									dataInicialIso,
								aplicar_apenas_competencia:
									padrao?.aplicar_apenas_competencia ??
									padraoExistente?.aplicar_apenas_competencia ??
									null,
								// Os valores de duração fazem parte do padrão canônico. Ao
								// omiti-los, a API normaliza o padrão como 0 minuto e passa a
								// projetar uma jornada diferente da que está sendo editada.
								trabalho_minutos:
									padrao?.trabalho_minutos ??
									padraoExistente?.trabalho_minutos ??
									padraoReferencia?.trabalho_minutos ??
									undefined,
								descanso_minutos:
									padrao?.descanso_minutos ??
									padraoExistente?.descanso_minutos ??
									padraoReferencia?.descanso_minutos ??
									undefined,
								// A recorrência por rotação exige estes dois campos no
								// contrato de alocação de origem. Antes eles eram exibidos
								// e editados no modal, mas se perdiam ao reconstruir o
								// padrão canônico para a prévia.
								...(tipoRecorrenciaPadrao ===
								'ROTACAO_PERIODICA'
									? {
											periodo_semanas: Math.max(
												2,
												Math.min(
													6,
													periodoSemanasPadrao || 2,
												),
											),
											semanas_rotacao:
												semanasRotacaoPadrao,
										}
									: {}),
							};
						});
					// Uma recorrência existente é versionada pelo `padrao_id`. Ao
					// editar um horário, o item alterado precisa substituir o padrão
					// anterior — nunca ser anexado como uma segunda cópia da mesma
					// recorrência.
					const padroesOrigemConfigurados = Array.from(
						new Map(
							padroesOrigemSemDuplicidade.map((padrao = {}) => [
								padrao.padrao_id ||
									JSON.stringify({
										tipo_recorrencia:
											padrao.tipo_recorrencia,
										dias_semana: padrao.dias_semana,
										datas_especificas:
											padrao.datas_especificas,
									}),
								padrao,
							]),
						).values(),
					);

					const campoDimensao =
						modalidade === 'ENFERMAGEM'
							? 'categoria_id'
							: 'especialidade_id';
					const dimensaoId =
						Number(
							getEspecialidadeIdFromLinha(linhaOrigem) ||
								getEspecialidadeIdFromLinha(
									itemOrigem?.sourceLinha,
								),
						) || undefined;

					return {
						vinculo_profissional_id: getVinculoIdFromLinha(
							linhaOrigemEstrutural,
						),
						setor_origem_id: setorOrigem,
						[campoDimensao]: dimensaoId,
						regime: regimePayload?.regime,
						vigencia_inicio: dataInicialIso,
						vigencia_fim:
							formatDateInputToISO(
								linhaOrigem?.regime_escala?.vigencia_fim ||
									linhaOrigem?.regime_escala?.data_fim,
							) || null,
						padroes_origem: padroesOrigemConfigurados,
					};
				})()
			: padroesNovosDaJornada.length > 0
				? (() => {
						const campoDimensao =
							modalidade === 'ENFERMAGEM'
								? 'categoria_id'
								: 'especialidade_id';
						return {
							vinculo_profissional_id: getVinculoIdFromLinha(
								linhaOrigemEstrutural,
							),
							setor_origem_id: setorOrigem,
							[campoDimensao]:
								Number(
									getEspecialidadeIdFromLinha(
										linhaOrigemEstrutural,
									),
								) || undefined,
							regime:
								linhaOrigemEstrutural?.regime_escala?.regime ||
								undefined,
							vigencia_inicio:
								getDataInicialProfissionalEscalaIsoFromLinha(
									linhaOrigemEstrutural,
								) || undefined,
							vigencia_fim: null,
							padroes_origem: [
								...getPadroesAlocacaoOrigem(
									linhaOrigemEstrutural,
								),
								...padroesNovosDaJornada,
							],
						};
					})()
				: null;
		// Uma inclusão de setor feita junto com edições já existentes não pode
		// virar uma segunda operação ou uma origem paralela. Acrescentamos cada
		// novo destino ao estado estrutural que acabou de ser montado, sempre
		// conservando os UUIDs dos padrões devolvidos pela origem.
		let padroesFinaisComNovosDestinos =
			configuracao?.padroes_origem || padroesOrigem;
		const particoesNovosDestinos = [];
		novosDestinos.forEach((novoItem = {}) => {
			const { regimePayload } = buildGerenciamentoTurnosPayloadData({
				...novoItem,
				sourceLinha: linhaOrigem,
			});
			const estadoFinalNovoDestino =
				montarEstadoFinalOrigemComNovoDestino({
					padroesOrigem: padroesFinaisComNovosDestinos,
					padroesDestino: normalizePadroesAlocacao(
						regimePayload?.padroes_alocacao,
					),
					setorDestinoId: Number(novoItem?.setorId || 0),
				});
			if (estadoFinalNovoDestino.padroesNaoMapeados.length > 0) {
				throw new Error(
					'Não foi possível correlacionar todos os dias e horários do novo setor com a jornada. Revise os campos e tente novamente.',
				);
			}
			// Preserva os UUIDs canônicos. Um novo dia só é agregado ao padrão da
			// origem quando a faixa integral é idêntica e a partição correspondente
			// cobre a ocorrência por completo; em qualquer outra faixa a origem fica
			// inalterada.
			padroesFinaisComNovosDestinos = Array.from(
				new Map(
					[...estadoFinalNovoDestino.padroesFinais].map(
						(padrao = {}) => [
							JSON.stringify({
								tipo_recorrencia: normalizeCode(
									padrao?.tipo_recorrencia,
								),
								preset_ciclo: getPayloadPresetCiclo(
									padrao?.preset_ciclo,
								),
								dias_semana: getPadraoDiasSemana(padrao),
								datas_especificas:
									getPadraoDatasEspecificas(padrao),
								hora_inicio: formatHourInputToTimeValue(
									padrao?.hora_inicio,
								),
								hora_fim: formatHourInputToTimeValue(
									padrao?.hora_fim,
								),
							}),
							padrao,
						],
					),
				).values(),
			);
			particoesNovosDestinos.push(
				...estadoFinalNovoDestino.particoesNovas,
			);
		});
		if (novosDestinos.length > 0) {
			particoes = Array.from(
				new Map(
					[...particoes, ...particoesNovosDestinos].map(
						(particao = {}) => [JSON.stringify(particao), particao],
					),
				).values(),
			);
			configuracao = {
				...(entradaEditar?.configuracao || {}),
				...(configuracao || {}),
				padroes_origem: padroesFinaisComNovosDestinos,
			};
		}

		// `acoes.editar.entrada` é a fonte canônica do comando: além da rota
		// correta, ela carrega a versão e, nas jornadas assistidas, o perfil
		// vigente que não deve ser recriado nem receber justificativa artificial.
		// Só substituímos os campos que a pessoa efetivamente editou e mantemos a
		// lista integral de partições no formato que a API declarou.
		const edicaoAssistida = isAcaoEdicaoOrigemAssistida(acaoEditarOrigem);
		if (!edicaoAssistida && configuracao && !entradaEditar?.configuracao) {
			throw new Error(
				'A API não devolveu a configuração inicial necessária para editar esta jornada. Atualize a página e tente novamente.',
			);
		}
		const padraoEditado = configuracao?.padroes_origem?.[0] || {};
		let body = edicaoAssistida
			? (() => {
					const {
						perfil_planejamento: _perfilPlanejamento,
						snapshot_planejamento_etag: _snapshotPlanejamentoEtag,
						metadados_planejamento: _metadadosPlanejamento,
						...entradaSemEstadoPlanejamento
					} = entradaEditar;
					const padraoDesejadoAtual =
						entradaEditar?.padrao_desejado || {};
					const padraoDesejado = {
						...padraoDesejadoAtual,
						preset_ciclo:
							getPayloadPresetCiclo(
								padraoEditado?.preset_ciclo,
							) || padraoDesejadoAtual?.preset_ciclo,
						...(padraoEditado?.hora_inicio &&
						padraoEditado?.hora_fim
							? {
									hora_inicio: formatHourInputToTimeValue(
										padraoEditado.hora_inicio,
									),
									hora_fim: formatHourInputToTimeValue(
										padraoEditado.hora_fim,
									),
								}
							: {}),
						...(Array.isArray(padraoEditado?.dias_semana)
							? { dias_semana: padraoEditado.dias_semana }
							: {}),
						...(Array.isArray(padraoEditado?.semanas_rotacao)
							? { semanas_rotacao: padraoEditado.semanas_rotacao }
							: {}),
						...(normalizeCode(padraoEditado?.tipo_recorrencia) ===
						'ROTACAO_PERIODICA'
							? {
									periodo_semanas: Math.max(
										2,
										Math.min(
											6,
											Number(
												padraoEditado?.periodo_semanas ||
													padraoEditado
														?.semanas_rotacao
														?.length ||
													2,
											),
										),
									),
								}
							: {}),
					};
					return {
						...entradaSemEstadoPlanejamento,
						// A entrada HATEOAS é a base do comando, mas estes campos são
						// editáveis no formulário. Sem sobrescrevê-los aqui, a sugestão
						// recebia novamente o regime e a vigência antigos.
						...(configuracao?.regime
							? { regime: configuracao.regime }
							: {}),
						...(configuracao?.vigencia_inicio
							? { vigencia_inicio: configuracao.vigencia_inicio }
							: {}),
						padrao_desejado: padraoDesejado,
						particoes,
					};
				})()
			: {
					...entradaEditar,
					...(configuracao
						? {
								configuracao: {
									...entradaEditar.configuracao,
									...configuracao,
									padroes_origem: configuracao.padroes_origem,
								},
							}
						: {}),
					particoes,
				};
		// O contrato de alocação de origem exige que a edição seja enviada dentro
		// de `configuracao.padroes_origem`. A entrada HATEOAS já contém todos os
		// identificadores e a versão correta; para um ciclo com um único padrão
		// ativo, substituímos somente os horários pelo que está no input do modal.
		// Isso é feito no envelope final, imediatamente antes do POST de prévia,
		// para que nenhuma etapa de reconciliação volte a usar o horário anterior.
		if (!edicaoAssistida && itemOrigem && body?.configuracao) {
			const horaInicioDigitada = formatHourInputToTimeValue(
				itemOrigem?.horaInicio,
			);
			const horaFimDigitada = formatHourInputToTimeValue(
				itemOrigem?.horaFim,
			);
			const padroesDoComando = Array.isArray(
				body.configuracao?.padroes_origem,
			)
				? body.configuracao.padroes_origem
				: [];
			const padroesDeCiclo = padroesDoComando.filter(
				(padrao = {}) =>
					getPadraoDiasSemana(padrao).length === 0 &&
					getPadraoDatasEspecificas(padrao).length === 0 &&
					['CICLO_CONTINUO', 'ROTACAO_PERIODICA'].includes(
						normalizeCode(padrao?.tipo_recorrencia),
					),
			);
			if (
				horaInicioDigitada &&
				horaFimDigitada &&
				padroesDeCiclo.length === 1
			) {
				const padraoAlvoId = getPadraoOrigemId(padroesDeCiclo[0]);
				body.configuracao = {
					...body.configuracao,
					padroes_origem: padroesDoComando.map((padrao = {}) =>
						getPadraoOrigemId(padrao) === padraoAlvoId
							? {
									...padrao,
									hora_inicio: horaInicioDigitada,
									hora_fim: horaFimDigitada,
								}
							: padrao,
					),
				};
			}
		}
		if (!edicaoAssistida && body?.configuracao) {
			const reconciliacaoPadroes = reconciliarPadroesOrigemComParticoes({
				padroesConfiguracao: body.configuracao?.padroes_origem,
				padroesCanonicos: padroesOrigem,
				particoes,
			});
			if (reconciliacaoPadroes.padroesParticoesInvalidos.length > 0) {
				throw new Error(
					'Uma das partições não pertence mais à origem atual. Atualize a escala antes de salvar a edição.',
				);
			}
			body = {
				...body,
				configuracao: {
					...body.configuracao,
					padroes_origem: reconciliacaoPadroes.padroesFinais,
				},
			};
		}
		const resumoEdicao = buildResumoEdicaoOrigem({
			entrada: edicaoAssistida
				? entradaEditar
				: entradaEditar?.configuracao || {},
			proposta: edicaoAssistida ? body : body?.configuracao || {},
		});
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'PREVIEW_ATUALIZACAO_JORNADA',
			setorId: setorOrigem,
			itemAnterior: itemOrigem?.originalSnapshot
				? { ...itemOrigem, ...itemOrigem.originalSnapshot }
				: undefined,
			itemAtualizado: itemOrigem || itensDestino[0],
			campos: {
				setores_editados: itensDestino.map((item = {}) => item.setorId),
				setores_novos: novosDestinos.map((item = {}) => item.setorId),
			},
			payload: body,
		});
		let preview;
		if (isAcaoSomenteLeitura(acaoEditarOrigem)) {
			// Em enfermagem, algumas origens expõem `editar` como uma consulta de
			// preparação. Esse GET não é a mutação nem a prévia: o resultado deve
			// fornecer `acoes.prever`, cuja method/href determinam o POST/PATCH.
			const preparacao = await requestExecutarAcaoEdicaoAlocacaoOrigem({
				token,
				action: acaoEditarOrigem,
				body: acaoEditarOrigem.entrada,
				scope,
			});
			if (preparacao?.error) {
				throw createApiResponseError(
					preparacao,
					'Não foi possível preparar a edição da jornada de origem.',
				);
			}
			const acaoPrever =
				getAcoesAlocacaoOrigemFromPayload(preparacao)?.prever ||
				preparacao?.acoes?.prever ||
				null;
			if (acaoPrever?.href && acaoPrever?.disponivel !== false) {
				preview = await requestExecutarAcaoEdicaoAlocacaoOrigem({
					token,
					action: acaoPrever,
					body: montarEntradaPreviaEdicaoOrigem({
						entradaAcao: acaoPrever.entrada,
						corpoEdicao: body,
					}),
					scope,
				});
			} else {
				// O Swagger mantém este POST como rota de prévia padrão quando a
				// preparação não publica uma ação específica. Nunca considere o GET
				// de preparação como edição concluída.
				preview = await requestPreverParticionamentoAlocacaoOrigem({
					token,
					body,
					scope,
				});
			}
		} else {
			preview = await requestExecutarAcaoEdicaoAlocacaoOrigem({
				token,
				action: acaoEditarOrigem,
				body,
				scope,
			});
		}
		registrarOperacaoGerenciamentoTurnos({
			operacao: 'RESULTADO_PREVIEW_ATUALIZACAO_JORNADA',
			setorId: setorOrigem,
			itemAtualizado: itemOrigem || itensDestino[0],
			resultado: {
				error: preview?.error || null,
				valido: preview?.valido,
				bloqueado: preview?.bloqueado,
				issues: preview?.issues,
				entrada_aplicar: preview?.acoes?.aplicar?.entrada,
			},
		});
		if (edicaoAssistida) {
			if (preview?.error) {
				throw createApiResponseError(
					preview,
					'Não foi possível gerar alternativas para esta jornada.',
				);
			}
			const alternativas = Array.isArray(preview?.alternativas)
				? preview.alternativas.filter(
						(alternativa = {}) =>
							alternativa?.acoes?.prever?.entrada &&
							alternativa?.acoes?.prever?.disponivel !== false,
					)
				: [];
			if (!alternativas.length) {
				// A API pode responder sem alternativas para a combinação atual. Isso
				// não é erro de requisição e não deve gerar alerta técnico no modal.
				return;
			}
			const pendenciaAlternativas = {
				tipo: 'ESCOLHER_ALTERNATIVA_JORNADA_ASSISTIDA',
				alternativas,
				setorDestino: Number(itemOrigem?.setorId || setorOrigem),
				origem: {
					setor: setorOrigem,
					nome: getLinhaSetorNome(linhaOrigem, setorOptions),
				},
				particoes,
				alocacoesIndependentes: [],
				resumoEdicao,
				originatedFromProfessionalModal,
				linhaOrigemProfissional: originatedFromProfessionalModal
					? itemOrigem?.sourceLinha || null
					: null,
				issues: Array.isArray(preview?.issues) ? preview.issues : [],
			};

			// Em uma edição assistida, uma única alternativa — ou várias cópias da
			// mesma proposta — não representa escolha para a pessoa responsável.
			// Geramos a prévia assinada uma única vez e mostramos diretamente a
			// confirmação final. Criação de setor continua exigindo escolha
			// explícita entre alternativas distintas.
			if (alternativasJornadaSaoEquivalentes(alternativas)) {
				await prepararPreviewAlternativaJornadaAssistida(
					alternativas[0],
					pendenciaAlternativas,
				);
				return;
			}

			setParticionamentoPendente(pendenciaAlternativas);
			return;
		}
		if (preview?.error) {
			throw createApiResponseError(
				preview,
				'Não foi possível validar a atualização da jornada.',
			);
		}
		if (!isParticionamentoPreviewValido(preview)) {
			if (showProfissionalHorarioSobrepostoPopup(preview)) return;
			setSubmitErrorScope('gerenciamentoTurnos');
			setSubmitError(
				getPreviewBlockMessage(
					preview,
					'Esta atualização da jornada precisa de ajustes antes de ser confirmada.',
				),
			);
			return;
		}
		const entradaAplicar = preview?.acoes?.aplicar?.entrada;
		const previewEtag = getPreenchimentoPreviewEtag(preview);
		if (!entradaAplicar || !previewEtag) {
			throw new Error(
				'Não foi possível preparar a atualização das faixas da jornada.',
			);
		}
		const getDescricaoProcessoSetor = (item = {}) =>
			getLinhaSetorNome(
				item?.sourceLinha || { setor_id: item?.setorId },
				setorOptions,
			) || `Setor ${item?.setorId || ''}`;
		const processos = [
			...(itemOrigem && hasGerenciamentoTurnosItemChanges(itemOrigem)
				? [
						{
							tipo: 'Edição',
							alvo: 'Setor de origem',
							detalhe: getDescricaoProcessoSetor(itemOrigem),
						},
					]
				: []),
			...itensEditados
				.filter((item = {}) =>
					isLinhaDestinoDeSplit(item?.sourceLinha || {}),
				)
				.map((item = {}) => ({
					tipo: 'Edição',
					alvo: 'Setor variante',
					detalhe: getDescricaoProcessoSetor(item),
				})),
			...novosDestinos.map((item = {}) => ({
				tipo: 'Criação',
				alvo: 'Setor variante',
				detalhe: getDescricaoProcessoSetor(item),
			})),
		];
		const resumoFaixasAtualizadas = getResumoFaixasAtualizadas([
			itemOrigem,
			...itensDestino,
			...novosDestinos,
		]);
		setParticionamentoPendente({
			tipo: itemOrigem
				? 'ATUALIZAR_JORNADA_ORIGEM'
				: 'ATUALIZAR_PARTICOES',
			entrada: entradaAplicar,
			action: preview?.acoes?.aplicar || null,
			previewEtag,
			idempotencyKey: `atualizar-particoes-${Date.now()}-${Math.random()
				.toString(36)
				.slice(2, 10)}`,
			setorDestino: Number(
				itensDestino[0]?.setorId || itemOrigem?.setorId || setorOrigem,
			),
			origem: {
				setor: setorOrigem,
				nome: getLinhaSetorNome(linhaOrigem, setorOptions),
			},
			particoes,
			alocacoesIndependentes: [],
			resumoEdicao,
			resumoFaixasAtualizadas,
			processos,
			originatedFromProfessionalModal,
			linhaOrigemProfissional: originatedFromProfessionalModal
				? itemOrigem?.sourceLinha || null
				: null,
			issues: Array.isArray(preview?.issues) ? preview.issues : [],
		});
	};

	const prepararPreviewAlternativaJornadaAssistida = async (
		alternativa = {},
		pendenciaBase = null,
	) => {
		const acaoPrever = alternativa?.acoes?.prever;
		if (!acaoPrever?.entrada || !acaoPrever?.href) {
			// A ação pode não estar disponível mesmo sem falha de rede/API. Não
			// convertemos essa condição local em alerta de erro.
			return;
		}

		setIsSubmittingGerenciamentoTurnos(true);
		setSubmitError('');
		setSubmitErrorScope('');
		try {
			// A alternativa assina o próprio comando de preview. Não acrescentamos
			// snapshot, metadados ou partições calculadas no navegador.
			const preview = await requestExecutarAcaoEdicaoAlocacaoOrigem({
				token,
				action: acaoPrever,
				body: JSON.parse(JSON.stringify(acaoPrever.entrada)),
				scope,
			});
			if (preview?.error) {
				throw createApiResponseError(
					preview,
					'Não foi possível validar a alternativa selecionada.',
				);
			}
			if (!isParticionamentoPreviewValido(preview)) {
				setSubmitErrorScope('gerenciamentoTurnos');
				setSubmitError(
					getPreviewBlockMessage(
						preview,
						'Esta alternativa não pode ser confirmada. Revise a jornada e tente novamente.',
					),
				);
				return;
			}
			const entradaAplicar = preview?.acoes?.aplicar?.entrada;
			const previewEtag = getPreenchimentoPreviewEtag(preview);
			if (!entradaAplicar || !previewEtag) {
				throw new Error(
					'A prévia não retornou uma confirmação válida para esta alternativa.',
				);
			}
			setParticionamentoPendente((atual = {}) => ({
				...(pendenciaBase || atual),
				tipo: 'ATUALIZAR_JORNADA_ORIGEM',
				entrada: entradaAplicar,
				action: preview?.acoes?.aplicar || null,
				previewEtag,
				idempotencyKey: `atualizar-jornada-assistida-${Date.now()}-${Math.random()
					.toString(36)
					.slice(2, 10)}`,
				issues: Array.isArray(preview?.issues) ? preview.issues : [],
				alternativaSelecionada: alternativa,
			}));
		} catch (error) {
			const apiResult = error?.apiResult || error;
			if (isPreviewOrSnapshotOutdated(apiResult)) {
				setParticionamentoPendente(null);
				await handleOpenGerenciamentoTurnosModal(
					gerenciamentoTurnosLinha,
				);
				setSubmitErrorScope('gerenciamentoTurnos');
				setSubmitError(
					'A escala foi atualizada enquanto esta alternativa era analisada. Revise os dados e gere uma nova prévia.',
				);
				return;
			}
			setSubmitErrorScope('gerenciamentoTurnos');
			setSubmitError(
				getNonRetryableValidationError(apiResult)
					? getFriendlyProfissionalApiErrorMessage({
							result: apiResult,
							isEditing: true,
						})
					: error?.message ||
							'Não foi possível preparar a alternativa.',
			);
		} finally {
			setIsSubmittingGerenciamentoTurnos(false);
		}
	};

	// Compatibilidade transitória: se algum fluxo ainda criar uma linha pelo
	// endpoint legado, a própria resposta declara a linha que precisa ser
	// adotada. O Swagger modela essa variante de prever com
	// `regime_vinculo_id` (sem um campo `acao` adicional).
	const adotarLinhaLegadaComoOrigem = async ({
		respostaCadastro = {},
		contexto = {},
	} = {}) => {
		const proximaAcao = respostaCadastro?.proxima_acao || {};
		const regimeVinculoId = Number(proximaAcao?.regime_vinculo_id || 0);
		if (!regimeVinculoId) return respostaCadastro;

		const preview = await requestPreverParticionamentoAlocacaoOrigem({
			token,
			scope,
			body: {
				contexto: {
					estabelecimento: getEstabelecimentoIdValue(
						contexto?.estabelecimento ||
							contextoAplicado?.estabelecimento,
					),
					setor: Number(contexto?.setor || 0) || undefined,
					mes_referencia:
						formatDateInputToISO(contexto?.mes_referencia) ||
						contextoAplicado?.mes_referencia,
				},
				regime_vinculo_id: regimeVinculoId,
				particoes: [],
			},
		});
		const entradaAplicar = preview?.acoes?.aplicar?.entrada;
		const previewEtag = getPreenchimentoPreviewEtag(preview);
		const acaoAplicar = preview?.acoes?.aplicar || null;
		if (preview?.error) {
			throw createApiResponseError(
				preview,
				acaoAplicar?.motivo ||
					getPreviewBlockMessage(
						preview,
						'Não foi possível adotar a alocação criada pelo fluxo legado.',
					),
			);
		}
		if (
			!isPreviewAcaoConfirmavel(preview) ||
			acaoAplicar?.disponivel === false ||
			!entradaAplicar ||
			!previewEtag
		) {
			return respostaCadastro;
		}

		setParticionamentoPendente({
			tipo: 'ADOTAR_REGIME_COMO_ALOCACAO_ORIGEM',
			entrada: JSON.parse(JSON.stringify(entradaAplicar)),
			action: acaoAplicar,
			previewEtag,
			idempotencyKey: `adotar-legado-${regimeVinculoId}-${Date.now()}-${Math.random()
				.toString(36)
				.slice(2, 10)}`,
			setorDestino: Number(contexto?.setor || 0),
			origem: { setor: Number(contexto?.setor || 0), regimeVinculoId },
			particoes: [],
			issues: Array.isArray(preview?.issues) ? preview.issues : [],
		});
		return { ...respostaCadastro, adocao_legado_pendente: true };
	};

	const criarAlocacoesIndependentesParticionadas = async ({
		itens = [],
		baseLinha = getGerenciamentoTurnosBaseLinha(),
	} = {}) => {
		for (const item of itens) {
			const setorId = Number(item?.setorId || 0);
			const cpf = normalizeCpf(getCpfFromLinha(baseLinha));
			const { regimePayload, dataInicialIso, dimensionValue } =
				buildGerenciamentoTurnosPayloadData({
					...item,
					sourceLinha: baseLinha,
				});
			if (!setorId || !cpf || !regimePayload?.padroes_alocacao?.length) {
				throw new Error(
					'Não foi possível preparar a alocação independente no setor.',
				);
			}
			const dimensionField =
				modalidade === 'ENFERMAGEM'
					? 'categoria_id'
					: 'especialidade_id';
			const profissionalCadastro =
				gerenciamentoTurnosProfissionalCadastro || {};
			const dimensionValueCadastro =
				Number(profissionalCadastro?.[dimensionField] || 0) ||
				Number(dimensionValue || 0) ||
				undefined;
			const saveResponse = await requestSalvarProfissionalAmbienteEscala({
				token,
				scope,
				body: {
					contexto: {
						estabelecimento: getEstabelecimentoIdValue(
							contextoAplicado.estabelecimento,
						),
						setor: setorId,
						mes_referencia:
							getMesReferenciaFromDateInput(dataInicialIso) ||
							contextoAplicado.mes_referencia,
					},
					cpf,
					profissional: {
						nome: formatProfessionalNameInput(
							profissionalCadastro?.nome ||
								getLinhaDisplayName(baseLinha),
							{ trimEnd: true },
						),
						conselho:
							getConselhoTipoFromLinha(baseLinha) || undefined,
						uf_conselho:
							getUfConselhoFromLinha(baseLinha) || undefined,
						numero_registro:
							normalizeDigits(
								getNumeroConselhoFromLinha(baseLinha),
								20,
							) || undefined,
					},
					vinculo_profissional: {
						vinculo_tipo_id:
							resolveGerenciamentoTurnosVinculoTipoId(
								baseLinha,
								profissionalCadastro,
							),
						data_admissao:
							formatDateInputToISO(
								profissionalCadastro?.admissao ||
									getAdmissaoFromLinha(baseLinha),
							) || undefined,
						[dimensionField]: dimensionValueCadastro,
					},
					regime_escala: {
						...regimePayload,
						[dimensionField]: dimensionValueCadastro,
					},
				},
			});
			if (saveResponse?.error) {
				throw new Error(getParticionamentoPreviewMessage(saveResponse));
			}
			if (modalidade === 'ENFERMAGEM') {
				const adocaoLegado = await adotarLinhaLegadaComoOrigem({
					respostaCadastro: saveResponse,
					contexto: {
						estabelecimento: contextoAplicado.estabelecimento,
						setor: setorId,
						mes_referencia:
							getMesReferenciaFromDateInput(dataInicialIso) ||
							contextoAplicado.mes_referencia,
					},
				});
				if (adocaoLegado?.adocao_legado_pendente) {
					throw new Error(
						'A nova alocação foi preparada e aguarda confirmação antes de ser aplicada.',
					);
				}
			}
		}
	};

	const prepararRemocaoSetorParticionado = async (
		linhaDestino = {},
		{ alocacaoOrigemId: alocacaoOrigemIdInformada = '' } = {},
	) => {
		const setorDestino = Number(getLinhaSetorId(linhaDestino) || 0);
		let linhasProfissional = Array.isArray(
			gerenciamentoTurnosLinha?.setores_linhas,
		)
			? gerenciamentoTurnosLinha.setores_linhas
			: [gerenciamentoTurnosLinha].filter(Boolean);
		const alocacaoOrigemId = String(alocacaoOrigemIdInformada || '').trim();

		// A remoção de um destino é uma nova versão da origem: o estado final
		// precisa conter as demais partições, não apenas os cards visíveis do
		// mês. A rota específica devolve exatamente esse agregado.
		if (token && contextoAplicado) {
			const estadoEstrutural =
				await carregarEstadoAlocacaoOrigemDoProfissional(linhaDestino, {
					alocacaoOrigemId,
				});
			if (
				reconciliarLinhaEstruturalAusente({
					linha: linhaDestino,
					estadoEstrutural,
				})
			) {
				return { removidaDaVisualizacao: true };
			}
			if (estadoEstrutural.linhasProfissional.length > 0) {
				linhasProfissional = estadoEstrutural.linhasProfissional;
			}
		}

		const particoesDoDestino = getParticoesPersistidas(
			linhasProfissional,
		).filter(
			(particao = {}) =>
				Number(particao?.setor_destino_id || 0) === setorDestino,
		);
		if (!setorDestino || particoesDoDestino.length === 0) return false;

		const padroesDoDestino = new Set(
			particoesDoDestino
				.map((particao = {}) =>
					String(particao?.padrao_origem_id || ''),
				)
				.filter(Boolean),
		);
		const linhasMarcadasComoOrigem =
			linhasProfissional.filter(isLinhaOrigemDeSplit);
		const candidatasOrigem =
			linhasMarcadasComoOrigem.length > 0
				? linhasMarcadasComoOrigem
				: linhasProfissional.filter(
						(linha = {}) => !isLinhaDestinoDeSplit(linha),
					);
		const origensPorPadrao = candidatasOrigem.filter((linha = {}) =>
			getPadroesAlocacaoOrigem(linha).some((padrao = {}) =>
				padroesDoDestino.has(String(getPadraoOrigemId(padrao) || '')),
			),
		);
		const origensCompativeis =
			origensPorPadrao.length > 0
				? origensPorPadrao
				: candidatasOrigem.filter(
						(linha = {}) =>
							Boolean(alocacaoOrigemId) &&
							String(getAlocacaoOrigemId(linha) || '') ===
								alocacaoOrigemId,
					);
		if (origensCompativeis.length !== 1) {
			throw new Error(
				'Não foi possível identificar uma única jornada de origem para remover este recorte. Atualize a escala e tente novamente.',
			);
		}

		const linhaOrigem = origensCompativeis[0];
		const padroesOrigemDaJornada = getPadroesAlocacaoOrigem(linhaOrigem);
		const padroesOrigem = new Set(
			padroesOrigemDaJornada
				.map((padrao = {}) => String(getPadraoOrigemId(padrao) || ''))
				.filter(Boolean),
		);
		const particoesDaOrigem = getParticoesPersistidas(
			linhasProfissional,
		).filter((particao = {}) => {
			const pertenceOrigem = padroesOrigem.has(
				String(particao?.padrao_origem_id || ''),
			);
			return pertenceOrigem;
		});
		const estadoFinalRemocao = montarEstadoFinalOrigemSemDestino({
			padroesOrigem: padroesOrigemDaJornada,
			particoes: particoesDaOrigem,
			setorDestinoId: setorDestino,
		});
		const particoesFinais = estadoFinalRemocao.particoesFinais;
		const regimeVinculoId = getLinhaRegimeVinculoId(linhaOrigem);
		const alocacaoOrigemIdFinal =
			alocacaoOrigemId || getAlocacaoOrigemId(linhaOrigem);
		const versaoEsperada = getVersaoEsperadaAlocacaoOrigem(linhaOrigem);
		if (
			(!regimeVinculoId && !alocacaoOrigemIdFinal) ||
			(alocacaoOrigemIdFinal && !versaoEsperada)
		) {
			throw new Error(
				'Não foi possível identificar a jornada de origem para remover este recorte.',
			);
		}

		const configuracaoPersistida =
			linhaOrigem?.alocacao_origem?.configuracao ||
			linhaOrigem?.regime_escala?.alocacao_origem?.configuracao ||
			{};
		const campoDimensao =
			modalidade === 'ENFERMAGEM' ? 'categoria_id' : 'especialidade_id';
		const configuracaoFinal = Object.fromEntries(
			Object.entries({
				...configuracaoPersistida,
				vinculo_profissional_id:
					configuracaoPersistida?.vinculo_profissional_id ||
					getVinculoIdFromLinha(linhaOrigem),
				setor_origem_id:
					configuracaoPersistida?.setor_origem_id ||
					Number(getLinhaSetorId(linhaOrigem) || 0),
				[campoDimensao]:
					configuracaoPersistida?.[campoDimensao] ||
					Number(getEspecialidadeIdFromLinha(linhaOrigem)) ||
					undefined,
				regime:
					configuracaoPersistida?.regime ||
					linhaOrigem?.regime_escala?.regime ||
					undefined,
				vigencia_inicio:
					configuracaoPersistida?.vigencia_inicio ||
					getDataInicialProfissionalEscalaIsoFromLinha(linhaOrigem) ||
					undefined,
				vigencia_fim: configuracaoPersistida?.vigencia_fim ?? null,
				padroes_origem: estadoFinalRemocao.padroesFinais,
			}).filter(([, value]) => value !== undefined),
		);
		const body = {
			contexto: {
				estabelecimento: getEstabelecimentoIdValue(
					contextoAplicado.estabelecimento,
				),
				setor: Number(getLinhaSetorId(linhaOrigem) || 0),
				mes_referencia: contextoAplicado.mes_referencia,
			},
			...(alocacaoOrigemIdFinal
				? {
						alocacao_origem_id: alocacaoOrigemIdFinal,
						versao_esperada: versaoEsperada,
					}
				: { regime_vinculo_id: regimeVinculoId }),
			configuracao: configuracaoFinal,
			particoes: particoesFinais,
			justificativa: 'Remoção de recorte de jornada entre setores',
		};
		const preview = await requestPreverParticionamentoAlocacaoOrigem({
			token,
			body,
			scope,
		});
		if (preview?.error) {
			throw createApiResponseError(
				preview,
				getParticionamentoPreviewMessage(preview),
			);
		}
		if (!isParticionamentoPreviewValido(preview)) return false;
		const entradaAplicar = preview?.acoes?.aplicar?.entrada;
		const previewEtag = getPreenchimentoPreviewEtag(preview);
		if (!entradaAplicar || !previewEtag) {
			throw new Error(
				'Não foi possível preparar a remoção do recorte. Atualize a escala e tente novamente.',
			);
		}

		setParticionamentoPendente({
			tipo: 'REMOVER_DESTINO_PARTICIONADO',
			entrada: entradaAplicar,
			action: preview?.acoes?.aplicar || null,
			previewEtag,
			idempotencyKey: `remover-particao-${Date.now()}-${Math.random()
				.toString(36)
				.slice(2, 10)}`,
			setorDestino,
			origem: {
				setor: Number(getLinhaSetorId(linhaOrigem) || 0),
			},
			particoes: particoesFinais,
			linhasRemovidas: [linhaDestino],
			issues: Array.isArray(preview?.issues) ? preview.issues : [],
		});
		return true;
	};

	const redirecionarExclusaoParaFluxoJornada = async ({
		linhaDestino = {},
		response = {},
	} = {}) => {
		if (!exigeFluxoJornada(response)) return false;
		const alocacaoOrigemId = getAlocacaoOrigemIdFromPayload(response);
		if (!alocacaoOrigemId) {
			throw new Error(
				'O servidor informou que esta exclusão exige o fluxo de jornada, mas não retornou a alocação de origem necessária para refazer a operação.',
			);
		}
		// O erro pode chegar antes que o resumo mensal tenha informado se a linha
		// é origem ou destino. Consulte o agregado e redirecione apenas o
		// destino real. Para a própria origem, o contrato continua sendo o
		// preview genérico ENCERRAR_ALOCACAO_SETOR; trocar para partições aqui
		// encerraria a entidade errada.
		const resolucao = await resolverLinhaEstruturalParaExclusao({
			linha: linhaDestino,
			alocacaoOrigemId,
		});
		if (resolucao?.papelSetor !== 'DESTINO') return false;
		return prepararRemocaoSetorParticionado(resolucao.linha, {
			alocacaoOrigemId,
		});
	};

	const confirmarParticionamentoNovoSetor = async () => {
		if (!particionamentoPendente || !token || !contextoAplicado) return;
		gerenciamentoTurnosRascunhoProtegidoRef.current = true;
		setIsSubmittingGerenciamentoTurnos(true);
		setSubmitError('');
		setSubmitErrorScope('');
		try {
			const tipoParticionamento = particionamentoPendente.tipo;
			const setorDestino = particionamentoPendente.setorDestino;
			const novoItemPendente = particionamentoPendente.novoItem || {};
			const baseLinha =
				particionamentoPendente.linhaOrigemProfissional ||
				getGerenciamentoTurnosBaseLinha();
			registrarOperacaoGerenciamentoTurnos({
				operacao: 'APLICAR_ATUALIZACAO_JORNADA',
				setorId: setorDestino,
				itemAtualizado: novoItemPendente,
				campos: { tipo_particionamento: tipoParticionamento },
				payload: particionamentoPendente.entrada,
			});
			const response = particionamentoPendente.entrada
				? await requestAplicarParticionamentoAlocacaoOrigem({
						token,
						body: particionamentoPendente.entrada,
						previewEtag: particionamentoPendente.previewEtag,
						idempotencyKey: particionamentoPendente.idempotencyKey,
						action: particionamentoPendente.action,
						scope,
					})
				: {};
			registrarOperacaoGerenciamentoTurnos({
				operacao: 'RESULTADO_APLICAR_ATUALIZACAO_JORNADA',
				setorId: setorDestino,
				itemAtualizado: novoItemPendente,
				resultado: {
					error: response?.error || null,
					versao: response?.versao || response?.resultado?.versao,
					acoes: response?.acoes || response?.resultado?.acoes,
				},
			});
			if (response?.error) {
				throw createApiResponseError(
					response,
					getParticionamentoPreviewMessage(response),
				);
			}
			const acoesPosAplicacao =
				response?.acoes || response?.resultado?.acoes || {};
			// A aplicação devolve as leituras autoritativas a seguir. Executá-las
			// evita reutilizar o setor que estava selecionado antes do apply (em
			// especial quando um destino acabou de ser removido da jornada).
			const leiturasPosAplicacao = await Promise.all([
				acoesPosAplicacao?.consultar_estado_atual
					? requestExecutarAcaoEdicaoAlocacaoOrigem({
							token,
							action: acoesPosAplicacao.consultar_estado_atual,
							body: acoesPosAplicacao.consultar_estado_atual
								.entrada,
							scope,
						})
					: Promise.resolve(null),
				acoesPosAplicacao?.consultar_origem
					? requestExecutarAcaoEdicaoAlocacaoOrigem({
							token,
							action: acoesPosAplicacao.consultar_origem,
							body: acoesPosAplicacao.consultar_origem.entrada,
							scope,
						})
					: Promise.resolve(null),
			]);
			if (leiturasPosAplicacao.some((leitura = {}) => leitura?.error)) {
				throw createApiResponseError(
					leiturasPosAplicacao.find((leitura = {}) => leitura?.error),
					'Não foi possível reconciliar a jornada após a aplicação.',
				);
			}
			const estadoAplicado =
				response?.estado_aplicado ||
				response?.resultado?.estado_aplicado ||
				null;
			const linhasReconciliadasPelasAcoes = [
				...leiturasPosAplicacao.flatMap((leitura = {}) => [
					...(Array.isArray(leitura?.linhas) ? leitura.linhas : []),
					...(Array.isArray(leitura?.ambiente?.linhas)
						? leitura.ambiente.linhas
						: []),
				]),
				...(estadoAplicado
					? buildLinhasEstruturaisDaAlocacaoOrigem(estadoAplicado)
					: []),
			];
			if (particionamentoPendente?.alocacoesIndependentes?.length > 0) {
				// A confirmação só pode aplicar a entrada assinada pelo preview. A
				// criação de origem independente é uma escolha explícita e chega aqui
				// como `CRIAR_ORIGEM_INDEPENDENTE`, já com a ação HATEOAS do backend;
				// não complemente este apply com a rota legado de ambiente.
				throw new Error(
					'Existem setores independentes não confirmados. Gere uma nova prévia e escolha explicitamente a criação de carga independente.',
				);
			}
			const alocacaoOrigemId =
				getAlocacaoOrigemIdFromPayload(response) ||
				getAlocacaoOrigemIdFromPayload(
					particionamentoPendente?.entrada,
				);
			const particoesAplicadas =
				getParticoesFromPayload(response).length > 0
					? getParticoesFromPayload(response)
					: getParticoesFromPayload(particionamentoPendente?.entrada)
								.length > 0
						? getParticoesFromPayload(
								particionamentoPendente.entrada,
							)
						: particionamentoPendente?.particoes || [];
			if (alocacaoOrigemId) {
				setGerenciamentoTurnosLinha((prev) =>
					persistirEstadoParticionadoNaLinha(prev, {
						alocacaoOrigemId,
						particoes: particoesAplicadas,
					}),
				);
				setGerenciamentoTurnosSetoresEditaveis((prev) =>
					prev.map((item = {}) => ({
						...item,
						sourceLinha: persistirEstadoParticionadoNaLinha(
							item?.sourceLinha || {},
							{
								alocacaoOrigemId,
								particoes: particoesAplicadas,
							},
						),
					})),
				);
			}
			// A aplicação pode devolver apenas o resultado congelado. Releia o
			// agregado oficial antes de reconstruir as abas, para não depender de
			// cards que ainda estejam sendo projetados na competência atual.
			let estadoEstruturalAtualizado = null;
			try {
				estadoEstruturalAtualizado =
					await carregarEstadoAlocacaoOrigemDoProfissional(baseLinha);
			} catch {
				// A mutação já foi aceita; o refresh mensal abaixo permanece como
				// contingência visual e não deve reportar a aplicação como falha.
			}
			if (tipoParticionamento === 'REMOVER_DESTINO_PARTICIONADO') {
				removeSetoresFromGerenciamentoTurnosModal(
					particionamentoPendente.linhasRemovidas || [],
				);
				setParticionamentoPendente(null);
				setIsDeleteFromGerenciamentoTurnos(false);
				const ambienteAtualizado = await loadAmbiente({
					authToken: token,
					targetContexto:
						getContextoVisualizacaoRequest(contextoAplicado),
					showLoading: false,
					// A remoção de um destino altera a versão da jornada. Não aceite
					// uma representação em cache depois de confirmar a aplicação.
					forceRefresh: true,
				});
				const setorOrigem = Number(
					particionamentoPendente?.origem?.setor || 0,
				);
				const contextoOrigem = {
					...getContextoVisualizacaoRequest(contextoAplicado),
					setor:
						setorOrigem ||
						getContextoVisualizacaoRequest(contextoAplicado).setor,
				};
				const ambienteOrigem = setorOrigem
					? await requestAbrirAmbientePreenchimentoEscala({
							token,
							contexto: contextoOrigem,
							modo: 'detalhado',
							scope,
						})
					: null;
				const linhasAtualizadas = [
					...(Array.isArray(response?.linhas) ? response.linhas : []),
					...(Array.isArray(response?.ambiente?.linhas)
						? response.ambiente.linhas
						: []),
					...linhasReconciliadasPelasAcoes,
					...(Array.isArray(ambienteAtualizado?.linhas)
						? ambienteAtualizado.linhas
						: []),
					...(Array.isArray(ambienteOrigem?.linhas)
						? ambienteOrigem.linhas
						: []),
					...(estadoEstruturalAtualizado?.linhasProfissional || []),
				];
				const cpfProfissional = normalizeCpf(
					getCpfFromLinha(baseLinha),
				);
				const profissionalId = getProfissionalIdFromLinha(baseLinha);
				const linhasProfissional = removerLinhasEstruturaisDuplicadas(
					linhasAtualizadas.filter((linhaAtualizada = {}) => {
						const cpfLinha = normalizeCpf(
							getCpfFromLinha(linhaAtualizada),
						);
						const profissionalLinhaId =
							getProfissionalIdFromLinha(linhaAtualizada);
						return Boolean(
							(profissionalId &&
								profissionalLinhaId === profissionalId) ||
							(cpfProfissional && cpfLinha === cpfProfissional),
						);
					}),
				);
				const linhaModalAtualizada =
					consolidateLinhasPorProfissional(linhasProfissional)[0] ||
					null;
				if (linhaModalAtualizada) {
					const itensAtualizados =
						buildGerenciamentoTurnosEditableItems(
							buildTurnosSetores({
								linha: linhaModalAtualizada,
								setorOptions,
								contextoAplicado,
								regimesOptions: Array.isArray(
									ambienteOpcoes?.regimes,
								)
									? ambienteOpcoes.regimes
									: [],
							}),
						);
					setGerenciamentoTurnosLinha(linhaModalAtualizada);
					setGerenciamentoTurnosSetoresEditaveis(itensAtualizados);
					setGerenciamentoTurnosSetoresAbertos((prev) =>
						itensAtualizados.reduce(
							(acc, item) => ({
								...acc,
								[item.id]: prev[item.id] ?? true,
							}),
							{},
						),
					);
				}
				setFeedbackMessage(
					'Recorte removido e jornada de origem liberada.',
				);
				return;
			}

			const ambienteAtualizado = await loadAmbiente({
				authToken: token,
				targetContexto:
					getContextoVisualizacaoRequest(contextoAplicado),
				showLoading: false,
				forceRefresh: true,
			});
			const contextoDestino = {
				...getContextoVisualizacaoRequest(contextoAplicado),
				setor: setorDestino,
			};
			const ambienteDestino =
				String(contextoDestino.setor) ===
				String(getContextoVisualizacaoRequest(contextoAplicado).setor)
					? null
					: await requestAbrirAmbientePreenchimentoEscala({
							token,
							contexto: contextoDestino,
							modo: 'detalhado',
							scope,
							forceRefresh: true,
						});
			const linhasAtualizadas = [
				...(Array.isArray(response?.linhas) ? response.linhas : []),
				...(Array.isArray(response?.ambiente?.linhas)
					? response.ambiente.linhas
					: []),
				...linhasReconciliadasPelasAcoes,
				...(Array.isArray(ambienteAtualizado?.linhas)
					? ambienteAtualizado.linhas
					: []),
				...(Array.isArray(ambienteDestino?.linhas)
					? ambienteDestino.linhas
					: []),
				...(estadoEstruturalAtualizado?.linhasProfissional || []),
			];
			const cpfProfissional = normalizeCpf(getCpfFromLinha(baseLinha));
			const profissionalId = getProfissionalIdFromLinha(baseLinha);
			const linhasProfissional = removerLinhasEstruturaisDuplicadas(
				linhasAtualizadas.filter((linhaAtualizada = {}) => {
					const cpfLinha = normalizeCpf(
						getCpfFromLinha(linhaAtualizada),
					);
					const profissionalLinhaId =
						getProfissionalIdFromLinha(linhaAtualizada);
					return Boolean(
						(profissionalId &&
							profissionalLinhaId === profissionalId) ||
						(cpfProfissional && cpfLinha === cpfProfissional),
					);
				}),
			);
			const linhaModalAtualizada =
				consolidateLinhasPorProfissional(linhasProfissional)[0] || null;
			const linhaModalComEstadoParticionado =
				linhaModalAtualizada && alocacaoOrigemId
					? persistirEstadoParticionadoNaLinha(linhaModalAtualizada, {
							alocacaoOrigemId,
							particoes: particoesAplicadas,
						})
					: linhaModalAtualizada;
			const itensAtualizados = linhaModalAtualizada
				? buildGerenciamentoTurnosEditableItems(
						buildTurnosSetores({
							linha: linhaModalComEstadoParticionado,
							setorOptions,
							contextoAplicado,
							regimesOptions: Array.isArray(
								ambienteOpcoes?.regimes,
							)
								? ambienteOpcoes.regimes
								: [],
						}),
					)
				: [];
			const novaAbaJaAtualizada = itensAtualizados.some(
				(item) => String(item.setorId) === String(setorDestino),
			);
			const setorCadastro = setorOptions.find(
				(setor = {}) =>
					String(setor?.id || setor?.value || '') ===
					String(setorDestino),
			);
			const itemAtualizadoDestino = itensAtualizados.find(
				(item = {}) => String(item.setorId) === String(setorDestino),
			);
			const itemPendente = novoItemPendente?.setorId
				? buildGerenciamentoTurnosEditableItems([
						{
							...novoItemPendente,
							id: `${novoItemPendente.setorId}-novo-setor`,
							setorId: String(novoItemPendente.setorId),
							setorNome: formatSetorOptionLabel(setorCadastro),
							regime: novoItemPendente.regimeValue || '',
							dataInicial:
								novoItemPendente.dataInicialValue || '',
							// Quando a projeção mensal demora para incluir o destino, não
							// reutilize a linha da origem: isso fazia a nova aba aparecer
							// como setor-base e, em alguns casos, desaparecer no merge.
							sourceLinha: (() => {
								const dataInicialVariante =
									formatDateInputToISO(
										novoItemPendente.dataInicialValue,
									) || getTodayISODateLocal();
								const linhaDestino =
									itemAtualizadoDestino?.sourceLinha ||
									(alocacaoOrigemId
										? persistirEstadoParticionadoNaLinha(
												baseLinha,
												{
													alocacaoOrigemId,
													particoes:
														particoesAplicadas,
												},
											)
										: baseLinha);
								return {
									...linhaDestino,
									linha_id:
										linhaDestino?.linha_id ||
										`${novoItemPendente.setorId}-destino-pendente`,
									setor: linhaDestino?.setor ||
										setorCadastro || {
											id: Number(setorDestino),
										},
									setor_id: Number(setorDestino),
									vigencia_inicio: dataInicialVariante,
									data_inicial: dataInicialVariante,
									regime_escala: {
										...(linhaDestino?.regime_escala || {}),
										vigencia_inicio: dataInicialVariante,
										data_ancora: dataInicialVariante,
										projecao_split: {
											...(linhaDestino?.regime_escala
												?.projecao_split || {}),
											derivada_de_split: true,
											papel_setor: 'DESTINO',
											alocacao_origem_id:
												alocacaoOrigemId,
										},
									},
								};
							})(),
						},
					])[0]
				: null;
			const itensParaExibir = itemPendente
				? [
						...itensAtualizados.filter(
							(item = {}) =>
								String(item.setorId) !== String(setorDestino),
						),
						itemPendente,
					]
				: itensAtualizados;

			// Só troca a fonte do modal quando a leitura já contém o destino. Isso
			// evita que uma leitura momentaneamente defasada esconda a aba criada.
			if (linhaModalComEstadoParticionado && novaAbaJaAtualizada) {
				setGerenciamentoTurnosLinha(linhaModalComEstadoParticionado);
			}
			if (itensParaExibir.length > 0) {
				setGerenciamentoTurnosSetoresEditaveis(itensParaExibir);
				setGerenciamentoTurnosSetoresAbertos((prev) =>
					itensParaExibir.reduce(
						(acc, item) => ({
							...acc,
							[item.id]:
								String(item.setorId) === String(setorDestino)
									? true
									: prev[item.id] || false,
						}),
						{},
					),
				);
			}

			resetGerenciamentoTurnosNovoSetor();
			setParticionamentoPendente(null);
			if (particionamentoPendente.originatedFromProfessionalModal) {
				if (tipoParticionamento === 'CRIAR_ORIGEM_INICIAL') {
					cadastroVinculoPendenteRef.current = null;
				}
				setIsNovoProfissionalModalOpen(false);
				setIsEditingProfissional(false);
				setEditingProfissionalLinha(null);
			}
			setFeedbackMessage(
				tipoParticionamento === 'CRIAR_ORIGEM_INICIAL'
					? 'Jornada de origem criada com sucesso.'
					: tipoParticionamento === 'CRIAR_ORIGEM_INDEPENDENTE'
						? 'Carga independente criada com sucesso.'
						: tipoParticionamento === 'ATUALIZAR_JORNADA_ORIGEM'
							? 'Jornada de origem atualizada com sucesso.'
							: 'Jornada dividida entre os setores com sucesso.',
			);
			if (
				tipoParticionamento === 'ATUALIZAR_JORNADA_ORIGEM' &&
				!particionamentoPendente.originatedFromProfessionalModal
			) {
				// A origem foi versionada. Reabrimos a partir da leitura canônica em
				// vez de manter a aba reconstruída com o snapshot que gerou o apply;
				// assim a próxima edição usa a versão e os horários recém-persistidos.
				gerenciamentoTurnosRascunhoProtegidoRef.current = false;
				await handleOpenGerenciamentoTurnosModal(
					linhaModalComEstadoParticionado ||
						linhaModalAtualizada ||
						baseLinha,
				);
			}
		} catch (error) {
			const apiResult = error?.apiResult || error;
			if (isPreviewOrSnapshotOutdated(apiResult)) {
				setParticionamentoPendente(null);
				gerenciamentoTurnosRascunhoProtegidoRef.current = false;
				await handleOpenGerenciamentoTurnosModal(
					gerenciamentoTurnosLinha,
				);
				setSubmitErrorScope('gerenciamentoTurnos');
				setSubmitError(
					'A escala foi atualizada antes da confirmação. Revise os dados e gere uma nova prévia.',
				);
				return;
			}
			setSubmitErrorScope('gerenciamentoTurnosNovoSetor');
			setSubmitError(
				getNonRetryableValidationError(apiResult)
					? getFriendlyProfissionalApiErrorMessage({
							result: apiResult,
							isEditing: true,
						})
					: String(error?.message || '') ||
							'Não foi possível aplicar a divisão de jornada.',
			);
		} finally {
			gerenciamentoTurnosRascunhoProtegidoRef.current = false;
			setIsSubmittingGerenciamentoTurnos(false);
		}
	};

	const handleSubmitGerenciamentoTurnos = async ({
		criarNovoSetor = isGerenciamentoTurnosNovoSetorAberto,
		usarSugestoes = false,
	} = {}) => {
		if (!token || !contextoAplicado || !gerenciamentoTurnosLinha) return;
		const setoresEditaveisAtuais =
			gerenciamentoTurnosSetoresEditaveisRef.current.length > 0
				? gerenciamentoTurnosSetoresEditaveisRef.current
				: gerenciamentoTurnosSetoresEditaveis;
		const setoresComHorariosVisiveis = setoresEditaveisAtuais.map(
			sincronizarHorariosCicloVisiveis,
		);
		// O planejamento por sugestões está temporariamente indisponível na
		// interface. A criação de setor segue somente os campos manuais.
		const planejamentoDiretoAtivo = false;
		// A variante preserva a vigência da jornada de origem, exibida como campo
		// somente leitura no modal. Interface, preview e apply usam a mesma data.
		const dataInicialNovoSetor =
			gerenciamentoTurnosNovoDataInicial ||
			formatISOToDisplay(getTodayISODateLocal());
		const presetPlanejado = normalizeCode(
			planejamentoNovoSetorForm?.preset,
		);
		const diasPlanejados =
			presetPlanejado === 'SEMANAL'
				? normalizeDiasSemanaValues(
						planejamentoNovoSetorForm?.diasPadrao,
					)
				: [];
		const horariosPlanejados = diasPlanejados.reduce((acc, dia) => {
			acc[dia] = {
				hora_inicio: planejamentoNovoSetorForm?.horaInicio || '',
				hora_fim: planejamentoNovoSetorForm?.horaFim || '',
			};
			return acc;
		}, {});

		const novoItem = {
			id: 'novo',
			setorId: planejamentoDiretoAtivo
				? planejamentoNovoSetorForm?.setorId
				: gerenciamentoTurnosNovoSetor,
			regimeValue: planejamentoDiretoAtivo
				? normalizeCode(
						gerenciamentoTurnosNovoRegime ||
							gerenciamentoTurnosLinha?.regime_escala?.regime ||
							'PLANTONISTA',
					)
				: gerenciamentoTurnosNovoRegime,
			recorrenciaValue: planejamentoDiretoAtivo
				? presetPlanejado
				: gerenciamentoTurnosNovoRecorrencia,
			nomeRecorrencia: planejamentoDiretoAtivo
				? presetPlanejado
				: gerenciamentoTurnosNovoRecorrencia,
			tipoRecorrencia: planejamentoDiretoAtivo
				? presetPlanejado
				: getGerenciamentoTurnosRecorrenciaOption(
						gerenciamentoTurnosNovoRecorrencia,
					)?.tipo_recorrencia || 'SEMANAL',
			presetCiclo: planejamentoDiretoAtivo
				? presetPlanejado
				: getGerenciamentoTurnosRecorrenciaOption(
						gerenciamentoTurnosNovoRecorrencia,
					)?.preset_ciclo || 'SEMANAL',
			dataInicialValue: criarNovoSetor
				? dataInicialNovoSetor
				: gerenciamentoTurnosNovoDataInicial,
			diasSemanaValores: planejamentoDiretoAtivo
				? diasPlanejados
				: gerenciamentoTurnosNovoDias,
			horariosByDia: planejamentoDiretoAtivo
				? horariosPlanejados
				: gerenciamentoTurnosNovoHorarios,
			horaInicio: planejamentoDiretoAtivo
				? planejamentoNovoSetorForm?.horaInicio
				: gerenciamentoTurnosNovoHoraInicio,
			horaFim: planejamentoDiretoAtivo
				? planejamentoNovoSetorForm?.horaFim
				: gerenciamentoTurnosNovoHoraFim,
			periodoRotacao: planejamentoDiretoAtivo
				? planejamentoNovoSetorForm?.periodoRotacao
				: gerenciamentoTurnosNovoUsaRotacaoPeriodica
					? gerenciamentoTurnosNovoPeriodoRotacao
					: undefined,
			semanasRotacao: planejamentoDiretoAtivo
				? planejamentoNovoSetorForm?.semanasRotacao
				: gerenciamentoTurnosNovoUsaRotacaoPeriodica
					? gerenciamentoTurnosNovoSemanasRotacao
					: [],
			datasEspecificasEditaveis: gerenciamentoTurnosNovoDatas,
			dimensionValue:
				modalidade === 'ENFERMAGEM'
					? gerenciamentoTurnosProfissionalCadastro?.categoria_id
					: gerenciamentoTurnosProfissionalCadastro?.especialidade_id,
			sourceLinha: getGerenciamentoTurnosBaseLinha(),
		};
		registrarOperacaoGerenciamentoTurnos({
			operacao: criarNovoSetor
				? 'SOLICITAR_CRIACAO_NOVO_SETOR'
				: 'SOLICITAR_SALVAMENTO_SETORES',
			setorId: criarNovoSetor ? novoItem.setorId : undefined,
			itemAtualizado: criarNovoSetor ? novoItem : undefined,
			campos: {
				setores_com_alteracao: setoresComHorariosVisiveis
					.filter(hasGerenciamentoTurnosItemChanges)
					.map((item = {}) => ({
						setor_id: item.setorId,
						dias_semana: item.diasSemanaValores,
						horarios_por_dia: item.horariosByDia,
					})),
			},
		});
		const validationErrors = setoresComHorariosVisiveis.reduce(
			(acc, item) => ({
				...acc,
				...validateGerenciamentoTurnosItem(item, `${item.id}.`),
			}),
			{},
		);

		if (criarNovoSetor) {
			if (!novoItem.setorId) {
				validationErrors['novo.setor'] = 'Selecione o setor.';
			}
			if (
				planejamentoDiretoAtivo &&
				!planejamentoNovoSetorForm?.isValid
			) {
				validationErrors['novo.planejamento'] =
					'Preencha os dias e horários da escala antes de criar o novo setor.';
			}
			Object.assign(
				validationErrors,
				validateGerenciamentoTurnosItem(novoItem, 'novo.'),
			);
		}

		if (Object.keys(validationErrors).length > 0) {
			setGerenciamentoTurnosErrors(validationErrors);
			return;
		}

		// Salvar sem nenhuma edição não é uma operação de atualização. Além de
		// evitar chamadas desnecessárias, preservamos a linha que abriu o modal:
		// o ambiente detalhado pode conter versões históricas do mesmo setor e,
		// ao consolidá-las novamente, a primeira versão poderia substituir a
		// representação que o usuário estava apenas visualizando.
		const itensEditados = setoresComHorariosVisiveis.filter(
			hasGerenciamentoTurnosItemChanges,
		);
		if (!criarNovoSetor && itensEditados.length === 0) {
			setGerenciamentoTurnosErrors({});
			setSubmitError('');
			setSubmitErrorScope('');
			setFeedbackMessage(
				'Nenhuma alteração foi feita nos turnos do profissional.',
			);
			return;
		}

		setGerenciamentoTurnosErrors({});
		setIsSubmittingGerenciamentoTurnos(true);
		setSubmitError('');
		setSubmitErrorScope('');
		setSugestaoJornadaDisponivel(false);
		setProfissionalHorarioSobrepostoPopup(null);

		try {
			const baseLinha = getGerenciamentoTurnosBaseLinha();
			const profissionalCadastro =
				gerenciamentoTurnosProfissionalCadastro || {};
			const cpf = normalizeCpf(getCpfFromLinha(baseLinha));
			const dimensionField =
				modalidade === 'ENFERMAGEM'
					? 'categoria_id'
					: 'especialidade_id';
			const applyLinhas = [];
			let novoSetorCriadoItem = null;
			let novaLinhaSetorCriada = null;
			const submitAttemptId = `gerenciamento-turnos-${Date.now()}-${Math.random()
				.toString(36)
				.slice(2, 10)}`;
			const itensDaJornadaOrigem = itensEditados.filter((item = {}) =>
				exigeEdicaoViaAlocacaoOrigem(item?.sourceLinha || baseLinha),
			);

			// Uma alocação de origem é um agregado: regime, recorrência, vigência e
			// horários precisam versionar a jornada e todas as suas partições numa
			// única operação. Os writers ALTERAR_TURNO/ALTERAR_REGIME são válidos
			// somente para as linhas independentes e o backend os bloqueia nas
			// linhas de origem/destino com ALTERACAO_ESTRUTURAL_EXIGE_FLUXO_JORNADA.
			const possuiOperacaoEstrutural = itensDaJornadaOrigem.length > 0;
			if (possuiOperacaoEstrutural) {
				if (
					itensDaJornadaOrigem.length > 0 &&
					itensDaJornadaOrigem.length !== itensEditados.length
				) {
					throw new Error(
						'Salve a atualização da jornada compartilhada separadamente das alterações dos setores independentes.',
					);
				}

				// O modal pode reunir várias origens independentes. A primeira aba
				// (`baseLinha`) é apenas uma referência visual e não pode ser tratada
				// como edição quando a alteração pertence a outra origem. Incluí-la
				// aqui fazia uma única edição disparar falsamente o bloqueio de duas
				// jornadas compartilhadas, antes mesmo do POST de preview.
				const origensEditadas = new Set(
					itensDaJornadaOrigem
						.map((item = {}) =>
							String(
								getAlocacaoOrigemId(item?.sourceLinha || {}) ||
									'',
							).trim(),
						)
						.filter(Boolean),
				);
				if (origensEditadas.size > 1) {
					throw new Error(
						'Edite uma jornada compartilhada por vez para revisar o impacto em todos os seus setores.',
					);
				}

				// Não encaminhe uma linha de origem ao writer genérico. A operação
				// alocacao-origem/prever recebe a configuração e as partições finais,
				// devolve a ação canônica e sua revisão para aplicação posterior.
				// Tanto a origem quanto qualquer uma de suas partições podem iniciar
				// a edição. O detalhe estrutural identifica a origem efetiva e a
				// ação HATEOAS versiona a configuração completa com todas as
				// partições finais; nunca encaminhamos uma faixa derivada ao writer
				// genérico de regime/turno.
				const alocacaoOrigemEditada =
					Array.from(origensEditadas)[0] || '';
				const itensDaJornadaVisiveis =
					setoresComHorariosVisiveis.filter(
						(item = {}) =>
							String(
								getAlocacaoOrigemId(
									item?.sourceLinha || baseLinha,
								) || '',
							).trim() === alocacaoOrigemEditada,
					);
				// A configuração pertence à origem, mas a alteração de horário de um
				// setor variante pertence à sua partição. Antes usávamos somente um
				// item como origem e descartávamos os destinos editados; a prévia era
				// confirmada com o mesmo payload e nenhum horário mudava.
				const itemOrigem =
					itensDaJornadaVisiveis.find((item = {}) =>
						isLinhaOrigemDeSplit(item?.sourceLinha || {}),
					) ||
					itensDaJornadaVisiveis.find(
						(item = {}) =>
							!isLinhaDestinoDeSplit(item?.sourceLinha || {}),
					) ||
					itensDaJornadaOrigem[0];
				const itensDestinoEditados = itensDaJornadaOrigem.filter(
					(item = {}) =>
						isLinhaDestinoDeSplit(item?.sourceLinha || {}),
				);
				if (!itemOrigem) {
					throw new Error(
						'Não foi possível identificar o setor de origem desta jornada. Atualize a escala e tente novamente.',
					);
				}
				await prepararAtualizacaoSetoresParticionados(
					itensDestinoEditados,
					{
						itemOrigem,
						novosItens: criarNovoSetor ? [novoItem] : [],
					},
				);
				return;
			}

			// Sem origem conhecida, esta é a primeira jornada do vínculo. A criação
			// permanece no fluxo canônico de preview -> confirmação -> apply.
			if (criarNovoSetor) {
				await prepararNovoSetorNaJornadaDoProfissional(
					novoItem,
					baseLinha,
					{ usarSugestoes },
				);
				return;
			}

			const updateResults = [];
			for (const [itemIndex, item] of itensEditados.entries()) {
				const linhaSetor = item.sourceLinha || baseLinha;
				const { regimePayload, dataInicialIso } =
					buildGerenciamentoTurnosPayloadData(item);
				const regimeVinculoId =
					Number(
						item.regimeVinculoId ||
							linhaSetor?.regime_vinculo_id ||
							0,
					) || 0;
				const setorId = Number(
					item.setorId || getLinhaSetorId(linhaSetor),
				);
				if (!regimeVinculoId) {
					updateResults.push({
						error: {
							message:
								'Não foi possível identificar a linha deste setor para edição.',
						},
						item,
					});
					continue;
				}
				const previewBody = buildGerenciamentoTurnosActionCommand({
					item,
					linhaSetor,
					regimePayload,
					dataInicialIso,
					regimeVinculoId,
				});
				if (!previewBody) continue;

				const previewResponse = await requestPreverAcoesAmbienteEscala({
					token,
					body: previewBody,
					scope,
				});
				if (previewResponse?.error) {
					updateResults.push({ response: previewResponse, item });
					continue;
				}
				if (!isPreviewAcaoConfirmavel(previewResponse)) {
					updateResults.push({
						error: {
							message: getPreviewBlockMessage(
								previewResponse,
								'Esta alteração não pode ser confirmada.',
							),
						},
						item,
					});
					continue;
				}
				const applyAction = previewResponse?.acoes?.aplicar || null;
				if (applyAction?.disponivel === false) {
					updateResults.push({
						error: {
							message:
								applyAction?.motivo ||
								'Esta alteração não está disponível para confirmação.',
						},
						item,
					});
					continue;
				}

				const idempotencyKey = [
					buildActionIdempotencyKey(previewBody),
					'edicao',
					submitAttemptId,
					itemIndex + 1,
				]
					.filter(Boolean)
					.join('-');
				const updateResponse = await requestAplicarAcoesAmbienteEscala({
					token,
					scope,
					action: applyAction,
					body: buildApplyActionBodyFromPreview({
						preview: previewResponse,
						fallbackBody: {
							...previewBody,
							chave_idempotencia: idempotencyKey,
						},
						idempotencyKey,
					}),
				});

				updateResults.push({
					response: updateResponse,
					item,
					setorId,
					regimeVinculoId,
					dataInicialIso,
					acao: previewBody.acao,
					diasSemanaRecorrentes: normalizeDiasSemanaValues(
						item?.diasSemanaValores,
					),
				});
			}

			const updateError = updateResults.find(
				(result) => result?.response?.error || result?.error,
			);
			if (updateError) {
				const errorResult = updateError.response || updateError.error;
				const mensagemPreview = String(
					errorResult?.message || '',
				).trim();
				if (showProfissionalHorarioSobrepostoPopup(errorResult)) {
					return;
				}
				setSubmitErrorScope('gerenciamentoTurnos');
				setSubmitError(
					mensagemPreview ||
						getFriendlyProfissionalApiErrorMessage({
							result: errorResult,
							isEditing: true,
						}),
				);
				return;
			}

			// ALTERAR_REGIME versiona a recorrência, mas não materializa os
			// novos dias na competência por conta própria. Sem esta segunda etapa
			// a configuração aparece corretamente no modal, enquanto a grade fica
			// sem card para os dias recém-incluídos (por exemplo, uma terça-feira).
			// Usa-se a linha retornada pela ação sempre que disponível, pois uma
			// alteração de regime pode devolver uma nova versão do vínculo.
			updateResults
				.filter(
					(result = {}) =>
						result?.acao === 'ALTERAR_REGIME' &&
						!result?.response?.error,
				)
				.forEach((result = {}) => {
					const linhasRetornadas = [
						result?.response?.linha,
						...(Array.isArray(result?.response?.linhas)
							? result.response.linhas
							: []),
						...(Array.isArray(result?.response?.ambiente?.linhas)
							? result.response.ambiente.linhas
							: []),
					].filter(Boolean);
					const linhaAtualizada =
						linhasRetornadas.find(
							(linha = {}) =>
								String(getLinhaSetorId(linha) || '') ===
								String(result.setorId || ''),
						) || linhasRetornadas[0];
					const regimeVinculoIdAtualizado =
						getLinhaRegimeVinculoId(linhaAtualizada) ||
						Number(
							result?.response?.regime_vinculo_id ||
								result?.response?.regime_escala?.id ||
								0,
						) ||
						Number(result.regimeVinculoId || 0);
					if (!regimeVinculoIdAtualizado || !result.setorId) return;
					result.regimeVinculoIdAtualizado =
						regimeVinculoIdAtualizado;

					applyLinhas.push({
						setor: result.setorId,
						prefix: 'editar-regime-profissional',
						regime_vinculo_id: regimeVinculoIdAtualizado,
						primeiro_dia_trabalho_mes:
							getDataInicialProfissionalEscalaIsoFromLinha(
								linhaAtualizada,
							) ||
							result.dataInicialIso ||
							undefined,
					});
				});

			// O preenchimento monta a sugestão, mas pode mantê-la em SUGERIDO sem
			// criar o card, mesmo depois de ALTERAR_REGIME. A própria resposta
			// declara ATRIBUIR como ação permitida; por isso confirmamos somente
			// sugestões sem card dos dias recorrentes configurados neste setor.
			const atualizacoesParaAtribuir = updateResults.filter(
				(result = {}) =>
					result?.acao === 'ALTERAR_REGIME' &&
					Array.isArray(result?.diasSemanaRecorrentes) &&
					result.diasSemanaRecorrentes.length > 0 &&
					Number(
						result?.regimeVinculoIdAtualizado ||
							result?.regimeVinculoId ||
							0,
					) > 0,
			);
			if (atualizacoesParaAtribuir.length > 0) {
				const ambienteComSugestoes =
					await requestAbrirAmbientePreenchimentoEscala({
						token,
						contexto:
							getContextoVisualizacaoRequest(contextoAplicado),
						modo: 'detalhado',
						scope,
						forceRefresh: true,
					});
				if (ambienteComSugestoes?.error) {
					throw createApiResponseError(
						ambienteComSugestoes,
						'Não foi possível atualizar as novas ocorrências da recorrência.',
					);
				}

				for (const atualizacao of atualizacoesParaAtribuir) {
					const regimeVinculoIdAtualizado = Number(
						atualizacao.regimeVinculoIdAtualizado ||
							atualizacao.regimeVinculoId ||
							0,
					);
					const linhaAtualizada = (
						ambienteComSugestoes?.linhas || []
					).find(
						(linha = {}) =>
							String(getLinhaSetorId(linha) || '') ===
								String(atualizacao.setorId || '') &&
							getLinhaRegimeVinculoId(linha) ===
								regimeVinculoIdAtualizado,
					);
					const datasParaAtribuir = (linhaAtualizada?.celulas || [])
						.filter(
							(celula = {}) =>
								celula?.sugerido === true &&
								Number(celula?.quantidade_cards || 0) === 0 &&
								atualizacao.diasSemanaRecorrentes.includes(
									Number(celula?.dia_semana || 0),
								),
						)
						.map((celula = {}) =>
							formatDateInputToISO(celula?.data),
						)
						.filter(Boolean);

					for (const data of datasParaAtribuir) {
						const comandoAtribuir = {
							contexto: {
								estabelecimento: getEstabelecimentoIdValue(
									contextoAplicado.estabelecimento,
								),
								setor: Number(atualizacao.setorId),
								mes_referencia: contextoAplicado.mes_referencia,
							},
							acao: 'ATRIBUIR',
							regime_vinculo_id: regimeVinculoIdAtualizado,
							datas: [data],
						};
						const previewAtribuir =
							await requestPreverAcoesAmbienteEscala({
								token,
								body: comandoAtribuir,
								scope,
							});
						if (
							previewAtribuir?.error ||
							!isPreviewAcaoConfirmavel(previewAtribuir)
						) {
							throw createApiResponseError(
								previewAtribuir,
								'Não foi possível confirmar a nova ocorrência da recorrência.',
							);
						}

						const chaveIdempotencia =
							buildActionIdempotencyKey(comandoAtribuir);
						const respostaAtribuir =
							await requestAplicarAcoesAmbienteEscala({
								token,
								scope,
								action: previewAtribuir?.acoes?.aplicar,
								body: buildApplyActionBodyFromPreview({
									preview: previewAtribuir,
									fallbackBody: {
										...comandoAtribuir,
										chave_idempotencia: chaveIdempotencia,
									},
									idempotencyKey: chaveIdempotencia,
								}),
							});
						if (respostaAtribuir?.error) {
							throw createApiResponseError(
								respostaAtribuir,
								'Não foi possível atribuir a nova ocorrência da recorrência.',
							);
						}
					}
				}
			}

			if (criarNovoSetor) {
				const { regimePayload, dataInicialIso, dimensionValue } =
					buildGerenciamentoTurnosPayloadData({
						...novoItem,
						sourceLinha: baseLinha,
					});
				const setorId = Number(novoItem.setorId || 0);
				if (!setorId) {
					setSubmitErrorScope('gerenciamentoTurnosNovoSetor');
					setSubmitError('Selecione o setor para criar a escala.');
					return;
				}
				if (modalidade !== 'ENFERMAGEM' && !cpf) {
					setSubmitErrorScope('gerenciamentoTurnosNovoSetor');
					setSubmitError(
						'Não foi possível identificar o CPF do profissional para criar a escala em outro setor.',
					);
					return;
				}
				const vinculoTipoIdBase =
					resolveGerenciamentoTurnosVinculoTipoId(
						baseLinha,
						profissionalCadastro,
					);
				const dimensionValueCadastro =
					Number(profissionalCadastro?.[dimensionField] || 0) ||
					Number(profissionalCadastro?.especialidade_id || 0) ||
					Number(profissionalCadastro?.categoria_id || 0) ||
					Number(
						baseLinha?.vinculo_profissional?.[dimensionField] ||
							baseLinha?.[dimensionField] ||
							getFirstCardFromLinha(baseLinha)?.[
								dimensionField
							] ||
							0,
					) ||
					Number(dimensionValue || 0) ||
					undefined;
				const vinculoTipoId = vinculoTipoIdBase || undefined;
				const admissaoCadastro =
					formatDateInputToISO(
						profissionalCadastro?.admissao ||
							profissionalCadastro?.data_admissao ||
							getAdmissaoFromLinha(baseLinha),
					) || undefined;
				const cargoCadastro =
					String(
						profissionalCadastro?.cargo ||
							getCargoFromLinha(baseLinha) ||
							'',
					).trim() || undefined;
				const regimePayloadCadastro = {
					...regimePayload,
					[dimensionField]: dimensionValueCadastro,
				};
				const cargaHorariaSemanal = getCargaHorariaSemanalFromLinha(
					baseLinha,
					{
						regime: regimePayloadCadastro.regime,
						hora_inicio: regimePayloadCadastro.hora_inicio,
						hora_fim: regimePayloadCadastro.hora_fim,
						dias_semana: regimePayloadCadastro.dias_semana,
						ocorrencias: regimePayloadCadastro.ocorrencias,
					},
				);
				const saveBody = {
					contexto: {
						estabelecimento: getEstabelecimentoIdValue(
							contextoAplicado.estabelecimento,
						),
						setor: setorId,
						mes_referencia:
							getMesReferenciaFromDateInput(dataInicialIso) ||
							contextoAplicado.mes_referencia,
					},
					// O vínculo identifica a jornada já existente. O backend usa a
					// primeira alocação desse vínculo como setor-base; o usuário não
					// escolhe nem pode trocar essa referência ao criar outro setor.
					vinculo_profissional_id:
						Number(getVinculoIdFromLinha(baseLinha) || 0) ||
						undefined,
					cpf,
					profissional: {
						nome:
							formatProfessionalNameInput(
								profissionalCadastro?.nome ||
									getLinhaDisplayName(baseLinha),
								{ trimEnd: true },
							) || undefined,
						conselho:
							String(
								profissionalCadastro?.conselho ||
									getConselhoTipoFromLinha(baseLinha) ||
									'CRM',
							)
								.trim()
								.toUpperCase() || undefined,
						uf_conselho:
							String(
								getUfConselhoFromProfissional(
									profissionalCadastro,
								) ||
									getUfConselhoFromLinha(baseLinha) ||
									'',
							)
								.trim()
								.toUpperCase() || undefined,
						numero_registro:
							normalizeDigits(
								getNumeroConselhoFromProfissional(
									profissionalCadastro,
								) || getNumeroConselhoFromLinha(baseLinha),
								20,
							) || undefined,
						telefone:
							normalizeDigits(
								profissionalCadastro?.telefone ||
									getPhoneFromLinha(baseLinha),
								11,
							) || undefined,
					},
					vinculo_profissional: {
						vinculo_tipo_id: vinculoTipoId,
						data_admissao: admissaoCadastro,
						cargo: cargoCadastro,
						carga_horaria_semanal: cargaHorariaSemanal || undefined,
						[dimensionField]:
							Number(dimensionValueCadastro) || undefined,
					},
					regime_escala: regimePayloadCadastro,
				};
				let saveResponse;
				if (modalidade === 'ENFERMAGEM' && !criarNovoSetor) {
					// A criação direta de uma jornada não passa por
					// adicionar-profissional: essa é uma rota de compatibilidade e
					// pressupõe um regime/origem já criado. Para um profissional já
					// vinculado, a própria origem é criada em duas etapas canônicas:
					// prever a configuração e aplicar exatamente a ação devolvida.
					const vinculoProfissionalId = Number(
						getVinculoIdFromLinha(baseLinha) || 0,
					);
					if (!vinculoProfissionalId) {
						setSubmitErrorScope('gerenciamentoTurnosNovoSetor');
						setSubmitError(
							'Não foi possível identificar o vínculo do profissional para criar a jornada neste setor.',
						);
						return;
					}

					const padroesOrigem = normalizePadroesAlocacao(
						regimePayloadCadastro.padroes_alocacao,
					).map((padrao = {}) =>
						Object.fromEntries(
							Object.entries({
								tipo_recorrencia: padrao.tipo_recorrencia,
								preset_ciclo: getPayloadPresetCiclo(
									padrao.preset_ciclo,
								),
								dias_semana: padrao.dias_semana,
								datas_especificas: padrao.datas_especificas,
								hora_inicio: formatHourInputToTimeValue(
									padrao.hora_inicio,
								),
								hora_fim: formatHourInputToTimeValue(
									padrao.hora_fim,
								),
								trabalho_horas: padrao.trabalho_horas,
								descanso_horas: padrao.descanso_horas,
								data_ancora: formatDateInputToISO(
									padrao.data_ancora,
								),
								periodo_semanas: padrao.periodo_semanas,
								semanas_rotacao: padrao.semanas_rotacao,
							}).filter(
								([, value]) =>
									value !== undefined && value !== '',
							),
						),
					);
					const previewResponse =
						await requestPreverParticionamentoAlocacaoOrigem({
							token,
							scope,
							body: {
								contexto: saveBody.contexto,
								configuracao: {
									vinculo_profissional_id:
										vinculoProfissionalId,
									setor_origem_id: setorId,
									[dimensionField]:
										Number(dimensionValueCadastro) ||
										undefined,
									regime: regimePayloadCadastro.regime,
									vigencia_inicio: dataInicialIso,
									vigencia_fim: null,
									padroes_origem: padroesOrigem,
								},
								particoes: [],
							},
						});
					const acaoAplicar = previewResponse?.acoes?.aplicar || null;
					const previewEtag =
						getPreenchimentoPreviewEtag(previewResponse);
					if (previewResponse?.error) {
						if (isAlocacaoOrigemAtivaExigeEdicao(previewResponse)) {
							const alocacaoOrigemId =
								getAlocacaoOrigemIdFromPayload(previewResponse);
							const acaoPreferida =
								getAcaoParaAdicionarSetorNaAlocacaoOrigem(
									previewResponse,
								);
							if (alocacaoOrigemId && acaoPreferida) {
								await prepararParticionamentoNovoSetor(
									novoItem,
									baseLinha,
									{
										alocacaoOrigemId,
										acaoPreferida,
									},
								);
								return;
							}
						}
						if (
							showProfissionalHorarioSobrepostoPopup(
								previewResponse,
							)
						) {
							return;
						}
						setSubmitErrorScope('gerenciamentoTurnosNovoSetor');
						setSubmitError(
							getFriendlyProfissionalApiErrorMessage({
								result: previewResponse,
								isEditing: false,
							}),
						);
						return;
					}
					if (
						!isPreviewAcaoConfirmavel(previewResponse) ||
						acaoAplicar?.disponivel === false ||
						!acaoAplicar?.entrada ||
						!previewEtag
					) {
						setSubmitErrorScope('gerenciamentoTurnosNovoSetor');
						setSubmitError(
							acaoAplicar?.motivo ||
								getPreviewBlockMessage(
									previewResponse,
									'Esta jornada não pode ser criada com a configuração informada.',
								),
						);
						return;
					}

					saveResponse =
						await requestAplicarParticionamentoAlocacaoOrigem({
							token,
							scope,
							action: acaoAplicar,
							body: JSON.parse(
								JSON.stringify(acaoAplicar.entrada),
							),
							previewEtag,
							idempotencyKey: `criar-origem-direta-${vinculoProfissionalId}-${Date.now()}-${Math.random()
								.toString(36)
								.slice(2, 10)}`,
						});
				} else {
					saveResponse =
						await requestSalvarProfissionalAmbienteEscala({
							token,
							body: saveBody,
							scope,
						});
				}

				if (saveResponse?.error) {
					if (showProfissionalHorarioSobrepostoPopup(saveResponse)) {
						return;
					}
					setSubmitErrorScope('gerenciamentoTurnosNovoSetor');
					setSubmitError(
						getFriendlyProfissionalApiErrorMessage({
							result: saveResponse,
							isEditing: false,
						}),
					);
					return;
				}

				const linhaRespostaNovoSetor =
					saveResponse?.linha ||
					(Array.isArray(saveResponse?.ambiente?.linhas)
						? saveResponse.ambiente.linhas.find(
								(linhaResposta = {}) =>
									String(getLinhaSetorId(linhaResposta)) ===
									String(setorId),
							)
						: null) ||
					{};
				const regimeEscalaResposta =
					linhaRespostaNovoSetor?.regime_escala ||
					saveResponse?.regime_escala ||
					{};
				const padroesResposta = Array.isArray(
					regimeEscalaResposta?.padroes_alocacao,
				)
					? regimeEscalaResposta.padroes_alocacao
					: [];
				const regimeEscalaNovoSetor = {
					...regimePayloadCadastro,
					...regimeEscalaResposta,
					// A resposta da API é a fonte de verdade para ciclo e recorrência.
					// O payload local só preenche os campos que a rota não devolve.
					padroes_alocacao:
						padroesResposta.length > 0
							? padroesResposta
							: regimePayloadCadastro.padroes_alocacao,
					vigencia_inicio:
						formatDateInputToISO(
							regimeEscalaResposta?.vigencia_inicio,
						) || dataInicialIso,
					data_ancora:
						formatDateInputToISO(
							regimeEscalaResposta?.data_ancora,
						) || dataInicialIso,
					alocacao_origem_id: undefined,
					alocacao_origem: undefined,
					particoes_alocacao_origem: undefined,
					projecao_split: {},
				};
				let novoRegimeVinculoId =
					Number(
						linhaRespostaNovoSetor?.regime_vinculo_id ||
							saveResponse?.regime_vinculo_id ||
							saveResponse?.ambiente?.linhas?.[0]
								?.regime_vinculo_id ||
							0,
					) || 0;
				// Para enfermagem, a adoção legada acima já aplicou a origem usando a
				// entrada assinada. Reenviar a mesma linha a prever-preenchimento
				// criaria uma segunda operação sobre o mesmo regime.
				if (novoRegimeVinculoId && modalidade !== 'ENFERMAGEM') {
					applyLinhas.push({
						setor: setorId,
						prefix: 'novo-profissional',
						regime_vinculo_id: novoRegimeVinculoId,
						primeiro_dia_trabalho_mes: dataInicialIso || undefined,
					});
				}
				const setorCadastro = setorOptions.find(
					(setor) => String(setor.id) === String(setorId),
				);
				const novaLinhaSetor = {
					...baseLinha,
					...(saveResponse?.linha || {}),
					// Esta é uma alocação independente. Não carregue a estrutura nem
					// as abas agregadas do setor-base, pois isso a faria aparecer como
					// origem e impediria a inclusão imediata no modal.
					setores_linhas: undefined,
					alocacao_origem_id: undefined,
					alocacao_origem: undefined,
					particoes_alocacao_origem: undefined,
					linha_id:
						linhaRespostaNovoSetor?.linha_id ||
						`novo-setor-${setorId}-${novoRegimeVinculoId || submitAttemptId}`,
					setor: linhaRespostaNovoSetor?.setor ||
						setorCadastro || {
							id: setorId,
							nome: formatSetorOptionLabel(setorCadastro),
						},
					regime_vinculo_id:
						novoRegimeVinculoId ||
						linhaRespostaNovoSetor?.regime_vinculo_id ||
						null,
					regime_escala: {
						...regimeEscalaNovoSetor,
						...(novoRegimeVinculoId
							? { id: novoRegimeVinculoId }
							: {}),
					},
				};
				novaLinhaSetorCriada = novaLinhaSetor;
				novoSetorCriadoItem =
					buildGerenciamentoTurnosEditableItems(
						buildTurnosSetores({
							linha: novaLinhaSetor,
							setorOptions,
							contextoAplicado,
							regimesOptions: Array.isArray(
								ambienteOpcoes?.regimes,
							)
								? ambienteOpcoes.regimes
								: [],
						}),
					)[0] || null;
			}

			if (applyLinhas.length > 0) {
				const applyGroupsBySetor = applyLinhas.reduce(
					(acc, item = {}) => {
						const setorKey = Number(item.setor || 0);
						if (!setorKey) return acc;
						const prefix = item.prefix || 'editar-profissional';
						const groupKey = `${setorKey}:${prefix}`;
						if (!acc.has(groupKey)) {
							acc.set(groupKey, {
								setor: setorKey,
								prefix,
								linhas: [],
							});
						}
						acc.get(groupKey).linhas.push({
							regime_vinculo_id: item.regime_vinculo_id,
							primeiro_dia_trabalho_mes:
								item.primeiro_dia_trabalho_mes || undefined,
						});
						return acc;
					},
					new Map(),
				);

				const applyResults = await Promise.all(
					Array.from(applyGroupsBySetor.values()).map(
						async (applyGroup) => {
							const setorAplicacao = applyGroup.setor;
							const linhasAplicacao = applyGroup.linhas;
							const firstLinhaAplicacao =
								linhasAplicacao[0] || {};
							const applyOperationToken = `${setorAplicacao}-${linhasAplicacao
								.map((linha) => linha.regime_vinculo_id)
								.filter(Boolean)
								.join('-')}-${submitAttemptId}`;
							const applyResponse =
								await aplicarPreenchimentoComPreview({
									contexto: {
										estabelecimento:
											getEstabelecimentoIdValue(
												contextoAplicado.estabelecimento,
											),
										setor: setorAplicacao,
										mes_referencia:
											contextoAplicado.mes_referencia,
									},
									chave_idempotencia:
										buildApplyPreenchimentoIdempotencyKey({
											contexto: {
												...contextoAplicado,
												setor: setorAplicacao,
											},
											prefix: applyGroup.prefix,
											regimeVinculoId:
												firstLinhaAplicacao.regime_vinculo_id,
											primeiroDiaTrabalhoMes:
												firstLinhaAplicacao.primeiro_dia_trabalho_mes,
											fallbackToken: applyOperationToken,
										}),
									linhas: linhasAplicacao,
									meses_futuros: 12,
								});

							return applyResponse;
						},
					),
				);

				const applyError = applyResults.find(
					(response) => response?.error,
				);
				if (applyError) {
					if (showProfissionalHorarioSobrepostoPopup(applyError)) {
						return;
					}
					setSubmitErrorScope(
						criarNovoSetor
							? 'gerenciamentoTurnosNovoSetor'
							: 'gerenciamentoTurnos',
					);
					setSubmitError(
						getServerErrorDisplayMessage(applyError)
							? 'A edição não foi aplicada. Tente novamente mais tarde.'
							: getFriendlyProfissionalApiErrorMessage({
									result: applyError,
									isEditing: true,
								}),
					);
					return;
				}
			}

			const ambienteAtualizado = await loadAmbiente({
				authToken: token,
				targetContexto:
					getContextoVisualizacaoRequest(contextoAplicado),
				showLoading: false,
				forceRefresh: criarNovoSetor,
			});
			if (criarNovoSetor) {
				// O contexto aberto pode estar filtrado no setor de origem. Releia
				// também o setor acabado de criar, sem cache, para que a nova aba
				// apareça imediatamente no modal.
				const ambienteNovoSetor =
					await requestAbrirAmbientePreenchimentoEscala({
						token,
						contexto: {
							...getContextoVisualizacaoRequest(contextoAplicado),
							setor:
								Number(gerenciamentoTurnosNovoSetor || 0) ||
								undefined,
						},
						modo: 'detalhado',
						scope,
						forceRefresh: true,
					});
				const linhasAtualizadas = [
					...(Array.isArray(ambienteAtualizado?.linhas)
						? ambienteAtualizado.linhas
						: []),
					...(Array.isArray(ambienteNovoSetor?.linhas)
						? ambienteNovoSetor.linhas
						: []),
				];
				const cpfProfissional = normalizeCpf(
					getCpfFromLinha(baseLinha),
				);
				const profissionalId = getProfissionalIdFromLinha(baseLinha);
				const linhasProfissional = linhasAtualizadas.filter(
					(linhaAtualizada) => {
						const cpfLinha = normalizeCpf(
							getCpfFromLinha(linhaAtualizada),
						);
						const profissionalLinhaId =
							getProfissionalIdFromLinha(linhaAtualizada);
						return Boolean(
							(profissionalId &&
								profissionalLinhaId === profissionalId) ||
							(cpfProfissional && cpfLinha === cpfProfissional),
						);
					},
				);
				const linhaModalAtualizada = Array.isArray(
					gerenciamentoTurnosLinha?.setores_linhas,
				)
					? consolidateLinhasPorProfissional(linhasProfissional)[0]
					: linhasProfissional.find(
							(linhaAtualizada) =>
								String(getLinhaSetorId(linhaAtualizada)) ===
								String(gerenciamentoTurnosNovoSetor),
						) || linhasProfissional[0];
				const itensAtualizados = linhaModalAtualizada
					? buildGerenciamentoTurnosEditableItems(
							buildTurnosSetores({
								linha: linhaModalAtualizada,
								setorOptions,
								contextoAplicado,
								regimesOptions: Array.isArray(
									ambienteOpcoes?.regimes,
								)
									? ambienteOpcoes.regimes
									: [],
							}),
						)
					: novoSetorCriadoItem
						? [novoSetorCriadoItem]
						: [];
				const novaAbaJaAtualizada = itensAtualizados.some(
					(item) =>
						String(item.setorId) ===
						String(gerenciamentoTurnosNovoSetor),
				);
				const itensComNovoSetor =
					novoSetorCriadoItem && !novaAbaJaAtualizada
						? [...itensAtualizados, novoSetorCriadoItem]
						: itensAtualizados;
				const atualizacaoCompletaDoProfissional =
					Boolean(linhaModalAtualizada);

				if (itensComNovoSetor.length > 0) {
					setGerenciamentoTurnosSetoresEditaveis((prev) => {
						const itensParaExibir =
							atualizacaoCompletaDoProfissional
								? itensComNovoSetor
								: [
										...prev.filter(
											(item) =>
												String(item.setorId) !==
												String(
													gerenciamentoTurnosNovoSetor,
												),
										),
										...itensComNovoSetor,
									];

						return itensParaExibir.map((itemAtualizado) => {
							const itemAnterior = prev.find(
								(item) =>
									String(item.setorId) ===
									String(itemAtualizado.setorId),
							);
							return itemAnterior &&
								hasGerenciamentoTurnosItemChanges(itemAnterior)
								? {
										...itemAtualizado,
										...itemAnterior,
										sourceLinha: itemAtualizado.sourceLinha,
										regimeVinculoId:
											itemAtualizado.regimeVinculoId,
									}
								: itemAtualizado;
						});
					});
					setGerenciamentoTurnosSetoresAbertos((prev) => ({
						...prev,
						...itensComNovoSetor.reduce(
							(acc, item) => ({
								...acc,
								[item.id]:
									String(item.setorId) ===
									String(gerenciamentoTurnosNovoSetor)
										? true
										: prev[item.id] || false,
							}),
							{},
						),
					}));
				}
				resetGerenciamentoTurnosNovoSetor();
				if (novaLinhaSetorCriada) {
					setGerenciamentoTurnosLinha((linhaAtual) => {
						if (!linhaAtual) return linhaAtual;
						const setoresAtuais = Array.isArray(
							linhaAtual?.setores_linhas,
						)
							? linhaAtual.setores_linhas
							: [
									{
										...linhaAtual,
										setores_linhas: undefined,
									},
								];
						const setorCriado = String(
							getLinhaSetorId(novaLinhaSetorCriada) ||
								gerenciamentoTurnosNovoSetor,
						);

						return {
							...linhaAtual,
							setores_linhas: [
								...setoresAtuais.filter(
									(item) =>
										String(getLinhaSetorId(item)) !==
										setorCriado,
								),
								novaLinhaSetorCriada,
							],
						};
					});
				}
				setFeedbackMessage(
					'Profissional adicionado ao novo setor com sucesso.',
				);
				return;
			}

			const linhasAtualizadas = Array.isArray(ambienteAtualizado?.linhas)
				? ambienteAtualizado.linhas
				: [];
			const cpfProfissional = normalizeCpf(getCpfFromLinha(baseLinha));
			const profissionalId = getProfissionalIdFromLinha(baseLinha);
			const linhasProfissional = linhasAtualizadas.filter(
				(linhaAtualizada = {}) => {
					const cpfLinha = normalizeCpf(
						getCpfFromLinha(linhaAtualizada),
					);
					const profissionalLinhaId =
						getProfissionalIdFromLinha(linhaAtualizada);
					return Boolean(
						(profissionalId &&
							profissionalLinhaId === profissionalId) ||
						(cpfProfissional && cpfLinha === cpfProfissional),
					);
				},
			);
			const linhaModalAtualizada = Array.isArray(
				gerenciamentoTurnosLinha?.setores_linhas,
			)
				? consolidateLinhasPorProfissional(linhasProfissional)[0]
				: linhasProfissional.find(
						(linhaAtualizada = {}) =>
							String(getLinhaSetorId(linhaAtualizada)) ===
							String(getLinhaSetorId(baseLinha)),
					) || linhasProfissional[0];
			if (linhaModalAtualizada) {
				const itensAtualizados = buildGerenciamentoTurnosEditableItems(
					buildTurnosSetores({
						linha: linhaModalAtualizada,
						setorOptions,
						contextoAplicado,
						regimesOptions: Array.isArray(ambienteOpcoes?.regimes)
							? ambienteOpcoes.regimes
							: [],
					}),
				);
				setGerenciamentoTurnosLinha(linhaModalAtualizada);
				setGerenciamentoTurnosSetoresEditaveis(itensAtualizados);
				setGerenciamentoTurnosSetoresAbertos((prev) =>
					itensAtualizados.reduce(
						(acc, item) => ({
							...acc,
							[item.id]: prev[item.id] ?? true,
						}),
						{},
					),
				);
			}
			setFeedbackMessage('Turnos do profissional salvos com sucesso.');
		} catch (error) {
			if (showProfissionalHorarioSobrepostoPopup(error)) return;
			const apiResult = error?.apiResult || error;
			if (isPreviewOrSnapshotOutdated(apiResult)) {
				setParticionamentoPendente(null);
				await handleOpenGerenciamentoTurnosModal(
					gerenciamentoTurnosLinha,
				);
				setSubmitErrorScope('gerenciamentoTurnos');
				setSubmitError(
					'A escala foi atualizada enquanto você a editava. Revise os dados e gere uma nova prévia.',
				);
				return;
			}
			const erroValidacao = getNonRetryableValidationError(apiResult);
			const mensagemErro = erroValidacao
				? getFriendlyProfissionalApiErrorMessage({
						result: apiResult,
						isEditing: true,
					})
				: String(error?.message || '').trim() ||
					getProfissionalApiFallbackMessage({
						isEditing: true,
					});
			if (erroValidacao) {
				const campoFoco = String(
					erroValidacao?.campo_foco ||
						erroValidacao?.campo ||
						(Array.isArray(erroValidacao?.erros)
							? erroValidacao.erros.find((item = {}) =>
									Boolean(item?.campo_foco || item?.campo),
								)?.campo_foco ||
								erroValidacao.erros.find((item = {}) =>
									Boolean(item?.campo),
								)?.campo
							: '') ||
						'',
				);
				const itemComErro = gerenciamentoTurnosSetoresEditaveis.find(
					hasGerenciamentoTurnosItemChanges,
				);
				if (itemComErro) {
					setGerenciamentoTurnosErrors((current) => ({
						...current,
						[`${itemComErro.id}.servidor`]: campoFoco
							? `${campoFoco}: ${mensagemErro}`
							: mensagemErro,
					}));
				}
			}
			setSubmitErrorScope(
				criarNovoSetor
					? 'gerenciamentoTurnosNovoSetor'
					: 'gerenciamentoTurnos',
			);
			setSubmitError(mensagemErro);
		} finally {
			setIsSubmittingGerenciamentoTurnos(false);
		}
	};

	const handleSelectContexto = async () => {
		if (!token) return;

		const estabelecimentoSelecionado = String(
			contexto.estabelecimento || userCnes || '',
		).trim();

		const nextErrors = {
			estabelecimento: validateContextoField(
				'estabelecimento',
				estabelecimentoSelecionado,
				{ showUnidadeFilter },
			),
			setor: validateContextoField('setor', contexto.setor),
		};
		setContextoErrors(nextErrors);

		if (nextErrors.estabelecimento || nextErrors.setor) {
			setSubmitErrorScope('contexto');
			setSubmitError('Preencha os campos obrigatórios.');
			return;
		}

		const targetContexto = {
			estabelecimento: estabelecimentoSelecionado,
			setor: isSetorTodosValue(contexto.setor)
				? ''
				: Number(contexto.setor),
			mes_referencia: formatDateInputToISO(
				contexto.mes_referencia || getDefaultContexto().mes_referencia,
			),
		};

		if (!targetContexto.mes_referencia) {
			setSubmitErrorScope('contexto');
			setSubmitError('Competência inválida.');
			return;
		}

		autoLoadedContextKeyRef.current = buildProfessionalManagementContextKey(
			{
				scope,
				contexto: {
					...targetContexto,
					setor: isSetorTodosValue(contexto.setor)
						? SETOR_TODOS_VALUE
						: targetContexto.setor,
				},
			},
		);
		await loadOpcoesAmbiente({
			authToken: token,
			targetContexto: {
				...targetContexto,
				setor:
					targetContexto.setor ||
					Number(setorOptions[0]?.id || 0) ||
					undefined,
			},
		});
		await loadAmbiente({ authToken: token, targetContexto });
	};

	const handleUnidadeInputChange = (value) => {
		clearAppliedContextState();
		setUnidadeSearchTerm(value);
		setShowUnidadeSuggestions(true);
		setShowSetorSuggestions(false);
		setContextoErrors((prev) => ({
			...prev,
			estabelecimento: '',
		}));

		const inputNormalizado = normalizeSearchValue(value);
		const unidadeCorrespondente = unidadeOptions.find((unidade) => {
			const label = normalizeSearchValue(
				formatUnidadeOptionLabel(unidade),
			);
			const cnes = normalizeSearchValue(
				unidade?.id || unidade?.cnes || '',
			);
			return label === inputNormalizado || cnes === inputNormalizado;
		});

		setContexto((prev) => ({
			...prev,
			estabelecimento: unidadeCorrespondente
				? String(unidadeCorrespondente.id)
				: '',
			setor: unidadeCorrespondente ? prev.setor : '',
		}));
		if (!unidadeCorrespondente) {
			setSetorSearchTerm('');
			setShowSetorSuggestions(false);
		}
	};

	const handleSelectUnidade = (unidade) => {
		clearAppliedContextState();
		setUnidadeSearchTerm(formatUnidadeOptionLabel(unidade));
		setContextoErrors((prev) => ({ ...prev, estabelecimento: '' }));
		setContexto((prev) => ({
			...prev,
			estabelecimento: String(unidade.id),
			setor: '',
		}));
		setSetorSearchTerm('');
		setShowUnidadeSuggestions(false);
	};

	const handleSetorInputChange = (value) => {
		if (setorInputDisabled) return;

		clearAppliedContextState();
		setSetorSearchTerm(value);
		setShowSetorSuggestions(true);
		setShowUnidadeSuggestions(false);
		setContextoErrors((prev) => ({ ...prev, setor: '' }));

		const inputNormalizado = normalizeSearchValue(value);
		const setorCorrespondente = setorOptionsComTodos.find((setor) => {
			const label = normalizeSearchValue(formatSetorOptionLabel(setor));
			const identificador = normalizeSearchValue(
				setor?.identificador || '',
			);
			const id = normalizeSearchValue(setor?.id || '');
			return (
				label === inputNormalizado ||
				identificador === inputNormalizado ||
				id === inputNormalizado
			);
		});

		setContexto((prev) => ({
			...prev,
			setor: setorCorrespondente ? String(setorCorrespondente.id) : '',
		}));
	};

	const handleSelectSetor = (setor) => {
		clearAppliedContextState();
		setSetorSearchTerm(formatSetorOptionLabel(setor));
		setContextoErrors((prev) => ({ ...prev, setor: '' }));
		setContexto((prev) => ({
			...prev,
			setor: String(setor.id),
		}));
		setShowSetorSuggestions(false);
	};

	const handleOpenNovoProfissionalModal = () => {
		initialNovoProfissionalEditSnapshotRef.current = null;
		cadastroVinculoPendenteRef.current = null;
		alocacaoIntentIdempotencyKeysRef.current.clear();
		setOrigemAtivaPendente(null);
		setIsVacanciaModalOpen(false);
		setIsEditingVacancia(false);
		setIsViewingVacancia(false);
		setEditingVacanciaLinha(null);
		setIsEditingProfissional(false);
		setEditingProfissionalLinha(null);
		setNovoProfissionalForm(
			modalidade === 'MEDICA'
				? withWeeklyRecurrence({
						...getDefaultNovoProfissionalForm(),
						setor_id: isSetorTodosAplicado ? '' : contexto.setor,
					})
				: {
						...getDefaultNovoProfissionalForm(),
						setor_id: isSetorTodosAplicado ? '' : contexto.setor,
					},
		);
		setNovoProfissionalLockedFields(
			getDefaultNovoProfissionalLockedFields(),
		);
		setNovoProfissionalTemEscalaOrigem(false);
		setNovoProfissionalErrors({});
		setNovoProfissionalErrorDisplayMode('');
		setSubmitError('');
		setSubmitErrorScope('');
		setNovoProfissionalApplyError('');
		setProfissionalHorarioSobrepostoPopup(null);
		setIsNovoProfissionalModalOpen(true);
	};

	const handleCloseNovoProfissionalModal = () => {
		initialNovoProfissionalEditSnapshotRef.current = null;
		cadastroVinculoPendenteRef.current = null;
		alocacaoIntentIdempotencyKeysRef.current.clear();
		setOrigemAtivaPendente(null);
		setIsEditingProfissional(false);
		setEditingProfissionalLinha(null);
		setIsEditingVacancia(false);
		setIsViewingVacancia(false);
		setEditingVacanciaLinha(null);
		setNovoProfissionalForm(getDefaultNovoProfissionalForm());
		setNovoProfissionalErrors({});
		setNovoProfissionalLockedFields(
			getDefaultNovoProfissionalLockedFields(),
		);
		setNovoProfissionalTemEscalaOrigem(false);
		setNovoProfissionalErrorDisplayMode('');
		setNovoProfissionalApplyError('');
		setProfissionalHorarioSobrepostoPopup(null);
		setIsNovoProfissionalModalOpen(false);
	};

	const handleOpenEditProfissionalModal = async (linha) => {
		if (!linha || linha?.placeholder) return;
		const linhaEscopo = getLinhaEscopoSetorAtual(linha, contextoAplicado);
		if (isVacanciaLinha(linhaEscopo)) {
			handleOpenEditVacanciaModal(linhaEscopo, { readOnly: true });
			return;
		}

		const regimeEscalaLinha = linhaEscopo?.regime_escala || {};
		const padroesAlocacaoLinha = normalizePadroesAlocacao(
			regimeEscalaLinha?.padroes_alocacao,
		);
		const padroesBaseLinha = padroesAlocacaoLinha.filter(
			(item = {}) =>
				normalizeCode(item?.tipo_recorrencia) !== 'DATAS_ESPECIFICAS',
		);
		const padraoPrimarioLinha =
			padroesBaseLinha[0] || padroesAlocacaoLinha[0] || {};
		const regimeLinha = String(regimeEscalaLinha?.regime || '').trim();
		const tipoRecorrenciaLinha =
			normalizeCode(
				padraoPrimarioLinha?.tipo_recorrencia ||
					regimeEscalaLinha?.tipo_recorrencia,
			) || getTipoRecorrenciaFallbackByRegime(regimeEscalaLinha?.regime);
		const usaRecorrenciaSemanalLinha = tipoRecorrenciaLinha === 'SEMANAL';
		const diasSemanaRegimeLinha = Array.isArray(
			regimeEscalaLinha?.dias_semana,
		)
			? normalizeDiasSemanaValues(regimeEscalaLinha.dias_semana)
			: [];
		const diasSemanaPadroesLinha = normalizeDiasSemanaValues(
			padroesBaseLinha.flatMap((item) => item?.dias_semana || []),
		);
		const diasSemanaInferidosLinha = usaRecorrenciaSemanalLinha
			? inferDiasSemanaFromLinha(linhaEscopo)
			: [];
		const diasSemanaLinhaBase =
			diasSemanaPadroesLinha.length > 0
				? diasSemanaPadroesLinha
				: diasSemanaRegimeLinha.length > 0
					? diasSemanaRegimeLinha
					: diasSemanaInferidosLinha;
		const semanasRotacaoLinha = (
			padroesBaseLinha.find(
				(item = {}) =>
					normalizeCode(item?.preset_ciclo) === 'ROTACAO_PERIODICA' ||
					normalizeCode(item?.tipo_recorrencia) ===
						'ROTACAO_PERIODICA',
			)?.semanas_rotacao ||
			regimeEscalaLinha?.semanas_rotacao ||
			[]
		).map((semana = {}) =>
			normalizeDiasSemanaValues(semana?.dias_semana || semana),
		);
		const diasSemanaHorariosLinha = (() => {
			const horariosPadroes = padroesAlocacaoLinha.reduce(
				(acc, item = {}) => {
					if (
						normalizeCode(item?.tipo_recorrencia) ===
						'DATAS_ESPECIFICAS'
					) {
						return acc;
					}
					const diaSemana = Number(item?.dias_semana?.[0] || 0);
					if (!diaSemana) return acc;
					acc[diaSemana] = {
						hora_inicio: getHourInputFromTimeValue(
							item?.hora_inicio,
						),
						hora_fim: getHourInputFromTimeValue(item?.hora_fim),
					};
					return acc;
				},
				{},
			);
			if (Object.keys(horariosPadroes).length > 0) {
				return horariosPadroes;
			}

			const horariosOcorrencias =
				buildDiasSemanaHorariosMapFromOcorrencias(
					regimeEscalaLinha?.ocorrencias,
				);
			if (Object.keys(horariosOcorrencias).length > 0) {
				return horariosOcorrencias;
			}

			return buildDiasSemanaHorariosMapFromDias({
				dias_semana: diasSemanaLinhaBase,
				hora_inicio:
					padraoPrimarioLinha?.hora_inicio ||
					regimeEscalaLinha?.hora_inicio,
				hora_fim:
					padraoPrimarioLinha?.hora_fim ||
					regimeEscalaLinha?.hora_fim,
			});
		})();
		const presetCicloLinha =
			getCanonicalPresetCiclo(padraoPrimarioLinha?.preset_ciclo) ||
			inferPresetCicloFromRegimeEscala(regimeEscalaLinha);
		const nomeRecorrenciaLinha = getRecorrenciaNomeValue({
			tipo_recorrencia: tipoRecorrenciaLinha,
			preset_ciclo: presetCicloLinha,
			padroes_alocacao: padroesAlocacaoLinha,
		});
		const presetLinhaDefaults = getPresetDefaults(presetCicloLinha);
		const trabalhoHorasLinha =
			Number(regimeEscalaLinha?.trabalho_horas || 0) ||
			(Number(regimeEscalaLinha?.trabalho_minutos || 0) > 0
				? Number(regimeEscalaLinha?.trabalho_minutos || 0) / 60
				: 0) ||
			presetLinhaDefaults?.trabalho_horas ||
			undefined;
		const descansoHorasLinha =
			Number(regimeEscalaLinha?.descanso_horas || 0) ||
			(Number(regimeEscalaLinha?.descanso_minutos || 0) > 0
				? Number(regimeEscalaLinha?.descanso_minutos || 0) / 60
				: 0) ||
			presetLinhaDefaults?.descanso_horas ||
			undefined;
		// Reconstrói datas_extras a partir dos padrões DATAS_ESPECIFICAS
		// guardados em padroes_alocacao (gerados ao salvar o profissional).
		const datasExtrasLinha = (() => {
			// Para recorrência DATAS_ESPECIFICAS toda a lista são as datas.
			// Para outras recorrências somente os padrões secundários
			// (tipo_recorrencia === 'DATAS_ESPECIFICAS') são as extras.
			const padroesExtras =
				tipoRecorrenciaLinha === 'DATAS_ESPECIFICAS'
					? padroesAlocacaoLinha
					: padroesAlocacaoLinha.filter(
							(p) =>
								normalizeCode(p?.tipo_recorrencia) ===
								'DATAS_ESPECIFICAS',
						);

			return padroesExtras
				.flatMap((padrao = {}) => {
					const datas = Array.isArray(padrao?.datas_especificas)
						? padrao.datas_especificas
						: padrao?.data
							? [padrao.data]
							: [];
					const horaInicio = getHourInputFromTimeValue(
						padrao?.hora_inicio,
					);
					const horaFim = getHourInputFromTimeValue(padrao?.hora_fim);
					return datas.map((dataISO) => {
						// Normaliza para ISO YYYY-MM-DD para casar com os
						// option values do select (que também são ISO).
						const dataISONorm =
							formatDateInputToISO(dataISO) || dataISO || '';
						return {
							id: `data-extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
							selected_date: dataISONorm,
							hora_inicio: horaInicio || '',
							hora_fim: horaFim || '',
						};
					});
				})
				.filter((item) => item.selected_date);
		})();
		const datasExtrasTrocaLinha = extrairDatasExtrasTrocaLinha(linhaEscopo);
		const datasExtrasModalLinha = (() => {
			const seen = new Set();
			const competencia = String(
				formatDateInputToISO(contextoAplicado?.mes_referencia) || '',
			).slice(0, 7);
			return (
				[...datasExtrasLinha, ...datasExtrasTrocaLinha]
					.filter((item = {}) => {
						const key = `${formatDateInputToISO(item?.selected_date) || item?.selected_date || ''}-${String(item?.hora_inicio || '').slice(0, 5)}-${String(item?.hora_fim || '').slice(0, 5)}`;
						if (seen.has(key)) return false;
						seen.add(key);
						return true;
					})
					// Não exiba no formulário uma exceção de outro mês que não foi
					// devolvida na competência onde ela deveria existir.
					.filter((item = {}) => {
						const data = formatDateInputToISO(item?.selected_date);
						return !competencia || data.slice(0, 7) === competencia;
					})
			);
		})();

		const cpfLinha = getCpfFromLinha(linhaEscopo);
		const vinculoTipoId = getVinculoTipoIdFromLinha(linhaEscopo);
		// Em edições de uma jornada já existente, `vigencia_inicio` pode apontar
		// para a versão mais recente da escala. O campo do modal representa a
		// criação original, cuja referência canônica é a data âncora do padrão.
		const dataInicialEscalaLinha = formatDateInputToISO(
			regimeEscalaLinha?.data_ancora ||
				padraoPrimarioLinha?.data_ancora ||
				linhaEscopo?.data_ancora ||
				regimeEscalaLinha?.vigencia_inicio ||
				linhaEscopo?.vigencia_inicio ||
				getDataInicialProfissionalEscalaIsoFromLinha(linhaEscopo),
		);

		const modalPayloadBase = {
			...getDefaultNovoProfissionalForm(),
			setor_id: String(
				getLinhaSetorId(linhaEscopo) ||
					(isSetorTodosAplicado ? '' : contextoAplicado?.setor) ||
					'',
			),
			cpf: cpfLinha,
			nome: formatProfessionalNameInput(
				getLinhaDisplayName(linhaEscopo),
				{
					trimEnd: true,
				},
			),
			admissao: getAdmissaoFromLinha(linhaEscopo),
			vigencia_inicio: formatISOToDisplay(dataInicialEscalaLinha),
			nome_vinculo: getNomeVinculoFromLinha(linhaEscopo),
			vinculo_tipo_id: vinculoTipoId ? String(vinculoTipoId) : '',
			cargo: getCargoFromLinha(linhaEscopo),
			numero_registro: getNumeroConselhoFromLinha(linhaEscopo),
			uf_conselho: getUfConselhoFromLinha(linhaEscopo) || 'RJ',
			especialidade_id: getEspecialidadeIdFromLinha(linhaEscopo),
			telefone: getPhoneFromLinha(linhaEscopo),
			regime: regimeLinha,
			nome_recorrencia:
				modalidade === 'MEDICA' ? 'SEMANAL' : nomeRecorrenciaLinha,
			tipo_recorrencia:
				modalidade === 'MEDICA' ? 'SEMANAL' : tipoRecorrenciaLinha,
			preset_ciclo:
				modalidade === 'MEDICA' ? 'SEMANAL' : presetCicloLinha,
			trabalho_horas: trabalhoHorasLinha,
			descanso_horas: descansoHorasLinha,
			conselho: String(
				linhaEscopo?.profissional?.conselho ||
					linhaEscopo?.vinculo_profissional?.conselho ||
					'CRM',
			),
			hora_inicio: String(
				padraoPrimarioLinha?.hora_inicio ||
					regimeEscalaLinha?.hora_inicio ||
					presetLinhaDefaults?.hora_inicio ||
					'07:00',
			).slice(0, 5),
			hora_fim: String(
				padraoPrimarioLinha?.hora_fim ||
					regimeEscalaLinha?.hora_fim ||
					presetLinhaDefaults?.hora_fim ||
					'19:00',
			).slice(0, 5),
			data_ancora: formatISOToDisplay(dataInicialEscalaLinha),
			dias_semana_horarios: diasSemanaHorariosLinha,
			dias_semana: diasSemanaLinhaBase,
			semanas_rotacao:
				semanasRotacaoLinha.length >= 2
					? semanasRotacaoLinha
					: getDefaultNovoProfissionalForm().semanas_rotacao,
			datas_extras: datasExtrasModalLinha,
		};

		setIsVacanciaModalOpen(false);
		setIsEditingVacancia(false);
		setIsViewingVacancia(false);
		setEditingVacanciaLinha(null);
		setIsEditingProfissional(true);
		setEditingProfissionalLinha(linhaEscopo);
		setNovoProfissionalTemEscalaOrigem(false);
		setNovoProfissionalLockedFields({});
		setNovoProfissionalErrors({});
		setNovoProfissionalErrorDisplayMode('');
		setSubmitError('');
		setSubmitErrorScope('');
		setNovoProfissionalApplyError('');
		setProfissionalHorarioSobrepostoPopup(null);
		setFeedbackMessage('');
		setNovoProfissionalForm(modalPayloadBase);
		initialNovoProfissionalEditSnapshotRef.current =
			buildNovoProfissionalEditSnapshot(modalPayloadBase);
		setIsNovoProfissionalModalOpen(true);

		const cpf = normalizeCpf(cpfLinha);
		const vinculoProfissionalId = getVinculoIdFromLinha(linhaEscopo);
		if (!token || !cpf) {
			return;
		}

		const estabelecimentoBusca = getEstabelecimentoIdValue(
			contexto.estabelecimento ||
				contextoAplicado?.estabelecimento ||
				userCnes,
		);

		const result = await requestBuscarProfissionalEscala({
			token,
			cpf,
			estabelecimento: estabelecimentoBusca || undefined,
			vinculo: vinculoProfissionalId || undefined,
			scope,
		});

		if (result?.error || !Array.isArray(result) || result.length === 0) {
			return;
		}

		const profissional = result[0] || {};
		novoProfissionalConsultadoRef.current = profissional;
		// A busca devolve `especialidade` na escala médica e `categoria` na
		// enfermagem; os aliases *_id não estão presentes em todas as versões da
		// API. O estado interno continua neutro e a serialização escolhe o campo
		// correto apenas na borda da requisição.
		const dimensionRaw =
			modalidade === 'ENFERMAGEM'
				? (profissional?.categoria_id ??
					profissional?.categoria?.id ??
					profissional?.categoria)
				: (profissional?.especialidade_id ??
					profissional?.especialidade?.id ??
					profissional?.especialidade);
		const especialidadeId = Number(dimensionRaw || 0);
		const nomeVinculoApi = getNomeVinculoFromProfissional(profissional);
		const vinculoOpcaoPorNome = findVinculoOpcaoByNome(
			vinculoOpcoes,
			nomeVinculoApi,
		);
		const vinculoTipoApi =
			vinculoOpcaoPorNome?.value ||
			getVinculoTipoIdFromProfissional(profissional);

		const modalPayloadComApi = {
			...modalPayloadBase,
			nome:
				formatProfessionalNameInput(profissional?.nome, {
					trimEnd: true,
				}) || modalPayloadBase.nome,
			admissao:
				formatISOToDisplay(
					profissional?.admissao || profissional?.data_admissao || '',
				) || modalPayloadBase.admissao,
			nome_vinculo:
				vinculoOpcaoPorNome?.label ||
				nomeVinculoApi ||
				modalPayloadBase.nome_vinculo,
			vinculo_tipo_id: vinculoTipoApi
				? String(vinculoTipoApi)
				: modalPayloadBase.vinculo_tipo_id,
			cargo: profissional?.cargo || modalPayloadBase.cargo,
			numero_registro:
				getNumeroConselhoFromProfissional(profissional) ||
				modalPayloadBase.numero_registro,
			uf_conselho:
				getUfConselhoFromProfissional(profissional) ||
				modalPayloadBase.uf_conselho,
			especialidade_id: especialidadeId
				? String(especialidadeId)
				: modalPayloadBase.especialidade_id,
			telefone:
				formatPhoneMask(profissional?.telefone || '') ||
				modalPayloadBase.telefone,
			conselho:
				String(
					profissional?.conselho ||
						modalPayloadBase.conselho ||
						'CRM',
				) || 'CRM',
		};
		initialNovoProfissionalEditSnapshotRef.current =
			buildNovoProfissionalEditSnapshot(modalPayloadComApi);

		setNovoProfissionalForm((prev) => ({
			...prev,
			nome: modalPayloadComApi.nome,
			admissao: modalPayloadComApi.admissao,
			nome_vinculo: modalPayloadComApi.nome_vinculo,
			vinculo_tipo_id: vinculoTipoApi
				? String(vinculoTipoApi)
				: prev.vinculo_tipo_id,
			cargo: modalPayloadComApi.cargo,
			numero_registro: modalPayloadComApi.numero_registro,
			uf_conselho: modalPayloadComApi.uf_conselho,
			especialidade_id: especialidadeId
				? String(especialidadeId)
				: prev.especialidade_id,
			telefone: modalPayloadComApi.telefone,
			conselho: modalPayloadComApi.conselho,
		}));
	};

	const handleOpenEditVacanciaModal = (linha, { readOnly = false } = {}) => {
		if (!linha || linha?.placeholder) return;

		const vacanciaSource = linha?.vacancia || {};
		const regimeEscalaLinha = linha?.regime_escala || {};
		const vacanciaVinculoTipoSource =
			vacanciaSource?.vinculo_tipo ||
			linha?.vinculo_profissional?.vinculo_tipo ||
			regimeEscalaLinha?.vinculo_tipo ||
			getFirstCardFromLinha(linha)?.vinculo_tipo ||
			{};
		const vacanciaVinculoTipoId =
			Number(
				vacanciaVinculoTipoSource?.id ||
					vacanciaVinculoTipoSource?.vinculo_tipo_id ||
					vacanciaVinculoTipoSource?.value ||
					vacanciaVinculoTipoSource?.codigo ||
					linha?.vacancia?.vinculo_tipo_id ||
					linha?.vinculo_profissional?.vinculo_tipo_id ||
					linha?.vinculo_profissional?.vinculo_tipo ||
					linha?.vinculo_tipo_id ||
					0,
			) || null;
		const vacanciaVinculoNome = String(
			vacanciaVinculoTipoSource?.nome ||
				vacanciaVinculoTipoSource?.label ||
				vacanciaVinculoTipoSource?.descricao ||
				vacanciaVinculoTipoSource?.titulo ||
				vacanciaSource?.nome_vinculo ||
				linha?.nome_vinculo ||
				getNomeVinculoFromLinha(linha),
		).trim();
		const padroesAlocacaoLinha = normalizePadroesAlocacao(
			regimeEscalaLinha?.padroes_alocacao,
		);
		const padroesBaseLinha = padroesAlocacaoLinha.filter(
			(item = {}) =>
				normalizeCode(item?.tipo_recorrencia) !== 'DATAS_ESPECIFICAS',
		);
		const padraoPrimarioLinha =
			padroesBaseLinha[0] || padroesAlocacaoLinha[0] || {};
		const regimeLinha = String(
			vacanciaSource?.regime || regimeEscalaLinha?.regime || '',
		).trim();
		const tipoRecorrenciaLinha =
			normalizeCode(
				padraoPrimarioLinha?.tipo_recorrencia ||
					vacanciaSource?.tipo_recorrencia ||
					regimeEscalaLinha?.tipo_recorrencia,
			) || getTipoRecorrenciaFallbackByRegime(regimeEscalaLinha?.regime);
		const usaRecorrenciaSemanalLinha = tipoRecorrenciaLinha === 'SEMANAL';
		const diasSemanaRegimeLinha = Array.isArray(
			regimeEscalaLinha?.dias_semana,
		)
			? normalizeDiasSemanaValues(regimeEscalaLinha.dias_semana)
			: [];
		const diasSemanaPadroesLinha = normalizeDiasSemanaValues(
			padroesBaseLinha.flatMap((padrao) => padrao?.dias_semana || []),
		);
		const vacanciaDiasSemanaSource = Array.isArray(
			vacanciaSource?.dias_semana,
		)
			? normalizeDiasSemanaValues(vacanciaSource.dias_semana)
			: [];
		const vacanciaDatasExtrasLinha = (() => {
			if (
				Array.isArray(vacanciaSource?.datas_extras) &&
				vacanciaSource.datas_extras.length > 0
			) {
				return vacanciaSource.datas_extras
					.map((item = {}) => ({
						id: `data-extra-${Date.now()}-${Math.random()
							.toString(36)
							.slice(2, 8)}`,
						selected_date:
							formatDateInputToISO(
								item?.selected_date || item?.data || '',
							) || '',
						hora_inicio: getHourInputFromTimeValue(
							item?.hora_inicio || item?.horaInicio || '',
						),
						hora_fim: getHourInputFromTimeValue(
							item?.hora_fim || item?.horaFim || '',
						),
					}))
					.filter((item) => item.selected_date);
			}

			const padroesExtras = (
				Array.isArray(regimeEscalaLinha?.padroes_alocacao)
					? regimeEscalaLinha.padroes_alocacao
					: []
			).filter(
				(padrao = {}) =>
					normalizeCode(padrao?.tipo_recorrencia) ===
					'DATAS_ESPECIFICAS',
			);

			return padroesExtras
				.flatMap((padrao = {}) => {
					const datas = Array.isArray(padrao?.datas_especificas)
						? padrao.datas_especificas
						: padrao?.data
							? [padrao.data]
							: [];
					const horaInicio = getHourInputFromTimeValue(
						padrao?.hora_inicio || padrao?.horaInicio || '',
					);
					const horaFim = getHourInputFromTimeValue(
						padrao?.hora_fim || padrao?.horaFim || '',
					);
					return datas.map((dataISO, index) => ({
						id: `data-extra-${Date.now()}-${Math.random()
							.toString(36)
							.slice(2, 8)}-${index}`,
						selected_date:
							formatDateInputToISO(dataISO) || dataISO || '',
						hora_inicio: horaInicio || '',
						hora_fim: horaFim || '',
					}));
				})
				.filter((item) => item.selected_date);
		})();
		const diasSemanaInferidosLinha = usaRecorrenciaSemanalLinha
			? inferDiasSemanaFromLinha(linha)
			: [];
		const presetCicloLinha =
			inferPresetCicloFromRegimeEscala(regimeEscalaLinha);
		const dataInicioVacanciaLinha =
			getVacanciaInicioFromLinha(linha) ||
			formatISOToDisplay(
				vacanciaSource?.data_inicio_vacancia ||
					regimeEscalaLinha?.data_ancora ||
					regimeEscalaLinha?.vigencia_inicio ||
					'',
			);
		const diasSemanaCicloInferidosLinha =
			tipoRecorrenciaLinha === 'CICLO_CONTINUO'
				? inferCycleDiasSemanaSelection({
						presetCiclo: presetCicloLinha,
						startDate: dataInicioVacanciaLinha,
						mesReferencia: contextoAplicado?.mes_referencia,
					})
				: [];
		const diasSemanaLinhaBase = (() => {
			if (diasSemanaPadroesLinha.length > 0)
				return diasSemanaPadroesLinha;
			if (diasSemanaRegimeLinha.length > 0) return diasSemanaRegimeLinha;
			if (diasSemanaInferidosLinha.length > 0)
				return diasSemanaInferidosLinha;
			if (diasSemanaCicloInferidosLinha.length > 0) {
				return diasSemanaCicloInferidosLinha;
			}
			return vacanciaDiasSemanaSource;
		})();
		const vacanciaDiasSemanaHorariosSource = (() => {
			const horariosPadroes = padroesBaseLinha.reduce(
				(acc, padrao = {}) => {
					normalizeDiasSemanaValues(padrao?.dias_semana).forEach(
						(diaSemana) => {
							acc[diaSemana] = {
								hora_inicio: getHourInputFromTimeValue(
									padrao?.hora_inicio,
								),
								hora_fim: getHourInputFromTimeValue(
									padrao?.hora_fim,
								),
							};
						},
					);
					return acc;
				},
				{},
			);
			if (Object.keys(horariosPadroes).length > 0) {
				return horariosPadroes;
			}

			const horariosOcorrencias =
				buildDiasSemanaHorariosMapFromOcorrencias(
					regimeEscalaLinha?.ocorrencias,
				);
			if (Object.keys(horariosOcorrencias).length > 0) {
				return horariosOcorrencias;
			}

			if (
				vacanciaSource?.dias_semana_horarios &&
				typeof vacanciaSource.dias_semana_horarios === 'object'
			) {
				return normalizeDiasSemanaHorariosMap(
					vacanciaSource.dias_semana_horarios,
				);
			}
			if (Array.isArray(vacanciaSource?.ocorrencias)) {
				const mapFromOcorrencias =
					buildDiasSemanaHorariosMapFromOcorrencias(
						vacanciaSource.ocorrencias,
					);
				if (Object.keys(mapFromOcorrencias).length > 0) {
					return mapFromOcorrencias;
				}
			}
			if (vacanciaDiasSemanaSource.length > 0) {
				return buildDiasSemanaHorariosMapFromDias({
					dias_semana: vacanciaDiasSemanaSource,
					hora_inicio: String(
						vacanciaSource?.hora_inicio ||
							regimeEscalaLinha?.hora_inicio ||
							'',
					).slice(0, 5),
					hora_fim: String(
						vacanciaSource?.hora_fim ||
							regimeEscalaLinha?.hora_fim ||
							'',
					).slice(0, 5),
				});
			}
			return {};
		})();
		const diasSemanaHorariosLinha = (() => {
			if (Object.keys(vacanciaDiasSemanaHorariosSource).length > 0) {
				return vacanciaDiasSemanaHorariosSource;
			}
			const horariosOcorrencias =
				buildDiasSemanaHorariosMapFromOcorrencias(
					regimeEscalaLinha?.ocorrencias,
				);
			if (Object.keys(horariosOcorrencias).length > 0) {
				return horariosOcorrencias;
			}
			const horariosPadroes = (
				Array.isArray(regimeEscalaLinha?.padroes_alocacao)
					? regimeEscalaLinha.padroes_alocacao
					: []
			).reduce((acc, padrao = {}) => {
				normalizeDiasSemanaValues(padrao?.dias_semana).forEach(
					(diaSemana) => {
						acc[diaSemana] = {
							hora_inicio: String(
								padrao?.hora_inicio ||
									regimeEscalaLinha?.hora_inicio ||
									'',
							).slice(0, 5),
							hora_fim: String(
								padrao?.hora_fim ||
									regimeEscalaLinha?.hora_fim ||
									'',
							).slice(0, 5),
						};
					},
				);
				return acc;
			}, {});
			if (Object.keys(horariosPadroes).length > 0) {
				return horariosPadroes;
			}

			return buildDiasSemanaHorariosMapFromDias({
				dias_semana: diasSemanaLinhaBase,
				hora_inicio: regimeEscalaLinha?.hora_inicio,
				hora_fim: regimeEscalaLinha?.hora_fim,
			});
		})();
		const presetLinhaDefaults = getPresetDefaults(presetCicloLinha);
		const trabalhoHorasLinha =
			Number(regimeEscalaLinha?.trabalho_horas || 0) ||
			(Number(regimeEscalaLinha?.trabalho_minutos || 0) > 0
				? Number(regimeEscalaLinha?.trabalho_minutos || 0) / 60
				: 0) ||
			presetLinhaDefaults?.trabalho_horas ||
			undefined;
		const descansoHorasLinha =
			Number(regimeEscalaLinha?.descanso_horas || 0) ||
			(Number(regimeEscalaLinha?.descanso_minutos || 0) > 0
				? Number(regimeEscalaLinha?.descanso_minutos || 0) / 60
				: 0) ||
			presetLinhaDefaults?.descanso_horas ||
			undefined;

		setIsNovoProfissionalModalOpen(false);
		setIsEditingProfissional(false);
		setEditingProfissionalLinha(null);
		setIsEditingVacancia(!readOnly);
		setIsViewingVacancia(readOnly);
		setEditingVacanciaLinha(linha);
		setVacanciaErrors({});
		setSubmitError('');
		setSubmitErrorScope('');
		setFeedbackMessage('');
		const vacanciaModalPayload = {
			...getDefaultVacanciaForm(),
			setor_id: String(getLinhaSetorId(linha) || ''),
			data_inicio_vacancia: dataInicioVacanciaLinha,
			especialidade_id: getEspecialidadeIdFromLinha(linha),
			nome_vinculo:
				vacanciaVinculoNome ||
				getNomeVinculoFromLinha(linha) ||
				linha?.vacancia?.nome_vinculo ||
				linha?.nome_vinculo ||
				'',
			vinculo_tipo_id: vacanciaVinculoTipoId
				? String(vacanciaVinculoTipoId)
				: linha?.vinculo_profissional?.vinculo_tipo_id ||
					linha?.vinculo_profissional?.vinculo_tipo ||
					linha?.vacancia?.vinculo_tipo_id ||
					linha?.vinculo_tipo_id ||
					regimeEscalaLinha?.vinculo_tipo_id ||
					getFirstCardFromLinha(linha)?.vinculo_tipo_id ||
					'',
			observacao: getVacanciaObservacaoFromLinha(linha),
			regime: regimeLinha || 'PLANTONISTA',
			nome_recorrencia:
				modalidade === 'MEDICA'
					? 'SEMANAL'
					: vacanciaSource?.nome_recorrencia ||
						getRecorrenciaNomeValue({
							tipo_recorrencia: tipoRecorrenciaLinha,
							preset_ciclo: presetCicloLinha,
							padroes_alocacao:
								regimeEscalaLinha?.padroes_alocacao,
						}),
			tipo_recorrencia:
				modalidade === 'MEDICA' ? 'SEMANAL' : tipoRecorrenciaLinha,
			preset_ciclo:
				modalidade === 'MEDICA'
					? 'SEMANAL'
					: vacanciaSource?.preset_ciclo || presetCicloLinha,
			trabalho_horas:
				Number(vacanciaSource?.trabalho_horas || 0) ||
				trabalhoHorasLinha,
			descanso_horas:
				Number(vacanciaSource?.descanso_horas || 0) ||
				descansoHorasLinha,
			hora_inicio: String(
				padraoPrimarioLinha?.hora_inicio ||
					vacanciaSource?.hora_inicio ||
					regimeEscalaLinha?.hora_inicio ||
					presetLinhaDefaults?.hora_inicio ||
					'07:00',
			).slice(0, 5),
			hora_fim: String(
				padraoPrimarioLinha?.hora_fim ||
					vacanciaSource?.hora_fim ||
					regimeEscalaLinha?.hora_fim ||
					presetLinhaDefaults?.hora_fim ||
					'07:00',
			).slice(0, 5),
			data_ancora: formatISOToDisplay(
				vacanciaSource?.data_ancora ||
					regimeEscalaLinha?.data_ancora ||
					regimeEscalaLinha?.vigencia_inicio ||
					'',
			),
			datas_extras: vacanciaDatasExtrasLinha,
			dias_semana_horarios: diasSemanaHorariosLinha,
			dias_semana: diasSemanaLinhaBase,
		};
		setVacanciaForm(vacanciaModalPayload);
		initialVacanciaEditSnapshotRef.current =
			buildVacanciaEditSnapshot(vacanciaModalPayload);
		setIsVacanciaModalOpen(true);
	};

	const handleOpenVacanciaModal = () => {
		initialVacanciaEditSnapshotRef.current = null;
		setIsNovoProfissionalModalOpen(false);
		setIsEditingProfissional(false);
		setEditingProfissionalLinha(null);
		setIsEditingVacancia(false);
		setIsViewingVacancia(false);
		setEditingVacanciaLinha(null);
		setVacanciaForm(
			modalidade === 'MEDICA'
				? withWeeklyRecurrence({
						...getDefaultVacanciaForm(),
						setor_id: isSetorTodosAplicado ? '' : contexto.setor,
					})
				: {
						...getDefaultVacanciaForm(),
						setor_id: isSetorTodosAplicado ? '' : contexto.setor,
					},
		);
		setVacanciaErrors({});
		setSubmitError('');
		setSubmitErrorScope('');
		setVacanciaApplyError('');
		setIsVacanciaModalOpen(true);
	};

	const handleCloseVacanciaModal = () => {
		initialVacanciaEditSnapshotRef.current = null;
		setIsEditingVacancia(false);
		setIsViewingVacancia(false);
		setEditingVacanciaLinha(null);
		setVacanciaForm(getDefaultVacanciaForm());
		setVacanciaErrors({});
		setVacanciaApplyError('');
		setIsVacanciaModalOpen(false);
	};

	const handleOpenDeleteModal = (linha = null) => {
		const targetLinha = linha || linhaSelecionada;
		if (!targetLinha) return;
		setDeleteTargetLinha(targetLinha);
		setIsDeleteFromGerenciamentoTurnos(false);
		if (linha?.linha_id) {
			setSelectedLinhaId(String(linha.linha_id));
		}
		setSelectedDiaIso('');
		setSubmitError('');
		setSubmitErrorScope('');
		setDeleteReason('');
		setDeleteReasonOther('');
		setDeletePreview(null);
		setIsDeleteConfirmModalOpen(true);
	};

	const handleCloseDeleteModal = () => {
		setDeleteTargetLinha(null);
		setIsDeleteFromGerenciamentoTurnos(false);
		setDeleteReason('');
		setDeleteReasonOther('');
		setDeletePreview(null);
		setIsDeleteConfirmModalOpen(false);
	};

	const removeSetoresFromGerenciamentoTurnosModal = useCallback(
		(linhasRemovidas = []) => {
			const removidas = Array.isArray(linhasRemovidas)
				? linhasRemovidas.filter(Boolean)
				: [];
			if (removidas.length === 0) return;

			const setoresRemovidos = removidas
				.map((linha) => String(getLinhaSetorId(linha) || '').trim())
				.filter(Boolean);
			setGerenciamentoTurnosSetoresRemovidos((prev) =>
				Array.from(new Set([...prev, ...setoresRemovidos])),
			);

			const shouldRemoveLinha = (linha = {}, item = null) =>
				removidas.some((linhaRemovida = {}) => {
					const regimeRemovido = Number(
						linhaRemovida?.regime_vinculo_id || 0,
					);
					const regimeAtual = Number(
						item?.regimeVinculoId || linha?.regime_vinculo_id || 0,
					);
					const setorRemovido = String(
						getLinhaSetorId(linhaRemovida) || '',
					);
					const setorAtual = String(
						item?.setorId || getLinhaSetorId(linha) || '',
					);
					const linhaRemovidaId = String(
						linhaRemovida?.linha_id || '',
					);
					const linhaAtualId = String(linha?.linha_id || '');

					if (linhaRemovidaId && linhaRemovidaId === linhaAtualId) {
						return true;
					}
					if (setorRemovido && setorAtual) {
						return setorRemovido === setorAtual;
					}
					return Boolean(
						regimeRemovido && regimeRemovido === regimeAtual,
					);
				});

			// A API pode devolver a jornada-base ainda com a estrutura de recorte
			// após a exclusão do destino. Para que ela possa ser realocada, os
			// setores restantes voltam imediatamente ao modo editável no modal.
			const setoresRestantes = gerenciamentoTurnosSetoresEditaveis
				.filter(
					(item) => !shouldRemoveLinha(item?.sourceLinha || {}, item),
				)
				.map((item) => String(item?.setorId || '').trim())
				.filter(Boolean);
			setGerenciamentoTurnosSetoresComParticoesLiberadas((prev) =>
				Array.from(new Set([...prev, ...setoresRestantes])),
			);

			setGerenciamentoTurnosSetoresEditaveis((prev) =>
				prev.filter(
					(item) => !shouldRemoveLinha(item?.sourceLinha || {}, item),
				),
			);
			setGerenciamentoTurnosSetoresAbertos((prev) => {
				const next = { ...prev };
				gerenciamentoTurnosSetoresEditaveis.forEach((item) => {
					if (shouldRemoveLinha(item?.sourceLinha || {}, item)) {
						delete next[item.id];
					}
				});
				return next;
			});
			setGerenciamentoTurnosLinha((prev) => {
				if (!prev) return prev;
				const linhaSemParticoesDoSetorRemovido =
					removerParticoesDosSetoresDestinos(
						prev,
						new Set(setoresRemovidos),
					);
				if (
					Array.isArray(
						linhaSemParticoesDoSetorRemovido?.setores_linhas,
					)
				) {
					return {
						...linhaSemParticoesDoSetorRemovido,
						setores_linhas:
							linhaSemParticoesDoSetorRemovido.setores_linhas.filter(
								(linhaSetor) => !shouldRemoveLinha(linhaSetor),
							),
					};
				}

				return shouldRemoveLinha(linhaSemParticoesDoSetorRemovido)
					? null
					: linhaSemParticoesDoSetorRemovido;
			});
		},
		[gerenciamentoTurnosSetoresEditaveis],
	);

	const handleCloseEditPanel = () => {
		setOpenMiniTabs(getDefaultMiniTabs());
		setPanelErrors({});
		setIsEditPanelOpen(false);
	};

	const toggleMiniTab = (key) => {
		setOpenMiniTabs((prevState) => {
			const shouldOpen = !prevState[key];
			const resetState = getDefaultMiniTabs();
			if (shouldOpen) resetState[key] = true;
			return resetState;
		});
	};

	const handleClickCell = (linha, celula, rowIndex) => {
		if (!linha || linha?.placeholder) return;
		const origemId = String(
			getCardItemId(getCellPrimaryCard(celula)) || '',
		);
		setSelectedLinhaId(String(linha?.linha_id || ''));
		setSelectedDiaIso(String(celula?.data || ''));
		setEditingProfissionalIndex(rowIndex);
		setPanelForm((prev) => ({
			...prev,
			item_origem_id: origemId,
			item_destino_id: '',
			troca_profissional_id: '',
			troca_data: '',
			troca_turno_id: '',
			troca_perfil: 'COMPENSAR',
			troca_recorte_item_id: '',
			troca_recorte_inicio: '',
			troca_recorte_fim: '',
		}));
		setOpenMiniTabs(getDefaultMiniTabs());
		setIsEditPanelOpen(true);
	};

	const handleNovoProfissionalInput = (field, value) => {
		if (
			novoProfissionalTemEscalaOrigem &&
			!isEditingProfissional &&
			field === 'regime'
		)
			return;
		if (
			novoProfissionalLockedFields[field] &&
			field !== 'admissao' &&
			field !== 'vinculo_tipo_id' &&
			field !== 'nome_vinculo'
		)
			return;
		// Alterar o rascunho invalida a decisão e a chave de uma prévia anterior.
		// Um retry sem alterações, por outro lado, reutiliza a mesma chave.
		if (origemAtivaPendente) setOrigemAtivaPendente(null);
		alocacaoIntentIdempotencyKeysRef.current.clear();

		let nextValue = value;
		if (field === 'nome') nextValue = formatProfessionalNameInput(value);
		if (field === 'cpf') {
			nextValue = formatCpfMask(value);
			if (
				normalizeCpf(value) !== normalizeCpf(novoProfissionalForm.cpf)
			) {
				novoProfissionalConsultadoRef.current = null;
				setNovoProfissionalTemEscalaOrigem(false);
				setNovoProfissionalLockedFields(
					isEditingProfissional
						? {}
						: getDefaultNovoProfissionalLockedFields(),
				);
			}
		}
		if (field === 'telefone') nextValue = formatPhoneMask(value);
		if (field === 'numero_registro')
			nextValue = formatConselhoNumber(value);
		if (field === 'vinculo_tipo_id') nextValue = formatNumericId(value, 10);
		if (field === 'vigencia_inicio')
			nextValue = formatDateDisplayMask(value);

		const isRegimeField = field === 'regime';

		setNovoProfissionalForm((prev) => {
			let nextForm = {
				...prev,
				...(field === 'vigencia_inicio'
					? { data_ancora: nextValue }
					: {}),
				...((field === 'hora_inicio' || field === 'hora_fim') &&
				modalidade === 'ENFERMAGEM' &&
				['CICLO_CONTINUO', 'ROTACAO_PERIODICA', 'SEMANAL'].includes(
					normalizeCode(prev?.tipo_recorrencia),
				)
					? { turno_personalizado: true }
					: {}),
				[field]: nextValue,
				...(field === 'nome_vinculo' ? { vinculo_tipo_id: '' } : {}),
			};

			// Lógica especial para preencher o cargo em ENFERMAGEM
			if (modalidade === 'ENFERMAGEM' && field === 'especialidade_id') {
				const categoriaObj = (dimensaoOptions || []).find(
					(item) => String(item.id) === String(nextValue),
				);
				const categoriaLabel =
					categoriaObj?.nome || categoriaObj?.label || '';
				nextForm = {
					...nextForm,
					cargo: categoriaLabel || 'selecione a categoria',
				};
			}

			const nextHoraInicio =
				field === 'hora_inicio' ? nextValue : prev.hora_inicio;
			const nextHoraFim =
				field === 'hora_fim' ? nextValue : prev.hora_fim;
			const duracaoTurno = calculateShiftDurationHours(
				nextHoraInicio,
				nextHoraFim,
			);

			if (isRegimeField) {
				if (!String(prev?.nome_recorrencia || '').trim()) {
					nextForm.tipo_recorrencia = '';
					nextForm.preset_ciclo = '';
					nextForm.dias_semana = [];
					nextForm.dias_semana_horarios = {};
					nextForm.trabalho_horas =
						duracaoTurno || prev.trabalho_horas || undefined;
					nextForm.descanso_horas = prev.descanso_horas;
				} else if (
					normalizeCode(prev?.tipo_recorrencia) === 'CICLO_CONTINUO'
				) {
					const presetCiclo =
						getPayloadPresetCiclo(prev.preset_ciclo) ||
						inferContinuousPresetFromShift({
							hora_inicio: nextHoraInicio,
							hora_fim: nextHoraFim,
							fallbackPreset: prev.preset_ciclo,
						});
					const presetDefaults =
						getRecurrencePresetDefaults(prev.preset_ciclo) ||
						getRecurrencePresetDefaults(presetCiclo);
					nextForm.tipo_recorrencia = 'CICLO_CONTINUO';
					nextForm.nome_recorrencia =
						getCanonicalPresetCiclo(presetCiclo) ||
						prev.nome_recorrencia;
					nextForm.preset_ciclo = presetCiclo;
					nextForm.dias_semana = [];
					nextForm.dias_semana_horarios = {};
					nextForm.trabalho_horas =
						presetDefaults?.trabalho_horas ||
						duracaoTurno ||
						prev.trabalho_horas;
					nextForm.descanso_horas =
						presetDefaults?.descanso_horas || prev.descanso_horas;
				}
			}

			if (
				field === 'vigencia_inicio' &&
				isWeeklyRecorrencia(nextForm.tipo_recorrencia)
			) {
				const diasSemanaAtuais = normalizeDiasSemanaValues(
					prev?.dias_semana,
				);
				// A vigência define quando a regra começa, não um dia adicional de
				// trabalho. Alterá-la deve preservar a seleção explícita do usuário
				// em ambas as modalidades.
				nextForm.dias_semana = diasSemanaAtuais;
				nextForm.dias_semana_horarios =
					syncDiasSemanaHorariosWithSelection({
						dias_semana: nextForm.dias_semana,
						dias_semana_horarios: prev?.dias_semana_horarios,
						fallbackHoraInicio: nextHoraInicio,
						fallbackHoraFim: nextHoraFim,
					});
			}

			if (
				field === 'vigencia_inicio' &&
				normalizeCode(nextForm.tipo_recorrencia) === 'CICLO_CONTINUO'
			) {
				nextForm.dias_semana = [];
				nextForm.dias_semana_horarios = {};
			}

			if (
				field === 'vigencia_inicio' &&
				isSpecificDatesRecorrencia(nextForm.tipo_recorrencia)
			) {
				nextForm.dias_semana = syncDiasSemanaWithStartDateSeed({
					dias_semana:
						normalizeDiasSemanaValues(prev?.dias_semana).length > 0
							? prev.dias_semana
							: [],
					previousStartDate: prev?.vigencia_inicio,
					nextStartDate: nextValue,
				});
				nextForm.dias_semana_horarios =
					syncDiasSemanaHorariosWithSelection({
						dias_semana: getDiasSemanaWithRequiredStartDate({
							dias_semana: nextForm.dias_semana,
							startDate: nextValue,
						}),
						dias_semana_horarios: prev?.dias_semana_horarios,
						fallbackHoraInicio: nextHoraInicio,
						fallbackHoraFim: nextHoraFim,
					});
			}

			if (
				(field === 'hora_inicio' || field === 'hora_fim') &&
				isWeeklyRecorrencia(nextForm.tipo_recorrencia)
			) {
				nextForm.trabalho_horas =
					duracaoTurno || nextForm.trabalho_horas || undefined;
			}

			if (
				(field === 'hora_inicio' || field === 'hora_fim') &&
				normalizeCode(nextForm.tipo_recorrencia) === 'CICLO_CONTINUO'
			) {
				const presetCiclo = inferContinuousPresetFromShift({
					hora_inicio: nextHoraInicio,
					hora_fim: nextHoraFim,
					fallbackPreset: nextForm.preset_ciclo,
				});
				const presetDefaults = getRecurrencePresetDefaults(presetCiclo);
				nextForm.preset_ciclo = presetCiclo;
				nextForm.dias_semana = [];
				nextForm.dias_semana_horarios = {};
				nextForm.trabalho_horas =
					presetDefaults?.trabalho_horas ||
					duracaoTurno ||
					nextForm.trabalho_horas ||
					undefined;
				nextForm.descanso_horas =
					presetDefaults?.descanso_horas ||
					nextForm.descanso_horas ||
					undefined;
			}

			return nextForm;
		});

		const nextValidationForm = {
			...novoProfissionalForm,
			[field]: nextValue,
			permitir_inicio_semanal_fim_semana: modalidade === 'MEDICA',
		};
		const erroAlocacaoAntesDaAdmissao = getErroAlocacaoAntesDaAdmissao({
			dataAdmissao: nextValidationForm.admissao,
			dataInicioAlocacao: nextValidationForm.vigencia_inicio,
		});
		setNovoProfissionalErrors((prev) => ({
			...prev,
			[field]: validateNovoProfissionalField(field, nextValue, {
				lockedFields: novoProfissionalLockedFields,
				form: nextValidationForm,
				allowPastVigenciaInicio: isEditingProfissional,
			}),
			...((field === 'admissao' || field === 'vigencia_inicio') && {
				admissao: validateNovoProfissionalField(
					'admissao',
					nextValidationForm.admissao,
					{ lockedFields: novoProfissionalLockedFields },
				),
				vigencia_inicio:
					erroAlocacaoAntesDaAdmissao ||
					validateNovoProfissionalField(
						'vigencia_inicio',
						nextValidationForm.vigencia_inicio,
						{
							lockedFields: novoProfissionalLockedFields,
							form: nextValidationForm,
							allowPastVigenciaInicio: isEditingProfissional,
						},
					),
			}),
			...(isRegimeField ? { dias_semana: '', nome_recorrencia: '' } : {}),
		}));
	};

	const handleNovoProfissionalNomeBlur = () => {
		const nextValue = formatProfessionalNameInput(
			novoProfissionalForm.nome,
			{
				trimEnd: true,
			},
		);

		setNovoProfissionalForm((prev) => ({
			...prev,
			nome: nextValue,
		}));
		setNovoProfissionalErrors((prev) => ({
			...prev,
			nome: validateNovoProfissionalField('nome', nextValue, {
				lockedFields: novoProfissionalLockedFields,
			}),
		}));
	};

	const handleNovoProfissionalRecorrenciaChange = (value) => {
		if (novoProfissionalTemEscalaOrigem && !isEditingProfissional) return;
		const option = recorrenciaNomeOptions.find(
			(item) => normalizeCode(item?.value || '') === normalizeCode(value),
		);
		setNovoProfissionalForm((prev) => {
			const nextForm = {
				...prev,
				nome_recorrencia: value,
			};

			if (!option) {
				nextForm.tipo_recorrencia = '';
				nextForm.preset_ciclo = '';
				return nextForm;
			}

			if (option.tipo_recorrencia === 'SEMANAL') {
				const startIso = formatDateInputToISO(prev?.vigencia_inicio);

				nextForm.tipo_recorrencia = 'SEMANAL';
				nextForm.preset_ciclo = 'SEMANAL';
				nextForm.vigencia_inicio = startIso
					? formatISOToDisplay(startIso)
					: prev?.vigencia_inicio;
				nextForm.data_ancora = nextForm.vigencia_inicio;
				nextForm.dias_semana = [];
				nextForm.dias_semana_horarios = {};
				nextForm.turno_personalizado = false;
				nextForm.datas_extras = [];
				return nextForm;
			}

			if (option.tipo_recorrencia === 'DATAS_ESPECIFICAS') {
				const startIso = formatDateInputToISO(prev?.vigencia_inicio);
				const diaInicial = getIsoWeekdayFromValue(startIso);
				nextForm.tipo_recorrencia = 'DATAS_ESPECIFICAS';
				nextForm.preset_ciclo = 'PERSONALIZADO';
				nextForm.dias_semana = diaInicial ? [diaInicial] : [];
				nextForm.dias_semana_horarios =
					buildDiasSemanaHorariosMapFromDias({
						dias_semana: nextForm.dias_semana,
						hora_inicio: prev?.hora_inicio,
						hora_fim: prev?.hora_fim,
					});
				nextForm.datas_extras = [];
				return nextForm;
			}

			if (option.tipo_recorrencia === 'ROTACAO_PERIODICA') {
				const startIso = formatDateInputToISO(prev?.vigencia_inicio);
				nextForm.tipo_recorrencia = 'ROTACAO_PERIODICA';
				nextForm.preset_ciclo = 'ROTACAO_PERIODICA';
				nextForm.vigencia_inicio = startIso
					? formatISOToDisplay(startIso)
					: prev?.vigencia_inicio;
				nextForm.data_ancora = nextForm.vigencia_inicio;
				nextForm.dias_semana = [];
				nextForm.dias_semana_horarios = {};
				nextForm.semanas_rotacao = Array.from(
					{
						length: Math.max(
							2,
							Math.min(6, prev?.semanas_rotacao?.length || 2),
						),
					},
					(_, index) => prev?.semanas_rotacao?.[index] || [],
				);
				nextForm.turno_personalizado = false;
				nextForm.datas_extras = [];
				return nextForm;
			}

			const presetDefaults = getRecurrencePresetDefaults(
				option.preset_ciclo,
			);
			const startIso = formatDateInputToISO(prev?.vigencia_inicio);

			nextForm.tipo_recorrencia = 'CICLO_CONTINUO';
			nextForm.preset_ciclo = option.preset_ciclo;
			nextForm.hora_inicio = String(
				presetDefaults?.hora_inicio || prev?.hora_inicio || '',
			).slice(0, 5);
			nextForm.hora_fim = String(
				presetDefaults?.hora_fim || prev?.hora_fim || '',
			).slice(0, 5);
			nextForm.trabalho_horas =
				presetDefaults?.trabalho_horas || prev?.trabalho_horas || '';
			nextForm.descanso_horas =
				presetDefaults?.descanso_horas || prev?.descanso_horas || '';
			nextForm.vigencia_inicio = startIso
				? formatISOToDisplay(startIso)
				: prev?.vigencia_inicio;
			nextForm.data_ancora = nextForm.vigencia_inicio;
			nextForm.dias_semana = [];
			nextForm.dias_semana_horarios = {};
			nextForm.turno_personalizado = false;
			nextForm.datas_extras = [];

			return nextForm;
		});

		setNovoProfissionalErrors((prev) => ({
			...prev,
			nome_recorrencia: validateNovoProfissionalField(
				'nome_recorrencia',
				value,
				{ lockedFields: novoProfissionalLockedFields },
			),
			dias_semana: '',
			vigencia_inicio: '',
		}));
	};

	const toggleNovoProfissionalDiaSemana = (diaSemana) => {
		setNovoProfissionalForm((prev) => {
			const usesDirectWeeklyDays =
				(modalidade === 'MEDICA' || modalidade === 'ENFERMAGEM') &&
				isWeeklyRecorrencia(prev?.tipo_recorrencia);
			const lockedWeekday =
				!usesDirectWeeklyDays &&
				String(prev?.tipo_recorrencia || '').trim()
					? getRequiredWeekdayFromStartDate(prev?.vigencia_inicio)
					: null;
			if (lockedWeekday === diaSemana) {
				return prev;
			}

			const diasAtuais = Array.isArray(prev?.dias_semana)
				? prev.dias_semana
				: [];
			const jaSelecionado = diasAtuais.includes(diaSemana);
			const diasSemana = jaSelecionado
				? diasAtuais.filter((item) => item !== diaSemana)
				: [...diasAtuais, diaSemana].sort((a, b) => a - b);
			const diasSemanaEfetivos = usesDirectWeeklyDays
				? normalizeDiasSemanaValues(diasSemana)
				: getDiasSemanaWithRequiredStartDate({
						dias_semana: diasSemana,
						startDate: prev?.vigencia_inicio,
					});

			return {
				...prev,
				dias_semana: diasSemana,
				dias_semana_horarios: syncDiasSemanaHorariosWithSelection({
					dias_semana: diasSemanaEfetivos,
					dias_semana_horarios: prev?.dias_semana_horarios,
					fallbackHoraInicio: prev?.hora_inicio,
					fallbackHoraFim: prev?.hora_fim,
				}),
			};
		});

		setNovoProfissionalErrors((prev) => ({
			...prev,
			dias_semana: '',
		}));
	};

	const handleNovoProfissionalDiaSemanaHorarioChange = (
		diaSemana,
		field,
		value,
	) => {
		setNovoProfissionalForm((prev) => {
			const horariosAtuais = normalizeDiasSemanaHorariosMap(
				prev?.dias_semana_horarios,
			);
			const diaAtual = horariosAtuais?.[diaSemana] || {};
			const proximoDia = {
				...diaAtual,
				[field]: normalizeHourInputValue(value),
			};

			const proximosHorarios = { ...horariosAtuais };
			proximosHorarios[diaSemana] = proximoDia;

			return {
				...prev,
				dias_semana_horarios: proximosHorarios,
			};
		});
	};

	const handleAdicionarNovoProfissionalDataExtra = (afterItemId = null) => {
		setNovoProfissionalForm((prev) => {
			const itensAtuais = Array.isArray(prev?.datas_extras)
				? prev.datas_extras
				: [];
			const novoItem = createNovoProfissionalDataExtraItem();

			if (!afterItemId) {
				return {
					...prev,
					datas_extras: [...itensAtuais, novoItem],
				};
			}

			const itemIndex = itensAtuais.findIndex(
				(item) => item.id === afterItemId,
			);

			if (itemIndex === -1) {
				return {
					...prev,
					datas_extras: [...itensAtuais, novoItem],
				};
			}

			const proximosItens = [...itensAtuais];
			proximosItens.splice(itemIndex + 1, 0, novoItem);

			return {
				...prev,
				datas_extras: proximosItens,
			};
		});
	};

	const handleNovoProfissionalDataExtraChange = (itemId, value) => {
		setNovoProfissionalForm((prev) => ({
			...prev,
			datas_extras: (Array.isArray(prev?.datas_extras)
				? prev.datas_extras
				: []
			).map((item) =>
				item.id === itemId && !isDataExtraReadOnly(item)
					? {
							...item,
							selected_date: value,
						}
					: item,
			),
		}));
		setNovoProfissionalErrors((prev) => ({
			...prev,
			datas_extras: '',
		}));
	};

	const handleNovoProfissionalDataExtraHorarioChange = (
		itemId,
		field,
		value,
	) => {
		setNovoProfissionalForm((prev) => ({
			...prev,
			datas_extras: (Array.isArray(prev?.datas_extras)
				? prev.datas_extras
				: []
			).map((item) =>
				item.id === itemId && !isDataExtraReadOnly(item)
					? {
							...item,
							[field]: normalizeHourInputValue(value),
						}
					: item,
			),
		}));
		setNovoProfissionalErrors((prev) => ({
			...prev,
			datas_extras: '',
		}));
	};

	const handleRemoverNovoProfissionalDataExtra = (itemId) => {
		setNovoProfissionalForm((prev) => ({
			...prev,
			datas_extras: (Array.isArray(prev?.datas_extras)
				? prev.datas_extras
				: []
			).filter((item) => item.id !== itemId || isDataExtraReadOnly(item)),
		}));
	};

	const handleVacanciaRecorrenciaChange = (value) => {
		const option = recorrenciaNomeOptions.find(
			(item) => normalizeCode(item?.value || '') === normalizeCode(value),
		);

		setVacanciaForm((prev) => {
			const nextForm = {
				...prev,
				nome_recorrencia: value,
			};

			if (!option) {
				nextForm.tipo_recorrencia = '';
				nextForm.preset_ciclo = '';
				nextForm.datas_extras = [];
				return nextForm;
			}

			if (option.tipo_recorrencia === 'SEMANAL') {
				const isMedicalWeekly = modalidade === 'MEDICA';
				nextForm.tipo_recorrencia = 'SEMANAL';
				nextForm.preset_ciclo = 'SEMANAL';
				nextForm.dias_semana = [];
				nextForm.dias_semana_horarios =
					syncDiasSemanaHorariosWithSelection({
						dias_semana: isMedicalWeekly
							? nextForm.dias_semana
							: getDiasSemanaWithRequiredStartDate({
									dias_semana: nextForm.dias_semana,
									startDate: prev?.data_inicio_vacancia,
								}),
						dias_semana_horarios: prev?.dias_semana_horarios,
						fallbackHoraInicio: prev?.hora_inicio,
						fallbackHoraFim: prev?.hora_fim,
					});
				nextForm.datas_extras = [];
				return nextForm;
			}

			if (option.tipo_recorrencia === 'DATAS_ESPECIFICAS') {
				nextForm.tipo_recorrencia = 'DATAS_ESPECIFICAS';
				nextForm.preset_ciclo = 'PERSONALIZADO';
				nextForm.dias_semana = [];
				nextForm.dias_semana_horarios = {};
				nextForm.datas_extras = Array.isArray(prev?.datas_extras)
					? prev.datas_extras
					: [];
				return nextForm;
			}

			const presetDefaults = getRecurrencePresetDefaults(
				option.preset_ciclo,
			);
			nextForm.tipo_recorrencia = 'CICLO_CONTINUO';
			nextForm.preset_ciclo = getPayloadPresetCiclo(option.preset_ciclo);
			nextForm.hora_inicio = String(
				presetDefaults?.hora_inicio || prev?.hora_inicio || '',
			).slice(0, 5);
			nextForm.hora_fim = String(
				presetDefaults?.hora_fim || prev?.hora_fim || '',
			).slice(0, 5);
			nextForm.trabalho_horas =
				presetDefaults?.trabalho_horas || prev?.trabalho_horas || '';
			nextForm.descanso_horas =
				presetDefaults?.descanso_horas || prev?.descanso_horas || '';
			nextForm.dias_semana = [];
			nextForm.dias_semana_horarios = {};
			nextForm.turno_personalizado = false;
			nextForm.datas_extras = [];
			return nextForm;
		});

		setVacanciaErrors((prev) => ({
			...prev,
			nome_recorrencia: '',
			dias_semana: '',
			datas_extras: '',
		}));
	};

	const setNovoProfissionalRotacaoSemanas = (quantidade) => {
		const total = Math.max(2, Math.min(6, Number(quantidade) || 2));
		setNovoProfissionalForm((prev) => ({
			...prev,
			semanas_rotacao: Array.from(
				{ length: total },
				(_, index) => prev?.semanas_rotacao?.[index] || [],
			),
		}));
	};

	const toggleNovoProfissionalRotacaoDia = (semanaIndex, diaSemana) => {
		setNovoProfissionalForm((prev) => ({
			...prev,
			semanas_rotacao: (prev?.semanas_rotacao || []).map(
				(diasSemana, index) =>
					index === semanaIndex
						? normalizeDiasSemanaValues(
								diasSemana.includes(diaSemana)
									? diasSemana.filter(
											(item) => item !== diaSemana,
										)
									: [...diasSemana, diaSemana],
							)
						: diasSemana,
			),
		}));
		setNovoProfissionalErrors((prev) => ({
			...prev,
			semanas_rotacao: '',
		}));
	};

	const handleVacanciaTurnoCicloChange = (turno) => {
		setVacanciaForm((prev) => ({
			...prev,
			hora_inicio: turno?.inicio || prev.hora_inicio,
			hora_fim: turno?.fim || prev.hora_fim,
			trabalho_horas: 12,
			turno_personalizado: false,
		}));
		setVacanciaErrors((prev) => ({
			...prev,
			hora_inicio: '',
			hora_fim: '',
		}));
	};

	const handleVacanciaPersonalizarTurno = () => {
		setVacanciaForm((prev) => ({ ...prev, turno_personalizado: true }));
	};

	const handleVacanciaUsarTurnosPredefinidos = () => {
		setVacanciaForm((prev) => ({ ...prev, turno_personalizado: false }));
	};

	const handleAdicionarVacanciaDataExtra = (afterItemId = null) => {
		setVacanciaForm((prev) => {
			const itensAtuais = Array.isArray(prev?.datas_extras)
				? prev.datas_extras
				: [];
			const novoItem = createNovoProfissionalDataExtraItem();

			if (!afterItemId) {
				return {
					...prev,
					datas_extras: [...itensAtuais, novoItem],
				};
			}

			const itemIndex = itensAtuais.findIndex(
				(item) => item.id === afterItemId,
			);
			const proximosItens = [...itensAtuais];
			proximosItens.splice(
				itemIndex === -1 ? proximosItens.length : itemIndex + 1,
				0,
				novoItem,
			);
			return {
				...prev,
				datas_extras: proximosItens,
			};
		});
	};

	const handleVacanciaDataExtraChange = (itemId, value) => {
		setVacanciaForm((prev) => ({
			...prev,
			datas_extras: (Array.isArray(prev?.datas_extras)
				? prev.datas_extras
				: []
			).map((item) =>
				item.id === itemId ? { ...item, selected_date: value } : item,
			),
		}));
		setVacanciaErrors((prev) => ({ ...prev, datas_extras: '' }));
	};

	const handleVacanciaDataExtraHorarioChange = (itemId, field, value) => {
		setVacanciaForm((prev) => ({
			...prev,
			datas_extras: (Array.isArray(prev?.datas_extras)
				? prev.datas_extras
				: []
			).map((item) =>
				item.id === itemId
					? { ...item, [field]: normalizeHourInputValue(value) }
					: item,
			),
		}));
		setVacanciaErrors((prev) => ({ ...prev, datas_extras: '' }));
	};

	const handleRemoverVacanciaDataExtra = (itemId) => {
		setVacanciaForm((prev) => ({
			...prev,
			datas_extras: (Array.isArray(prev?.datas_extras)
				? prev.datas_extras
				: []
			).filter((item) => item.id !== itemId),
		}));
	};

	const toggleVacanciaDiaSemana = (diaSemana) => {
		setVacanciaForm((prev) => {
			const isMedicalWeekly =
				modalidade === 'MEDICA' &&
				isWeeklyRecorrencia(prev?.tipo_recorrencia);
			const lockedWeekday = isMedicalWeekly
				? null
				: getLockedWeekdayFromStartDate({
						regime: prev?.regime,
						startDate: prev?.data_inicio_vacancia,
					});
			if (lockedWeekday === diaSemana) {
				return prev;
			}

			const diasAtuais = Array.isArray(prev?.dias_semana)
				? prev.dias_semana
				: [];
			const jaSelecionado = diasAtuais.includes(diaSemana);
			const diasSemana = jaSelecionado
				? diasAtuais.filter((item) => item !== diaSemana)
				: [...diasAtuais, diaSemana].sort((a, b) => a - b);
			const diasSemanaEfetivos = isMedicalWeekly
				? normalizeDiasSemanaValues(diasSemana)
				: getEffectiveDiasSemanaSelection({
						dias_semana: diasSemana,
						regime: prev?.regime,
						startDate: prev?.data_inicio_vacancia,
					});
			return {
				...prev,
				dias_semana: diasSemana,
				dias_semana_horarios: syncDiasSemanaHorariosWithSelection({
					dias_semana: diasSemanaEfetivos,
					dias_semana_horarios: prev?.dias_semana_horarios,
					fallbackHoraInicio: prev?.hora_inicio,
					fallbackHoraFim: prev?.hora_fim,
				}),
			};
		});

		setVacanciaErrors((prev) => ({
			...prev,
			dias_semana: '',
		}));
	};

	const handleVacanciaDiaSemanaHorarioChange = (diaSemana, field, value) => {
		setVacanciaForm((prev) => {
			const horariosAtuais = normalizeDiasSemanaHorariosMap(
				prev?.dias_semana_horarios,
			);
			const horarioAtualDoDia = horariosAtuais?.[diaSemana] || {};
			const proximoDia = {
				...horarioAtualDoDia,
				[field]: normalizeHourInputValue(value),
			};
			const proximosHorarios = { ...horariosAtuais };

			const isCycleRecurrence =
				normalizeCode(prev?.tipo_recorrencia) === 'CICLO_CONTINUO';

			if (isCycleRecurrence) {
				const diasSelecionados = normalizeDiasSemanaValues(
					prev?.dias_semana,
				);
				diasSelecionados.forEach((dia) => {
					const horarioAtual = horariosAtuais?.[dia] || {};
					const proximoHorario = {
						...horarioAtual,
						[field]: normalizeHourInputValue(value),
					};

					if (
						!proximoHorario?.hora_inicio &&
						!proximoHorario?.hora_fim
					) {
						delete proximosHorarios[dia];
					} else {
						proximosHorarios[dia] = proximoHorario;
					}
				});
			} else {
				proximosHorarios[diaSemana] = proximoDia;
			}

			return {
				...prev,
				dias_semana_horarios: proximosHorarios,
			};
		});
	};

	const handleVacanciaInput = (field, value) => {
		let nextValue = value;
		if (field === 'vinculo_tipo_id') nextValue = formatNumericId(value, 10);
		if (field === 'data_inicio_vacancia')
			nextValue = formatDateDisplayMask(value);

		const isRegimeField = field === 'regime';

		setVacanciaForm((prev) => {
			const selectedVinculo =
				field === 'vinculo_tipo_id'
					? vinculoOpcoes.find(
							(v) => String(v.value) === String(nextValue),
						)
					: null;
			const nextForm = {
				...prev,
				...(field === 'data_inicio_vacancia'
					? { data_ancora: nextValue }
					: {}),
				[field]: nextValue,
				...(field === 'vinculo_tipo_id'
					? {
							nome_vinculo:
								selectedVinculo?.label || prev.nome_vinculo,
						}
					: {}),
			};

			const nextHoraInicio =
				field === 'hora_inicio' ? nextValue : prev.hora_inicio;
			const nextHoraFim =
				field === 'hora_fim' ? nextValue : prev.hora_fim;
			if (
				(field === 'hora_inicio' || field === 'hora_fim') &&
				normalizeCode(prev?.tipo_recorrencia) === 'CICLO_CONTINUO'
			) {
				const horasTrabalho =
					Number(prev?.trabalho_horas) ||
					Number(
						getRecurrencePresetDefaults(prev?.preset_ciclo)
							?.trabalho_horas,
					) ||
					12;
				const horarioAlterado = formatHourInputToTimeValue(nextValue);
				if (horarioAlterado) {
					nextForm[
						field === 'hora_inicio' ? 'hora_fim' : 'hora_inicio'
					] = addMinutesToHour(
						horarioAlterado,
						(field === 'hora_inicio' ? 1 : -1) *
							Math.round(horasTrabalho * 60),
					);
				}
			}
			const duracaoTurno = calculateShiftDurationHours(
				nextHoraInicio,
				nextHoraFim,
			);

			if (isRegimeField) {
				if (!String(prev?.nome_recorrencia || '').trim()) {
					nextForm.tipo_recorrencia = '';
					nextForm.preset_ciclo = '';
					nextForm.dias_semana = [];
					nextForm.dias_semana_horarios = {};
					nextForm.trabalho_horas =
						duracaoTurno || prev.trabalho_horas || undefined;
					nextForm.descanso_horas = prev.descanso_horas;
				} else if (
					normalizeCode(prev?.tipo_recorrencia) === 'CICLO_CONTINUO'
				) {
					const presetCiclo = inferContinuousPresetFromShift({
						hora_inicio: nextHoraInicio,
						hora_fim: nextHoraFim,
						fallbackPreset: prev.preset_ciclo,
					});
					const presetDefaults = getPresetDefaults(presetCiclo);
					nextForm.tipo_recorrencia = 'CICLO_CONTINUO';
					nextForm.preset_ciclo = presetCiclo;
					nextForm.nome_recorrencia =
						getCanonicalPresetCiclo(presetCiclo) || 'CICLO_12X36';
					nextForm.dias_semana = [];
					nextForm.dias_semana_horarios = {};
					nextForm.trabalho_horas =
						presetDefaults?.trabalho_horas ||
						duracaoTurno ||
						prev.trabalho_horas ||
						undefined;
					nextForm.descanso_horas =
						presetDefaults?.descanso_horas || prev.descanso_horas;
				}
			}

			if (
				field === 'data_inicio_vacancia' &&
				normalizeCode(nextForm.tipo_recorrencia) === 'CICLO_CONTINUO'
			) {
				nextForm.dias_semana = [];
				nextForm.dias_semana_horarios = {};
			}

			if (
				(field === 'hora_inicio' || field === 'hora_fim') &&
				isWeeklyRecorrencia(nextForm.tipo_recorrencia)
			) {
				nextForm.trabalho_horas =
					duracaoTurno || nextForm.trabalho_horas || undefined;
			}

			if (
				(field === 'hora_inicio' || field === 'hora_fim') &&
				normalizeCode(nextForm.tipo_recorrencia) === 'CICLO_CONTINUO'
			) {
				const presetCiclo = inferContinuousPresetFromShift({
					hora_inicio: nextHoraInicio,
					hora_fim: nextHoraFim,
					fallbackPreset: nextForm.preset_ciclo,
				});
				const presetDefaults = getPresetDefaults(presetCiclo);
				nextForm.preset_ciclo = presetCiclo;
				nextForm.trabalho_horas =
					presetDefaults?.trabalho_horas ||
					duracaoTurno ||
					nextForm.trabalho_horas ||
					undefined;
				nextForm.descanso_horas =
					presetDefaults?.descanso_horas ||
					nextForm.descanso_horas ||
					undefined;
				nextForm.dias_semana = [];
				nextForm.dias_semana_horarios = {};
			}

			return nextForm;
		});
		setVacanciaErrors((prev) => ({
			...prev,
			[field]: validateVacanciaField(field, nextValue),
			...(isRegimeField ? { dias_semana: '' } : {}),
		}));
	};

	const handlePanelInput = (field, value) => {
		setPanelForm((prev) => ({
			...prev,
			...(field === 'troca_profissional_id'
				? {
						troca_data: '',
						troca_turno_id: '',
						item_destino_id: '',
						troca_perfil: 'COMPENSAR',
						troca_recorte_item_id: '',
						troca_recorte_inicio: '',
						troca_recorte_fim: '',
					}
				: {}),
			...(field === 'troca_data'
				? {
						troca_turno_id: '',
						item_destino_id: '',
						troca_perfil: 'COMPENSAR',
						troca_recorte_item_id: '',
						troca_recorte_inicio: '',
						troca_recorte_fim: '',
					}
				: {}),
			...(field === 'troca_turno_id'
				? {
						item_destino_id: value,
						troca_perfil: '',
						troca_recorte_item_id: '',
						troca_recorte_inicio: '',
						troca_recorte_fim: '',
					}
				: {}),
			[field]: value,
		}));
		setPanelErrors((prev) => ({ ...prev, [field]: '' }));
	};

	const handleBuscarPorCpf = async () => {
		if (!token) return;
		setNovoProfissionalErrorDisplayMode('search');

		const cpf = normalizeCpf(novoProfissionalForm.cpf);
		if (!cpf) {
			setNovoProfissionalForm((prev) => ({
				...prev,
				nome: '',
				admissao: '',
				nome_vinculo: '',
				vinculo_tipo_id: '',
				cargo: DEFAULT_MEDICO_CARGO,
				numero_registro: '',
				uf_conselho: 'RJ',
				especialidade_id: '',
				telefone: '',
				regime: '',
				hora_inicio: '',
				hora_fim: '',
			}));
			setNovoProfissionalLockedFields(
				getDefaultNovoProfissionalLockedFields(),
			);
			setFeedbackMessage('');
		}
		if (!cpf || cpf.length !== 11) {
			setNovoProfissionalErrors((prev) => ({
				...prev,
				cpf: 'Informe um CPF válido com 11 dígitos.',
			}));
			return;
		}

		const estabelecimentoBusca = getEstabelecimentoIdValue(
			contexto.estabelecimento ||
				contextoAplicado?.estabelecimento ||
				userCnes,
		);

		if (showUnidadeFilter && !estabelecimentoBusca) {
			setSubmitErrorScope('novoProfissional');
			setSubmitError(
				'Selecione uma unidade antes de pesquisar profissional por CPF.',
			);
			return;
		}

		setSubmitError('');
		setSubmitErrorScope('');
		setNovoProfissionalErrors((prev) => ({ ...prev, cpf: '' }));
		setFeedbackMessage('');

		const result = await requestBuscarProfissionalEscala({
			token,
			cpf,
			estabelecimento: estabelecimentoBusca || undefined,
			scope,
		});

		if (result?.error) {
			setNovoProfissionalErrors((prev) => ({
				...prev,
				cpf: getErrorMessage(
					result,
					'Não foi possível buscar profissional pelo CPF.',
				),
			}));
			setSubmitErrorScope('');
			setSubmitError(
				getErrorMessage(
					result,
					'Não foi possível buscar profissional pelo CPF.',
				),
			);
			return;
		}

		if (!Array.isArray(result) || result.length === 0) {
			setNovoProfissionalErrors((prev) => ({
				...prev,
				cpf: 'Nenhum profissional encontrado para o CPF informado.',
			}));
			return;
		}

		const profissional = result[0] || {};
		novoProfissionalConsultadoRef.current = profissional;
		// A pesquisa de cadastro não contém regime/recorrência. A leitura em
		// todos os setores permite espelhar a escala de origem sem depender do
		// filtro visual que estiver aberto no momento.
		let preenchimentoEscalaOrigem = null;
		const mesReferenciaBusca =
			contextoAplicado?.mes_referencia || contexto?.mes_referencia;
		if (estabelecimentoBusca && mesReferenciaBusca) {
			const ambienteEscalasExistentes =
				await requestAbrirAmbientePreenchimentoEscala({
					token,
					contexto: {
						estabelecimento: estabelecimentoBusca,
						setor: '',
						mes_referencia: mesReferenciaBusca,
					},
					modo: 'detalhado',
					scope,
				});
			if (!ambienteEscalasExistentes?.error) {
				const linhasEscalasExistentes = [
					...(Array.isArray(ambienteEscalasExistentes?.linhas)
						? ambienteEscalasExistentes.linhas
						: []),
					...(Array.isArray(ambienteEscalasExistentes?.ambiente?.linhas)
						? ambienteEscalasExistentes.ambiente.linhas
						: []),
				];
				preenchimentoEscalaOrigem = getEscalaOrigemPreenchimentoPorCpf({
					linhas: linhasEscalasExistentes,
					cpf,
					ambiente: ambienteEscalasExistentes,
					mesReferencia: mesReferenciaBusca,
					modalidade,
				});
			}
		}
		setNovoProfissionalTemEscalaOrigem(Boolean(preenchimentoEscalaOrigem));
		const dimensionRaw =
			modalidade === 'ENFERMAGEM'
				? (profissional?.categoria_id ??
					profissional?.categoria?.id ??
					profissional?.categoria)
				: (profissional?.especialidade_id ??
					profissional?.especialidade?.id ??
					profissional?.especialidade);
		const especialidadeId = Number(dimensionRaw || 0);
		const nomeVinculo = getNomeVinculoFromProfissional(profissional);
		const vinculoOpcaoPorNome = findVinculoOpcaoByNome(
			vinculoOpcoes,
			nomeVinculo,
		);
		const vinculoTipoId =
			vinculoOpcaoPorNome?.value ||
			getVinculoTipoIdFromProfissional(profissional);
		const cargoProfissional =
			String(profissional?.cargo || '').trim() || DEFAULT_MEDICO_CARGO;
		const categoriaObj = (dimensaoOptions || []).find(
			(item) => String(item.id) === String(especialidadeId),
		);
		const categoriaLabel = categoriaObj?.nome || categoriaObj?.label || '';
		const cargoInicialEnfermagem =
			modalidade === 'ENFERMAGEM'
				? categoriaLabel || 'selecione a categoria'
				: cargoProfissional;

		const nomeProfissional = formatProfessionalNameInput(
			profissional?.nome,
			{
				trimEnd: true,
			},
		);
		const telefoneMascarado = formatPhoneMask(profissional?.telefone || '');
		const nextLockedFields = {
			nome: Boolean(nomeProfissional),
			admissao: false,
			nome_vinculo: false,
			cargo: true,
			numero_registro: Boolean(
				getNumeroConselhoFromProfissional(profissional),
			),
			uf_conselho: Boolean(getUfConselhoFromProfissional(profissional)),
			telefone: Boolean(telefoneMascarado),
			especialidade_id: Boolean(especialidadeId),
		};

		setNovoProfissionalForm((prev) => ({
			...prev,
			cpf: formatCpfMask(cpf),
			nome: nomeProfissional || prev.nome,
			admissao:
				formatISOToDisplay(
					profissional?.admissao || profissional?.data_admissao || '',
				) || prev.admissao,
			nome_vinculo:
				vinculoOpcaoPorNome?.label || nomeVinculo || prev.nome_vinculo,
			cargo: cargoInicialEnfermagem,
			numero_registro:
				getNumeroConselhoFromProfissional(profissional) ||
				prev.numero_registro,
			uf_conselho:
				getUfConselhoFromProfissional(profissional) ||
				prev.uf_conselho ||
				'RJ',
			telefone: telefoneMascarado || prev.telefone,
			vinculo_tipo_id: vinculoTipoId
				? String(vinculoTipoId)
				: prev.vinculo_tipo_id,
			especialidade_id: especialidadeId
				? String(especialidadeId)
				: prev.especialidade_id,
			...(preenchimentoEscalaOrigem
				? {
						regime: preenchimentoEscalaOrigem.regime || prev.regime,
						nome_recorrencia:
							preenchimentoEscalaOrigem.nome_recorrencia ||
							prev.nome_recorrencia,
						tipo_recorrencia:
							preenchimentoEscalaOrigem.tipo_recorrencia ||
							prev.tipo_recorrencia,
						preset_ciclo:
							preenchimentoEscalaOrigem.preset_ciclo ||
							prev.preset_ciclo,
					}
				: {}),
		}));
		setNovoProfissionalLockedFields(nextLockedFields);
		setNovoProfissionalErrors({});

		setFeedbackMessage(
			preenchimentoEscalaOrigem
				? 'Dados do profissional e regime/recorrência da escala de origem carregados com sucesso.'
				: 'Dados do profissional carregados com sucesso.',
		);
	};

	const buildRegimePayload = ({
		source,
		dimensionField,
		dimensionValue,
		fallbackVigenciaInicio,
		fallbackDataAncora,
		forceDefaultRegime = true,
		useFallbackVigenciaInicio = true,
		useFallbackDataAncora = true,
		incluirDiaInicialSemanal = false,
	}) => {
		const regime =
			source?.regime || (forceDefaultRegime ? 'PLANTONISTA' : undefined);
		const hasManualDiasSemana =
			normalizeDiasSemanaValues(source?.dias_semana).length > 0;
		const tipoRecorrencia = normalizeCode(
			source?.tipo_recorrencia ||
				getPresetDefaults(source?.preset_ciclo)?.tipo_recorrencia ||
				(hasManualDiasSemana ? 'SEMANAL' : '') ||
				'CICLO_CONTINUO',
		);
		const presetInformado = normalizePresetCode(source?.preset_ciclo);
		const isCiclo24x120 =
			presetInformado === '24X120' ||
			(Number(source?.trabalho_horas || 0) === 24 &&
				Number(source?.descanso_horas || 0) === 120);
		const presetCiclo =
			(isCiclo24x120 ? 'PERSONALIZADO' : '') ||
			getPayloadPresetCiclo(source?.preset_ciclo) ||
			(tipoRecorrencia === 'SEMANAL' ? 'SEMANAL' : 'PERSONALIZADO');
		const presetDefaults = getPresetDefaults(
			isCiclo24x120 ? '24X120' : presetCiclo,
		);
		const vigenciaInicio =
			formatDateInputToISO(source?.vigencia_inicio) ||
			(useFallbackVigenciaInicio
				? formatDateInputToISO(fallbackVigenciaInicio)
				: undefined) ||
			undefined;
		const dataAncora =
			formatDateInputToISO(source?.data_ancora) ||
			(useFallbackDataAncora
				? formatDateInputToISO(fallbackDataAncora)
				: undefined) ||
			undefined;
		const fallbackHoraInicio =
			String(
				source?.hora_inicio || presetDefaults?.hora_inicio || '',
			).slice(0, 5) || undefined;
		const fallbackHoraFim =
			String(source?.hora_fim || presetDefaults?.hora_fim || '').slice(
				0,
				5,
			) || undefined;
		const weeklyOccurrences =
			tipoRecorrencia === 'SEMANAL'
				? Array.isArray(source?.ocorrencias) &&
					source.ocorrencias.length > 0
					? source.ocorrencias
							.map((ocorrencia) => ({
								dia_semana: Number(ocorrencia?.dia_semana || 0),
								hora_inicio: String(
									ocorrencia?.hora_inicio || '',
								).slice(0, 5),
								hora_fim: String(
									ocorrencia?.hora_fim || '',
								).slice(0, 5),
							}))
							.filter(
								(ocorrencia) =>
									ocorrencia.dia_semana >= 1 &&
									ocorrencia.dia_semana <= 7 &&
									ocorrencia.hora_inicio &&
									ocorrencia.hora_fim,
							)
					: buildWeeklyOccurrencesPayload({
							dias_semana: source?.dias_semana,
							dias_semana_horarios: source?.dias_semana_horarios,
							fallbackHoraInicio,
							fallbackHoraFim,
							regime: incluirDiaInicialSemanal ? regime : '',
							startDate: vigenciaInicio || dataAncora,
						})
				: [];
		const weeklyReference = getWeeklyOccurrenceReference(
			weeklyOccurrences,
			fallbackHoraInicio,
			fallbackHoraFim,
		);
		const horaInicio =
			tipoRecorrencia === 'SEMANAL'
				? weeklyReference?.hora_inicio || undefined
				: fallbackHoraInicio;
		const horaFim =
			tipoRecorrencia === 'SEMANAL'
				? weeklyReference?.hora_fim || undefined
				: fallbackHoraFim;
		const trabalhoHoras =
			Number(source?.trabalho_horas || 0) ||
			presetDefaults?.trabalho_horas ||
			undefined;
		const descansoHoras =
			Number(source?.descanso_horas || 0) ||
			presetDefaults?.descanso_horas ||
			undefined;
		const basePayload = {
			[dimensionField]: Number(dimensionValue) || undefined,
			regime,
			hora_inicio: horaInicio,
			hora_fim: horaFim,
			vigencia_inicio: vigenciaInicio,
		};
		const padroesAlocacao = buildPadroesAlocacaoPayload({
			source,
			fallbackHoraInicio: horaInicio || fallbackHoraInicio,
			fallbackHoraFim: horaFim || fallbackHoraFim,
			regime,
			vigenciaInicio: vigenciaInicio || dataAncora,
			mesReferencia: fallbackVigenciaInicio || fallbackDataAncora,
			incluirDiaInicialSemanal,
		});

		if (tipoRecorrencia === 'SEMANAL') {
			const diasSemanaBase = normalizeDiasSemanaValues(
				source?.dias_semana,
			);
			const diasSemana =
				diasSemanaBase.length > 0
					? diasSemanaBase
					: normalizeDiasSemanaValues(
							weeklyOccurrences.map(
								(ocorrencia) => ocorrencia?.dia_semana,
							),
						);
			return {
				...basePayload,
				tipo_recorrencia: 'SEMANAL',
				preset_ciclo: 'SEMANAL',
				dias_semana: diasSemana,
				ocorrencias: weeklyOccurrences,
				padroes_alocacao:
					padroesAlocacao.length > 0 ? padroesAlocacao : undefined,
				trabalho_horas:
					trabalhoHoras ||
					getWeeklyHoursFromOccurrences(weeklyOccurrences) ||
					calculateShiftDurationHours(horaInicio, horaFim) ||
					undefined,
				descanso_horas: descansoHoras,
			};
		}

		if (tipoRecorrencia === 'ROTACAO_PERIODICA') {
			return {
				...basePayload,
				tipo_recorrencia: 'ROTACAO_PERIODICA',
				preset_ciclo: 'ROTACAO_PERIODICA',
				padroes_alocacao:
					padroesAlocacao.length > 0 ? padroesAlocacao : undefined,
				data_ancora: dataAncora,
			};
		}

		if (tipoRecorrencia === 'DATAS_ESPECIFICAS') {
			const diasSemana = getDiasSemanaWithRequiredStartDate({
				dias_semana:
					normalizeDiasSemanaValues(source?.dias_semana).length > 0
						? source?.dias_semana
						: padroesAlocacao.flatMap(
								(item) => item?.dias_semana || [],
							),
				startDate: vigenciaInicio,
			});

			return {
				...basePayload,
				tipo_recorrencia: 'DATAS_ESPECIFICAS',
				preset_ciclo:
					presetCiclo === 'SEMANAL' ? 'PERSONALIZADO' : presetCiclo,
				padroes_alocacao:
					padroesAlocacao.length > 0 ? padroesAlocacao : undefined,
				trabalho_horas: trabalhoHoras,
				descanso_horas: descansoHoras,
				dias_semana: diasSemana.length > 0 ? diasSemana : undefined,
				datas_especificas: Array.isArray(source?.datas_especificas)
					? source.datas_especificas
							.map((item) => formatDateInputToISO(item))
							.filter(Boolean)
					: padroesAlocacao
								.flatMap(
									(item) => item?.datas_especificas || [],
								)
								.filter(Boolean).length > 0
						? padroesAlocacao
								.flatMap(
									(item) => item?.datas_especificas || [],
								)
								.filter(Boolean)
						: undefined,
			};
		}

		return {
			...basePayload,
			tipo_recorrencia: 'CICLO_CONTINUO',
			preset_ciclo:
				presetCiclo === 'SEMANAL' ? 'PERSONALIZADO' : presetCiclo,
			padroes_alocacao:
				padroesAlocacao.length > 0 ? padroesAlocacao : undefined,
			trabalho_horas: trabalhoHoras,
			descanso_horas: descansoHoras,
			data_ancora: dataAncora,
		};
	};

	const handleCloseProfissionalHorarioSobrepostoPopup = () => {
		setProfissionalHorarioSobrepostoPopup(null);
	};

	const showProfissionalHorarioSobrepostoPopup = (result) => {
		const popupData = buildProfissionalHorarioSobrepostoDisplay(result);
		if (!popupData) return false;

		setProfissionalHorarioSobrepostoPopup({
			...popupData,
			cpf: normalizeCpf(novoProfissionalForm?.cpf),
		});
		return true;
	};

	const showConflitoDeProfissionalExistente = () => {
		if (isEditingProfissional || !novoProfissionalLockedFields?.nome) {
			return false;
		}
		setProfissionalHorarioSobrepostoPopup({
			titulo: 'Criação impedida por conflito de escala',
			mensagem:
				'O profissional já está alocado em outro setor, e a nova escala conflita com uma jornada já criada.',
			conflitos: [],
			renderedConflictLines: [],
			cpf: normalizeCpf(novoProfissionalForm?.cpf),
		});
		return true;
	};

	const handleConsultarSetoresDoProfissionalEmConflito = async () => {
		setIsAbrindoGerenciamentoJornada(true);
		const cpf = normalizeCpf(
			profissionalHorarioSobrepostoPopup?.cpf ||
				novoProfissionalForm?.cpf,
		);
		try {
			if (!cpf || !token) return;
			const estabelecimento = getEstabelecimentoIdValue(
				contexto?.estabelecimento ||
					contextoAplicado?.estabelecimento ||
					userCnes,
			);
			let profissionalConsultado = novoProfissionalConsultadoRef.current;
			if (!profissionalConsultado) {
				const resultadoBusca = await requestBuscarProfissionalEscala({
					token,
					cpf,
					estabelecimento: estabelecimento || undefined,
					scope,
				});
				if (resultadoBusca?.error || !Array.isArray(resultadoBusca)) return;
				profissionalConsultado = resultadoBusca[0] || null;
				novoProfissionalConsultadoRef.current = profissionalConsultado;
			}
			const vinculoId = Number(
				profissionalConsultado?.vinculo_profissional_id ||
					profissionalConsultado?.vinculo_estabelecimento_id ||
					profissionalConsultado?.vinculo ||
					0,
			);
			if (!vinculoId) return;
			const linha = {
				profissional: {
					id: profissionalConsultado?.id,
					cpf,
					nome: profissionalConsultado?.nome,
				},
				vinculo_profissional: { id: vinculoId, cpf },
			};

			setProfissionalHorarioSobrepostoPopup(null);
			handleCloseNovoProfissionalModal();
			await handleOpenGerenciamentoTurnosModal(linha);
		} finally {
			setIsAbrindoGerenciamentoJornada(false);
		}
	};

	const aplicarPreenchimentoComPreview = async (body) => {
		const previewResponse = await requestPreverPreenchimentoAmbienteEscala({
			token,
			body,
			scope,
		});
		if (previewResponse?.error) return previewResponse;
		const applyAction = previewResponse?.acoes?.aplicar;
		if (applyAction?.disponivel === false) {
			return {
				error: true,
				message:
					applyAction?.motivo ||
					'Esta operação não está disponível para confirmação.',
			};
		}

		const previewEtag = getPreenchimentoPreviewEtag(previewResponse);
		if (!previewEtag) {
			return {
				error: true,
				message:
					'Não foi possível obter o ETag do preview de preenchimento.',
			};
		}

		return requestAplicarPreenchimentoAmbienteEscala({
			token,
			body: {
				...body,
				...(applyAction?.entrada || {}),
				preview_etag: previewEtag,
			},
			action: applyAction,
			scope,
		});
	};

	const escolherParticaoDaOrigemAtiva = async () => {
		const pendencia = origemAtivaPendente;
		if (!pendencia) return;
		const candidatas = pendencia.candidatas || [];
		const candidata =
			candidatas.length === 1
				? candidatas[0]
				: candidatas.find(
						(item = {}) =>
							String(
								getAlocacaoOrigemIdFromPayload(item) || '',
							) ===
							String(pendencia.alocacaoOrigemIdSelecionada || ''),
					);
		if (!candidata) {
			setSubmitErrorScope('novoProfissional');
			setSubmitError(
				'Selecione a jornada de origem que deve receber este setor.',
			);
			return;
		}

		const alocacaoOrigemId = getAlocacaoOrigemIdFromPayload(candidata);
		const acaoPreferida =
			getAcaoParaAdicionarSetorNaAlocacaoOrigem(candidata) ||
			getAcaoParaAdicionarSetorNaAlocacaoOrigem(pendencia.preview);
		if (!alocacaoOrigemId || !acaoPreferida) {
			setSubmitErrorScope('novoProfissional');
			setSubmitError(
				'O servidor não retornou uma ação válida para editar esta jornada.',
			);
			return;
		}

		setIsSubmittingAdicionar(true);
		setSubmitError('');
		try {
			await prepararParticionamentoNovoSetor(
				pendencia.novoItem,
				pendencia.linhaReferencia,
				{
					alocacaoOrigemId,
					acaoPreferida,
					originatedFromProfessionalModal:
						pendencia.originatedFromProfessionalModal === true,
					idempotencyKey: pendencia.idempotencyKey,
				},
			);
			setOrigemAtivaPendente(null);
		} catch (error) {
			setSubmitErrorScope('novoProfissional');
			setSubmitError(
				error?.message ||
					'Não foi possível preparar a divisão da jornada atual.',
			);
		} finally {
			setIsSubmittingAdicionar(false);
		}
	};

	const criarOrigemIndependenteDaOrigemAtiva = async (
		pendenciaForcada = null,
	) => {
		const pendencia = pendenciaForcada || origemAtivaPendente;
		if (!pendencia) return;
		const acao = getAcaoRefazerComoOrigemIndependente(pendencia.preview);
		const submitScope = pendencia.originatedFromProfessionalModal
			? 'novoProfissional'
			: 'gerenciamentoTurnosNovoSetor';
		const novoItem = pendencia.novoItem || {};
		const linhaReferencia =
			pendencia.linhaReferencia || getGerenciamentoTurnosBaseLinha();
		const {
			regimePayload,
			dataInicialIso,
			dimensionValue: dimensionValueDaLinha,
		} = buildGerenciamentoTurnosPayloadData({
			...novoItem,
			dimensionValue: pendencia.dimensionValue || novoItem.dimensionValue,
			sourceLinha: linhaReferencia,
		});
		const dimensionValue = String(
			pendencia.dimensionValue || dimensionValueDaLinha || '',
		);
		const setorDestino = Number(novoItem?.setorId || 0);
		const vinculoProfissionalId = Number(
			getVinculoIdFromLinha(linhaReferencia) || 0,
		);
		const dimensionField =
			modalidade === 'ENFERMAGEM' ? 'categoria_id' : 'especialidade_id';
		const padroesOrigem = normalizePadroesAlocacao(
			regimePayload?.padroes_alocacao,
		).map((padrao = {}) =>
			Object.fromEntries(
				Object.entries({
					tipo_recorrencia: padrao.tipo_recorrencia,
					preset_ciclo: getPayloadPresetCiclo(padrao.preset_ciclo),
					dias_semana: getPadraoDiasSemana(padrao),
					datas_especificas: getPadraoDatasEspecificas(padrao),
					hora_inicio: formatHourInputToTimeValue(padrao.hora_inicio),
					hora_fim: formatHourInputToTimeValue(padrao.hora_fim),
					data_ancora: formatDateInputToISO(padrao.data_ancora),
					periodo_semanas: padrao.periodo_semanas,
					semanas_rotacao: padrao.semanas_rotacao,
				}).filter(([, value]) => value !== undefined && value !== ''),
			),
		);
		if (
			!setorDestino ||
			!vinculoProfissionalId ||
			!Number(dimensionValue) ||
			!padroesOrigem.length
		) {
			setSubmitErrorScope(submitScope);
			setSubmitError(
				'Não foi possível identificar a especialidade/categoria do vínculo para criar a carga independente. Atualize a escala e tente novamente.',
			);
			return;
		}

		// A carga independente é uma nova origem. Ela não herda UUID, versão ou
		// partições da jornada consultada; somente o vínculo profissional é
		// compartilhado. A intenção é adicionada exclusivamente após a escolha
		// explícita da pessoa usuária.
		const entradaDaAcao = JSON.parse(JSON.stringify(acao?.entrada || {}));
		const {
			alocacao_origem_id: _alocacaoOrigemId,
			versao_esperada: _versaoEsperada,
			particoes: _particoesAtuais,
			configuracao: configuracaoDaAcao = {},
			...entradaSemOrigemAnterior
		} = entradaDaAcao;
		const {
			categoria_id: _categoriaAnterior,
			especialidade_id: _especialidadeAnterior,
			...configuracaoSemDimensaoAnterior
		} = configuracaoDaAcao;
		const corpoIndependente = {
			...entradaSemOrigemAnterior,
			contexto: {
				estabelecimento: getEstabelecimentoIdValue(
					contextoAplicado.estabelecimento,
				),
				mes_referencia:
					getMesReferenciaFromDateInput(dataInicialIso) ||
					contextoAplicado.mes_referencia,
			},
			intencao_criacao: 'CRIAR_ORIGEM_INDEPENDENTE',
			configuracao: {
				...configuracaoSemDimensaoAnterior,
				vinculo_profissional_id: vinculoProfissionalId,
				setor_origem_id: setorDestino,
				[dimensionField]: Number(dimensionValue) || undefined,
				regime: regimePayload.regime,
				vigencia_inicio: dataInicialIso,
				vigencia_fim: null,
				padroes_origem: padroesOrigem,
			},
			particoes: [],
			justificativa: `Carga independente no setor ${setorDestino}.`,
		};

		setIsSubmittingAdicionar(true);
		setSubmitError('');
		try {
			const preview =
				acao?.href && acao?.disponivel !== false
					? await requestExecutarAcaoEdicaoAlocacaoOrigem({
							token,
							action: acao,
							body: corpoIndependente,
							scope,
						})
					: await requestPreverParticionamentoAlocacaoOrigem({
							token,
							body: corpoIndependente,
							scope,
						});
			if (preview?.error) {
				throw createApiResponseError(
					preview,
					getParticionamentoPreviewMessage(preview),
				);
			}
			if (!isParticionamentoPreviewValido(preview)) {
				if (showProfissionalHorarioSobrepostoPopup(preview)) return;
				setSubmitErrorScope(submitScope);
				setSubmitError(
					getPreviewBlockMessage(
						preview,
						'A criação de carga independente foi bloqueada.',
					),
				);
				return;
			}
			const entradaAplicar = preview?.acoes?.aplicar?.entrada;
			const previewEtag = getPreenchimentoPreviewEtag(preview);
			if (!entradaAplicar || !previewEtag) {
				throw new Error(
					'Não foi possível preparar a confirmação da carga independente.',
				);
			}
			setParticionamentoPendente({
				tipo: 'CRIAR_ORIGEM_INDEPENDENTE',
				entrada: JSON.parse(JSON.stringify(entradaAplicar)),
				action: preview?.acoes?.aplicar || null,
				previewEtag,
				idempotencyKey:
					pendencia.idempotencyKey ||
					`origem-independente-${vinculoProfissionalId}-${Date.now()}-${Math.random()
						.toString(36)
						.slice(2, 10)}`,
				setorDestino,
				origem: { setor: setorDestino },
				particoes: [],
				originatedFromProfessionalModal:
					pendencia.originatedFromProfessionalModal === true,
				linhaOrigemProfissional: linhaReferencia,
				issues: Array.isArray(preview?.issues) ? preview.issues : [],
			});
			if (!pendenciaForcada) setOrigemAtivaPendente(null);
		} catch (error) {
			setSubmitErrorScope(submitScope);
			setSubmitError(
				error?.message ||
					'Não foi possível preparar a carga independente.',
			);
		} finally {
			setIsSubmittingAdicionar(false);
		}
	};

	const handleSubmitNovoProfissional = async () => {
		setNovoProfissionalErrorDisplayMode('submit');
		setSubmitError('');
		setSubmitErrorScope('');
		setNovoProfissionalApplyError('');
		setProfissionalHorarioSobrepostoPopup(null);

		const shouldRequireRegimeFields = true;

		if (!token || !contextoAplicado) {
			setSubmitErrorScope('novoProfissional');
			setSubmitError(
				'Selecione o contexto da escala antes de adicionar profissional.',
			);
			return;
		}

		if (
			isEditingProfissional &&
			initialNovoProfissionalEditSnapshotRef.current &&
			!hasSnapshotChanges(
				initialNovoProfissionalEditSnapshotRef.current,
				buildNovoProfissionalEditSnapshot(novoProfissionalForm),
			)
		) {
			setIsNovoProfissionalModalOpen(false);
			setIsEditingProfissional(false);
			setEditingProfissionalLinha(null);
			return;
		}

		const nextErrors = {
			nome: validateNovoProfissionalField(
				'nome',
				novoProfissionalForm.nome,
				{
					lockedFields: novoProfissionalLockedFields,
				},
			),
			cpf: validateNovoProfissionalField(
				'cpf',
				novoProfissionalForm.cpf,
				{
					lockedFields: novoProfissionalLockedFields,
				},
			),
			admissao: validateNovoProfissionalField(
				'admissao',
				novoProfissionalForm.admissao,
				{ lockedFields: novoProfissionalLockedFields },
			),
			vigencia_inicio: validateNovoProfissionalField(
				'vigencia_inicio',
				novoProfissionalForm.vigencia_inicio,
				{
					lockedFields: novoProfissionalLockedFields,
					form: {
						...novoProfissionalForm,
						permitir_inicio_semanal_fim_semana:
							modalidade === 'MEDICA',
					},
					allowPastVigenciaInicio: isEditingProfissional,
				},
			),
			regime: shouldRequireRegimeFields
				? validateNovoProfissionalField(
						'regime',
						novoProfissionalForm.regime,
						{
							lockedFields: novoProfissionalLockedFields,
						},
					)
				: '',
			nome_recorrencia: shouldRequireRegimeFields
				? validateNovoProfissionalField(
						'nome_recorrencia',
						novoProfissionalForm.nome_recorrencia,
						{ lockedFields: novoProfissionalLockedFields },
					)
				: '',
			hora_inicio:
				modalidade === 'ENFERMAGEM' &&
				(novoProfissionalUsaRecorrenciaCiclo ||
					novoProfissionalUsaRotacaoPeriodica) &&
				!formatHourInputToTimeValue(novoProfissionalForm.hora_inicio)
					? !formatHourInputToTimeValue(novoProfissionalForm.hora_fim)
						? 'Informe os horários de início e fim do plantão.'
						: 'Informe a hora inicial do plantão.'
					: '',
			hora_fim:
				modalidade === 'ENFERMAGEM' &&
				(novoProfissionalUsaRecorrenciaCiclo ||
					novoProfissionalUsaRotacaoPeriodica) &&
				!formatHourInputToTimeValue(novoProfissionalForm.hora_fim)
					? 'Informe a hora final do plantão.'
					: '',
			dias_semana:
				shouldRequireRegimeFields &&
				Boolean(String(novoProfissionalForm.regime || '').trim()) &&
				isWeeklyRecorrencia(novoProfissionalForm.tipo_recorrencia)
					? (() => {
							const diasEfetivos = normalizeDiasSemanaValues(
								novoProfissionalForm.dias_semana,
							);
							const erroDias = validateNovoProfissionalField(
								'dias_semana',
								diasEfetivos,
								{ lockedFields: novoProfissionalLockedFields },
							);
							if (erroDias) return erroDias;
							const diasComHorarioIncompleto =
								getIncompleteWeeklyScheduleDays({
									dias_semana: diasEfetivos,
									dias_semana_horarios:
										novoProfissionalForm.dias_semana_horarios,
								});
							return diasComHorarioIncompleto.length > 0
								? 'Informe hora inicial e final para cada dia selecionado.'
								: '';
						})()
					: '',
			datas_extras: '',
			semanas_rotacao:
				novoProfissionalUsaRotacaoPeriodica &&
				(!Array.isArray(novoProfissionalForm.semanas_rotacao) ||
					novoProfissionalForm.semanas_rotacao.length < 2 ||
					novoProfissionalForm.semanas_rotacao.length > 6 ||
					novoProfissionalForm.semanas_rotacao.some(
						(diasSemana) =>
							normalizeDiasSemanaValues(diasSemana).length === 0,
					))
					? 'Configure de 2 a 6 semanas, com ao menos um dia em cada uma.'
					: '',
			vinculo_tipo_id: validateNovoProfissionalField(
				'vinculo_tipo_id',
				novoProfissionalForm.vinculo_tipo_id,
				{ lockedFields: novoProfissionalLockedFields },
			),
			telefone: validateNovoProfissionalField(
				'telefone',
				novoProfissionalForm.telefone,
				{ lockedFields: novoProfissionalLockedFields },
			),
		};
		const erroAlocacaoAntesDaAdmissao = getErroAlocacaoAntesDaAdmissao({
			dataAdmissao: novoProfissionalForm.admissao,
			dataInicioAlocacao: novoProfissionalForm.vigencia_inicio,
		});
		if (!isEditingProfissional && erroAlocacaoAntesDaAdmissao) {
			nextErrors.vigencia_inicio = erroAlocacaoAntesDaAdmissao;
		}
		setNovoProfissionalErrors(nextErrors);

		const cpf = normalizeCpf(novoProfissionalForm.cpf);
		if (Object.values(nextErrors).some(Boolean)) {
			return;
		}

		const dimensionField =
			modalidade === 'ENFERMAGEM' ? 'categoria_id' : 'especialidade_id';
		const dimensionPatchField =
			modalidade === 'ENFERMAGEM' ? 'categoria' : 'especialidade';
		const dimensionValue = novoProfissionalForm.especialidade_id;
		if (!dimensionValue) {
			setNovoProfissionalErrors((prev) => ({
				...prev,
				especialidade_id:
					modalidade === 'ENFERMAGEM'
						? 'Selecione a categoria.'
						: 'Selecione a área de atuação.',
			}));
			return;
		}
		const vigenciaInicioIsoNovoProfissional = formatDateInputToISO(
			novoProfissionalForm.vigencia_inicio,
		);
		const mesReferenciaVigenciaNovoProfissional =
			getMesReferenciaFromDateInput(
				novoProfissionalForm.vigencia_inicio,
			) ||
			contextoAplicado?.mes_referencia ||
			contexto?.mes_referencia ||
			'';

		const linhaEdicao = getLinhaEscopoSetorAtual(
			editingProfissionalLinha || {},
			contextoAplicado,
		);
		const setorEdicao = Number(getLinhaSetorId(linhaEdicao) || 0);
		// A pessoa pode abrir a edição a partir de qualquer setor. Quando a
		// linha pertence a uma jornada de origem (inclusive uma partição), a
		// alteração estrutural é encaminhada abaixo para `alocacao-origem`, que
		// devolve o preview e a aplicação canônicos da jornada completa.
		const isJornadaEstruturalEmEdicao = Boolean(
			exigeEdicaoViaAlocacaoOrigem(linhaEdicao),
		);
		const vinculoIdEdicao = getVinculoIdFromLinha(linhaEdicao);
		const effectiveDiasSemanaNovoProfissional = isWeeklyRecorrencia(
			novoProfissionalForm.tipo_recorrencia,
		)
			? modalidade === 'MEDICA' || modalidade === 'ENFERMAGEM'
				? normalizeDiasSemanaValues(novoProfissionalForm.dias_semana)
				: getDiasSemanaWithRequiredStartDate({
						dias_semana: novoProfissionalForm.dias_semana,
						startDate: novoProfissionalForm.vigencia_inicio,
					})
			: [];
		const weeklyOccurrencesNovoProfissional = buildWeeklyOccurrencesPayload(
			{
				dias_semana: effectiveDiasSemanaNovoProfissional,
				dias_semana_horarios: novoProfissionalForm.dias_semana_horarios,
				fallbackHoraInicio: novoProfissionalForm.hora_inicio,
				fallbackHoraFim: novoProfissionalForm.hora_fim,
				regime: '',
				startDate: novoProfissionalForm.vigencia_inicio,
			},
		);
		const isNovoProfissionalDatasEspecificas = isSpecificDatesRecorrencia(
			novoProfissionalForm.nome_recorrencia,
		);
		const novoProfissionalDatasEspecificasPayload =
			isNovoProfissionalDatasEspecificas
				? getEditableDatasExtras(novoProfissionalForm.datas_extras)
						.map((item) => item.selected_date)
						.filter(Boolean)
				: undefined;
		let regimePayload = buildRegimePayload({
			source: {
				...novoProfissionalForm,
				data_ancora:
					modalidade === 'ENFERMAGEM' &&
					(novoProfissionalUsaRecorrenciaCiclo ||
						novoProfissionalUsaRotacaoPeriodica)
						? novoProfissionalForm.vigencia_inicio
						: novoProfissionalForm.data_ancora,
				datas_extras: getEditableDatasExtras(
					novoProfissionalForm.datas_extras,
				),
				dias_semana: isNovoProfissionalDatasEspecificas
					? []
					: effectiveDiasSemanaNovoProfissional,
				datas_especificas: novoProfissionalDatasEspecificasPayload,
				dias_semana_horarios: novoProfissionalForm.dias_semana_horarios,
				ocorrencias: weeklyOccurrencesNovoProfissional,
			},
			dimensionField,
			dimensionValue,
			fallbackVigenciaInicio:
				vigenciaInicioIsoNovoProfissional ||
				mesReferenciaVigenciaNovoProfissional,
			fallbackDataAncora:
				novoProfissionalForm?.data_ancora ||
				vigenciaInicioIsoNovoProfissional ||
				mesReferenciaVigenciaNovoProfissional,
			forceDefaultRegime: false,
			useFallbackVigenciaInicio: true,
			useFallbackDataAncora: true,
			incluirDiaInicialSemanal: false,
		});
		// Para recorrências não-DATAS_ESPECIFICAS, injeta cada data extra
		// individualmente em padroes_alocacao como DATAS_ESPECIFICAS.
		if (!isNovoProfissionalDatasEspecificas) {
			const extraDatePadroes = getEditableDatasExtras(
				novoProfissionalForm.datas_extras,
			)
				.filter((item) => item.selected_date)
				.map((item) => ({
					tipo_recorrencia: 'DATAS_ESPECIFICAS',
					preset_ciclo: 'PERSONALIZADO',
					datas_especificas: [
						formatDateInputToISO(item.selected_date) ||
							item.selected_date,
					],
					hora_inicio:
						formatHourInputToTimeValue(item.hora_inicio) ||
						undefined,
					hora_fim:
						formatHourInputToTimeValue(item.hora_fim) || undefined,
				}));

			if (extraDatePadroes.length > 0) {
				const tipoRecorrenciaPrincipal = normalizeCode(
					regimePayload?.tipo_recorrencia ||
						novoProfissionalForm.tipo_recorrencia,
				);
				const padroesExistentes = Array.isArray(
					regimePayload.padroes_alocacao,
				)
					? regimePayload.padroes_alocacao
					: [];
				const padroesBaseExistentes = padroesExistentes.filter(
					(padrao) =>
						normalizeCode(padrao?.tipo_recorrencia) !==
						'DATAS_ESPECIFICAS',
				);
				const basePresetCiclo =
					regimePayload.preset_ciclo ||
					getPayloadPresetCiclo(novoProfissionalForm.preset_ciclo) ||
					'PERSONALIZADO';
				const basePresetDefaults =
					getRecurrencePresetDefaults(basePresetCiclo) ||
					getRecurrencePresetDefaults(
						novoProfissionalForm.preset_ciclo,
					);
				const baseHoraInicio =
					formatHourInputToTimeValue(regimePayload.hora_inicio) ||
					formatHourInputToTimeValue(
						novoProfissionalForm.hora_inicio,
					) ||
					weeklyOccurrencesNovoProfissional[0]?.hora_inicio ||
					basePresetDefaults?.hora_inicio ||
					undefined;
				const baseHoraFim =
					formatHourInputToTimeValue(regimePayload.hora_fim) ||
					formatHourInputToTimeValue(novoProfissionalForm.hora_fim) ||
					weeklyOccurrencesNovoProfissional[0]?.hora_fim ||
					basePresetDefaults?.hora_fim ||
					undefined;
				const baseTrabalhoHoras =
					Number(regimePayload.trabalho_horas || 0) ||
					Number(novoProfissionalForm.trabalho_horas || 0) ||
					basePresetDefaults?.trabalho_horas ||
					calculateShiftDurationHours(baseHoraInicio, baseHoraFim) ||
					undefined;
				const baseDescansoHoras =
					Number(regimePayload.descanso_horas || 0) ||
					Number(novoProfissionalForm.descanso_horas || 0) ||
					basePresetDefaults?.descanso_horas ||
					undefined;
				const padroesBase =
					padroesBaseExistentes.length > 0
						? padroesBaseExistentes
						: tipoRecorrenciaPrincipal === 'CICLO_CONTINUO'
							? [
									{
										tipo_recorrencia: 'CICLO_CONTINUO',
										preset_ciclo: basePresetCiclo,
										trabalho_horas: baseTrabalhoHoras,
										descanso_horas: baseDescansoHoras,
										trabalho_minutos: baseTrabalhoHoras
											? Math.round(
													Number(baseTrabalhoHoras) *
														60,
												)
											: undefined,
										descanso_minutos: baseDescansoHoras
											? Math.round(
													Number(baseDescansoHoras) *
														60,
												)
											: undefined,
										data_ancora:
											regimePayload.data_ancora ||
											undefined,
										hora_inicio: baseHoraInicio,
										hora_fim: baseHoraFim,
									},
								]
							: [];

				regimePayload.padroes_alocacao = [
					...padroesBase.filter(
						(padrao) =>
							padrao?.tipo_recorrencia &&
							padrao?.hora_inicio &&
							padrao?.hora_fim,
					),
					...extraDatePadroes,
				];
			}
		}

		const vinculoTipoId =
			Number(normalizeDigits(novoProfissionalForm.vinculo_tipo_id, 10)) ||
			getVinculoTipoIdFromLinha(linhaEdicao) ||
			undefined;
		const cargaHorariaSemanal = getCargaHorariaSemanalFromLinha(
			linhaEdicao,
			{
				regime: regimePayload.regime,
				hora_inicio: regimePayload.hora_inicio,
				hora_fim: regimePayload.hora_fim,
				dias_semana: regimePayload.dias_semana,
				ocorrencias: regimePayload.ocorrencias,
			},
		);

		const currentCadastroSnapshot = buildCadastroProfissionalSnapshot({
			numeroRegistro: getNumeroConselhoFromLinha(linhaEdicao),
			ufConselho: getUfConselhoFromLinha(linhaEdicao),
			telefone: getPhoneFromLinha(linhaEdicao),
			vinculo: getVinculoTipoIdFromLinha(linhaEdicao) || '',
			especialidade: getEspecialidadeIdFromLinha(linhaEdicao),
			cargo: getCargoFromLinha(linhaEdicao),
			cargaHorariaSemanal: getCargaHorariaSemanalFromLinha(linhaEdicao),
			ativo: true,
		});

		const nextCadastroSnapshot = buildCadastroProfissionalSnapshot({
			numeroRegistro: novoProfissionalForm.numero_registro,
			ufConselho: novoProfissionalForm.uf_conselho,
			telefone: novoProfissionalForm.telefone,
			vinculo: vinculoTipoId,
			especialidade: dimensionValue,
			cargo: novoProfissionalForm.cargo,
			cargaHorariaSemanal,
			ativo: true,
		});

		const currentLinhaCadastroSnapshot = buildLinhaCadastroSnapshot({
			vinculoTipoId: getVinculoTipoIdFromLinha(linhaEdicao) || '',
			dataAdmissao: getAdmissaoFromLinha(linhaEdicao),
			cargo: getCargoFromLinha(linhaEdicao),
			dimensionValue: getEspecialidadeIdFromLinha(linhaEdicao),
		});

		const nextLinhaCadastroSnapshot = buildLinhaCadastroSnapshot({
			vinculoTipoId,
			dataAdmissao: novoProfissionalForm.admissao,
			cargo: novoProfissionalForm.cargo,
			dimensionValue,
		});

		const shouldPatchCadastroProfissional =
			isEditingProfissional &&
			Boolean(vinculoIdEdicao) &&
			hasSnapshotChanges(
				currentLinhaCadastroSnapshot,
				nextLinhaCadastroSnapshot,
			);

		const shouldUpsertLinhaProfissional = hasSnapshotChanges(
			currentCadastroSnapshot,
			nextCadastroSnapshot,
		);
		const setorOperacionalProfissional =
			Number(
				novoProfissionalForm.setor_id ||
					getLinhaSetorId(linhaEdicao) ||
					contextoAplicado.setor ||
					0,
			) || 0;

		if (!setorOperacionalProfissional) {
			setSubmitErrorScope('novoProfissional');
			setSubmitError('Selecione o setor para salvar o profissional.');
			return;
		}

		const body = {
			contexto: {
				estabelecimento: getEstabelecimentoIdValue(
					contextoAplicado.estabelecimento,
				),
				setor: setorOperacionalProfissional,
				mes_referencia:
					mesReferenciaVigenciaNovoProfissional ||
					contextoAplicado.mes_referencia,
			},
			cpf,
			profissional: {
				id: isEditingProfissional
					? getProfissionalIdFromLinha(linhaEdicao) || undefined
					: undefined,
				nome:
					formatProfessionalNameInput(novoProfissionalForm.nome, {
						trimEnd: true,
					}) || undefined,
				conselho:
					String(
						novoProfissionalForm.conselho ||
							getConselhoTipoFromLinha(linhaEdicao) ||
							'CRM',
					)
						.trim()
						.toUpperCase() || undefined,
				uf_conselho:
					String(novoProfissionalForm.uf_conselho || '')
						.trim()
						.toUpperCase() || undefined,
				numero_registro:
					normalizeDigits(novoProfissionalForm.numero_registro, 20) ||
					undefined,
				telefone:
					normalizeDigits(novoProfissionalForm.telefone, 11) ||
					undefined,
			},
			vinculo_profissional: {
				id: isEditingProfissional
					? getVinculoIdFromLinha(linhaEdicao) || undefined
					: undefined,
				vinculo_tipo_id: vinculoTipoId,
				data_admissao:
					formatDateInputToISO(novoProfissionalForm.admissao) ||
					undefined,
				cargo:
					String(
						novoProfissionalForm.cargo ||
							getCargoFromLinha(linhaEdicao) ||
							'',
					).trim() || undefined,
				carga_horaria_semanal: cargaHorariaSemanal || undefined,
				[dimensionField]: Number(dimensionValue) || undefined,
			},
			regime_escala: {
				...regimePayload,
				vigencia_inicio:
					vigenciaInicioIsoNovoProfissional ||
					regimePayload?.vigencia_inicio,
				data_ancora:
					regimePayload?.data_ancora ||
					vigenciaInicioIsoNovoProfissional ||
					undefined,
				id: isEditingProfissional
					? Number(
							linhaEdicao?.regime_escala?.id ||
								linhaEdicao?.regime_vinculo_id ||
								0,
						) || undefined
					: undefined,
				versao: isEditingProfissional
					? Number(linhaEdicao?.regime_escala?.versao || 0) ||
						undefined
					: undefined,
				vigencia_fim: isEditingProfissional
					? formatDateInputToISO(
							linhaEdicao?.regime_escala?.vigencia_fim || '',
						) || undefined
					: undefined,
			},
			regime_vinculo_id: isEditingProfissional
				? Number(linhaEdicao?.regime_vinculo_id || 0) || undefined
				: undefined,
			linha_id: isEditingProfissional
				? linhaEdicao?.linha_id || undefined
				: undefined,
		};

		const escalaLinhaChanges = (() => {
			if (isEdicaoProfissionalConsolidadaTodos) {
				return {
					hasAnyChanges: false,
					hasTurnoChanges: false,
					hasRegimeChanges: false,
					hasDataInicialChanges: false,
				};
			}

			if (!isEditingProfissional) {
				return {
					hasAnyChanges: true,
					hasTurnoChanges: true,
					hasRegimeChanges: true,
					hasDataInicialChanges: true,
				};
			}

			const normalizeEscalaSnapshot = (
				regimePayload = {},
				currentDimensionValue,
			) => ({
				dimensionValue: String(Number(currentDimensionValue) || ''),
				regime: normalizeCode(regimePayload?.regime || ''),
				tipoRecorrencia: normalizeCode(
					regimePayload?.tipo_recorrencia || '',
				),
				presetCiclo: getCanonicalPresetCiclo(
					regimePayload?.preset_ciclo,
				),
				horaInicio: String(regimePayload?.hora_inicio || '').slice(
					0,
					5,
				),
				horaFim: String(regimePayload?.hora_fim || '').slice(0, 5),
				trabalhoHoras: String(
					Number(regimePayload?.trabalho_horas || 0) || '',
				),
				descansoHoras: String(
					Number(regimePayload?.descanso_horas || 0) || '',
				),
				vigenciaInicio: formatDateInputToISO(
					regimePayload?.vigencia_inicio,
				),
				dataAncora: formatDateInputToISO(regimePayload?.data_ancora),
				diasSemana: normalizeDiasSemanaValues(
					regimePayload?.dias_semana,
				).join(','),
				ocorrencias: serializeWeeklyOccurrences(
					regimePayload?.ocorrencias,
				),
				padroesAlocacao: serializePadroesAlocacao(
					regimePayload?.padroes_alocacao,
				),
			});

			const currentRegimePayload = buildRegimePayload({
				source: linhaEdicao?.regime_escala || {},
				dimensionField,
				dimensionValue: getEspecialidadeIdFromLinha(linhaEdicao),
				fallbackVigenciaInicio:
					linhaEdicao?.regime_escala?.vigencia_inicio,
				fallbackDataAncora: linhaEdicao?.regime_escala?.data_ancora,
				forceDefaultRegime: false,
				useFallbackVigenciaInicio: false,
				useFallbackDataAncora: false,
			});

			const currentSnapshot = normalizeEscalaSnapshot(
				currentRegimePayload,
				getEspecialidadeIdFromLinha(linhaEdicao),
			);
			const nextSnapshot = normalizeEscalaSnapshot(
				body.regime_escala,
				dimensionValue,
			);

			const changedKeys = Object.keys(nextSnapshot).filter(
				(key) =>
					String(currentSnapshot[key] || '') !==
					String(nextSnapshot[key] || ''),
			);

			return {
				hasAnyChanges: changedKeys.length > 0,
				hasTurnoChanges:
					changedKeys.includes('horaInicio') ||
					changedKeys.includes('horaFim'),
				hasRegimeChanges:
					changedKeys.includes('regime') ||
					changedKeys.includes('tipoRecorrencia') ||
					changedKeys.includes('presetCiclo') ||
					changedKeys.includes('trabalhoHoras') ||
					changedKeys.includes('descansoHoras') ||
					changedKeys.includes('diasSemana') ||
					changedKeys.includes('ocorrencias') ||
					changedKeys.includes('padroesAlocacao') ||
					changedKeys.includes('dimensionValue'),
				hasDataInicialChanges:
					changedKeys.includes('vigenciaInicio') ||
					changedKeys.includes('dataAncora'),
			};
		})();

		const shouldSalvarLinhaProfissional =
			!isEditingProfissional &&
			(shouldUpsertLinhaProfissional ||
				Boolean(escalaLinhaChanges?.hasAnyChanges));

		const shouldPatchEscalaProfissional =
			isEditingProfissional && Boolean(escalaLinhaChanges?.hasAnyChanges);

		const shouldPatchEdicaoProfissional =
			isEditingProfissional &&
			Boolean(vinculoIdEdicao) &&
			Boolean(
				shouldPatchCadastroProfissional ||
				shouldUpsertLinhaProfissional ||
				shouldPatchEscalaProfissional,
			);
		const shouldPatchDadosProfissional =
			isEditingProfissional &&
			Boolean(vinculoIdEdicao) &&
			Boolean(
				shouldPatchCadastroProfissional ||
				hasSnapshotChanges(
					{ ...currentCadastroSnapshot, cargaHorariaSemanal: '' },
					{ ...nextCadastroSnapshot, cargaHorariaSemanal: '' },
				),
			);
		// Regime, recorrência, vigência e horários de uma origem não passam pelo
		// PATCH genérico. Eles seguem a ação HATEOAS da própria origem e geram
		// uma nova versão após a prévia assinada.
		const shouldPatchViaOrigem =
			isJornadaEstruturalEmEdicao && shouldPatchEscalaProfissional;
		const shouldEnviarPatchEdicao = isJornadaEstruturalEmEdicao
			? shouldPatchDadosProfissional
			: shouldPatchEdicaoProfissional;

		const resolvedTurnoCodigo = (() => {
			const horaInicio = String(
				body?.regime_escala?.hora_inicio || '',
			).slice(0, 5);
			const horaFim = String(body?.regime_escala?.hora_fim || '').slice(
				0,
				5,
			);
			const turnoOption = turnosOptions.find(
				(item) =>
					String(item?.hora_inicio || '').slice(0, 5) ===
						horaInicio &&
					String(item?.hora_fim || '').slice(0, 5) === horaFim,
			);

			if (turnoOption?.codigo) return String(turnoOption.codigo);

			const indicadorTurno = getShiftIndicator({
				hora_inicio: horaInicio,
				hora_fim: horaFim,
				regime: body?.regime_escala?.regime,
			});

			if (indicadorTurno === 'D') return 'DIURNO';
			if (indicadorTurno === 'N') return 'NOTURNO';
			if (indicadorTurno === '24h') return '24H';
			return undefined;
		})();

		const patchIdempotencyKey = (() => {
			const regimeId =
				Number(
					body?.regime_vinculo_id ||
						linhaEdicao?.regime_vinculo_id ||
						0,
				) || 0;
			const regimeSlug =
				normalizeCode(
					body?.regime_escala?.regime || '',
				).toLowerCase() || 'regime';
			const monthName =
				getMonthNamePtBr(
					body?.contexto?.mes_referencia ||
						contextoAplicado?.mes_referencia,
				) || 'mes';
			const prefix =
				modalidade === 'ENFERMAGEM'
					? 'vinculo-enfermagem'
					: 'vinculo-medico';

			return `${prefix}-${regimeId}-${regimeSlug}-${monthName}`;
		})();

		setIsSubmittingAdicionar(true);
		setSubmitError('');
		setFeedbackMessage('');

		try {
			let response = null;

			if (!isEditingProfissional && shouldSalvarLinhaProfissional) {
				// O vínculo institucional não cria uma linha de escala. A criação da
				// origem é uma segunda operação, assinada pelo preview, para que um
				// único vínculo possa sustentar todos os setores/partições futuros.
				const cadastroIdentidade = JSON.stringify({
					estabelecimento: getEstabelecimentoIdValue(
						contextoAplicado.estabelecimento,
					),
					cpf,
					vinculo_tipo_id: Number(vinculoTipoId) || null,
					data_admissao:
						formatDateInputToISO(novoProfissionalForm.admissao) ||
						null,
					[dimensionField]: Number(dimensionValue) || null,
				});
				const cadastroPendente = cadastroVinculoPendenteRef.current;
				let vinculoProfissionalId =
					cadastroPendente?.identidade === cadastroIdentidade
						? Number(cadastroPendente?.vinculoProfissionalId || 0)
						: 0;

				if (!vinculoProfissionalId) {
					const cadastroResponse =
						await requestCadastrarVinculoProfissionalEstabelecimento(
							{
								token,
								scope,
								body: (() => {
									// Este endpoint tem contrato próprio. Não reutilize os nomes
									// internos/legados do formulário nem campos de escala.
									const {
										id: _profissionalId,
										telefone: _telefone,
										...profissionalSemTelefone
									} = body.profissional || {};
									const {
										id: _vinculoId,
										vinculo_tipo_id: _vinculoTipoId,
										carga_horaria_semanal:
											_cargaHorariaSemanal,
										...vinculoSemCamposLegados
									} = body.vinculo_profissional || {};
									return {
										estabelecimento:
											getEstabelecimentoIdValue(
												contextoAplicado.estabelecimento,
											),
										cpf,
										profissional: {
											...profissionalSemTelefone,
											conselho:
												modalidade === 'ENFERMAGEM'
													? 'COREN'
													: profissionalSemTelefone.conselho,
										},
										vinculo_profissional: {
											...vinculoSemCamposLegados,
											tipo_vinculo_id: _vinculoTipoId,
										},
									};
								})(),
							},
						);
					if (cadastroResponse?.error) {
						setSubmitErrorScope('novoProfissional');
						setSubmitError(
							getFriendlyProfissionalApiErrorMessage({
								result: cadastroResponse,
								isEditing: false,
							}),
						);
						return;
					}
					vinculoProfissionalId = Number(
						cadastroResponse?.vinculo_profissional_id || 0,
					);
					if (!vinculoProfissionalId) {
						setSubmitErrorScope('novoProfissional');
						setSubmitError(
							'O cadastro foi concluído, mas não retornou a identificação do vínculo necessária para criar a escala.',
						);
						return;
					}
					cadastroVinculoPendenteRef.current = {
						identidade: cadastroIdentidade,
						vinculoProfissionalId,
					};
				}

				const padroesOrigem = normalizePadroesAlocacao(
					body?.regime_escala?.padroes_alocacao,
				).map((padrao = {}) =>
					Object.fromEntries(
						Object.entries({
							tipo_recorrencia: padrao.tipo_recorrencia,
							preset_ciclo: getPayloadPresetCiclo(
								padrao.preset_ciclo,
							),
							dias_semana: padrao.dias_semana,
							datas_especificas: padrao.datas_especificas,
							hora_inicio: formatHourInputToTimeValue(
								padrao.hora_inicio,
							),
							hora_fim: formatHourInputToTimeValue(
								padrao.hora_fim,
							),
							trabalho_horas: padrao.trabalho_horas,
							descanso_horas: padrao.descanso_horas,
							data_ancora: formatDateInputToISO(
								padrao.data_ancora,
							),
							periodo_semanas: padrao.periodo_semanas,
							semanas_rotacao: padrao.semanas_rotacao,
						}).filter(
							([, value]) => value !== undefined && value !== '',
						),
					),
				);
				const previewBody = {
					contexto: {
						estabelecimento: getEstabelecimentoIdValue(
							contextoAplicado.estabelecimento,
						),
						setor: setorOperacionalProfissional,
						mes_referencia:
							mesReferenciaVigenciaNovoProfissional ||
							contextoAplicado.mes_referencia,
					},
					configuracao: {
						vinculo_profissional_id: vinculoProfissionalId,
						setor_origem_id: setorOperacionalProfissional,
						[dimensionField]: Number(dimensionValue) || undefined,
						regime: body?.regime_escala?.regime,
						vigencia_inicio:
							vigenciaInicioIsoNovoProfissional ||
							body?.regime_escala?.vigencia_inicio,
						vigencia_fim: null,
						padroes_origem: padroesOrigem,
					},
					// O formulário de novo profissional define somente a origem. Se a
					// tela passar a permitir vários setores no mesmo cadastro, todos os
					// destinos devem ser incluídos aqui na mesma chamada, nunca criados
					// como vínculos/regimes independentes.
					particoes: [],
				};
				const preview =
					await requestPreverParticionamentoAlocacaoOrigem({
						token,
						body: previewBody,
						scope,
					});
				const entradaAplicar = preview?.acoes?.aplicar?.entrada;
				const previewEtag = getPreenchimentoPreviewEtag(preview);
				const acaoAplicar = preview?.acoes?.aplicar || null;
				// O backend pode devolver este bloqueio em HTTP 200. Não trate a
				// presença/ausência de `error` como a decisão de domínio.
				if (isAlocacaoOrigemAtivaExigeEdicao(preview)) {
					const candidatas =
						getCandidatasAlocacaoOrigemAtiva(preview);
					const alocacaoOrigemId =
						getAlocacaoOrigemIdFromPayload(preview);
					const candidatasResolvidas =
						candidatas.length > 0
							? candidatas
							: alocacaoOrigemId
								? [{ alocacao_origem_id: alocacaoOrigemId }]
								: [];
					const novoItem = {
						id: 'novo',
						setorId: String(setorOperacionalProfissional),
						dimensionValue,
						sourceLinha: {
							...body,
							setor: { id: setorOperacionalProfissional },
							vinculo_profissional: {
								...body.vinculo_profissional,
								id: vinculoProfissionalId,
							},
							profissional: body.profissional,
							regime_escala: body.regime_escala,
						},
						regimeValue: body.regime_escala?.regime,
						recorrenciaValue: novoProfissionalForm.nome_recorrencia,
						tipoRecorrencia: body.regime_escala?.tipo_recorrencia,
						presetCiclo: body.regime_escala?.preset_ciclo,
						dataInicialValue: novoProfissionalForm.vigencia_inicio,
						diasSemanaValores: effectiveDiasSemanaNovoProfissional,
						horariosByDia:
							novoProfissionalForm.dias_semana_horarios,
						horaInicio: novoProfissionalForm.hora_inicio,
						horaFim: novoProfissionalForm.hora_fim,
						periodoRotacao: novoProfissionalForm.periodo_semanas,
						semanasRotacao: novoProfissionalForm.semanas_rotacao,
						datasEspecificasEditaveis: getEditableDatasExtras(
							novoProfissionalForm.datas_extras,
						).map((item = {}) => ({
							id: item.id,
							data: formatISOToDisplay(item.selected_date),
							horaInicio: item.hora_inicio,
							horaFim: item.hora_fim,
						})),
					};
					const assinaturaIntencao = [
						scope,
						vinculoProfissionalId,
						setorOperacionalProfissional,
						vigenciaInicioIsoNovoProfissional,
						JSON.stringify(
							body.regime_escala?.padroes_alocacao || [],
						),
					].join('|');
					const candidataSelecionada =
						candidatasResolvidas.find(
							(candidata = {}) =>
								String(
									getAlocacaoOrigemIdFromPayload(candidata) ||
										'',
								) === String(alocacaoOrigemId || ''),
						) || candidatasResolvidas[0];
					const alocacaoOrigemSelecionada =
						getAlocacaoOrigemIdFromPayload(candidataSelecionada) ||
						alocacaoOrigemId;
					if (!alocacaoOrigemSelecionada) {
						throw new Error(
							'O servidor informou uma jornada ativa, mas não retornou seu identificador para preparar a inclusão do setor.',
						);
					}
					// A resposta 409/ALOCACAO_ORIGEM_ATIVA_EXIGE_EDICAO continua na
					// jornada indicada pelo backend. Não criamos uma segunda origem:
					// dias já existentes geram partição; dias novos seguem para a
					// preparação canônica como padrões isolados da mesma origem.
					await prepararParticionamentoNovoSetor(
						novoItem,
						novoItem.sourceLinha,
						{
							alocacaoOrigemId: alocacaoOrigemSelecionada,
							acaoPreferida:
								getAcaoParaAdicionarSetorNaAlocacaoOrigem(
									candidataSelecionada,
								) || null,
							originatedFromProfessionalModal: true,
							idempotencyKey:
								getChaveIdempotenciaAlocacao(
									assinaturaIntencao,
								),
						},
					);
					return;
				}
				if (preview?.error) {
					const motivo =
						acaoAplicar?.motivo ||
						getPreviewBlockMessage(
							preview,
							'Não foi possível validar a alocação inicial.',
						);
					const mensagem =
						'O vínculo foi cadastrado, mas a escala ainda não foi criada. ' +
						`${motivo} Revise os dados e envie novamente para continuar a alocação.`;
					setSubmitErrorScope('novoProfissional');
					setSubmitError(mensagem);
					setNovoProfissionalApplyError(mensagem);
					return;
				}
				// Ausência de ação confirmável em uma prévia bem-sucedida é um estado
				// operacional da API, não uma falha de requisição. Não exibimos um
				// alerta de erro sem que uma chamada tenha falhado.
				if (
					!isPreviewAcaoConfirmavel(preview) ||
					acaoAplicar?.disponivel === false ||
					!entradaAplicar ||
					!previewEtag
				) {
					const mensagem =
						'O vínculo foi cadastrado, mas a escala ainda não foi criada. ' +
						getPreviewBlockMessage(
							preview,
							acaoAplicar?.motivo ||
								'Esta alocação precisa de ajustes antes da confirmação.',
						);
					setSubmitErrorScope('novoProfissional');
					setSubmitError(mensagem);
					setNovoProfissionalApplyError(mensagem);
					return;
				}

				// A primeira origem também segue preview -> confirmação -> aplicar.
				// Manter a entrada assinada evita que o frontend reconstrua ou altere
				// UUID, versão e partições devolvidos pelo backend entre as etapas.
				setParticionamentoPendente({
					tipo: 'CRIAR_ORIGEM_INICIAL',
					entrada: JSON.parse(JSON.stringify(entradaAplicar)),
					action: acaoAplicar,
					previewEtag,
					idempotencyKey: getChaveIdempotenciaAlocacao(
						[
							'origem-inicial',
							scope,
							vinculoProfissionalId,
							setorOperacionalProfissional,
							vigenciaInicioIsoNovoProfissional,
							JSON.stringify(
								body.regime_escala?.padroes_alocacao || [],
							),
						].join('|'),
					),
					setorDestino: setorOperacionalProfissional,
					origem: { setor: setorOperacionalProfissional },
					particoes: [],
					originatedFromProfessionalModal: true,
					linhaOrigemProfissional: {
						setor: { id: setorOperacionalProfissional },
						vinculo_profissional: { id: vinculoProfissionalId },
						profissional: body.profissional,
					},
					issues: Array.isArray(preview?.issues)
						? preview.issues
						: [],
				});
				return;
			}

			if (shouldEnviarPatchEdicao) {
				const updateBody = {
					vinculo: vinculoTipoId,
					[dimensionPatchField]: Number(dimensionValue) || undefined,
					numero_registro:
						nextCadastroSnapshot.numeroRegistro || undefined,
					uf_conselho: nextCadastroSnapshot.ufConselho || undefined,
					cargo:
						String(novoProfissionalForm.cargo || '').trim() ||
						undefined,
					telefone: nextCadastroSnapshot.telefone || undefined,
					carga_horaria_semanal: cargaHorariaSemanal || undefined,
					ativo: true,
					...(!isEdicaoProfissionalConsolidadaTodos &&
						!isJornadaEstruturalEmEdicao && {
							regime_escala: {
								regime_vinculo_id:
									Number(
										body?.regime_vinculo_id ||
											linhaEdicao?.regime_vinculo_id ||
											0,
									) || undefined,
								setor:
									setorOperacionalProfissional || undefined,
								mes_referencia:
									formatDateInputToISO(
										body?.contexto?.mes_referencia ||
											contextoAplicado?.mes_referencia,
									) || undefined,
								regime:
									body?.regime_escala?.regime || undefined,
								hora_inicio:
									body?.regime_escala?.hora_inicio ||
									undefined,
								hora_fim:
									body?.regime_escala?.hora_fim || undefined,
								turno: resolvedTurnoCodigo,
								tipo_recorrencia:
									body?.regime_escala?.tipo_recorrencia ||
									undefined,
								preset_ciclo:
									body?.regime_escala?.tipo_recorrencia ===
									'SEMANAL'
										? 'SEMANAL'
										: body?.regime_escala?.preset_ciclo ||
											undefined,
								dias_semana:
									body?.regime_escala?.tipo_recorrencia ===
									'SEMANAL'
										? normalizeDiasSemanaValues(
												body?.regime_escala
													?.dias_semana,
											)
										: undefined,
								ocorrencias:
									body?.regime_escala?.tipo_recorrencia ===
									'SEMANAL'
										? body?.regime_escala?.ocorrencias ||
											undefined
										: undefined,
								padroes_alocacao:
									body?.regime_escala?.padroes_alocacao ||
									undefined,
								vigencia_inicio:
									body?.regime_escala?.vigencia_inicio ||
									undefined,
								trabalho_horas:
									body?.regime_escala?.trabalho_horas ||
									undefined,
								descanso_horas:
									body?.regime_escala?.descanso_horas ||
									undefined,
								data_ancora:
									body?.regime_escala?.tipo_recorrencia ===
									'CICLO_CONTINUO'
										? body?.regime_escala?.data_ancora ||
											undefined
										: undefined,
							},
						}),
					chave_idempotencia: patchIdempotencyKey,
				};

				const updateResponse =
					await requestAtualizarVinculoProfissionalEstabelecimento({
						token,
						id: vinculoIdEdicao,
						body: updateBody,
						scope,
					});

				if (updateResponse?.error) {
					if (
						showProfissionalHorarioSobrepostoPopup(updateResponse)
					) {
						return;
					}

					setSubmitErrorScope('novoProfissional');
					setSubmitError(
						getFriendlyProfissionalApiErrorMessage({
							result: updateResponse,
							isEditing: isEditingProfissional,
						}),
					);
					return;
				}
			}

			if (shouldPatchViaOrigem) {
				await prepararAtualizacaoSetoresParticionados([], {
					itemOrigem: {
						id: `edicao-profissional-${
							linhaEdicao?.linha_id ||
							linhaEdicao?.regime_vinculo_id ||
							'origem'
						}`,
						setorId: setorEdicao,
						sourceLinha: linhaEdicao,
					},
					regimePayloadOverride: regimePayload,
					dataInicialIsoOverride: vigenciaInicioIsoNovoProfissional,
					originatedFromProfessionalModal: true,
				});
				return;
			}

			if (shouldSalvarLinhaProfissional) {
				response = await requestSalvarProfissionalAmbienteEscala({
					token,
					body,
					scope,
				});
			}

			if (isEditingProfissional && !shouldPatchEdicaoProfissional) {
				setFeedbackMessage('Nenhuma alteração foi identificada.');
				setIsNovoProfissionalModalOpen(false);
				setIsEditingProfissional(false);
				setEditingProfissionalLinha(null);
				return;
			}

			if (response?.error) {
				if (showProfissionalHorarioSobrepostoPopup(response)) {
					return;
				}

				setSubmitErrorScope('novoProfissional');
				setSubmitError(
					getFriendlyProfissionalApiErrorMessage({
						result: response,
						isEditing: isEditingProfissional,
					}),
				);
				return;
			}

			let regimeVinculoId =
				Number(
					response?.linha?.regime_vinculo_id ||
						response?.regime_vinculo_id ||
						response?.ambiente?.linhas?.[0]?.regime_vinculo_id ||
						editingProfissionalLinha?.regime_vinculo_id ||
						0,
				) || null;
			const primeiroDiaTrabalhoMes =
				vigenciaInicioIsoNovoProfissional ||
				formatDateInputToISO(novoProfissionalForm.data_ancora) ||
				mesReferenciaVigenciaNovoProfissional ||
				'';
			const applyOperationToken =
				response?.operacao?.id ||
				response?.linha?.linha_id ||
				`${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
			const shouldAplicarPreenchimentoProfissional =
				Boolean(regimeVinculoId) &&
				(!isEditingProfissional || shouldPatchEscalaProfissional);

			if (shouldAplicarPreenchimentoProfissional) {
				const applyBody = {
					contexto: {
						estabelecimento: getEstabelecimentoIdValue(
							contextoAplicado.estabelecimento,
						),
						setor: setorOperacionalProfissional,
						mes_referencia:
							mesReferenciaVigenciaNovoProfissional ||
							contextoAplicado.mes_referencia,
					},
					chave_idempotencia: buildApplyPreenchimentoIdempotencyKey({
						contexto: contextoAplicado,
						prefix: isEditingProfissional
							? 'editar-profissional'
							: 'novo-profissional',
						regimeVinculoId,
						primeiroDiaTrabalhoMes,
						operacaoId: response?.operacao?.id,
						fallbackToken: applyOperationToken,
					}),
					linhas: [
						{
							regime_vinculo_id: regimeVinculoId,
							primeiro_dia_trabalho_mes:
								primeiroDiaTrabalhoMes || undefined,
						},
					],
				};

				const applyResponse =
					await aplicarPreenchimentoComPreview(applyBody);

				if (applyResponse?.error) {
					if (showProfissionalHorarioSobrepostoPopup(applyResponse)) {
						return;
					}

					const applyErrorMessage =
						getFriendlyProfissionalApiErrorMessage({
							result: applyResponse,
							isEditing: isEditingProfissional,
						});
					setSubmitErrorScope('novoProfissional');
					setSubmitError(applyErrorMessage);
					setNovoProfissionalApplyError(applyErrorMessage);
					return;
				}
			}

			setFeedbackMessage(
				isEditingProfissional
					? shouldPatchEdicaoProfissional
						? shouldPatchEscalaProfissional
							? 'Alterações do profissional e da escala salvas com sucesso.'
							: 'Dados do profissional salvos com sucesso.'
						: 'Dados cadastrais do profissional salvos com sucesso.'
					: 'Profissional adicionado e alocado com sucesso.',
			);
			setIsNovoProfissionalModalOpen(false);
			setIsEditingProfissional(false);
			setEditingProfissionalLinha(null);
			await loadAmbiente({
				authToken: token,
				targetContexto:
					getContextoVisualizacaoRequest(contextoAplicado),
				showLoading: false,
				// A troca altera ambos os lados da competência. Não aceite uma
				// representação revalidada em cache logo após o 201.
				forceRefresh: true,
			});
		} catch (_error) {
			if (showProfissionalHorarioSobrepostoPopup(_error)) {
				return;
			}
			if (showConflitoDeProfissionalExistente()) return;

			setSubmitErrorScope('novoProfissional');
			setSubmitError(
				getProfissionalApiFallbackMessage({
					isEditing: isEditingProfissional,
				}),
			);
		} finally {
			setIsSubmittingAdicionar(false);
		}
	};

	const handleSubmitVacancia = async () => {
		setVacanciaApplyError('');
		if (!token || !contextoAplicado) {
			setSubmitErrorScope('vacancia');
			setSubmitError(
				'Selecione o contexto da escala antes de adicionar vacância.',
			);
			return;
		}

		if (
			isEditingVacancia &&
			initialVacanciaEditSnapshotRef.current &&
			!hasSnapshotChanges(
				initialVacanciaEditSnapshotRef.current,
				buildVacanciaEditSnapshot(vacanciaForm),
			)
		) {
			setIsVacanciaModalOpen(false);
			setIsEditingVacancia(false);
			setEditingVacanciaLinha(null);
			return;
		}

		const nextErrors = {
			data_inicio_vacancia: validateVacanciaField(
				'data_inicio_vacancia',
				vacanciaForm.data_inicio_vacancia,
			),
			especialidade_id: validateVacanciaField(
				'especialidade_id',
				vacanciaForm.especialidade_id,
			),
			vinculo_tipo_id: validateVacanciaField(
				'vinculo_tipo_id',
				vacanciaForm.vinculo_tipo_id,
			),
			regime: validateVacanciaField('regime', vacanciaForm.regime),
			nome_recorrencia: validateVacanciaField(
				'nome_recorrencia',
				vacanciaForm.nome_recorrencia,
			),
			dias_semana:
				Boolean(String(vacanciaForm.regime || '').trim()) &&
				!isSpecificDatesRecorrencia(vacanciaForm.nome_recorrencia) &&
				isWeeklyRecorrencia(vacanciaForm.tipo_recorrencia)
					? (() => {
							const diasEfetivos = vacanciaHasRecorrencia
								? vacanciaUsaSemanalMedica
									? normalizeDiasSemanaValues(
											vacanciaForm.dias_semana,
										)
									: getEffectiveDiasSemanaSelection({
											dias_semana:
												vacanciaForm.dias_semana,
											regime: vacanciaForm.regime,
											startDate:
												vacanciaForm.data_inicio_vacancia,
										})
								: normalizeDiasSemanaValues(
										vacanciaForm.dias_semana,
									);
							const erroDias = validateVacanciaField(
								'dias_semana',
								diasEfetivos,
							);
							if (erroDias) return erroDias;
							return getIncompleteWeeklyScheduleDays({
								dias_semana: vacanciaForm.dias_semana,
								dias_semana_horarios:
									vacanciaForm.dias_semana_horarios,
								regime:
									vacanciaHasRecorrencia &&
									!vacanciaUsaSemanalMedica
										? vacanciaForm.regime
										: '',
								startDate: vacanciaForm.data_inicio_vacancia,
							}).length > 0
								? 'Preencha hora inicial e final em todos os dias selecionados.'
								: '';
						})()
					: '',
			datas_extras: isSpecificDatesRecorrencia(
				vacanciaForm.nome_recorrencia,
			)
				? (() => {
						const datasValidas = (vacanciaForm.datas_extras || [])
							.map((item) => ({
								data: formatDateInputToISO(item?.selected_date),
								hora_inicio: formatHourInputToTimeValue(
									item?.hora_inicio,
								),
								hora_fim: formatHourInputToTimeValue(
									item?.hora_fim,
								),
							}))
							.filter(
								(item) =>
									item.data &&
									item.hora_inicio &&
									item.hora_fim,
							);
						return validateVacanciaField(
							'datas_extras',
							datasValidas,
						);
					})()
				: '',
		};
		setVacanciaErrors(nextErrors);
		if (Object.values(nextErrors).some(Boolean)) return;

		const dimensionField =
			modalidade === 'ENFERMAGEM' ? 'categoria_id' : 'especialidade_id';
		const dimensionValue = vacanciaForm.especialidade_id;
		if (!dimensionValue) {
			return;
		}

		const sourcePayload = {
			...vacanciaForm,
			dias_semana: isSpecificDatesRecorrencia(
				vacanciaForm.nome_recorrencia,
			)
				? []
				: vacanciaUsaRecorrenciaCiclo
					? []
					: vacanciaHasRecorrencia
						? vacanciaUsaSemanalMedica
							? normalizeDiasSemanaValues(
									vacanciaForm.dias_semana,
								)
							: getEffectiveDiasSemanaSelection({
									dias_semana: vacanciaForm.dias_semana,
									regime: vacanciaForm.regime,
									startDate:
										vacanciaForm.data_inicio_vacancia,
								})
						: normalizeDiasSemanaValues(vacanciaForm.dias_semana),
			dias_semana_horarios: vacanciaForm.dias_semana_horarios,
			datas_extras: vacanciaForm.datas_extras,
			datas_especificas: (vacanciaForm.datas_extras || [])
				.map((item) =>
					formatDateInputToISO(
						item?.selected_date || item?.data || '',
					),
				)
				.filter(Boolean),
		};

		if (
			normalizeCode(vacanciaForm.tipo_recorrencia) === 'CICLO_CONTINUO' &&
			Array.isArray(sourcePayload.dias_semana) &&
			sourcePayload.dias_semana.length > 0
		) {
			const firstSelectedWeekday = normalizeDiasSemanaValues(
				sourcePayload.dias_semana,
			)[0];
			const firstDayHorario = getDiaSemanaHorarioConfig({
				diaSemana: firstSelectedWeekday,
				diasSemanaHorarios: sourcePayload.dias_semana_horarios,
				fallbackHoraInicio: sourcePayload.hora_inicio,
				fallbackHoraFim: sourcePayload.hora_fim,
			});

			if (firstDayHorario?.hora_inicio) {
				sourcePayload.hora_inicio = firstDayHorario.hora_inicio;
			}
			if (firstDayHorario?.hora_fim) {
				sourcePayload.hora_fim = firstDayHorario.hora_fim;
			}
		}

		const rawRegimePayload = buildRegimePayload({
			source: sourcePayload,
			dimensionField,
			dimensionValue,
			fallbackVigenciaInicio:
				vacanciaForm?.data_inicio_vacancia ||
				contextoAplicado?.mes_referencia,
			fallbackDataAncora: vacanciaForm?.data_inicio_vacancia,
			forceDefaultRegime: false,
			incluirDiaInicialSemanal: !vacanciaUsaSemanalMedica,
		});
		const rawRegimePayloadWithExtraDates = {
			...rawRegimePayload,
		};
		if (Array.isArray(rawRegimePayload?.padroes_alocacao)) {
			rawRegimePayloadWithExtraDates.padroes_alocacao = [
				...rawRegimePayload.padroes_alocacao,
			];
		}
		if (!isSpecificDatesRecorrencia(vacanciaForm.nome_recorrencia)) {
			const extraDatePadroes = (vacanciaForm.datas_extras || [])
				.filter((item = {}) => item?.selected_date)
				.map((item = {}) => {
					const data = formatDateInputToISO(item.selected_date);
					const hora_inicio = formatHourInputToTimeValue(
						item.hora_inicio,
					);
					const hora_fim = formatHourInputToTimeValue(item.hora_fim);
					if (!data || !hora_inicio || !hora_fim) return null;
					return {
						tipo_recorrencia: 'DATAS_ESPECIFICAS',
						preset_ciclo: 'PERSONALIZADO',
						datas_especificas: [data],
						hora_inicio,
						hora_fim,
					};
				})
				.filter(Boolean);
			if (extraDatePadroes.length > 0) {
				rawRegimePayloadWithExtraDates.padroes_alocacao = [
					...(Array.isArray(
						rawRegimePayloadWithExtraDates.padroes_alocacao,
					)
						? rawRegimePayloadWithExtraDates.padroes_alocacao
						: []),
					...extraDatePadroes,
				];
			}
		}
		const effectiveRegimePayload = rawRegimePayloadWithExtraDates;
		const regimeEscalaVacancia = {
			...effectiveRegimePayload,
			[dimensionField]: Number(dimensionValue) || undefined,
		};
		const setorOperacionalVacancia =
			Number(
				vacanciaForm.setor_id ||
					getLinhaSetorId(editingVacanciaLinha) ||
					contextoAplicado.setor ||
					0,
			) || 0;

		if (!setorOperacionalVacancia) {
			setSubmitErrorScope('vacancia');
			setSubmitError('Selecione o setor para salvar a vacância.');
			return;
		}

		const body = {
			contexto: {
				estabelecimento: getEstabelecimentoIdValue(
					contextoAplicado.estabelecimento,
				),
				setor: setorOperacionalVacancia,
				mes_referencia: contextoAplicado.mes_referencia,
			},
			vacancia: {
				regime_vinculo_id: isEditingVacancia
					? Number(editingVacanciaLinha?.regime_vinculo_id || 0) ||
						undefined
					: undefined,
				[dimensionField]: Number(dimensionValue) || undefined,
				data_inicio_vacancia:
					formatDateInputToISO(vacanciaForm.data_inicio_vacancia) ||
					contextoAplicado.mes_referencia,
				vinculo_tipo_id:
					Number(vacanciaForm.vinculo_tipo_id || 0) || undefined,
				observacao: vacanciaForm.observacao || undefined,
			},
			regime_escala: regimeEscalaVacancia,
		};

		const requestBody = body;

		setIsSubmittingVacancia(true);
		setSubmitError('');
		setFeedbackMessage('');

		try {
			const response = await requestAdicionarVacanciaAmbienteEscala({
				token,
				body: requestBody,
				scope,
			});

			if (response?.error) {
				setSubmitErrorScope('vacancia');
				setSubmitError(
					getErrorMessage(
						response,
						isEditingVacancia
							? 'Não foi possível salvar alterações da vacância.'
							: 'Não foi possível adicionar vacância.',
					),
				);
				return;
			}

			const regimeVinculoId =
				Number(
					response?.linha?.regime_vinculo_id ||
						response?.regime_vinculo_id ||
						response?.ambiente?.linhas?.[0]?.regime_vinculo_id ||
						editingVacanciaLinha?.regime_vinculo_id ||
						editingVacanciaLinha?.regime_escala?.id ||
						0,
				) || null;
			const primeiroDiaTrabalhoMes =
				formatDateInputToISO(vacanciaForm.data_inicio_vacancia) ||
				contextoAplicado?.mes_referencia ||
				'';
			const applyOperationToken =
				response?.operacao?.id ||
				response?.linha?.linha_id ||
				`${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
			const shouldAplicarPreenchimentoVacancia = Boolean(regimeVinculoId);

			if (shouldAplicarPreenchimentoVacancia) {
				const applyBody = {
					contexto: {
						estabelecimento: getEstabelecimentoIdValue(
							contextoAplicado.estabelecimento,
						),
						setor: setorOperacionalVacancia,
						mes_referencia: contextoAplicado.mes_referencia,
					},
					chave_idempotencia: buildApplyPreenchimentoIdempotencyKey({
						contexto: contextoAplicado,
						prefix: isEditingVacancia
							? 'editar-vacancia'
							: 'nova-vacancia',
						regimeVinculoId,
						primeiroDiaTrabalhoMes,
						operacaoId: response?.operacao?.id,
						fallbackToken: applyOperationToken,
					}),
					linhas: [
						{
							regime_vinculo_id: regimeVinculoId,
							primeiro_dia_trabalho_mes:
								primeiroDiaTrabalhoMes || undefined,
						},
					],
				};

				const applyResponse =
					await aplicarPreenchimentoComPreview(applyBody);

				if (applyResponse?.error) {
					const applyErrorMessage = getErrorMessage(
						applyResponse,
						isEditingVacancia
							? 'Alterações da vacância salvas, mas não foi possível reaplicar o preenchimento da escala automaticamente.'
							: 'Vacância criada, mas não foi possível alocar os cards automaticamente na escala.',
					);
					setSubmitErrorScope('vacancia');
					setSubmitError(applyErrorMessage);
					setVacanciaApplyError(applyErrorMessage);
					return;
				}

				applyAmbienteResponse(applyResponse);
			}

			setFeedbackMessage(
				isEditingVacancia
					? 'Alterações da vacância salvas e escala atualizada com sucesso.'
					: 'Vacância adicionada e alocada com sucesso.',
			);
			setIsVacanciaModalOpen(false);
			setIsEditingVacancia(false);
			setEditingVacanciaLinha(null);
			await loadAmbiente({
				authToken: token,
				targetContexto:
					getContextoVisualizacaoRequest(contextoAplicado),
				showLoading: false,
			});
		} finally {
			setIsSubmittingVacancia(false);
		}
	};

	const buildActionPayload = ({
		aplicar = false,
		panelFormOverride = null,
		openMiniTabsOverride = null,
	} = {}) => {
		const currentPanelForm = panelFormOverride || panelForm;
		const currentOpenMiniTabs = openMiniTabsOverride || openMiniTabs;
		const acao =
			currentOpenMiniTabs.excluir &&
			currentPanelForm.delete_mode === 'competencia'
				? 'REMOVER_COMPETENCIA'
				: currentPanelForm.acao;
		if (!acao) return null;
		const normalizedAcao = normalizeCode(acao);
		const isLegacyFutureRemoval = normalizedAcao === 'REMOVER_FUTURO';
		const isRemocaoFuturaEstabelecimento =
			isLegacyFutureRemoval && isSetorTodosAplicado;
		const acaoEfetiva = isLegacyFutureRemoval
			? isRemocaoFuturaEstabelecimento
				? 'ENCERRAR_ALOCACOES_MODALIDADE'
				: 'ENCERRAR_ALOCACAO_SETOR'
			: acao;

		const datasExclusao =
			acao === 'REMOVER_DIA' && selectedDiaIso
				? [selectedDiaIso]
				: undefined;
		const mesReferenciaInicioEscala =
			getMesReferenciaInicioEscalaFromLinha(linhaSelecionada) ||
			contextoAplicado.mes_referencia;
		const inicioEscalaIso = getInicioEscalaIsoFromLinha(linhaSelecionada);
		const dataInicialProfissionalIso =
			getDataInicialProfissionalEscalaIsoFromLinha(linhaSelecionada);
		const mesReferenciaAcao =
			acao === 'REMOVER_COMPETENCIA' || acao === 'REMOVER_FUTURO'
				? mesReferenciaInicioEscala
				: contextoAplicado.mes_referencia;
		const contextoOperacional = getContextoOperacional(
			contextoAplicado,
			linhaSelecionada,
		);

		const base = {
			contexto: {
				estabelecimento: getEstabelecimentoIdValue(
					contextoAplicado.estabelecimento,
				),
				setor: contextoOperacional.setor,
				mes_referencia: mesReferenciaAcao,
			},
			acao: acaoEfetiva,
			regime_vinculo_id:
				Number(
					currentPanelForm.regime_vinculo_id ||
						linhaSelecionada?.regime_vinculo_id ||
						0,
				) || undefined,
		};

		if (acao === 'ATRIBUIR' || acao === 'REMOVER_DIA') {
			base.datas =
				acao === 'REMOVER_DIA'
					? datasExclusao
					: selectedDiaIso
						? [selectedDiaIso]
						: undefined;
			if (currentPanelForm.observacao) {
				base.observacao = currentPanelForm.observacao;
			}
		}

		if (acao === 'REMOVER_COMPETENCIA') {
			if (inicioEscalaIso) {
				base.primeiro_dia_trabalho_mes = inicioEscalaIso;
			}
			if (currentPanelForm.observacao) {
				base.observacao = currentPanelForm.observacao;
			}
		}

		if (acao === 'REMOVER_FUTURO' && inicioEscalaIso) {
			base.primeiro_dia_trabalho_mes = inicioEscalaIso;
		}

		if (isLegacyFutureRemoval) {
			base.data_corte = dataInicialProfissionalIso;
			if (currentPanelForm.observacao) {
				base.observacao = currentPanelForm.observacao;
			}
		}

		if (acao === 'REGISTRAR_AUSENCIA') {
			base.tipo_item = currentPanelForm.tipo_item || 'FERIAS';
			base.periodo = {
				data_inicio:
					formatDateInputToISO(currentPanelForm.periodo_inicio) ||
					selectedDiaIso,
				data_fim:
					formatDateInputToISO(currentPanelForm.periodo_fim) ||
					selectedDiaIso,
			};
			if (currentPanelForm.observacao) {
				base.observacao = currentPanelForm.observacao;
			}
		}

		if (acao === 'REGISTRAR_TROCA') {
			// item_origem_id: card do profissional cujo nome foi referenciado (célula clicada)
			const itemOrigemId =
				Number(
					currentPanelForm.item_origem_id ||
						getCardItemId(getCellPrimaryCard(selectedCell)) ||
						0,
				) || undefined;

			// item_destino_id: prefer seleção explícita do turno; se não houver,
			// verifica se os filtros (profissional + data + turno) já isolaram exatamente
			// um item - nesse caso usa esse item_id automaticamente como destino.
			let itemDestinoId =
				Number(
					currentPanelForm.troca_turno_id ||
						currentPanelForm.item_destino_id ||
						0,
				) || undefined;

			if (!itemDestinoId) {
				const selectableOptions = trocaTurnoOptions.filter(
					(item) => item?.disabled !== true,
				);
				if (selectableOptions.length === 1) {
					itemDestinoId =
						Number(selectableOptions[0]?.value || 0) || undefined;
				}
			}

			const trocaPayload = {
				contexto: base.contexto,
				acao,
				item_origem_id: itemOrigemId,
				item_destino_id: itemDestinoId,
			};

			if (aplicar) {
				trocaPayload.chave_idempotencia =
					buildActionIdempotencyKey(trocaPayload);
			}

			return trocaPayload;
		}

		if (acao === 'ALTERAR_TURNO') {
			base.hora_inicio = currentPanelForm.hora_inicio || undefined;
			base.hora_fim = currentPanelForm.hora_fim || undefined;
			if (currentPanelForm.observacao) {
				base.observacao = currentPanelForm.observacao;
			}
		}

		if (aplicar) {
			base.chave_idempotencia = buildActionIdempotencyKey(base);
		}

		return base;
	};

	const handleAplicarAcao = async () => {
		if (!token || !contextoAplicado) return;
		if (openMiniTabs.troca) {
			if (isTrocaExistenteSelecionada) {
				return;
			}
			if (trocaBloqueadaPorConsulta) {
				setPanelErrors((prev) => ({
					...prev,
					form: trocaConsultaMensagemErro,
				}));
				return;
			}
			if (!trocaPacotePayload) {
				setPanelErrors((prev) => ({
					...prev,
					form: 'Selecione o profissional e o plantão para a troca.',
				}));
				return;
			}
			if (
				trocaRecorteCapacidadeHabilitada &&
				trocaRecorteSugerido &&
				panelForm.troca_perfil === 'RECORTE' &&
				(!panelForm.troca_recorte_item_id ||
					!formatHourInputToTimeValue(
						panelForm.troca_recorte_inicio,
					) ||
					!formatHourInputToTimeValue(panelForm.troca_recorte_fim))
			) {
				setPanelErrors((prev) => ({
					...prev,
					form: 'Informe início e fim do recorte para concluir a troca.',
				}));
				return;
			}
			if (!trocaAvaliacaoValida) {
				const mensagem =
					trocaAvaliacao?.erros_pacote?.[0]?.mensagem ||
					trocaAvaliacao?.itens?.flatMap(
						(item) => item?.erros || [],
					)?.[0]?.mensagem ||
					(trocaAvaliacao?.saldo?.equivalente === false
						? 'A troca ainda não possui carga horária equivalente. Adicione um complemento.'
						: 'Aguarde a validação ou ajuste os itens da troca.');
				setPanelErrors((prev) => ({ ...prev, form: mensagem }));
				return;
			}

			setIsSubmittingPanel(true);
			setPanelErrors({});
			const response = await requestEfetivarTrocaEquivalente({
				token,
				body: trocaEfetivacaoPayload || trocaPacotePayload,
				action: trocaAvaliacao?.acoes?.efetivar,
				idempotencyKey: trocaEfetivacaoIdempotencyKey,
				scope,
			});
			setIsSubmittingPanel(false);
			if (response?.error) {
				const avaliacaoAtual = response?.error?.data;
				if (avaliacaoAtual?.saldo || avaliacaoAtual?.erros_pacote) {
					setTrocaAvaliacao(avaliacaoAtual);
				}
				setPanelErrors((prev) => ({
					...prev,
					form:
						avaliacaoAtual?.erros_pacote?.[0]?.mensagem ||
						avaliacaoAtual?.itens?.flatMap(
							(item) => item?.erros || [],
						)?.[0]?.mensagem ||
						getErrorMessage(
							response,
							'Não foi possível efetivar a troca equivalente.',
						),
				}));
				return;
			}

			setOpenMiniTabs(getDefaultMiniTabs());
			setFeedbackMessage('Troca registrada com sucesso.');
			await loadAmbiente({
				authToken: token,
				targetContexto:
					getContextoVisualizacaoRequest(contextoAplicado),
				showLoading: false,
			});
			return;
		}
		if (
			openMiniTabs.excluir &&
			panelForm.delete_mode !== 'competencia' &&
			!selectedDiaIso
		) {
			setPanelErrors((prev) => ({
				...prev,
				form: 'Selecione um dia na grade antes de excluir.',
			}));
			return;
		}
		const previewBody = buildActionPayload({ aplicar: false });
		if (!previewBody) {
			setPanelErrors((prev) => ({
				...prev,
				form: 'Selecione uma ação ao abrir uma das opções abaixo.',
			}));
			return;
		}
		if (
			normalizeCode(previewBody?.acao) === 'REMOVER_DIA' &&
			getAlocacaoOrigemId(linhaSelecionada)
		) {
			setPanelErrors((prev) => ({
				...prev,
				form: 'Este dia pertence a uma jornada repartida e não pode ser removido isoladamente. Gerencie a alteração estrutural pelo gerenciamento de turnos.',
			}));
			return;
		}
		if (
			[
				'ENCERRAR_ALOCACAO_SETOR',
				'ENCERRAR_ALOCACOES_MODALIDADE',
			].includes(normalizeCode(previewBody?.acao)) &&
			!formatDateInputToISO(previewBody?.data_corte)
		) {
			setPanelErrors((prev) => ({
				...prev,
				form: 'Não foi possível identificar a data inicial do profissional para encerrar a escala.',
			}));
			return;
		}

		if (
			!isActionAvailable({
				linha: linhaSelecionada,
				celula: selectedCell,
				acao: previewBody.acao,
			})
		) {
			setPanelErrors((prev) => ({
				...prev,
				form: getActionUnavailableMessage(previewBody.acao),
			}));
			return;
		}

		if (normalizeCode(previewBody.acao || '') === 'REGISTRAR_TROCA') {
			// Verifica destino: resolvido pelo turno selecionado OU auto-resolve
			// quando filtros (profissional + data + turno) narrowam a exatamente 1 item.
			// Dá mensagens progressivas de acordo com o que ainda falta filtrar.
			if (!previewBody.item_destino_id) {
				if (!panelForm.troca_profissional_id) {
					setPanelErrors((prev) => ({
						...prev,
						form: 'Selecione o profissional com quem deseja trocar.',
					}));
				} else if (!panelForm.troca_data) {
					setPanelErrors((prev) => ({
						...prev,
						form: 'Selecione a data do plantão para a troca.',
					}));
				} else {
					setPanelErrors((prev) => ({
						...prev,
						form: 'Selecione o turno de destino para concluir a troca.',
					}));
				}
				return;
			}
		}

		const isAusenciaAction =
			normalizeCode(previewBody?.acao || '') === 'REGISTRAR_AUSENCIA';
		const isEncerramentoAlocacaoSetor =
			normalizeCode(previewBody?.acao || '') ===
			'ENCERRAR_ALOCACAO_SETOR';
		let previewResponseForApply = null;
		let applyAction = null;

		if (
			(shouldPreviewBeforeApply ||
				isAusenciaAction ||
				isEncerramentoAlocacaoSetor) &&
			normalizeCode(previewBody?.acao || '') !== 'REGISTRAR_TROCA'
		) {
			const previewResponse = await requestPreverAcoesAmbienteEscala({
				token,
				body: previewBody,
				scope,
			});

			if (previewResponse?.error) {
				setSubmitErrorScope('panel');
				setSubmitError(
					getErrorMessage(
						previewResponse,
						'Não foi possível validar a ação antes de aplicar.',
					),
				);
				return;
			}
			if (!isPreviewAcaoConfirmavel(previewResponse)) {
				setSubmitErrorScope('panel');
				setSubmitError(
					isNenhumaJornadaElegivel(previewResponse)
						? 'Não existe jornada elegível para registrar esta ausência no período informado.'
						: getPreviewBlockMessage(
								previewResponse,
								'Esta ação não está disponível para confirmação.',
							),
				);
				return;
			}
			previewResponseForApply = previewResponse;
			applyAction = previewResponse?.acoes?.aplicar || null;
			if (applyAction?.disponivel === false) {
				setSubmitErrorScope('panel');
				setSubmitError(
					applyAction?.motivo ||
						'Esta ação não está disponível para confirmação.',
				);
				return;
			}
		}

		const fallbackApplyBody = buildActionPayload({ aplicar: true });
		const body = buildApplyActionBodyFromPreview({
			preview: previewResponseForApply,
			fallbackBody: fallbackApplyBody,
			idempotencyKey: fallbackApplyBody?.chave_idempotencia,
		});

		setIsSubmittingPanel(true);
		setSubmitError('');
		setSubmitErrorScope('');
		const response = await requestAplicarAcoesAmbienteEscala({
			token,
			body,
			action: applyAction,
			scope,
		});
		setIsSubmittingPanel(false);

		if (response?.error) {
			setSubmitErrorScope('panel');
			setSubmitError(
				isPreviewDesatualizado(response)
					? 'A escala foi alterada enquanto esta ação era preparada. Revise os dados e clique em Enviar novamente para gerar uma nova confirmação.'
					: getErrorMessage(
							response,
							'Não foi possível aplicar a ação.',
						),
			);
			return;
		}

		applyAmbienteResponse(response);
		if (
			normalizeCode(body?.acao || '') === 'REMOVER_COMPETENCIA' &&
			!responseHasCompetenciaCleanupState(
				response,
				body?.regime_vinculo_id,
			)
		) {
			await loadAmbiente({
				authToken: token,
				targetContexto:
					getContextoVisualizacaoRequest(contextoAplicado),
				showLoading: false,
			});
		}
		if (openMiniTabs.excluir) {
			setOpenMiniTabs(getDefaultMiniTabs());
		}
		setFeedbackMessage(
			normalizeCode(body?.acao || '') === 'REMOVER_COMPETENCIA'
				? getCompetenciaCleanupFeedbackMessage(response)
				: normalizeCode(body?.acao || '') === 'REGISTRAR_TROCA'
					? 'Troca registrada com sucesso.'
					: 'Ação aplicada com sucesso.',
		);
	};

	const handleApplyDeletePreview = async (pendencia = deletePreview) => {
		if (!pendencia?.preview || !pendencia?.command || !contextoAplicado)
			return;
		if (!isPreviewAcaoConfirmavel(pendencia.preview)) return;

		const bodyAplicacao = buildApplyActionBodyFromPreview({
			preview: pendencia.preview,
			fallbackBody: pendencia.command,
			idempotencyKey: pendencia.command.chave_idempotencia,
		});
		setIsSubmittingPanel(true);
		setSubmitError('');
		setSubmitErrorScope('');
		const response = await requestAplicarAcoesAmbienteEscala({
			token,
			body: bodyAplicacao,
			action: pendencia.preview?.acoes?.aplicar || null,
			scope,
		});
		setIsSubmittingPanel(false);

		if (response?.error) {
			if (isPreviewDesatualizado(response)) setDeletePreview(null);
			const recursoAusente =
				Number(response?.error?.status || response?.status || 0) ===
					404 ||
				normalizeCode(
					response?.error?.code || response?.codigo || '',
				) === 'ESCALA_RECURSO_NAO_ENCONTRADO';
			if (recursoAusente) {
				setLinhasOcultadasAposExclusao((prev) =>
					Array.from(
						new Set([
							...prev,
							...getMarcadoresOcultacaoAposEncerramento({
								linhas: pendencia.linhasExclusao,
								escopo: pendencia.escopo,
							}),
						]),
					),
				);
				setIsDeleteConfirmModalOpen(false);
				setDeleteTargetLinha(null);
				setDeletePreview(null);
				await loadAmbiente({
					authToken: token,
					targetContexto:
						getContextoVisualizacaoRequest(contextoAplicado),
					showLoading: false,
					forceRefresh: true,
				});
				setFeedbackMessage(
					'Esta alocação já não está disponível na escala atual e foi removida da visualização.',
				);
				return;
			}
			setSubmitErrorScope('delete');
			setSubmitError(
				isPreviewDesatualizado(response)
					? 'A escala mudou após a simulação. Gere um novo impacto antes de confirmar a exclusão.'
					: getErrorMessage(
							response,
							'Não foi possível aplicar a exclusão.',
						),
			);
			return;
		}

		applyAmbienteResponse(response);
		setLinhasOcultadasAposExclusao((prev) =>
			Array.from(
				new Set([
					...prev,
					...getMarcadoresOcultacaoAposEncerramento({
						linhas: pendencia.linhasExclusao,
						escopo: pendencia.escopo,
					}),
				]),
			),
		);
		if (isDeleteFromGerenciamentoTurnos) {
			removeSetoresFromGerenciamentoTurnosModal(
				pendencia.linhasExclusao || [],
			);
			setIsDeleteFromGerenciamentoTurnos(false);
		}
		setIsDeleteConfirmModalOpen(false);
		setDeleteTargetLinha(null);
		setDeleteReason('');
		setDeleteReasonOther('');
		setDeletePreview(null);
		await loadAmbiente({
			authToken: token,
			targetContexto: getContextoVisualizacaoRequest(contextoAplicado),
			showLoading: false,
			forceRefresh: true,
		});
		setFeedbackMessage(
			pendencia.isVacancia
				? 'Vacância encerrada com sucesso.'
				: pendencia.escopo === 'MODALIDADE'
					? 'Profissional removido de todos os setores desta modalidade com sucesso.'
					: 'Profissional encerrado no setor com sucesso.',
		);
	};

	const handleConfirmDelete = async () => {
		const targetLinha = deleteTargetLinha || linhaSelecionada;
		if (!targetLinha || !contextoAplicado) return;
		const isVacanciaDelete = isVacanciaLinha(targetLinha);
		const linhasExclusaoResolvidas = getLinhasExclusaoPorContexto({
			linha: targetLinha,
			isSetorTodos: isSetorTodosAplicado,
			setorAtual: contextoAplicado?.setor,
		});
		// Mesmo quando a linha residual chega sem regime_vinculo_id, envie a
		// tentativa ao backend. Ele é a fonte de verdade para explicar se a
		// jornada já foi encerrada, se o vínculo está inconsistente ou se falta
		// algum identificador; o front não deve simular uma exclusão nesses casos.
		const linhasExclusaoIniciais =
			linhasExclusaoResolvidas.length > 0
				? linhasExclusaoResolvidas
				: [targetLinha];
		const futureDeleteReason = String(deleteReason || '').trim();
		const futureDeleteReasonOther = String(deleteReasonOther || '').trim();
		const encerramentoDaUnidade =
			!isVacanciaDelete &&
			!isDeleteFromGerenciamentoTurnos &&
			isSetorTodosAplicado;

		const linhasExclusao = await reconciliarLinhasParaExclusao(
			linhasExclusaoIniciais,
		);
		const setorAlvo = String(getLinhaSetorId(targetLinha) || '');
		// O backend agora encerra corretamente um vínculo institucional mesmo
		// quando a linha selecionada é uma alocação de origem. Mantemos a linha
		// do setor escolhido no preview; não a redirecionamos para outra linha
		// nem para o fluxo de alocação-origem.
		let linhaOperacional =
			linhasExclusao.find(
				(linha = {}) =>
					Boolean(getLinhaRegimeVinculoId(linha)) &&
					String(getLinhaSetorId(linha) || '') === setorAlvo,
			) ||
			linhasExclusao.find((linha = {}) =>
				Boolean(getLinhaRegimeVinculoId(linha)),
			);
		// A classificação do card mensal pode estar defasada: uma origem pode
		// chegar sem o selo visual e um destino pode aparentar ser independente.
		// Consultamos a representação estrutural antes do primeiro preview para
		// não enviar o regime de uma linha derivada ao encerramento genérico.
		// O encerramento de toda a unidade permanece agregado no backend e não é
		// convertido em remoção de uma partição individual.
		if (!encerramentoDaUnidade && linhaOperacional) {
			try {
				const resolucaoEstrutural =
					await resolverLinhaEstruturalParaExclusao({
						linha: linhaOperacional,
					});
				if (resolucaoEstrutural?.papelSetor === 'DESTINO') {
					const remocaoPreparada =
						await prepararRemocaoSetorParticionado(
							resolucaoEstrutural.linha,
							{
								alocacaoOrigemId: getAlocacaoOrigemId(
									resolucaoEstrutural.linha,
								),
							},
						);
					if (remocaoPreparada) {
						setIsDeleteConfirmModalOpen(false);
						setDeleteTargetLinha(null);
						return;
					}
				}
				if (getLinhaRegimeVinculoId(resolucaoEstrutural?.linha)) {
					linhaOperacional = resolucaoEstrutural.linha;
				}
			} catch (error) {
				// Tanto na escala médica quanto na de enfermagem, o card mensal pode
				// não trazer os metadados de partição. A consulta estrutural é a
				// tentativa comum para preservar o tratamento do recorte integral; se
				// ela falhar, o preview oficial ainda decide a exclusão.
			}
		}
		const setorOperacional = Number(getLinhaSetorId(linhaOperacional) || 0);
		const regimeVinculoId = getLinhaRegimeVinculoId(linhaOperacional);

		// O endpoint de encerramento sempre exige um setor concreto no contexto,
		// inclusive quando a intenção é encerrar o vínculo inteiro na unidade.
		// A listagem em TODOS apenas define o escopo da decisão; a linha
		// operacional é reconciliada na resposta canônica antes do preview.
		if (!linhaOperacional || !regimeVinculoId || !setorOperacional) {
			setSubmitErrorScope('delete');
			setSubmitError(
				'O profissional não possui alocações futuras. As alocações de hoje e anteriores fazem parte do histórico e não podem ser apagadas.',
			);
			return;
		}

		const dataCorte = getTodayISODateLocal();

		const acaoEncerramento = encerramentoDaUnidade
			? 'ENCERRAR_ALOCACOES_MODALIDADE'
			: 'ENCERRAR_ALOCACAO_SETOR';
		const observacaoEncerramento = encerramentoDaUnidade
			? 'Profissional deixa todos os setores desta modalidade.'
			: 'Encerramento da alocação neste setor solicitado pela gestão.';
		const criarComandoEncerramento = (linha = {}) => {
			const comando = {
				contexto: {
					estabelecimento: getEstabelecimentoIdValue(
						contextoAplicado.estabelecimento,
					),
					setor: Number(getLinhaSetorId(linha) || 0),
					mes_referencia: contextoAplicado.mes_referencia,
				},
				acao: acaoEncerramento,
				regime_vinculo_id: getLinhaRegimeVinculoId(linha),
				data_corte: dataCorte,
				observacao: observacaoEncerramento,
			};
			const baseIdempotencia = buildActionIdempotencyKey(comando);
			comando.chave_idempotencia = `${baseIdempotencia}-${Date.now()}-${Math.random()
				.toString(36)
				.slice(2, 8)}`;
			return comando;
		};

		setIsSubmittingPanel(true);
		setSubmitError('');
		setSubmitErrorScope('');
		let previewResponse = null;
		let comandoEncerramento = null;
		let linhaOperacionalConfirmada = null;
		comandoEncerramento = criarComandoEncerramento(linhaOperacional);
		previewResponse = await requestPreverAcoesAmbienteEscala({
			token,
			body: comandoEncerramento,
			scope,
		});
		linhaOperacionalConfirmada = linhaOperacional;
		setIsSubmittingPanel(false);

		if (previewResponse?.error && exigeFluxoJornada(previewResponse)) {
			try {
				const resolucaoEstrutural =
					await resolverLinhaEstruturalParaExclusao({
						linha: linhaOperacionalConfirmada,
						alocacaoOrigemId:
							getAlocacaoOrigemIdFromPayload(previewResponse),
					});
				if (resolucaoEstrutural?.papelSetor === 'DESTINO') {
					const remocaoPreparada =
						await prepararRemocaoSetorParticionado(
							resolucaoEstrutural.linha,
							{
								alocacaoOrigemId:
									getAlocacaoOrigemIdFromPayload(
										previewResponse,
									) ||
									getAlocacaoOrigemId(
										resolucaoEstrutural.linha,
									),
							},
						);
					if (remocaoPreparada) {
						setIsDeleteConfirmModalOpen(false);
						setDeleteTargetLinha(null);
						return;
					}
				}

				// Para uma origem, o Swagger não define um endpoint alternativo: a
				// mesma ação ENCERRAR_ALOCACAO_SETOR deve ser prevista pelo writer
				// genérico. Porém o detalhe pode revelar que o resumo usou outro
				// regime da mesma jornada. Repetimos uma única vez com a linha
				// canônica, sem transformar a origem em remoção de partição.
				const linhaCanonica = resolucaoEstrutural?.linha;
				const regimeCanonico = getLinhaRegimeVinculoId(linhaCanonica);
				const mudouLinhaOperacional =
					regimeCanonico &&
					String(regimeCanonico) !==
						String(
							getLinhaRegimeVinculoId(linhaOperacionalConfirmada),
						);
				if (
					resolucaoEstrutural?.papelSetor === 'ORIGEM' &&
					mudouLinhaOperacional
				) {
					comandoEncerramento =
						criarComandoEncerramento(linhaCanonica);
					previewResponse = await requestPreverAcoesAmbienteEscala({
						token,
						body: comandoEncerramento,
						scope,
					});
					linhaOperacionalConfirmada = linhaCanonica;
				}
			} catch (error) {
				// A resposta original será exibida abaixo. Não escondemos uma
				// validação do servidor caso o agregado estrutural esteja indisponível.
			}
		}

		if (previewResponse?.error) {
			const recursoAusente =
				Number(
					previewResponse?.error?.status ||
						previewResponse?.status ||
						0,
				) === 404 ||
				normalizeCode(
					previewResponse?.error?.code ||
						previewResponse?.codigo ||
						'',
				) === 'ESCALA_RECURSO_NAO_ENCONTRADO';
			if (recursoAusente) {
				setLinhasOcultadasAposExclusao((prev) =>
					Array.from(
						new Set([
							...prev,
							...getMarcadoresOcultacaoAposEncerramento({
								linhas: encerramentoDaUnidade
									? linhasExclusao
									: [targetLinha],
								escopo: encerramentoDaUnidade
									? 'MODALIDADE'
									: 'SETOR',
							}),
						]),
					),
				);
				setIsDeleteConfirmModalOpen(false);
				setDeleteTargetLinha(null);
				await loadAmbiente({
					authToken: token,
					targetContexto:
						getContextoVisualizacaoRequest(contextoAplicado),
					showLoading: false,
				});
				setFeedbackMessage(
					'Esta alocação já não está disponível na escala atual e foi removida da visualização.',
				);
				return;
			}
			setSubmitErrorScope('delete');
			setSubmitError(
				getErrorMessage(
					previewResponse,
					'Não foi possível validar o encerramento antes da confirmação.',
				),
			);
			return;
		}

		// A linha canônica pode ser a origem estrutural, enquanto o card que a
		// pessoa excluiu é a projeção mensal do mesmo setor. Guardamos ambas para
		// reconciliar a UI após um 200, em vez de deixar o card-base reaparecer
		// até a próxima navegação de competência.
		const linhasEncerramento = Array.from(
			new Map(
				(encerramentoDaUnidade
					? linhasExclusao
					: [targetLinha, linhaOperacionalConfirmada]
				)
					.filter(Boolean)
					.map((linha = {}) => [
						getLinhaExclusaoLocalKey(linha),
						linha,
					]),
			).values(),
		);
		const pendenciaEncerramento = {
			preview: previewResponse,
			command: comandoEncerramento,
			linhasExclusao: linhasEncerramento,
			escopo: encerramentoDaUnidade ? 'MODALIDADE' : 'SETOR',
			isVacancia: isVacanciaDelete,
		};
		if (!isPreviewAcaoConfirmavel(previewResponse)) {
			setDeletePreview({
				preview: previewResponse,
				command: comandoEncerramento,
				linhasExclusao: linhasEncerramento,
				escopo: encerramentoDaUnidade ? 'MODALIDADE' : 'SETOR',
				isVacancia: isVacanciaDelete,
			});
			setSubmitErrorScope('delete');
			setSubmitError(
				getPreviewBlockMessage(
					previewResponse,
					'Esta exclusão não está disponível para confirmação.',
				),
			);
			return;
		}
		await handleApplyDeletePreview(pendenciaEncerramento);
		// A validação e a aplicação acima são sequenciais. Este guarda mantém o
		// fluxo antigo abaixo isolado, sem reaplicar o mesmo encerramento.
		if (!deletePreview) return;

		const targetLinhaReconciliada =
			linhasExclusao.find(
				(linha = {}) =>
					String(getLinhaSetorId(linha) || '') ===
					String(getLinhaSetorId(targetLinha) || ''),
			) || targetLinha;
		try {
			// A consulta de alocação de origem é necessária somente quando a linha
			// já é conhecida como destino de uma jornada repartida. Para linhas
			// usuais — e para a própria origem — o contrato prevê diretamente
			// prever-acoes → aplicar-acoes; abrir o agregado não é uma exclusão.
			const resultadoPreparacaoRemocao = isLinhaDestinoDeSplit(
				targetLinhaReconciliada,
			)
				? await prepararRemocaoSetorParticionado(
						targetLinhaReconciliada,
					)
				: null;
			if (resultadoPreparacaoRemocao?.removidaDaVisualizacao) {
				setIsDeleteConfirmModalOpen(false);
				setDeleteTargetLinha(null);
				return;
			}
			if (resultadoPreparacaoRemocao) {
				setIsDeleteConfirmModalOpen(false);
				setDeleteTargetLinha(null);
				return;
			}
			if (isDeleteFromGerenciamentoTurnos) {
				const linhasParticionamento = Array.isArray(
					gerenciamentoTurnosLinha?.setores_linhas,
				)
					? gerenciamentoTurnosLinha.setores_linhas
					: [gerenciamentoTurnosLinha].filter(Boolean);
				const setorDestino = Number(getLinhaSetorId(targetLinha) || 0);
				const setorDerivado =
					!isLinhaOrigemDeSplit(targetLinha) &&
					(isLinhaDestinoDeSplit(targetLinha) ||
						getParticoesPersistidas(linhasParticionamento).some(
							(particao = {}) =>
								Number(particao?.setor_destino_id || 0) ===
								setorDestino,
						));
				if (setorDerivado) {
					setSubmitErrorScope('delete');
					setSubmitError(
						'Este setor é derivado de uma jornada repartida e deve ser removido pelo fluxo de edição das partições da origem.',
					);
					return;
				}
			}
		} catch (error) {
			setSubmitErrorScope('delete');
			setSubmitError(
				String(error?.message || '') ||
					'Não foi possível preparar a remoção do recorte de jornada.',
			);
			return;
		}

		const escopoRemocaoFutura =
			!isDeleteFromGerenciamentoTurnos && isSetorTodosAplicado
				? 'MODALIDADE'
				: 'SETOR';
		const isEncerramentoModalidade = escopoRemocaoFutura === 'MODALIDADE';
		const acaoRemocaoFutura = isEncerramentoModalidade
			? 'ENCERRAR_ALOCACOES_MODALIDADE'
			: 'ENCERRAR_ALOCACAO_SETOR';

		const futuroObservacao = isEncerramentoModalidade
			? 'Profissional deixa todos os setores desta modalidade.'
			: isVacanciaDelete
				? 'Remocao da vacancia da escala a partir de hoje.'
				: 'Remocao do profissional da escala a partir de hoje.';
		const dataCorteEstabelecimento = getTodayISODateLocal();

		if (!dataCorteEstabelecimento) {
			setSubmitErrorScope('delete');
			setSubmitError(
				'Não foi possível identificar a data inicial do profissional para encerrar a escala.',
			);
			return;
		}

		const operacoesExclusao = linhasExclusao.map((linhaSetor, index) => {
			const shouldApplyFutureRemoval =
				!isEncerramentoModalidade ||
				index === linhasExclusao.length - 1;
			const inicioEscalaIso = getInicioEscalaIsoFromLinha(linhaSetor);
			// O comando é avaliado no contexto que a pessoa está vendo. A data de
			// início da escala é uma informação histórica da linha e não deve
			// deslocar a competência de uma exclusão atual.
			const mesReferenciaDelete = contextoAplicado.mes_referencia;
			const dataCorte = dataCorteEstabelecimento;
			const contextoDelete = {
				estabelecimento: getEstabelecimentoIdValue(
					contextoAplicado.estabelecimento,
				),
				setor: getContextoOperacional(contextoAplicado, linhaSetor)
					.setor,
				mes_referencia: mesReferenciaDelete,
			};
			const contextoDeleteFuturo = {
				estabelecimento: contextoDelete.estabelecimento,
				setor: contextoDelete.setor,
				mes_referencia: mesReferenciaDelete,
			};
			const regimeVinculoId = getLinhaRegimeVinculoId(linhaSetor);
			const base = {
				contexto: contextoDelete,
				...(regimeVinculoId
					? { regime_vinculo_id: regimeVinculoId }
					: {}),
				...(inicioEscalaIso
					? { primeiro_dia_trabalho_mes: inicioEscalaIso }
					: {}),
			};

			const actionBody = shouldApplyFutureRemoval
				? {
						...base,
						contexto: contextoDeleteFuturo,
						acao: acaoRemocaoFutura,
						data_corte: dataCorte,
						observacao: futuroObservacao,
					}
				: null;

			// Uma nova confirmação é uma nova decisão. A chave permanece estável
			// no preview e no apply desta decisão, mas não reaproveita a chave de
			// uma exclusão anterior com payload diferente.
			if (actionBody) {
				const baseKey = buildActionIdempotencyKey(actionBody);
				actionBody.chave_idempotencia = `${baseKey}-${Date.now()}-${index}-${Math.random()
					.toString(36)
					.slice(2, 8)}`;
			}

			return {
				linha: linhaSetor,
				actionBody,
			};
		});

		if (
			operacoesExclusao.some(
				(operacao = {}) =>
					operacao?.actionBody &&
					!Number(operacao.actionBody?.regime_vinculo_id || 0),
			)
		) {
			setSubmitErrorScope('delete');
			setSubmitError(
				'Não foi possível identificar a linha atual do vínculo para exclusão. Recarregue a escala e tente novamente.',
			);
			return;
		}

		if (
			operacoesExclusao.some(
				(operacao = {}) =>
					operacao.actionBody &&
					!formatDateInputToISO(operacao.actionBody.data_corte),
			)
		) {
			setSubmitErrorScope('delete');
			setSubmitError(
				'Não foi possível identificar a data inicial do profissional para encerrar a escala.',
			);
			return;
		}

		const exigePreviewEncerramentoSetor = operacoesExclusao.some(
			(operacao = {}) =>
				normalizeCode(operacao?.actionBody?.acao || '') ===
				'ENCERRAR_ALOCACAO_SETOR',
		);

		if (shouldPreviewBeforeApply || exigePreviewEncerramentoSetor) {
			for (const operacao of operacoesExclusao) {
				if (!operacao.actionBody) continue;

				const previewResponse = await requestPreverAcoesAmbienteEscala({
					token,
					body: operacao.actionBody,
					scope,
				});

				if (previewResponse?.error) {
					try {
						const redirecionouParaJornada =
							await redirecionarExclusaoParaFluxoJornada({
								linhaDestino: operacao.linha || targetLinha,
								response: previewResponse,
							});
						if (redirecionouParaJornada) {
							setIsDeleteConfirmModalOpen(false);
							setDeleteTargetLinha(null);
							return;
						}
					} catch (error) {
						setSubmitErrorScope('delete');
						setSubmitError(
							String(error?.message || '') ||
								'Não foi possível preparar a exclusão pelo fluxo de jornada.',
						);
						return;
					}
					setSubmitErrorScope('delete');
					setSubmitError(
						getErrorMessage(
							previewResponse,
							isVacanciaDelete
								? 'Não foi possível validar a exclusão da vacância.'
								: 'Não foi possível validar a exclusão do profissional.',
						),
					);
					return;
				}
				if (!isPreviewAcaoConfirmavel(previewResponse)) {
					setSubmitErrorScope('delete');
					setSubmitError(
						getPreviewBlockMessage(
							previewResponse,
							'Esta exclusão não está disponível para confirmação.',
						),
					);
					return;
				}
				operacao.encerramentoPreviewEtag =
					previewResponse?.preview_etag ||
					previewResponse?.preview?.preview_etag ||
					'';
				operacao.previewResponse = previewResponse;
			}
		}

		setIsSubmittingPanel(true);
		setSubmitError('');

		try {
			let lastFutureResponse = null;
			const throwDeleteError = (response, phase, linha = null) => {
				const error = new Error('Falha na exclusão consolidada.');
				error.response = response;
				error.phase = phase;
				error.linha = linha;
				throw error;
			};
			const aplicarOperacaoExclusao = async ({
				body,
				previewResponse: previewExistente = null,
				phase,
				linha,
			}) => {
				if (!body) return;
				const previewResponse =
					previewExistente ||
					(await requestPreverAcoesAmbienteEscala({
						token,
						body,
						scope,
					}));
				if (previewResponse?.error) {
					throwDeleteError(
						previewResponse,
						`preview-${phase}`,
						linha,
					);
				}
				if (!isPreviewAcaoConfirmavel(previewResponse)) {
					throwDeleteError(
						{
							error: true,
							message: getPreviewBlockMessage(
								previewResponse,
								'Esta exclusão não está disponível para confirmação.',
							),
						},
						`${phase}-bloqueado`,
						linha,
					);
				}
				const applyAction = previewResponse?.acoes?.aplicar || null;
				if (applyAction?.disponivel === false) {
					throwDeleteError(
						{
							error: true,
							message:
								applyAction?.motivo ||
								'Não foi possível confirmar esta exclusão.',
						},
						`${phase}-indisponivel`,
						linha,
					);
				}
				// A entrada HATEOAS é o comando canônico. Ela pode promover uma
				// ação para JORNADA ou completar o alvo resolvido pelo backend;
				// portanto não a recompomos no navegador.
				const bodyAplicacao = buildApplyActionBodyFromPreview({
					preview: previewResponse,
					fallbackBody: body,
					idempotencyKey: body.chave_idempotencia,
				});
				const response = await requestAplicarAcoesAmbienteEscala({
					token,
					body: bodyAplicacao,
					action: applyAction,
					scope,
				});
				if (response?.error) throwDeleteError(response, phase, linha);
				lastFutureResponse = response;
			};

			for (const operacao of operacoesExclusao) {
				await aplicarOperacaoExclusao({
					body: operacao.actionBody,
					previewResponse: operacao.previewResponse,
					phase: 'encerramento',
					linha: operacao.linha,
				});
			}

			setIsDeleteConfirmModalOpen(false);
			setDeleteTargetLinha(null);
			setDeleteReason('');
			setDeleteReasonOther('');
			applyAmbienteResponse(lastFutureResponse);
			if (isDeleteFromGerenciamentoTurnos) {
				removeSetoresFromGerenciamentoTurnosModal(
					linhasExclusao.map((linha) => linha),
				);
				setIsDeleteFromGerenciamentoTurnos(false);
			}
			setLinhasOcultadasAposExclusao((prev) =>
				Array.from(
					new Set([
						...prev,
						...linhasExclusao.map(getLinhaExclusaoLocalKey),
					]),
				),
			);
			await loadAmbiente({
				authToken: token,
				targetContexto:
					getContextoVisualizacaoRequest(contextoAplicado),
				showLoading: false,
			});
			setFeedbackMessage(
				isVacanciaDelete
					? 'Vacância encerrada com sucesso.'
					: isEncerramentoModalidade
						? 'Profissional removido de todos os setores desta modalidade com sucesso.'
						: 'Profissional encerrado no setor com sucesso.',
			);
		} catch (deleteError) {
			const respostaExclusao = deleteError?.response || deleteError;
			const recursoAusente =
				Number(
					respostaExclusao?.error?.status ||
						respostaExclusao?.status ||
						0,
				) === 404 ||
				normalizeCode(
					respostaExclusao?.error?.code ||
						respostaExclusao?.codigo ||
						'',
				) === 'ESCALA_RECURSO_NAO_ENCONTRADO';
			// O contrato não diferencia recurso inexistente de recurso que deixou
			// de pertencer ao escopo do usuário. Em ambos os casos, a linha não é
			// mais operável: remova o resíduo local e recarregue a fonte oficial.
			if (recursoAusente) {
				setIsDeleteConfirmModalOpen(false);
				setDeleteTargetLinha(null);
				setLinhasOcultadasAposExclusao((prev) =>
					Array.from(
						new Set([
							...prev,
							getLinhaExclusaoLocalKey(targetLinha),
						]),
					),
				);
				await loadAmbiente({
					authToken: token,
					targetContexto:
						getContextoVisualizacaoRequest(contextoAplicado),
					showLoading: false,
				});
				setFeedbackMessage(
					'Esta alocação já não está disponível na escala atual e foi removida da visualização.',
				);
				return;
			}
			try {
				const redirecionouParaJornada =
					await redirecionarExclusaoParaFluxoJornada({
						linhaDestino: deleteError?.linha || targetLinha,
						response: respostaExclusao,
					});
				if (redirecionouParaJornada) {
					setIsDeleteConfirmModalOpen(false);
					setDeleteTargetLinha(null);
					return;
				}
			} catch (error) {
				setSubmitErrorScope('delete');
				setSubmitError(
					String(error?.message || '') ||
						'Não foi possível preparar a exclusão pelo fluxo de jornada.',
				);
				return;
			}
			setSubmitErrorScope('delete');
			setSubmitError(
				getErrorMessage(
					deleteError?.response || deleteError,
					deleteError?.phase === 'futuro'
						? 'Não foi possível aplicar o encerramento do profissional.'
						: 'Não foi possível validar o encerramento do profissional.',
				),
			);
			await loadAmbiente({
				authToken: token,
				targetContexto:
					getContextoVisualizacaoRequest(contextoAplicado),
				showLoading: false,
			});
		} finally {
			setIsSubmittingPanel(false);
		}
	};

	const navigateMonth = async ({ offset = 0, toCurrent = false } = {}) => {
		const baseIso = formatDateInputToISO(
			contextoAplicado?.mes_referencia ||
				contexto.mes_referencia ||
				getDefaultContexto().mes_referencia,
		);
		if (!baseIso) return;

		const [ano, mes] = baseIso.split('-');
		const targetDate = new Date(Number(ano), Number(mes) - 1, 1);
		if (toCurrent) {
			const today = parseISODateLocal(
				getDefaultContexto().mes_referencia,
			);
			if (!today) return;
			targetDate.setFullYear(today.getFullYear(), today.getMonth(), 1);
		} else {
			targetDate.setMonth(targetDate.getMonth() + offset);
		}

		const nextIso = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-01`;
		setContexto((prev) => ({ ...prev, mes_referencia: nextIso }));

		if (!token || !contextoAplicado?.setor) return;
		const targetContexto = {
			estabelecimento: getEstabelecimentoIdValue(
				contextoAplicado?.estabelecimento ||
					contexto.estabelecimento ||
					userCnes,
			),
			setor: isSetorTodosValue(contextoAplicado?.setor)
				? ''
				: Number(contextoAplicado.setor),
			mes_referencia: nextIso,
		};

		autoLoadedContextKeyRef.current = buildProfessionalManagementContextKey(
			{
				scope,
				contexto: targetContexto,
			},
		);
		await loadOpcoesAmbiente({ authToken: token, targetContexto });
		await loadAmbiente({
			authToken: token,
			targetContexto,
			showLoading: true,
			loadingMode: 'month-navigation',
		});
	};

	const goToPreviousMonth = () => navigateMonth({ offset: -1 });
	const goToNextMonth = () => navigateMonth({ offset: 1 });
	const goToCurrentMonth = () => navigateMonth({ toCurrent: true });

	const regimesOptions = Array.isArray(ambienteOpcoes?.regimes)
		? ambienteOpcoes.regimes
		: [];
	const recorrenciaNomeOptions = useMemo(() => {
		const modalidades = buildRecorrenciaNomeOptionsFromModalidades(
			ambienteOpcoes?.modalidades_recorrencia,
		);
		if (modalidades) {
			return modalidade === 'ENFERMAGEM'
				? getNursingRecorrenciaNomeOptions(modalidades)
				: modalidades;
		}
		const presets = Array.isArray(ambienteOpcoes?.presets_ciclo)
			? ambienteOpcoes.presets_ciclo
			: Array.isArray(ambienteOpcoes?.presets_ciclos)
				? ambienteOpcoes.presets_ciclos
				: null;
		const fallbackOptions = !presets
			? RECORRENCIA_NOME_OPTIONS
			: buildRecorrenciaNomeOptionsFromPresets(presets);

		return modalidade === 'ENFERMAGEM'
			? getNursingRecorrenciaNomeOptions(fallbackOptions)
			: fallbackOptions;
	}, [
		ambienteOpcoes?.modalidades_recorrencia,
		ambienteOpcoes?.presets_ciclo,
		ambienteOpcoes?.presets_ciclos,
		modalidade,
	]);

	const recurrencePresetDefaultsMap = useMemo(() => {
		const presets = Array.isArray(ambienteOpcoes?.presets_ciclo)
			? ambienteOpcoes.presets_ciclo
			: Array.isArray(ambienteOpcoes?.presets_ciclos)
				? ambienteOpcoes.presets_ciclos
				: [];
		return presets.reduce((acc, preset) => {
			const codigo = String(preset?.codigo || '').trim();
			if (!codigo) return acc;
			const normalized = normalizeCode(codigo);
			if (normalized === 'SEMANAL' || normalized === 'PERSONALIZADO') {
				return acc;
			}
			const defaults = {
				tipo_recorrencia: 'CICLO_CONTINUO',
				preset_ciclo: getPayloadPresetCiclo(codigo) || codigo,
				trabalho_horas: Number(preset?.trabalho_horas || 0) || '',
				descanso_horas: Number(preset?.descanso_horas || 0) || '',
				hora_inicio: String(preset?.hora_inicio || '').slice(0, 5),
				hora_fim: String(preset?.hora_fim || '').slice(0, 5),
			};
			getPresetLookupKeys(codigo).forEach((key) => {
				acc[key] = defaults;
			});
			return acc;
		}, {});
	}, [ambienteOpcoes?.presets_ciclo, ambienteOpcoes?.presets_ciclos]);

	const getRecurrencePresetDefaults = useCallback(
		(preset = '') => {
			const foundKey = getPresetLookupKeys(preset).find(
				(key) => recurrencePresetDefaultsMap[key],
			);
			return (
				(foundKey ? recurrencePresetDefaultsMap[foundKey] : null) ||
				getPresetDefaults(preset) ||
				getPresetDefaults(getPayloadPresetCiclo(preset))
			);
		},
		[recurrencePresetDefaultsMap],
	);

	const turnosOptions = useMemo(
		() =>
			Array.isArray(ambienteOpcoes?.turnos) ? ambienteOpcoes.turnos : [],
		[ambienteOpcoes?.turnos],
	);
	const dimensaoOptions =
		modalidade === 'ENFERMAGEM'
			? Array.isArray(ambienteOpcoes?.categorias)
				? ambienteOpcoes.categorias
				: []
			: Array.isArray(ambienteOpcoes?.especialidades)
				? ambienteOpcoes.especialidades
				: [];
	const selectRegimeLabel =
		modalidade === 'ENFERMAGEM' ? 'Categoria' : 'Área de atuação';

	const selectedDayLabel = selectedDiaIso
		? formatISOToDisplay(selectedDiaIso)
		: '--/--/----';
	const todayISODateLocal = useMemo(() => getTodayISODateLocal(), []);
	const novoProfissionalHasRegime = Boolean(
		String(novoProfissionalForm.regime || '').trim(),
	);
	const novoProfissionalHasRecorrencia = Boolean(
		String(novoProfissionalForm.nome_recorrencia || '').trim(),
	);
	const novoProfissionalRegimeRecorrenciaBloqueados =
		novoProfissionalTemEscalaOrigem && !isEditingProfissional;
	const novoProfissionalDataInicialEhDiaUtil = isWeekdayDateValue(
		novoProfissionalForm.vigencia_inicio,
	);
	const novoProfissionalDiasSemanaSelecionados = Array.isArray(
		novoProfissionalForm.dias_semana,
	)
		? novoProfissionalForm.dias_semana
		: [];
	const novoProfissionalUsaRecorrenciaCiclo =
		normalizeCode(novoProfissionalForm.tipo_recorrencia) ===
		'CICLO_CONTINUO';
	const novoProfissionalUsaRotacaoPeriodica =
		normalizeCode(novoProfissionalForm.tipo_recorrencia) ===
		'ROTACAO_PERIODICA';
	const novoProfissionalUsaSemanalMedica =
		modalidade === 'MEDICA' &&
		isWeeklyRecorrencia(novoProfissionalForm.tipo_recorrencia);
	const novoProfissionalUsaSemanalEnfermagem =
		modalidade === 'ENFERMAGEM' &&
		isWeeklyRecorrencia(novoProfissionalForm.tipo_recorrencia);
	const novoProfissionalDiaSemanaBloqueado = (() => {
		if (
			modalidade === 'ENFERMAGEM' ||
			!isWeeklyRecorrencia(novoProfissionalForm.tipo_recorrencia) ||
			novoProfissionalUsaSemanalMedica
		) {
			return null;
		}

		const diaInicial = getRequiredWeekdayFromStartDate(
			novoProfissionalForm.vigencia_inicio,
		);
		return diaInicial >= 1 && diaInicial <= 5 ? diaInicial : null;
	})();
	const novoProfissionalDiasSemanaEfetivos = isWeeklyRecorrencia(
		novoProfissionalForm.tipo_recorrencia,
	)
		? novoProfissionalUsaSemanalMedica ||
			novoProfissionalUsaSemanalEnfermagem
			? normalizeDiasSemanaValues(novoProfissionalDiasSemanaSelecionados)
			: novoProfissionalDataInicialEhDiaUtil
				? getDiasSemanaWithRequiredStartDate({
						dias_semana: novoProfissionalDiasSemanaSelecionados,
						startDate: novoProfissionalForm.vigencia_inicio,
					})
				: normalizeDiasSemanaValues(
						novoProfissionalDiasSemanaSelecionados,
					).filter((diaSemana) => diaSemana >= 1 && diaSemana <= 5)
		: [];
	const novoProfissionalIsDataEspecifica = isSpecificDatesRecorrencia(
		novoProfissionalForm.nome_recorrencia,
	);
	const novoProfissionalMostrarBlocoDiasSemana =
		novoProfissionalHasRegime &&
		isWeeklyRecorrencia(novoProfissionalForm.tipo_recorrencia);
	const novoProfissionalMostrarBlocoTurnoCiclo =
		modalidade === 'ENFERMAGEM' &&
		(novoProfissionalUsaRecorrenciaCiclo ||
			novoProfissionalUsaRotacaoPeriodica);
	const gerenciamentoTurnosNovoRecorrenciaOption =
		recorrenciaNomeOptions.find(
			(item) =>
				normalizeCode(item?.value || '') ===
				normalizeCode(gerenciamentoTurnosNovoRecorrencia),
		) || RECORRENCIA_NOME_OPTIONS[0];
	const gerenciamentoTurnosNovoUsaRecorrenciaSemanal =
		gerenciamentoTurnosNovoRecorrenciaOption?.tipo_recorrencia ===
		'SEMANAL';
	const gerenciamentoTurnosNovoUsaRotacaoPeriodica =
		modalidade === 'ENFERMAGEM' &&
		gerenciamentoTurnosNovoRecorrenciaOption?.tipo_recorrencia ===
			'ROTACAO_PERIODICA';
	const novoProfissionalDatasExtras = Array.isArray(
		novoProfissionalForm.datas_extras,
	)
		? novoProfissionalForm.datas_extras
		: [];
	// O formulário é editável mesmo quando foi aberto a partir de uma
	// partição. No envio, a jornada é atualizada pela origem com o estado
	// completo das partições, em vez de bloquear controles locais.
	const novoProfissionalJornadaInterligadaDetalhe = {
		bloqueiaDias: false,
		horariosPorDia: {},
	};
	const mesVisualizadoISO = formatDateInputToISO(
		contextoAplicado?.mes_referencia || contexto.mes_referencia || '',
	);
	const mesVisualizadoKey = mesVisualizadoISO
		? String(mesVisualizadoISO).slice(0, 7)
		: '';
	const isDataExtraNoMesVisualizado = (value = '') => {
		const iso = formatDateInputToISO(value);
		if (!iso || !mesVisualizadoKey) return false;
		return String(iso).slice(0, 7) === mesVisualizadoKey;
	};
	const getNovoProfissionalDataExtraOptionsComSelecionada = (item = {}) => {
		const options = getNovoProfissionalDataExtraOptions(item?.id);
		const selectedISO = formatDateInputToISO(item?.selected_date || '');
		if (!selectedISO) return options;

		const jaExiste = options.some(
			(option) => formatDateInputToISO(option?.value) === selectedISO,
		);
		if (jaExiste) return options;

		return [
			{
				value: selectedISO,
				label: formatISOToDisplay(selectedISO) || selectedISO,
			},
			...options,
		];
	};
	const panelRegimeDisplay =
		regimesOptions.find(
			(item) =>
				String(item?.codigo || item?.value || '') ===
				String(panelForm.regime || ''),
		)?.nome ||
		regimesOptions.find(
			(item) =>
				String(item?.codigo || item?.value || '') ===
				String(panelForm.regime || ''),
		)?.label ||
		panelForm.regime ||
		'Selecione';
	const panelHorarioInicio = String(panelForm.hora_inicio || '').slice(0, 5);
	const panelHorarioFim = String(panelForm.hora_fim || '').slice(0, 5);
	const panelTurnoDisplay =
		panelHorarioInicio && panelHorarioFim
			? `${panelHorarioInicio} às ${panelHorarioFim}`
			: 'Horário não informado';
	const vacanciaHasDataInicio = Boolean(
		String(vacanciaForm.data_inicio_vacancia || '').trim(),
	);
	const vacanciaHasEspecialidade = Boolean(
		String(vacanciaForm.especialidade_id || '').trim(),
	);
	const vacanciaHasVinculo = Boolean(
		String(vacanciaForm.vinculo_tipo_id || '').trim(),
	);
	const vacanciaHasRegime = Boolean(String(vacanciaForm.regime || '').trim());
	const vacanciaHasRecorrencia = Boolean(
		String(vacanciaForm.nome_recorrencia || '').trim(),
	);
	const vacanciaHasObservacao = Boolean(
		String(vacanciaForm.observacao || '').trim(),
	);
	const vacanciaModalReadOnly = isViewingVacancia;
	const vacanciaIsDataEspecifica = isSpecificDatesRecorrencia(
		vacanciaForm.nome_recorrencia,
	);
	const vacanciaRecorrenciaOption = recorrenciaNomeOptions.find(
		(item) =>
			normalizeCode(item?.value || '') ===
			normalizeCode(vacanciaForm.nome_recorrencia),
	);
	const vacanciaTipoRecorrencia = normalizeCode(
		vacanciaForm.tipo_recorrencia ||
			vacanciaRecorrenciaOption?.tipo_recorrencia,
	);
	const vacanciaUsaRecorrenciaCiclo =
		vacanciaTipoRecorrencia === 'CICLO_CONTINUO';
	const vacanciaUsaSemanalMedica =
		modalidade === 'MEDICA' && vacanciaTipoRecorrencia === 'SEMANAL';

	const vacanciaDiasSemanaSelecionados = Array.isArray(
		vacanciaForm.dias_semana,
	)
		? vacanciaForm.dias_semana
		: [];
	const vacanciaMostrarDiasRecorrentesSomenteLeitura =
		vacanciaModalReadOnly && vacanciaHasRecorrencia;
	const vacanciaDiaSemanaBloqueado =
		!vacanciaMostrarDiasRecorrentesSomenteLeitura &&
		vacanciaHasRecorrencia &&
		!vacanciaUsaSemanalMedica
			? getLockedWeekdayFromStartDate({
					regime: vacanciaForm.regime,
					startDate: vacanciaForm.data_inicio_vacancia,
				})
			: null;
	const vacanciaDiasSemanaEfetivos =
		vacanciaMostrarDiasRecorrentesSomenteLeitura
			? normalizeDiasSemanaValues(vacanciaDiasSemanaSelecionados)
			: vacanciaUsaSemanalMedica
				? normalizeDiasSemanaValues(vacanciaDiasSemanaSelecionados)
				: getEffectiveDiasSemanaSelection({
						dias_semana: vacanciaDiasSemanaSelecionados,
						regime: vacanciaHasRecorrencia
							? vacanciaForm.regime
							: '',
						startDate: vacanciaForm.data_inicio_vacancia,
					});
	const vacanciaUsaRecorrenciaSemanal =
		vacanciaTipoRecorrencia === 'SEMANAL' && !vacanciaIsDataEspecifica;
	const vacanciaMostrarBlocoDiasSemana =
		vacanciaHasRegime && vacanciaUsaRecorrenciaSemanal;
	const vacanciaMostrarBlocoTurnoCiclo =
		modalidade === 'ENFERMAGEM' && vacanciaUsaRecorrenciaCiclo;
	const vacanciaTurnoEhPersonalizado =
		Boolean(vacanciaForm.turno_personalizado) ||
		(Boolean(vacanciaForm.hora_inicio) &&
			Boolean(vacanciaForm.hora_fim) &&
			!TURNOS_DOZE_HORAS_OPTIONS.some(
				(turno) =>
					turno.inicio === vacanciaForm.hora_inicio &&
					turno.fim === vacanciaForm.hora_fim,
			));
	const vacanciaDatasExtras = Array.isArray(vacanciaForm.datas_extras)
		? vacanciaForm.datas_extras
		: [];
	const novoProfissionalVisibleMessages = useMemo(() => {
		const messages = [];

		if (novoProfissionalErrorDisplayMode === 'search') {
			if (novoProfissionalErrors.cpf) {
				messages.push(novoProfissionalErrors.cpf);
			}
		}

		if (novoProfissionalErrorDisplayMode === 'submit') {
			if (novoProfissionalErrors.cpf) {
				messages.push(novoProfissionalErrors.cpf);
			}
		}

		if (submitErrorScope === 'novoProfissional' && submitError) {
			messages.push(submitError);
		}

		if (novoProfissionalApplyError) {
			messages.push(novoProfissionalApplyError);
		}

		return Array.from(new Set(messages.filter(Boolean)));
	}, [
		novoProfissionalErrorDisplayMode,
		novoProfissionalErrors.cpf,
		novoProfissionalApplyError,
		submitError,
		submitErrorScope,
	]);

	if (!loadingInicial && !hasManagementAccess) {
		return (
			<div className='flex flex-col min-h-screen font-inter'>
				<NavBar />
				<main className='flex-grow flex items-center justify-center px-4 mt-[80px]'>
					<section className='bg-[#13335A] rounded-md w-full max-w-[720px]'>
						<div className='py-8 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6'>
							<div className='mx-auto max-w-screen-sm text-center'>
								<h1 className='mb-4 text-white text-6xl tracking-tight font-extrabold lg:text-7xl text-primary-600 '>
									401
								</h1>
								<p className='mb-10 text-3xl tracking-tight font-bold text-slate-100 md:text-4xl '>
									Acesso Negado
								</p>
								<p className='mb-7 text-lg font-light text-gray-300 '>
									A tela de gerenciamento de profissionais
									exige permissão de edição de escala médica
									ou de enfermagem.
								</p>
								<a
									href='/escala'
									className='inline-flex hover:scale-105 transition-transform text-gray-300 bg-primary-600 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center my-4'>
									Voltar para a escala
								</a>
							</div>
						</div>
					</section>
				</main>
				<Footer />
			</div>
		);
	}

	return (
		<div className='flex flex-col min-h-screen '>
			<NavBar />

			<div className='mt-[80px] mb-4 font-inter'>
				<h1 className='text-center text-[26px] sm:text-[28px] font-semibold text-[#13335A] bg-[#F0F4F8] p-[30px]'>
					Gerenciamento de Profissionais
				</h1>
			</div>

			<main className='flex-grow w-full'>
				<div className='max-w-[1600px] w-[95%] mx-auto flex flex-col gap-4 pb-10 font-inter max-sm:w-full max-sm:px-3'>
					<div className='w-[90%] sm:w-[95%] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 max-w-[1600px] py-2'>
						<button
							type='button'
							onClick={() => navigate(-1)}
							className='inline-flex items-center  gap-1.5 text-[18px] font-medium text-[#17395f]  hover:text-[#60758f] duration-300 transition-colors '>
							<MdArrowBackIosNew size={15} />
							Voltar
						</button>
					</div>
					{/* Card: Selecionar setor */}
					<div className='bg-white border border-gray-200 shadow-md rounded-md'>
						<div className='px-5 py-3 '>
							<p className='text-[18px] font-semibold text-[#004A80]'>
								Selecione o setor
							</p>
						</div>
						<hr className='mx-[20px]'></hr>
						<div className='px-5 py-4 flex flex-col gap-3 w-full max-sm:px-3'>
							<div className='flex gap-3 w-full max-sm:flex-col'>
								<div
									className={`flex gap-3 items-end ${
										showUnidadeFilter ? '' : ''
									} w-full max-sm:flex-col max-sm:items-stretch`}>
									{showUnidadeFilter && (
										<div className='flex flex-col gap-1 relative w-full'>
											<label className='text-[14px] text-[#13335A] font-medium'>
												Unidade
											</label>
											<input
												type='text'
												value={unidadeSearchTerm}
												onChange={(e) =>
													handleUnidadeInputChange(
														e.target.value,
													)
												}
												onFocus={() => {
													setShowSetorSuggestions(
														false,
													);
													setShowUnidadeSuggestions(
														true,
													);
												}}
												onBlur={() => {
													setTimeout(
														() =>
															setShowUnidadeSuggestions(
																false,
															),
														120,
													);
												}}
												placeholder='Selecione a unidade'
												className={`border rounded-md text-[13px] font-medium px-2  py-[9.2px] text-[#13335A] bg-white outline-none w-full h-full ${
													unidadeInputInvalida
														? 'border-[#dc2626]'
														: 'border-[#E3E3E3]'
												}`}
												aria-invalid={Boolean(
													contextoErrors.estabelecimento,
												)}
											/>

											{showUnidadeSuggestions && (
												<div className='absolute left-0 right-0 top-full mt-1 z-30 w-full max-h-[220px] overflow-y-auto rounded-[8px] border border-[#E3E3E3] bg-white shadow-sm'>
													{unidadeOptionsFiltradas.length >
													0 ? (
														unidadeOptionsFiltradas.map(
															(item) => (
																<button
																	type='button'
																	key={
																		item.id
																	}
																	onMouseDown={(
																		e,
																	) => {
																		e.preventDefault();
																		handleSelectUnidade(
																			item,
																		);
																	}}
																	className='block w-full px-3 py-2 text-left text-[14px] text-[#4F5257] hover:bg-[#F3F4F6]'>
																	{formatUnidadeOptionLabel(
																		item,
																	)}
																</button>
															),
														)
													) : (
														<div className='px-3 py-2 text-[13px] text-[#9CA3AF]'>
															Nenhuma unidade
															encontrada
														</div>
													)}
												</div>
											)}
										</div>
									)}

									<div className='flex flex-col gap-1 relative w-full'>
										<label className='text-[14px] text-[#13335A] font-medium'>
											Setor
										</label>
										<input
											type='text'
											value={setorSearchTerm}
											onChange={(e) =>
												handleSetorInputChange(
													e.target.value,
												)
											}
											onFocus={() => {
												if (setorInputDisabled) return;
												setShowUnidadeSuggestions(
													false,
												);
												setShowSetorSuggestions(true);
											}}
											onBlur={() => {
												setTimeout(
													() =>
														setShowSetorSuggestions(
															false,
														),
													120,
												);
											}}
											disabled={setorInputDisabled}
											placeholder='Selecione o Setor'
											className={`border rounded-md text-[13px] font-medium px-2 py-[9.2px] text-[#13335A] bg-white outline-none w-full h-full disabled:bg-gray-100 disabled:text-gray-400 ${
												setorInputInvalido
													? 'border-[#dc2626]'
													: 'border-[#E3E3E3]'
											}`}
											aria-invalid={Boolean(
												contextoErrors.setor,
											)}
										/>

										{showSetorSuggestions && (
											<div className='absolute left-0 right-0 top-full mt-1 z-30 w-full max-h-[220px] overflow-y-auto rounded-[8px] border border-[#E3E3E3] bg-white shadow-sm'>
												{setorOptionsFiltrados.length >
												0 ? (
													setorOptionsFiltrados.map(
														(item) => (
															<button
																type='button'
																key={item.id}
																onMouseDown={(
																	e,
																) => {
																	e.preventDefault();
																	handleSelectSetor(
																		item,
																	);
																}}
																className='block w-full px-3 py-2 text-left text-[14px] text-[#4F5257] hover:bg-[#F3F4F6]'>
																{formatSetorOptionLabel(
																	item,
																)}
															</button>
														),
													)
												) : (
													<div className='px-3 py-2 text-[13px] text-[#9CA3AF]'>
														Nenhum setor encontrado
													</div>
												)}
											</div>
										)}
									</div>
								</div>

								<div className='flex items-end justify-end max-sm:w-full'>
									<button
										type='button'
										onClick={handleSelectContexto}
										className=' bg-white border border-gray-200 hover:bg-gray-50 transition text-[#13335A] shadow-sm text-[14px] font-semibold w-[230px] h-[37px] rounded-md whitespace-nowrap max-sm:w-full '>
										Selecionar
									</button>
								</div>
							</div>

							{contextoAreaMessage && !hasContextoAplicado && (
								<div
									className={`px-1 text-[10px] font-bold font-inter ${
										submitErrorScope === 'contexto' &&
										submitError
											? 'text-[#d42727]'
											: 'text-[#65686D]'
									}`}>
									{contextoAreaMessage}
								</div>
							)}
						</div>
					</div>

					{hasContextoAplicado && (
						<div className='flex flex-col gap-4'>
							{/* Botões */}
							<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
								<div className='flex flex-wrap gap-3 max-sm:w-full max-sm:flex-col'>
									<button
										type='button'
										onClick={
											handleOpenNovoProfissionalModal
										}
										disabled={
											!contextoAplicado || disableActions
										}
										className='flex items-center justify-between gap-2 bg-[#004A80] text-white px-5 py-2 hover:bg-[#0d466c] transition  rounded-[7px] shadow-md text-[13px] disabled:opacity-50 disabled:cursor-not-allowed max-sm:w-full max-sm:justify-center'>
										<span className='text-xl leading-none mb-1 flex items-center'>
											+
										</span>
										<span className='inline-block w-[2px] h-6 bg-[#003D69] rounded-full '></span>
										<span className='flex items-center h-full'>
											Adicionar novo profissional
										</span>
									</button>
									<button
										type='button'
										onClick={handleOpenVacanciaModal}
										disabled={
											!contextoAplicado || disableActions
										}
										className='flex items-center gap-2 bg-white border border-[#BD504E] hover:bg-[#BD504E] hover:text-white  text-[#BD504E] text-[13px] font-semibold px-4 py-2 rounded-md transition-all duration-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed max-sm:w-full max-sm:justify-center'>
										Adicionar vacância
									</button>
								</div>
								{MOSTRAR_GERENCIAR_SOLICITACOES_UI && (
									<div className='flex justify-start sm:ml-auto sm:justify-end'>
										<button
											type='button'
											onClick={() =>
												navigate(
													'/escala-solicitacoes',
													{
														state: {
															escalaOrigem:
																modalidade ===
																'ENFERMAGEM'
																	? 'enfermagem'
																	: 'medica',
															modalidade,
															estabelecimento:
																contextoAplicado?.estabelecimento ||
																userCnes,
														},
													},
												)
											}
											disabled={
												!contextoAplicado?.estabelecimento
											}
											className='flex items-center gap-2 hover:bg-[#004A80] border-[2px] text-[#004A80] hover:text-white px-4 py-3  border-[#004A80]  transition-all duration-300 rounded-[7px] shadow-md text-[13px]'>
											<span className='flex items-center h-full text-[13px]  font-inter font-semibold'>
												Gerenciar Solicitações
											</span>
										</button>
									</div>
								)}
							</div>

							{/* Grade */}
							<div className='bg-white border border-gray-200 shadow-sm rounded-md overflow-x-auto font-inter'>
								<div
									className='flex items-stretch w-full'
									style={calendarTableMinWidthStyle}>
									<div className='flex-1 min-w-0'>
										{/* Header */}
										<div className='flex items-stretch border-b font-inter border-gray-200 '>
											<div
												className={`border-r border-gray-200 px-3 py-1 flex items-center justify-between gap-2 max-sm:px-2 ${isMobileProfessionalColumnOpen ? 'max-sm:justify-between' : 'max-sm:justify-center'} ${professionalColumnWidthClass}`}
												style={
													professionalColumnWidthStyle
												}>
												<span
													className={`text-[13px] font-semibold text-[#13335A] ${isMobileProfessionalColumnOpen ? '' : 'max-sm:hidden'}`}>
													Profissionais
												</span>
												<button
													type='button'
													aria-label={
														isMobileProfessionalColumnOpen
															? 'Retrair coluna de profissionais'
															: 'Expandir coluna de profissionais'
													}
													onClick={() =>
														setIsMobileProfessionalColumnOpen(
															(prev) => !prev,
														)
													}
													className='hidden max-sm:flex h-7 w-7 items-center justify-center bg-transparent text-[#13335A] transition hover:text-[#004A80]'>
													{isMobileProfessionalColumnOpen ? (
														<MdChevronLeft className='h-5 w-5' />
													) : (
														<MdChevronRight className='h-5 w-5' />
													)}
												</button>
											</div>
											<div className='flex-1 px-3 py-1 flex items-center'>
												<div className='flex-1 text-[13px] font-semibold text-[#13335A] capitalize'>
													{monthLabel}
												</div>
												<div className='flex-1 text-center text-[13px] font-semibold text-[#13335A]'>
													{setorLabel}
												</div>
												<div className='flex-1 flex items-center justify-end gap-2'>
													<button
														type='button'
														onClick={
															goToCurrentMonth
														}
														className='flex items-center gap-1 text-[11px] font-normal text-[#4F5257] border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50 transition'>
														<MdCalendarToday className='w-3.5 h-3.5 text-[#4F5257]' />
														Hoje
													</button>
													<button
														type='button'
														onClick={
															goToPreviousMonth
														}
														className=' rounded-full border border-gray-200 hover:bg-gray-50 transition text-[#374151]'>
														<MdChevronLeft className='w-5 h-5 text-[#13335A]' />
													</button>
													<button
														type='button'
														onClick={goToNextMonth}
														className=' rounded-full border border-gray-200 hover:bg-gray-50 transition text-[#374151]'>
														<MdChevronRight className='w-5 h-5 text-[#13335A]' />
													</button>
												</div>
											</div>
										</div>

										{/* Linha de dias */}
										<div className='flex items-stretch border-b  border-gray-200 min-h-[46px]'>
											<div
												className={`border-r border-gray-200 px-2 py-1 content-center items-center max-sm:px-1 ${professionalColumnWidthClass}`}
												style={
													professionalColumnWidthStyle
												}>
												<div
													className={`h-[26px] border border-gray-200 content-center rounded flex items-center px-1 gap-2 ${isMobileProfessionalColumnOpen ? '' : 'max-sm:hidden'}`}>
													<input
														type='text'
														value={searchTerm}
														onChange={(e) =>
															setSearchTerm(
																e.target.value,
															)
														}
														className='flex-1 h-[15px] bg-white outline-none text-[11px] text-[#13335A]'
													/>
													<button
														type='button'
														className='text-gray-300 hover:text-gray-600 transition scale-x-[-1]'>
														<IoIosSearch className='w-4 h-4 ' />
													</button>
												</div>
											</div>
											<div
												className='flex-1 grid'
												style={diasGridStyle}>
												{diaCalendario.map(
													(item, index) => {
														const isoDia =
															formatDateInputToISO(
																item?.data ||
																	'',
															);
														const diaNum =
															getCalendarDayNumber(
																item,
															);
														const diaSemana =
															getCalendarWeekdayShortPtBr(
																item,
															);
														const isHoje =
															isoDia ===
															todayISODateLocal;
														return (
															<div
																key={
																	isoDia ||
																	index
																}
																className={`relative flex items-center justify-center font-inter py-0.5 ${
																	isHoje
																		? ''
																		: 'font-light'
																}`}>
																{index <
																	diaCalendario.length -
																		1 && (
																	<span
																		aria-hidden='true'
																		className='absolute right-0 top-1/2 -translate-y-1/2 h-[55%] w-[1px] bg-gray-200'
																	/>
																)}
																<div className='flex flex-col items-center content-center'>
																	<span
																		className={`text-[13px] leading-tight ${
																			isHoje
																				? 'font-bold text-[#3a3b3b]'
																				: 'font-normal text-[#4F5257]'
																		}`}>
																		{diaNum}
																	</span>
																	<span
																		className={`text-[9px] leading-tight ${
																			isHoje
																				? 'font-bold text-[#3a3b3b]'
																				: 'font-medium text-[#6B7280]'
																		}`}>
																		{
																			diaSemana
																		}
																	</span>
																</div>
															</div>
														);
													},
												)}
											</div>
											<div
												aria-hidden='true'
												className='w-[14px] shrink-0 border-l border-gray-100 bg-white'
											/>
										</div>

										{/* Linhas com rolagem */}
										<div
											className='overflow-y-scroll [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-[14px] [&::-webkit-scrollbar-track]:bg-[#F8FAFC] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#CBD5E1]'
											style={{
												height: `${TABLE_BODY_HEIGHT}px`,
											}}>
											{loadingInicial || loadingAmbiente ? (
												<div
													className='flex min-h-full flex-col gap-3 px-4 py-4'
													style={{
														minHeight: `${TABLE_BODY_HEIGHT}px`,
													}}
													role='status'
													aria-label='Carregando ambiente assistido'>
													{Array.from({ length: 8 }).map((_, index) => (
														<div
															key={`skeleton-linha-${index}`}
															className='flex h-[42px] animate-pulse items-center gap-3 border-b border-gray-100 px-2 last:border-b-0'>
																			<span className='h-7 w-7 shrink-0 rounded-full bg-[#E7EEF5]' />
																			<span className='h-3 w-36 rounded bg-[#E7EEF5]' />
																			<span className='ml-auto h-3 w-2/5 rounded bg-[#F0F4F8]' />
																		</div>
																	))}
													</div>
											) : (
												<div
													className={`transform transition-[opacity,transform] duration-300 ease-out motion-reduce:transform-none motion-reduce:transition-none ${gradeProntaParaExibir ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`}>
													{linhasRender.map(
													(linha, rowIndex) => {
														const isPlaceholder =
															Boolean(
																linha?.placeholder,
															);
														const isSelectedRow =
															!isPlaceholder &&
															String(
																linha?.linha_id,
															) ===
																String(
																	selectedLinhaId,
																);

														return (
															<div
																key={
																	linha?.linha_id ||
																	rowIndex
																}
																className='flex items-stretch border-b border-gray-100 last:border-b-0'
																style={{
																	height: `${TABLE_ROW_HEIGHT}px`,
																}}>
																<div
																	className={`border-r border-gray-200 px-1 flex items-center max-sm:justify-center ${isSelectedRow ? 'text-[20px]' : ''} ${professionalColumnWidthClass}`}
																	style={
																		professionalColumnWidthStyle
																	}>
																	{!isPlaceholder ? (
																		<div
																			className={`w-full flex items-center gap-3 px-1 ${isMobileProfessionalColumnOpen ? '' : 'max-sm:justify-center'}`}>
																			<div className='min-w-0 flex flex-1 items-center gap-2'>
																				{(() => {
																					const linhaShiftIndicator =
																						getLinhaShiftIndicatorMajority(
																							linha,
																						);
																					const linhaShiftTextClass =
																						linhaShiftIndicator ===
																						'24h'
																							? 'text-[13.5px]'
																							: 'text-[15px]';

																					return (
																						<>
																							<span
																								className={`p-2 h-[30px] w-[30px] text-center text-white rounded-full ${linhaShiftTextClass} font-semibold flex items-center justify-center ${isMobileProfessionalColumnOpen ? '' : 'max-sm:hidden'} ${
																									linha?.tipo_item ===
																									'VACANCIA'
																										? 'bg-[#BD504E]'
																										: 'bg-[#4D6C92]'
																								}`}
																								aria-hidden='true'>
																								{
																									linhaShiftIndicator
																								}
																							</span>
																							{!isMobileProfessionalColumnOpen && (
																								<button
																									type='button'
																									aria-label={
																										isVacanciaLinha(
																											linha,
																										)
																											? 'Visualizar vacância'
																											: 'Editar profissional'
																									}
																									onClick={() =>
																										handleOpenEditProfissionalModal(
																											linha,
																										)
																									}
																									className={`hidden max-sm:flex p-2 h-[30px] w-[30px] text-center text-white rounded-full ${linhaShiftTextClass} font-semibold items-center justify-center transition hover:scale-105 hover:ring-2 hover:ring-[#004A80]/30 focus:outline-none focus:ring-2 focus:ring-[#004A80]/40 disabled:opacity-60 disabled:cursor-not-allowed ${
																										linha?.tipo_item ===
																										'VACANCIA'
																											? 'bg-[#BD504E]'
																											: 'bg-[#4D6C92]'
																									}`}>
																									{
																										linhaShiftIndicator
																									}
																								</button>
																							)}
																						</>
																					);
																				})()}
																				<div
																					className={`min-w-0 flex flex-col leading-tight ${isMobileProfessionalColumnOpen ? '' : 'max-sm:hidden'}`}>
																					<span className='text-[12px] font-semibold text-[#13335A] truncate'>
																						{getLinhaDisplayName(
																							linha,
																						)}
																					</span>
																					<span className='text-[10px] font-medium text-[#6B7280] truncate'>
																						{getDimensionLabel(
																							linha,
																							modalidade,
																						)}
																					</span>
																				</div>
																			</div>

																			<div
																				className={`relative ml-auto h-9 w-9 shrink-0 ${isMobileProfessionalColumnOpen ? '' : 'max-sm:hidden'}`}>
																				{(() => {
																					const actionButtonClass =
																						'w-4 h-4 rounded-[3px] border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed';
																					const actionPositionClasses =
																						[
																							'top-0 right-0',
																							'bottom-0 right-0',
																							'bottom-0 left-0',
																							'top-0 left-0',
																						];
																					const actions =
																						[
																							{
																								key: 'delete',
																								render: (
																									<button
																										type='button'
																										aria-label={
																											isVacanciaLinha(
																												linha,
																											)
																												? 'Excluir vacância'
																												: 'Excluir profissional'
																										}
																										onClick={() =>
																											handleOpenDeleteModal(
																												linha,
																											)
																										}
																										disabled={
																											disableActions
																										}
																										className={
																											actionButtonClass
																										}>
																										<MdDeleteOutline className='w-4 h-4 text-[#BD504E]' />
																									</button>
																								),
																							},
																							{
																								key: 'edit',
																								render: (
																									<button
																										type='button'
																										aria-label={
																											isVacanciaLinha(
																												linha,
																											)
																												? 'Visualizar vacância'
																												: 'Editar profissional'
																										}
																										onClick={() =>
																											handleOpenEditProfissionalModal(
																												linha,
																											)
																										}
																										className={
																											actionButtonClass
																										}>
																										{isVacanciaLinha(
																											linha,
																										) ? (
																											<MdVisibility className='w-3 h-3 text-[#004A80]' />
																										) : (
																											<MdOutlineEdit className='w-4 h-4 text-[#004A80]' />
																										)}
																									</button>
																								),
																							},
																							...(isSetorTodosAplicado &&
																							!isVacanciaLinha(
																								linha,
																							)
																								? [
																										{
																											key: 'turnos',
																											render: (
																												<button
																													type='button'
																													aria-label='Gerenciamento de turnos'
																													onClick={() =>
																														handleOpenGerenciamentoTurnosModal(
																															linha,
																														)
																													}
																													className={
																														actionButtonClass
																													}>
																													<MdSettings className='w-3.5 h-3.5 text-[#004A80]' />
																												</button>
																											),
																										},
																									]
																								: []),
																						];

																					if (
																						actions.length ===
																						1
																					) {
																						return (
																							<div className='absolute right-0 top-1/2 -translate-y-1/2'>
																								{
																									actions[0]
																										.render
																								}
																							</div>
																						);
																					}

																					return actions
																						.slice(
																							0,
																							4,
																						)
																						.map(
																							(
																								action,
																								actionIndex,
																							) => (
																								<div
																									key={
																										action.key
																									}
																									className={`absolute ${actionPositionClasses[actionIndex]}`}>
																									{
																										action.render
																									}
																								</div>
																							),
																						);
																				})()}
																			</div>
																		</div>
																	) : (
																		<div className='w-full h-full' />
																	)}
																</div>
																<div
																	className='flex-1 grid'
																	style={
																		diasGridStyle
																	}>
																	{diaCalendario.map(
																		(
																			dia,
																			colIndex,
																		) => {
																			const celula =
																				!isPlaceholder
																					? (
																							linha?.celulas ||
																							[]
																						).find(
																							(
																								item,
																							) =>
																								String(
																									item?.data,
																								) ===
																								String(
																									dia?.data,
																								),
																						) ||
																						null
																					: null;
																			const selected =
																				!isPlaceholder &&
																				String(
																					selectedLinhaId,
																				) ===
																					String(
																						linha?.linha_id,
																					) &&
																				String(
																					selectedDiaIso,
																				) ===
																					String(
																						dia?.data,
																					);
																			const visualClass =
																				celula
																					? getCellVisualClass(
																							celula,
																							linha,
																							modalidade,
																						)
																					: EMPTY_CELL_CLASS;
																			const hasPersistedCellCard =
																				hasPersistedCard(
																					celula ||
																						{},
																				);
																			const isCelulaLegada =
																				isCelulaLegadaRenderizavel(
																					{
																						celula:
																							celula ||
																							{},
																						linha,
																						modalidade,
																					},
																				);
																			const shiftText =
																				getCellShiftText(
																					celula ||
																						{},
																					linha,
																				);
																			const cellShiftTextClass =
																				shiftText ===
																				'24h'
																					? 'text-[13.5px]'
																					: 'text-[15px]';

																			return (
																				<div
																					key={`${dia?.data || colIndex}-${linha?.linha_id || rowIndex}`}
																					className='h-full w-full items-center content-center'>
																					<button
																						type='button'
																						onClick={() => {
																							// Ocorrências legadas são exibidas para manter o
																							// histórico da escala, mas não abrem o painel de
																							// edição/exclusão.
																							if (
																								!hasPersistedCellCard
																							)
																								return;
																							handleClickCell(
																								linha,
																								celula || {
																									data: dia?.data,
																								},
																								rowIndex,
																							);
																						}}
																						disabled={
																							isPlaceholder ||
																							!hasPersistedCellCard
																						}
																						className={`h-full w-full max-h-[30px] max-w-[30px] rounded-[4px] mx-auto ${visualClass} ${
																							selected
																								? 'ring-2 ring-[#0fc2f8]'
																								: ''
																						} ${cellShiftTextClass} font-normal text-white flex items-center justify-center disabled:cursor-default ${
																							isCelulaLegada
																								? 'opacity-75'
																								: ''
																						}`}>
																						{
																							shiftText
																						}
																					</button>
																				</div>
																			);
																		},
																	)}
																</div>
															</div>
														);
													},
													)}
												</div>
											)}
										</div>

										{/* Legenda */}
										<div className='flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 py-3 border-t border-gray-200 bg-white'>
											{legendaItems.map(
												({ cor, label }) => (
													<div
														key={label}
														className='flex items-center gap-1.5'>
														<span
															className={`w-3 h-3 rounded-[2px] inline-block ${cor}`}
														/>
														<span className='text-[10px] text-[#6B7280]'>
															{label}
														</span>
													</div>
												),
											)}
											{LEGENDA_TEXTO.map(
												({ sigla, label }) => (
													<div
														key={label}
														className='flex items-center gap-1'>
														<div className='text-[10px] font-normal px-1 h-[15px] text-[#4F5257]  border rounded-sm  flex items-center justify-center'>
															{sigla}
														</div>
														<span className='text-[10px] text-[#6B7280]'>
															{label}
														</span>
													</div>
												),
											)}
										</div>
									</div>

									{/* Aba de edição (direita) */}
									<div
										className='relative min-w-0 overflow-hidden bg-white border-l border-gray-200'
										style={{
											width: isEditPanelOpen
												? `${EDIT_PANEL_WIDTH}px`
												: '0px',
											transition: 'width 280ms ease',
											pointerEvents: isEditPanelOpen
												? 'auto'
												: 'none',
										}}
										aria-hidden={!isEditPanelOpen}>
										<div
											className='h-full flex flex-col px-3 py-2 gap-1'
											style={{
												width: `${EDIT_PANEL_WIDTH}px`,
												transform: isEditPanelOpen
													? 'translateX(0)'
													: 'translateX(40px)',
												opacity: isEditPanelOpen
													? 1
													: 0,
												transition:
													'transform 280ms ease, opacity 180ms ease',
											}}>
											<div className='flex flex-col'>
												<div className='flex items-center  justify-between font-inter  border-gray-200'>
													<div className='text-[13px] font-medium text-[#13335A]'>
														Editar dia{' '}
														{selectedDayLabel.split(
															'/',
														)[0] || '--'}
													</div>
													<button
														type='button'
														onClick={
															handleCloseEditPanel
														}
														aria-label='Fechar edição'
														className=' rounded-md text-xl leading-none  text-gray-300 hover:text-gray-600  transition flex items-center justify-center'>
														&times;
													</button>
												</div>
												<div className=''>
													<div
														title={currentEditName}
														className='max-w-[150px] bg-white text-[#13335A] font-medium rounded-md text-[13px] flex justify-start outline-none whitespace-nowrap overflow-hidden text-ellipsis'>
														{currentEditName}
													</div>
													<div className='text-[10px] text-[#b9bec7] landing-0'>
														Lembre-se de selecionar
														o dia inicial e depois
														editar
													</div>
												</div>
											</div>
											<div className='flex-1 '>
												<div className='space-y-3'>
													<div>
														<label className='block text-[11px] font-medium text-[#4F5257] mb-1'>
															Regime
														</label>
														<div
															className={`w-full h-[30px] border border-gray-200 rounded-md px-2 text-[12px] outline-none flex items-center ${getLockedFieldClassName(true)}`}>
															{panelRegimeDisplay}
														</div>
													</div>

													<div>
														<label className='block text-[11px] font-medium text-[#4F5257] mb-1'>
															Turno
														</label>
														<div
															className={`w-full h-[30px] border border-gray-200 rounded-md px-2 text-[12px] outline-none flex items-center ${getLockedFieldClassName(true)}`}>
															{panelTurnoDisplay}
														</div>
													</div>

													<div className='space-y-2 pt-1'>
														{[
															{
																key: 'ferias',
																label: 'Férias',
																cor: 'bg-[#6ACEF8]',
																corAtiva:
																	'bg-[#56BFEF]',
															},
															{
																key: 'atestado',
																label: 'Atestado',
																cor: 'bg-[#FB965C]',
																corAtiva:
																	'bg-[#F28747]',
															},
															{
																key: 'folga',
																label: 'Folga',
																cor: 'bg-[#8BBD8B]',
																corAtiva:
																	'bg-[#73AF73]',
															},
															{
																key: 'licenca',
																label: 'Licença',
																cor: 'bg-[#A476C7]',
																corAtiva:
																	'bg-[#8E5FB7]',
															},
															{
																key: 'troca',
																label: 'Troca',
																cor: 'bg-[#967771]',
																corAtiva:
																	'bg-[#7F615C]',
															},
															{
																key: 'falta',
																label: 'Falta',
																cor: 'bg-[#545454]',
																corAtiva:
																	'bg-[#4B4B4B]',
															},
														].map(
															({
																key,
																label,
																cor,
																corAtiva,
															}) => (
																<div
																	key={key}
																	className={
																		openMiniTabs[
																			key
																		]
																			? 'space-y-0'
																			: 'space-y-1'
																	}>
																	<button
																		type='button'
																		onClick={() => {
																			if (
																				key ===
																					'troca' &&
																				(isTrocaExistenteSelecionada ||
																					trocaBloqueadaPorConsulta)
																			) {
																				return;
																			}
																			if (
																				key ===
																					'troca' &&
																				!openMiniTabs.troca
																			) {
																				setTrocaConsultaErro(
																					null,
																				);
																				setIsLoadingTrocaConsulta(
																					true,
																				);
																			}
																			toggleMiniTab(
																				key,
																			);
																			handlePanelInput(
																				'acao',
																				key ===
																					'troca'
																					? 'REGISTRAR_TROCA'
																					: 'REGISTRAR_AUSENCIA',
																			);
																			if (
																				key !==
																				'troca'
																			) {
																				const tipoMap =
																					{
																						ferias: 'FERIAS',
																						atestado:
																							'ATESTADO',
																						folga: 'FOLGA',
																						licenca:
																							'LICENCA',
																						falta: 'FALTA',
																					};
																				handlePanelInput(
																					'tipo_item',
																					tipoMap[
																						key
																					],
																				);
																			}
																		}}
																		aria-disabled={
																			key ===
																				'troca' &&
																			(isTrocaExistenteSelecionada ||
																				trocaBloqueadaPorConsulta)
																		}
																		disabled={
																			key ===
																				'troca' &&
																			(isTrocaExistenteSelecionada ||
																				trocaBloqueadaPorConsulta)
																		}
																		title={
																			key ===
																				'troca' &&
																			isTrocaExistenteSelecionada
																				? 'Troca já efetivada'
																				: key ===
																							'troca' &&
																					  trocaBloqueadaPorConsulta
																					? trocaConsultaMensagemErro
																					: undefined
																		}
																		className={`h-[26px] px-2 flex items-center justify-between w-full text-white transition-all duration-200 ${
																			openMiniTabs[
																				key
																			]
																				? 'rounded-t-md rounded-b-none'
																				: 'rounded-md'
																		} ${
																			openMiniTabs[
																				key
																			] ||
																			(key ===
																				'troca' &&
																				(isTrocaExistenteSelecionada ||
																					trocaBloqueadaPorConsulta))
																				? `${corAtiva} shadow-inner ring-1 ring-black/10`
																				: cor
																		}`}>
																		<span className='text-white text-[11px] font-semibold'>
																			{
																				label
																			}
																		</span>
																		<span
																			className={`w-[26px] h-[14px] rounded-full flex items-center px-[2px] transition-colors duration-200 ${
																				openMiniTabs[
																					key
																				] ||
																				(key ===
																					'troca' &&
																					isTrocaExistenteSelecionada)
																					? 'justify-end bg-[#13335A]'
																					: 'justify-start bg-white/40'
																			}`}>
																			<span className='w-[10px] h-[10px] rounded-full bg-white' />
																		</span>
																	</button>

																	<div
																		className='grid overflow-hidden'
																		style={{
																			gridTemplateRows:
																				openMiniTabs[
																					key
																				]
																					? '1fr'
																					: '0fr',
																			transition:
																				'grid-template-rows 320ms cubic-bezier(0.22, 1, 0.36, 1)',
																			pointerEvents:
																				openMiniTabs[
																					key
																				]
																					? 'auto'
																					: 'none',
																		}}>
																		<div className='min-h-0 overflow-hidden'>
																			<div className='border border-gray-200 rounded-b-md rounded-t-none bg-white px-2 py-2 space-y-1.5'>
																				{key ===
																				'falta' ? (
																					<div className='text-[10px] text-[#666] leading-tight'>
																						Clique
																						em
																						Enviar
																						para
																						registrar
																						a
																						falta
																						do
																						profissional
																						selecionado.
																					</div>
																				) : key ===
																				  'troca' ? (
																					<>
																						{isTrocaExistenteSelecionada ? (
																							<div className='space-y-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-[10px] text-gray-600'>
																								<div>
																									<span className='block text-[9px] uppercase tracking-wide text-gray-400'>
																										Origem
																									</span>
																									<span className='block truncate'>
																										{
																											trocaExistenteResumo?.origem
																										}
																									</span>
																								</div>
																								<div>
																									<span className='block text-[9px] uppercase tracking-wide text-gray-400'>
																										Destino
																									</span>
																									<span className='block truncate'>
																										{
																											trocaExistenteResumo?.destino
																										}
																									</span>
																								</div>
																								<div className='grid grid-cols-2 gap-2'>
																									<div>
																										<span className='block text-[9px] uppercase tracking-wide text-gray-400'>
																											Data
																										</span>
																										<span className='block truncate'>
																											{
																												trocaExistenteResumo?.data
																											}
																										</span>
																									</div>
																									<div>
																										<span className='block text-[9px] uppercase tracking-wide text-gray-400'>
																											Carga
																										</span>
																										<span className='block truncate'>
																											{
																												trocaExistenteResumo?.carga
																											}
																										</span>
																									</div>
																								</div>
																								{trocaExistenteResumo?.observacao && (
																									<div>
																										<span className='block text-[9px] uppercase tracking-wide text-gray-400'>
																											Observação
																										</span>
																										<span className='block whitespace-pre-wrap break-words'>
																											{
																												trocaExistenteResumo.observacao
																											}
																										</span>
																									</div>
																								)}
																							</div>
																						) : isLoadingTrocaConsulta ? (
																							<div className='flex min-h-[108px] items-center justify-center gap-2 text-[10px] text-[#64748B]'>
																								<MdAutorenew className='h-4 w-4 animate-spin text-[#004A80]' />
																								<span>
																									Consultando
																									opções
																									de
																									troca...
																								</span>
																							</div>
																						) : trocaBloqueadaPorConsulta ? (
																							<div
																								role='alert'
																								className='rounded-md border border-[#F1B6B2] bg-[#FFF5F5] px-2.5 py-2 text-[10px] leading-relaxed text-[#B42318]'>
																								{
																									trocaConsultaMensagemErro
																								}
																							</div>
																						) : (
																							<>
																								<div className='flex flex-col gap-3'>
																									<div>
																										<label className='mb-1 block text-[8.5px] font-medium text-[#7B8798]'>
																											Trocar
																											com
																											este:
																										</label>
																										<div className='relative'>
																											<input
																												ref={
																													trocaProfissionalInputRef
																												}
																												type='text'
																												value={
																													trocaProfissionalSearchTerm
																												}
																												onChange={(
																													e,
																												) => {
																													const value =
																														e
																															.target
																															.value;
																													setTrocaProfissionalSearchTerm(
																														value,
																													);
																													setShowTrocaProfissionalSuggestions(
																														true,
																													);
																													const selectedOption =
																														trocaProfissionalOptions.find(
																															(
																																item,
																															) =>
																																String(
																																	item.value,
																																) ===
																																String(
																																	panelForm.troca_profissional_id,
																																),
																														);
																													if (
																														selectedOption &&
																														value !==
																															selectedOption.label
																													)
																														handlePanelInput(
																															'troca_profissional_id',
																															'',
																														);
																												}}
																												onFocus={() => {
																													updateTrocaProfissionalSuggestionsPosition();
																													setShowTrocaProfissionalSuggestions(
																														true,
																													);
																												}}
																												onBlur={() =>
																													setTimeout(
																														() =>
																															setShowTrocaProfissionalSuggestions(
																																false,
																															),
																														120,
																													)
																												}
																												disabled={
																													isLoadingTrocaConsulta ||
																													!currentTrocaOrigemItemId
																												}
																												placeholder={
																													isLoadingTrocaConsulta
																														? 'Carregando profissionais...'
																														: 'Selecione ou busque um profissional'
																												}
																												className='h-[30px] w-full rounded-md border border-[#D8DEE8] bg-white px-2 text-[10px] text-[#334155] shadow-sm outline-none transition placeholder:text-[#94A3B8] focus:border-[#8CB3D3] focus:ring-1 focus:ring-[#CFE0EF] disabled:bg-[#F8FAFC] disabled:text-[#94A3B8]'
																											/>

																											{showTrocaProfissionalSuggestions &&
																												trocaProfissionalSuggestionsPosition &&
																												typeof document !==
																													'undefined' &&
																												createPortal(
																													<div
																														className='fixed z-[1000] max-h-[180px] overflow-y-auto rounded-md border border-[#D8DEE8] bg-white py-1 shadow-lg'
																														style={
																															trocaProfissionalSuggestionsPosition
																														}>
																														{trocaProfissionalOptionsFiltradas.length >
																														0 ? (
																															trocaProfissionalOptionsFiltradas.map(
																																(
																																	item,
																																) => (
																																	<button
																																		type='button'
																																		key={
																																			item.value
																																		}
																																		onMouseDown={(
																																			e,
																																		) => {
																																			e.preventDefault();
																																			setTrocaProfissionalSearchTerm(
																																				item.label,
																																			);
																																			setShowTrocaProfissionalSuggestions(
																																				false,
																																			);
																																			handlePanelInput(
																																				'troca_profissional_id',
																																				item.value,
																																			);
																																		}}
																																		className='block w-full px-2 py-1.5 text-left text-[10px] text-[#334155] transition hover:bg-[#F3F4F6]'>
																																		{
																																			item.label
																																		}
																																	</button>
																																),
																															)
																														) : (
																															<div className='px-2 py-1.5 text-[10px] text-[#94A3B8]'>
																																Nenhum
																																profissional
																																encontrado
																															</div>
																														)}
																													</div>,
																													document.body,
																												)}
																										</div>
																									</div>
																									<div>
																										<label className='mb-1 block text-[8.5px] font-medium text-[#7B8798]'>
																											Data
																										</label>
																										<select
																											value={
																												panelForm.troca_data
																											}
																											onChange={(
																												e,
																											) =>
																												handlePanelInput(
																													'troca_data',
																													e
																														.target
																														.value,
																												)
																											}
																											disabled={
																												isLoadingTrocaConsulta ||
																												!panelForm.troca_profissional_id
																											}
																											className='h-[30px] w-full rounded-md border border-[#D8DEE8] bg-white px-2 text-[10px] text-[#334155] shadow-sm outline-none transition focus:border-[#8CB3D3] focus:ring-1 focus:ring-[#CFE0EF] disabled:bg-[#F8FAFC] disabled:text-[#94A3B8]'>
																											<option value=''>
																												{isLoadingTrocaConsulta
																													? 'Carregando datas...'
																													: panelForm.troca_profissional_id
																														? trocaDataOptions.length >
																															0
																															? 'Todas as datas'
																															: 'Nenhuma data compatível com esta troca'
																														: 'Selecione primeiro o profissional'}
																											</option>
																											{trocaDataOptions.map(
																												(
																													item,
																												) => (
																													<option
																														key={
																															item.value
																														}
																														disabled={
																															item.disabled
																														}
																														title={
																															item.error
																														}
																														value={
																															item.value
																														}>
																														{
																															item.label
																														}
																													</option>
																												),
																											)}
																										</select>
																									</div>
																									<div
																										className={
																											trocaTurnoOptions.length ===
																											1
																												? 'hidden'
																												: 'order-4'
																										}>
																										<label className='mb-1 block text-[8.5px] font-medium text-[#7B8798]'>
																											Selecione
																											o
																											turno
																										</label>
																										<select
																											value={
																												panelForm.troca_turno_id
																											}
																											onChange={(
																												e,
																											) => {
																												const selectedOption =
																													trocaTurnoOptions.find(
																														(
																															item,
																														) =>
																															String(
																																item.value,
																															) ===
																															String(
																																e
																																	.target
																																	.value,
																															),
																													);
																												setPanelForm(
																													(
																														prev,
																													) => ({
																														...prev,
																														troca_turno_id:
																															e
																																.target
																																.value,
																														item_destino_id:
																															e
																																.target
																																.value,
																														troca_perfil:
																															'',
																														troca_recorte_item_id:
																															'',
																														troca_recorte_inicio:
																															'',
																														troca_recorte_fim:
																															'',
																														troca_data:
																															selectedOption?.data ||
																															prev.troca_data,
																													}),
																												);
																												setPanelErrors(
																													(
																														prev,
																													) => ({
																														...prev,
																														troca_turno_id:
																															'',
																													}),
																												);
																											}}
																											disabled={
																												isLoadingTrocaConsulta ||
																												!panelForm.troca_profissional_id
																											}
																											className='h-[30px] w-full rounded-md border border-[#D8DEE8] bg-white px-2 text-[10px] text-[#334155] shadow-sm outline-none transition focus:border-[#8CB3D3] focus:ring-1 focus:ring-[#CFE0EF] disabled:bg-[#F8FAFC] disabled:text-[#94A3B8]'>
																											<option value=''>
																												{isLoadingTrocaConsulta
																													? 'Carregando plantões...'
																													: panelForm.troca_profissional_id
																														? trocaTurnoOptions.length >
																															0
																															? 'Selecione'
																															: 'Sem plantões disponíveis'
																														: 'Selecione primeiro o profissional'}
																											</option>
																											{trocaTurnoOptions.map(
																												(
																													item,
																												) => (
																													<option
																														key={
																															item.value
																														}
																														disabled={
																															item.disabled
																														}
																														title={
																															item.error
																														}
																														value={
																															item.value
																														}>
																														{
																															item.label
																														}
																													</option>
																												),
																											)}
																										</select>
																									</div>
																									{trocaDestinoResolvidoId &&
																										trocaPodeEscolherPerfil && (
																											<div className='order-3 space-y-1.5'>
																												<div className='flex items-center gap-1 text-[8.5px] font-medium text-[#7B8798]'>
																													<span>
																														Perfil
																														da
																														troca
																													</span>
																													<TrocaPerfilInfo />
																												</div>
																												<div className='px-1 grid grid-cols-1 gap-0.5'>
																													<label
																														className={`flex cursor-pointer items-center gap-1.5 py-0.5 text-[10px] leading-tight transition ${
																															panelForm.troca_perfil ===
																															'COMPENSAR'
																																? 'text-[#004A80]'
																																: 'text-[#64748B] hover:text-[#334155]'
																														}`}>
																														<input
																															type='radio'
																															name='perfil-troca'
																															checked={
																																panelForm.troca_perfil ===
																																'COMPENSAR'
																															}
																															onChange={() =>
																																handleTrocaPerfilChange(
																																	'COMPENSAR',
																																)
																															}
																															className='h-2.5 w-2.5 accent-[#004A80]'
																														/>
																														<span>
																															Trocar
																															os
																															dias
																														</span>
																													</label>
																													<label
																														className={`flex cursor-pointer items-center gap-1.5 py-0.5 text-[10px] leading-tight transition ${
																															panelForm.troca_perfil ===
																															'RECORTE'
																																? 'text-[#004A80]'
																																: 'text-[#64748B] hover:text-[#334155]'
																														}`}>
																														<input
																															type='radio'
																															name='perfil-troca'
																															checked={
																																panelForm.troca_perfil ===
																																'RECORTE'
																															}
																															onChange={() =>
																																handleTrocaPerfilChange(
																																	'RECORTE',
																																)
																															}
																															className='h-2.5 w-2.5 accent-[#004A80]'
																														/>
																														<span>
																															Dividir
																															plantão
																														</span>
																													</label>
																												</div>
																												{panelForm.troca_perfil ===
																													'RECORTE' && (
																													<>
																														<div className='mt-1.5'>
																															<span className='text-[8.5px] font-medium text-[#7B8798]'>
																																Selecione
																																o
																																turno
																															</span>
																														</div>
																														<select
																															value={
																																trocaRecorteOptions.find(
																																	(
																																		option,
																																	) =>
																																		String(
																																			option.itemId,
																																		) ===
																																			String(
																																				panelForm.troca_recorte_item_id ||
																																					'',
																																			) &&
																																		String(
																																			option.inicio,
																																		) ===
																																			String(
																																				panelForm.troca_recorte_inicio ||
																																					'',
																																			) &&
																																		String(
																																			option.fim,
																																		) ===
																																			String(
																																				panelForm.troca_recorte_fim ||
																																					'',
																																			),
																																)
																																	?.value ||
																																''
																															}
																															onChange={(
																																e,
																															) => {
																																const selected =
																																	trocaRecorteOptions.find(
																																		(
																																			option,
																																		) =>
																																			String(
																																				option.value,
																																			) ===
																																			String(
																																				e
																																					.target
																																					.value,
																																			),
																																	);
																																setPanelForm(
																																	(
																																		prev,
																																	) => ({
																																		...prev,
																																		troca_recorte_item_id:
																																			selected?.itemId ||
																																			'',
																																		troca_recorte_inicio:
																																			selected?.inicio ||
																																			'',
																																		troca_recorte_fim:
																																			selected?.fim ||
																																			'',
																																	}),
																																);
																																setPanelErrors(
																																	(
																																		prev,
																																	) => ({
																																		...prev,
																																		form: '',
																																	}),
																																);
																															}}
																															className='mt-1 h-[30px] w-full rounded-md border border-[#D8DEE8] bg-white px-2 text-[10px] text-[#334155] shadow-sm outline-none transition focus:border-[#8CB3D3] focus:ring-1 focus:ring-[#CFE0EF]'>
																															<option value=''>
																																Selecione
																																a
																																faixa
																															</option>
																															{trocaRecorteOptions.map(
																																(
																																	option,
																																) => (
																																	<option
																																		key={
																																			option.value
																																		}
																																		value={
																																			option.value
																																		}>
																																		{`${option.label}${option.source === 'sugestao' ? ' · sugestão' : ''}`}
																																	</option>
																																),
																															)}
																														</select>
																													</>
																												)}
																											</div>
																										)}
																								</div>
																								{Boolean(
																									trocaDestinoResolvidoId,
																								) &&
																									(panelForm.troca_perfil ===
																										'COMPENSAR' ||
																										(!trocaPodeEscolherPerfil &&
																											!isLoadingTrocaAvaliacao)) &&
																									(trocaAvaliacao
																										?.saldo
																										?.equivalente ===
																										false ||
																										trocaItensComplementares.length >
																											0) && (
																										<div>
																											<label className='block text-[10px] text-[#666] mb-0.5'>
																												Complemento
																											</label>
																											{trocaItensComplementares.map(
																												(
																													item,
																												) => (
																													<div
																														key={`${item.lado}-${item.item_id}`}
																														className='mb-1 flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1 py-0.5 text-[9px] text-gray-500'>
																														<span className='min-w-0 flex-1 truncate'>
																															{item.label ||
																																`Card ${item.item_id}`}
																														</span>
																														<button
																															type='button'
																															onClick={() => {
																																setTrocaItensComplementares(
																																	(
																																		prev,
																																	) =>
																																		prev.filter(
																																			(
																																				selected,
																																			) =>
																																				selected.item_id !==
																																				item.item_id,
																																		),
																																);
																															}}
																															aria-label='Remover complemento'
																															className='text-[13px] leading-none text-[#BD504E]'>
																															×
																														</button>
																													</div>
																												),
																											)}
																											<select
																												value=''
																												onChange={(
																													e,
																												) =>
																													handleAdicionarTrocaComplemento(
																														e
																															.target
																															.value,
																													)
																												}
																												disabled={
																													isLoadingTrocaAvaliacao ||
																													!possuiTrocaComplementoDisponivel
																												}
																												className='w-full h-[22px] border border-gray-200 rounded-md px-1 text-[10px] outline-none text-gray-500 disabled:bg-gray-100 disabled:text-gray-400'>
																												<option value=''>
																													Selecione
																												</option>
																												{trocaComplementoOptions.map(
																													(
																														item,
																													) => (
																														<option
																															key={
																																item.value
																															}
																															value={
																																item.value
																															}
																															disabled={
																																item.disabled
																															}
																															title={
																																item.error
																															}>
																															{
																																item.label
																															}
																														</option>
																													),
																												)}
																											</select>
																											{trocaAvaliacao
																												?.saldo
																												?.equivalente ===
																												false &&
																												(!possuiTrocaComplementoDisponivel &&
																												!isLoadingTrocaAvaliacao ? (
																													<p className='mt-1 text-[9px] font-medium leading-tight text-[#BD504E]'>
																														Não
																														existem
																														horários
																														complementares
																														disponíveis.
																													</p>
																												) : (
																													<p className='mt-1 text-[9px] leading-tight text-[#6B7280]'>
																														Saldo:{' '}
																														{Math.abs(
																															Number(
																																trocaAvaliacao
																																	?.saldo
																																	?.diferenca_minutos ||
																																	0,
																															),
																														) /
																															60}{' '}
																														h
																														pendentes
																													</p>
																												))}
																										</div>
																									)}
																								{trocaAvaliacaoValida && (
																									<p className='text-[9px] font-medium text-[#3F7D53]'>
																										Carga
																										horária
																										equivalente.
																									</p>
																								)}
																								{trocaAvaliacaoMensagemErro && (
																									<p className='text-[9px] leading-tight text-[#BD504E]'>
																										{
																											trocaAvaliacaoMensagemErro
																										}
																									</p>
																								)}
																							</>
																						)}
																					</>
																				) : (
																					<>
																						<div>
																							<label className='block text-[10px] text-[#666] mb-0.5'>
																								Data
																								início
																							</label>
																							<input
																								type='text'
																								placeholder='00/00/0000'
																								value={
																									panelForm.periodo_inicio
																								}
																								onChange={(
																									e,
																								) =>
																									handlePanelInput(
																										'periodo_inicio',
																										e
																											.target
																											.value,
																									)
																								}
																								className='w-full h-[22px] border border-gray-200 rounded-md px-1 text-[10px] outline-none text-gray-500'
																							/>
																						</div>
																						<div>
																							<label className='block text-[10px] text-[#666] mb-0.5'>
																								Data
																								fim
																							</label>
																							<input
																								type='text'
																								placeholder='00/00/0000'
																								value={
																									panelForm.periodo_fim
																								}
																								onChange={(
																									e,
																								) =>
																									handlePanelInput(
																										'periodo_fim',
																										e
																											.target
																											.value,
																									)
																								}
																								className='w-full h-[22px] border border-gray-200 rounded-md px-1 text-[10px] outline-none text-gray-500'
																							/>
																						</div>
																						{key !==
																							'ferias' && (
																							<div>
																								<label className='block text-[10px] text-[#666] mb-0.5'>
																									Observação
																								</label>
																								<input
																									type='text'
																									value={
																										panelForm.observacao
																									}
																									onChange={(
																										e,
																									) =>
																										handlePanelInput(
																											'observacao',
																											e
																												.target
																												.value,
																										)
																									}
																									className='w-full h-[22px] border border-gray-200 rounded-md px-1 text-[10px] outline-none text-gray-500'
																								/>
																							</div>
																						)}
																					</>
																				)}
																			</div>
																		</div>
																	</div>
																</div>
															),
														)}
													</div>

													{(panelErrors.form ||
														(submitErrorScope ===
															'panel' &&
															submitError)) && (
														<div className='text-[10px] text-[#dc2626]'>
															{panelErrors.form ||
																submitError}
														</div>
													)}

													<div
														className={
															openMiniTabs.excluir
																? 'space-y-0'
																: 'space-y-1'
														}>
														<button
															type='button'
															onClick={() => {
																toggleMiniTab(
																	'excluir',
																);
																handlePanelInput(
																	'acao',
																	'REMOVER_DIA',
																);
															}}
															className={`w-full h-[24px] text-white text-[11px] font-semibold shadow-sm transition-all duration-200 ${
																openMiniTabs.excluir
																	? 'rounded-t-md rounded-b-none'
																	: 'rounded-md'
															} ${
																openMiniTabs.excluir
																	? 'bg-[#A63F3D] shadow-inner ring-1 ring-black/10'
																	: 'bg-[#BD504E]'
															}`}>
															EXCLUIR
														</button>

														<div
															className='grid overflow-hidden'
															style={{
																gridTemplateRows:
																	openMiniTabs.excluir
																		? '1fr'
																		: '0fr',
																transition:
																	'grid-template-rows 320ms cubic-bezier(0.22, 1, 0.36, 1)',
																pointerEvents:
																	openMiniTabs.excluir
																		? 'auto'
																		: 'none',
															}}>
															<div className='min-h-0 overflow-hidden'>
																<div className='border border-gray-200 rounded-b-md rounded-t-none bg-white px-2 py-2 space-y-1.5'>
																	<p className='text-[10px] text-[#666] leading-tight font-medium'>
																		Como
																		deseja
																		excluir
																		o
																		profissional?
																	</p>
																	<select
																		value={
																			panelForm.delete_mode
																		}
																		onChange={(
																			e,
																		) => {
																			handlePanelInput(
																				'delete_mode',
																				e
																					.target
																					.value,
																			);
																			handlePanelInput(
																				'acao',
																				e
																					.target
																					.value ===
																					'competencia'
																					? 'REMOVER_COMPETENCIA'
																					: 'REMOVER_DIA',
																			);
																		}}
																		className='w-full min-h-[26px] border border-gray-200 rounded-md px-1 text-[10px] outline-none text-gray-600'>
																		<option value='dia'>
																			Apenas
																			deste
																			dia
																		</option>
																		<option value='competencia'>
																			Toda
																			a
																			competência
																		</option>
																	</select>
																	<div>
																		<label className='block text-[10px] text-[#666] mb-0.5 font-medium'>
																			Motivo
																			da
																			exclusão
																		</label>
																		<textarea
																			value={
																				panelForm.observacao
																			}
																			onChange={(
																				e,
																			) =>
																				handlePanelInput(
																					'observacao',
																					e
																						.target
																						.value,
																				)
																			}
																			rows={
																				3
																			}
																			className='w-full min-h-[54px] max-h-[88px] border border-gray-200 rounded-md px-2 py-1 text-[10px] outline-none text-gray-500 resize-none whitespace-pre-wrap break-words overflow-y-auto'
																		/>
																	</div>
																</div>
															</div>
														</div>
													</div>
													<div className='px-3 py-3 border-gray-200 flex flex-col gap-2 items-center'>
														<button
															type='button'
															onClick={
																handleAplicarAcao
															}
															disabled={
																disableActions ||
																isSubmittingPanel ||
																isTrocaExistenteSelecionada ||
																trocaBloqueadaPorConsulta ||
																(openMiniTabs.troca &&
																	(!trocaAvaliacaoValida ||
																		isLoadingTrocaAvaliacao))
															}
															className=' w-full max-w-[170px] h-[30px] border border-gray-200 rounded-md shadow-sm text-[12px] text-[#7b7d80] hover:bg-gray-50 transition disabled:opacity-50'>
															Enviar
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</main>

			{isNovoProfissionalModalOpen && (
				<div
					className='fixed inset-x-0 bottom-0 top-20 z-50 flex items-center justify-center overflow-hidden px-4 py-4 max-sm:px-2'
					role='dialog'
					aria-modal='true'
					aria-label={
						isEditingProfissional
							? 'Editar profissional'
							: 'Adicionar novo profissional'
					}>
					<button
						type='button'
						className='absolute inset-0 bg-black/30'
						onClick={handleCloseNovoProfissionalModal}
						aria-label='Fechar modal'
					/>

					<div className='relative w-[92%] max-w-[900px] max-h-[calc(100vh-7rem)] overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl max-lg:w-full sm:max-lg:max-w-[620px] max-sm:max-w-[350px]'>
						<div className='bg-[#fcfcfc] w-full h-[25px] flex justify-end px-1  rounded-tl-md rounded-tr-md'>
							<button
								type='button'
								onClick={handleCloseNovoProfissionalModal}
								className=' text-gray-400 hover:text-gray-600 items-center align-middle flex content-center text-3xl pb-2 transition-all duration-300 text-center rounded-full '
								aria-label='Fechar'>
								&times;
							</button>
						</div>

						<div className='max-h-[calc(100vh-9rem)] overflow-x-hidden overflow-y-auto px-4 py-5 font-inter max-sm:px-3 max-sm:py-3'>
							{/* <div className='text-center text-[16px] font-semibold text-[#13335A] mb-4'>
								{isEditingProfissional
									? 'Editar profissional'
									: 'Adicionar novo profissional'}
							</div> */}
							{/* Linha CPF + pesquisar */}
							<div className='flex items-center justify-center gap-3 mb-4 max-lg:flex-col max-lg:items-stretch max-lg:gap-2 max-sm:mb-3'>
								<span className='text-[14px] font-semibold text-[#13335A]'>
									CPF
								</span>
								<input
									type='text'
									placeholder='123456789-00'
									value={novoProfissionalForm.cpf}
									onChange={(e) =>
										handleNovoProfissionalInput(
											'cpf',
											e.target.value,
										)
									}
									inputMode='numeric'
									maxLength={14}
									readOnly={isEditingProfissional}
									aria-readonly={isEditingProfissional}
									aria-invalid={Boolean(
										novoProfissionalErrors.cpf,
									)}
									className={`w-[210px] h-[32px] border text-gray-600 rounded-md px-2 text-[13px] outline-none max-lg:w-full ${isEditingProfissional ? getLockedFieldClassName(true) : 'bg-white'} ${novoProfissionalErrors.cpf ? 'border-[#dc2626]' : 'border-gray-200'}`}
								/>
								<button
									type='button'
									onClick={handleBuscarPorCpf}
									disabled={isEditingProfissional}
									className='h-[28px] px-4 border border-gray-200 rounded-md shadow-md text-[13px] text-[#76797e] hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed max-lg:w-full'>
									Pesquisar
								</button>
							</div>

							<hr className='border-gray-200 mb-4 max-sm:mb-3' />

							{isSetorTodosAplicado && !isEditingProfissional && (
								<div className='mb-4 rounded-md border border-[#D8DEE8] bg-[#F8FAFC] p-3'>
									<label className='mb-1 block text-[13px] font-medium text-[#13335A]'>
										Setor
									</label>
									<SearchableSetorSelect
										value={novoProfissionalForm.setor_id}
										options={setorOptions}
										onChange={(value) =>
											handleNovoProfissionalInput(
												'setor_id',
												value,
											)
										}
										getOptionLabel={formatSetorOptionLabel}
										className='h-[34px] rounded-md border border-gray-200 bg-white px-3 text-[13px] text-gray-600 outline-none'
									/>
								</div>
							)}

							{/* Form */}
							<div className='grid grid-cols-1 lg:grid-cols-2 gap-x-3 gap-y-4 max-sm:gap-y-3'>
								{/* Nome */}
								<div className='lg:col-span-2'>
									<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
										Nome
									</label>
									<input
										type='text'
										placeholder='Nome completo'
										value={novoProfissionalForm.nome}
										onChange={(e) =>
											handleNovoProfissionalInput(
												'nome',
												e.target.value,
											)
										}
										onBlur={handleNovoProfissionalNomeBlur}
										readOnly={Boolean(
											novoProfissionalLockedFields.nome,
										)}
										aria-readonly={Boolean(
											novoProfissionalLockedFields.nome,
										)}
										aria-invalid={Boolean(
											novoProfissionalErrors.nome,
										)}
										className={`w-full h-[34px] border text-gray-600 rounded-md px-3 text-[13px] outline-none ${getLockedFieldClassName(Boolean(novoProfissionalLockedFields.nome))} ${novoProfissionalErrors.nome ? 'border-[#dc2626]' : 'border-gray-200'}`}
									/>
									{getFieldErrorMessage(
										novoProfissionalErrors.nome,
									)}
								</div>

								{/* Admissão */}
								<div>
									<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
										Admissão
									</label>
									<div className='relative'>
										<input
											type='text'
											placeholder='00/00/0000'
											value={
												novoProfissionalForm.admissao
											}
											onChange={(e) =>
												handleNovoProfissionalInput(
													'admissao',
													e.target.value,
												)
											}
											readOnly={false}
											aria-readonly={false}
											aria-invalid={Boolean(
												novoProfissionalErrors.admissao,
											)}
											className={`w-full h-[34px] border rounded-md px-3 pr-10 text-gray-600 text-[13px] outline-none bg-white ${novoProfissionalErrors.admissao ? 'border-[#dc2626]' : 'border-gray-200'}`}
										/>
										<input
											ref={admissionDatePickerRef}
											type='date'
											value={formatDateInputToISO(
												novoProfissionalForm.admissao,
											)}
											onChange={(e) =>
												handleNovoProfissionalInput(
													'admissao',
													formatISOToDisplay(
														e.target.value,
													),
												)
											}
											className='absolute opacity-0 pointer-events-none text-gray-600'
											tabIndex={-1}
										/>
										<button
											type='button'
											onClick={() =>
												admissionDatePickerRef.current?.showPicker?.() ||
												admissionDatePickerRef.current?.click?.()
											}
											className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'
											aria-label='Selecionar data de admissão'>
											<MdCalendarToday className='w-4 h-4' />
										</button>
									</div>
									{getFieldErrorMessage(
										novoProfissionalErrors.admissao,
									)}
								</div>

								{/* Vínculo */}
								<div>
									<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
										Vínculo
									</label>
									<select
										value={
											novoProfissionalForm.vinculo_tipo_id
										}
										onChange={(e) => {
											const selected = vinculoOpcoes.find(
												(v) =>
													String(v.value) ===
													String(e.target.value),
											);
											if (selected) {
												setNovoProfissionalForm(
													(prev) => ({
														...prev,
														nome_vinculo:
															selected.label,
														vinculo_tipo_id:
															selected.value,
													}),
												);
											} else {
												setNovoProfissionalForm(
													(prev) => ({
														...prev,
														nome_vinculo: '',
														vinculo_tipo_id: '',
													}),
												);
											}
										}}
										disabled={vinculoOpcoes.length === 0}
										aria-invalid={Boolean(
											novoProfissionalErrors.vinculo_tipo_id,
										)}
										className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none text-gray-600 ${novoProfissionalErrors.vinculo_tipo_id ? 'border-[#dc2626]' : 'border-gray-200'} ${getLockedFieldClassName(vinculoOpcoes.length === 0)}`}>
										<option value=''>
											{vinculoOpcoes.length > 0
												? 'Selecione o vínculo'
												: 'Nenhum vínculo disponível'}
										</option>
										{novoProfissionalForm.vinculo_tipo_id &&
											!vinculoOpcoes.some(
												(v) =>
													String(v.value) ===
													String(
														novoProfissionalForm.vinculo_tipo_id,
													),
											) && (
												<option
													value={
														novoProfissionalForm.vinculo_tipo_id
													}>
													{novoProfissionalForm.nome_vinculo ||
														String(
															novoProfissionalForm.vinculo_tipo_id,
														)}
												</option>
											)}
										{vinculoOpcoes.map((v) => (
											<option
												key={v.value}
												value={v.value}>
												{v.label}
											</option>
										))}
									</select>
									{getFieldErrorMessage(
										novoProfissionalErrors.vinculo_tipo_id,
									)}
								</div>

								{/* Cargo (sempre "Cargo" em ambos os modos) */}
								<div>
									<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
										Cargo
									</label>
									<input
										type='text'
										placeholder='Cargo'
										value={
											modalidade === 'ENFERMAGEM' &&
											novoProfissionalForm.cargo ===
												'Médico'
												? 'escolha uma categoria'
												: novoProfissionalForm.cargo
										}
										onChange={(e) =>
											handleNovoProfissionalInput(
												'cargo',
												e.target.value,
											)
										}
										readOnly={Boolean(
											novoProfissionalLockedFields.cargo,
										)}
										aria-readonly={Boolean(
											novoProfissionalLockedFields.cargo,
										)}
										className={`w-full h-[34px] border border-gray-200 rounded-md px-3 text-[13px] outline-none text-gray-600 ${getLockedFieldClassName(Boolean(novoProfissionalLockedFields.cargo))}`}
									/>
								</div>

								{/* Número do conselho + UF */}
								<div className='grid grid-cols-3 gap-3 max-sm:gap-2'>
									<div className='col-span-2'>
										<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
											Número do conselho
										</label>
										<input
											type='text'
											placeholder='0000000'
											value={
												novoProfissionalForm.numero_registro
											}
											onChange={(e) =>
												handleNovoProfissionalInput(
													'numero_registro',
													e.target.value,
												)
											}
											inputMode='numeric'
											maxLength={20}
											readOnly={Boolean(
												novoProfissionalLockedFields.numero_registro,
											)}
											aria-readonly={Boolean(
												novoProfissionalLockedFields.numero_registro,
											)}
											className={`w-full h-[34px] border border-gray-200 rounded-md px-3 text-[13px] outline-none text-gray-600 ${getLockedFieldClassName(Boolean(novoProfissionalLockedFields.numero_registro))}`}
										/>
									</div>
									<div>
										<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
											UF
										</label>
										<select
											value={
												novoProfissionalForm.uf_conselho
											}
											onChange={(e) =>
												handleNovoProfissionalInput(
													'uf_conselho',
													e.target.value,
												)
											}
											disabled={Boolean(
												novoProfissionalLockedFields.uf_conselho,
											)}
											className={`w-full h-[34px] border border-gray-200 rounded-md px-3 text-[13px] outline-none text-gray-600 ${getLockedFieldClassName(Boolean(novoProfissionalLockedFields.uf_conselho))}`}>
											<option value=''>UF</option>
											{[
												'AC',
												'AL',
												'AP',
												'AM',
												'BA',
												'CE',
												'DF',
												'ES',
												'GO',
												'MA',
												'MT',
												'MS',
												'MG',
												'PA',
												'PB',
												'PR',
												'PE',
												'PI',
												'RJ',
												'RN',
												'RS',
												'RO',
												'RR',
												'SC',
												'SP',
												'SE',
												'TO',
											].map((uf) => (
												<option
													key={uf}
													value={uf}>
													{uf}
												</option>
											))}
										</select>
									</div>
								</div>

								{/* Categoria/Especialidade */}
								<div>
									<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
										{modalidade === 'ENFERMAGEM'
											? 'Categoria'
											: selectRegimeLabel}
									</label>
									<select
										value={
											novoProfissionalForm.especialidade_id
										}
										onChange={(e) =>
											handleNovoProfissionalInput(
												'especialidade_id',
												e.target.value,
											)
										}
										disabled={Boolean(
											novoProfissionalLockedFields.especialidade_id,
										)}
										aria-invalid={Boolean(
											novoProfissionalErrors.especialidade_id,
										)}
										className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none text-gray-600 ${getLockedFieldClassName(Boolean(novoProfissionalLockedFields.especialidade_id))} ${novoProfissionalErrors.especialidade_id ? 'border-[#dc2626]' : 'border-gray-200'}`}>
										<option value=''>
											{modalidade === 'ENFERMAGEM'
												? 'Selecione a categoria'
												: selectRegimeLabel}
										</option>
										{dimensaoOptions.map((item) => (
											<option
												key={item?.id}
												value={item?.id}>
												{item?.nome ||
													item?.label ||
													item?.id}
											</option>
										))}
									</select>
								</div>

								{/* Telefone */}
								<div>
									<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
										Telefone
									</label>
									<input
										type='text'
										placeholder='(00) 0 0000-0000'
										value={novoProfissionalForm.telefone}
										onChange={(e) =>
											handleNovoProfissionalInput(
												'telefone',
												e.target.value,
											)
										}
										inputMode='numeric'
										maxLength={15}
										readOnly={Boolean(
											novoProfissionalLockedFields.telefone,
										)}
										aria-readonly={Boolean(
											novoProfissionalLockedFields.telefone,
										)}
										aria-invalid={Boolean(
											novoProfissionalErrors.telefone,
										)}
										className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none text-gray-600 ${getLockedFieldClassName(Boolean(novoProfissionalLockedFields.telefone))} ${novoProfissionalErrors.telefone ? 'border-[#dc2626]' : 'border-gray-200'}`}
									/>
								</div>

								{isEdicaoProfissionalConsolidadaTodos && (
									<p className='lg:col-span-2 rounded-md border border-[#B8C7D9] bg-[#F8FAFC] px-3 py-2 text-[11px] text-[#4F647D]'>
										As informações de escala abaixo são
										somente para visualização. Use o botão
										de engrenagem para ajustar turnos, dias
										e setores.
									</p>
								)}

								<fieldset
									disabled={
										isEdicaoProfissionalConsolidadaTodos
									}
									aria-label='Informações da escala'
									className={`lg:col-span-2 ${isEdicaoProfissionalConsolidadaTodos ? 'cursor-not-allowed opacity-75' : ''}`}>
									<div className='grid grid-cols-1 lg:grid-cols-3 gap-3 max-sm:gap-2'>
										<div>
											<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
												Regime
											</label>
											<select
												value={
													novoProfissionalForm.regime
												}
												onChange={(e) =>
													handleNovoProfissionalInput(
														'regime',
														e.target.value,
														)
														}
													disabled={
														novoProfissionalRegimeRecorrenciaBloqueados
													}
													aria-readonly={
														novoProfissionalRegimeRecorrenciaBloqueados
													}
													aria-invalid={Boolean(
													novoProfissionalErrors.regime,
												)}
													className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none ${getLockedFieldClassName(novoProfissionalRegimeRecorrenciaBloqueados)} ${novoProfissionalHasRegime ? 'text-gray-600' : 'text-gray-300'} ${novoProfissionalErrors.regime ? 'border-[#dc2626]' : 'border-gray-200'}`}>
												<option
													value=''
													className='text-gray-300'>
													Selecione
												</option>
												{regimesOptions.map((item) => (
													<option
														key={item?.codigo}
														value={item?.codigo}
														className='text-gray-600'>
														{item?.nome ||
															item?.codigo}
													</option>
												))}
											</select>
											{getFieldErrorMessage(
												novoProfissionalErrors.regime,
											)}
										</div>

										<div>
											<label className='block text-[13px] font-medium text-[#13335A] mb-1'>
												Recorrência
											</label>
											<select
												value={
													novoProfissionalForm.nome_recorrencia
												}
												disabled={
													modalidade === 'MEDICA' ||
													novoProfissionalRegimeRecorrenciaBloqueados
												}
												onChange={(e) =>
													handleNovoProfissionalRecorrenciaChange(
														e.target.value,
													)
												}
												aria-readonly={
													novoProfissionalRegimeRecorrenciaBloqueados
												}
												aria-invalid={Boolean(
													novoProfissionalErrors.nome_recorrencia,
												)}
												className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none ${getLockedFieldClassName(modalidade === 'MEDICA' || novoProfissionalRegimeRecorrenciaBloqueados)} ${novoProfissionalForm.nome_recorrencia ? 'text-gray-600' : 'text-gray-300'} ${novoProfissionalErrors.nome_recorrencia ? 'border-[#dc2626]' : 'border-gray-200'}`}>
												<option
													value=''
													className='text-gray-300'>
													Selecione
												</option>
												{recorrenciaNomeOptions.map(
													(item) =>
														(modalidade !==
															'MEDICA' ||
															isWeeklyRecorrencia(
																item?.tipo_recorrencia ||
																	item?.value,
															)) && (
															<option
																key={
																	item?.value
																}
																value={
																	item?.value
																}
																className='text-gray-600'>
																{item?.label}
															</option>
														),
												)}
											</select>
											{getFieldErrorMessage(
												novoProfissionalErrors.nome_recorrencia,
											)}
										</div>

										<div>
											<label className='mb-1 flex items-center gap-1 text-[13px] font-medium text-[#13335A]'>
												<span>Data inicial</span>
												<DataInicialInfo
													isEditing={
														isEditingProfissional
													}
												/>
											</label>
											<div className='relative'>
												<input
													type='text'
													placeholder='00/00/0000'
													value={
														novoProfissionalForm.vigencia_inicio
													}
													onChange={(e) =>
														handleNovoProfissionalInput(
															'vigencia_inicio',
															e.target.value,
														)
													}
													aria-invalid={Boolean(
														novoProfissionalErrors.vigencia_inicio,
													)}
													className={`w-full h-[34px] border rounded-md px-3 pr-10 text-gray-600 text-[13px] outline-none ${novoProfissionalErrors.vigencia_inicio ? 'border-[#dc2626]' : 'border-gray-200'}`}
												/>
												<input
													ref={
														vigenciaInicioDatePickerRef
													}
													type='date'
													min={
														isEditingProfissional
															? undefined
															: getTodayISODateLocal()
													}
													value={formatDateInputToISO(
														novoProfissionalForm.vigencia_inicio,
													)}
													onChange={(e) =>
														handleNovoProfissionalInput(
															'vigencia_inicio',
															formatISOToDisplay(
																e.target.value,
															),
														)
													}
													className='absolute opacity-0 pointer-events-none text-gray-600'
													tabIndex={-1}
												/>
												<button
													type='button'
													onClick={() =>
														vigenciaInicioDatePickerRef.current?.showPicker?.() ||
														vigenciaInicioDatePickerRef.current?.click?.()
													}
													className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'
													aria-label='Selecionar data inicial'>
													<MdCalendarToday className='w-4 h-4' />
												</button>
											</div>
											{getFieldErrorMessage(
												novoProfissionalErrors.vigencia_inicio,
											)}
										</div>
									</div>

									{novoProfissionalMostrarBlocoTurnoCiclo && (
										<div className='mt-3 rounded-md border px-3 py-2 shadow-md'>
											<label className='mb-2 block text-[13px] font-medium text-[#13335A]'>
												Horário
											</label>
											<div className='w-full max-w-[137px]'>
												<DiaSemanaHorarioBloco
													minWidthClassName='w-full'
													marginTopClassName='mt-0'
													forceFullTimeInputs
													horaInicio={
														novoProfissionalForm.hora_inicio
													}
													horaFim={
														novoProfissionalForm.hora_fim
													}
													onChangeHoraInicio={(
														value,
													) =>
														handleNovoProfissionalInput(
															'hora_inicio',
															value,
														)
													}
													onChangeHoraFim={(value) =>
														handleNovoProfissionalInput(
															'hora_fim',
															value,
														)
													}
												/>
											</div>
											{getFieldErrorMessage(
												novoProfissionalErrors.hora_inicio ||
													novoProfissionalErrors.hora_fim,
											)}
										</div>
									)}

									{modalidade === 'ENFERMAGEM' &&
										novoProfissionalUsaRotacaoPeriodica && (
											<div className='mt-3 rounded-md border border-[#B8C7D9] px-3 py-3 shadow-md'>
												<div className='flex flex-wrap items-center justify-between gap-2'>
													<div>
														<p className='text-[13px] font-medium text-[#13335A]'>
															Rotação entre
															semanas
														</p>
														<p className='text-[12px] text-[#4F647D]'>
															Defina os dias de
															trabalho de cada
															semana, na ordem em
															que se repetem.
														</p>
													</div>
													<label className='text-[12px] font-medium text-[#13335A]'>
														Semanas
														<select
															value={
																novoProfissionalForm
																	.semanas_rotacao
																	?.length ||
																2
															}
															onChange={(event) =>
																setNovoProfissionalRotacaoSemanas(
																	event.target
																		.value,
																)
															}
															className='ml-2 h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'>
															{[
																2, 3, 4, 5, 6,
															].map(
																(
																	quantidade,
																) => (
																	<option
																		key={
																			quantidade
																		}
																		value={
																			quantidade
																		}>
																		{
																			quantidade
																		}{' '}
																		semanas
																	</option>
																),
															)}
														</select>
													</label>
												</div>
												<div className='mt-3 grid gap-3 md:grid-cols-2'>
													{(
														novoProfissionalForm.semanas_rotacao ||
														[]
													).map(
														(
															diasSemana,
															semanaIndex,
														) => (
															<div
																key={
																	semanaIndex
																}>
																<p className='text-[12px] font-medium text-[#13335A]'>
																	Semana{' '}
																	{semanaIndex +
																		1}
																</p>
																<div className='mt-1 flex flex-wrap gap-1'>
																	{DIAS_SEMANA_OPTIONS.map(
																		(
																			dia,
																		) => {
																			const isSelected =
																				diasSemana.includes(
																					dia.value,
																				);
																			return (
																				<button
																					type='button'
																					key={
																						dia.value
																					}
																					onClick={() =>
																						toggleNovoProfissionalRotacaoDia(
																							semanaIndex,
																							dia.value,
																						)
																					}
																					aria-label={
																						dia.label
																					}
																					className={`w-20 rounded-md border px-2 py-1 text-center text-[12px] ${isSelected ? 'border-[#004A80] bg-[#004A80] text-white' : 'border-gray-200 bg-white text-[#36516b]'}`}>
																					{
																						dia.label
																					}
																				</button>
																			);
																		},
																	)}
																</div>
															</div>
														),
													)}
												</div>
												{getFieldErrorMessage(
													novoProfissionalErrors.semanas_rotacao,
												)}
											</div>
										)}

									{novoProfissionalMostrarBlocoDiasSemana && (
										<div className='mt-3 border py-1 px-2 pb-2 rounded-md shadow-md max-md:overflow-x-hidden'>
											<label className='block text-[13px] font-medium text-[#13335A] mb-1 '>
												Dias da semana
											</label>
											<div className='flex w-full justify-start gap-2 flex-wrap lg:justify-between lg:gap-0'>
												{DIAS_SEMANA_OPTIONS.map(
													(dia) => {
														const isSelected =
															novoProfissionalDiasSemanaEfetivos.includes(
																dia.value,
															);
														const isFimDeSemanaEmCiclo =
															novoProfissionalUsaRecorrenciaCiclo &&
															modalidade ===
																'ENFERMAGEM' &&
															(dia.value === 6 ||
																dia.value ===
																	7);
														const isLocked =
															isFimDeSemanaEmCiclo ||
															novoProfissionalDiaSemanaBloqueado ===
																dia.value ||
															(isSelected &&
																novoProfissionalJornadaInterligadaDetalhe.bloqueiaDias);
														const diaHorario =
															isSelected
																? getDiaSemanaHorarioConfig(
																		{
																			diaSemana:
																				dia.value,
																			diasSemanaHorarios:
																				novoProfissionalForm.dias_semana_horarios,
																			fallbackHoraInicio:
																				novoProfissionalForm.hora_inicio,
																			fallbackHoraFim:
																				novoProfissionalForm.hora_fim,
																		},
																	)
																: {
																		hora_inicio:
																			'',
																		hora_fim:
																			'',
																	};
														const isDiaJornadaInterligada =
															isSelected &&
															novoProfissionalJornadaInterligadaDetalhe.bloqueiaDias;
														const horariosDiaJornadaInterligada =
															Array.isArray(
																novoProfissionalJornadaInterligadaDetalhe
																	.horariosPorDia?.[
																	dia.value
																],
															)
																? novoProfissionalJornadaInterligadaDetalhe
																		.horariosPorDia[
																		dia
																			.value
																	]
																: [diaHorario];

														return (
															<div
																key={dia.value}
																className='relative pb-3 flex flex-col items-center max-md:pb-2'>
																<button
																	type='button'
																	onClick={() =>
																		toggleNovoProfissionalDiaSemana(
																			dia.value,
																		)
																	}
																	disabled={
																		isLocked
																	}
																	className={`min-w-[97px] h-[34px] px-3 rounded-md border text-[12px] font-medium transition-all duration-200 disabled:opacity-100 disabled:cursor-default max-md:min-w-[84px] max-md:h-[30px] max-md:px-2 ${
																		isSelected
																			? 'bg-[#004A80] border-[#004A80] text-white'
																			: 'bg-white border-gray-200 text-[#6B7280] hover:bg-[#F3F4F6]'
																	}`}>
																	{dia.label}
																</button>
																{!isDiaJornadaInterligada && (
																	<DiaSemanaHorarioBloco
																		disabled={
																			!isSelected ||
																			isDiaJornadaInterligada
																		}
																		minWidthClassName='min-w-[97px] max-md:min-w-[84px] max-md:h-[30px]'
																		horaInicio={
																			diaHorario.hora_inicio
																		}
																		horaFim={
																			diaHorario.hora_fim
																		}
																		onChangeHoraInicio={(
																			nextValue,
																		) =>
																			handleNovoProfissionalDiaSemanaHorarioChange(
																				dia.value,
																				'hora_inicio',
																				nextValue,
																			)
																		}
																		onChangeHoraFim={(
																			nextValue,
																		) =>
																			handleNovoProfissionalDiaSemanaHorarioChange(
																				dia.value,
																				'hora_fim',
																				nextValue,
																			)
																		}
																	/>
																)}
																{isDiaJornadaInterligada && (
																	<FieldInfoTooltip
																		ariaLabel='Informações sobre a jornada interligada'
																		openToLeft
																		widthClassName='w-[300px]'
																		triggerContainerClassName='relative mt-1 flex w-full justify-center'
																		triggerClassName='mt-1 flex min-w-[97px] flex-col rounded-md border border-[#B8C7D9] bg-[#F8FBFE] px-1 py-1 text-[10px] font-medium text-[#13335A] transition-all duration-200 hover:scale-[1.03] hover:border-[#8CB3D3] hover:bg-[#E6F1F9] hover:shadow-sm focus:scale-[1.03] focus:bg-[#E6F1F9] max-md:min-w-[84px]'
																		triggerContent={
																			<>
																				{horariosDiaJornadaInterligada.map(
																					(
																						horario,
																						index,
																					) => (
																						<span
																							key={`${dia.value}-${horario.hora_inicio}-${horario.hora_fim}-${index}`}>
																							{
																								horario.hora_inicio
																							}{' '}
																							às{' '}
																							{
																								horario.hora_fim
																							}
																						</span>
																					),
																				)}
																			</>
																		}
																		text={
																			<>
																				<p>
																					Esta
																					jornada
																					é
																					derivada
																					ou
																					interligada
																					aos
																					horários
																					de
																					outro
																					setor.
																					Edite
																					as
																					faixas
																					no
																					gerenciamento
																					de
																					turnos.
																				</p>
																				<button
																					type='button'
																					onClick={
																						handleAbrirGerenciamentoJornadaInterligada
																					}
																					disabled={
																						isAbrindoGerenciamentoJornada
																					}
																					className='mt-2 flex items-center gap-1 text-[11px] font-semibold text-[#004A80] underline disabled:cursor-wait disabled:opacity-70'>
																					{isAbrindoGerenciamentoJornada && (
																						<MdAutorenew className='h-3.5 w-3.5 animate-spin' />
																					)}
																					{isAbrindoGerenciamentoJornada
																						? 'Abrindo gerenciamento...'
																						: 'Gerenciar turnos'}
																				</button>
																			</>
																		}
																	/>
																)}
																{novoProfissionalDataInicialEhDiaUtil &&
																	novoProfissionalDiaSemanaBloqueado ===
																		dia.value &&
																	!isDiaJornadaInterligada && (
																		<span className='pointer-events-none absolute left-1/2  -translate-x-1/2 top-20 whitespace-nowrap text-[10px] font-semibold  text-[#004A80] '>
																			Primeiro
																			Dia
																		</span>
																	)}
															</div>
														);
													},
												)}
											</div>

											{novoProfissionalUsaRecorrenciaCiclo && (
												<p className='mt-2 text-[11px] text-[#6B7280]'>
													Os dias e horários foram
													preenchidos automaticamente
													com base na data inicial e
													no ciclo selecionado.
												</p>
											)}
											{getFieldErrorMessage(
												novoProfissionalErrors.dias_semana,
											)}
										</div>
									)}
									{novoProfissionalDatasExtras.length > 0 && (
										<div className='mt-4 border py-1 px-2 rounded-md shadow-md max-sm:mt-3 max-md:overflow-x-hidden'>
											{novoProfissionalDatasExtras.length >
												0 && (
												<p className='block text-[13px] font-medium text-[#13335A] '>
													Datas específicas
												</p>
											)}
											{novoProfissionalDatasExtras.length >
												0 && (
												<div className='py-2 flex flex-wrap gap-3 max-sm:justify-start max-sm:gap-2'>
													{novoProfissionalDatasExtras.map(
														(item) => {
															const dataExtraReadOnly =
																isDataExtraReadOnly(
																	item,
																);
															const dataExtraSelecionadaISO =
																formatDateInputToISO(
																	item?.selected_date ||
																		'',
																);
															const dataExtraForaDoMesVisualizado =
																Boolean(
																	dataExtraSelecionadaISO,
																) &&
																!isDataExtraNoMesVisualizado(
																	dataExtraSelecionadaISO,
																);
															const dataExtraOptions =
																getNovoProfissionalDataExtraOptionsComSelecionada(
																	item,
																);
															return (
																<div
																	key={
																		item.id
																	}
																	className='flex flex-col items-center gap-1 max-sm:gap-1'>
																	<div className='relative rounded-md border border-gray-200 bg-[#ffffff] p-1'>
																		<span
																			className={`mb-1 block  text-center text-[9px] font-semibold uppercase ${
																				dataExtraReadOnly
																					? 'text-[#6F5752]'
																					: 'text-[#6B7280]'
																			}`}>
																			{dataExtraReadOnly
																				? 'Troca'
																				: 'Alocação'}
																		</span>
																		{!dataExtraReadOnly && (
																			<button
																				type='button'
																				disabled={
																					dataExtraForaDoMesVisualizado
																				}
																				onClick={() =>
																					handleRemoverNovoProfissionalDataExtra(
																						item.id,
																					)
																				}
																				className='absolute right-1 top-1 flex h-[14px] w-[14px] items-center justify-center rounded-[3px] bg-gray-100 text-[17px] font-normal leading-none text-[#BD504E] transition hover:bg-gray-200 hover:text-[#883433] disabled:cursor-not-allowed disabled:opacity-50'>
																				&times;
																			</button>
																		)}
																		<div className='flex flex-col gap-1 max-w-[110px]'>
																			<select
																				value={
																					item.selected_date ||
																					''
																				}
																				disabled={
																					dataExtraForaDoMesVisualizado ||
																					dataExtraReadOnly
																				}
																				onChange={(
																					e,
																				) =>
																					handleNovoProfissionalDataExtraChange(
																						item.id,
																						e
																							.target
																							.value,
																					)
																				}
																				className='h-[34px] w-full rounded-md  border border-gray-200 px-1 text-[12px] text-[#4F5257] outline-none appearance-none disabled:bg-[#F3F4F6] disabled:text-[#6B7280]'>
																				<option value=''>
																					Dia
																					00
																				</option>
																				{dataExtraOptions.map(
																					(
																						option,
																					) => (
																						<option
																							key={
																								option.value
																							}
																							value={
																								option.value
																							}>
																							{
																								option.label
																							}
																						</option>
																					),
																				)}
																			</select>

																			<DiaSemanaHorarioBloco
																				disabled={
																					dataExtraForaDoMesVisualizado ||
																					dataExtraReadOnly
																				}
																				minWidthClassName='w-full '
																				marginTopClassName='mt-0'
																				containerClassName='rounded-md '
																				horaInicio={
																					item.hora_inicio ||
																					''
																				}
																				horaFim={
																					item.hora_fim ||
																					''
																				}
																				onChangeHoraInicio={(
																					nextValue,
																				) =>
																					handleNovoProfissionalDataExtraHorarioChange(
																						item.id,
																						'hora_inicio',
																						nextValue,
																					)
																				}
																				onChangeHoraFim={(
																					nextValue,
																				) =>
																					handleNovoProfissionalDataExtraHorarioChange(
																						item.id,
																						'hora_fim',
																						nextValue,
																					)
																				}
																			/>
																			{/* {dataExtraForaDoMesVisualizado && (
																				<p className='text-[10px] text-[#6B7280] leading-tight'>
																					Data de outra competência (somente leitura).
																				</p>
																			)} */}
																		</div>
																	</div>
																</div>
															);
														},
													)}
													<DataEspecificaAction
														compact
														onClick={
															handleAdicionarNovoProfissionalDataExtra
														}
													/>
												</div>
											)}
										</div>
									)}

									{novoProfissionalDatasExtras.length ===
										0 && (
										<DataEspecificaAction
											onClick={
												handleAdicionarNovoProfissionalDataExtra
											}
										/>
									)}

									{novoProfissionalIsDataEspecifica && (
										<div className=''>
											{/* <label className='block text-[13px] font-medium text-[#13335A] mb-2'>
												Datas específicas
											</label>
											{novoProfissionalDatasExtras.length >
												0 && (
												<div className='mt-2 flex flex-wrap gap-6 rounded-md border border-gray-200 p-3'>
													{novoProfissionalDatasExtras.map(
														(item) => (
															<div
																key={item.id}
																className='relative'>
																<button
																	type='button'
																	onClick={() =>
																		handleRemoverNovoProfissionalDataExtra(
																			item.id,
																		)
																	}
																	className='absolute right-[-20px] top-[-9px] flex h-6 w-6 justify-center rounded-full text-[16px] font-bold text-[#c7c7c7] hover:text-[#999999] transition'>
																	&times;
																</button>
																<div className='flex flex-col gap-1 max-w-[110px]'>
																	<select
																		value={
																			item.selected_date ||
																			''
																		}
																		onChange={(
																			e,
																		) =>
																			handleNovoProfissionalDataExtraChange(
																				item.id,
																				e
																					.target
																					.value,
																			)
																		}
																		className='h-[34px] w-full rounded-md border border-gray-200 px-1 text-[12px] text-[#4F5257] outline-none'>
																		<option value=''>
																			Dia
																			00/00/0000
																		</option>
																		{getNovoProfissionalDataExtraOptions(
																			item.id,
																		).map(
																			(
																				option,
																			) => (
																				<option
																					key={
																						option.value
																					}
																					value={
																						option.value
																					}>
																					{
																						option.label
																					}
																				</option>
																			),
																		)}
																	</select>
																	<DiaSemanaHorarioBloco
																		disabled={
																			false
																		}
																		minWidthClassName='w-full'
																		marginTopClassName='mt-0'
																		containerClassName='rounded-md'
																		horaInicio={
																			item.hora_inicio ||
																			''
																		}
																		horaFim={
																			item.hora_fim ||
																			''
																		}
																		onChangeHoraInicio={(
																			nextValue,
																		) =>
																			handleNovoProfissionalDataExtraHorarioChange(
																				item.id,
																				'hora_inicio',
																				nextValue,
																			)
																		}
																		onChangeHoraFim={(
																			nextValue,
																		) =>
																			handleNovoProfissionalDataExtraHorarioChange(
																				item.id,
																				'hora_fim',
																				nextValue,
																			)
																		}
																	/>
																</div>
															</div>
														),
													)}
												</div>
											)} */}
											{novoProfissionalDatasExtras.length ===
												0 && (
												<DataEspecificaAction
													onClick={
														handleAdicionarNovoProfissionalDataExtra
													}
												/>
											)}
										</div>
									)}
								</fieldset>
							</div>

							{novoProfissionalVisibleMessages.length > 0 && (
								<div className='mt-4 flex flex-col items-center gap-2 text-center'>
									{novoProfissionalVisibleMessages.map(
										(message, index) => (
											<div
												key={`${message}-${index}`}
												className='max-w-full whitespace-pre-line rounded-md border border-[#F5C2C7] bg-[#FDECEE] px-3 py-2 text-left text-[13px] leading-relaxed text-[#A61B29]'>
												{message}
											</div>
										),
									)}
								</div>
							)}

							<div className='flex justify-center mt-3'>
								<button
									type='button'
									onClick={handleSubmitNovoProfissional}
									disabled={isSubmittingAdicionar}
									className='w-[170px] h-[34px] border border-gray-200 rounded-md shadow-md text-[13px] text-[#7b7d80] hover:bg-gray-50 transition disabled:opacity-50 max-sm:w-[150px] max-sm:h-[32px]'>
									{isSubmittingAdicionar
										? 'Enviando...'
										: isEditingProfissional
											? 'Enviar'
											: 'Enviar'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{profissionalHorarioSobrepostoPopup && (
				<div
					className='fixed inset-0 z-[80] flex items-center justify-center px-4'
					role='dialog'
					aria-modal='true'
					aria-labelledby='profissional-horario-sobreposto-title'>
					<div className='absolute inset-0 bg-black/30' />

					<div className='relative z-[81] w-full max-w-[560px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl max-sm:max-w-[340px]'>
						<div className='flex min-h-[34px] items-center justify-between border-b border-gray-100 bg-[#fcfcfc] px-3'>
							<h3
								id='profissional-horario-sobreposto-title'
								className='text-[15px] font-semibold text-[#13335A]'>
								{profissionalHorarioSobrepostoPopup.titulo}
							</h3>
							<button
								type='button'
								onClick={
									handleCloseProfissionalHorarioSobrepostoPopup
								}
								className='flex h-8 w-8 items-center justify-center rounded-full text-3xl leading-none text-gray-400 transition-all duration-300 hover:text-gray-600'
								aria-label='Fechar popup'>
								&times;
							</button>
						</div>

						<div className='px-5 py-4 font-inter text-[#4F5257] max-sm:px-4'>
							<p className='text-[13px] leading-relaxed'>
								{profissionalHorarioSobrepostoPopup.mensagem}
							</p>

						</div>

						<div className='flex flex-wrap justify-center gap-2 border-t border-gray-100 px-4 py-3'>
							<button
									type='button'
									onClick={
										handleConsultarSetoresDoProfissionalEmConflito
									}
									disabled={isAbrindoGerenciamentoJornada}
									className='flex h-[34px] items-center justify-center gap-2 rounded-md bg-[#005A9C] px-4 text-[13px] text-white shadow-sm transition hover:bg-[#004A80] disabled:opacity-70'>
									{isAbrindoGerenciamentoJornada && (
										<span className='h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent' />
									)}
									{isAbrindoGerenciamentoJornada
										? 'Abrindo setores e turnos...'
										: 'Consultar setores e turnos'}
								</button>
							<button
								type='button'
								onClick={
									handleCloseProfissionalHorarioSobrepostoPopup
								}
								className='h-[34px] w-[130px] rounded-md border border-gray-200 text-[13px] text-[#7b7d80] shadow-md transition hover:bg-gray-50'>
								Fechar
							</button>
						</div>
					</div>
				</div>
			)}

			{isGerenciamentoTurnosModalOpen && (
				<div
					className='fixed inset-x-0 bottom-0 top-20 z-50 flex items-center justify-center overflow-hidden px-4 py-4 max-sm:px-2'
					role='dialog'
					aria-modal='true'
					aria-labelledby='gerenciamento-turnos-title'>
					<button
						type='button'
						className='absolute inset-0 bg-black/30'
						onClick={handleCloseGerenciamentoTurnosModal}
						aria-label='Fechar modal'
					/>

					<div className='relative flex w-full max-w-[980px] min-h-0 max-h-[calc(100vh-7rem)] flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl'>
						<div className='flex min-h-[38px] shrink-0 items-center justify-between border-b border-gray-100 bg-[#fcfcfc] px-4'>
							<div className='min-w-0'>
								<h3
									id='gerenciamento-turnos-title'
									className='truncate text-[16px] font-semibold text-[#13335A]'>
									{getLinhaDisplayName(
										gerenciamentoTurnosLinha,
									)}
								</h3>
							</div>
							<button
								type='button'
								onClick={handleCloseGerenciamentoTurnosModal}
								className='flex h-8 w-8 items-center justify-center rounded-full text-3xl leading-none text-gray-400 transition-all duration-300 hover:text-gray-600'
								aria-label='Fechar'>
								&times;
							</button>
						</div>

						<div className='min-h-0 flex-1 overflow-y-auto px-5 py-4 font-inter max-sm:px-3'>
							<p className='mb-4 text-[14px] font-medium leading-relaxed text-[#8f8f8f]'>
								Confira e ajuste os turnos deste profissional
								neste ou em outros setores
							</p>

							{(submitErrorScope === 'gerenciamentoTurnos' &&
								submitError) ||
							Object.entries(gerenciamentoTurnosErrors).some(
								([key]) => !key.startsWith('novo.'),
							) ? (
								<div className='mb-3 rounded-md border border-[#F5C2C7] bg-[#FDECEE] px-3 py-2 text-[12px] leading-relaxed text-[#A61B29]'>
									{submitErrorScope ===
										'gerenciamentoTurnos' && submitError
										? submitError
										: Array.from(
												new Set(
													Object.values(
														Object.entries(
															gerenciamentoTurnosErrors,
														)
															.filter(
																([key]) =>
																	!key.startsWith(
																		'novo.',
																	),
															)
															.map(
																([, message]) =>
																	message,
															)
															.filter(Boolean),
													),
												),
											).join(' ')}
								</div>
							) : null}

							{isGerenciamentoTurnosLoading ? (
								<div
									className='flex min-h-[260px] flex-col items-center justify-center gap-3 text-[#4F6F8E]'
									role='status'
									aria-live='polite'>
									<MdAutorenew className='h-7 w-7 animate-spin' />
									<span className='text-[13px] font-medium'>
										Carregando setores e turnos...
									</span>
								</div>
							) : isGerenciamentoTurnosReady ? (
								<>
									<div className='flex flex-col gap-3'>
										{gerenciamentoTurnosSetoresEditaveis.map(
											(item) => {
												const isSetorAberto =
													gerenciamentoTurnosSetoresAbertos[
														item.id
													] !== false;
												const isSetorBase = Boolean(
													item.isSetorBase,
												);
												const isSetorDerivado = Boolean(
													item.isSetorDerivado,
												);
												const isRecorrenciaSemanal =
													normalizeCode(
														item.tipoRecorrencia,
													) === 'SEMANAL';
												const isRotacaoPeriodica =
													normalizeCode(
														item.tipoRecorrencia,
													) === 'ROTACAO_PERIODICA';
												const cicloLabel =
													getRecorrenciaDisplayLabel({
														value:
															item.recorrenciaValue ||
															item.nomeRecorrencia,
														options:
															recorrenciaNomeOptions,
														fallback:
															item.presetCiclo ||
															'Ciclo contínuo',
													});
												const semanasRotacao =
													Array.isArray(
														item.semanasRotacao,
													)
														? item.semanasRotacao
														: [];

												return (
													<>
														<div
															key={item.id}
															className='overflow-visible rounded-md bg-[#004A80] shadow-sm'>
															<div
																className={`flex min-h-[38px] cursor-pointer items-center bg-[#004A80] text-white transition hover:bg-[#0d5b99] ${
																	isSetorTodosAplicado
																		? isSetorAberto
																			? 'rounded-t-md'
																			: 'rounded-md'
																		: ''
																}`}
																onClick={() =>
																	toggleGerenciamentoTurnosSetorAberto(
																		item.id,
																	)
																}>
																<button
																	type='button'
																	onClick={(
																		event,
																	) => {
																		event.stopPropagation();
																		toggleGerenciamentoTurnosSetorAberto(
																			item.id,
																		);
																	}}
																	className='flex min-w-0 flex-1 items-center justify-between gap-3 px-3 py-2 text-left'
																	aria-expanded={
																		isSetorAberto
																	}>
																	<span className='flex min-w-0 items-center gap-2'>
																		<span className='min-w-0 truncate text-[13px] font-semibold tracking-wide text-white'>
																			{
																				item.setorNome
																			}
																		</span>
																	</span>
																	<span className='hidden shrink-0 items-center gap-2 sm:flex'>
																		{isSetorBase && (
																			<span
																				className='rounded-md border border-[#3A78A7] bg-[#0D5B99] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/95'
																				title='Setor-base: referência da jornada para a divisão de horários'>
																				Setor-base
																			</span>
																		)}
																		<span className='truncate text-[11px] font-medium text-white/80'>
																			{
																				item.regime
																			}{' '}
																			•{' '}
																			{
																				item.dataInicial
																			}
																		</span>
																	</span>
																</button>
																<MdKeyboardArrowDown
																	className={`h-6 w-6 shrink-0 text-white transition-transform duration-200 ${
																		isSetorAberto
																			? 'rotate-180'
																			: ''
																	}`}
																	aria-hidden='true'
																/>
																<button
																	type='button'
																	onClick={(
																		event,
																	) => {
																		event.stopPropagation();
																		handleOpenDeleteModalFromGerenciamentoTurnos(
																			item,
																		);
																	}}
																	className='mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[22px] leading-none text-white hover:text-gray-200 hover: transition-colors hover:scale-[105%] transition-all duration-300 ease-out '
																	aria-label={`Excluir ${item.setorNome} deste profissional`}>
																	&times;
																</button>
															</div>

															<div
																className={`transition-[max-height,opacity] duration-300 ease-in-out ${
																	isSetorAberto
																		? 'max-h-[1100px] overflow-visible opacity-100'
																		: 'max-h-0 overflow-hidden opacity-0'
																}`}>
																<div className='border border-[#004A80] bg-white px-3 py-3 rounded-b-md'>
																	<fieldset className='min-w-0'>
																		<div className='grid grid-cols-1 gap-2 md:grid-cols-[0.9fr_0.9fr_0.95fr_0.95fr]'>
																			<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																				Regime
																				<select
																					value={
																						item.regimeValue
																					}
																					onChange={(
																						e,
																					) =>
																						handleGerenciamentoTurnosSetorRegimeChange(
																							item.id,
																							e
																								.target
																								.value,
																						)
																					}
																					className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'>
																					{getGerenciamentoTurnosRegimeOptions(
																						item.regime,
																						ambienteOpcoes?.regimes,
																					).map(
																						(
																							regime,
																						) => (
																							<option
																								key={
																									regime
																								}
																								value={
																									regime
																								}>
																								{
																									regime
																								}
																							</option>
																						),
																					)}
																				</select>
																			</label>
																			<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																				<span>
																					Recorrência
																				</span>
																				<select
																					value={
																						item.recorrenciaValue ||
																						item.nomeRecorrencia ||
																						'SEMANAL'
																					}
																					onChange={(
																						e,
																					) =>
																						handleGerenciamentoTurnosSetorRecorrenciaChange(
																							item.id,
																							e
																								.target
																								.value,
																						)
																					}
																					className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'>
																					{recorrenciaNomeOptions.map(
																						(
																							option,
																						) =>
																							(modalidade !==
																								'MEDICA' ||
																								isWeeklyRecorrencia(
																									option?.tipo_recorrencia,
																								)) && (
																								<option
																									key={
																										option.value
																									}
																									value={
																										option.value
																									}>
																									{
																										option.label
																									}
																								</option>
																							),
																					)}
																				</select>
																			</label>
																			<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																				<span className='flex items-center gap-1'>
																					Última
																					atualização
																					<UltimaAtualizacaoInfo />
																				</span>
																				<input
																					type='date'
																					value={formatDateInputToISO(
																						item.dataUltimaAtualizacao ||
																							item.dataInicialValue,
																					)}
																					readOnly
																					aria-label='Última atualização da alocação'
																					className='h-[32px] w-full min-w-0 cursor-not-allowed rounded-md border border-gray-200 bg-[#F3F4F6] px-2 text-[12px] font-normal text-[#6B7280] outline-none'
																				/>
																			</label>
																			<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																				<span className='flex items-center gap-1'>
																					Data
																					inicial
																					<DataInicialOriginalInfo />
																				</span>
																				<input
																					type='text'
																					value={
																						item.dataInicialOriginal ||
																						'--/--/----'
																					}
																					readOnly
																					aria-label='Data inicial do profissional'
																					className='h-[32px] w-full min-w-0 cursor-not-allowed rounded-md border border-gray-200 bg-[#F3F4F6] px-2 text-[12px] font-normal text-[#6B7280] outline-none'
																				/>
																			</label>
																		</div>

																		<div className='mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[1fr_260px]'>
																			{isRecorrenciaSemanal ? (
																				<div className='rounded-md border border-[#B8C7D9] bg-white px-2 py-2 shadow-sm'>
																					<p className='mb-2 text-[12px] font-medium text-[#13335A]'>
																						Dias
																						recorrentes
																					</p>
																					<div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4'>
																						{DIAS_SEMANA_OPTIONS.map(
																							(
																								dia,
																							) => {
																								const isSelected =
																									item.diasSemanaValores?.includes(
																										dia.value,
																									);
																								const isBloqueadoPorParticaoIntegral =
																									Boolean(
																										item.isSetorBase &&
																										item.diasSemanaBloqueadosPorParticaoIntegral?.includes(
																											dia.value,
																										),
																									);
																								const diaHorario =
																									item
																										.horariosByDia?.[
																										dia
																											.value
																									] ||
																									{};
																								const horariosDoDia =
																									Array.isArray(
																										item
																											?.horariosOptionsByDia?.[
																											dia
																												.value
																										],
																									)
																										? item
																												.horariosOptionsByDia[
																												dia
																													.value
																											]
																										: [];
																								const horariosParticionadosDoDia =
																									Array.isArray(
																										item
																											?.horariosParticionadosByDia?.[
																											dia
																												.value
																										],
																									)
																										? item
																												.horariosParticionadosByDia[
																												dia
																													.value
																											]
																										: [];

																								return (
																									<div
																										key={`${item.id}-${dia.value}`}
																										className='group relative flex flex-col items-stretch'>
																										<button
																											type='button'
																											disabled={
																												isBloqueadoPorParticaoIntegral
																											}
																											title={
																												isBloqueadoPorParticaoIntegral
																													? 'Este dia já está ocupado por outro setor. Rearranje o horário no mesmo dia do setor informado para que as horas faltantes sejam encaixadas neste dia.'
																													: undefined
																											}
																											onClick={() =>
																												toggleGerenciamentoTurnosSetorDia(
																													item.id,
																													dia.value,
																												)
																											}
																											className={`h-[30px] w-full rounded-md border px-2 text-[12px] font-medium transition ${
																												isBloqueadoPorParticaoIntegral
																													? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
																													: isSelected
																														? 'bg-[#004A80] border-[#004A80] text-white'
																														: 'bg-white border-gray-200 text-[#6B7280] hover:bg-[#F3F4F6]'
																											}`}>
																											{
																												dia.label
																											}
																										</button>
																										{isBloqueadoPorParticaoIntegral && (
																											<div
																												role='tooltip'
																												className='pointer-events-none absolute bottom-full left-1/2 z-[80] mb-2 hidden w-64 -translate-x-1/2 rounded bg-[#13335A] px-3 py-2 text-left text-[11px] font-normal leading-4 text-white shadow-lg group-hover:block'>
																												Este
																												dia
																												já
																												está
																												ocupado
																												por
																												outro
																												setor.
																												Rearranje
																												o
																												horário
																												no
																												mesmo
																												dia
																												do
																												setor
																												informado
																												para
																												que
																												as
																												horas
																												faltantes
																												sejam
																												encaixadas
																												neste
																												dia.
																											</div>
																										)}
																										{horariosParticionadosDoDia.length >
																										1 ? (
																											<details className='group relative mt-1 w-full'>
																												<summary
																													aria-label={`Ver todos os horários de ${dia.label}`}
																													className={`flex h-[30px] cursor-pointer list-none items-center justify-between rounded-md border px-2 text-[12px] font-medium [&::-webkit-details-marker]:hidden ${
																														isSelected
																															? 'border-[#B8C7D9] bg-[#F8FBFE] text-[#13335A] hover:bg-[#EEF6FB]'
																															: 'pointer-events-none border-gray-100 bg-gray-50 text-gray-300'
																													}`}>
																													<span className='min-w-0'>
																														<span className='truncate'>
																															{
																																horariosParticionadosDoDia[0]
																																	.hora_inicio
																															}{' '}
																															às{' '}
																															{
																																horariosParticionadosDoDia[0]
																																	.hora_fim
																															}
																														</span>
																													</span>
																													<span className='flex shrink-0 items-center text-[#4F6F8E]'>
																														<svg
																															aria-hidden='true'
																															viewBox='0 0 16 16'
																															className='h-4 w-4 transition-transform group-open:rotate-180'>
																															<path
																																d='m4 6 4 4 4-4'
																																fill='none'
																																stroke='currentColor'
																																strokeWidth='1.8'
																																strokeLinecap='round'
																																strokeLinejoin='round'
																															/>
																														</svg>
																													</span>
																												</summary>
																												<div className='absolute left-0 right-0 z-50 mt-1 rounded-md border border-[#B8C7D9] bg-white p-1.5 shadow-lg'>
																													<div className='flex flex-col gap-1'>
																														{horariosParticionadosDoDia.map(
																															(
																																horario,
																																horarioIndex,
																															) => (
																																<div
																																	key={`${item.id}-${dia.value}-${horario.hora_inicio}-${horario.hora_fim}-${horarioIndex}`}
																																	className='flex h-[28px] items-center rounded bg-[#F8FBFE] px-2 text-[12px] font-medium text-[#4F5257]'>
																																	{
																																		horario.hora_inicio
																																	}{' '}
																																	às{' '}
																																	{
																																		horario.hora_fim
																																	}
																																</div>
																															),
																														)}
																													</div>
																												</div>
																											</details>
																										) : (
																											<DiaSemanaHorarioBloco
																												disabled={
																													!isSelected ||
																													isBloqueadoPorParticaoIntegral
																												}
																												minWidthClassName='w-full'
																												marginTopClassName='mt-1'
																												horaInicio={
																													diaHorario.hora_inicio ||
																													''
																												}
																												horaFim={
																													diaHorario.hora_fim ||
																													''
																												}
																												onChangeHoraInicio={(
																													nextValue,
																												) =>
																													handleGerenciamentoTurnosSetorHorarioChange(
																														item.id,
																														dia.value,
																														'hora_inicio',
																														nextValue,
																													)
																												}
																												onChangeHoraFim={(
																													nextValue,
																												) =>
																													handleGerenciamentoTurnosSetorHorarioChange(
																														item.id,
																														dia.value,
																														'hora_fim',
																														nextValue,
																													)
																												}
																											/>
																										)}
																									</div>
																								);
																							},
																						)}
																					</div>
																				</div>
																			) : (
																				<div className='rounded-md border border-[#B8C7D9] bg-white px-2 py-2 shadow-sm'>
																					<p className='mb-2 text-[12px] font-medium text-[#13335A]'>
																						Escala
																						do
																						profissional
																					</p>
																					<div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
																						<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																							Ciclo
																							<input
																								type='text'
																								value={
																									cicloLabel
																								}
																								readOnly
																								className='h-[32px] rounded-md border border-gray-200 bg-[#F8FAFC] px-2 text-[12px] font-normal text-[#4F5257] outline-none'
																							/>
																						</label>
																						<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																							Hora
																							inicial
																							<input
																								type='text'
																								ref={bindGerenciamentoTurnosCicloInput(
																									item.id,
																									'horaInicio',
																								)}
																								value={
																									item.horaInicio ||
																									''
																								}
																								onChange={(
																									e,
																								) =>
																									handleGerenciamentoTurnosSetorCicloHorarioChange(
																										item.id,
																										'horaInicio',
																										e
																											.target
																											.value,
																									)
																								}
																								className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'
																							/>
																						</label>
																						<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																							Hora
																							final
																							<input
																								type='text'
																								ref={bindGerenciamentoTurnosCicloInput(
																									item.id,
																									'horaFim',
																								)}
																								value={
																									item.horaFim ||
																									''
																								}
																								onChange={(
																									e,
																								) =>
																									handleGerenciamentoTurnosSetorCicloHorarioChange(
																										item.id,
																										'horaFim',
																										e
																											.target
																											.value,
																									)
																								}
																								className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'
																							/>
																						</label>
																					</div>
																					{isRotacaoPeriodica && (
																						<div className='mt-3 rounded-md border border-[#B8C7D9] px-3 py-3 shadow-md'>
																							<div className='flex flex-wrap items-center justify-between gap-2'>
																								<div>
																									<p className='text-[13px] font-medium text-[#13335A]'>
																										Rotação
																										entre
																										semanas
																									</p>
																									<p className='text-[12px] text-[#4F647D]'>
																										Defina
																										os
																										dias
																										de
																										trabalho
																										de
																										cada
																										semana,
																										na
																										ordem
																										em
																										que
																										se
																										repetem.
																									</p>
																								</div>
																								<label className='text-[12px] font-medium text-[#13335A]'>
																									Semanas
																									<select
																										value={
																											item.periodoRotacao ||
																											semanasRotacao.length ||
																											2
																										}
																										onChange={(
																											event,
																										) =>
																											setGerenciamentoTurnosSetorRotacaoSemanas(
																												item.id,
																												event
																													.target
																													.value,
																											)
																										}
																										className='ml-2 h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'>
																										{[
																											2,
																											3,
																											4,
																											5,
																											6,
																										].map(
																											(
																												quantidade,
																											) => (
																												<option
																													key={
																														quantidade
																													}
																													value={
																														quantidade
																													}>
																													{
																														quantidade
																													}{' '}
																													semanas
																												</option>
																											),
																										)}
																									</select>
																								</label>
																							</div>
																							<div className='mt-3 grid gap-3 md:grid-cols-2'>
																								{semanasRotacao.map(
																									(
																										diasSemana,
																										semanaIndex,
																									) => (
																										<div
																											key={`${item.id}-semana-${semanaIndex}`}>
																											<p className='text-[12px] font-medium text-[#13335A]'>
																												Semana{' '}
																												{semanaIndex +
																													1}
																											</p>
																											<div className='mt-1 flex flex-wrap gap-1'>
																												{DIAS_SEMANA_OPTIONS.map(
																													(
																														dia,
																													) => (
																														<button
																															type='button'
																															key={`${item.id}-semana-${semanaIndex}-${dia.value}`}
																															aria-label={
																																dia.label
																															}
																															className={`inline-flex h-[28px] w-20 items-center justify-center rounded-md border px-2 text-[12px] ${diasSemana.includes(dia.value) ? 'border-[#004A80] bg-[#004A80] text-white' : 'border-gray-200 bg-white text-[#36516b]'}`}
																															onClick={() =>
																																toggleGerenciamentoTurnosSetorRotacaoDia(
																																	item.id,
																																	semanaIndex,
																																	dia.value,
																																)
																															}>
																															{
																																dia.label
																															}
																														</button>
																													),
																												)}
																											</div>
																										</div>
																									),
																								)}
																							</div>
																							{getFieldErrorMessage(
																								gerenciamentoTurnosErrors[
																									`${item.id}.rotacao`
																								],
																							)}
																						</div>
																					)}
																				</div>
																			)}

																			<div className='rounded-md border border-[#B8C7D9] bg-white px-2 py-2 shadow-sm'>
																				<div className='mb-2 flex items-center justify-between gap-2'>
																					<p className='text-[12px] font-medium text-[#13335A]'>
																						Dias
																						específicos
																					</p>
																					<button
																						type='button'
																						onClick={() =>
																							handleAdicionarGerenciamentoTurnosSetorData(
																								item.id,
																							)
																						}
																						className='flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-[18px] leading-none text-[#004A80] shadow-sm transition hover:bg-gray-50'
																						aria-label='Adicionar dia específico'>
																						+
																					</button>
																				</div>
																				<div className='flex flex-col gap-2'>
																					{item
																						.datasEspecificasEditaveis
																						?.length >
																					0 ? (
																						item.datasEspecificasEditaveis.map(
																							(
																								dataItem,
																							) => (
																								<div
																									key={
																										dataItem.id
																									}
																									className='grid grid-cols-[104px_112px_20px] items-center gap-1'
																			title={
																				dataItem.readOnly || dataItem.origem === 'troca'
																					? 'Data específica proveniente de troca: disponível somente para consulta.'
																					: dataItem.legado
																					? 'Data específica legada: disponível somente para consulta.'
																					: undefined
																									}>
																									<DateMaskInputWithPicker
																										value={
																											dataItem.data
																										}
																										placeholder='00/00/0000'
																										onChange={(
																											value,
																										) =>
																											handleGerenciamentoTurnosSetorDataChange(
																												item.id,
																												dataItem.id,
																												'data',
																												value,
																											)
																										}
																										ariaLabel={`Selecionar data específica de ${item.setorNome}`}
																				disabled={
																				dataItem.legado === true ||
																				dataItem.readOnly === true
																										}
																										className='h-[26px] w-[104px] min-w-0 rounded-md border border-gray-200 px-1.5 text-[11px] text-[#4F5257] outline-none'
																									/>
																									<DiaSemanaHorarioBloco
																										compact
																										showClockIcon={
																											false
																										}
																			disabled={
																				dataItem.legado === true ||
																				dataItem.readOnly === true
																										}
																										minWidthClassName='w-[112px] min-w-[112px]'
																										marginTopClassName='mt-0'
																										horaInicio={
																											dataItem.horaInicio ||
																											''
																										}
																										horaFim={
																											dataItem.horaFim ||
																											''
																										}
																										onChangeHoraInicio={(
																											nextValue,
																										) =>
																											handleGerenciamentoTurnosSetorDataChange(
																												item.id,
																												dataItem.id,
																												'horaInicio',
																												nextValue,
																											)
																										}
																										onChangeHoraFim={(
																											nextValue,
																										) =>
																											handleGerenciamentoTurnosSetorDataChange(
																												item.id,
																												dataItem.id,
																												'horaFim',
																												nextValue,
																											)
																										}
																									/>
																									<button
																										type='button'
																		disabled={
																				dataItem.legado === true ||
																				dataItem.readOnly === true
																										}
																										onClick={() =>
																											handleRemoverGerenciamentoTurnosSetorData(
																												item.id,
																												dataItem.id,
																											)
																										}
																										className='flex h-[26px] w-5 items-center justify-center rounded-full text-[18px] leading-none text-gray-400 transition hover:text-[#BD504E] disabled:cursor-not-allowed disabled:opacity-50'
																										aria-label='Remover dia específico'>
																										&times;
																									</button>
																								</div>
																							),
																						)
																					) : (
																						<span className='text-[11px] text-[#9CA3AF]'>
																							Sem
																							datas
																							específicas
																						</span>
																					)}
																				</div>
																			</div>
																		</div>
																	</fieldset>
																</div>
															</div>
														</div>
													</>
												);
											},
										)}
									</div>

									<div
										className={`transition-[opacity,margin] duration-300 ease-in-out ${
											isGerenciamentoTurnosNovoSetorAberto
												? 'mt-4 opacity-100'
												: 'mt-0 hidden opacity-0'
										}`}>
										<div className='rounded-md border border-dashed border-[#B8C7D9] bg-[#F8FAFC] p-3'>
											<div className='mb-2 flex items-center justify-between gap-3'>
												<p className='text-[13px] font-semibold text-[#13335A]'>
													Adicionar em outro setor
												</p>
												<button
													type='button'
													onClick={() => {
														setIsGerenciamentoTurnosNovoSetorAberto(
															false,
														);
														setPlanejamentoConfirmacaoDisponivel(
															false,
														);
														setPlanejamentoConfirmacaoSolicitacao(
															0,
														);
														setPlanejamentoNovoSetorForm(
															null,
														);
														setUsarSugestoesNovoSetor(
															false,
														);
													}}
													className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[22px] leading-none text-gray-400 transition-colors duration-300 hover:text-[#BD504E]'
													aria-label='Fechar bloco de adicionar em outro setor'>
													&times;
												</button>
											</div>
											<div className=''>
												<div className='grid grid-cols-1 gap-2 md:grid-cols-4'>
													<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
														Setor
														<SearchableSetorSelect
															value={
																gerenciamentoTurnosNovoSetor
															}
															options={
																gerenciamentoTurnosSetoresDisponiveis
															}
															onChange={
																handleGerenciamentoTurnosNovoSetorChange
															}
															getOptionLabel={
																formatSetorOptionLabel
															}
															className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'
														/>
													</label>
											<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
												Recorrência
												<input
													type='text'
													readOnly
													title='A recorrência é herdada do setor de origem.'
													value={
														gerenciamentoTurnosNovoRecorrenciaOption?.label ||
														gerenciamentoTurnosNovoRecorrencia
													}
													className='h-[32px] w-full min-w-0 rounded-md border border-gray-200 bg-gray-100 px-2 text-[12px] font-normal text-[#4F5257] outline-none'
												/>
											</label>
											<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
												Regime
												<input
													type='text'
													readOnly
													title='O regime é herdado do setor de origem.'
													value={gerenciamentoTurnosNovoRegime}
													className='h-[32px] w-full min-w-0 rounded-md border border-gray-200 bg-gray-100 px-2 text-[12px] font-normal text-[#4F5257] outline-none'
												/>
											</label>
													<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
														Data inicial
														<input
															type='date'
															min={getDataInicialMinimaNovoSetor(
																getGerenciamentoTurnosBaseLinha(),
															)}
																			value={formatDateInputToISO(
																				gerenciamentoTurnosNovoDataInicial,
																			)}
															disabled
																			title='A data inicial é herdada do setor de origem.'
															className='h-[32px] w-full min-w-0 rounded-md border border-gray-200 bg-gray-100 px-2 text-[12px] font-normal text-[#4F5257] outline-none'
														/>
													</label>
												</div>

												<div className='mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[1fr_260px]'>
													<div className='rounded-md border border-gray-200 bg-white px-2 py-2'>
														{gerenciamentoTurnosNovoUsaRecorrenciaSemanal ? (
															<>
																<p className='mb-2 text-[12px] font-medium text-[#13335A]'>
																	Dias
																	recorrentes
																</p>
																<div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4'>
																	{DIAS_SEMANA_OPTIONS.map(
																		(
																			dia,
																		) => {
																			const isSelected =
																				gerenciamentoTurnosNovoDias.includes(
																					dia.value,
																				);
																			const diaHorario =
																				gerenciamentoTurnosNovoHorarios?.[
																					dia
																						.value
																				] ||
																				{};

																			return (
																				<div
																					key={`novo-setor-${dia.value}`}
																					className='flex flex-col items-stretch'>
																					<button
																						type='button'
																						onClick={() =>
																							toggleGerenciamentoTurnosNovoDia(
																								dia.value,
																							)
																						}
																						className={`h-[30px] w-full rounded-md border px-2 text-[12px] font-medium transition ${
																							isSelected
																								? 'bg-[#004A80] border-[#004A80] text-white'
																								: 'bg-white border-gray-200 text-[#6B7280] hover:bg-[#F3F4F6]'
																						}`}>
																						{
																							dia.label
																						}
																					</button>
																					<DiaSemanaHorarioBloco
																						disabled={
																							!isSelected
																						}
																						minWidthClassName='w-full'
																						marginTopClassName='mt-1'
																						horaInicio={
																							diaHorario.hora_inicio ||
																							''
																						}
																						horaFim={
																							diaHorario.hora_fim ||
																							''
																						}
																						onChangeHoraInicio={(
																							nextValue,
																						) =>
																							handleGerenciamentoTurnosNovoHorarioChange(
																								dia.value,
																								'hora_inicio',
																								nextValue,
																							)
																						}
																						onChangeHoraFim={(
																							nextValue,
																						) =>
																							handleGerenciamentoTurnosNovoHorarioChange(
																								dia.value,
																								'hora_fim',
																								nextValue,
																							)
																						}
																					/>
																				</div>
																			);
																		},
																	)}
																</div>
															</>
														) : (
															<div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
																<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																	Ciclo
																	<input
																		type='text'
																		readOnly
																		value={
																			gerenciamentoTurnosNovoRecorrenciaOption?.label ||
																			'Ciclo contínuo'
																		}
																		className='h-[32px] rounded-md border border-gray-200 bg-[#F8FAFC] px-2 text-[12px] text-[#4F5257] outline-none'
																	/>
																</label>
																<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																	Hora inicial
																	<input
																		type='text'
																		value={
																			gerenciamentoTurnosNovoHoraInicio
																		}
																		onChange={(
																			e,
																		) =>
																			handleGerenciamentoTurnosNovoCicloHorarioChange(
																				'horaInicio',
																				e
																					.target
																					.value,
																			)
																		}
																		className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] text-[#4F5257] outline-none'
																	/>
																</label>
																<label className='flex flex-col gap-1 text-[11px] font-medium text-[#13335A]'>
																	Hora final
																	<input
																		type='text'
																		value={
																			gerenciamentoTurnosNovoHoraFim
																		}
																		onChange={(
																			e,
																		) =>
																			handleGerenciamentoTurnosNovoCicloHorarioChange(
																				'horaFim',
																				e
																					.target
																					.value,
																			)
																		}
																		className='h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] text-[#4F5257] outline-none'
																	/>
																</label>
															</div>
														)}
														{gerenciamentoTurnosNovoUsaRotacaoPeriodica && (
															<div className='mt-3 rounded-md border border-[#B8C7D9] px-3 py-3 shadow-sm'>
																<div className='flex items-center justify-between gap-2'>
																	<p className='text-[13px] font-medium text-[#13335A]'>
																		Rotação
																		entre
																		semanas
																	</p>
																	<label className='text-[12px] font-medium text-[#13335A]'>
																		Semanas
																		<select
																			value={
																				gerenciamentoTurnosNovoPeriodoRotacao
																			}
																			disabled
																			title='O período da rotação é herdado do setor de origem.'
																			className='ml-2 h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none'>
																			{[
																				2,
																				3,
																				4,
																				5,
																				6,
																			].map(
																				(
																					quantidade,
																				) => (
																					<option
																						key={
																							quantidade
																						}
																						value={
																							quantidade
																						}>
																						{
																							quantidade
																						}{' '}
																						semanas
																					</option>
																				),
																			)}
																		</select>
																	</label>
																</div>
																<div className='mt-3 grid gap-3 md:grid-cols-2'>
																	{gerenciamentoTurnosNovoSemanasRotacao.map(
																		(
																			diasSemana,
																			semanaIndex,
																		) => (
																			<div
																				key={
																					'novo-setor-semana-' +
																					semanaIndex
																				}>
																				<p className='text-[12px] font-medium text-[#13335A]'>
																					Semana{' '}
																					{semanaIndex +
																						1}
																				</p>
																				<div className='mt-1 flex flex-wrap gap-1'>
																					{DIAS_SEMANA_OPTIONS.map(
																						(
																							dia,
																						) => (
																							<button
																								type='button'
																								key={
																									'novo-setor-semana-' +
																									semanaIndex +
																									'-' +
																									dia.value
																								}
																								onClick={() =>
																									toggleGerenciamentoTurnosNovoRotacaoDia(
																										semanaIndex,
																										dia.value,
																									)
																								}
																								className={
																									'inline-flex h-[28px] w-20 items-center justify-center rounded-md border px-2 text-[12px] ' +
																									(diasSemana.includes(
																										dia.value,
																									)
																										? 'border-[#004A80] bg-[#004A80] text-white'
																										: 'border-gray-200 bg-white text-[#36516b]')
																								}>
																								{
																									dia.label
																								}
																							</button>
																						),
																					)}
																				</div>
																			</div>
																		),
																	)}
																</div>
																{getFieldErrorMessage(
																	gerenciamentoTurnosErrors[
																		'novo.rotacao'
																	],
																)}
															</div>
														)}
													</div>

													<div className='rounded-md border border-gray-200 bg-white px-2 py-2'>
														<div className='mb-2 flex items-center justify-between gap-2'>
															<p className='text-[12px] font-medium text-[#13335A]'>
																Dias específicos
															</p>
															<button
																type='button'
																onClick={
																	handleAdicionarGerenciamentoTurnosNovoData
																}
																className='flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-[18px] leading-none text-[#004A80] shadow-sm transition hover:bg-gray-50'
																aria-label='Adicionar dia específico'>
																+
															</button>
														</div>
														<div className='flex flex-col gap-2'>
															{gerenciamentoTurnosNovoDatas.length >
															0 ? (
																gerenciamentoTurnosNovoDatas.map(
																	(
																		dataItem,
																	) => (
																		<div
																			key={
																				dataItem.id
																			}
																			className='grid grid-cols-[104px_112px_20px] items-center gap-1'>
																			<DateMaskInputWithPicker
																				value={
																					dataItem.data
																				}
																				placeholder='00/00/0000'
																				onChange={(
																					value,
																				) =>
																					handleGerenciamentoTurnosNovoDataChange(
																						dataItem.id,
																						'data',
																						value,
																					)
																				}
																				ariaLabel='Selecionar data específica do novo setor'
																				className='h-[26px] w-[104px] min-w-0 rounded-md border border-gray-200 px-1.5 text-[11px] text-[#4F5257] outline-none'
																			/>
																			<DiaSemanaHorarioBloco
																				compact
																				showClockIcon={
																					false
																				}
																				minWidthClassName='w-[112px] min-w-[112px]'
																				marginTopClassName='mt-0'
																				horaInicio={
																					dataItem.horaInicio ||
																					''
																				}
																				horaFim={
																					dataItem.horaFim ||
																					''
																				}
																				onChangeHoraInicio={(
																					nextValue,
																				) =>
																					handleGerenciamentoTurnosNovoDataChange(
																						dataItem.id,
																						'horaInicio',
																						nextValue,
																					)
																				}
																				onChangeHoraFim={(
																					nextValue,
																				) =>
																					handleGerenciamentoTurnosNovoDataChange(
																						dataItem.id,
																						'horaFim',
																						nextValue,
																					)
																				}
																			/>
																			<button
																				type='button'
																				onClick={() =>
																					handleRemoverGerenciamentoTurnosNovoData(
																						dataItem.id,
																					)
																				}
																				className='flex h-[26px] w-5 items-center justify-center rounded-full text-[18px] leading-none text-gray-400 transition hover:text-[#BD504E]'
																				aria-label='Remover dia específico'>
																				&times;
																			</button>
																		</div>
																	),
																)
															) : (
																<span className='text-[11px] text-[#9CA3AF]'>
																	Sem datas
																	específicas
																</span>
															)}
														</div>
													</div>
												</div>
												{usarSugestoesNovoSetor && modalidade === 'ENFERMAGEM' && (
													<div className='mt-6 border-t border-dashed border-[#B8C7D9] pt-5'>
														<div className='mb-3 flex items-center justify-between'>
															<p className='text-[13px] font-semibold text-[#13335A]'>Sugestões de escala</p>
															<button type='button' onClick={() => setUsarSugestoesNovoSetor(false)} className='text-[12px] font-medium text-[#36516B] underline'>Fechar sugestões</button>
														</div>
														<PlanejamentoProfissionalEnfermagem
														token={token}
														contexto={contextoAplicado}
														linhas={gerenciamentoTurnosLinha?.setores_linhas || []}
														vinculoProfissionalId={getVinculoIdFromLinha(getGerenciamentoTurnosBaseLinha())}
														hideVinculoSelector
														setoresDestino={gerenciamentoTurnosSetoresDisponiveis}
															initialIntent={{
																setorDestinoId: gerenciamentoTurnosNovoSetor,
																preset: getPresetPlanejamentoDoNovoSetor(),
																cicloPersonalizado: normalizeCode(gerenciamentoTurnosNovoRecorrencia).includes('24X120')
																	? {
																		trabalhoHoras: 24,
																		descansoHoras: 120,
																		dataAncora: formatDateInputToISO(gerenciamentoTurnosNovoDataInicial) || getTodayISODateLocal(),
																	}
																	: null,
																horaInicio: gerenciamentoTurnosNovoHoraInicio,
																horaFim: gerenciamentoTurnosNovoHoraFim,
																diasPadrao: gerenciamentoTurnosNovoDias,
																periodoRotacao: gerenciamentoTurnosNovoPeriodoRotacao,
																semanasRotacao: gerenciamentoTurnosNovoSemanasRotacao,
															}}
															compact
															autoGenerate
															confirmRequestVersion={planejamentoConfirmacaoSolicitacao}
															onConfirmAvailabilityChange={setPlanejamentoConfirmacaoDisponivel}
																		onApplied={handlePlanejamentoEnfermagemApplied}
													/>
													</div>
												)}
												{(submitErrorScope ===
													'gerenciamentoTurnosNovoSetor' &&
													submitError) ||
												Object.entries(
													gerenciamentoTurnosErrors,
												).some(([key]) =>
													key.startsWith('novo.'),
												) ? (
													<div
														className='mt-4 rounded-md border border-[#F5C2C7] bg-[#FDECEE] px-3 py-2 text-[12px] leading-relaxed text-[#A61B29]'
														role='alert'>
														<div>
															{submitErrorScope ===
																'gerenciamentoTurnosNovoSetor' &&
															submitError
																? submitError
																: Array.from(
																		new Set(
																			Object.entries(
																				gerenciamentoTurnosErrors,
																			)
																				.filter(
																					([
																						key,
																					]) =>
																						key.startsWith(
																							'novo.',
																						),
																				)
																				.map(
																					([
																						,
																						message,
																					]) =>
																						message,
																				)
																				.filter(
																					Boolean,
																				),
																		),
																																						).join(' ')}
																																				</div>
																							</div>
												) : null}
												{sugestaoJornadaDisponivel && !usarSugestoesNovoSetor && (
													<button
														type='button'
														onClick={() => setUsarSugestoesNovoSetor(true)}
														disabled={isSubmittingGerenciamentoTurnos}
														className='mt-3 h-[30px] rounded-md border border-[#004A80] bg-white px-3 text-[12px] font-semibold text-[#004A80] transition hover:bg-[#EEF4FA] disabled:cursor-not-allowed disabled:opacity-60'>
														Gerar alternativas
													</button>
												)}
											</div>
										</div>
									</div>
									{!isGerenciamentoTurnosNovoSetorAberto && (
										<div className='mt-6 flex justify-center'>
											<button
												type='button'
												onClick={() => {
													setPlanejamentoConfirmacaoDisponivel(
														false,
													);
													setPlanejamentoConfirmacaoSolicitacao(
														0,
													);
													setPlanejamentoNovoSetorForm(
														null,
													);
																			handleAbrirNovoSetorComConfiguracaoDaOrigem();
												}}
												className='flex h-8 w-8 items-center justify-center rounded-md border border-[#004A80] bg-white text-[22px] leading-none text-[#004A80] shadow-sm transition-all duration-300 ease-out hover:bg-[#EEF4FA]'
												aria-expanded={
													isGerenciamentoTurnosNovoSetorAberto
												}
												aria-label='Adicionar em outro setor'>
												+
											</button>
										</div>
									)}
								</>
							) : (
								<div
									className='flex min-h-[260px] items-center justify-center text-center text-[13px] font-medium text-[#A61B29]'
									role='alert'>
									{gerenciamentoTurnosLoadError ||
										'Não foi possível validar os setores e turnos deste profissional.'}
								</div>
							)}
						</div>

						<div className='mt-auto flex shrink-0 justify-center gap-3 border-t border-gray-100 px-4 py-3'>
							<button
								type='button'
								onClick={handleCloseGerenciamentoTurnosModal}
								className='h-[34px] w-[130px] rounded-md border border-gray-200 text-[13px] text-[#7b7d80] shadow-md transition hover:bg-gray-50'>
								Fechar
							</button>
							<button
								type='button'
									onClick={() => {
										if (usarSugestoesNovoSetor) {
											setPlanejamentoConfirmacaoSolicitacao((value) => value + 1);
											return;
										}
										handleSubmitGerenciamentoTurnos();
									}}
									disabled={
										isSubmittingGerenciamentoTurnos ||
										isGerenciamentoTurnosLoading ||
										!isGerenciamentoTurnosReady ||
										(usarSugestoesNovoSetor && !planejamentoConfirmacaoDisponivel)
								}
								className='h-[34px] w-[150px] rounded-md border border-[#004A80] bg-[#004A80] text-[13px] text-white shadow-md transition hover:bg-[#0d466c] disabled:cursor-not-allowed disabled:opacity-60'>
								{isGerenciamentoTurnosLoading
									? 'Carregando...'
									: !isGerenciamentoTurnosReady
										? 'Indisponível'
										: usarSugestoesNovoSetor
											? 'Aplicar sugestão'
											: isGerenciamentoTurnosNovoSetorAberto
											? 'Criar escala'
											: isSubmittingGerenciamentoTurnos
												? 'Salvando...'
												: 'Salvar'}
							</button>
						</div>
					</div>
				</div>
			)}

			{origemAtivaPendente && (
				<div
					className='fixed inset-0 z-[70] flex items-center justify-center px-4'
					role='dialog'
					aria-modal='true'
					aria-labelledby='origem-ativa-decisao-title'>
					<div className='absolute inset-0 bg-black/40' />
					<div className='relative w-full max-w-[620px] rounded-md bg-white shadow-xl'>
						<div className='border-b border-gray-200 px-5 py-4'>
							<h3
								id='origem-ativa-decisao-title'
								className='text-[17px] font-semibold text-[#13335A]'>
								Este profissional já possui uma jornada ativa
							</h3>
						</div>
						<div className='space-y-3 px-5 py-4 text-[13px] leading-relaxed text-[#4F5257]'>
							<p>
								O novo setor será incluído na jornada existente.
								Dias que já coincidem com a origem serão
								particionados; dias novos serão preparados como
								padrões isolados dessa mesma jornada.
							</p>
							{origemAtivaPendente.candidatas.length > 1 && (
								<label className='flex flex-col gap-1 font-medium text-[#13335A]'>
									Jornada de origem
									<select
										value={
											origemAtivaPendente.alocacaoOrigemIdSelecionada ||
											''
										}
										onChange={(event) =>
											setOrigemAtivaPendente((atual) => ({
												...atual,
												alocacaoOrigemIdSelecionada:
													event.target.value,
											}))
										}
										className='h-[34px] rounded-md border border-gray-200 bg-white px-3 text-[13px] font-normal text-gray-600'>
										<option value=''>
											Selecione a jornada
										</option>
										{origemAtivaPendente.candidatas.map(
											(candidata = {}) => {
												const id =
													getAlocacaoOrigemIdFromPayload(
														candidata,
													);
												const setor =
													candidata?.setor_origem || {
														id: candidata?.setor_origem_id,
													};
												return (
													<option
														key={id}
														value={id}>
														{getLinhaSetorNome(
															{ setor },
															setorOptions,
														) || `Jornada ${id}`}
													</option>
												);
											},
										)}
									</select>
								</label>
							)}
							<div className='rounded-md border border-[#B9D9F7] bg-[#F2F8FE] p-3'>
								<p className='font-semibold text-[#13335A]'>
									Dividir a jornada atual entre setores
								</p>
								<p className='mt-1'>
									Recomendado. Gera uma prévia com todas as
									partições atuais mais o novo setor, antes de
									qualquer aplicação.
								</p>
							</div>
						</div>
						<div className='flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-3'>
							<button
								type='button'
								onClick={() => setOrigemAtivaPendente(null)}
								disabled={isSubmittingAdicionar}
								className='h-[34px] rounded-md border border-gray-200 px-4 text-[13px] text-[#4F5257] hover:bg-gray-50 disabled:opacity-60'>
								Cancelar
							</button>
							<button
								type='button'
								onClick={escolherParticaoDaOrigemAtiva}
								disabled={isSubmittingAdicionar}
								className='h-[34px] rounded-md border border-[#004A80] bg-[#004A80] px-4 text-[13px] text-white hover:bg-[#0d466c] disabled:opacity-60'>
								{isSubmittingAdicionar
									? 'Preparando...'
									: 'Continuar'}
							</button>
						</div>
					</div>
				</div>
			)}

			{particionamentoPendente && (
				<div
					className='fixed inset-0 z-[60] flex items-center justify-center px-4'
					role='dialog'
					aria-modal='true'
					aria-labelledby='particionamento-preview-title'>
					<button
						type='button'
						className='absolute inset-0 bg-black/30'
						disabled={isSubmittingGerenciamentoTurnos}
						onClick={() => setParticionamentoPendente(null)}
						aria-label='Fechar prévia da divisão de jornada'
					/>
					<div className='relative w-full max-w-[520px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl'>
						<div className='border-b border-gray-100 bg-[#fcfcfc] px-4 py-3'>
							<h3
								id='particionamento-preview-title'
								className='text-[16px] font-semibold text-[#13335A]'>
								{particionamentoPendente.tipo ===
								'REMOVER_DESTINO_PARTICIONADO'
									? 'Confirmar remoção do recorte'
									: particionamentoPendente.tipo ===
										  'ESCOLHER_ALTERNATIVA_JORNADA_ASSISTIDA'
										? 'Escolher alternativa de jornada'
										: particionamentoPendente.tipo ===
											  'CRIAR_ALOCACOES_INDEPENDENTES'
											? 'Confirmar criação de horário'
											: particionamentoPendente.tipo ===
												  'CRIAR_ORIGEM_INDEPENDENTE'
												? 'Confirmar criação de carga independente'
												: particionamentoPendente.tipo ===
													  'CRIAR_ORIGEM_INICIAL'
													? 'Confirmar criação de jornada'
													: particionamentoPendente.tipo ===
														  'ATUALIZAR_JORNADA_ORIGEM'
														? 'Confirmar atualização da jornada'
														: particionamentoPendente.tipo ===
															  'CRIAR_DESTINO_PARTICIONADO'
															? 'Confirmar inserção do profissional em outro setor'
															: 'Confirmar divisão de jornada'}
							</h3>
						</div>
						<div className='px-4 py-4 text-[13px] leading-relaxed text-[#4F5257]'>
							<p>
								{particionamentoPendente.tipo ===
								'REMOVER_DESTINO_PARTICIONADO' ? (
									'O setor derivado será removido e a jornada de origem voltará ao estado definido pelas faixas restantes.'
								) : particionamentoPendente.tipo ===
								  'ESCOLHER_ALTERNATIVA_JORNADA_ASSISTIDA' ? (
									'Escolha uma das alternativas abaixo para confirmar a edição.'
								) : particionamentoPendente.tipo ===
										'CRIAR_ALOCACOES_INDEPENDENTES' ||
								  particionamentoPendente.tipo ===
										'CRIAR_ORIGEM_INDEPENDENTE' ? (
									<>
										Esta é uma carga independente escolhida
										explicitamente. A prévia foi produzida
										pela ação devolvida pelo servidor e não
										altera a jornada de origem existente.
									</>
								) : particionamentoPendente.tipo ===
								  'CRIAR_ORIGEM_INICIAL' ? (
									<>
										O profissional será adicionado à escala
										deste setor conforme o período escolhido
										para ele. Posteriormente, a inserção
										deste mesmo profissional em outros
										setores será afetada pelos dias e
										horários definidos anteriormente.
									</>
								) : particionamentoPendente.tipo ===
								  'ATUALIZAR_JORNADA_ORIGEM' ? (
									<>
										A jornada será recalculada e atualizada
										com base nas mudanças feitas. Setores e
										horários vinculados a este setor serão
										afetados por esta mudança.
									</>
								) : particionamentoPendente.tipo ===
								  'CRIAR_DESTINO_PARTICIONADO' ? (
									<>
										Os horários e dias criados neste novo setor
										influenciarão e serão influenciados pela escala de
										outros setores criados anteriormente, como em{' '}
										<strong>
											{particionamentoPendente?.origem?.nome ||
												getLinhaSetorNome(
													{
														setor_id:
															particionamentoPendente?.origem?.setor,
													},
													setorOptions,
												)}
										</strong>
										.
									</>
								) : (
									<>
										O novo horário usará como referência o
										horário do setor-base{' '}
										<strong>
											{particionamentoPendente?.origem
												?.nome ||
												getLinhaSetorNome(
													{
														setor_id:
															particionamentoPendente
																?.origem?.setor,
													},
													setorOptions,
												)}
										</strong>
										, que foi o primeiro setor criado nesta
										jornada.
									</>
								)}
							</p>
							{particionamentoPendente.tipo ===
							'ATUALIZAR_JORNADA_ORIGEM'
								? (() => {
										const horario =
											getResumoHorarioAtualizacaoJornada(
												particionamentoPendente.resumoEdicao,
											);
										const faixasAtualizadas = Array.from(
											new Map(
												(
													particionamentoPendente.resumoFaixasAtualizadas ||
													[]
												).map((faixa = {}) => [
													`${faixa.dia}-${faixa.inicio}-${faixa.fim}`,
													faixa,
												]),
											).values(),
										);
										const setor =
											particionamentoPendente
												.processos?.[0]?.detalhe ||
											particionamentoPendente?.origem
												?.nome ||
											'Setor informado';
										return (
											<div className='mt-3 rounded-md border border-[#D7E3EF] bg-[#F7FAFD] px-3 py-2'>
												<p className='text-[12px] font-semibold text-[#13335A]'>
													Operações que serão
													aplicadas
												</p>
												<div className='mt-1 space-y-1 text-[12px] text-[#4F5257]'>
													<p>
														<strong>Atualização:</strong>{' '}
														{faixasAtualizadas.length > 0
															? faixasAtualizadas
																	.map(
																		(faixa = {}) =>
																			`${faixa.dia}, das ${faixa.inicio} às ${faixa.fim}`,
																	)
																	.join('; ')
															: `de ${horario.anterior} para ${horario.atual}`}
													</p>
													<p>
														<strong>Setor:</strong>{' '}
														{setor}
													</p>
												</div>
											</div>
										);
									})()
								: Array.isArray(
										particionamentoPendente.processos,
									) &&
									particionamentoPendente.processos.length >
										0 && (
										<div className='mt-3 rounded-md border border-[#D7E3EF] bg-[#F7FAFD] px-3 py-2'>
											<p className='text-[12px] font-semibold text-[#13335A]'>
												Operações que serão aplicadas
											</p>
											<ol className='mt-1 list-decimal space-y-1 pl-4 text-[12px] text-[#4F5257]'>
												{particionamentoPendente.processos.map(
													(processo = {}, indice) => (
														<li
															key={`${processo.tipo}-${processo.alvo}-${processo.detalhe}-${indice}`}>
															<strong>
																{processo.tipo}
															</strong>
															: {processo.alvo}
															{processo.detalhe
																? ` — ${processo.detalhe}`
																: ''}
														</li>
													),
												)}
											</ol>
										</div>
									)}
							{particionamentoPendente.tipo ===
								'ESCOLHER_ALTERNATIVA_JORNADA_ASSISTIDA' && (
								<div className='mt-3 grid gap-2'>
									{(
										particionamentoPendente.alternativas ||
										[]
									).map((alternativa = {}) => {
										const modalidadeAlternativa = String(
											alternativa?.preset_proposto ||
												alternativa?.preset_solicitado ||
												alternativa?.padrao_proposto
													?.preset_ciclo ||
												'Modalidade da escala',
										)
											.replace(/^CICLO_/i, '')
											.replace(/_/g, ' ');
										const padraoAlternativa =
											alternativa?.padrao_proposto ||
											alternativa?.padrao_desejado ||
											{};
										const mantidos =
											particionamentoPendente
												?.resumoEdicao?.mantidos || [];
										const editados = (
											particionamentoPendente
												?.resumoEdicao?.editados || []
										).map((campo = {}) => {
											const valorDaAlternativa =
												campo.chave === 'preset_ciclo'
													? alternativa?.preset_proposto ||
														alternativa?.preset_solicitado ||
														padraoAlternativa?.preset_ciclo
													: padraoAlternativa?.[
															campo.chave
														];
											return {
												...campo,
												valor: valorDaAlternativa
													? formatResumoEdicaoOrigemValue(
															campo.chave,
															valorDaAlternativa,
														)
													: campo.valor,
											};
										});
										return (
											<button
												type='button'
												key={
													alternativa?.sugestao_id ||
													`${alternativa?.ordem || 'alternativa'}-${modalidadeAlternativa}`
												}
												disabled={
													isSubmittingGerenciamentoTurnos
												}
												onClick={() =>
													prepararPreviewAlternativaJornadaAssistida(
														alternativa,
													)
												}
												className='group relative cursor-pointer rounded-lg border border-[#9BB8D3] bg-white px-3 py-3 text-left shadow-sm transition-all duration-150 hover:-translate-y-px hover:border-[#004A80] hover:bg-[#F8FBFE] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004A80] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60'>
												<span className='absolute right-3 top-3 rounded-full bg-[#EAF3FA] px-2 py-0.5 text-[10px] font-semibold text-[#004A80] transition-colors group-hover:bg-[#004A80] group-hover:text-white'>
													Selecionar
												</span>
												<span className='block pr-20 text-[12px] font-semibold text-[#13335A]'>
													Alternativa{' '}
													{alternativa?.ordem || ''}:{' '}
													{modalidadeAlternativa}
												</span>
												<span className='mt-0.5 block text-[11px] text-[#4F647D]'>
													Média semanal:{' '}
													{formatMinutesAsHours(
														alternativa?.carga_media_semanal_minutos ||
															alternativa?.media_semanal_minutos,
													)}
												</span>
												<div className='mt-2 grid gap-2 border-t border-[#E4ECF3] pt-2 text-[11px] leading-snug'>
													{mantidos.length > 0 && (
														<div>
															<span className='font-semibold text-[#4F647D]'>
																Mantido
															</span>
															<div className='mt-0.5 grid gap-0.5 text-[#63778C]'>
																{mantidos.map(
																	(campo) => (
																		<span
																			key={`mantido-${campo.chave}`}>
																			{
																				campo.label
																			}
																			:{' '}
																			{
																				campo.valor
																			}
																		</span>
																	),
																)}
															</div>
														</div>
													)}
													{editados.length > 0 && (
														<div>
															<span className='font-semibold text-[#0D5B99]'>
																Editado
															</span>
															<div className='mt-0.5 grid gap-0.5 text-[#36516B]'>
																{editados.map(
																	(campo) => (
																		<span
																			key={`editado-${campo.chave}`}>
																			{
																				campo.label
																			}
																			:{' '}
																			{
																				campo.valor
																			}
																		</span>
																	),
																)}
															</div>
														</div>
													)}
												</div>
											</button>
										);
									})}
								</div>
							)}
								{particionamentoPendente.tipo ===
									'CRIAR_DESTINO_PARTICIONADO' ? (
									<p className='mt-2'>
										Ausências, exclusões ou alterações feitas nas escalas
										dos setores anteriormente criados podem afetar a criação
										de novos dias neste setor.
									</p>
								) : (
									particionamentoPendente.tipo !==
										'REMOVER_DESTINO_PARTICIONADO' &&
									particionamentoPendente.tipo !==
										'CRIAR_ALOCACOES_INDEPENDENTES' &&
								particionamentoPendente.tipo !==
									'CRIAR_ORIGEM_INDEPENDENTE' &&
								particionamentoPendente.tipo !==
									'ESCOLHER_ALTERNATIVA_JORNADA_ASSISTIDA' &&
								particionamentoPendente.tipo !==
									'ATUALIZAR_JORNADA_ORIGEM' && (
										<p className='mt-2'>
											Ele ocupará apenas o período selecionado
										desse horário. Por isso, ausências,
										exclusões e alterações feitas no
										setor-base também podem afetar este novo
										horário.
										</p>
									)
								)}
							{particionamentoPendente.issues.length > 0 && (
								<div className='mt-3 rounded-md border border-[#F2D49B] bg-[#FFF7E6] px-3 py-2 text-[12px] text-[#8A5A00]'>
									{particionamentoPendente.issues
										.map(
											(issue) =>
												issue?.mensagem ||
												issue?.message ||
												issue?.codigo,
										)
										.filter(Boolean)
										.join(' ')}
								</div>
							)}
						</div>
						<div className='flex justify-end gap-2 border-t border-gray-100 px-4 py-3'>
							<button
								type='button'
								disabled={isSubmittingGerenciamentoTurnos}
								onClick={() => setParticionamentoPendente(null)}
								className='h-[34px] rounded-md border border-gray-200 px-4 text-[13px] text-[#4F5257] transition hover:bg-gray-50 disabled:opacity-60'>
								Cancelar
							</button>
							{particionamentoPendente.tipo !==
								'ESCOLHER_ALTERNATIVA_JORNADA_ASSISTIDA' && (
								<button
									type='button'
									disabled={isSubmittingGerenciamentoTurnos}
									onClick={confirmarParticionamentoNovoSetor}
									className='h-[34px] rounded-md border border-[#004A80] bg-[#004A80] px-4 text-[13px] text-white transition hover:bg-[#0d466c] disabled:opacity-60'>
									{isSubmittingGerenciamentoTurnos
										? 'Aplicando...'
										: 'Confirmar'}
								</button>
							)}
						</div>
					</div>
				</div>
			)}

			{isVacanciaModalOpen && (
				<div
					className='fixed inset-x-0 bottom-0 top-20 z-50 flex items-center justify-center overflow-hidden px-4 py-4 max-sm:px-2'
					role='dialog'
					aria-modal='true'
					aria-label={
						isViewingVacancia
							? 'Visualizar vacância'
							: isEditingVacancia
								? 'Editar vacância'
								: 'Adicionar vacância'
					}>
					<button
						type='button'
						className='absolute inset-0 bg-black/30'
						onClick={handleCloseVacanciaModal}
						aria-label='Fechar modal'
					/>

					<div className='relative w-[92%] max-w-[800px] max-h-[calc(100vh-7rem)] overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl max-lg:w-full sm:max-lg:max-w-[620px] max-sm:max-w-[350px]'>
						<div className='bg-[#fcfcfc] w-full h-[25px] flex justify-end px-1 rounded-tl-md rounded-tr-md'>
							<button
								type='button'
								onClick={handleCloseVacanciaModal}
								className='text-gray-400 hover:text-gray-600 items-center align-middle flex content-center text-3xl pb-2 transition-all duration-300 text-center rounded-full'
								aria-label='Fechar'>
								&times;
							</button>
						</div>

						<div className='max-h-[calc(100vh-9rem)] overflow-x-hidden overflow-y-auto px-5 py-5 font-inter max-sm:px-3 max-sm:py-3'>
							<div className='bg-[#BD504E] rounded-md h-[42px] flex items-center justify-center shadow-sm max-sm:h-[34px]'>
								<span className='text-white text-[16px] font-semibold tracking-wide max-sm:text-[14px]'>
									{isViewingVacancia
										? 'VACÂNCIA'
										: isEditingVacancia
											? 'EDITAR VACÂNCIA'
											: 'VACÂNCIA'}
								</span>
							</div>

							<hr className='border-gray-200 my-4 max-sm:my-3' />

							{isSetorTodosAplicado && (
								<div className='mb-4 rounded-md border border-[#D8DEE8] bg-[#F8FAFC] p-3'>
									<label className='mb-1 block text-[14px] font-medium text-[#13335A]'>
										Setor
									</label>
									<SearchableSetorSelect
										value={vacanciaForm.setor_id}
										options={setorOptions}
										disabled={vacanciaModalReadOnly}
										onChange={(value) =>
											handleVacanciaInput(
												'setor_id',
												value,
											)
										}
										getOptionLabel={formatSetorOptionLabel}
										className='h-[34px] rounded-md border border-gray-200 bg-white px-3 text-[13px] text-gray-600 outline-none disabled:bg-[#F3F4F6] disabled:text-[#6B7280]'
									/>
								</div>
							)}

							<div className='grid grid-cols-1 lg:grid-cols-2 gap-x-2 gap-y-4 max-sm:gap-y-3'>
								{/* Início da vacância */}
								<div>
									<label className='block text-[14px] font-medium text-[#13335A] mb-1'>
										Início da vacância
									</label>
									<div className='relative'>
										<input
											type='text'
											placeholder='00/00/0000'
											value={
												vacanciaForm.data_inicio_vacancia
											}
											onChange={(e) =>
												handleVacanciaInput(
													'data_inicio_vacancia',
													e.target.value,
												)
											}
											readOnly={vacanciaModalReadOnly}
											aria-invalid={Boolean(
												vacanciaErrors.data_inicio_vacancia,
											)}
											className={`w-full h-[34px] border rounded-md px-3 pr-10 text-[13px] outline-none ${vacanciaModalReadOnly ? 'bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed' : 'bg-white'} ${vacanciaHasDataInicio ? 'text-gray-600' : 'text-gray-400'} ${vacanciaErrors.data_inicio_vacancia ? 'border-[#dc2626]' : 'border-gray-200'}`}
										/>
										<input
											ref={vacanciaDatePickerRef}
											type='date'
											min={getTodayISODateLocal()}
											value={formatDateInputToISO(
												vacanciaForm.data_inicio_vacancia,
											)}
											disabled={vacanciaModalReadOnly}
											onChange={(e) =>
												handleVacanciaInput(
													'data_inicio_vacancia',
													formatISOToDisplay(
														e.target.value,
													),
												)
											}
											className='absolute opacity-0 pointer-events-none'
											tabIndex={-1}
										/>
										<button
											type='button'
											onClick={() =>
												vacanciaDatePickerRef.current?.showPicker?.() ||
												vacanciaDatePickerRef.current?.click?.()
											}
											className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'
											disabled={vacanciaModalReadOnly}
											aria-label='Selecionar data de início da vacância'>
											<MdCalendarToday className='w-4 h-4' />
										</button>
									</div>
									{getFieldErrorMessage(
										vacanciaErrors.data_inicio_vacancia,
									)}
								</div>

								{/* Especialidade */}
								<div>
									<label className='block text-[14px] font-medium text-[#13335A] mb-1'>
										{selectRegimeLabel}
									</label>
									<select
										value={vacanciaForm.especialidade_id}
										disabled={vacanciaModalReadOnly}
										onChange={(e) =>
											handleVacanciaInput(
												'especialidade_id',
												e.target.value,
											)
										}
										aria-invalid={Boolean(
											vacanciaErrors.especialidade_id,
										)}
										className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none ${vacanciaModalReadOnly ? 'bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed' : 'bg-white'} ${vacanciaHasEspecialidade ? 'text-gray-600' : 'text-gray-300'} ${vacanciaErrors.especialidade_id ? 'border-[#dc2626]' : 'border-gray-200'}`}>
										<option
											value=''
											className='text-gray-300'>
											Selecione
										</option>
										{dimensaoOptions.map((item) => (
											<option
												key={item?.id}
												value={item?.id}
												className='text-gray-600'>
												{item?.nome ||
													item?.label ||
													item?.id}
											</option>
										))}
									</select>
									{getFieldErrorMessage(
										vacanciaErrors.especialidade_id,
									)}
								</div>

								{/* Vínculo */}
								<div>
									<label className='block text-[14px] font-medium text-[#13335A] mb-1'>
										Vínculo
									</label>
									<select
										value={vacanciaForm.vinculo_tipo_id}
										disabled={
											vacanciaModalReadOnly ||
											vinculoOpcoes.length === 0
										}
										onChange={(e) =>
											handleVacanciaInput(
												'vinculo_tipo_id',
												e.target.value,
											)
										}
										aria-invalid={Boolean(
											vacanciaErrors.vinculo_tipo_id,
										)}
										className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none ${vacanciaModalReadOnly || vinculoOpcoes.length === 0 ? 'bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed' : 'bg-white'} ${vacanciaHasVinculo ? 'text-gray-600' : 'text-gray-300'} ${vacanciaErrors.vinculo_tipo_id ? 'border-[#dc2626]' : 'border-gray-200'}`}>
										<option
											value=''
											className='text-gray-300'>
											{vinculoOpcoes.length > 0
												? 'Selecione'
												: 'Nenhum vínculo disponível'}
										</option>
										{vacanciaForm.vinculo_tipo_id &&
											!vinculoOpcoes.some(
												(v) =>
													String(v.value) ===
													String(
														vacanciaForm.vinculo_tipo_id,
													),
											) && (
												<option
													value={
														vacanciaForm.vinculo_tipo_id
													}
													className='text-gray-600'>
													{vacanciaForm.nome_vinculo ||
														String(
															vacanciaForm.vinculo_tipo_id,
														)}
												</option>
											)}
										{vinculoOpcoes.map((v) => (
											<option
												key={v.value}
												value={v.value}
												className='text-gray-600'>
												{v.label}
											</option>
										))}
									</select>
									{getFieldErrorMessage(
										vacanciaErrors.vinculo_tipo_id,
									)}
								</div>

								{/* Regime */}
								<div>
									<label className='block text-[14px] font-medium text-[#13335A] mb-1'>
										Regime
									</label>
									<select
										value={vacanciaForm.regime}
										disabled={vacanciaModalReadOnly}
										onChange={(e) =>
											handleVacanciaInput(
												'regime',
												e.target.value,
											)
										}
										aria-invalid={Boolean(
											vacanciaErrors.regime,
										)}
										className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none ${vacanciaModalReadOnly ? 'bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed' : 'bg-white'} ${vacanciaHasRegime ? 'text-gray-600' : 'text-gray-300'} ${vacanciaErrors.regime ? 'border-[#dc2626]' : 'border-gray-200'}`}>
										<option
											value=''
											className='text-gray-300'>
											Selecione
										</option>
										{regimesOptions.map((item) => (
											<option
												key={item?.codigo}
												value={item?.codigo}
												className='text-gray-600'>
												{item?.nome || item?.codigo}
											</option>
										))}
									</select>
									{getFieldErrorMessage(
										vacanciaErrors.regime,
									)}
								</div>

								{/* Recorrência */}
								<div>
									<label className='block text-[14px] font-medium text-[#13335A] mb-1'>
										Recorrência
									</label>
									<select
										value={vacanciaForm.nome_recorrencia}
										disabled={
											vacanciaModalReadOnly ||
											modalidade === 'MEDICA'
										}
										onChange={(e) =>
											handleVacanciaRecorrenciaChange(
												e.target.value,
											)
										}
										aria-invalid={Boolean(
											vacanciaErrors.nome_recorrencia,
										)}
										className={`w-full h-[34px] border rounded-md px-3 text-[13px] outline-none ${vacanciaModalReadOnly || modalidade === 'MEDICA' ? 'bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed' : 'bg-white'} ${vacanciaHasRecorrencia ? 'text-gray-600' : 'text-gray-300'} ${vacanciaErrors.nome_recorrencia ? 'border-[#dc2626]' : 'border-gray-200'}`}>
										<option
											value=''
											className='text-gray-300'>
											Selecione
										</option>
										{recorrenciaNomeOptions.map(
											(item) =>
												(modalidade !== 'MEDICA' ||
													isWeeklyRecorrencia(
														item?.tipo_recorrencia ||
															item?.value,
													)) && (
													<option
														key={item?.value}
														value={item?.value}
														className='text-gray-600'>
														{item?.label}
													</option>
												),
										)}
									</select>
									{getFieldErrorMessage(
										vacanciaErrors.nome_recorrencia,
									)}
								</div>

								{vacanciaMostrarBlocoTurnoCiclo && (
									<div className='lg:col-span-2 rounded-md border px-3 py-2 shadow-md'>
										<label className='mb-2 block text-[13px] font-medium text-[#13335A]'>
											Turno
										</label>
										{vacanciaTurnoEhPersonalizado ? (
											<div className='space-y-2'>
												<div className='w-full max-w-[137px]'>
													<DiaSemanaHorarioBloco
														minWidthClassName='w-full'
														marginTopClassName='mt-0'
														forceFullTimeInputs
														disabled={
															vacanciaModalReadOnly
														}
														horaInicio={
															vacanciaForm.hora_inicio
														}
														horaFim={
															vacanciaForm.hora_fim
														}
														onChangeHoraInicio={(
															value,
														) =>
															handleVacanciaInput(
																'hora_inicio',
																value,
															)
														}
														onChangeHoraFim={(
															value,
														) =>
															handleVacanciaInput(
																'hora_fim',
																value,
															)
														}
													/>
													{getFieldErrorMessage(
														vacanciaErrors.hora_inicio ||
															vacanciaErrors.hora_fim,
													)}
												</div>
												{!vacanciaModalReadOnly && (
													<button
														type='button'
														onClick={
															handleVacanciaUsarTurnosPredefinidos
														}
														className='text-[12px] font-medium text-[#004A80] underline underline-offset-2'>
														Usar turnos predefinidos
													</button>
												)}
											</div>
										) : (
											<div>
												<div className='grid grid-cols-3 gap-2'>
													{TURNOS_DOZE_HORAS_OPTIONS.map(
														(turno) => (
															<button
																type='button'
																key={
																	turno.value
																}
																disabled={
																	vacanciaModalReadOnly
																}
																onClick={() =>
																	handleVacanciaTurnoCicloChange(
																		turno,
																	)
																}
																className={`h-[32px] rounded-md border px-2 text-[12px] font-medium transition-colors ${turno.inicio === vacanciaForm.hora_inicio && turno.fim === vacanciaForm.hora_fim ? 'border-[#004A80] bg-[#004A80] text-white' : 'border-gray-200 bg-white text-[#4F5257] hover:bg-[#F3F4F6]'}`}>
																{turno.label}
															</button>
														),
													)}
												</div>
												{!vacanciaModalReadOnly && (
													<button
														type='button'
														onClick={
															handleVacanciaPersonalizarTurno
														}
														className='mt-2 h-[32px] rounded-md border border-gray-200 bg-white px-3 text-[12px] font-medium text-[#4F5257] transition-colors hover:bg-[#F3F4F6]'>
														Personalizar
													</button>
												)}
											</div>
										)}
									</div>
								)}

								{/* Dias da Semana */}
								{vacanciaMostrarBlocoDiasSemana && (
									<div className='lg:col-span-2 border py-1 px-2 rounded-md shadow-md max-md:overflow-x-hidden'>
										<label className='block text-[14px] font-medium text-[#13335A] mb-1'>
											Dias da semana
										</label>
										<div className='flex flex-wrap justify-start gap-2'>
											{DIAS_SEMANA_OPTIONS.map((dia) => {
												const isSelected =
													vacanciaDiasSemanaEfetivos.includes(
														dia.value,
													);
												const isLocked =
													vacanciaDiaSemanaBloqueado ===
													dia.value;
												const diaHorario = isSelected
													? getDiaSemanaHorarioConfig(
															{
																diaSemana:
																	dia.value,
																diasSemanaHorarios:
																	vacanciaForm.dias_semana_horarios,
																fallbackHoraInicio:
																	vacanciaForm.hora_inicio,
																fallbackHoraFim:
																	vacanciaForm.hora_fim,
															},
														)
													: {
															hora_inicio: '',
															hora_fim: '',
														};
												return (
													<div
														key={dia.value}
														className='relative pb-4 flex flex-col items-center max-md:pb-3'>
														<button
															type='button'
															onClick={() =>
																toggleVacanciaDiaSemana(
																	dia.value,
																)
															}
															disabled={
																isLocked ||
																vacanciaModalReadOnly
															}
															className={`min-w-[92px] h-[34px] px-3 rounded-md border text-[12px] font-medium transition-all duration-200 disabled:opacity-100 disabled:cursor-default max-md:min-w-[84px] max-md:h-[30px] max-md:px-2 ${
																isSelected
																	? 'bg-[#004A80] border-[#004A80] text-white'
																	: 'bg-white border-gray-200 text-[#6B7280] hover:bg-[#F3F4F6]'
															}`}>
															{dia.label}
														</button>
														<DiaSemanaHorarioBloco
															disabled={
																!isSelected ||
																vacanciaModalReadOnly
															}
															minWidthClassName='min-w-[92px] max-md:min-w-[84px] max-md:h-[30px]'
															horaInicio={
																diaHorario.hora_inicio
															}
															horaFim={
																diaHorario.hora_fim
															}
															onChangeHoraInicio={(
																nextValue,
															) =>
																handleVacanciaDiaSemanaHorarioChange(
																	dia.value,
																	'hora_inicio',
																	nextValue,
																)
															}
															onChangeHoraFim={(
																nextValue,
															) =>
																handleVacanciaDiaSemanaHorarioChange(
																	dia.value,
																	'hora_fim',
																	nextValue,
																)
															}
														/>
														{isLocked && (
															<span className='pointer-events-none absolute left-1/2 top-[80px] -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold  text-[#004A80] '>
																Primeiro dia
															</span>
														)}
													</div>
												);
											})}
										</div>
										{getFieldErrorMessage(
											vacanciaErrors.dias_semana,
										)}
										{vacanciaUsaRecorrenciaCiclo && (
											<p className='mt-2 text-[11px] text-[#6B7280]'>
												Os dias e horários foram
												preenchidos automaticamente com
												base na data inicial e no ciclo
												selecionado.
											</p>
										)}
									</div>
								)}

								{(vacanciaIsDataEspecifica ||
									(modalidade === 'MEDICA' &&
										vacanciaMostrarBlocoDiasSemana)) && (
									<div className='lg:col-span-2 '>
										{vacanciaDatasExtras.length > 0 && (
											<div className='rounded-md border border-gray-200 p-3 max-sm:p-2 shadow-md'>
												<p className='block text-[14px] font-medium text-[#13335A] mb-1'>
													Datas específicas
												</p>
												<div className='flex flex-wrap gap-3 max-sm:justify-start max-sm:gap-2'>
													{vacanciaDatasExtras.map(
														(item) => (
															<div
																key={item.id}
																className='relative border rounded-md border-gray-200 p-1 flex flex-col gap-2 bg-[#f5f5f5] max-sm:gap-1'>
																{!vacanciaModalReadOnly && (
																	<button
																		type='button'
																		onClick={() =>
																			handleRemoverVacanciaDataExtra(
																				item.id,
																			)
																		}
																		className='absolute right-[10px] top-[13px] flex w-[12px] h-[12px] justify-center items-center text-[17px] font-normal bg-gray-100 hover:bg-gray-200 text-[#BD504E] hover:text-[#883433] rounded-[3px] transition '>
																		&times;
																	</button>
																)}
																<div className='flex flex-col gap-1 max-w-[110px]'>
																	<select
																		value={
																			item.selected_date ||
																			''
																		}
																		disabled={
																			vacanciaModalReadOnly
																		}
																		onChange={(
																			e,
																		) =>
																			handleVacanciaDataExtraChange(
																				item.id,
																				e
																					.target
																					.value,
																			)
																		}
																		className='h-[34px] w-full rounded-md border border-gray-200 px-1 text-[12px] text-[#4F5257] outline-none appearance-none disabled:bg-[#fdfdfd] disabled:text-[#6B7280]'>
																		<option value=''>
																			Dia
																			00
																		</option>
																		{getVacanciaDataExtraOptions(
																			item.id,
																		).map(
																			(
																				option,
																			) => (
																				<option
																					key={
																						option.value
																					}
																					value={
																						option.value
																					}>
																					{
																						option.label
																					}
																				</option>
																			),
																		)}
																	</select>
																	<DiaSemanaHorarioBloco
																		disabled={
																			vacanciaModalReadOnly
																		}
																		minWidthClassName='w-full '
																		marginTopClassName='mt-0'
																		containerClassName='rounded-md '
																		horaInicio={
																			item.hora_inicio ||
																			''
																		}
																		horaFim={
																			item.hora_fim ||
																			''
																		}
																		onChangeHoraInicio={(
																			nextValue,
																		) =>
																			handleVacanciaDataExtraHorarioChange(
																				item.id,
																				'hora_inicio',
																				nextValue,
																			)
																		}
																		onChangeHoraFim={(
																			nextValue,
																		) =>
																			handleVacanciaDataExtraHorarioChange(
																				item.id,
																				'hora_fim',
																				nextValue,
																			)
																		}
																	/>
																</div>
															</div>
														),
													)}
													{!vacanciaModalReadOnly && (
														<DataEspecificaAction
															compact
															onClick={
																handleAdicionarVacanciaDataExtra
															}
														/>
													)}
												</div>
											</div>
										)}
										{!vacanciaModalReadOnly &&
											vacanciaDatasExtras.length ===
												0 && (
												<DataEspecificaAction
													onClick={
														handleAdicionarVacanciaDataExtra
													}
												/>
											)}
										{getFieldErrorMessage(
											vacanciaErrors.datas_extras,
										)}
									</div>
								)}

								{/* Observação */}
								<div className='lg:col-span-2'>
									<label className='block text-[14px] font-medium text-[#13335A] mb-1'>
										Observação
									</label>
									<textarea
										rows={5}
										value={vacanciaForm.observacao}
										readOnly={vacanciaModalReadOnly}
										onChange={(e) =>
											handleVacanciaInput(
												'observacao',
												e.target.value,
											)
										}
										className={`w-full border border-gray-200 rounded-md px-3 py-2 text-[13px] outline-none resize-none max-sm:max-h-[96px] ${vacanciaModalReadOnly ? 'bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed' : 'bg-white'} ${vacanciaHasObservacao ? 'text-gray-600' : 'text-gray-400'}`}
									/>
								</div>
							</div>

							{(vacanciaApplyError ||
								(submitErrorScope === 'vacancia' &&
									submitError)) && (
								<div className='mt-4 text-[12px] text-[#dc2626]'>
									{vacanciaApplyError || submitError}
								</div>
							)}

							<div className='flex justify-center'>
								{vacanciaModalReadOnly ? (
									<button
										type='button'
										onClick={handleCloseVacanciaModal}
										className='w-[170px] h-[34px] border border-gray-200 rounded-md shadow-md text-[13px] text-[#7b7d80] hover:bg-gray-50 transition max-sm:w-[150px] max-sm:h-[32px]'>
										Fechar
									</button>
								) : (
									<button
										type='button'
										onClick={handleSubmitVacancia}
										disabled={isSubmittingVacancia}
										className='w-[170px] h-[34px] border border-gray-200 rounded-md shadow-md text-[13px] text-[#7b7d80] hover:bg-gray-50 transition disabled:opacity-50 max-sm:w-[150px] max-sm:h-[32px]'>
										{isSubmittingVacancia
											? 'Enviando...'
											: isEditingVacancia
												? 'Salvar'
												: 'Enviar'}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{isDeleteConfirmModalOpen && (
				<div
					className='fixed inset-0 z-[90] font-inter flex items-center justify-center px-4 max-sm:items-center max-sm:overflow-hidden max-sm:px-3 max-sm:py-4'
					role='dialog'
					aria-modal='true'
					aria-label='Confirmar exclusão'>
					<button
						type='button'
						className='absolute inset-0 bg-black/20'
						onClick={handleCloseDeleteModal}
						aria-label='Fechar modal de exclusão'
					/>

					<div className='relative max-h-[calc(100vh-2rem)] w-full max-w-[530px] overflow-y-auto overflow-x-hidden rounded-[14px] border border-[#E5E7EB] bg-white px-6 py-7 shadow-[0_10px_30px_rgba(15,23,42,0.14)] font-inter max-sm:max-w-[350px] max-sm:max-h-[84vh] max-sm:rounded-[10px] max-sm:px-4 max-sm:py-4'>
						<div className='flex items-center gap-4 max-sm:gap-3'>
							<div className='flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-[#f0f0f0] max-sm:h-[40px] max-sm:w-[40px]'>
								<MdOutlineWarningAmber className='h-7 w-7 text-[#D9534F] max-sm:h-6 max-sm:w-6' />
							</div>
							<div className='min-w-0 pt-1'>
								<h2 className='text-[17px] font-semibold text-[#4B5563] max-sm:text-[15px]'>
									Confirmar Exclusão
								</h2>
							</div>
						</div>

						<div className='mt-7 space-y-3 pl-[2px] max-sm:mt-4 max-sm:space-y-2'>
							<p className='text-[14px] leading-2 text-[#5F6368] max-sm:text-[13px]'>
								{isVacanciaLinha(
									deleteTargetLinha || linhaSelecionada,
								)
									? 'Você confirma que deseja excluir esta vacância?'
									: 'Você tem certeza que deseja excluir o profissional da escala?'}
							</p>
							<p className='text-[14px] leading-2 text-[#5F6368] max-sm:text-[13px]'>
								Esta ação não pode ser desfeita
							</p>
						</div>

						{!deletePreview &&
							(!isSetorTodosAplicado ||
								isVacanciaLinha(
									deleteTargetLinha || linhaSelecionada,
								)) && (
								<div className='mt-5 flex flex-col gap-4 max-sm:mt-4'>
									{
										<div>
											<label className='block text-[14px] font-medium text-[#5F6368] mb-2 max-sm:text-[13px] max-sm:mb-1'>
												Motivo da exclusão
											</label>
											<div className='flex flex-col gap-3 max-w-[350px] max-sm:max-w-none max-sm:gap-2'>
												<select
													value={deleteReason}
													onChange={(e) => {
														const nextValue =
															e.target.value;
														setDeleteReason(
															nextValue,
														);
														setDeletePreview(null);
														if (
															nextValue !==
															'OUTRO'
														) {
															setDeleteReasonOther(
																'',
															);
														}
													}}
													className='w-full h-[38px] rounded-[8px] border border-[#D9DDE3] bg-white px-3 text-[13px] text-[#5F6368] outline-none transition focus:border-[#9CA3AF] max-sm:h-[34px]'>
													<option value=''>
														Selecione um motivo
													</option>
													{DELETE_REASON_OPTIONS.map(
														(option) => (
															<option
																key={
																	option.value
																}
																value={
																	option.value
																}>
																{option.label}
															</option>
														),
													)}
												</select>
												{deleteReason === 'OUTRO' && (
													<textarea
														rows={2}
														value={
															deleteReasonOther
														}
														onChange={(e) => {
															setDeleteReasonOther(
																e.target.value,
															);
															setDeletePreview(
																null,
															);
														}}
														placeholder='Descreva o motivo'
														className='w-full min-h-[38px] rounded-[8px] border border-[#D9DDE3] bg-white px-3 py-2 text-[13px] text-[#5F6368] outline-none resize-none transition focus:border-[#9CA3AF] max-sm:min-h-[34px]'
													/>
												)}
											</div>
										</div>
									}
								</div>
							)}

						{deletePreview && (
							<div className='mt-5 rounded-[8px] border border-[#BFDBFE] bg-[#F0F7FF] p-3 text-[12px] text-[#374151] max-sm:mt-4'>
								<p className='font-semibold text-[#1E4E79]'>
									{deletePreview.preview?.resumo?.titulo ||
										'Revisão necessária antes da exclusão'}
								</p>
								<p className='mt-1 leading-5'>
									{deletePreview.preview?.resumo?.mensagem ||
										deletePreview.preview?.mensagem ||
										'Confira os impactos e os avisos informados antes de confirmar.'}
								</p>
								{getQuantidadeJornadasSemCardsSaneadas(
									deletePreview.preview,
								) > 0 && (
									<p className='mt-2 rounded-md border border-[#BBE5C5] bg-[#F0FDF4] px-2 py-1.5 text-[#166534]'>
										Serão removidas{' '}
										{getQuantidadeJornadasSemCardsSaneadas(
											deletePreview.preview,
										)}{' '}
										ocorrência(s) técnica(s) futura(s) sem
										cards ao confirmar a exclusão.
									</p>
								)}
								{Array.isArray(
									deletePreview.preview?.consequencias,
								) &&
									deletePreview.preview.consequencias.length >
										0 && (
										<ul className='mt-2 list-disc space-y-1 pl-4'>
											{deletePreview.preview.consequencias.map(
												(consequencia, index) => (
													<li
														key={`consequencia-${index}`}>
														{consequencia?.mensagem ||
															consequencia?.descricao ||
															consequencia?.codigo ||
															'Impacto identificado na escala.'}
													</li>
												),
											)}
										</ul>
									)}
								{getPreviewIssues(deletePreview.preview)
									.length > 0 && (
									<ul className='mt-2 list-disc space-y-1 pl-4 text-[#9A6700]'>
										{getPreviewIssues(
											deletePreview.preview,
										).map((issue, index) => (
											<li
												key={`issue-${issue?.issue_id || index}`}>
												{issue?.mensagem ||
													issue?.titulo ||
													issue?.codigo}
											</li>
										))}
									</ul>
								)}
								{getJornadaDivergentePreviewDetail(
									deletePreview.preview,
								).hasDivergence && (
									<div className='mt-2 rounded-md border border-[#F4C7C3] bg-[#FFF4F2] px-2 py-2 text-[#9D2A20]'>
										<p className='font-semibold'>
											Há cards de jornada sem linha ativa
											correspondente.
										</p>
										<p className='mt-1 leading-5'>
											{getJornadaDivergentePreviewDetail(
												deletePreview.preview,
											).setoresCardsIds.length > 0 && (
												<>
													Cards nos setores:{' '}
													{getJornadaDivergentePreviewDetail(
														deletePreview.preview,
													).setoresCardsIds.join(
														', ',
													)}
													.{' '}
												</>
											)}
											{getJornadaDivergentePreviewDetail(
												deletePreview.preview,
											).setoresSemLinhaAtivaIds.length >
												0 && (
												<>
													Sem linha ativa:{' '}
													{getJornadaDivergentePreviewDetail(
														deletePreview.preview,
													).setoresSemLinhaAtivaIds.join(
														', ',
													)}
													.{' '}
												</>
											)}
											{getJornadaDivergentePreviewDetail(
												deletePreview.preview,
											).setoresLinhasAtivasIds.length >
												0 && (
												<>
													Linhas ativas:{' '}
													{getJornadaDivergentePreviewDetail(
														deletePreview.preview,
													).setoresLinhasAtivasIds.join(
														', ',
													)}
													.
												</>
											)}
										</p>
										<p className='mt-1 leading-5'>
											Esta divergência precisa de
											reconciliação administrativa antes
											da exclusão.
										</p>
									</div>
								)}
								{!isPreviewAcaoConfirmavel(
									deletePreview.preview,
								) && (
									<p className='mt-2 font-medium text-[#B42318]'>
										{getPreviewBlockMessage(
											deletePreview.preview,
											'Este encerramento não pode ser confirmado nas condições atuais.',
										)}
										{
											' Corrija as pendências e gere uma nova confirmação.'
										}
									</p>
								)}
							</div>
						)}

						{submitErrorScope === 'delete' && submitError && (
							<div className='mt-4 text-[12px] text-[#dc2626]'>
								{submitError}
							</div>
						)}

						<div className='mt-8 flex items-center justify-center gap-3 max-sm:mt-5 max-sm:gap-2'>
							<button
								type='button'
								onClick={handleCloseDeleteModal}
								className='min-w-[90px] h-[36px] rounded-[8px] border border-[#D9DDE3] bg-white px-5 text-[13px] font-medium text-[#5F6368] shadow-sm transition hover:bg-[#F9FAFB] max-sm:h-[34px] max-sm:px-4'>
								Cancelar
							</button>
							<button
								type='button'
								onClick={handleConfirmDelete}
								disabled={
									isSubmittingPanel ||
									Boolean(
										deletePreview &&
										!isPreviewAcaoConfirmavel(
											deletePreview.preview,
										),
									)
								}
								className='min-w-[90px] h-[36px] rounded-[8px] border border-[#C64D4A] bg-[#C64D4A] px-5 text-[13px] font-medium text-white shadow-sm transition hover:bg-[#B84441] disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-[34px] max-sm:px-4'>
								{isSubmittingPanel ? 'Excluindo...' : 'Excluir'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default GerenciarProfissionais;
